import { FilterComponentType, InfoConditionType, VALUE_TRANSFORMS, enumAttribute, getAllowedAttributeValues } from "@repo/attributes";
import { Currency, Languages, Locale } from "@repo/i18n";
import { translatable } from "@repo/translatable";
import { ALL_BUYER_DELIVERY_COUNTRIES } from "@repo/shared-primitives";
import * as z$1 from "zod";
import { z } from "zod";
import { makeZodEnumFilter, zodNumberFilter, zodNumericFilter, zodRelationFilter, zodStringFilter } from "@repo/zod-filters";
import slugify from "slugify";
import { baseSlugify } from "@repo/slug-generators";

//#region src/lib/game-configuration.types.ts
let SupportedGames = /* @__PURE__ */ function(SupportedGames) {
	SupportedGames["MTG"] = "mtg";
	SupportedGames["Riftbound"] = "riftbound";
	SupportedGames["Lorcana"] = "lorcana";
	SupportedGames["Sorcery"] = "sorcery";
	SupportedGames["FAB"] = "fab";
	SupportedGames["Pokemon"] = "pokemon";
	SupportedGames["Wankul"] = "wankul";
	SupportedGames["OnePiece"] = "onepiece";
	SupportedGames["YuGiOh"] = "ygo";
	SupportedGames["YuGiOhOcg"] = "ygo-ocg";
	SupportedGames["ExampleGame"] = "example-game";
	SupportedGames["Rise"] = "rise";
	SupportedGames["Drakerion"] = "drakerion";
	SupportedGames["Cyberpunk"] = "cyberpunk";
	SupportedGames["PokemonJapan"] = "pokemon-japan";
	SupportedGames["StarWarsUnlimited"] = "swu";
	SupportedGames["NarutoMythos"] = "naruto-mythos";
	SupportedGames["GrandArchive"] = "grand-archive";
	SupportedGames["Eoa"] = "eoa";
	SupportedGames["DbsFusion"] = "dbs-fusion";
	SupportedGames["DbsMasters"] = "dbs-masters";
	SupportedGames["Gundam"] = "gundam";
	SupportedGames["ChronoCore"] = "chrono-core";
	SupportedGames["Palworld"] = "palworld";
	SupportedGames["Azuki"] = "azuki";
	SupportedGames["Dreamland"] = "dreamland";
	return SupportedGames;
}({});
const PRIMARY_GAMES = new Set([
	SupportedGames.MTG,
	SupportedGames.Pokemon,
	SupportedGames.YuGiOh,
	SupportedGames.Sorcery,
	SupportedGames.OnePiece,
	SupportedGames.FAB,
	SupportedGames.Lorcana,
	SupportedGames.Riftbound,
	SupportedGames.Cyberpunk
]);
let SupportedMarketplace = /* @__PURE__ */ function(SupportedMarketplace) {
	SupportedMarketplace["Cardmarket"] = "Cardmarket";
	SupportedMarketplace["Tcgplayer"] = "TCGplayer";
	return SupportedMarketplace;
}({});
let SupportedGradingService = /* @__PURE__ */ function(SupportedGradingService) {
	SupportedGradingService["PSA"] = "PSA";
	SupportedGradingService["Beckett"] = "Beckett";
	SupportedGradingService["CGC"] = "CGC";
	SupportedGradingService["TAG"] = "TAG";
	SupportedGradingService["PCG"] = "PCG";
	SupportedGradingService["CCC"] = "CCC";
	SupportedGradingService["PCA"] = "PCA";
	SupportedGradingService["CollectAura"] = "CollectAura";
	SupportedGradingService["MTGGrade"] = "MTGGrade";
	SupportedGradingService["PureGrading"] = "PureGrading";
	SupportedGradingService["SGS"] = "SGS";
	SupportedGradingService["AceGrading"] = "AceGrading";
	return SupportedGradingService;
}({});
let SetType = /* @__PURE__ */ function(SetType) {
	SetType["PHYSICAL"] = "physical";
	SetType["DIGITAL"] = "digital";
	return SetType;
}({});
/**
* Sorted enum of card conditions
* Used in the backend and most of the codebase
*/
let CardCondition = /* @__PURE__ */ function(CardCondition) {
	CardCondition["NEAR_MINT"] = "A";
	CardCondition["LIGHTLY_PLAYED"] = "B";
	CardCondition["MODERATELY_PLAYED"] = "C";
	CardCondition["HEAVILY_PLAYED"] = "D";
	CardCondition["DAMAGED"] = "E";
	return CardCondition;
}({});
/**
* Sorted enum of card conditions
*/
let AmericanCardCondition = /* @__PURE__ */ function(AmericanCardCondition) {
	AmericanCardCondition["NEAR_MINT"] = "Near Mint";
	AmericanCardCondition["LIGHTLY_PLAYED"] = "Lightly Played";
	AmericanCardCondition["MODERATELY_PLAYED"] = "Moderately Played";
	AmericanCardCondition["HEAVILY_PLAYED"] = "Heavily Played";
	AmericanCardCondition["DAMAGED"] = "Damaged";
	return AmericanCardCondition;
}({});
/**
* Card conditions for European games
*/
let EuropeanCardCondition = /* @__PURE__ */ function(EuropeanCardCondition) {
	EuropeanCardCondition["NEAR_MINT"] = "Near Mint";
	EuropeanCardCondition["EXCELLENT"] = "Excellent";
	EuropeanCardCondition["GOOD"] = "Good";
	EuropeanCardCondition["PLAYED"] = "Played";
	EuropeanCardCondition["POOR"] = "Poor";
	return EuropeanCardCondition;
}({});
/**
* Shortened card conditions
*/
let ShortAmericanCardCondition = /* @__PURE__ */ function(ShortAmericanCardCondition) {
	ShortAmericanCardCondition["NM"] = "NM";
	ShortAmericanCardCondition["LP"] = "LP";
	ShortAmericanCardCondition["MP"] = "MP";
	ShortAmericanCardCondition["HP"] = "HP";
	ShortAmericanCardCondition["DMG"] = "DMG";
	return ShortAmericanCardCondition;
}({});
/**
* Shortened card conditions for European games
*/
let ShortEuropeanCardCondition = /* @__PURE__ */ function(ShortEuropeanCardCondition) {
	ShortEuropeanCardCondition["NM"] = "NM";
	ShortEuropeanCardCondition["EX"] = "EX";
	ShortEuropeanCardCondition["GD"] = "GD";
	ShortEuropeanCardCondition["PL"] = "PL";
	ShortEuropeanCardCondition["PO"] = "PO";
	return ShortEuropeanCardCondition;
}({});
function isCardFinish(value) {
	return Object.values(CardFinish).includes(value);
}
/**
* All the card finishes for every game
*/
let CardFinish = /* @__PURE__ */ function(CardFinish) {
	CardFinish["STANDARD"] = "Standard";
	CardFinish["FOIL"] = "Foil";
	CardFinish["RAINBOW"] = "Rainbow";
	CardFinish["GOLD"] = "Gold";
	CardFinish["RAINBOW_FOIL"] = "Rainbow Foil";
	CardFinish["COLD_FOIL"] = "Cold Foil";
	CardFinish["GOLD_FOIL"] = "Gold Foil";
	CardFinish["ETCHED"] = "Etched";
	CardFinish["SIGNED"] = "Signed";
	CardFinish["REVERSE"] = "Reverse Holo";
	CardFinish["BLAST_FOIL_RAINBOW"] = "Blast Foil Rainbow";
	CardFinish["BUBBLES_FOIL"] = "Bubbles Foil";
	CardFinish["CRACKED_ICE_FOIL"] = "Cracked Ice Foil";
	CardFinish["GALAXY_FOIL"] = "Galaxy Foil";
	CardFinish["GLOSSY_FOIL"] = "Glitter Foil";
	CardFinish["MULTI_SIDELINE_FOIL"] = "Multi Sideline Foil";
	CardFinish["RAINBOW_SOLID_TEXTURE_STAMP"] = "Rainbow Solid Texture Stamp";
	CardFinish["STAR_FOIL"] = "Star Foil";
	CardFinish["SURGE_FOIL"] = "Surge Foil";
	CardFinish["FULL_ART_GOLD_SIGN"] = "Full Art Gold Sign";
	CardFinish["DEPTH_LENTICULAR"] = "Depth Lenticular";
	CardFinish["FLIP_LENTICULAR"] = "Flip Lenticular";
	CardFinish["LENTICULAR"] = "Lenticular";
	CardFinish["ZARIMOTH"] = "Zarimoth";
	CardFinish["SERIALIZED"] = "Serialized";
	CardFinish["SERIALIZED_ROSE_GOLD"] = "Serialized Rose Gold";
	CardFinish["SERIALIZED_GOLD"] = "Serialized Gold";
	CardFinish["HOLOGRAPHIC"] = "Holographic";
	CardFinish["EMBOSSED"] = "Embossed";
	CardFinish["HOLOFOIL"] = "Holofoil";
	return CardFinish;
}({});
/**
* All the list presets for every game
*/
let ListPresetType = /* @__PURE__ */ function(ListPresetType) {
	ListPresetType["STANDARD"] = "Standard";
	ListPresetType["PLAYSET"] = "Playset";
	ListPresetType["FOIL"] = "Foil";
	ListPresetType["FOIL_PLAYSET"] = "Foil Playset";
	ListPresetType["MASTERSET"] = "Master Set";
	return ListPresetType;
}({});
const cardmarketVendorId = {
	key: SupportedMarketplace.Cardmarket,
	label: "Cardmarket ID",
	isMarketplaceId: true
};
const tcgplayerVendorId = {
	key: SupportedMarketplace.Tcgplayer,
	label: "TCGplayer ID",
	isMarketplaceId: true
};

//#endregion
//#region src/lib/products.types.ts
/**
* ProductType defines whether a product is a card or a sealed product
*/
let ProductType = /* @__PURE__ */ function(ProductType) {
	ProductType["CARD"] = "card";
	ProductType["SEALED"] = "sealed";
	return ProductType;
}({});

//#endregion
//#region src/lib/attributes/common-attributes.ts
const nameAttribute = {
	entity: "product",
	name: translatable("global.attributes.name.name", "Name"),
	description: translatable("global.attributes.name.description", "The full card name"),
	getTranslationFields(context) {
		const fields = [];
		if (context.language !== void 0) fields.push(`translations.${context.language}`);
		return fields;
	},
	type: "i18n-string",
	array: false,
	selectable: true,
	filter: FilterComponentType.Input
};
/**
* Shared finish attribute values - used by both finishAttribute and finishesAttribute
*/
const finishAttributeValues = {
	Standard: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	Foil: { label: translatable("global.attributes.finish.values.foil", "Foil") },
	Rainbow: { label: translatable("global.attributes.finish.values.rainbow", "Rainbow") },
	"Rainbow Foil": { label: translatable("global.attributes.finish.values.rainbowfoil", "Rainbow Foil") },
	"Cold Foil": { label: translatable("global.attributes.finish.values.coldfoil", "Cold Foil") },
	"Gold Foil": { label: translatable("global.attributes.finish.values.goldfoil", "Gold Foil") },
	Etched: { label: translatable("global.attributes.finish.values.etched", "Etched") },
	Signed: { label: translatable("global.attributes.finish.values.signed", "Signed") },
	"Reverse Holo": { label: translatable("global.attributes.finish.values.reverse", "Reverse Holo") },
	"Blast Foil Rainbow": { label: translatable("global.attributes.finish.values.blastFoilRainbow", "Blast Foil Rainbow") },
	"Bubbles Foil": { label: translatable("global.attributes.finish.values.bubblesFoil", "Bubbles Foil") },
	"Cracked Ice Foil": { label: translatable("global.attributes.finish.values.crackedIceFoil", "Cracked Ice Foil") },
	"Galaxy Foil": { label: translatable("global.attributes.finish.values.galaxyFoil", "Galaxy Foil") },
	"Glitter Foil": { label: translatable("global.attributes.finish.values.glitterFoil", "Glitter Foil") },
	"Multi Sideline Foil": { label: translatable("global.attributes.finish.values.multiSidelineFoil", "Multi Sideline Foil") },
	"Rainbow Solid Texture Stamp": { label: translatable("global.attributes.finish.values.rainbowSolidTextureStamp", "Rainbow Solid Texture Stamp") },
	"Star Foil": { label: translatable("global.attributes.finish.values.starFoil", "Star Foil") },
	"Surge Foil": { label: translatable("global.attributes.finish.values.surgeFoil", "Surge Foil") },
	"Full Art Gold Sign": { label: translatable("global.attributes.finish.values.fullArtGoldSign", "Full Art Gold Sign") },
	"Depth Lenticular": { label: translatable("global.attributes.finish.values.depthLenticular", "Depth Lenticular") },
	"Flip Lenticular": { label: translatable("global.attributes.finish.values.flipLenticular", "Flip Lenticular") },
	Lenticular: { label: translatable("global.attributes.finish.values.lenticular", "Lenticular") },
	Gold: { label: translatable("global.attributes.finish.values.gold", "Gold") },
	Zarimoth: { label: translatable("global.attributes.finish.values.zarimoth", "Zarimoth") },
	Serialized: { label: translatable("global.attributes.finish.values.serialized", "Serialized") },
	"Serialized Rose Gold": { label: translatable("global.attributes.finish.values.serializedRoseGold", "Serialized Rose Gold") },
	"Serialized Gold": { label: translatable("global.attributes.finish.values.serializedGold", "Serialized Gold") },
	Holographic: { label: translatable("global.attributes.finish.values.holographic", "Holographic") },
	Embossed: { label: translatable("global.attributes.finish.values.embossed", "Embossed") },
	Holofoil: { label: translatable("global.attributes.finish.values.holofoil", "Holofoil") }
};
/**
* finishesAttribute - for products/variants
* Represents all possible finishes for a product (array of finishes)
* Maps to field: "finishes"
*/
const finishesAttribute = enumAttribute(CardFinish, {
	entity: "product",
	name: translatable("global.attributes.finish.name", "Finish"),
	type: "enum",
	array: true,
	path: "finishes",
	description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
	filter: FilterComponentType.Select,
	defaultOpen: false,
	values: finishAttributeValues
});
/**
* finishAttribute - for inventory/list items
* Represents the specific finish of an inventory instance (single value)
* Maps to field: "finish"
*/
const finishAttribute = enumAttribute(CardFinish, {
	entity: "inventory",
	name: translatable("global.attributes.finish.name", "Finish"),
	type: "enum",
	array: false,
	description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
	filter: FilterComponentType.Select,
	defaultOpen: false,
	values: finishAttributeValues
});
const gameAttribute = {
	entity: "product",
	relation: "game",
	type: "relation",
	idKey: "_id",
	dimensionKey: "slug",
	nameKey: "slug",
	name: translatable("global.attributes.game.name", "Game"),
	description: translatable("global.attributes.game.description", "Game"),
	selectable: true,
	filter: FilterComponentType.Select,
	array: false,
	values: [],
	singleChoice: false,
	defaultOpen: true,
	info: {
		message: translatable("global.attributes.game.infos", "Pick a game to see more game related filters"),
		condition: { type: InfoConditionType.NoSelection }
	}
};
const expansionAttribute = {
	entity: "product",
	relation: "expansion",
	type: "relation",
	idKey: "_id",
	nameKey: "slug",
	path: "expansion._id",
	name: translatable("global.attributes.expansion.name", "Expansion"),
	description: translatable("global.attributes.expansion.description", "Expansion"),
	selectable: true,
	filter: FilterComponentType.Select,
	array: false,
	defaultOpen: false,
	values: []
};
const printNumberAttribute = {
	entity: "product",
	type: "string",
	name: translatable("global.attributes.printNumber.name", "Print Number"),
	description: translatable("global.attributes.printNumber.description", "Official print number for this printing"),
	selectable: true,
	array: false
};
/**
* This is a sortable print number, which is used to sort the printing
* It is not meant to be displayed
*/
const sortNumberAttribute = {
	entity: "product",
	type: "string",
	name: translatable("global.attributes.sortNumber.name", "Sort Number"),
	description: translatable("global.attributes.sortNumber.description", "Sort number for this printing"),
	selectable: true,
	array: false
};
const productTypeAttribute = enumAttribute(ProductType, {
	entity: "product",
	name: translatable("global.attributes.productType.name", "Product Type"),
	description: translatable("global.attributes.productType.description", "The product type (single or sealed)"),
	type: "enum",
	array: false,
	values: {
		[ProductType.CARD]: { label: translatable("global.attributes.productType.values.card", "Single cards") },
		[ProductType.SEALED]: { label: translatable("global.attributes.productType.values.sealed", "Sealed products") }
	},
	filter: FilterComponentType.CheckboxList,
	defaultOpen: true
});
const languagesAttribute = enumAttribute(Languages, {
	entity: "product",
	name: translatable("global.attributes.languages.name", "Languages"),
	description: translatable("global.attributes.languages.description", "Languages that this product is available in"),
	type: "enum",
	array: true,
	values: {
		[Languages.English]: { label: translatable("global.attributes.language.values.en", "English") },
		[Languages.Spanish]: { label: translatable("global.attributes.language.values.es", "Spanish") },
		[Languages.French]: { label: translatable("global.attributes.language.values.fr", "French") },
		[Languages.German]: { label: translatable("global.attributes.language.values.de", "German") },
		[Languages.Italian]: { label: translatable("global.attributes.language.values.it", "Italian") },
		[Languages.PortugueseBrazil]: { label: translatable("global.attributes.language.values.pt-BR", "Portuguese (Brazil)") },
		[Languages.PortuguesePortugal]: { label: translatable("global.attributes.language.values.pt-PT", "Portuguese (Portugal)") },
		[Languages.Russian]: { label: translatable("global.attributes.language.values.ru", "Russian") },
		[Languages.Chinese]: { label: translatable("global.attributes.language.values.zh-cn", "Chinese") },
		[Languages.ChineseSimplified]: { label: translatable("global.attributes.language.values.zh-Hans", "Chinese Simplified") },
		[Languages.ChineseTraditional]: { label: translatable("global.attributes.language.values.zh-Hant", "Chinese Traditional") },
		[Languages.Japanese]: { label: translatable("global.attributes.language.values.ja", "Japanese") },
		[Languages.Korean]: { label: translatable("global.attributes.language.values.ko", "Korean") },
		[Languages.Dutch]: { label: translatable("global.attributes.language.values.nl", "Dutch") },
		[Languages.Polish]: { label: translatable("global.attributes.language.values.pl", "Polish") },
		[Languages.Czech]: { label: translatable("global.attributes.language.values.cs", "Czech") },
		[Languages.Hungarian]: { label: translatable("global.attributes.language.values.hu", "Hungarian") },
		[Languages.Thai]: { label: translatable("global.attributes.language.values.th", "Thai") },
		[Languages.Indonesian]: { label: translatable("global.attributes.language.values.id", "Indonesian") },
		[Languages.Latin]: { label: translatable("global.attributes.language.values.la", "Latin") },
		[Languages.AncientGreek]: { label: translatable("global.attributes.language.values.grc", "Ancient Greek") },
		[Languages.Arabic]: { label: translatable("global.attributes.language.values.ar", "Arabic") },
		[Languages.Hebrew]: { label: translatable("global.attributes.language.values.he", "Hebrew") },
		[Languages.Sanskrit]: { label: translatable("global.attributes.language.values.sa", "Sanskrit") },
		[Languages.Phyrexian]: { label: translatable("global.attributes.language.values.art-x-phyrexian", "Phyrexian") }
	},
	filter: FilterComponentType.Select
});
const imageAttribute = {
	entity: "product",
	type: "string",
	name: translatable("global.attributes.image.name", "Image"),
	description: translatable("global.attributes.image.description", "Image of the product"),
	selectable: true,
	array: false
};
const priceEuAttribute = {
	entity: "product",
	name: translatable("global.attributes.marketValue.name", "Market Value"),
	description: translatable("global.attributes.marketValue.description", "The market value in the selected currency"),
	array: false,
	type: "range",
	selectable: false,
	filter: FilterComponentType.RangeSlider,
	unit: Currency.EUR,
	defaultOpen: true,
	valueTransform: VALUE_TRANSFORMS.CURRENCY,
	layoutDirection: "vertical"
};
const priceUsAttribute = {
	entity: "product",
	name: translatable("global.attributes.marketValue.name", "Market Value"),
	description: translatable("global.attributes.marketValue.description", "The market value in the selected currency"),
	array: false,
	type: "range",
	selectable: false,
	filter: FilterComponentType.RangeSlider,
	unit: Currency.USD,
	defaultOpen: true,
	valueTransform: VALUE_TRANSFORMS.CURRENCY,
	layoutDirection: "vertical"
};
const change24hEuAttribute = {
	entity: "product",
	name: translatable("global.attributes.change24h.name", "24h Change"),
	description: translatable("global.attributes.change24h.description", "Price change percentage in the last 24 hours"),
	array: false,
	type: "range",
	selectable: true,
	filter: FilterComponentType.RangeSlider,
	unit: "%",
	defaultOpen: true,
	valueTransform: VALUE_TRANSFORMS.PERCENTAGE
};
const change24hUsAttribute = {
	entity: "product",
	name: translatable("global.attributes.change24h.name", "24h Change"),
	description: translatable("global.attributes.change24h.description", "Price change percentage in the last 24 hours"),
	array: false,
	type: "range",
	selectable: true,
	filter: FilterComponentType.RangeSlider,
	unit: "%",
	defaultOpen: true,
	valueTransform: VALUE_TRANSFORMS.PERCENTAGE
};
const change7dEuAttribute = {
	entity: "product",
	name: translatable("global.attributes.change7d.name", "7d Change"),
	description: translatable("global.attributes.change7d.description", "Price change percentage in the last 7 days"),
	array: false,
	type: "range",
	selectable: true,
	filter: FilterComponentType.RangeSlider,
	unit: "%",
	defaultOpen: true,
	valueTransform: VALUE_TRANSFORMS.PERCENTAGE
};
const change7dUsAttribute = {
	entity: "product",
	name: translatable("global.attributes.change7d.name", "7d Change"),
	description: translatable("global.attributes.change7d.description", "Price change percentage in the last 7 days"),
	array: false,
	type: "range",
	selectable: true,
	filter: FilterComponentType.RangeSlider,
	unit: "%",
	defaultOpen: true,
	valueTransform: VALUE_TRANSFORMS.PERCENTAGE
};
const change30dEuAttribute = {
	entity: "product",
	name: translatable("global.attributes.change30d.name", "30d Change"),
	description: translatable("global.attributes.change30d.description", "Price change percentage in the last 30 days"),
	array: false,
	type: "range",
	selectable: true,
	filter: FilterComponentType.RangeSlider,
	unit: "%",
	defaultOpen: true,
	valueTransform: VALUE_TRANSFORMS.PERCENTAGE
};
/**
* Listing price filter attribute.
* Used in marketplace search to filter by actual listing prices (priceNormalizedEur).
* Unit is EUR because the canonical normalized price is EUR-denominated.
* The useFilterValueTransform hook handles EUR→displayCurrency conversion
* and shows the user's selected currency symbol.
*/
const listingPriceAttribute = {
	entity: "product",
	name: translatable("global.attributes.listingPrice.name", "Price"),
	description: translatable("global.attributes.listingPrice.description", "Filter by listing price"),
	array: false,
	type: "range",
	selectable: false,
	filter: FilterComponentType.RangeSlider,
	unit: Currency.EUR,
	defaultOpen: true,
	valueTransform: VALUE_TRANSFORMS.CURRENCY,
	layoutDirection: "vertical"
};
const change30dUsAttribute = {
	entity: "product",
	name: translatable("global.attributes.change30d.name", "30d Change"),
	description: translatable("global.attributes.change30d.description", "Price change percentage in the last 30 days"),
	array: false,
	type: "range",
	selectable: true,
	filter: FilterComponentType.RangeSlider,
	unit: "%",
	defaultOpen: true,
	valueTransform: VALUE_TRANSFORMS.PERCENTAGE
};
const commonAttributes = {
	name: nameAttribute,
	finish: finishAttribute,
	finishes: finishesAttribute,
	languages: languagesAttribute,
	expansion: expansionAttribute,
	game: gameAttribute,
	image: imageAttribute,
	printNumber: printNumberAttribute,
	sortNumber: sortNumberAttribute,
	productType: productTypeAttribute,
	priceUs: priceUsAttribute,
	priceEu: priceEuAttribute,
	change24hEu: change24hEuAttribute,
	change24hUs: change24hUsAttribute,
	change7dEu: change7dEuAttribute,
	change7dUs: change7dUsAttribute,
	change30dEu: change30dEuAttribute,
	change30dUs: change30dUsAttribute
};

//#endregion
//#region src/lib/attributes/i18n/en.json
var en_default$25 = { attributes: {
	"change24h": {
		"description": "Price change in the last 24 hours",
		"name": "24h change",
		"toggle": "Daily"
	},
	"change7d": {
		"description": "Price change in the last 7 days",
		"name": "7d change",
		"toggle": "Weekly"
	},
	"change30d": {
		"description": "Price change in the last 30 days",
		"name": "30d change",
		"toggle": "Monthly"
	},
	"expansion": {
		"description": "Expansion",
		"name": "Expansion"
	},
	"finish": {
		"description": "Finish of the card (foil/non-foil)",
		"name": "Finish",
		"values": {
			"coldfoil": "Cold Foil",
			"etched": "Etched",
			"foil": "Foil",
			"gold": "Gold",
			"goldfoil": "Gold Foil",
			"holofoil": "Holofoil",
			"rainbowfoil": "Rainbow Foil",
			"rainbow": "Rainbow",
			"reverse": "Reverse Holo",
			"signed": "Signed",
			"standard": "Standard",
			"blastFoilRainbow": "Blast Foil Rainbow",
			"bubblesFoil": "Bubbles Foil",
			"crackedIceFoil": "Cracked Ice Foil",
			"galaxyFoil": "Galaxy Foil",
			"glitterFoil": "Glitter Foil",
			"goldFoil": "Gold Foil",
			"multiSidelineFoil": "Multi Sideline Foil",
			"rainbowSolidTextureStamp": "Rainbow Solid Texture Stamp",
			"starFoil": "Star Foil",
			"surgeFoil": "Surge Foil",
			"fullArtGoldSign": "Full Art Gold Sign",
			"depthLenticular": "Depth Lenticular",
			"flipLenticular": "Flip Lenticular",
			"lenticular": "Lenticular",
			"zarimoth": "Zarimoth",
			"serialized": "Serialized",
			"serializedRoseGold": "Serialized Rose Gold",
			"serializedGold": "Serialized Gold",
			"holographic": "Holographic",
			"embossed": "Embossed"
		},
		"short": {
			"coldfoil": "CF",
			"etched": "ET",
			"foil": "F",
			"gold": "G",
			"goldfoil": "GF",
			"holofoil": "HF",
			"rainbowfoil": "RF",
			"rainbow": "RB",
			"reverse": "RH",
			"signed": "SIG",
			"standard": "NF",
			"blastFoilRainbow": "BFR",
			"bubblesFoil": "BF",
			"crackedIceFoil": "CIF",
			"galaxyFoil": "GXF",
			"glitterFoil": "GLF",
			"goldFoil": "GF",
			"multiSidelineFoil": "MSF",
			"rainbowSolidTextureStamp": "RSTS",
			"starFoil": "STF",
			"surgeFoil": "SUF",
			"fullArtGoldSign": "FAGS",
			"depthLenticular": "DL",
			"flipLenticular": "FL",
			"lenticular": "LEN",
			"zarimoth": "ZAR",
			"serialized": "SER",
			"serializedRoseGold": "SRG",
			"serializedGold": "SG",
			"holographic": "HOLO",
			"embossed": "EMB"
		}
	},
	"game": {
		"description": "Game",
		"infos": "Pick a game to see more game filters",
		"name": "Game"
	},
	"image": {
		"description": "Image of the product",
		"name": "Image"
	},
	"languages": {
		"description": "Languages that this product is available in",
		"name": "Languages"
	},
	"listingPrice": {
		"description": "Filter by listing price",
		"name": "Price"
	},
	"marketValue": {
		"description": "The market value in the selected currency",
		"name": "Market Value"
	},
	"name": {
		"description": "The full card name",
		"name": "Name"
	},
	"price": {
		"description": "Price in the selected currency",
		"name": "Price"
	},
	"printNumber": {
		"description": "Official print number for this printing",
		"name": "Print Number"
	},
	"cardmarketId": {
		"description": "The Cardmarket product ID for this printing and finish",
		"name": "Cardmarket ID"
	},
	"tcgplayerId": {
		"description": "The TCGplayer product ID for this printing and finish",
		"name": "TCGplayer ID"
	},
	"productId": {
		"description": "The CardNexus product ID",
		"name": "CardNexus ID"
	},
	"rarity": { "name": "Rarity" },
	"value": { "name": "Value" },
	"variant": {
		"description": "Variant of the card (first edition, unlimited, etc.)",
		"name": "Variant"
	},
	"productType": {
		"description": "Type of the product (single or sealed)",
		"name": "Product Type",
		"values": {
			"card": "Single cards",
			"sealed": "Sealed products"
		}
	}
} };

//#endregion
//#region src/lib/attributes/i18n/fr.json
var fr_default$25 = { attributes: {
	"change24h": {
		"description": "Variation de prix au cours des dernières 24 heures",
		"name": "Variation 24h",
		"toggle": "Quotidien"
	},
	"change7d": {
		"description": "Variation de prix au cours des 7 derniers jours",
		"name": "Variation 7j",
		"toggle": "Hebdomadaire"
	},
	"change30d": {
		"description": "Variation de prix au cours des 30 derniers jours",
		"name": "Variation 30j",
		"toggle": "Mensuel"
	},
	"expansion": {
		"description": "Extension",
		"name": "Extension"
	},
	"finish": {
		"description": "Finition de la carte (foil/non-foil)",
		"name": "Finition",
		"values": {
			"coldfoil": "Cold Foil",
			"etched": "Etched",
			"foil": "Foil",
			"gold": "Gold",
			"goldfoil": "Gold Foil",
			"holofoil": "Holofoil",
			"rainbowfoil": "Rainbow Foil",
			"rainbow": "Rainbow",
			"reverse": "Reverse Holo",
			"signed": "Signed",
			"standard": "Standard",
			"blastFoilRainbow": "Blast Foil Rainbow",
			"bubblesFoil": "Bubbles Foil",
			"crackedIceFoil": "Cracked Ice Foil",
			"galaxyFoil": "Galaxy Foil",
			"glitterFoil": "Glitter Foil",
			"goldFoil": "Gold Foil",
			"multiSidelineFoil": "Multi Sideline Foil",
			"rainbowSolidTextureStamp": "Rainbow Solid Texture Stamp",
			"starFoil": "Star Foil",
			"surgeFoil": "Surge Foil",
			"fullArtGoldSign": "Full Art Gold Sign",
			"depthLenticular": "Depth Lenticular",
			"flipLenticular": "Flip Lenticular",
			"lenticular": "Lenticular",
			"zarimoth": "Zarimoth",
			"serialized": "Sérialisé",
			"serializedRoseGold": "Sérialisé Rose Gold",
			"serializedGold": "Sérialisé Gold",
			"holographic": "Holographique",
			"embossed": "Gaufré"
		},
		"short": {
			"coldfoil": "CF",
			"etched": "ET",
			"foil": "F",
			"gold": "G",
			"goldfoil": "GF",
			"holofoil": "HF",
			"rainbowfoil": "RF",
			"rainbow": "RB",
			"reverse": "RH",
			"signed": "SIG",
			"standard": "NF",
			"blastFoilRainbow": "BFR",
			"bubblesFoil": "BF",
			"crackedIceFoil": "CIF",
			"galaxyFoil": "GXF",
			"glitterFoil": "GLF",
			"goldFoil": "GF",
			"multiSidelineFoil": "MSF",
			"rainbowSolidTextureStamp": "RSTS",
			"starFoil": "STF",
			"surgeFoil": "SUF",
			"fullArtGoldSign": "FAGS",
			"depthLenticular": "DL",
			"flipLenticular": "FL",
			"lenticular": "LEN",
			"zarimoth": "ZAR",
			"serialized": "SER",
			"serializedRoseGold": "SRG",
			"serializedGold": "SG",
			"holographic": "HOLO",
			"embossed": "GAU"
		}
	},
	"game": {
		"description": "Jeu",
		"infos": "Choisissez un jeu pour voir plus de filtres",
		"name": "Jeu"
	},
	"image": {
		"description": "Image du produit",
		"name": "Image"
	},
	"languages": {
		"description": "Langues disponibles pour ce produit",
		"name": "Langues"
	},
	"listingPrice": {
		"description": "Filtrer par prix d'annonce",
		"name": "Prix"
	},
	"marketValue": {
		"description": "La valeur marché dans la devise sélectionnée",
		"name": "Valeur Marché"
	},
	"name": {
		"description": "Le nom complet de la carte",
		"name": "Nom"
	},
	"price": {
		"description": "Prix dans la devise sélectionnée",
		"name": "Prix"
	},
	"printNumber": {
		"description": "Numéro de collection officiel pour cette impression",
		"name": "Numéro de collection"
	},
	"cardmarketId": {
		"description": "L'identifiant produit Cardmarket pour cette impression et cette finition",
		"name": "ID Cardmarket"
	},
	"tcgplayerId": {
		"description": "L'identifiant produit TCGplayer pour cette impression et cette finition",
		"name": "ID TCGplayer"
	},
	"productId": {
		"description": "L'identifiant produit CardNexus",
		"name": "ID CardNexus"
	},
	"rarity": { "name": "Rareté" },
	"value": { "name": "Valeur" },
	"variant": {
		"description": "Variante de la carte (first edition, unlimited, etc.)",
		"name": "Variante"
	},
	"productType": {
		"description": "Type du produit (carte à l'unité, booster etc etc..)",
		"name": "Type de produit",
		"values": {
			"card": "Carte à l'unité",
			"sealed": "Produit scellé"
		}
	}
} };

//#endregion
//#region src/lib/attributes/i18n/common-translations.ts
const commonAttributeTranslationsMap = {
	[Locale.English]: en_default$25,
	[Locale.French]: fr_default$25
};

//#endregion
//#region src/lib/catalogue-quality.ts
const DEFAULT = {
	[SupportedMarketplace.Tcgplayer]: {
		applicable: true,
		sourceOfTruth: false
	},
	[SupportedMarketplace.Cardmarket]: {
		applicable: true,
		sourceOfTruth: false
	}
};
const NONE = {
	[SupportedMarketplace.Tcgplayer]: {
		applicable: false,
		sourceOfTruth: false
	},
	[SupportedMarketplace.Cardmarket]: {
		applicable: false,
		sourceOfTruth: false
	}
};
const CATALOGUE_QUALITY = {
	[SupportedGames.OnePiece]: {
		[SupportedMarketplace.Tcgplayer]: {
			applicable: true,
			sourceOfTruth: true
		},
		[SupportedMarketplace.Cardmarket]: {
			applicable: true,
			sourceOfTruth: false
		}
	},
	[SupportedGames.Pokemon]: {
		[SupportedMarketplace.Tcgplayer]: {
			applicable: true,
			sourceOfTruth: true
		},
		[SupportedMarketplace.Cardmarket]: {
			applicable: true,
			sourceOfTruth: false
		}
	},
	[SupportedGames.PokemonJapan]: {
		[SupportedMarketplace.Tcgplayer]: {
			applicable: true,
			sourceOfTruth: true
		},
		[SupportedMarketplace.Cardmarket]: {
			applicable: true,
			sourceOfTruth: false
		}
	},
	[SupportedGames.StarWarsUnlimited]: DEFAULT,
	[SupportedGames.MTG]: DEFAULT,
	[SupportedGames.FAB]: DEFAULT,
	[SupportedGames.Lorcana]: DEFAULT,
	[SupportedGames.GrandArchive]: DEFAULT,
	[SupportedGames.YuGiOh]: DEFAULT,
	[SupportedGames.YuGiOhOcg]: {
		[SupportedMarketplace.Tcgplayer]: {
			applicable: false,
			sourceOfTruth: false
		},
		[SupportedMarketplace.Cardmarket]: {
			applicable: true,
			sourceOfTruth: true
		}
	},
	[SupportedGames.Riftbound]: DEFAULT,
	[SupportedGames.Sorcery]: DEFAULT,
	[SupportedGames.NarutoMythos]: NONE,
	[SupportedGames.Cyberpunk]: NONE,
	[SupportedGames.Drakerion]: NONE,
	[SupportedGames.Rise]: NONE,
	[SupportedGames.Wankul]: NONE,
	[SupportedGames.Eoa]: NONE,
	[SupportedGames.ChronoCore]: NONE,
	[SupportedGames.Azuki]: NONE,
	[SupportedGames.Dreamland]: NONE,
	[SupportedGames.DbsFusion]: {
		[SupportedMarketplace.Tcgplayer]: {
			applicable: true,
			sourceOfTruth: true
		},
		[SupportedMarketplace.Cardmarket]: {
			applicable: true,
			sourceOfTruth: false
		}
	},
	[SupportedGames.DbsMasters]: {
		[SupportedMarketplace.Tcgplayer]: {
			applicable: true,
			sourceOfTruth: true
		},
		[SupportedMarketplace.Cardmarket]: {
			applicable: true,
			sourceOfTruth: false
		}
	},
	[SupportedGames.Palworld]: {
		[SupportedMarketplace.Tcgplayer]: {
			applicable: true,
			sourceOfTruth: false
		},
		[SupportedMarketplace.Cardmarket]: {
			applicable: false,
			sourceOfTruth: false
		}
	},
	[SupportedGames.Gundam]: {
		[SupportedMarketplace.Tcgplayer]: {
			applicable: true,
			sourceOfTruth: true
		},
		[SupportedMarketplace.Cardmarket]: {
			applicable: false,
			sourceOfTruth: false
		}
	},
	[SupportedGames.ExampleGame]: DEFAULT
};
function getCatalogueQuality(slug) {
	return CATALOGUE_QUALITY[slug] ?? DEFAULT;
}

//#endregion
//#region src/lib/default-price-source.ts
/** Countries priced from TCGplayer; the other countries we serve use Cardmarket. */
const TCGPLAYER_COUNTRIES = new Set(["US", "CA"]);
/** The same list `getShippingDestinationCookie` validates against. */
const SERVED_COUNTRIES = new Set(ALL_BUYER_DELIVERY_COUNTRIES);
/**
* The price source a visitor from `countryCode` should see before they pick one:
* TCGplayer in the US and Canada, Cardmarket in the other countries we deliver
* to. A missing country, or one we do not deliver to, falls back to TCGplayer —
* the platform-wide default, and what the shipping destination falls back to.
*
* @param countryCode - ISO 3166-1 alpha-2 code, in any case.
*/
function defaultPriceSourceForCountry(countryCode) {
	if (!countryCode) return SupportedMarketplace.Tcgplayer;
	const code = countryCode.toUpperCase();
	if (TCGPLAYER_COUNTRIES.has(code)) return SupportedMarketplace.Tcgplayer;
	return SERVED_COUNTRIES.has(code) ? SupportedMarketplace.Cardmarket : SupportedMarketplace.Tcgplayer;
}

//#endregion
//#region src/lib/catalogue-review-trust.ts
/**
* Id namespaces whose values the importer attaches by fuzzy matching (name or
* number similarity against a marketplace catalogue) rather than reading from
* the game's source of truth. In the catalogue review model a hint id can
* suggest a pairing candidate but never proves identity — only namespaces
* absent from this list feed the renamed/merged auto-pairing tiers.
*
* Keyed by game slug. Namespaces: "tcg" (TCGplayer), "cm" (Cardmarket),
* "scry" (Scryfall).
*/
const HINT_ID_NAMESPACES = {
	sorcery: ["tcg", "cm"],
	fab: ["tcg"],
	"grand-archive": ["tcg"]
};
function hintIdNamespaces(gameSlug) {
	return HINT_ID_NAMESPACES[gameSlug] ?? [];
}
/**
* Vendor-id attribute keys whose values identify a card rather than one
* printing of it — every printing of the card carries the same value (FAB's
* `fabId`, Yu-Gi-Oh!'s `konamiId`, MTG's `scryfallOracleId`). Such an id
* cannot show that a product moved between expansions, so it is excluded from
* product-level identity.
*
* Keyed by game slug. Values are attribute keys of the game's `vendorIds`
* (or its additional external reference keys).
*/
const CARD_LEVEL_ID_NAMESPACES = {
	fab: ["fabId"],
	ygo: ["konamiId"],
	"ygo-ocg": ["konamiId"],
	mtg: ["scryfallOracleId"]
};
/** The card-level vendor-id keys of a game (empty when it declares none). */
function cardLevelIdNamespaces(gameSlug) {
	return CARD_LEVEL_ID_NAMESPACES[gameSlug] ?? [];
}

//#endregion
//#region src/lib/games/azuki/azuki.types.ts
let AzukiRarity = /* @__PURE__ */ function(AzukiRarity) {
	AzukiRarity["COMMON"] = "Common";
	AzukiRarity["UNCOMMON"] = "Uncommon";
	AzukiRarity["RARE"] = "Rare";
	AzukiRarity["SUPER_RARE"] = "Super Rare";
	AzukiRarity["SUPER_RARE_ONE_STAR"] = "Super Rare ★";
	AzukiRarity["SUPER_RARE_TWO_STAR"] = "Super Rare ★★";
	AzukiRarity["LEADER"] = "Leader";
	AzukiRarity["LEADER_ONE_STAR"] = "Leader ★";
	AzukiRarity["LEADER_TWO_STAR"] = "Leader ★★";
	AzukiRarity["GATE"] = "Gate";
	AzukiRarity["GATE_ONE_STAR"] = "Gate ★";
	AzukiRarity["IKZ"] = "IKZ";
	AzukiRarity["IKZ_ONE_STAR"] = "IKZ ★";
	return AzukiRarity;
}({});
let AzukiFinish = /* @__PURE__ */ function(AzukiFinish) {
	AzukiFinish["STANDARD"] = "Standard";
	return AzukiFinish;
}({});
let AzukiElement = /* @__PURE__ */ function(AzukiElement) {
	AzukiElement["NEUTRAL"] = "Neutral";
	AzukiElement["WATER"] = "Water";
	AzukiElement["LIGHTNING"] = "Lightning";
	AzukiElement["EARTH"] = "Earth";
	AzukiElement["FIRE"] = "Fire";
	return AzukiElement;
}({});
let AzukiCategory = /* @__PURE__ */ function(AzukiCategory) {
	AzukiCategory["ENTITY"] = "Entity";
	AzukiCategory["SPELL"] = "Spell";
	AzukiCategory["WEAPON"] = "Weapon";
	AzukiCategory["LEADER"] = "Leader";
	AzukiCategory["GATE"] = "Gate";
	AzukiCategory["IKZ"] = "IKZ";
	return AzukiCategory;
}({});

//#endregion
//#region src/lib/games/azuki/azuki.presets.ts
const azukiListPresets = {
	Standard: {
		name: translatable("azuki.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("azuki.presets.standard.description", "A complete set with one copy of each card"),
		matchers: [{
			filters: { finish: [AzukiFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("azuki.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("azuki.presets.playset.description", "A complete set with the maximum four copies of each card allowed in a deck"),
		matchers: [{
			filters: { finish: [AzukiFinish.STANDARD] },
			quantity: 4
		}]
	}
};

//#endregion
//#region src/lib/games/azuki/azuki.game.ts
const azukiFinishValues = { [AzukiFinish.STANDARD]: { label: translatable("global.attributes.finish.values.standard", "Standard") } };
const azukiFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(AzukiRarity).optional(),
	finish: makeZodEnumFilter(AzukiFinish).optional(),
	finishes: makeZodEnumFilter(AzukiFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	element: makeZodEnumFilter(AzukiElement).optional(),
	category: makeZodEnumFilter(AzukiCategory).optional(),
	ikzCost: zodNumericFilter.optional(),
	attack: zodNumericFilter.optional(),
	health: zodNumericFilter.optional(),
	gatePower: zodNumericFilter.optional()
});
const AzukiGameConfiguration = {
	name: "Azuki TCG",
	shortName: "Azuki",
	slug: "azuki",
	icon: {
		dark: "azuki-icon.png",
		light: "azuki-icon.png"
	},
	image: {
		dark: "azuki.png",
		light: "azuki.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(AzukiFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: azukiFinishValues
		}),
		finish: enumAttribute(AzukiFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: azukiFinishValues
		}),
		rarity: enumAttribute(AzukiRarity, {
			entity: "variant",
			name: translatable("azuki.attributes.rarity.name", "Rarity"),
			description: translatable("azuki.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[AzukiRarity.COMMON]: { label: translatable("azuki.attributes.rarity.values.common", "Common") },
				[AzukiRarity.UNCOMMON]: { label: translatable("azuki.attributes.rarity.values.uncommon", "Uncommon") },
				[AzukiRarity.RARE]: { label: translatable("azuki.attributes.rarity.values.rare", "Rare") },
				[AzukiRarity.SUPER_RARE]: { label: translatable("azuki.attributes.rarity.values.super_rare", "Super Rare") },
				[AzukiRarity.SUPER_RARE_ONE_STAR]: { label: translatable("azuki.attributes.rarity.values.super_rare_1", "Super Rare ★") },
				[AzukiRarity.SUPER_RARE_TWO_STAR]: { label: translatable("azuki.attributes.rarity.values.super_rare_2", "Super Rare ★★") },
				[AzukiRarity.LEADER]: { label: translatable("azuki.attributes.rarity.values.leader", "Leader") },
				[AzukiRarity.LEADER_ONE_STAR]: { label: translatable("azuki.attributes.rarity.values.leader_1", "Leader ★") },
				[AzukiRarity.LEADER_TWO_STAR]: { label: translatable("azuki.attributes.rarity.values.leader_2", "Leader ★★") },
				[AzukiRarity.GATE]: { label: translatable("azuki.attributes.rarity.values.gate", "Gate") },
				[AzukiRarity.GATE_ONE_STAR]: { label: translatable("azuki.attributes.rarity.values.gate_1", "Gate ★") },
				[AzukiRarity.IKZ]: { label: translatable("azuki.attributes.rarity.values.ikz", "IKZ") },
				[AzukiRarity.IKZ_ONE_STAR]: { label: translatable("azuki.attributes.rarity.values.ikz_1", "IKZ ★") }
			}
		}),
		printNumber: printNumberAttribute,
		element: enumAttribute(AzukiElement, {
			entity: "card",
			name: translatable("azuki.attributes.element.name", "Element"),
			description: translatable("azuki.attributes.element.description", "The element of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[AzukiElement.NEUTRAL]: {
					label: translatable("azuki.attributes.element.values.neutral", "Neutral"),
					image: "/images/azuki/elements/neutral.png"
				},
				[AzukiElement.WATER]: {
					label: translatable("azuki.attributes.element.values.water", "Water"),
					image: "/images/azuki/elements/water.png"
				},
				[AzukiElement.LIGHTNING]: {
					label: translatable("azuki.attributes.element.values.lightning", "Lightning"),
					image: "/images/azuki/elements/lightning.png"
				},
				[AzukiElement.EARTH]: {
					label: translatable("azuki.attributes.element.values.earth", "Earth"),
					image: "/images/azuki/elements/earth.png"
				},
				[AzukiElement.FIRE]: {
					label: translatable("azuki.attributes.element.values.fire", "Fire"),
					image: "/images/azuki/elements/fire.png"
				}
			}
		}),
		category: enumAttribute(AzukiCategory, {
			entity: "card",
			name: translatable("azuki.attributes.category.name", "Card Type"),
			description: translatable("azuki.attributes.category.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[AzukiCategory.ENTITY]: { label: translatable("azuki.attributes.category.values.entity", "Entity") },
				[AzukiCategory.SPELL]: { label: translatable("azuki.attributes.category.values.spell", "Spell") },
				[AzukiCategory.WEAPON]: { label: translatable("azuki.attributes.category.values.weapon", "Weapon") },
				[AzukiCategory.LEADER]: { label: translatable("azuki.attributes.category.values.leader", "Leader") },
				[AzukiCategory.GATE]: { label: translatable("azuki.attributes.category.values.gate", "Gate") },
				[AzukiCategory.IKZ]: { label: translatable("azuki.attributes.category.values.ikz", "IKZ") }
			}
		}),
		subtypes: {
			entity: "card",
			name: translatable("azuki.attributes.subtypes.name", "Subtypes"),
			description: translatable("azuki.attributes.subtypes.description", "The subtypes of the card"),
			type: "string",
			array: true
		},
		abilities: {
			entity: "card",
			name: translatable("azuki.attributes.abilities.name", "Abilities"),
			description: translatable("azuki.attributes.abilities.description", "The abilities of the card"),
			type: "string",
			array: true
		},
		cardText: {
			entity: "card",
			name: translatable("azuki.attributes.cardText.name", "Card Text"),
			description: translatable("azuki.attributes.cardText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		},
		errata: {
			entity: "variant",
			name: translatable("azuki.attributes.errata.name", "Errata"),
			description: translatable("azuki.attributes.errata.description", "Corrected wording published after the card was printed"),
			type: "string",
			array: false
		},
		ikzCost: {
			entity: "card",
			name: translatable("azuki.attributes.ikzCost.name", "IKZ Cost"),
			description: translatable("azuki.attributes.ikzCost.description", "IKZ cost to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		attack: {
			entity: "card",
			name: translatable("azuki.attributes.attack.name", "Attack"),
			description: translatable("azuki.attributes.attack.description", "Attack value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		health: {
			entity: "card",
			name: translatable("azuki.attributes.health.name", "Health"),
			description: translatable("azuki.attributes.health.description", "Health value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		gatePower: {
			entity: "card",
			name: translatable("azuki.attributes.gatePower.name", "Gate Power"),
			description: translatable("azuki.attributes.gatePower.description", "Gate power value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"element",
			"category"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: ["finishes", "languages"]
	}, {
		label: "Card Stats",
		filters: [
			"ikzCost",
			"attack",
			"health",
			"gatePower"
		]
	}],
	productDetailsOrder: {
		details: [
			"cardText",
			"errata",
			"abilities",
			"subtypes"
		],
		statistics: [
			"ikzCost",
			"attack",
			"health",
			"gatePower"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: azukiListPresets,
	basicAttributes: [
		"rarity",
		"element",
		"category"
	],
	finishHierarchy: [CardFinish.STANDARD],
	supportedMarketplaces: [],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "AZK01-009",
		cardName: "The Red Bean",
		expansion: "Gates Awakened",
		expansionCode: "AZK01",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/chrono-core/chrono-core.types.ts
let ChronoCoreRarity = /* @__PURE__ */ function(ChronoCoreRarity) {
	ChronoCoreRarity["COMMON"] = "Common";
	ChronoCoreRarity["UNCOMMON"] = "Uncommon";
	ChronoCoreRarity["RARE"] = "Rare";
	ChronoCoreRarity["SUPER_RARE"] = "Super Rare";
	ChronoCoreRarity["SECRET"] = "Secret";
	ChronoCoreRarity["PROMO"] = "Promo";
	return ChronoCoreRarity;
}({});
let ChronoCoreVariant = /* @__PURE__ */ function(ChronoCoreVariant) {
	ChronoCoreVariant["NORMAL"] = "Normal";
	ChronoCoreVariant["IMMERSIVE_ART"] = "Immersive Art";
	ChronoCoreVariant["SERIALIZED"] = "Serialized";
	ChronoCoreVariant["ARTIST_SIGNATURE"] = "Artist Signature";
	ChronoCoreVariant["CIRCUITRY"] = "Circuitry";
	ChronoCoreVariant["EARLY_ADOPTER"] = "Early Adopter";
	ChronoCoreVariant["PARTICIPANT"] = "Participant";
	ChronoCoreVariant["RILYNN_ART"] = "Rilynn Art";
	ChronoCoreVariant["TOP_4"] = "Top 4";
	ChronoCoreVariant["TOP_8"] = "Top 8";
	ChronoCoreVariant["WINNER"] = "Winner";
	return ChronoCoreVariant;
}({});
let ChronoCoreFinish = /* @__PURE__ */ function(ChronoCoreFinish) {
	ChronoCoreFinish["STANDARD"] = "Standard";
	ChronoCoreFinish["HOLOGRAPHIC"] = "Holographic";
	return ChronoCoreFinish;
}({});
let ChronoCoreCardType = /* @__PURE__ */ function(ChronoCoreCardType) {
	ChronoCoreCardType["CONFIGURATION"] = "Configuration";
	ChronoCoreCardType["CORE"] = "Core";
	ChronoCoreCardType["EQUIPMENT"] = "Equipment";
	ChronoCoreCardType["GEM"] = "Gem";
	ChronoCoreCardType["PILOT"] = "Pilot";
	ChronoCoreCardType["SUPPORT"] = "Support";
	ChronoCoreCardType["WEAPON"] = "Weapon";
	return ChronoCoreCardType;
}({});
let ChronoCoreSubtype = /* @__PURE__ */ function(ChronoCoreSubtype) {
	ChronoCoreSubtype["AUXILIARY"] = "Auxiliary";
	ChronoCoreSubtype["GAUNTLETS"] = "Gauntlets";
	ChronoCoreSubtype["GREAVES"] = "Greaves";
	ChronoCoreSubtype["ONE_HANDED_MELEE"] = "One-Handed Melee";
	ChronoCoreSubtype["ONE_HANDED_RANGED"] = "One-Handed Ranged";
	ChronoCoreSubtype["ONE_HANDED_SHIELD"] = "One-Handed Shield";
	ChronoCoreSubtype["TWO_HANDED_MELEE"] = "Two-Handed Melee";
	ChronoCoreSubtype["TWO_HANDED_RANGED"] = "Two-Handed Ranged";
	ChronoCoreSubtype["VISOR"] = "Visor";
	return ChronoCoreSubtype;
}({});
let ChronoCoreChassisType = /* @__PURE__ */ function(ChronoCoreChassisType) {
	ChronoCoreChassisType["LIGHTWEIGHT"] = "Lightweight";
	ChronoCoreChassisType["MIDWEIGHT"] = "Midweight";
	ChronoCoreChassisType["HEAVYWEIGHT"] = "Heavyweight";
	return ChronoCoreChassisType;
}({});
let ChronoCoreTrait = /* @__PURE__ */ function(ChronoCoreTrait) {
	ChronoCoreTrait["GADGET"] = "Gadget";
	ChronoCoreTrait["WILDCARD"] = "Wildcard";
	return ChronoCoreTrait;
}({});
let ChronoCoreDamageType = /* @__PURE__ */ function(ChronoCoreDamageType) {
	ChronoCoreDamageType["BALLISTIC"] = "Ballistic";
	ChronoCoreDamageType["BEAM"] = "Beam";
	ChronoCoreDamageType["PLASMA"] = "Plasma";
	ChronoCoreDamageType["STRIKE"] = "Strike";
	return ChronoCoreDamageType;
}({});
let ChronoCoreRealm = /* @__PURE__ */ function(ChronoCoreRealm) {
	ChronoCoreRealm["ARTIFICE"] = "Artifice";
	ChronoCoreRealm["CASTRA"] = "Castra";
	ChronoCoreRealm["NEKRIS"] = "Nekris";
	ChronoCoreRealm["UMBRA"] = "Umbra";
	return ChronoCoreRealm;
}({});
let ChronoCoreEquipRequirement = /* @__PURE__ */ function(ChronoCoreEquipRequirement) {
	ChronoCoreEquipRequirement["ALL_SLOTS"] = "All Slots";
	ChronoCoreEquipRequirement["AUXILIARY"] = "Auxiliary";
	ChronoCoreEquipRequirement["EQUIPMENT"] = "Equipment";
	ChronoCoreEquipRequirement["GAUNTLETS"] = "Gauntlets";
	ChronoCoreEquipRequirement["GREAVES"] = "Greaves";
	ChronoCoreEquipRequirement["ONE_HANDED_MELEE"] = "One-Handed Melee";
	ChronoCoreEquipRequirement["ONE_HANDED_RANGED"] = "One-Handed Ranged";
	ChronoCoreEquipRequirement["ONE_HANDED_SHIELD"] = "One-Handed Shield";
	ChronoCoreEquipRequirement["TWO_HANDED_MELEE"] = "Two-Handed Melee";
	ChronoCoreEquipRequirement["TWO_HANDED_RANGED"] = "Two-Handed Ranged";
	ChronoCoreEquipRequirement["VISOR"] = "Visor";
	ChronoCoreEquipRequirement["WEAPONS"] = "Weapons";
	return ChronoCoreEquipRequirement;
}({});
let ChronoCoreMkValue = /* @__PURE__ */ function(ChronoCoreMkValue) {
	ChronoCoreMkValue["MKI"] = "MKI";
	ChronoCoreMkValue["MKII"] = "MKII";
	ChronoCoreMkValue["MKIII"] = "MKIII";
	ChronoCoreMkValue["MKIV"] = "MKIV";
	return ChronoCoreMkValue;
}({});
let ChronoCoreOrientation = /* @__PURE__ */ function(ChronoCoreOrientation) {
	ChronoCoreOrientation["PORTRAIT"] = "portrait";
	ChronoCoreOrientation["LANDSCAPE"] = "landscape";
	return ChronoCoreOrientation;
}({});
let ChronoCoreTimingRestriction = /* @__PURE__ */ function(ChronoCoreTimingRestriction) {
	ChronoCoreTimingRestriction["ACTIVE_MAIN"] = "Active Main";
	ChronoCoreTimingRestriction["BEGINNING_OF_TURN"] = "Beginning of Turn";
	ChronoCoreTimingRestriction["COUNTER_ATTACK"] = "Counter Attack";
	ChronoCoreTimingRestriction["COUNTER_PLAY"] = "Counter Play";
	ChronoCoreTimingRestriction["ON_ATTACK"] = "On Attack";
	ChronoCoreTimingRestriction["ON_OVERCLOCK"] = "On Overclock";
	ChronoCoreTimingRestriction["ON_PLAY"] = "On Play";
	ChronoCoreTimingRestriction["ON_REVEAL"] = "On Reveal";
	ChronoCoreTimingRestriction["ON_SCRAP"] = "On Scrap";
	ChronoCoreTimingRestriction["OVERCLOCK"] = "Overclock";
	return ChronoCoreTimingRestriction;
}({});
let ChronoCoreKeyword = /* @__PURE__ */ function(ChronoCoreKeyword) {
	ChronoCoreKeyword["CLOAKING"] = "Cloaking";
	ChronoCoreKeyword["DATA_STORM"] = "Data Storm";
	ChronoCoreKeyword["DATAMINE"] = "Datamine";
	ChronoCoreKeyword["DECODING"] = "Decoding";
	ChronoCoreKeyword["FEEDBACK_LOOP"] = "Feedback Loop";
	ChronoCoreKeyword["FIREWALL"] = "Firewall";
	ChronoCoreKeyword["LOCKOUT"] = "Lockout";
	ChronoCoreKeyword["OPTIMIZATION_PROTOCOL"] = "Optimization Protocol";
	ChronoCoreKeyword["OVERSHIELD"] = "Overshield";
	ChronoCoreKeyword["POWER_BURST"] = "Power Burst";
	ChronoCoreKeyword["QUANTUM_BLOW"] = "Quantum Blow";
	ChronoCoreKeyword["RECONFIGURE"] = "Reconfigure";
	ChronoCoreKeyword["REGENERATE"] = "Regenerate";
	ChronoCoreKeyword["REINFORCE"] = "Reinforce";
	ChronoCoreKeyword["RENEW"] = "Renew";
	ChronoCoreKeyword["ROOTKIT"] = "Rootkit";
	ChronoCoreKeyword["SCRAPHEAP"] = "Scrapheap";
	ChronoCoreKeyword["SHOCKWAVE"] = "Shockwave";
	ChronoCoreKeyword["SHORT_CIRCUIT"] = "Short Circuit";
	ChronoCoreKeyword["SIPHON"] = "Siphon";
	ChronoCoreKeyword["SYSTEM_OVERRIDE"] = "System Override";
	ChronoCoreKeyword["SYSTEM_REBOOT"] = "System Reboot";
	ChronoCoreKeyword["VIRUS"] = "Virus";
	ChronoCoreKeyword["WORM"] = "Worm";
	return ChronoCoreKeyword;
}({});
let ChronoCoreOrigin = /* @__PURE__ */ function(ChronoCoreOrigin) {
	ChronoCoreOrigin["BOXTOPPER"] = "Boxtopper";
	ChronoCoreOrigin["CHRONO_CORE_CREW"] = "Chrono Core Crew";
	ChronoCoreOrigin["GEN_CON"] = "Gen Con";
	ChronoCoreOrigin["HOUSTON_PREMIER_EVENT"] = "Houston Premier Event";
	ChronoCoreOrigin["KICKSTARTER_EXCLUSIVE"] = "Kickstarter Exclusive";
	ChronoCoreOrigin["RELEASE_EVENT_KIT"] = "Release Event Kit";
	return ChronoCoreOrigin;
}({});

//#endregion
//#region src/lib/games/chrono-core/chrono-core.presets.ts
/**
* Deck-limit matchers per Comprehensive Rules V.2.3 §4.1:
* - deck cards (equipment/weapon/support/configuration) max 3 copies (4.1.3b),
*   except up to 1 MKIV equipment and 1 MKIV weapon, single copy each (4.1.3e)
* - MKI cards are loadout-only (4.1.3d): 1 of each equipment slot piece, up to
*   two one-handed weapons of which only one may be a shield, or one two-handed
* - pilot and core are loadout singles (4.1.2); gems are neither deck-legal
*   nor loadout cards, so they are excluded from playsets entirely
*/
const deckLimitMatchers = (finishes) => [
	{
		filters: {
			finish: finishes,
			variant: [ChronoCoreVariant.NORMAL],
			cardType: [ChronoCoreCardType.EQUIPMENT, ChronoCoreCardType.WEAPON],
			mkValue: [ChronoCoreMkValue.MKII, ChronoCoreMkValue.MKIII]
		},
		quantity: 3
	},
	{
		filters: {
			finish: finishes,
			variant: [ChronoCoreVariant.NORMAL],
			cardType: [ChronoCoreCardType.SUPPORT, ChronoCoreCardType.CONFIGURATION]
		},
		quantity: 3
	},
	{
		filters: {
			finish: finishes,
			variant: [ChronoCoreVariant.NORMAL],
			cardType: [ChronoCoreCardType.EQUIPMENT, ChronoCoreCardType.WEAPON],
			mkValue: [ChronoCoreMkValue.MKIV]
		},
		quantity: 1
	},
	{
		filters: {
			finish: finishes,
			variant: [ChronoCoreVariant.NORMAL],
			cardType: [ChronoCoreCardType.EQUIPMENT],
			mkValue: [ChronoCoreMkValue.MKI]
		},
		quantity: 1
	},
	{
		filters: {
			finish: finishes,
			variant: [ChronoCoreVariant.NORMAL],
			cardType: [ChronoCoreCardType.WEAPON],
			mkValue: [ChronoCoreMkValue.MKI],
			subtype: [ChronoCoreSubtype.ONE_HANDED_MELEE, ChronoCoreSubtype.ONE_HANDED_RANGED]
		},
		quantity: 2
	},
	{
		filters: {
			finish: finishes,
			variant: [ChronoCoreVariant.NORMAL],
			cardType: [ChronoCoreCardType.WEAPON],
			mkValue: [ChronoCoreMkValue.MKI],
			subtype: [
				ChronoCoreSubtype.ONE_HANDED_SHIELD,
				ChronoCoreSubtype.TWO_HANDED_MELEE,
				ChronoCoreSubtype.TWO_HANDED_RANGED
			]
		},
		quantity: 1
	},
	{
		filters: {
			finish: finishes,
			variant: [ChronoCoreVariant.NORMAL],
			cardType: [ChronoCoreCardType.PILOT, ChronoCoreCardType.CORE]
		},
		quantity: 1
	}
];
const chronoCoreListPresets = {
	Standard: {
		name: translatable("chrono-core.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("chrono-core.presets.standard.description", "A complete set with one copy of each card - standard finish where available, holographic otherwise"),
		matchers: [(
		/** Ordered finish list: the matcher resolves to the first finish the product has */
		{
			filters: {
				finish: [ChronoCoreFinish.STANDARD, ChronoCoreFinish.HOLOGRAPHIC],
				variant: [ChronoCoreVariant.NORMAL]
			},
			quantity: 1
		})]
	},
	Playset: {
		name: translatable("chrono-core.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("chrono-core.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck or loadout - standard finish where available, holographic otherwise"),
		matchers: deckLimitMatchers([ChronoCoreFinish.STANDARD, ChronoCoreFinish.HOLOGRAPHIC])
	},
	Foil: {
		name: translatable("chrono-core.presets.foil.name", "Holographic Set"),
		type: ListPresetType.FOIL,
		description: translatable("chrono-core.presets.foil.description", "A complete set with one copy of each card in holographic finish"),
		matchers: [{
			filters: {
				finish: [ChronoCoreFinish.HOLOGRAPHIC],
				variant: [ChronoCoreVariant.NORMAL]
			},
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("chrono-core.presets.foilPlayset.name", "Holographic Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("chrono-core.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck or loadout in holographic finish"),
		matchers: deckLimitMatchers([ChronoCoreFinish.HOLOGRAPHIC])
	},
	"Master Set": {
		name: translatable("chrono-core.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("chrono-core.presets.masterset.description", "A complete set with one copy of each card in all finishes"),
		matchers: [{
			filters: { finish: [ChronoCoreFinish.STANDARD] },
			quantity: 1
		}, {
			filters: { finish: [ChronoCoreFinish.HOLOGRAPHIC] },
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/chrono-core/chrono-core.game.ts
/**
* Shared finish values for Chrono Core - used by both the `finishes`
* (product) and `finish` (inventory) attributes.
*/
const chronoCoreFinishValues = {
	[ChronoCoreFinish.STANDARD]: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	[ChronoCoreFinish.HOLOGRAPHIC]: { label: translatable("global.attributes.finish.values.holographic", "Holographic") }
};
const chronoCoreFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(ChronoCoreRarity).optional(),
	variant: makeZodEnumFilter(ChronoCoreVariant).optional(),
	finish: makeZodEnumFilter(ChronoCoreFinish).optional(),
	finishes: makeZodEnumFilter(ChronoCoreFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	cardType: makeZodEnumFilter(ChronoCoreCardType).optional(),
	subtype: makeZodEnumFilter(ChronoCoreSubtype).optional(),
	chassisType: makeZodEnumFilter(ChronoCoreChassisType).optional(),
	traits: makeZodEnumFilter(ChronoCoreTrait).optional(),
	damageType: makeZodEnumFilter(ChronoCoreDamageType).optional(),
	realm: makeZodEnumFilter(ChronoCoreRealm).optional(),
	equipRequirements: makeZodEnumFilter(ChronoCoreEquipRequirement).optional(),
	mkValue: makeZodEnumFilter(ChronoCoreMkValue).optional(),
	timingRestrictions: makeZodEnumFilter(ChronoCoreTimingRestriction).optional(),
	keywords: makeZodEnumFilter(ChronoCoreKeyword).optional(),
	origins: makeZodEnumFilter(ChronoCoreOrigin).optional(),
	maxShieldValue: zodNumericFilter.optional(),
	maxLifeValue: zodNumericFilter.optional(),
	coreCost: zodNumericFilter.optional(),
	damageValue: zodNumericFilter.optional(),
	chargeValue: zodNumericFilter.optional()
});
const ChronoCoreGameConfiguration = {
	name: "Chrono Core",
	shortName: "Chrono Core",
	slug: "chrono-core",
	icon: {
		dark: "chrono-core-icon.png",
		light: "chrono-core-icon.png"
	},
	image: {
		dark: "chrono-core.png",
		light: "chrono-core.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(ChronoCoreFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: chronoCoreFinishValues
		}),
		finish: enumAttribute(ChronoCoreFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: chronoCoreFinishValues
		}),
		rarity: enumAttribute(ChronoCoreRarity, {
			entity: "variant",
			name: translatable("chrono-core.attributes.rarity.name", "Rarity"),
			description: translatable("chrono-core.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[ChronoCoreRarity.COMMON]: {
					label: translatable("chrono-core.attributes.rarity.values.common", "Common"),
					image: "/images/chrono-core/rarities/common.png"
				},
				[ChronoCoreRarity.UNCOMMON]: {
					label: translatable("chrono-core.attributes.rarity.values.uncommon", "Uncommon"),
					image: "/images/chrono-core/rarities/uncommon.png"
				},
				[ChronoCoreRarity.RARE]: {
					label: translatable("chrono-core.attributes.rarity.values.rare", "Rare"),
					image: "/images/chrono-core/rarities/rare.png"
				},
				[ChronoCoreRarity.SUPER_RARE]: {
					label: translatable("chrono-core.attributes.rarity.values.super_rare", "Super Rare"),
					image: "/images/chrono-core/rarities/super-rare.png"
				},
				[ChronoCoreRarity.SECRET]: {
					label: translatable("chrono-core.attributes.rarity.values.secret", "Secret"),
					image: "/images/chrono-core/rarities/secret.png"
				},
				[ChronoCoreRarity.PROMO]: {
					label: translatable("chrono-core.attributes.rarity.values.promo", "Promo"),
					image: "/images/chrono-core/rarities/promo.png"
				}
			}
		}),
		variant: enumAttribute(ChronoCoreVariant, {
			entity: "variant",
			name: translatable("chrono-core.attributes.variant.name", "Variant"),
			description: translatable("chrono-core.attributes.variant.description", "The printing variant/treatment of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[ChronoCoreVariant.NORMAL]: { label: translatable("chrono-core.attributes.variant.values.normal", "Normal") },
				[ChronoCoreVariant.IMMERSIVE_ART]: { label: translatable("chrono-core.attributes.variant.values.immersive_art", "Immersive Art") },
				[ChronoCoreVariant.SERIALIZED]: { label: translatable("chrono-core.attributes.variant.values.serialized", "Serialized") },
				[ChronoCoreVariant.ARTIST_SIGNATURE]: { label: translatable("chrono-core.attributes.variant.values.artist_signature", "Artist Signature") },
				[ChronoCoreVariant.CIRCUITRY]: { label: translatable("chrono-core.attributes.variant.values.circuitry", "Circuitry") },
				[ChronoCoreVariant.EARLY_ADOPTER]: { label: translatable("chrono-core.attributes.variant.values.early_adopter", "Early Adopter") },
				[ChronoCoreVariant.PARTICIPANT]: { label: translatable("chrono-core.attributes.variant.values.participant", "Participant") },
				[ChronoCoreVariant.RILYNN_ART]: { label: translatable("chrono-core.attributes.variant.values.rilynn_art", "Rilynn Art") },
				[ChronoCoreVariant.TOP_4]: { label: translatable("chrono-core.attributes.variant.values.top_4", "Top 4") },
				[ChronoCoreVariant.TOP_8]: { label: translatable("chrono-core.attributes.variant.values.top_8", "Top 8") },
				[ChronoCoreVariant.WINNER]: { label: translatable("chrono-core.attributes.variant.values.winner", "Winner") }
			}
		}),
		printNumber: printNumberAttribute,
		cardType: enumAttribute(ChronoCoreCardType, {
			entity: "card",
			name: translatable("chrono-core.attributes.cardType.name", "Card Type"),
			description: translatable("chrono-core.attributes.cardType.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[ChronoCoreCardType.CONFIGURATION]: {
					label: translatable("chrono-core.attributes.cardType.values.configuration", "Configuration"),
					image: "/images/chrono-core/types/configuration.png"
				},
				[ChronoCoreCardType.CORE]: {
					label: translatable("chrono-core.attributes.cardType.values.core", "Core"),
					image: "/images/chrono-core/types/core.png"
				},
				[ChronoCoreCardType.EQUIPMENT]: { label: translatable("chrono-core.attributes.cardType.values.equipment", "Equipment") },
				[ChronoCoreCardType.GEM]: { label: translatable("chrono-core.attributes.cardType.values.gem", "Gem") },
				[ChronoCoreCardType.PILOT]: {
					label: translatable("chrono-core.attributes.cardType.values.pilot", "Pilot"),
					image: "/images/chrono-core/types/pilot.png"
				},
				[ChronoCoreCardType.SUPPORT]: {
					label: translatable("chrono-core.attributes.cardType.values.support", "Support"),
					image: "/images/chrono-core/types/support.png"
				},
				[ChronoCoreCardType.WEAPON]: { label: translatable("chrono-core.attributes.cardType.values.weapon", "Weapon") }
			}
		}),
		subtype: enumAttribute(ChronoCoreSubtype, {
			entity: "card",
			name: translatable("chrono-core.attributes.subtype.name", "Subtype"),
			description: translatable("chrono-core.attributes.subtype.description", "The subtype of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[ChronoCoreSubtype.AUXILIARY]: {
					label: translatable("chrono-core.attributes.subtype.values.auxiliary", "Auxiliary"),
					image: "/images/chrono-core/subtypes/auxiliary.png"
				},
				[ChronoCoreSubtype.GAUNTLETS]: {
					label: translatable("chrono-core.attributes.subtype.values.gauntlets", "Gauntlets"),
					image: "/images/chrono-core/subtypes/gauntlets.png"
				},
				[ChronoCoreSubtype.GREAVES]: {
					label: translatable("chrono-core.attributes.subtype.values.greaves", "Greaves"),
					image: "/images/chrono-core/subtypes/greaves.png"
				},
				[ChronoCoreSubtype.ONE_HANDED_MELEE]: {
					label: translatable("chrono-core.attributes.subtype.values.onehanded_melee", "One-Handed Melee"),
					image: "/images/chrono-core/subtypes/one-handed-melee.png"
				},
				[ChronoCoreSubtype.ONE_HANDED_RANGED]: {
					label: translatable("chrono-core.attributes.subtype.values.onehanded_ranged", "One-Handed Ranged"),
					image: "/images/chrono-core/subtypes/one-handed-ranged.png"
				},
				[ChronoCoreSubtype.ONE_HANDED_SHIELD]: {
					label: translatable("chrono-core.attributes.subtype.values.onehanded_shield", "One-Handed Shield"),
					image: "/images/chrono-core/subtypes/one-handed-shield.png"
				},
				[ChronoCoreSubtype.TWO_HANDED_MELEE]: {
					label: translatable("chrono-core.attributes.subtype.values.twohanded_melee", "Two-Handed Melee"),
					image: "/images/chrono-core/subtypes/two-handed-melee.png"
				},
				[ChronoCoreSubtype.TWO_HANDED_RANGED]: {
					label: translatable("chrono-core.attributes.subtype.values.twohanded_ranged", "Two-Handed Ranged"),
					image: "/images/chrono-core/subtypes/two-handed-ranged.png"
				},
				[ChronoCoreSubtype.VISOR]: {
					label: translatable("chrono-core.attributes.subtype.values.visor", "Visor"),
					image: "/images/chrono-core/subtypes/visor.png"
				}
			}
		}),
		chassisType: enumAttribute(ChronoCoreChassisType, {
			entity: "card",
			name: translatable("chrono-core.attributes.chassisType.name", "Chassis Type"),
			description: translatable("chrono-core.attributes.chassisType.description", "The chassis type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[ChronoCoreChassisType.LIGHTWEIGHT]: {
					label: translatable("chrono-core.attributes.chassisType.values.lightweight", "Lightweight"),
					image: "/images/chrono-core/chassis-types/lightweight.png"
				},
				[ChronoCoreChassisType.MIDWEIGHT]: {
					label: translatable("chrono-core.attributes.chassisType.values.midweight", "Midweight"),
					image: "/images/chrono-core/chassis-types/midweight.png"
				},
				[ChronoCoreChassisType.HEAVYWEIGHT]: {
					label: translatable("chrono-core.attributes.chassisType.values.heavyweight", "Heavyweight"),
					image: "/images/chrono-core/chassis-types/heavyweight.png"
				}
			}
		}),
		traits: enumAttribute(ChronoCoreTrait, {
			entity: "card",
			name: translatable("chrono-core.attributes.traits.name", "Traits"),
			description: translatable("chrono-core.attributes.traits.description", "The traits of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[ChronoCoreTrait.GADGET]: { label: translatable("chrono-core.attributes.traits.values.gadget", "Gadget") },
				[ChronoCoreTrait.WILDCARD]: { label: translatable("chrono-core.attributes.traits.values.wildcard", "Wildcard") }
			}
		}),
		damageType: enumAttribute(ChronoCoreDamageType, {
			entity: "card",
			name: translatable("chrono-core.attributes.damageType.name", "Damage Type"),
			description: translatable("chrono-core.attributes.damageType.description", "The damage type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[ChronoCoreDamageType.BALLISTIC]: {
					label: translatable("chrono-core.attributes.damageType.values.ballistic", "Ballistic"),
					image: "/images/chrono-core/damage-types/ballistic.png"
				},
				[ChronoCoreDamageType.BEAM]: {
					label: translatable("chrono-core.attributes.damageType.values.beam", "Beam"),
					image: "/images/chrono-core/damage-types/beam.png"
				},
				[ChronoCoreDamageType.PLASMA]: {
					label: translatable("chrono-core.attributes.damageType.values.plasma", "Plasma"),
					image: "/images/chrono-core/damage-types/plasma.png"
				},
				[ChronoCoreDamageType.STRIKE]: {
					label: translatable("chrono-core.attributes.damageType.values.strike", "Strike"),
					image: "/images/chrono-core/damage-types/strike.png"
				}
			}
		}),
		realm: enumAttribute(ChronoCoreRealm, {
			entity: "card",
			name: translatable("chrono-core.attributes.realm.name", "Realm"),
			description: translatable("chrono-core.attributes.realm.description", "The realm of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[ChronoCoreRealm.ARTIFICE]: {
					label: translatable("chrono-core.attributes.realm.values.artifice", "Artifice"),
					image: "/images/chrono-core/realms/artifice.png"
				},
				[ChronoCoreRealm.CASTRA]: {
					label: translatable("chrono-core.attributes.realm.values.castra", "Castra"),
					image: "/images/chrono-core/realms/castra.png"
				},
				[ChronoCoreRealm.NEKRIS]: {
					label: translatable("chrono-core.attributes.realm.values.nekris", "Nekris"),
					image: "/images/chrono-core/realms/nekris.png"
				},
				[ChronoCoreRealm.UMBRA]: {
					label: translatable("chrono-core.attributes.realm.values.umbra", "Umbra"),
					image: "/images/chrono-core/realms/umbra.png"
				}
			}
		}),
		equipRequirements: enumAttribute(ChronoCoreEquipRequirement, {
			entity: "card",
			name: translatable("chrono-core.attributes.equipRequirements.name", "Equip Requirements"),
			description: translatable("chrono-core.attributes.equipRequirements.description", "The equip requirements of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[ChronoCoreEquipRequirement.ALL_SLOTS]: { label: translatable("chrono-core.attributes.equipRequirements.values.all_slots", "All Slots") },
				[ChronoCoreEquipRequirement.AUXILIARY]: { label: translatable("chrono-core.attributes.equipRequirements.values.auxiliary", "Auxiliary") },
				[ChronoCoreEquipRequirement.EQUIPMENT]: { label: translatable("chrono-core.attributes.equipRequirements.values.equipment", "Equipment") },
				[ChronoCoreEquipRequirement.GAUNTLETS]: { label: translatable("chrono-core.attributes.equipRequirements.values.gauntlets", "Gauntlets") },
				[ChronoCoreEquipRequirement.GREAVES]: { label: translatable("chrono-core.attributes.equipRequirements.values.greaves", "Greaves") },
				[ChronoCoreEquipRequirement.ONE_HANDED_MELEE]: { label: translatable("chrono-core.attributes.equipRequirements.values.onehanded_melee", "One-Handed Melee") },
				[ChronoCoreEquipRequirement.ONE_HANDED_RANGED]: { label: translatable("chrono-core.attributes.equipRequirements.values.onehanded_ranged", "One-Handed Ranged") },
				[ChronoCoreEquipRequirement.ONE_HANDED_SHIELD]: { label: translatable("chrono-core.attributes.equipRequirements.values.onehanded_shield", "One-Handed Shield") },
				[ChronoCoreEquipRequirement.TWO_HANDED_MELEE]: { label: translatable("chrono-core.attributes.equipRequirements.values.twohanded_melee", "Two-Handed Melee") },
				[ChronoCoreEquipRequirement.TWO_HANDED_RANGED]: { label: translatable("chrono-core.attributes.equipRequirements.values.twohanded_ranged", "Two-Handed Ranged") },
				[ChronoCoreEquipRequirement.VISOR]: { label: translatable("chrono-core.attributes.equipRequirements.values.visor", "Visor") },
				[ChronoCoreEquipRequirement.WEAPONS]: { label: translatable("chrono-core.attributes.equipRequirements.values.weapons", "Weapons") }
			}
		}),
		orientation: enumAttribute(ChronoCoreOrientation, {
			entity: "card",
			name: translatable("chrono-core.attributes.orientation.name", "Orientation"),
			description: translatable("chrono-core.attributes.orientation.description", "The orientation of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[ChronoCoreOrientation.PORTRAIT]: { label: translatable("chrono-core.attributes.orientation.values.portrait", "Portrait") },
				[ChronoCoreOrientation.LANDSCAPE]: { label: translatable("chrono-core.attributes.orientation.values.landscape", "Landscape") }
			},
			hidden: true
		}),
		mkValue: enumAttribute(ChronoCoreMkValue, {
			entity: "card",
			name: translatable("chrono-core.attributes.mkValue.name", "MK Value"),
			description: translatable("chrono-core.attributes.mkValue.description", "The MK value of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[ChronoCoreMkValue.MKI]: {
					label: translatable("chrono-core.attributes.mkValue.values.mki", "MKI"),
					image: "/images/chrono-core/mk-values/mki.png"
				},
				[ChronoCoreMkValue.MKII]: {
					label: translatable("chrono-core.attributes.mkValue.values.mkii", "MKII"),
					image: "/images/chrono-core/mk-values/mkii.png"
				},
				[ChronoCoreMkValue.MKIII]: {
					label: translatable("chrono-core.attributes.mkValue.values.mkiii", "MKIII"),
					image: "/images/chrono-core/mk-values/mkiii.png"
				},
				[ChronoCoreMkValue.MKIV]: {
					label: translatable("chrono-core.attributes.mkValue.values.mkiv", "MKIV"),
					image: "/images/chrono-core/mk-values/mkiv.png"
				}
			}
		}),
		timingRestrictions: enumAttribute(ChronoCoreTimingRestriction, {
			entity: "card",
			name: translatable("chrono-core.attributes.timingRestrictions.name", "Timing Restrictions"),
			description: translatable("chrono-core.attributes.timingRestrictions.description", "The timing restrictions of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[ChronoCoreTimingRestriction.ACTIVE_MAIN]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.active_main", "Active Main"),
					image: "/images/chrono-core/timings/active-main-simple.png"
				},
				[ChronoCoreTimingRestriction.BEGINNING_OF_TURN]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.beginning_of_turn", "Beginning of Turn"),
					image: "/images/chrono-core/timings/beginning-of-turn-simple.png"
				},
				[ChronoCoreTimingRestriction.COUNTER_ATTACK]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.counter_attack", "Counter Attack"),
					image: "/images/chrono-core/timings/counter-attack-simple.png"
				},
				[ChronoCoreTimingRestriction.COUNTER_PLAY]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.counter_play", "Counter Play"),
					image: "/images/chrono-core/timings/counter-play-simple.png"
				},
				[ChronoCoreTimingRestriction.ON_ATTACK]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.on_attack", "On Attack"),
					image: "/images/chrono-core/timings/on-attack-simple.png"
				},
				[ChronoCoreTimingRestriction.ON_OVERCLOCK]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.on_overclock", "On Overclock"),
					image: "/images/chrono-core/timings/on-overclock-simple.png"
				},
				[ChronoCoreTimingRestriction.ON_PLAY]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.on_play", "On Play"),
					image: "/images/chrono-core/timings/on-play-simple.png"
				},
				[ChronoCoreTimingRestriction.ON_REVEAL]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.on_reveal", "On Reveal"),
					image: "/images/chrono-core/timings/on-reveal-simple.png"
				},
				[ChronoCoreTimingRestriction.ON_SCRAP]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.on_scrap", "On Scrap"),
					image: "/images/chrono-core/timings/on-scrap-simple.png"
				},
				[ChronoCoreTimingRestriction.OVERCLOCK]: {
					label: translatable("chrono-core.attributes.timingRestrictions.values.overclock", "Overclock"),
					image: "/images/chrono-core/timings/overclock-simple.png"
				}
			}
		}),
		keywords: enumAttribute(ChronoCoreKeyword, {
			entity: "card",
			name: translatable("chrono-core.attributes.keywords.name", "Keywords"),
			description: translatable("chrono-core.attributes.keywords.description", "The keywords of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[ChronoCoreKeyword.CLOAKING]: { label: translatable("chrono-core.attributes.keywords.values.cloaking", "Cloaking") },
				[ChronoCoreKeyword.DATA_STORM]: { label: translatable("chrono-core.attributes.keywords.values.data_storm", "Data Storm") },
				[ChronoCoreKeyword.DATAMINE]: { label: translatable("chrono-core.attributes.keywords.values.datamine", "Datamine") },
				[ChronoCoreKeyword.DECODING]: { label: translatable("chrono-core.attributes.keywords.values.decoding", "Decoding") },
				[ChronoCoreKeyword.FEEDBACK_LOOP]: { label: translatable("chrono-core.attributes.keywords.values.feedback_loop", "Feedback Loop") },
				[ChronoCoreKeyword.FIREWALL]: { label: translatable("chrono-core.attributes.keywords.values.firewall", "Firewall") },
				[ChronoCoreKeyword.LOCKOUT]: { label: translatable("chrono-core.attributes.keywords.values.lockout", "Lockout") },
				[ChronoCoreKeyword.OPTIMIZATION_PROTOCOL]: { label: translatable("chrono-core.attributes.keywords.values.optimization_protocol", "Optimization Protocol") },
				[ChronoCoreKeyword.OVERSHIELD]: { label: translatable("chrono-core.attributes.keywords.values.overshield", "Overshield") },
				[ChronoCoreKeyword.POWER_BURST]: { label: translatable("chrono-core.attributes.keywords.values.power_burst", "Power Burst") },
				[ChronoCoreKeyword.QUANTUM_BLOW]: { label: translatable("chrono-core.attributes.keywords.values.quantum_blow", "Quantum Blow") },
				[ChronoCoreKeyword.RECONFIGURE]: { label: translatable("chrono-core.attributes.keywords.values.reconfigure", "Reconfigure") },
				[ChronoCoreKeyword.REGENERATE]: { label: translatable("chrono-core.attributes.keywords.values.regenerate", "Regenerate") },
				[ChronoCoreKeyword.REINFORCE]: { label: translatable("chrono-core.attributes.keywords.values.reinforce", "Reinforce") },
				[ChronoCoreKeyword.RENEW]: { label: translatable("chrono-core.attributes.keywords.values.renew", "Renew") },
				[ChronoCoreKeyword.ROOTKIT]: { label: translatable("chrono-core.attributes.keywords.values.rootkit", "Rootkit") },
				[ChronoCoreKeyword.SCRAPHEAP]: { label: translatable("chrono-core.attributes.keywords.values.scrapheap", "Scrapheap") },
				[ChronoCoreKeyword.SHOCKWAVE]: { label: translatable("chrono-core.attributes.keywords.values.shockwave", "Shockwave") },
				[ChronoCoreKeyword.SHORT_CIRCUIT]: { label: translatable("chrono-core.attributes.keywords.values.short_circuit", "Short Circuit") },
				[ChronoCoreKeyword.SIPHON]: { label: translatable("chrono-core.attributes.keywords.values.siphon", "Siphon") },
				[ChronoCoreKeyword.SYSTEM_OVERRIDE]: { label: translatable("chrono-core.attributes.keywords.values.system_override", "System Override") },
				[ChronoCoreKeyword.SYSTEM_REBOOT]: { label: translatable("chrono-core.attributes.keywords.values.system_reboot", "System Reboot") },
				[ChronoCoreKeyword.VIRUS]: { label: translatable("chrono-core.attributes.keywords.values.virus", "Virus") },
				[ChronoCoreKeyword.WORM]: { label: translatable("chrono-core.attributes.keywords.values.worm", "Worm") }
			}
		}),
		origins: enumAttribute(ChronoCoreOrigin, {
			entity: "variant",
			name: translatable("chrono-core.attributes.origins.name", "Origins"),
			description: translatable("chrono-core.attributes.origins.description", "The origins of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[ChronoCoreOrigin.BOXTOPPER]: { label: translatable("chrono-core.attributes.origins.values.boxtopper", "Boxtopper") },
				[ChronoCoreOrigin.CHRONO_CORE_CREW]: { label: translatable("chrono-core.attributes.origins.values.chrono_core_crew", "Chrono Core Crew") },
				[ChronoCoreOrigin.GEN_CON]: { label: translatable("chrono-core.attributes.origins.values.gen_con", "Gen Con") },
				[ChronoCoreOrigin.HOUSTON_PREMIER_EVENT]: { label: translatable("chrono-core.attributes.origins.values.houston_premier_event", "Houston Premier Event") },
				[ChronoCoreOrigin.KICKSTARTER_EXCLUSIVE]: { label: translatable("chrono-core.attributes.origins.values.kickstarter_exclusive", "Kickstarter Exclusive") },
				[ChronoCoreOrigin.RELEASE_EVENT_KIT]: { label: translatable("chrono-core.attributes.origins.values.release_event_kit", "Release Event Kit") }
			}
		}),
		maxShieldValue: {
			entity: "card",
			name: translatable("chrono-core.attributes.maxShieldValue.name", "Max Shield Value"),
			description: translatable("chrono-core.attributes.maxShieldValue.description", "Maximum shield value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		maxLifeValue: {
			entity: "card",
			name: translatable("chrono-core.attributes.maxLifeValue.name", "Max Life Value"),
			description: translatable("chrono-core.attributes.maxLifeValue.description", "Maximum life value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		coreCost: {
			entity: "card",
			name: translatable("chrono-core.attributes.coreCost.name", "Core Cost"),
			description: translatable("chrono-core.attributes.coreCost.description", "Core cost to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		damageValue: {
			entity: "card",
			name: translatable("chrono-core.attributes.damageValue.name", "Damage Value"),
			description: translatable("chrono-core.attributes.damageValue.description", "Damage value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		chargeValue: {
			entity: "card",
			name: translatable("chrono-core.attributes.chargeValue.name", "Charge Value"),
			description: translatable("chrono-core.attributes.chargeValue.description", "Charge value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		rulesText: {
			entity: "variant",
			name: translatable("chrono-core.attributes.rulesText.name", "Rules Text"),
			description: translatable("chrono-core.attributes.rulesText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"realm",
			"cardType",
			"finishes"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"variant",
			"mkValue",
			"chassisType",
			"subtype",
			"damageType",
			"keywords",
			"timingRestrictions",
			"origins",
			"traits",
			"equipRequirements"
		]
	}, {
		label: "Card Stats",
		filters: [
			"coreCost",
			"damageValue",
			"chargeValue",
			"maxShieldValue",
			"maxLifeValue"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText"],
		statistics: [
			"cardType",
			"realm",
			"mkValue",
			"chassisType",
			"subtype",
			"damageType",
			"coreCost",
			"damageValue",
			"chargeValue",
			"maxShieldValue",
			"maxLifeValue",
			"timingRestrictions",
			"keywords",
			"traits",
			"equipRequirements",
			"origins",
			"rarity",
			"orientation"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: chronoCoreListPresets,
	basicAttributes: ["rarity", "realm"],
	finishHierarchy: [CardFinish.STANDARD, CardFinish.HOLOGRAPHIC],
	supportedMarketplaces: [],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "WNR01-028",
		cardName: "The Fang V.1",
		expansion: "War of the Nine Realms",
		expansionCode: "WNR",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/cyberpunk/cyberpunk.types.ts
let CyberpunkRarity = /* @__PURE__ */ function(CyberpunkRarity) {
	CyberpunkRarity["Common"] = "Common";
	CyberpunkRarity["Uncommon"] = "Uncommon";
	CyberpunkRarity["Rare"] = "Rare";
	CyberpunkRarity["Epic"] = "Epic";
	CyberpunkRarity["Secret"] = "Secret";
	CyberpunkRarity["Iconic"] = "Iconic";
	CyberpunkRarity["Nova"] = "Nova";
	return CyberpunkRarity;
}({});
let CyberpunkFinish = /* @__PURE__ */ function(CyberpunkFinish) {
	CyberpunkFinish["Standard"] = "Standard";
	CyberpunkFinish["Foil"] = "Foil";
	return CyberpunkFinish;
}({});
let CyberpunkColor = /* @__PURE__ */ function(CyberpunkColor) {
	CyberpunkColor["Red"] = "Red";
	CyberpunkColor["Blue"] = "Blue";
	CyberpunkColor["Green"] = "Green";
	CyberpunkColor["Yellow"] = "Yellow";
	return CyberpunkColor;
}({});
let CyberpunkCardType = /* @__PURE__ */ function(CyberpunkCardType) {
	CyberpunkCardType["Legend"] = "Legend";
	CyberpunkCardType["Unit"] = "Unit";
	CyberpunkCardType["Gear"] = "Gear";
	CyberpunkCardType["Program"] = "Program";
	return CyberpunkCardType;
}({});
let CyberpunkClassification = /* @__PURE__ */ function(CyberpunkClassification) {
	CyberpunkClassification["Arasaka"] = "Arasaka";
	CyberpunkClassification["Corpo"] = "Corpo";
	CyberpunkClassification["Merc"] = "Merc";
	CyberpunkClassification["Doll"] = "Doll";
	CyberpunkClassification["Netrunner"] = "Netrunner";
	CyberpunkClassification["Ripperdoc"] = "Ripperdoc";
	CyberpunkClassification["Vehicle"] = "Vehicle";
	CyberpunkClassification["Drone"] = "Drone";
	CyberpunkClassification["Militech"] = "Militech";
	CyberpunkClassification["Zetatech"] = "Zetatech";
	CyberpunkClassification["Cyberware"] = "Cyberware";
	CyberpunkClassification["Weapon"] = "Weapon";
	CyberpunkClassification["Plan"] = "Plan";
	CyberpunkClassification["Implant"] = "Implant";
	CyberpunkClassification["Tech"] = "Tech";
	CyberpunkClassification["Overclocking"] = "Overclocking";
	CyberpunkClassification["SixthStreet"] = "6th Street";
	CyberpunkClassification["Aldecado"] = "Aldecado";
	CyberpunkClassification["Animal"] = "Animal";
	CyberpunkClassification["Braindance"] = "Braindance";
	CyberpunkClassification["Extreme"] = "Extreme";
	CyberpunkClassification["Fixer"] = "Fixer";
	CyberpunkClassification["Ganger"] = "Ganger";
	CyberpunkClassification["Maelstrom"] = "Maelstrom";
	CyberpunkClassification["MainesCrew"] = "Maine's Crew";
	CyberpunkClassification["Medtech"] = "Medtech";
	CyberpunkClassification["Mox"] = "Mox";
	CyberpunkClassification["Mystic"] = "Mystic";
	CyberpunkClassification["Ncpd"] = "NCPD";
	CyberpunkClassification["Nomad"] = "Nomad";
	CyberpunkClassification["Quickhack"] = "Quickhack";
	CyberpunkClassification["RaffenShiv"] = "Raffen Shiv";
	CyberpunkClassification["Rocker"] = "Rocker";
	CyberpunkClassification["Samurai"] = "Samurai";
	CyberpunkClassification["Scavenger"] = "Scavenger";
	CyberpunkClassification["Techie"] = "Techie";
	CyberpunkClassification["TraumaTeam"] = "Trauma Team";
	CyberpunkClassification["TygerClaws"] = "Tyger Claws";
	CyberpunkClassification["Valentino"] = "Valentino";
	CyberpunkClassification["VoodooBoys"] = "Voodoo Boys";
	return CyberpunkClassification;
}({});
let CyberpunkKeyword = /* @__PURE__ */ function(CyberpunkKeyword) {
	CyberpunkKeyword["GoSolo"] = "Go Solo";
	CyberpunkKeyword["Blocker"] = "Blocker";
	CyberpunkKeyword["Play"] = "Play";
	CyberpunkKeyword["Attack"] = "Attack";
	CyberpunkKeyword["Flip"] = "Flip";
	return CyberpunkKeyword;
}({});

//#endregion
//#region src/lib/games/cyberpunk/cyberpunk.presets.ts
/** Rare and above exist only in foil; a complete set has to take them there. */
const FOIL_ONLY_RARITIES = [
	CyberpunkRarity.Rare,
	CyberpunkRarity.Epic,
	CyberpunkRarity.Secret,
	CyberpunkRarity.Iconic,
	CyberpunkRarity.Nova
];
const cyberpunkListPresets = {
	Standard: {
		name: translatable("cyberpunk.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("cyberpunk.presets.standard.description", "A complete set with one copy of each card - standard finish where available, foil otherwise"),
		matchers: [{
			filters: { finish: [CyberpunkFinish.Standard] },
			quantity: 1
		}, {
			filters: {
				finish: [CyberpunkFinish.Foil],
				rarity: FOIL_ONLY_RARITIES
			},
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("cyberpunk.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("cyberpunk.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck - standard finish where available, foil otherwise"),
		matchers: [{
			filters: { finish: [CyberpunkFinish.Standard] },
			quantity: 3
		}, {
			filters: {
				finish: [CyberpunkFinish.Foil],
				rarity: FOIL_ONLY_RARITIES
			},
			quantity: 3
		}]
	},
	Foil: {
		name: translatable("cyberpunk.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("cyberpunk.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [CyberpunkFinish.Foil] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("cyberpunk.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("cyberpunk.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [{
			filters: { finish: [CyberpunkFinish.Foil] },
			quantity: 3
		}]
	},
	"Master Set": {
		name: translatable("cyberpunk.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("cyberpunk.presets.masterset.description", "A complete set with one copy of each card in all finishes and variants"),
		matchers: [{
			filters: {},
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/cyberpunk/cyberpunk.game.ts
/**
* Shared finish values for Cyberpunk - used by both finishes and finish attributes
*/
const cyberpunkFinishValues = {
	[CyberpunkFinish.Standard]: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	[CyberpunkFinish.Foil]: { label: translatable("global.attributes.finish.values.foil", "Foil") }
};
const cyberpunkFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(CyberpunkRarity).optional(),
	finish: makeZodEnumFilter(CyberpunkFinish).optional(),
	finishes: makeZodEnumFilter(CyberpunkFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	color: makeZodEnumFilter(CyberpunkColor).optional(),
	cardType: makeZodEnumFilter(CyberpunkCardType).optional(),
	classification: makeZodEnumFilter(CyberpunkClassification).optional(),
	keywords: makeZodEnumFilter(CyberpunkKeyword).optional(),
	cost: zodNumericFilter.optional(),
	power: zodNumericFilter.optional(),
	ram: zodNumericFilter.optional()
});
const CyberpunkGameConfiguration = {
	name: "Cyberpunk",
	shortName: "Cyberpunk",
	slug: "cyberpunk",
	icon: {
		dark: "cyberpunk-icon.png",
		light: "cyberpunk-icon.png"
	},
	image: {
		dark: "cyberpunk.png",
		light: "cyberpunk.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(CyberpunkFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: cyberpunkFinishValues
		}),
		finish: enumAttribute(CyberpunkFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: cyberpunkFinishValues
		}),
		rarity: enumAttribute(CyberpunkRarity, {
			entity: "variant",
			name: translatable("global.attributes.rarity.name", "Rarity"),
			description: translatable("global.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[CyberpunkRarity.Common]: {
					label: translatable("cyberpunk.attributes.rarity.values.common", "Common"),
					image: "/images/cyberpunk/rarity/common.png"
				},
				[CyberpunkRarity.Uncommon]: {
					label: translatable("cyberpunk.attributes.rarity.values.uncommon", "Uncommon"),
					image: "/images/cyberpunk/rarity/uncommon.png"
				},
				[CyberpunkRarity.Rare]: {
					label: translatable("cyberpunk.attributes.rarity.values.rare", "Rare"),
					image: "/images/cyberpunk/rarity/rare.png"
				},
				[CyberpunkRarity.Epic]: {
					label: translatable("cyberpunk.attributes.rarity.values.epic", "Epic"),
					image: "/images/cyberpunk/rarity/epic.png"
				},
				[CyberpunkRarity.Secret]: {
					label: translatable("cyberpunk.attributes.rarity.values.secret", "Secret"),
					image: "/images/cyberpunk/rarity/secret.png"
				},
				[CyberpunkRarity.Iconic]: {
					label: translatable("cyberpunk.attributes.rarity.values.iconic", "Iconic"),
					image: "/images/cyberpunk/rarity/iconic.png"
				},
				[CyberpunkRarity.Nova]: {
					label: translatable("cyberpunk.attributes.rarity.values.nova", "Nova"),
					image: "/images/cyberpunk/rarity/nova.png"
				}
			}
		}),
		printNumber: printNumberAttribute,
		color: enumAttribute(CyberpunkColor, {
			entity: "card",
			name: translatable("cyberpunk.attributes.color.name", "Color"),
			description: translatable("cyberpunk.attributes.color.description", "The color faction of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[CyberpunkColor.Red]: {
					label: translatable("cyberpunk.attributes.color.values.red", "Red"),
					image: "/images/cyberpunk/colors/red.png"
				},
				[CyberpunkColor.Blue]: {
					label: translatable("cyberpunk.attributes.color.values.blue", "Blue"),
					image: "/images/cyberpunk/colors/blue.png"
				},
				[CyberpunkColor.Green]: {
					label: translatable("cyberpunk.attributes.color.values.green", "Green"),
					image: "/images/cyberpunk/colors/green.png"
				},
				[CyberpunkColor.Yellow]: {
					label: translatable("cyberpunk.attributes.color.values.yellow", "Yellow"),
					image: "/images/cyberpunk/colors/yellow.png"
				}
			}
		}),
		cardType: enumAttribute(CyberpunkCardType, {
			entity: "card",
			name: translatable("cyberpunk.attributes.cardType.name", "Card Type"),
			description: translatable("cyberpunk.attributes.cardType.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[CyberpunkCardType.Legend]: { label: translatable("cyberpunk.attributes.cardType.values.legend", "Legend") },
				[CyberpunkCardType.Unit]: { label: translatable("cyberpunk.attributes.cardType.values.unit", "Unit") },
				[CyberpunkCardType.Gear]: { label: translatable("cyberpunk.attributes.cardType.values.gear", "Gear") },
				[CyberpunkCardType.Program]: { label: translatable("cyberpunk.attributes.cardType.values.program", "Program") }
			}
		}),
		classification: enumAttribute(CyberpunkClassification, {
			entity: "card",
			name: translatable("cyberpunk.attributes.classification.name", "Classification"),
			description: translatable("cyberpunk.attributes.classification.description", "Card classifications and affiliations"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[CyberpunkClassification.Arasaka]: { label: translatable("cyberpunk.attributes.classification.values.arasaka", "Arasaka") },
				[CyberpunkClassification.Corpo]: { label: translatable("cyberpunk.attributes.classification.values.corpo", "Corpo") },
				[CyberpunkClassification.Merc]: { label: translatable("cyberpunk.attributes.classification.values.merc", "Merc") },
				[CyberpunkClassification.Doll]: { label: translatable("cyberpunk.attributes.classification.values.doll", "Doll") },
				[CyberpunkClassification.Netrunner]: { label: translatable("cyberpunk.attributes.classification.values.netrunner", "Netrunner") },
				[CyberpunkClassification.Ripperdoc]: { label: translatable("cyberpunk.attributes.classification.values.ripperdoc", "Ripperdoc") },
				[CyberpunkClassification.Vehicle]: { label: translatable("cyberpunk.attributes.classification.values.vehicle", "Vehicle") },
				[CyberpunkClassification.Drone]: { label: translatable("cyberpunk.attributes.classification.values.drone", "Drone") },
				[CyberpunkClassification.Militech]: { label: translatable("cyberpunk.attributes.classification.values.militech", "Militech") },
				[CyberpunkClassification.Zetatech]: { label: translatable("cyberpunk.attributes.classification.values.zetatech", "Zetatech") },
				[CyberpunkClassification.Cyberware]: { label: translatable("cyberpunk.attributes.classification.values.cyberware", "Cyberware") },
				[CyberpunkClassification.Weapon]: { label: translatable("cyberpunk.attributes.classification.values.weapon", "Weapon") },
				[CyberpunkClassification.Plan]: { label: translatable("cyberpunk.attributes.classification.values.plan", "Plan") },
				[CyberpunkClassification.Implant]: { label: translatable("cyberpunk.attributes.classification.values.implant", "Implant") },
				[CyberpunkClassification.Tech]: { label: translatable("cyberpunk.attributes.classification.values.tech", "Tech") },
				[CyberpunkClassification.Overclocking]: { label: translatable("cyberpunk.attributes.classification.values.overclocking", "Overclocking") },
				[CyberpunkClassification.SixthStreet]: { label: translatable("cyberpunk.attributes.classification.values.sixthStreet", "6th Street") },
				[CyberpunkClassification.Aldecado]: { label: translatable("cyberpunk.attributes.classification.values.aldecado", "Aldecado") },
				[CyberpunkClassification.Animal]: { label: translatable("cyberpunk.attributes.classification.values.animal", "Animal") },
				[CyberpunkClassification.Braindance]: { label: translatable("cyberpunk.attributes.classification.values.braindance", "Braindance") },
				[CyberpunkClassification.Extreme]: { label: translatable("cyberpunk.attributes.classification.values.extreme", "Extreme") },
				[CyberpunkClassification.Fixer]: { label: translatable("cyberpunk.attributes.classification.values.fixer", "Fixer") },
				[CyberpunkClassification.Ganger]: { label: translatable("cyberpunk.attributes.classification.values.ganger", "Ganger") },
				[CyberpunkClassification.Maelstrom]: { label: translatable("cyberpunk.attributes.classification.values.maelstrom", "Maelstrom") },
				[CyberpunkClassification.MainesCrew]: { label: translatable("cyberpunk.attributes.classification.values.mainesCrew", "Maine's Crew") },
				[CyberpunkClassification.Medtech]: { label: translatable("cyberpunk.attributes.classification.values.medtech", "Medtech") },
				[CyberpunkClassification.Mox]: { label: translatable("cyberpunk.attributes.classification.values.mox", "Mox") },
				[CyberpunkClassification.Mystic]: { label: translatable("cyberpunk.attributes.classification.values.mystic", "Mystic") },
				[CyberpunkClassification.Ncpd]: { label: translatable("cyberpunk.attributes.classification.values.ncpd", "NCPD") },
				[CyberpunkClassification.Nomad]: { label: translatable("cyberpunk.attributes.classification.values.nomad", "Nomad") },
				[CyberpunkClassification.Quickhack]: { label: translatable("cyberpunk.attributes.classification.values.quickhack", "Quickhack") },
				[CyberpunkClassification.RaffenShiv]: { label: translatable("cyberpunk.attributes.classification.values.raffenShiv", "Raffen Shiv") },
				[CyberpunkClassification.Rocker]: { label: translatable("cyberpunk.attributes.classification.values.rocker", "Rocker") },
				[CyberpunkClassification.Samurai]: { label: translatable("cyberpunk.attributes.classification.values.samurai", "Samurai") },
				[CyberpunkClassification.Scavenger]: { label: translatable("cyberpunk.attributes.classification.values.scavenger", "Scavenger") },
				[CyberpunkClassification.Techie]: { label: translatable("cyberpunk.attributes.classification.values.techie", "Techie") },
				[CyberpunkClassification.TraumaTeam]: { label: translatable("cyberpunk.attributes.classification.values.traumaTeam", "Trauma Team") },
				[CyberpunkClassification.TygerClaws]: { label: translatable("cyberpunk.attributes.classification.values.tygerClaws", "Tyger Claws") },
				[CyberpunkClassification.Valentino]: { label: translatable("cyberpunk.attributes.classification.values.valentino", "Valentino") },
				[CyberpunkClassification.VoodooBoys]: { label: translatable("cyberpunk.attributes.classification.values.voodooBoys", "Voodoo Boys") }
			}
		}),
		keywords: enumAttribute(CyberpunkKeyword, {
			entity: "card",
			name: translatable("cyberpunk.attributes.keywords.name", "Keywords"),
			description: translatable("cyberpunk.attributes.keywords.description", "Card keywords and abilities"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[CyberpunkKeyword.GoSolo]: { label: translatable("cyberpunk.attributes.keywords.values.goSolo", "Go Solo") },
				[CyberpunkKeyword.Blocker]: { label: translatable("cyberpunk.attributes.keywords.values.blocker", "Blocker") },
				[CyberpunkKeyword.Play]: { label: translatable("cyberpunk.attributes.keywords.values.play", "Play") },
				[CyberpunkKeyword.Attack]: { label: translatable("cyberpunk.attributes.keywords.values.attack", "Attack") },
				[CyberpunkKeyword.Flip]: { label: translatable("cyberpunk.attributes.keywords.values.flip", "Flip") }
			}
		}),
		cost: {
			entity: "card",
			name: translatable("cyberpunk.attributes.cost.name", "Cost"),
			description: translatable("cyberpunk.attributes.cost.description", "Cost to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		power: {
			entity: "card",
			name: translatable("cyberpunk.attributes.power.name", "Power"),
			description: translatable("cyberpunk.attributes.power.description", "Power value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		ram: {
			entity: "card",
			name: translatable("cyberpunk.attributes.ram.name", "Ram"),
			description: translatable("cyberpunk.attributes.ram.description", "Ram requirement of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		rulesText: {
			entity: "variant",
			name: translatable("cyberpunk.attributes.rulesText.name", "Rules Text"),
			description: translatable("cyberpunk.attributes.rulesText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		},
		artist: {
			entity: "variant",
			name: translatable("cyberpunk.attributes.artist.name", "Artist"),
			description: translatable("cyberpunk.attributes.artist.description", "Card artist"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"finishes",
			"color",
			"cardType"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"classification",
			"keywords",
			"cost",
			"power",
			"ram"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText"],
		statistics: [
			"cardType",
			"color",
			"rarity",
			"classification",
			"keywords",
			"cost",
			"power",
			"ram",
			"artist"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: cyberpunkListPresets,
	basicAttributes: ["rarity", "color"],
	supportedMarketplaces: [SupportedMarketplace.Cardmarket, SupportedMarketplace.Tcgplayer],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "005a",
		cardName: "V — Streetkid",
		expansion: "Welcome to Night City - Retail",
		expansionCode: "MS01",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/dbs-fusion/dbs-fusion.types.ts
let DbsFusionRarity = /* @__PURE__ */ function(DbsFusionRarity) {
	DbsFusionRarity["LEADER"] = "leader";
	DbsFusionRarity["COMMON"] = "common";
	DbsFusionRarity["UNCOMMON"] = "uncommon";
	DbsFusionRarity["RARE"] = "rare";
	DbsFusionRarity["SUPER_RARE"] = "super_rare";
	DbsFusionRarity["SECRET_RARE"] = "secret_rare";
	DbsFusionRarity["PROMO"] = "promo";
	DbsFusionRarity["NONE"] = "none";
	return DbsFusionRarity;
}({});
let DbsFusionFinish = /* @__PURE__ */ function(DbsFusionFinish) {
	DbsFusionFinish["STANDARD"] = "Standard";
	DbsFusionFinish["HOLOFOIL"] = "Holofoil";
	return DbsFusionFinish;
}({});
let DbsFusionColor = /* @__PURE__ */ function(DbsFusionColor) {
	DbsFusionColor["RED"] = "Red";
	DbsFusionColor["BLUE"] = "Blue";
	DbsFusionColor["GREEN"] = "Green";
	DbsFusionColor["YELLOW"] = "Yellow";
	DbsFusionColor["BLACK"] = "Black";
	return DbsFusionColor;
}({});
let DbsFusionCardType = /* @__PURE__ */ function(DbsFusionCardType) {
	DbsFusionCardType["LEADER"] = "Leader";
	DbsFusionCardType["BATTLE"] = "Battle";
	DbsFusionCardType["EXTRA"] = "Extra";
	DbsFusionCardType["ENERGY_MARKER"] = "Energy Marker";
	DbsFusionCardType["CODE_CARD"] = "Code Card";
	return DbsFusionCardType;
}({});

//#endregion
//#region src/lib/games/dbs-fusion/dbs-fusion.presets.ts
const dbsFusionListPresets = {
	Standard: {
		name: translatable("dbs-fusion.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("dbs-fusion.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [DbsFusionFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("dbs-fusion.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("dbs-fusion.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [DbsFusionFinish.STANDARD] },
			quantity: 4
		}]
	},
	Foil: {
		name: translatable("dbs-fusion.presets.foil.name", "Holofoil Set"),
		type: ListPresetType.FOIL,
		description: translatable("dbs-fusion.presets.foil.description", "A complete set with one copy of each card in holofoil finish"),
		matchers: [{
			filters: { finish: [DbsFusionFinish.HOLOFOIL] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("dbs-fusion.presets.foilPlayset.name", "Holofoil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("dbs-fusion.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in holofoil finish"),
		matchers: [{
			filters: { finish: [DbsFusionFinish.HOLOFOIL] },
			quantity: 4
		}]
	},
	"Master Set": {
		name: translatable("dbs-fusion.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("dbs-fusion.presets.masterset.description", "A complete set with one copy of each card in both standard and holofoil finishes"),
		matchers: [{
			filters: { finish: [DbsFusionFinish.STANDARD] },
			quantity: 1
		}, {
			filters: { finish: [DbsFusionFinish.HOLOFOIL] },
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/dbs-fusion/dbs-fusion.game.ts
const dbsFusionFinishValues = {
	Standard: { label: translatable("dbs-fusion.attributes.finish.values.standard", "Standard") },
	Holofoil: { label: translatable("dbs-fusion.attributes.finish.values.holofoil", "Holofoil") }
};
const dbsFusionFilters = z.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	finish: makeZodEnumFilter(DbsFusionFinish).optional(),
	finishes: makeZodEnumFilter(DbsFusionFinish).optional(),
	rarity: makeZodEnumFilter(DbsFusionRarity).optional(),
	color: makeZodEnumFilter(DbsFusionColor).optional(),
	type: makeZodEnumFilter(DbsFusionCardType).optional(),
	power: zodNumericFilter.optional(),
	comboPower: zodNumericFilter.optional(),
	energyCost: zodNumericFilter.optional()
});
const DbsFusionGameConfiguration = {
	name: "Dragon Ball Super: Fusion World",
	shortName: "DBS: Fusion World",
	slug: "dbs-fusion",
	icon: {
		dark: "dbs-fusion-icon.png",
		light: "dbs-fusion-icon.png"
	},
	image: {
		dark: "dbs-fusion.png",
		light: "dbs-fusion.png"
	},
	attributes: {
		name: nameAttribute,
		rarity: enumAttribute(DbsFusionRarity, {
			entity: "variant",
			name: translatable("dbs-fusion.attributes.rarity.name", "Rarity"),
			description: translatable("dbs-fusion.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				leader: { label: translatable("dbs-fusion.attributes.rarity.values.leader", "Leader") },
				common: { label: translatable("dbs-fusion.attributes.rarity.values.common", "Common") },
				uncommon: { label: translatable("dbs-fusion.attributes.rarity.values.uncommon", "Uncommon") },
				rare: { label: translatable("dbs-fusion.attributes.rarity.values.rare", "Rare") },
				super_rare: { label: translatable("dbs-fusion.attributes.rarity.values.super_rare", "Super Rare") },
				secret_rare: { label: translatable("dbs-fusion.attributes.rarity.values.secret_rare", "Secret Rare") },
				promo: { label: translatable("dbs-fusion.attributes.rarity.values.promo", "Promo") },
				none: { label: translatable("dbs-fusion.attributes.rarity.values.none", "None") }
			}
		}),
		color: enumAttribute(DbsFusionColor, {
			entity: "card",
			name: translatable("dbs-fusion.attributes.color.name", "Color"),
			description: translatable("dbs-fusion.attributes.color.description", "The color of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				Red: { label: translatable("dbs-fusion.attributes.color.values.red", "Red") },
				Blue: { label: translatable("dbs-fusion.attributes.color.values.blue", "Blue") },
				Green: { label: translatable("dbs-fusion.attributes.color.values.green", "Green") },
				Yellow: { label: translatable("dbs-fusion.attributes.color.values.yellow", "Yellow") },
				Black: { label: translatable("dbs-fusion.attributes.color.values.black", "Black") }
			}
		}),
		finishes: enumAttribute(DbsFusionFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("dbs-fusion.attributes.finish.name", "Finish"),
			description: translatable("dbs-fusion.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: dbsFusionFinishValues
		}),
		finish: enumAttribute(DbsFusionFinish, {
			entity: "inventory",
			name: translatable("dbs-fusion.attributes.finish.name", "Finish"),
			description: translatable("dbs-fusion.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: dbsFusionFinishValues
		}),
		printNumber: printNumberAttribute,
		type: enumAttribute(DbsFusionCardType, {
			entity: "card",
			name: translatable("dbs-fusion.attributes.type.name", "Card Type"),
			description: translatable("dbs-fusion.attributes.type.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[DbsFusionCardType.LEADER]: { label: translatable("dbs-fusion.attributes.type.values.leader", "Leader") },
				[DbsFusionCardType.BATTLE]: { label: translatable("dbs-fusion.attributes.type.values.battle", "Battle") },
				[DbsFusionCardType.EXTRA]: { label: translatable("dbs-fusion.attributes.type.values.extra", "Extra") },
				[DbsFusionCardType.ENERGY_MARKER]: { label: translatable("dbs-fusion.attributes.type.values.energy_marker", "Energy Marker") },
				[DbsFusionCardType.CODE_CARD]: { label: translatable("dbs-fusion.attributes.type.values.code_card", "Code Card") }
			}
		}),
		power: {
			entity: "card",
			name: translatable("dbs-fusion.attributes.power.name", "Power"),
			description: translatable("dbs-fusion.attributes.power.description", "Power value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		comboPower: {
			entity: "card",
			name: translatable("dbs-fusion.attributes.comboPower.name", "Combo Power"),
			description: translatable("dbs-fusion.attributes.comboPower.description", "Combo power value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		cost: {
			entity: "card",
			name: translatable("dbs-fusion.attributes.cost.name", "Cost"),
			description: translatable("dbs-fusion.attributes.cost.description", "Cost to play the card"),
			type: "string",
			array: false
		},
		energyCost: {
			entity: "card",
			name: translatable("dbs-fusion.attributes.energyCost.name", "Energy Cost"),
			description: translatable("dbs-fusion.attributes.energyCost.description", "Energy cost to play the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		characterTraits: {
			entity: "card",
			name: translatable("dbs-fusion.attributes.characterTraits.name", "Character Traits"),
			description: translatable("dbs-fusion.attributes.characterTraits.description", "The character traits of the card"),
			type: "string",
			array: true
		},
		rulesText: {
			entity: "variant",
			name: translatable("dbs-fusion.attributes.rulesText.name", "Rules Text"),
			description: translatable("dbs-fusion.attributes.rulesText.description", "The rules text of the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"rarity",
			"color"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"type",
			"power",
			"comboPower",
			"energyCost"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText"],
		statistics: [
			"rarity",
			"color",
			"finish",
			"type",
			"power",
			"comboPower",
			"cost",
			"energyCost",
			"characterTraits"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: dbsFusionListPresets,
	finishHierarchy: [CardFinish.STANDARD, CardFinish.HOLOFOIL],
	basicAttributes: ["color", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Tcgplayer, SupportedMarketplace.Cardmarket],
	vendorIds: [cardmarketVendorId, tcgplayerVendorId],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "FB01-001",
		cardName: "Son Goku",
		expansion: "Awakened Pulse",
		expansionCode: "FB01",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/dbs-masters/dbs-masters.types.ts
let DbsMastersRarity = /* @__PURE__ */ function(DbsMastersRarity) {
	DbsMastersRarity["COMMON"] = "common";
	DbsMastersRarity["UNCOMMON"] = "uncommon";
	DbsMastersRarity["RARE"] = "rare";
	DbsMastersRarity["SUPER_RARE"] = "super_rare";
	DbsMastersRarity["SPECIAL_RARE"] = "special_rare";
	DbsMastersRarity["SPECIAL_LEADER_RARE"] = "special_leader_rare";
	DbsMastersRarity["CONCEPT_RARE"] = "concept_rare";
	DbsMastersRarity["GOD_RARE"] = "god_rare";
	DbsMastersRarity["SECRET_RARE"] = "secret_rare";
	DbsMastersRarity["STARTER_RARE"] = "starter_rare";
	DbsMastersRarity["CAMPAIGN_RARE"] = "campaign_rare";
	DbsMastersRarity["EXPANSION_RARE"] = "expansion_rare";
	DbsMastersRarity["PROMO"] = "promo";
	DbsMastersRarity["GIANT_FORCE_RARE"] = "giant_force_rare";
	DbsMastersRarity["DRAGON_BALL_RARE"] = "dragon_ball_rare";
	DbsMastersRarity["DUO_POWER_RARE"] = "duo_power_rare";
	DbsMastersRarity["ICONIC_ATTACK_RARE"] = "iconic_attack_rare";
	DbsMastersRarity["DESTROYER_AND_ANGEL_RARE"] = "destroyer_and_angel_rare";
	DbsMastersRarity["INFINITE_SAIYAN_RARE"] = "infinite_saiyan_rare";
	DbsMastersRarity["SON_GOHAN_RARE"] = "son_gohan_rare";
	DbsMastersRarity["REBOOT_LEADER_RARE"] = "reboot_leader_rare";
	DbsMastersRarity["NOBLE_HERO_RARE"] = "noble_hero_rare";
	DbsMastersRarity["IGNOBLE_VILLAIN_RARE"] = "ignoble_villain_rare";
	DbsMastersRarity["DESTRUCTION_RARE"] = "destruction_rare";
	DbsMastersRarity["SPECIAL_RARE_SIGNATURE"] = "special_rare_signature";
	DbsMastersRarity["FEATURE_RARE"] = "feature_rare";
	DbsMastersRarity["TOKEN"] = "token";
	DbsMastersRarity["MERIT"] = "merit";
	DbsMastersRarity["NONE"] = "none";
	return DbsMastersRarity;
}({});
let DbsMastersFinish = /* @__PURE__ */ function(DbsMastersFinish) {
	DbsMastersFinish["STANDARD"] = "Standard";
	DbsMastersFinish["FOIL"] = "Foil";
	return DbsMastersFinish;
}({});
let DbsMastersColor = /* @__PURE__ */ function(DbsMastersColor) {
	DbsMastersColor["RED"] = "Red";
	DbsMastersColor["BLUE"] = "Blue";
	DbsMastersColor["GREEN"] = "Green";
	DbsMastersColor["YELLOW"] = "Yellow";
	DbsMastersColor["BLACK"] = "Black";
	DbsMastersColor["WHITE"] = "White";
	return DbsMastersColor;
}({});
let DbsMastersCardType = /* @__PURE__ */ function(DbsMastersCardType) {
	DbsMastersCardType["LEADER"] = "Leader";
	DbsMastersCardType["BATTLE"] = "Battle";
	DbsMastersCardType["EXTRA"] = "Extra";
	DbsMastersCardType["Z_BATTLE"] = "Z-Battle";
	DbsMastersCardType["Z_UNISON"] = "Z-Unison";
	DbsMastersCardType["Z_EXTRA"] = "Z-Extra";
	DbsMastersCardType["Z_LEADER"] = "Z-Leader";
	DbsMastersCardType["UNISON"] = "Unison";
	DbsMastersCardType["TOKEN"] = "Token";
	DbsMastersCardType["ENERGY_MARKER"] = "Energy Marker";
	return DbsMastersCardType;
}({});
let DbsMastersEra = /* @__PURE__ */ function(DbsMastersEra) {
	DbsMastersEra["YOUNG_SON_GOKU_SAGA"] = "young_son_goku_saga";
	DbsMastersEra["PILAF_SAGA"] = "pilaf_saga";
	DbsMastersEra["FORTUNETELLER_BABA_SAGA"] = "fortuneteller_baba_saga";
	DbsMastersEra["KING_PICCOLO_SAGA"] = "king_piccolo_saga";
	DbsMastersEra["SAIYAN_SAGA"] = "saiyan_saga";
	DbsMastersEra["FRIEZA_SAGA"] = "frieza_saga";
	DbsMastersEra["GARLIC_JR_SAGA"] = "garlic_jr_saga";
	DbsMastersEra["ANDROID_13_SAGA"] = "android_13_saga";
	DbsMastersEra["ANDROID_CELL_SAGA"] = "android_cell_saga";
	DbsMastersEra["WORLD_MARTIAL_ARTS_TOURNAMENT_SAGA"] = "world_martial_arts_tournament_saga";
	DbsMastersEra["MAJIN_BUU_SAGA"] = "majin_buu_saga";
	DbsMastersEra["EVIL_WIZARD_BABIDI_SAGA"] = "evil_wizard_babidi_saga";
	DbsMastersEra["BATTLE_OF_GODS_SAGA"] = "battle_of_gods_saga";
	DbsMastersEra["RESURRECTION_F_SAGA"] = "resurrection_f_saga";
	DbsMastersEra["UNIVERSE_SURVIVAL_SAGA"] = "universe_survival_saga";
	DbsMastersEra["FUTURE_TRUNKS_SAGA"] = "future_trunks_saga";
	DbsMastersEra["DBS_BROLY_SAGA"] = "dbs_broly_saga";
	DbsMastersEra["DBS_SUPER_HERO_SAGA"] = "dbs_super_hero_saga";
	DbsMastersEra["DAIMA_SAGA"] = "daima_saga";
	DbsMastersEra["DARK_EMPIRE_SAGA"] = "dark_empire_saga";
	DbsMastersEra["DARK_DEMON_REALM_SAGA"] = "dark_demon_realm_saga";
	DbsMastersEra["PRISON_PLANET_SAGA"] = "prison_planet_saga";
	DbsMastersEra["SHADOW_DRAGON_SAGA"] = "shadow_dragon_saga";
	DbsMastersEra["BLACK_STAR_DRAGON_BALL_SAGA"] = "black_star_dragon_ball_saga";
	DbsMastersEra["BABY_SAGA"] = "baby_saga";
	DbsMastersEra["SUPER_17_SAGA"] = "super_17_saga";
	DbsMastersEra["BROLY_SAGA"] = "broly_saga";
	DbsMastersEra["COOLER_SAGA"] = "cooler_saga";
	DbsMastersEra["META_COOLER_SAGA"] = "meta_cooler_saga";
	DbsMastersEra["BARDOCK_SAGA"] = "bardock_saga";
	DbsMastersEra["TURLES_SAGA"] = "turles_saga";
	DbsMastersEra["LORD_SLUG_SAGA"] = "lord_slug_saga";
	DbsMastersEra["BOUJACK_SAGA"] = "boujack_saga";
	DbsMastersEra["JANEMBA_SAGA"] = "janemba_saga";
	DbsMastersEra["HIRUDEGARN_SAGA"] = "hirudegarn_saga";
	DbsMastersEra["CHAMPA_SAGA"] = "champa_saga";
	DbsMastersEra["ANOTHER_WORLD_BUDOKAI_SAGA"] = "another_world_budokai_saga";
	DbsMastersEra["DR_UIRO_SAGA"] = "dr_uiro_saga";
	DbsMastersEra["THE_PATH_TO_POWER_SAGA"] = "the_path_to_power_saga";
	DbsMastersEra["CHILLED_SAGA"] = "chilled_saga";
	DbsMastersEra["HATCHIYACK_SAGA"] = "hatchiyack_saga";
	DbsMastersEra["MYSTICAL_ADVENTURE_SAGA"] = "mystical_adventure_saga";
	DbsMastersEra["GALACTIC_PATROLMAN_SAGA"] = "galactic_patrolman_saga";
	DbsMastersEra["CURSE_OF_THE_BLOOD_RUBIES_SAGA"] = "curse_of_the_blood_rubies_saga";
	DbsMastersEra["SLEEPING_PRINCESS_IN_DEVILS_CASTLE_SAGA"] = "sleeping_princess_in_devils_castle_saga";
	DbsMastersEra["DRAGON_BALL_MINUS_SAGA"] = "dragon_ball_minus_saga";
	DbsMastersEra["SPECIAL"] = "special";
	return DbsMastersEra;
}({});

//#endregion
//#region src/lib/games/dbs-masters/dbs-masters.presets.ts
const dbsMastersListPresets = {
	Standard: {
		name: translatable("dbs-masters.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("dbs-masters.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [DbsMastersFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("dbs-masters.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("dbs-masters.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [DbsMastersFinish.STANDARD] },
			quantity: 4
		}]
	},
	Foil: {
		name: translatable("dbs-masters.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("dbs-masters.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [DbsMastersFinish.FOIL] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("dbs-masters.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("dbs-masters.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [{
			filters: { finish: [DbsMastersFinish.FOIL] },
			quantity: 4
		}]
	},
	"Master Set": {
		name: translatable("dbs-masters.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("dbs-masters.presets.masterset.description", "A complete set with one copy of each card in both standard and foil finishes"),
		matchers: [{
			filters: { finish: [DbsMastersFinish.STANDARD] },
			quantity: 1
		}, {
			filters: { finish: [DbsMastersFinish.FOIL] },
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/dbs-masters/dbs-masters.game.ts
const dbsMastersFinishValues = {
	Standard: { label: translatable("dbs-masters.attributes.finish.values.standard", "Standard") },
	Foil: { label: translatable("dbs-masters.attributes.finish.values.foil", "Foil") }
};
const dbsMastersFilters = z.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	finish: makeZodEnumFilter(DbsMastersFinish).optional(),
	finishes: makeZodEnumFilter(DbsMastersFinish).optional(),
	rarity: makeZodEnumFilter(DbsMastersRarity).optional(),
	color: makeZodEnumFilter(DbsMastersColor).optional(),
	type: makeZodEnumFilter(DbsMastersCardType).optional(),
	era: makeZodEnumFilter(DbsMastersEra).optional(),
	power: zodNumericFilter.optional(),
	comboPower: zodNumericFilter.optional()
});
const DbsMastersGameConfiguration = {
	name: "Dragon Ball Super: Masters",
	shortName: "DBS: Masters",
	slug: "dbs-masters",
	icon: {
		dark: "dbs-masters-icon.png",
		light: "dbs-masters-icon.png"
	},
	image: {
		dark: "dbs-masters.png",
		light: "dbs-masters.png"
	},
	attributes: {
		name: nameAttribute,
		rarity: enumAttribute(DbsMastersRarity, {
			entity: "variant",
			name: translatable("dbs-masters.attributes.rarity.name", "Rarity"),
			description: translatable("dbs-masters.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				common: { label: translatable("dbs-masters.attributes.rarity.values.common", "Common") },
				uncommon: { label: translatable("dbs-masters.attributes.rarity.values.uncommon", "Uncommon") },
				rare: { label: translatable("dbs-masters.attributes.rarity.values.rare", "Rare") },
				super_rare: { label: translatable("dbs-masters.attributes.rarity.values.super_rare", "Super Rare") },
				special_rare: { label: translatable("dbs-masters.attributes.rarity.values.special_rare", "Special Rare") },
				special_leader_rare: { label: translatable("dbs-masters.attributes.rarity.values.special_leader_rare", "Special Leader Rare") },
				concept_rare: { label: translatable("dbs-masters.attributes.rarity.values.concept_rare", "Concept Rare") },
				god_rare: { label: translatable("dbs-masters.attributes.rarity.values.god_rare", "God Rare") },
				secret_rare: { label: translatable("dbs-masters.attributes.rarity.values.secret_rare", "Secret Rare") },
				starter_rare: { label: translatable("dbs-masters.attributes.rarity.values.starter_rare", "Starter Rare") },
				campaign_rare: { label: translatable("dbs-masters.attributes.rarity.values.campaign_rare", "Campaign Rare") },
				expansion_rare: { label: translatable("dbs-masters.attributes.rarity.values.expansion_rare", "Expansion Rare") },
				promo: { label: translatable("dbs-masters.attributes.rarity.values.promo", "Promo") },
				giant_force_rare: { label: translatable("dbs-masters.attributes.rarity.values.giant_force_rare", "Giant Force Rare") },
				dragon_ball_rare: { label: translatable("dbs-masters.attributes.rarity.values.dragon_ball_rare", "Dragon Ball Rare") },
				duo_power_rare: { label: translatable("dbs-masters.attributes.rarity.values.duo_power_rare", "Duo Power Rare") },
				iconic_attack_rare: { label: translatable("dbs-masters.attributes.rarity.values.iconic_attack_rare", "Iconic Attack Rare") },
				destroyer_and_angel_rare: { label: translatable("dbs-masters.attributes.rarity.values.destroyer_and_angel_rare", "Destroyer & Angel Rare") },
				infinite_saiyan_rare: { label: translatable("dbs-masters.attributes.rarity.values.infinite_saiyan_rare", "Infinite Saiyan Rare") },
				son_gohan_rare: { label: translatable("dbs-masters.attributes.rarity.values.son_gohan_rare", "Son Gohan Rare") },
				reboot_leader_rare: { label: translatable("dbs-masters.attributes.rarity.values.reboot_leader_rare", "Reboot Leader Rare") },
				noble_hero_rare: { label: translatable("dbs-masters.attributes.rarity.values.noble_hero_rare", "Noble Hero Rare") },
				ignoble_villain_rare: { label: translatable("dbs-masters.attributes.rarity.values.ignoble_villain_rare", "Ignoble Villain Rare") },
				destruction_rare: { label: translatable("dbs-masters.attributes.rarity.values.destruction_rare", "Destruction Rare") },
				special_rare_signature: { label: translatable("dbs-masters.attributes.rarity.values.special_rare_signature", "Special Rare Signature") },
				feature_rare: { label: translatable("dbs-masters.attributes.rarity.values.feature_rare", "Feature Rare") },
				token: { label: translatable("dbs-masters.attributes.rarity.values.token", "Token") },
				merit: { label: translatable("dbs-masters.attributes.rarity.values.merit", "Merit") },
				none: { label: translatable("dbs-masters.attributes.rarity.values.none", "None") }
			}
		}),
		color: enumAttribute(DbsMastersColor, {
			entity: "card",
			name: translatable("dbs-masters.attributes.color.name", "Color"),
			description: translatable("dbs-masters.attributes.color.description", "The color of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				Red: { label: translatable("dbs-masters.attributes.color.values.red", "Red") },
				Blue: { label: translatable("dbs-masters.attributes.color.values.blue", "Blue") },
				Green: { label: translatable("dbs-masters.attributes.color.values.green", "Green") },
				Yellow: { label: translatable("dbs-masters.attributes.color.values.yellow", "Yellow") },
				Black: { label: translatable("dbs-masters.attributes.color.values.black", "Black") },
				White: { label: translatable("dbs-masters.attributes.color.values.white", "White") }
			}
		}),
		finishes: enumAttribute(DbsMastersFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("dbs-masters.attributes.finish.name", "Finish"),
			description: translatable("dbs-masters.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: dbsMastersFinishValues
		}),
		finish: enumAttribute(DbsMastersFinish, {
			entity: "inventory",
			name: translatable("dbs-masters.attributes.finish.name", "Finish"),
			description: translatable("dbs-masters.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: dbsMastersFinishValues
		}),
		printNumber: printNumberAttribute,
		type: enumAttribute(DbsMastersCardType, {
			entity: "card",
			name: translatable("dbs-masters.attributes.type.name", "Card Type"),
			description: translatable("dbs-masters.attributes.type.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[DbsMastersCardType.LEADER]: { label: translatable("dbs-masters.attributes.type.values.leader", "Leader") },
				[DbsMastersCardType.BATTLE]: { label: translatable("dbs-masters.attributes.type.values.battle", "Battle") },
				[DbsMastersCardType.EXTRA]: { label: translatable("dbs-masters.attributes.type.values.extra", "Extra") },
				[DbsMastersCardType.Z_BATTLE]: { label: translatable("dbs-masters.attributes.type.values.z_battle", "Z-Battle") },
				[DbsMastersCardType.Z_UNISON]: { label: translatable("dbs-masters.attributes.type.values.z_unison", "Z-Unison") },
				[DbsMastersCardType.Z_EXTRA]: { label: translatable("dbs-masters.attributes.type.values.z_extra", "Z-Extra") },
				[DbsMastersCardType.Z_LEADER]: { label: translatable("dbs-masters.attributes.type.values.z_leader", "Z-Leader") },
				[DbsMastersCardType.TOKEN]: { label: translatable("dbs-masters.attributes.type.values.token", "Token") },
				[DbsMastersCardType.ENERGY_MARKER]: { label: translatable("dbs-masters.attributes.type.values.energy_marker", "Energy Marker") },
				[DbsMastersCardType.UNISON]: { label: translatable("dbs-masters.attributes.type.values.unison", "Unison") }
			}
		}),
		power: {
			entity: "card",
			name: translatable("dbs-masters.attributes.power.name", "Power"),
			description: translatable("dbs-masters.attributes.power.description", "Power value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		comboPower: {
			entity: "card",
			name: translatable("dbs-masters.attributes.comboPower.name", "Combo Power"),
			description: translatable("dbs-masters.attributes.comboPower.description", "Combo power value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		cost: {
			entity: "card",
			name: translatable("dbs-masters.attributes.cost.name", "Cost"),
			description: translatable("dbs-masters.attributes.cost.description", "Energy cost to play the card"),
			type: "string",
			array: false
		},
		era: enumAttribute(DbsMastersEra, {
			entity: "card",
			name: translatable("dbs-masters.attributes.era.name", "Era"),
			description: translatable("dbs-masters.attributes.era.description", "The saga or era of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[DbsMastersEra.YOUNG_SON_GOKU_SAGA]: { label: translatable("dbs-masters.attributes.era.values.young_son_goku_saga", "Young Son Goku Saga") },
				[DbsMastersEra.PILAF_SAGA]: { label: translatable("dbs-masters.attributes.era.values.pilaf_saga", "Pilaf Saga") },
				[DbsMastersEra.FORTUNETELLER_BABA_SAGA]: { label: translatable("dbs-masters.attributes.era.values.fortuneteller_baba_saga", "Fortuneteller Baba Saga") },
				[DbsMastersEra.KING_PICCOLO_SAGA]: { label: translatable("dbs-masters.attributes.era.values.king_piccolo_saga", "King Piccolo Saga") },
				[DbsMastersEra.SAIYAN_SAGA]: { label: translatable("dbs-masters.attributes.era.values.saiyan_saga", "Saiyan Saga") },
				[DbsMastersEra.FRIEZA_SAGA]: { label: translatable("dbs-masters.attributes.era.values.frieza_saga", "Frieza Saga") },
				[DbsMastersEra.GARLIC_JR_SAGA]: { label: translatable("dbs-masters.attributes.era.values.garlic_jr_saga", "Garlic Jr. Saga") },
				[DbsMastersEra.ANDROID_13_SAGA]: { label: translatable("dbs-masters.attributes.era.values.android_13_saga", "Android 13 Saga") },
				[DbsMastersEra.ANDROID_CELL_SAGA]: { label: translatable("dbs-masters.attributes.era.values.android_cell_saga", "Android Cell Saga") },
				[DbsMastersEra.WORLD_MARTIAL_ARTS_TOURNAMENT_SAGA]: { label: translatable("dbs-masters.attributes.era.values.world_martial_arts_tournament_saga", "World Martial Arts Tournament Saga") },
				[DbsMastersEra.MAJIN_BUU_SAGA]: { label: translatable("dbs-masters.attributes.era.values.majin_buu_saga", "Majin Buu Saga") },
				[DbsMastersEra.EVIL_WIZARD_BABIDI_SAGA]: { label: translatable("dbs-masters.attributes.era.values.evil_wizard_babidi_saga", "The Evil Wizard Babidi Saga") },
				[DbsMastersEra.BATTLE_OF_GODS_SAGA]: { label: translatable("dbs-masters.attributes.era.values.battle_of_gods_saga", "Battle of Gods Saga") },
				[DbsMastersEra.RESURRECTION_F_SAGA]: { label: translatable("dbs-masters.attributes.era.values.resurrection_f_saga", "Resurrection 'F' Saga") },
				[DbsMastersEra.UNIVERSE_SURVIVAL_SAGA]: { label: translatable("dbs-masters.attributes.era.values.universe_survival_saga", "Universe Survival Saga") },
				[DbsMastersEra.FUTURE_TRUNKS_SAGA]: { label: translatable("dbs-masters.attributes.era.values.future_trunks_saga", "Future Trunks Saga") },
				[DbsMastersEra.DBS_BROLY_SAGA]: { label: translatable("dbs-masters.attributes.era.values.dbs_broly_saga", "DBS: Broly Saga") },
				[DbsMastersEra.DBS_SUPER_HERO_SAGA]: { label: translatable("dbs-masters.attributes.era.values.dbs_super_hero_saga", "DBS: SUPER HERO Saga") },
				[DbsMastersEra.DAIMA_SAGA]: { label: translatable("dbs-masters.attributes.era.values.daima_saga", "DAIMA Saga") },
				[DbsMastersEra.DARK_EMPIRE_SAGA]: { label: translatable("dbs-masters.attributes.era.values.dark_empire_saga", "Dark Empire Saga") },
				[DbsMastersEra.DARK_DEMON_REALM_SAGA]: { label: translatable("dbs-masters.attributes.era.values.dark_demon_realm_saga", "Dark Demon Realm Saga") },
				[DbsMastersEra.PRISON_PLANET_SAGA]: { label: translatable("dbs-masters.attributes.era.values.prison_planet_saga", "Prison Planet Saga") },
				[DbsMastersEra.SHADOW_DRAGON_SAGA]: { label: translatable("dbs-masters.attributes.era.values.shadow_dragon_saga", "Shadow Dragon Saga") },
				[DbsMastersEra.BLACK_STAR_DRAGON_BALL_SAGA]: { label: translatable("dbs-masters.attributes.era.values.black_star_dragon_ball_saga", "Black Star Dragon Ball Saga") },
				[DbsMastersEra.BABY_SAGA]: { label: translatable("dbs-masters.attributes.era.values.baby_saga", "Baby Saga") },
				[DbsMastersEra.SUPER_17_SAGA]: { label: translatable("dbs-masters.attributes.era.values.super_17_saga", "Super 17 Saga") },
				[DbsMastersEra.BROLY_SAGA]: { label: translatable("dbs-masters.attributes.era.values.broly_saga", "Broly Saga") },
				[DbsMastersEra.COOLER_SAGA]: { label: translatable("dbs-masters.attributes.era.values.cooler_saga", "Cooler Saga") },
				[DbsMastersEra.META_COOLER_SAGA]: { label: translatable("dbs-masters.attributes.era.values.meta_cooler_saga", "Meta-Cooler Saga") },
				[DbsMastersEra.BARDOCK_SAGA]: { label: translatable("dbs-masters.attributes.era.values.bardock_saga", "Bardock Saga") },
				[DbsMastersEra.TURLES_SAGA]: { label: translatable("dbs-masters.attributes.era.values.turles_saga", "Turles Saga") },
				[DbsMastersEra.LORD_SLUG_SAGA]: { label: translatable("dbs-masters.attributes.era.values.lord_slug_saga", "Lord Slug Saga") },
				[DbsMastersEra.BOUJACK_SAGA]: { label: translatable("dbs-masters.attributes.era.values.boujack_saga", "Boujack Saga") },
				[DbsMastersEra.JANEMBA_SAGA]: { label: translatable("dbs-masters.attributes.era.values.janemba_saga", "Janemba Saga") },
				[DbsMastersEra.HIRUDEGARN_SAGA]: { label: translatable("dbs-masters.attributes.era.values.hirudegarn_saga", "Hirudegarn Saga") },
				[DbsMastersEra.CHAMPA_SAGA]: { label: translatable("dbs-masters.attributes.era.values.champa_saga", "Champa Saga") },
				[DbsMastersEra.ANOTHER_WORLD_BUDOKAI_SAGA]: { label: translatable("dbs-masters.attributes.era.values.another_world_budokai_saga", "Another World Budokai Saga") },
				[DbsMastersEra.DR_UIRO_SAGA]: { label: translatable("dbs-masters.attributes.era.values.dr_uiro_saga", "Dr. Uiro Saga") },
				[DbsMastersEra.THE_PATH_TO_POWER_SAGA]: { label: translatable("dbs-masters.attributes.era.values.the_path_to_power_saga", "The Path to Power Saga") },
				[DbsMastersEra.CHILLED_SAGA]: { label: translatable("dbs-masters.attributes.era.values.chilled_saga", "Chilled Saga") },
				[DbsMastersEra.HATCHIYACK_SAGA]: { label: translatable("dbs-masters.attributes.era.values.hatchiyack_saga", "Hatchiyack Saga") },
				[DbsMastersEra.MYSTICAL_ADVENTURE_SAGA]: { label: translatable("dbs-masters.attributes.era.values.mystical_adventure_saga", "Mystical Adventure Saga") },
				[DbsMastersEra.GALACTIC_PATROLMAN_SAGA]: { label: translatable("dbs-masters.attributes.era.values.galactic_patrolman_saga", "Galactic Patrolman Saga") },
				[DbsMastersEra.CURSE_OF_THE_BLOOD_RUBIES_SAGA]: { label: translatable("dbs-masters.attributes.era.values.curse_of_the_blood_rubies_saga", "Curse of the Blood Rubies Saga") },
				[DbsMastersEra.SLEEPING_PRINCESS_IN_DEVILS_CASTLE_SAGA]: { label: translatable("dbs-masters.attributes.era.values.sleeping_princess_in_devils_castle_saga", "Sleeping Princess in Devil's Castle Saga") },
				[DbsMastersEra.DRAGON_BALL_MINUS_SAGA]: { label: translatable("dbs-masters.attributes.era.values.dragon_ball_minus_saga", "Dragon Ball Minus Saga") },
				[DbsMastersEra.SPECIAL]: { label: translatable("dbs-masters.attributes.era.values.special", "Special") }
			}
		}),
		specialTraits: {
			entity: "card",
			name: translatable("dbs-masters.attributes.specialTraits.name", "Special Traits"),
			description: translatable("dbs-masters.attributes.specialTraits.description", "The special traits of the card"),
			type: "string",
			array: true
		},
		character: {
			entity: "card",
			name: translatable("dbs-masters.attributes.character.name", "Character"),
			description: translatable("dbs-masters.attributes.character.description", "The character featured on the card"),
			type: "string",
			array: false
		},
		rulesText: {
			entity: "variant",
			name: translatable("dbs-masters.attributes.rulesText.name", "Rules Text"),
			description: translatable("dbs-masters.attributes.rulesText.description", "The rules text of the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"rarity",
			"color"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"type",
			"power",
			"comboPower",
			"cost",
			"era"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText"],
		statistics: [
			"rarity",
			"color",
			"finish",
			"type",
			"power",
			"comboPower",
			"cost",
			"era",
			"character",
			"specialTraits"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: dbsMastersListPresets,
	finishHierarchy: [CardFinish.STANDARD, CardFinish.FOIL],
	basicAttributes: ["color", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Tcgplayer, SupportedMarketplace.Cardmarket],
	vendorIds: [cardmarketVendorId, tcgplayerVendorId],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "BT1-001",
		cardName: "Son Goku",
		expansion: "Galactic Battle",
		expansionCode: "DBS-B01",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/drakerion/drakerion.types.ts
let DrakerionRarity = /* @__PURE__ */ function(DrakerionRarity) {
	DrakerionRarity["Common"] = "Common";
	DrakerionRarity["Rare"] = "Rare";
	DrakerionRarity["Starter"] = "Starter";
	DrakerionRarity["Unique"] = "Unique";
	DrakerionRarity["Promo"] = "Promo";
	return DrakerionRarity;
}({});
let DrakerionFinish = /* @__PURE__ */ function(DrakerionFinish) {
	DrakerionFinish["Standard"] = "Standard";
	DrakerionFinish["Foil"] = "Foil";
	DrakerionFinish["Gold"] = "Gold";
	DrakerionFinish["Zarimoth"] = "Zarimoth";
	return DrakerionFinish;
}({});
let DrakerionType = /* @__PURE__ */ function(DrakerionType) {
	DrakerionType["Character"] = "Character";
	DrakerionType["Attachment"] = "Attachment";
	DrakerionType["Event"] = "Event";
	DrakerionType["City"] = "City";
	DrakerionType["Banner"] = "Banner";
	DrakerionType["Maneuver"] = "Maneuver";
	return DrakerionType;
}({});
let DrakerionKingdom = /* @__PURE__ */ function(DrakerionKingdom) {
	DrakerionKingdom["Lokmar"] = "Lokmar";
	DrakerionKingdom["GilEstel"] = "Gil Estel";
	DrakerionKingdom["Wasteland"] = "Wasteland";
	DrakerionKingdom["Tyraslin"] = "Tyraslin";
	DrakerionKingdom["Kartej"] = "Kartej";
	DrakerionKingdom["Neutral"] = "Neutral";
	return DrakerionKingdom;
}({});
let DrakerionRequiresBanner = /* @__PURE__ */ function(DrakerionRequiresBanner) {
	DrakerionRequiresBanner["RequiresBanner"] = "RequiresBanner";
	DrakerionRequiresBanner["NoRequirement"] = "NoRequirement";
	return DrakerionRequiresBanner;
}({});
let DrakerionRequiresCity = /* @__PURE__ */ function(DrakerionRequiresCity) {
	DrakerionRequiresCity["RequiresCity"] = "RequiresCity";
	DrakerionRequiresCity["NoRequirement"] = "NoRequirement";
	return DrakerionRequiresCity;
}({});
let DrakerionTrait = /* @__PURE__ */ function(DrakerionTrait) {
	DrakerionTrait["Acolyte"] = "Acolyte";
	DrakerionTrait["Apprentice"] = "Apprentice";
	DrakerionTrait["Archer"] = "Archer";
	DrakerionTrait["Artisan"] = "Artisan";
	DrakerionTrait["Artist"] = "Artist";
	DrakerionTrait["Bodyguard"] = "Bodyguard";
	DrakerionTrait["Captain"] = "Captain";
	DrakerionTrait["Crossbowman"] = "Crossbowman";
	DrakerionTrait["Healer"] = "Healer";
	DrakerionTrait["Horserider"] = "Horserider";
	DrakerionTrait["Knight"] = "Knight";
	DrakerionTrait["Merchant"] = "Merchant";
	DrakerionTrait["Messenger"] = "Messenger";
	DrakerionTrait["Necromancer"] = "Necromancer";
	DrakerionTrait["Priest"] = "Priest";
	DrakerionTrait["Priestess"] = "Priestess";
	DrakerionTrait["Recruiter"] = "Recruiter";
	DrakerionTrait["ScalesRider"] = "Scales Rider";
	DrakerionTrait["Scavenger"] = "Scavenger";
	DrakerionTrait["Scholar"] = "Scholar";
	DrakerionTrait["Scout"] = "Scout";
	DrakerionTrait["Squire"] = "Squire";
	DrakerionTrait["Demon"] = "Demon";
	DrakerionTrait["Dragon"] = "Dragon";
	DrakerionTrait["Dwarf"] = "Dwarf";
	DrakerionTrait["Elf"] = "Elf";
	DrakerionTrait["Garuda"] = "Garuda";
	DrakerionTrait["Ghoul"] = "Ghoul";
	DrakerionTrait["Golem"] = "Golem";
	DrakerionTrait["Hippogriff"] = "Hippogriff";
	DrakerionTrait["Hound"] = "Hound";
	DrakerionTrait["Lilith"] = "Lilith";
	DrakerionTrait["Skeleton"] = "Skeleton";
	DrakerionTrait["Strigoi"] = "Strigoï";
	DrakerionTrait["Succubus"] = "Succubus";
	DrakerionTrait["Funestus"] = "Funestus";
	DrakerionTrait["Incubus"] = "Incubus";
	DrakerionTrait["Alania"] = "Alania";
	DrakerionTrait["AutumnGrove"] = "Autumn Grove";
	DrakerionTrait["Blood"] = "Blood";
	DrakerionTrait["Clan"] = "Clan";
	DrakerionTrait["CrimsonGuard"] = "Crimson Guard";
	DrakerionTrait["CrownCircle"] = "Crown Circle";
	DrakerionTrait["Dyad"] = "Dyad";
	DrakerionTrait["EdgeCircle"] = "Edge Circle";
	DrakerionTrait["ElderCouncil"] = "Elder Council";
	DrakerionTrait["PrimeLegion"] = "Prime Legion";
	DrakerionTrait["SummerGrove"] = "Summer Grove";
	DrakerionTrait["Tyraslin"] = "Tyraslin";
	DrakerionTrait["VassalCircle"] = "Vassal Circle";
	DrakerionTrait["VonKringstein"] = "Von Kringstein";
	DrakerionTrait["Avatar"] = "Avatar";
	DrakerionTrait["BannerBearer"] = "Banner-Bearer";
	DrakerionTrait["Elite"] = "Elite";
	DrakerionTrait["Exiled"] = "Exiled";
	DrakerionTrait["Faceless"] = "Faceless";
	DrakerionTrait["Founder"] = "Founder";
	DrakerionTrait["General"] = "General";
	DrakerionTrait["King"] = "King";
	DrakerionTrait["Legend"] = "Legend";
	DrakerionTrait["Master"] = "Master";
	DrakerionTrait["Noble"] = "Noble";
	DrakerionTrait["Prince"] = "Prince";
	DrakerionTrait["Ambush"] = "Ambush";
	DrakerionTrait["Infiltration"] = "Infiltration";
	DrakerionTrait["Invocation"] = "Invocation";
	DrakerionTrait["Roaming"] = "Roaming";
	DrakerionTrait["Sabotage"] = "Sabotage";
	DrakerionTrait["Spying"] = "Spying";
	DrakerionTrait["Ancestral"] = "Ancestral";
	DrakerionTrait["Arcane"] = "Arcane";
	DrakerionTrait["Corruption"] = "Corruption";
	DrakerionTrait["Dread"] = "Dread";
	DrakerionTrait["Elemental"] = "Elemental";
	DrakerionTrait["Exhumation"] = "Exhumation";
	DrakerionTrait["Hellios"] = "Hellios";
	DrakerionTrait["Hunt"] = "Hunt";
	DrakerionTrait["Illusion"] = "Illusion";
	DrakerionTrait["Moonlight"] = "Moonlight";
	DrakerionTrait["Nature"] = "Nature";
	DrakerionTrait["Obsidian"] = "Obsidian";
	DrakerionTrait["Obscur"] = "Obscur";
	DrakerionTrait["Portal"] = "Portal";
	DrakerionTrait["Possessed"] = "Possessed";
	DrakerionTrait["Ritual"] = "Ritual";
	DrakerionTrait["Ruins"] = "Ruins";
	DrakerionTrait["RuinsSeeker"] = "Ruins Seeker";
	DrakerionTrait["Shadow"] = "Shadow";
	DrakerionTrait["Skill"] = "Skill";
	DrakerionTrait["Song"] = "Song";
	DrakerionTrait["Spell"] = "Spell";
	DrakerionTrait["State"] = "State";
	DrakerionTrait["Tower"] = "Tower";
	DrakerionTrait["Vindicare"] = "Vindicare";
	DrakerionTrait["Void"] = "Void";
	DrakerionTrait["Weather"] = "Weather";
	DrakerionTrait["Armory"] = "Armory";
	DrakerionTrait["Army"] = "Army";
	DrakerionTrait["Battle"] = "Battle";
	DrakerionTrait["Bow"] = "Bow";
	DrakerionTrait["Cemetery"] = "Cemetery";
	DrakerionTrait["Diplomacy"] = "Diplomacy";
	DrakerionTrait["Forest"] = "Forest";
	DrakerionTrait["Fortress"] = "Fortress";
	DrakerionTrait["Heal"] = "Heal";
	DrakerionTrait["Horse"] = "Horse";
	DrakerionTrait["Item"] = "Item";
	DrakerionTrait["Mount"] = "Mount";
	DrakerionTrait["Mountain"] = "Mountain";
	DrakerionTrait["Necropolis"] = "Necropolis";
	DrakerionTrait["North"] = "North";
	DrakerionTrait["Object"] = "Object";
	DrakerionTrait["Order"] = "Order";
	DrakerionTrait["Reputation"] = "Reputation";
	DrakerionTrait["Shield"] = "Shield";
	DrakerionTrait["South"] = "South";
	DrakerionTrait["Stable"] = "Stable";
	DrakerionTrait["Weapon"] = "Weapon";
	return DrakerionTrait;
}({});

//#endregion
//#region src/lib/games/drakerion/drakerion.presets.ts
const drakerionListPresets = {
	Standard: {
		name: translatable("drakerion.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("drakerion.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [DrakerionFinish.Standard] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("drakerion.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("drakerion.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [DrakerionFinish.Standard] },
			quantity: 3
		}]
	},
	Foil: {
		name: translatable("drakerion.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("drakerion.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [DrakerionFinish.Foil] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("drakerion.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("drakerion.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [{
			filters: { finish: [DrakerionFinish.Foil] },
			quantity: 3
		}]
	},
	"Master Set": {
		name: translatable("drakerion.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("drakerion.presets.masterset.description", "A complete set with one copy of each card in all finishes"),
		matchers: [{
			filters: {},
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/drakerion/drakerion.game.ts
/**
* Shared finish values for Drakerion - used by both finishes and finish attributes
*/
const drakerionFinishValues = {
	[DrakerionFinish.Standard]: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	[DrakerionFinish.Foil]: { label: translatable("global.attributes.finish.values.foil", "Foil") },
	[DrakerionFinish.Gold]: { label: translatable("drakerion.attributes.finish.values.gold", "Gold") },
	[DrakerionFinish.Zarimoth]: { label: translatable("drakerion.attributes.finish.values.zarimoth", "Zarimoth") }
};
const drakerionFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(DrakerionRarity).optional(),
	finish: makeZodEnumFilter(DrakerionFinish).optional(),
	finishes: makeZodEnumFilter(DrakerionFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	type: makeZodEnumFilter(DrakerionType).optional(),
	kingdom: makeZodEnumFilter(DrakerionKingdom).optional(),
	cost: zodNumericFilter.optional(),
	attack: zodNumericFilter.optional(),
	riposte: zodNumericFilter.optional(),
	health: zodNumericFilter.optional(),
	rangedAttack: zodNumericFilter.optional(),
	prestige: zodNumericFilter.optional(),
	nbTactic: zodNumericFilter.optional(),
	nbSupply: zodNumericFilter.optional(),
	goldGain: zodNumericFilter.optional(),
	drawCount: zodNumericFilter.optional()
});
const DrakerionGameConfiguration = {
	name: "Drakerion",
	shortName: "Drakerion",
	slug: "drakerion",
	icon: {
		dark: "drakerion-icon.png",
		light: "drakerion-icon.png"
	},
	image: {
		dark: "drakerion.png",
		light: "drakerion.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(DrakerionFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: drakerionFinishValues
		}),
		finish: enumAttribute(DrakerionFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: drakerionFinishValues
		}),
		rarity: enumAttribute(DrakerionRarity, {
			entity: "variant",
			name: translatable("global.attributes.rarity.name", "Rarity"),
			description: translatable("global.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[DrakerionRarity.Common]: { label: translatable("drakerion.attributes.rarity.values.common", "Common") },
				[DrakerionRarity.Rare]: { label: translatable("drakerion.attributes.rarity.values.rare", "Rare") },
				[DrakerionRarity.Starter]: { label: translatable("drakerion.attributes.rarity.values.starter", "Starter") },
				[DrakerionRarity.Unique]: { label: translatable("drakerion.attributes.rarity.values.unique", "Unique") },
				[DrakerionRarity.Promo]: { label: translatable("drakerion.attributes.rarity.values.promo", "Promo") }
			}
		}),
		printNumber: printNumberAttribute,
		type: enumAttribute(DrakerionType, {
			entity: "card",
			name: translatable("drakerion.attributes.type.name", "Card Type"),
			description: translatable("drakerion.attributes.type.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[DrakerionType.Character]: { label: translatable("drakerion.attributes.type.values.character", "Character") },
				[DrakerionType.Attachment]: { label: translatable("drakerion.attributes.type.values.attachment", "Attachment") },
				[DrakerionType.Event]: { label: translatable("drakerion.attributes.type.values.event", "Event") },
				[DrakerionType.City]: { label: translatable("drakerion.attributes.type.values.city", "City") },
				[DrakerionType.Banner]: { label: translatable("drakerion.attributes.type.values.banner", "Banner") },
				[DrakerionType.Maneuver]: { label: translatable("drakerion.attributes.type.values.maneuver", "Maneuver") }
			}
		}),
		kingdom: enumAttribute(DrakerionKingdom, {
			entity: "card",
			name: translatable("drakerion.attributes.kingdom.name", "Kingdom"),
			description: translatable("drakerion.attributes.kingdom.description", "The kingdom the card belongs to"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[DrakerionKingdom.Lokmar]: {
					label: translatable("drakerion.attributes.kingdom.values.lokmar", "Lokmar"),
					image: "/images/drakerion/kingdoms/lokmar.png"
				},
				[DrakerionKingdom.GilEstel]: {
					label: translatable("drakerion.attributes.kingdom.values.gilEstel", "Gil Estel"),
					image: "/images/drakerion/kingdoms/gil-estel.png"
				},
				[DrakerionKingdom.Wasteland]: {
					label: translatable("drakerion.attributes.kingdom.values.wasteland", "Wasteland"),
					image: "/images/drakerion/kingdoms/wasteland.png"
				},
				[DrakerionKingdom.Tyraslin]: {
					label: translatable("drakerion.attributes.kingdom.values.tyraslin", "Tyraslin"),
					image: "/images/drakerion/kingdoms/tyraslin.png"
				},
				[DrakerionKingdom.Kartej]: {
					label: translatable("drakerion.attributes.kingdom.values.kartej", "Kartej"),
					image: "/images/drakerion/kingdoms/kartej.png"
				},
				[DrakerionKingdom.Neutral]: {
					label: translatable("drakerion.attributes.kingdom.values.neutral", "Neutral"),
					image: "/images/drakerion/kingdoms/neutral.png"
				}
			}
		}),
		traits: enumAttribute(DrakerionTrait, {
			entity: "card",
			name: translatable("drakerion.attributes.traits.name", "Traits"),
			description: translatable("drakerion.attributes.traits.description", "Card traits and subtypes"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[DrakerionTrait.Acolyte]: { label: translatable("drakerion.attributes.traits.values.acolyte", "Acolyte") },
				[DrakerionTrait.Apprentice]: { label: translatable("drakerion.attributes.traits.values.apprentice", "Apprentice") },
				[DrakerionTrait.Archer]: { label: translatable("drakerion.attributes.traits.values.archer", "Archer") },
				[DrakerionTrait.Artisan]: { label: translatable("drakerion.attributes.traits.values.artisan", "Artisan") },
				[DrakerionTrait.Artist]: { label: translatable("drakerion.attributes.traits.values.artist", "Artist") },
				[DrakerionTrait.Bodyguard]: { label: translatable("drakerion.attributes.traits.values.bodyguard", "Bodyguard") },
				[DrakerionTrait.Captain]: { label: translatable("drakerion.attributes.traits.values.captain", "Captain") },
				[DrakerionTrait.Crossbowman]: { label: translatable("drakerion.attributes.traits.values.crossbowman", "Crossbowman") },
				[DrakerionTrait.Healer]: { label: translatable("drakerion.attributes.traits.values.healer", "Healer") },
				[DrakerionTrait.Horserider]: { label: translatable("drakerion.attributes.traits.values.horseRider", "Horserider") },
				[DrakerionTrait.Knight]: { label: translatable("drakerion.attributes.traits.values.knight", "Knight") },
				[DrakerionTrait.Merchant]: { label: translatable("drakerion.attributes.traits.values.merchant", "Merchant") },
				[DrakerionTrait.Messenger]: { label: translatable("drakerion.attributes.traits.values.messenger", "Messenger") },
				[DrakerionTrait.Necromancer]: { label: translatable("drakerion.attributes.traits.values.necromancer", "Necromancer") },
				[DrakerionTrait.Priest]: { label: translatable("drakerion.attributes.traits.values.priest", "Priest") },
				[DrakerionTrait.Priestess]: { label: translatable("drakerion.attributes.traits.values.priestess", "Priestess") },
				[DrakerionTrait.Recruiter]: { label: translatable("drakerion.attributes.traits.values.recruiter", "Recruiter") },
				[DrakerionTrait.ScalesRider]: { label: translatable("drakerion.attributes.traits.values.scalesRider", "Scales Rider") },
				[DrakerionTrait.Scavenger]: { label: translatable("drakerion.attributes.traits.values.scavenger", "Scavenger") },
				[DrakerionTrait.Scholar]: { label: translatable("drakerion.attributes.traits.values.scholar", "Scholar") },
				[DrakerionTrait.Scout]: { label: translatable("drakerion.attributes.traits.values.scout", "Scout") },
				[DrakerionTrait.Squire]: { label: translatable("drakerion.attributes.traits.values.squire", "Squire") },
				[DrakerionTrait.Demon]: { label: translatable("drakerion.attributes.traits.values.demon", "Demon") },
				[DrakerionTrait.Dragon]: { label: translatable("drakerion.attributes.traits.values.dragon", "Dragon") },
				[DrakerionTrait.Dwarf]: { label: translatable("drakerion.attributes.traits.values.dwarf", "Dwarf") },
				[DrakerionTrait.Elf]: { label: translatable("drakerion.attributes.traits.values.elf", "Elf") },
				[DrakerionTrait.Garuda]: { label: translatable("drakerion.attributes.traits.values.garuda", "Garuda") },
				[DrakerionTrait.Ghoul]: { label: translatable("drakerion.attributes.traits.values.ghoul", "Ghoul") },
				[DrakerionTrait.Golem]: { label: translatable("drakerion.attributes.traits.values.golem", "Golem") },
				[DrakerionTrait.Hippogriff]: { label: translatable("drakerion.attributes.traits.values.hippogriff", "Hippogriff") },
				[DrakerionTrait.Hound]: { label: translatable("drakerion.attributes.traits.values.hound", "Hound") },
				[DrakerionTrait.Lilith]: { label: translatable("drakerion.attributes.traits.values.lilith", "Lilith") },
				[DrakerionTrait.Skeleton]: { label: translatable("drakerion.attributes.traits.values.skeleton", "Skeleton") },
				[DrakerionTrait.Strigoi]: { label: translatable("drakerion.attributes.traits.values.strigoi", "Strigoï") },
				[DrakerionTrait.Succubus]: { label: translatable("drakerion.attributes.traits.values.succubus", "Succubus") },
				[DrakerionTrait.Funestus]: { label: translatable("drakerion.attributes.traits.values.funestus", "Funestus") },
				[DrakerionTrait.Incubus]: { label: translatable("drakerion.attributes.traits.values.incubus", "Incubus") },
				[DrakerionTrait.Alania]: { label: translatable("drakerion.attributes.traits.values.alania", "Alania") },
				[DrakerionTrait.AutumnGrove]: { label: translatable("drakerion.attributes.traits.values.autumnGrove", "Autumn Grove") },
				[DrakerionTrait.Blood]: { label: translatable("drakerion.attributes.traits.values.blood", "Blood") },
				[DrakerionTrait.Clan]: { label: translatable("drakerion.attributes.traits.values.clan", "Clan") },
				[DrakerionTrait.CrimsonGuard]: { label: translatable("drakerion.attributes.traits.values.crimsonGuard", "Crimson Guard") },
				[DrakerionTrait.CrownCircle]: { label: translatable("drakerion.attributes.traits.values.crownCircle", "Crown Circle") },
				[DrakerionTrait.Dyad]: { label: translatable("drakerion.attributes.traits.values.dyad", "Dyad") },
				[DrakerionTrait.EdgeCircle]: { label: translatable("drakerion.attributes.traits.values.edgeCircle", "Edge Circle") },
				[DrakerionTrait.ElderCouncil]: { label: translatable("drakerion.attributes.traits.values.elderCouncil", "Elder Council") },
				[DrakerionTrait.PrimeLegion]: { label: translatable("drakerion.attributes.traits.values.primeLegion", "Prime Legion") },
				[DrakerionTrait.SummerGrove]: { label: translatable("drakerion.attributes.traits.values.summerGrove", "Summer Grove") },
				[DrakerionTrait.Tyraslin]: { label: translatable("drakerion.attributes.traits.values.tyraslin", "Tyraslin") },
				[DrakerionTrait.VassalCircle]: { label: translatable("drakerion.attributes.traits.values.vassalCircle", "Vassal Circle") },
				[DrakerionTrait.VonKringstein]: { label: translatable("drakerion.attributes.traits.values.vonKringstein", "Von Kringstein") },
				[DrakerionTrait.Avatar]: { label: translatable("drakerion.attributes.traits.values.avatar", "Avatar") },
				[DrakerionTrait.BannerBearer]: { label: translatable("drakerion.attributes.traits.values.bannerBearer", "Banner-Bearer") },
				[DrakerionTrait.Elite]: { label: translatable("drakerion.attributes.traits.values.elite", "Elite") },
				[DrakerionTrait.Exiled]: { label: translatable("drakerion.attributes.traits.values.exiled", "Exiled") },
				[DrakerionTrait.Faceless]: { label: translatable("drakerion.attributes.traits.values.faceless", "Faceless") },
				[DrakerionTrait.Founder]: { label: translatable("drakerion.attributes.traits.values.founder", "Founder") },
				[DrakerionTrait.General]: { label: translatable("drakerion.attributes.traits.values.general", "General") },
				[DrakerionTrait.King]: { label: translatable("drakerion.attributes.traits.values.king", "King") },
				[DrakerionTrait.Legend]: { label: translatable("drakerion.attributes.traits.values.legend", "Legend") },
				[DrakerionTrait.Master]: { label: translatable("drakerion.attributes.traits.values.master", "Master") },
				[DrakerionTrait.Noble]: { label: translatable("drakerion.attributes.traits.values.noble", "Noble") },
				[DrakerionTrait.Prince]: { label: translatable("drakerion.attributes.traits.values.prince", "Prince") },
				[DrakerionTrait.Ambush]: { label: translatable("drakerion.attributes.traits.values.ambush", "Ambush") },
				[DrakerionTrait.Infiltration]: { label: translatable("drakerion.attributes.traits.values.infiltration", "Infiltration") },
				[DrakerionTrait.Invocation]: { label: translatable("drakerion.attributes.traits.values.invocation", "Invocation") },
				[DrakerionTrait.Roaming]: { label: translatable("drakerion.attributes.traits.values.roaming", "Roaming") },
				[DrakerionTrait.Sabotage]: { label: translatable("drakerion.attributes.traits.values.sabotage", "Sabotage") },
				[DrakerionTrait.Spying]: { label: translatable("drakerion.attributes.traits.values.spying", "Spying") },
				[DrakerionTrait.Ancestral]: { label: translatable("drakerion.attributes.traits.values.ancestral", "Ancestral") },
				[DrakerionTrait.Arcane]: { label: translatable("drakerion.attributes.traits.values.arcane", "Arcane") },
				[DrakerionTrait.Corruption]: { label: translatable("drakerion.attributes.traits.values.corruption", "Corruption") },
				[DrakerionTrait.Dread]: { label: translatable("drakerion.attributes.traits.values.dread", "Dread") },
				[DrakerionTrait.Elemental]: { label: translatable("drakerion.attributes.traits.values.elemental", "Elemental") },
				[DrakerionTrait.Exhumation]: { label: translatable("drakerion.attributes.traits.values.exhumation", "Exhumation") },
				[DrakerionTrait.Hellios]: { label: translatable("drakerion.attributes.traits.values.hellios", "Hellios") },
				[DrakerionTrait.Hunt]: { label: translatable("drakerion.attributes.traits.values.hunt", "Hunt") },
				[DrakerionTrait.Illusion]: { label: translatable("drakerion.attributes.traits.values.illusion", "Illusion") },
				[DrakerionTrait.Moonlight]: { label: translatable("drakerion.attributes.traits.values.moonlight", "Moonlight") },
				[DrakerionTrait.Nature]: { label: translatable("drakerion.attributes.traits.values.nature", "Nature") },
				[DrakerionTrait.Obsidian]: { label: translatable("drakerion.attributes.traits.values.obsidian", "Obsidian") },
				[DrakerionTrait.Obscur]: { label: translatable("drakerion.attributes.traits.values.obscur", "Obscur") },
				[DrakerionTrait.Portal]: { label: translatable("drakerion.attributes.traits.values.portal", "Portal") },
				[DrakerionTrait.Possessed]: { label: translatable("drakerion.attributes.traits.values.possessed", "Possessed") },
				[DrakerionTrait.Ritual]: { label: translatable("drakerion.attributes.traits.values.ritual", "Ritual") },
				[DrakerionTrait.Ruins]: { label: translatable("drakerion.attributes.traits.values.ruins", "Ruins") },
				[DrakerionTrait.RuinsSeeker]: { label: translatable("drakerion.attributes.traits.values.ruinsSeeker", "Ruins Seeker") },
				[DrakerionTrait.Shadow]: { label: translatable("drakerion.attributes.traits.values.shadow", "Shadow") },
				[DrakerionTrait.Skill]: { label: translatable("drakerion.attributes.traits.values.skill", "Skill") },
				[DrakerionTrait.Song]: { label: translatable("drakerion.attributes.traits.values.song", "Song") },
				[DrakerionTrait.Spell]: { label: translatable("drakerion.attributes.traits.values.spell", "Spell") },
				[DrakerionTrait.State]: { label: translatable("drakerion.attributes.traits.values.state", "State") },
				[DrakerionTrait.Tower]: { label: translatable("drakerion.attributes.traits.values.tower", "Tower") },
				[DrakerionTrait.Vindicare]: { label: translatable("drakerion.attributes.traits.values.vindicare", "Vindicare") },
				[DrakerionTrait.Void]: { label: translatable("drakerion.attributes.traits.values.void", "Void") },
				[DrakerionTrait.Weather]: { label: translatable("drakerion.attributes.traits.values.weather", "Weather") },
				[DrakerionTrait.Armory]: { label: translatable("drakerion.attributes.traits.values.armory", "Armory") },
				[DrakerionTrait.Army]: { label: translatable("drakerion.attributes.traits.values.army", "Army") },
				[DrakerionTrait.Battle]: { label: translatable("drakerion.attributes.traits.values.battle", "Battle") },
				[DrakerionTrait.Bow]: { label: translatable("drakerion.attributes.traits.values.bow", "Bow") },
				[DrakerionTrait.Cemetery]: { label: translatable("drakerion.attributes.traits.values.cemetery", "Cemetery") },
				[DrakerionTrait.Diplomacy]: { label: translatable("drakerion.attributes.traits.values.diplomacy", "Diplomacy") },
				[DrakerionTrait.Forest]: { label: translatable("drakerion.attributes.traits.values.forest", "Forest") },
				[DrakerionTrait.Fortress]: { label: translatable("drakerion.attributes.traits.values.fortress", "Fortress") },
				[DrakerionTrait.Heal]: { label: translatable("drakerion.attributes.traits.values.heal", "Heal") },
				[DrakerionTrait.Horse]: { label: translatable("drakerion.attributes.traits.values.horse", "Horse") },
				[DrakerionTrait.Item]: { label: translatable("drakerion.attributes.traits.values.item", "Item") },
				[DrakerionTrait.Mount]: { label: translatable("drakerion.attributes.traits.values.mount", "Mount") },
				[DrakerionTrait.Mountain]: { label: translatable("drakerion.attributes.traits.values.mountain", "Mountain") },
				[DrakerionTrait.Necropolis]: { label: translatable("drakerion.attributes.traits.values.necropolis", "Necropolis") },
				[DrakerionTrait.North]: { label: translatable("drakerion.attributes.traits.values.north", "North") },
				[DrakerionTrait.Object]: { label: translatable("drakerion.attributes.traits.values.object", "Object") },
				[DrakerionTrait.Order]: { label: translatable("drakerion.attributes.traits.values.order", "Order") },
				[DrakerionTrait.Reputation]: { label: translatable("drakerion.attributes.traits.values.reputation", "Reputation") },
				[DrakerionTrait.Shield]: { label: translatable("drakerion.attributes.traits.values.shield", "Shield") },
				[DrakerionTrait.South]: { label: translatable("drakerion.attributes.traits.values.south", "South") },
				[DrakerionTrait.Stable]: { label: translatable("drakerion.attributes.traits.values.stable", "Stable") },
				[DrakerionTrait.Weapon]: { label: translatable("drakerion.attributes.traits.values.weapon", "Weapon") }
			}
		}),
		cost: {
			entity: "card",
			name: translatable("drakerion.attributes.cost.name", "Cost"),
			description: translatable("drakerion.attributes.cost.description", "Cost to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		attack: {
			entity: "card",
			name: translatable("drakerion.attributes.attack.name", "Attack"),
			description: translatable("drakerion.attributes.attack.description", "Attack power of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		riposte: {
			entity: "card",
			name: translatable("drakerion.attributes.riposte.name", "Riposte"),
			description: translatable("drakerion.attributes.riposte.description", "Riposte value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		health: {
			entity: "card",
			name: translatable("drakerion.attributes.health.name", "Health"),
			description: translatable("drakerion.attributes.health.description", "Health points of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		rangedAttack: {
			entity: "card",
			name: translatable("drakerion.attributes.rangedAttack.name", "Ranged Attack"),
			description: translatable("drakerion.attributes.rangedAttack.description", "Ranged attack power of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		prestige: {
			entity: "card",
			name: translatable("drakerion.attributes.prestige.name", "Prestige"),
			description: translatable("drakerion.attributes.prestige.description", "Prestige points of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		nbTactic: {
			entity: "card",
			name: translatable("drakerion.attributes.nbTactic.name", "Tactics"),
			description: translatable("drakerion.attributes.nbTactic.description", "Number of tactics"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		nbSupply: {
			entity: "card",
			name: translatable("drakerion.attributes.nbSupply.name", "Supplies"),
			description: translatable("drakerion.attributes.nbSupply.description", "Number of supplies"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		goldGain: {
			entity: "card",
			name: translatable("drakerion.attributes.goldGain.name", "Gold Gain"),
			description: translatable("drakerion.attributes.goldGain.description", "Gold gained from the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		drawCount: {
			entity: "card",
			name: translatable("drakerion.attributes.drawCount.name", "Draw Count"),
			description: translatable("drakerion.attributes.drawCount.description", "Number of cards drawn"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		keywords: {
			entity: "card",
			name: translatable("drakerion.attributes.keywords.name", "Keywords"),
			description: translatable("drakerion.attributes.keywords.description", "Card keywords and abilities"),
			type: "string",
			array: true
		},
		subtitle: {
			entity: "card",
			name: translatable("drakerion.attributes.subtitle.name", "Subtitle"),
			description: translatable("drakerion.attributes.subtitle.description", "Card subtitle"),
			type: "string",
			array: false
		},
		requiresBanner: enumAttribute(DrakerionRequiresBanner, {
			entity: "card",
			name: translatable("drakerion.attributes.requiresBanner.name", "Requires Banner"),
			description: translatable("drakerion.attributes.requiresBanner.description", "Whether the card requires a banner"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[DrakerionRequiresBanner.RequiresBanner]: { label: translatable("global.common.yes", "Yes") },
				[DrakerionRequiresBanner.NoRequirement]: { label: translatable("global.common.no", "No") }
			}
		}),
		requiresCity: enumAttribute(DrakerionRequiresCity, {
			entity: "card",
			name: translatable("drakerion.attributes.requiresCity.name", "Requires City"),
			description: translatable("drakerion.attributes.requiresCity.description", "Whether the card requires a city"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[DrakerionRequiresCity.RequiresCity]: { label: translatable("global.common.yes", "Yes") },
				[DrakerionRequiresCity.NoRequirement]: { label: translatable("global.common.no", "No") }
			}
		}),
		rulesText: {
			entity: "variant",
			name: translatable("drakerion.attributes.rulesText.name", "Rules Text"),
			description: translatable("drakerion.attributes.rulesText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"finishes",
			"type",
			"kingdom"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"traits",
			"cost",
			"attack",
			"riposte",
			"health",
			"rangedAttack",
			"prestige",
			"nbTactic",
			"nbSupply",
			"goldGain",
			"drawCount"
		]
	}],
	productDetailsOrder: {
		details: [
			"rulesText",
			"keywords",
			"traits",
			"subtitle"
		],
		statistics: [
			"type",
			"kingdom",
			"rarity",
			"cost",
			"attack",
			"riposte",
			"health",
			"rangedAttack",
			"prestige",
			"nbTactic",
			"nbSupply",
			"goldGain",
			"drawCount",
			"requiresBanner",
			"requiresCity"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: drakerionListPresets,
	basicAttributes: ["rarity", "kingdom"],
	supportedMarketplaces: [],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "001",
		cardName: "Drake",
		expansion: "Origins",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/dreamland/dreamland.types.ts
let DreamlandRarity = /* @__PURE__ */ function(DreamlandRarity) {
	DreamlandRarity["BANALE"] = "Banale";
	DreamlandRarity["PEU_BANALE"] = "Peu banale";
	DreamlandRarity["KIBRILL"] = "Kibrill'";
	DreamlandRarity["CRAZY"] = "Crazy";
	DreamlandRarity["DREAM"] = "Dream";
	DreamlandRarity["PROMO"] = "Promo";
	DreamlandRarity["INITIATION"] = "Initiation";
	DreamlandRarity["WINNER"] = "Winner";
	return DreamlandRarity;
}({});
let DreamlandFinish = /* @__PURE__ */ function(DreamlandFinish) {
	DreamlandFinish["STANDARD"] = "Standard";
	return DreamlandFinish;
}({});
let DreamlandCardType = /* @__PURE__ */ function(DreamlandCardType) {
	DreamlandCardType["PERSO"] = "Perso";
	DreamlandCardType["KICK"] = "Kick";
	DreamlandCardType["SOUTIEN"] = "Soutien";
	DreamlandCardType["LIEU"] = "Lieu";
	return DreamlandCardType;
}({});
let DreamlandClass = /* @__PURE__ */ function(DreamlandClass) {
	DreamlandClass["VOYAGEUR"] = "Voyageur";
	DreamlandClass["CREATURE"] = "Créature";
	return DreamlandClass;
}({});
let DreamlandAbilityType = /* @__PURE__ */ function(DreamlandAbilityType) {
	DreamlandAbilityType["CAPACITE"] = "Capacité";
	DreamlandAbilityType["DEGAT"] = "Dégât";
	return DreamlandAbilityType;
}({});
let DreamlandRank = /* @__PURE__ */ function(DreamlandRank) {
	DreamlandRank["B"] = "B";
	DreamlandRank["M"] = "M";
	DreamlandRank["S"] = "S";
	DreamlandRank["STAR"] = "Star";
	return DreamlandRank;
}({});

//#endregion
//#region src/lib/games/dreamland/dreamland.presets.ts
const dreamlandListPresets = { Standard: {
	name: translatable("dreamland.presets.standard.name", "Standard Set"),
	type: ListPresetType.STANDARD,
	description: translatable("dreamland.presets.standard.description", "A complete set with one copy of each card"),
	matchers: [{
		filters: { finish: [DreamlandFinish.STANDARD] },
		quantity: 1
	}]
} };

//#endregion
//#region src/lib/games/dreamland/dreamland.game.ts
const dreamlandFinishValues = { [DreamlandFinish.STANDARD]: { label: translatable("global.attributes.finish.values.standard", "Standard") } };
const dreamlandFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(DreamlandRarity).optional(),
	finish: makeZodEnumFilter(DreamlandFinish).optional(),
	finishes: makeZodEnumFilter(DreamlandFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	cardType: makeZodEnumFilter(DreamlandCardType).optional(),
	cardClass: makeZodEnumFilter(DreamlandClass).optional(),
	abilityType: makeZodEnumFilter(DreamlandAbilityType).optional(),
	rank: makeZodEnumFilter(DreamlandRank).optional(),
	aura: zodNumericFilter.optional(),
	force: zodNumericFilter.optional(),
	comboBonus: zodNumericFilter.optional()
});
const DreamlandGameConfiguration = {
	name: "Dreamland TCG",
	shortName: "Dreamland",
	slug: "dreamland",
	icon: {
		dark: "dreamland-icon.png",
		light: "dreamland-icon.png"
	},
	image: {
		dark: "dreamland.png",
		light: "dreamland.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(DreamlandFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: dreamlandFinishValues
		}),
		finish: enumAttribute(DreamlandFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: dreamlandFinishValues
		}),
		rarity: enumAttribute(DreamlandRarity, {
			entity: "variant",
			name: translatable("dreamland.attributes.rarity.name", "Rarity"),
			description: translatable("dreamland.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[DreamlandRarity.BANALE]: {
					label: translatable("dreamland.attributes.rarity.values.banale", "Banale"),
					image: "/images/dreamland/rarities/banale.png"
				},
				[DreamlandRarity.PEU_BANALE]: {
					label: translatable("dreamland.attributes.rarity.values.peu_banale", "Peu banale"),
					image: "/images/dreamland/rarities/peu-banale.png"
				},
				[DreamlandRarity.KIBRILL]: {
					label: translatable("dreamland.attributes.rarity.values.kibrill", "Kibrill'"),
					image: "/images/dreamland/rarities/kibrill.png"
				},
				[DreamlandRarity.CRAZY]: {
					label: translatable("dreamland.attributes.rarity.values.crazy", "Crazy"),
					image: "/images/dreamland/rarities/crazy.png"
				},
				[DreamlandRarity.DREAM]: {
					label: translatable("dreamland.attributes.rarity.values.dream", "Dream"),
					image: "/images/dreamland/rarities/dream.png"
				},
				[DreamlandRarity.PROMO]: { label: translatable("dreamland.attributes.rarity.values.promo", "Promo") },
				[DreamlandRarity.INITIATION]: { label: translatable("dreamland.attributes.rarity.values.initiation", "Initiation") },
				[DreamlandRarity.WINNER]: { label: translatable("dreamland.attributes.rarity.values.winner", "Winner") }
			}
		}),
		printNumber: printNumberAttribute,
		dreamlandId: {
			entity: "variant",
			name: translatable("dreamland.attributes.dreamlandId.name", "Dreamland ID"),
			description: translatable("dreamland.attributes.dreamlandId.description", "Identifier of the card in the Dreamland app"),
			type: "string",
			array: false
		},
		cardType: enumAttribute(DreamlandCardType, {
			entity: "card",
			name: translatable("dreamland.attributes.cardType.name", "Card Type"),
			description: translatable("dreamland.attributes.cardType.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[DreamlandCardType.PERSO]: { label: translatable("dreamland.attributes.cardType.values.perso", "Perso") },
				[DreamlandCardType.KICK]: { label: translatable("dreamland.attributes.cardType.values.kick", "Kick") },
				[DreamlandCardType.SOUTIEN]: { label: translatable("dreamland.attributes.cardType.values.soutien", "Soutien") },
				[DreamlandCardType.LIEU]: { label: translatable("dreamland.attributes.cardType.values.lieu", "Lieu") }
			}
		}),
		cardClass: enumAttribute(DreamlandClass, {
			entity: "card",
			name: translatable("dreamland.attributes.cardClass.name", "Class"),
			description: translatable("dreamland.attributes.cardClass.description", "Whether the Perso is a Voyageur or a Créature"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[DreamlandClass.VOYAGEUR]: { label: translatable("dreamland.attributes.cardClass.values.voyageur", "Voyageur") },
				[DreamlandClass.CREATURE]: { label: translatable("dreamland.attributes.cardClass.values.creature", "Créature") }
			}
		}),
		keywords: {
			entity: "card",
			name: translatable("dreamland.attributes.keywords.name", "Keywords"),
			description: translatable("dreamland.attributes.keywords.description", "The keywords of the card"),
			type: "string",
			array: true
		},
		abilityName: {
			entity: "card",
			name: translatable("dreamland.attributes.abilityName.name", "Ability"),
			description: translatable("dreamland.attributes.abilityName.description", "Name of the card's ability"),
			type: "string",
			array: false
		},
		abilityType: enumAttribute(DreamlandAbilityType, {
			entity: "card",
			name: translatable("dreamland.attributes.abilityType.name", "Ability Type"),
			description: translatable("dreamland.attributes.abilityType.description", "Whether the ability is a Capacité or a Dégât"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[DreamlandAbilityType.CAPACITE]: { label: translatable("dreamland.attributes.abilityType.values.capacite", "Capacité") },
				[DreamlandAbilityType.DEGAT]: { label: translatable("dreamland.attributes.abilityType.values.degat", "Dégât") }
			}
		}),
		activationCondition: {
			entity: "card",
			name: translatable("dreamland.attributes.activationCondition.name", "Activation Condition"),
			description: translatable("dreamland.attributes.activationCondition.description", "When the ability can be activated"),
			type: "string",
			array: false
		},
		rulesText: {
			entity: "card",
			name: translatable("dreamland.attributes.rulesText.name", "Card Text"),
			description: translatable("dreamland.attributes.rulesText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		},
		aura: {
			entity: "card",
			name: translatable("dreamland.attributes.aura.name", "Aura"),
			description: translatable("dreamland.attributes.aura.description", "Aura of the Perso"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		force: {
			entity: "card",
			name: translatable("dreamland.attributes.force.name", "Force"),
			description: translatable("dreamland.attributes.force.description", "Force of the Kick"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		rank: enumAttribute(DreamlandRank, {
			entity: "card",
			name: translatable("dreamland.attributes.rank.name", "Rank"),
			description: translatable("dreamland.attributes.rank.description", "Rank of the Kick"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[DreamlandRank.B]: { label: translatable("dreamland.attributes.rank.values.b", "B") },
				[DreamlandRank.M]: { label: translatable("dreamland.attributes.rank.values.m", "M") },
				[DreamlandRank.S]: { label: translatable("dreamland.attributes.rank.values.s", "S") },
				[DreamlandRank.STAR]: { label: translatable("dreamland.attributes.rank.values.star", "★") }
			}
		}),
		comboRanks: {
			entity: "card",
			name: translatable("dreamland.attributes.comboRanks.name", "Combo Ranks"),
			description: translatable("dreamland.attributes.comboRanks.description", "Ranks of the Kicks required to activate the combo"),
			type: "string",
			array: false
		},
		comboBonus: {
			entity: "card",
			name: translatable("dreamland.attributes.comboBonus.name", "Combo Bonus"),
			description: translatable("dreamland.attributes.comboBonus.description", "Bonus granted when the combo is activated"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"cardType",
			"cardClass"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"abilityType",
			"rank",
			"finishes",
			"languages"
		]
	}, {
		label: "Card Stats",
		filters: [
			"aura",
			"force",
			"comboBonus"
		]
	}],
	productDetailsOrder: {
		details: [
			"abilityName",
			"abilityType",
			"activationCondition",
			"rulesText",
			"keywords"
		],
		statistics: [
			"aura",
			"force",
			"rank",
			"comboRanks",
			"comboBonus"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: dreamlandListPresets,
	basicAttributes: ["rarity", "cardType"],
	finishHierarchy: [CardFinish.STANDARD],
	supportedMarketplaces: [],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "001/FDS",
		cardName: "NEIL",
		expansion: "Fracture des Songes",
		expansionCode: "FDS",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "French",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/eoa/eoa.types.ts
let EoaRarity = /* @__PURE__ */ function(EoaRarity) {
	EoaRarity["COMMON"] = "Common";
	EoaRarity["UNCOMMON"] = "Uncommon";
	EoaRarity["RARE"] = "Rare";
	EoaRarity["LEGENDARY"] = "Legendary";
	return EoaRarity;
}({});
let EoaFinish = /* @__PURE__ */ function(EoaFinish) {
	EoaFinish["STANDARD"] = "Standard";
	EoaFinish["FOIL"] = "Foil";
	return EoaFinish;
}({});
let EoaCardType = /* @__PURE__ */ function(EoaCardType) {
	EoaCardType["UNIT"] = "Unit";
	EoaCardType["TACTIC"] = "Tactic";
	EoaCardType["TERRITORY"] = "Territory";
	EoaCardType["ITEM"] = "Item";
	return EoaCardType;
}({});
let EoaAffinity = /* @__PURE__ */ function(EoaAffinity) {
	EoaAffinity["SOLARA"] = "Solara";
	EoaAffinity["LUX"] = "Lux";
	EoaAffinity["TERRA"] = "Terra";
	EoaAffinity["CAELUM"] = "Caelum";
	EoaAffinity["NOX"] = "Nox";
	EoaAffinity["NEUTRAL"] = "Neutral";
	return EoaAffinity;
}({});
let EoaSubtype = /* @__PURE__ */ function(EoaSubtype) {
	EoaSubtype["RENOWNED"] = "Renowned";
	EoaSubtype["HUMAN"] = "Human";
	EoaSubtype["EQUIPMENT"] = "Equipment";
	EoaSubtype["VAMPIRE"] = "Vampire";
	EoaSubtype["COUNTER"] = "Counter";
	EoaSubtype["SYLVAN"] = "Sylvan";
	EoaSubtype["FENRIR"] = "Fenrir";
	EoaSubtype["VEHICLE"] = "Vehicle";
	EoaSubtype["BEAST"] = "Beast";
	EoaSubtype["DEMON"] = "Demon";
	EoaSubtype["DRAGON"] = "Dragon";
	EoaSubtype["DRAGONKIN"] = "Dragonkin";
	EoaSubtype["LEPORIN"] = "Leporin";
	EoaSubtype["KITSUNE"] = "Kitsune";
	EoaSubtype["AUTOMATON"] = "Automaton";
	EoaSubtype["UNDEAD"] = "Undead";
	EoaSubtype["AVIAN"] = "Avian";
	EoaSubtype["NEKORA"] = "Nekora";
	EoaSubtype["ANGEL"] = "Angel";
	EoaSubtype["DRONE"] = "Drone";
	EoaSubtype["PRIMAL"] = "Primal";
	EoaSubtype["MONSTER"] = "Monster";
	EoaSubtype["PLANT"] = "Plant";
	EoaSubtype["TERRITORY"] = "Territory";
	return EoaSubtype;
}({});
let EoaVariant = /* @__PURE__ */ function(EoaVariant) {
	EoaVariant["NORMAL"] = "Normal";
	EoaVariant["SERIALIZED"] = "Serialized";
	EoaVariant["FRAMEBREAK"] = "Framebreak";
	EoaVariant["SIGNATURE"] = "Signature";
	return EoaVariant;
}({});

//#endregion
//#region src/lib/games/eoa/eoa.presets.ts
const eoaListPresets = {
	Standard: {
		name: translatable("eoa.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("eoa.presets.standard.description", "A complete set with one copy of each card - standard finish where available, foil otherwise"),
		matchers: [{
			filters: { finish: [EoaFinish.STANDARD] },
			quantity: 1
		}, {
			filters: {
				finish: [EoaFinish.FOIL],
				rarity: [EoaRarity.LEGENDARY],
				variant: [EoaVariant.NORMAL]
			},
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("eoa.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("eoa.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [EoaFinish.STANDARD] },
			quantity: 3
		}, {
			filters: {
				finish: [EoaFinish.FOIL],
				rarity: [EoaRarity.LEGENDARY],
				variant: [EoaVariant.NORMAL]
			},
			quantity: 3
		}]
	},
	Foil: {
		name: translatable("eoa.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("eoa.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: {
				finish: [EoaFinish.FOIL],
				variant: [EoaVariant.NORMAL]
			},
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("eoa.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("eoa.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [{
			filters: {
				finish: [EoaFinish.FOIL],
				variant: [EoaVariant.NORMAL]
			},
			quantity: 3
		}]
	},
	"Master Set": {
		name: translatable("eoa.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("eoa.presets.masterset.description", "A complete set with one copy of each card in all finishes and variants"),
		matchers: [{
			filters: { finish: [EoaFinish.STANDARD] },
			quantity: 1
		}, {
			filters: { finish: [EoaFinish.FOIL] },
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/eoa/eoa.game.ts
/**
* Shared finish values for Echoes of Astra - used by both the `finishes`
* (product) and `finish` (inventory) attributes.
*/
const eoaFinishValues = {
	[EoaFinish.STANDARD]: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	[EoaFinish.FOIL]: { label: translatable("global.attributes.finish.values.foil", "Foil") }
};
const eoaFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(EoaRarity).optional(),
	finish: makeZodEnumFilter(EoaFinish).optional(),
	finishes: makeZodEnumFilter(EoaFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	affinity: makeZodEnumFilter(EoaAffinity).optional(),
	cardType: makeZodEnumFilter(EoaCardType).optional(),
	subtypes: makeZodEnumFilter(EoaSubtype).optional(),
	variant: makeZodEnumFilter(EoaVariant).optional(),
	supplyCost: zodNumericFilter.optional(),
	influence: zodNumericFilter.optional(),
	attack: zodNumericFilter.optional(),
	health: zodNumericFilter.optional()
});
const EoaGameConfiguration = {
	name: "Echoes of Astra",
	shortName: "Echoes of Astra",
	slug: "eoa",
	icon: {
		dark: "eoa-icon.png",
		light: "eoa-icon.png"
	},
	image: {
		dark: "eoa.png",
		light: "eoa.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(EoaFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: eoaFinishValues
		}),
		finish: enumAttribute(EoaFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: eoaFinishValues
		}),
		rarity: enumAttribute(EoaRarity, {
			entity: "variant",
			name: translatable("global.attributes.rarity.name", "Rarity"),
			description: translatable("global.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[EoaRarity.COMMON]: {
					label: translatable("eoa.attributes.rarity.values.common", "Common"),
					image: "/images/eoa/rarities/common.png"
				},
				[EoaRarity.UNCOMMON]: {
					label: translatable("eoa.attributes.rarity.values.uncommon", "Uncommon"),
					image: "/images/eoa/rarities/uncommon.png"
				},
				[EoaRarity.RARE]: {
					label: translatable("eoa.attributes.rarity.values.rare", "Rare"),
					image: "/images/eoa/rarities/rare.png"
				},
				[EoaRarity.LEGENDARY]: {
					label: translatable("eoa.attributes.rarity.values.legendary", "Legendary"),
					image: "/images/eoa/rarities/legendary.png"
				}
			}
		}),
		printNumber: printNumberAttribute,
		affinity: enumAttribute(EoaAffinity, {
			entity: "card",
			name: translatable("eoa.attributes.affinity.name", "Affinity"),
			description: translatable("eoa.attributes.affinity.description", "The affinity (colour) of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[EoaAffinity.SOLARA]: {
					label: translatable("eoa.attributes.affinity.values.solara", "Solara"),
					image: "/images/eoa/affinities/solara.png"
				},
				[EoaAffinity.LUX]: {
					label: translatable("eoa.attributes.affinity.values.lux", "Lux"),
					image: "/images/eoa/affinities/lux.png"
				},
				[EoaAffinity.TERRA]: {
					label: translatable("eoa.attributes.affinity.values.terra", "Terra"),
					image: "/images/eoa/affinities/terra.png"
				},
				[EoaAffinity.CAELUM]: {
					label: translatable("eoa.attributes.affinity.values.caelum", "Caelum"),
					image: "/images/eoa/affinities/caelum.png"
				},
				[EoaAffinity.NOX]: {
					label: translatable("eoa.attributes.affinity.values.nox", "Nox"),
					image: "/images/eoa/affinities/nox.png"
				},
				[EoaAffinity.NEUTRAL]: {
					label: translatable("eoa.attributes.affinity.values.neutral", "Neutral"),
					image: "/images/eoa/affinities/neutral.png"
				}
			}
		}),
		cardType: enumAttribute(EoaCardType, {
			entity: "card",
			name: translatable("eoa.attributes.cardType.name", "Card Type"),
			description: translatable("eoa.attributes.cardType.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[EoaCardType.UNIT]: { label: translatable("eoa.attributes.cardType.values.unit", "Unit") },
				[EoaCardType.TACTIC]: { label: translatable("eoa.attributes.cardType.values.tactic", "Tactic") },
				[EoaCardType.TERRITORY]: { label: translatable("eoa.attributes.cardType.values.territory", "Territory") },
				[EoaCardType.ITEM]: { label: translatable("eoa.attributes.cardType.values.item", "Item") }
			}
		}),
		subtypes: enumAttribute(EoaSubtype, {
			entity: "card",
			name: translatable("eoa.attributes.subtypes.name", "Subtypes"),
			description: translatable("eoa.attributes.subtypes.description", "Card subtypes and traits"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[EoaSubtype.RENOWNED]: { label: translatable("eoa.attributes.subtypes.values.renowned", "Renowned") },
				[EoaSubtype.HUMAN]: { label: translatable("eoa.attributes.subtypes.values.human", "Human") },
				[EoaSubtype.EQUIPMENT]: { label: translatable("eoa.attributes.subtypes.values.equipment", "Equipment") },
				[EoaSubtype.VAMPIRE]: { label: translatable("eoa.attributes.subtypes.values.vampire", "Vampire") },
				[EoaSubtype.COUNTER]: { label: translatable("eoa.attributes.subtypes.values.counter", "Counter") },
				[EoaSubtype.SYLVAN]: { label: translatable("eoa.attributes.subtypes.values.sylvan", "Sylvan") },
				[EoaSubtype.FENRIR]: { label: translatable("eoa.attributes.subtypes.values.fenrir", "Fenrir") },
				[EoaSubtype.VEHICLE]: { label: translatable("eoa.attributes.subtypes.values.vehicle", "Vehicle") },
				[EoaSubtype.BEAST]: { label: translatable("eoa.attributes.subtypes.values.beast", "Beast") },
				[EoaSubtype.DEMON]: { label: translatable("eoa.attributes.subtypes.values.demon", "Demon") },
				[EoaSubtype.DRAGON]: { label: translatable("eoa.attributes.subtypes.values.dragon", "Dragon") },
				[EoaSubtype.DRAGONKIN]: { label: translatable("eoa.attributes.subtypes.values.dragonkin", "Dragonkin") },
				[EoaSubtype.LEPORIN]: { label: translatable("eoa.attributes.subtypes.values.leporin", "Leporin") },
				[EoaSubtype.KITSUNE]: { label: translatable("eoa.attributes.subtypes.values.kitsune", "Kitsune") },
				[EoaSubtype.AUTOMATON]: { label: translatable("eoa.attributes.subtypes.values.automaton", "Automaton") },
				[EoaSubtype.UNDEAD]: { label: translatable("eoa.attributes.subtypes.values.undead", "Undead") },
				[EoaSubtype.AVIAN]: { label: translatable("eoa.attributes.subtypes.values.avian", "Avian") },
				[EoaSubtype.NEKORA]: { label: translatable("eoa.attributes.subtypes.values.nekora", "Nekora") },
				[EoaSubtype.ANGEL]: { label: translatable("eoa.attributes.subtypes.values.angel", "Angel") },
				[EoaSubtype.DRONE]: { label: translatable("eoa.attributes.subtypes.values.drone", "Drone") },
				[EoaSubtype.PRIMAL]: { label: translatable("eoa.attributes.subtypes.values.primal", "Primal") },
				[EoaSubtype.MONSTER]: { label: translatable("eoa.attributes.subtypes.values.monster", "Monster") },
				[EoaSubtype.PLANT]: { label: translatable("eoa.attributes.subtypes.values.plant", "Plant") },
				[EoaSubtype.TERRITORY]: { label: translatable("eoa.attributes.subtypes.values.territory", "Territory") }
			}
		}),
		variant: enumAttribute(EoaVariant, {
			entity: "variant",
			name: translatable("eoa.attributes.variant.name", "Variant"),
			description: translatable("eoa.attributes.variant.description", "The printing variant/treatment of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[EoaVariant.NORMAL]: { label: translatable("eoa.attributes.variant.values.normal", "Normal") },
				[EoaVariant.SERIALIZED]: { label: translatable("eoa.attributes.variant.values.serialized", "Serialized") },
				[EoaVariant.FRAMEBREAK]: { label: translatable("eoa.attributes.variant.values.framebreak", "Framebreak") },
				[EoaVariant.SIGNATURE]: { label: translatable("eoa.attributes.variant.values.signature", "Signature") }
			}
		}),
		supplyCost: {
			entity: "card",
			name: translatable("eoa.attributes.supplyCost.name", "Supply Cost"),
			description: translatable("eoa.attributes.supplyCost.description", "Supply cost to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		influence: {
			entity: "card",
			name: translatable("eoa.attributes.influence.name", "Influence"),
			description: translatable("eoa.attributes.influence.description", "Influence generated by the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		attack: {
			entity: "card",
			name: translatable("eoa.attributes.attack.name", "Attack"),
			description: translatable("eoa.attributes.attack.description", "Attack power of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		health: {
			entity: "card",
			name: translatable("eoa.attributes.health.name", "Health"),
			description: translatable("eoa.attributes.health.description", "Health points of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		artist: {
			entity: "card",
			name: translatable("eoa.attributes.artist.name", "Artist"),
			description: translatable("eoa.attributes.artist.description", "The artist(s) who illustrated the card"),
			type: "string",
			array: true
		},
		rulesText: {
			entity: "variant",
			name: translatable("eoa.attributes.rulesText.name", "Rules Text"),
			description: translatable("eoa.attributes.rulesText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"finishes",
			"affinity",
			"cardType"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"subtypes",
			"variant",
			"supplyCost",
			"influence",
			"attack",
			"health"
		]
	}],
	productDetailsOrder: {
		details: [
			"rulesText",
			"subtypes",
			"artist"
		],
		statistics: [
			"cardType",
			"affinity",
			"rarity",
			"supplyCost",
			"influence",
			"attack",
			"health"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: eoaListPresets,
	basicAttributes: ["rarity", "affinity"],
	supportedMarketplaces: [],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "IMP001",
		cardName: "Assimilating Abomination",
		expansion: "Imperion 1st Ed",
		expansionCode: "IMP",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/example-game/example-game.game.ts
let ExampleGameRarity = /* @__PURE__ */ function(ExampleGameRarity) {
	ExampleGameRarity["Common"] = "common";
	ExampleGameRarity["Uncommon"] = "uncommon";
	ExampleGameRarity["Rare"] = "rare";
	ExampleGameRarity["Legendary"] = "legendary";
	return ExampleGameRarity;
}({});
let ExampleGameColor = /* @__PURE__ */ function(ExampleGameColor) {
	ExampleGameColor["Red"] = "red";
	ExampleGameColor["Blue"] = "blue";
	ExampleGameColor["Green"] = "green";
	ExampleGameColor["Yellow"] = "yellow";
	ExampleGameColor["Purple"] = "purple";
	return ExampleGameColor;
}({});
let ExampleGameFinish = /* @__PURE__ */ function(ExampleGameFinish) {
	ExampleGameFinish["Standard"] = "standard";
	ExampleGameFinish["Foil"] = "foil";
	return ExampleGameFinish;
}({});
/**
* Shared finish values for Example Game - used by both finishes and finish attributes
*/
const exampleGameFinishValues = {
	standard: { label: translatable("example.attributes.finish.values.standard", "Standard") },
	foil: { label: translatable("example.attributes.finish.values.foil", "Foil") }
};
const exampleGameFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(ExampleGameRarity).optional(),
	color: makeZodEnumFilter(ExampleGameColor).optional(),
	finish: makeZodEnumFilter(ExampleGameFinish).optional(),
	finishes: makeZodEnumFilter(ExampleGameFinish).optional(),
	cost: zodNumberFilter.optional(),
	power: zodNumberFilter.optional(),
	toughness: zodNumberFilter.optional()
});
const ExampleGameConfiguration = {
	name: "Example TCG",
	shortName: "Example",
	slug: "example-game",
	icon: {
		dark: "example-icon.png",
		light: "example-icon.png"
	},
	image: {
		dark: "example-game.png",
		light: "example-game.png"
	},
	attributes: {
		cost: {
			entity: "card",
			name: translatable("example.attributes.cost.name", "Cost"),
			description: translatable("example.attributes.cost.description", "The mana cost to play the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		power: {
			entity: "card",
			name: translatable("example.attributes.power.name", "Power"),
			description: translatable("example.attributes.power.description", "The attack power of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		toughness: {
			entity: "card",
			name: translatable("example.attributes.toughness.name", "Toughness"),
			description: translatable("example.attributes.toughness.description", "The defense of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		color: enumAttribute({
			red: "red",
			blue: "blue",
			green: "green",
			yellow: "yellow",
			purple: "purple"
		}, {
			entity: "card",
			name: translatable("example.attributes.color.name", "Color"),
			description: translatable("example.attributes.color.description", "The color of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				red: { label: translatable("example.attributes.color.values.red", "Red") },
				blue: { label: translatable("example.attributes.color.values.blue", "Blue") },
				green: { label: translatable("example.attributes.color.values.green", "Green") },
				yellow: { label: translatable("example.attributes.color.values.yellow", "Yellow") },
				purple: { label: translatable("example.attributes.color.values.purple", "Purple") }
			}
		}),
		rarity: enumAttribute({
			common: "common",
			uncommon: "uncommon",
			rare: "rare",
			legendary: "legendary"
		}, {
			entity: "variant",
			name: translatable("example.attributes.rarity.name", "Rarity"),
			description: translatable("example.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				common: { label: translatable("example.attributes.rarity.values.common", "Common") },
				uncommon: { label: translatable("example.attributes.rarity.values.uncommon", "Uncommon") },
				rare: { label: translatable("example.attributes.rarity.values.rare", "Rare") },
				legendary: { label: translatable("example.attributes.rarity.values.legendary", "Legendary") }
			}
		}),
		finishes: enumAttribute(ExampleGameFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("example.attributes.finish.name", "Finish"),
			description: translatable("example.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: exampleGameFinishValues
		}),
		finish: enumAttribute(ExampleGameFinish, {
			entity: "inventory",
			name: translatable("example.attributes.finish.name", "Finish"),
			description: translatable("example.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: exampleGameFinishValues
		}),
		abilities: {
			entity: "card",
			name: translatable("example.attributes.abilities.name", "Abilities"),
			description: translatable("example.attributes.abilities.description", "Special abilities of the card"),
			type: "string",
			array: true
		},
		rules_text: {
			entity: "card",
			name: translatable("example.attributes.rules_text.name", "Rules Text"),
			description: translatable("example.attributes.rules_text.description", "Rules text on the card"),
			type: "string",
			array: false
		},
		artist: {
			entity: "card",
			name: translatable("example.attributes.artist.name", "Artist"),
			description: translatable("example.attributes.artist.description", "The artist who illustrated the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"color",
			"rarity"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"cost",
			"power",
			"toughness"
		]
	}],
	productDetailsOrder: {
		details: [
			"abilities",
			"rules_text",
			"artist"
		],
		statistics: [
			"color",
			"cost",
			"rarity",
			"power",
			"toughness",
			"finish"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: {},
	basicAttributes: ["color", "rarity"],
	supportedMarketplaces: [],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "001",
		cardName: "Example Card",
		expansion: "Example Set",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/fab/fab.types.ts
let FABPrintingSuffix = /* @__PURE__ */ function(FABPrintingSuffix) {
	FABPrintingSuffix["RainbowFoil"] = "RF";
	FABPrintingSuffix["ColdFoil"] = "CF";
	FABPrintingSuffix["GoldFoil"] = "GF";
	FABPrintingSuffix["Marvel"] = "MV";
	FABPrintingSuffix["MarvelAlternateArt"] = "MVA";
	FABPrintingSuffix["AlternateArt"] = "AA";
	FABPrintingSuffix["ExtendedArt"] = "EA";
	FABPrintingSuffix["TreasurePack"] = "TP";
	FABPrintingSuffix["ClassicConstructed"] = "CC";
	FABPrintingSuffix["AlternateArtA"] = "A";
	FABPrintingSuffix["AlternateArtB"] = "B";
	FABPrintingSuffix["AlternateArtC"] = "C";
	FABPrintingSuffix["AlternateRainbowFoil"] = "ARF";
	return FABPrintingSuffix;
}({});
let FABCardFinish = /* @__PURE__ */ function(FABCardFinish) {
	FABCardFinish["Standard"] = "Standard";
	FABCardFinish["RainbowFoil"] = "Rainbow Foil";
	FABCardFinish["ColdFoil"] = "Cold Foil";
	FABCardFinish["GoldFoil"] = "Gold Foil";
	return FABCardFinish;
}({});
let FABCardVariant = /* @__PURE__ */ function(FABCardVariant) {
	FABCardVariant["UNLIMITED"] = "Unlimited";
	FABCardVariant["MARVEL"] = "Marvel";
	FABCardVariant["MARVEL_ALTERNATE_ART"] = "Marvel Alternate Art";
	FABCardVariant["EXTENDED_ART"] = "Extended Art";
	FABCardVariant["ALTERNATE_ART"] = "Alternate Art";
	FABCardVariant["ALTERNATE_ART_A"] = "Alternate Art #A";
	FABCardVariant["ALTERNATE_ART_B"] = "Alternate Art #B";
	FABCardVariant["ALTERNATE_ART_C"] = "Alternate Art #C";
	FABCardVariant["CLASSIC_CONSTRUCTED"] = "CC";
	return FABCardVariant;
}({});
let FABCardRarity = /* @__PURE__ */ function(FABCardRarity) {
	FABCardRarity["Common"] = "Common";
	FABCardRarity["Rare"] = "Rare";
	FABCardRarity["SuperRare"] = "Super Rare";
	FABCardRarity["Majestic"] = "Majestic";
	FABCardRarity["Legendary"] = "Legendary";
	FABCardRarity["Fabled"] = "Fabled";
	FABCardRarity["Token"] = "Token";
	FABCardRarity["Marvel"] = "Marvel";
	FABCardRarity["Promo"] = "Promo";
	return FABCardRarity;
}({});
let FABCardType = /* @__PURE__ */ function(FABCardType) {
	FABCardType["Action"] = "Action";
	FABCardType["AttackReaction"] = "Attack Reaction";
	FABCardType["Block"] = "Block";
	FABCardType["DefenseReaction"] = "Defense Reaction";
	FABCardType["DemiHero"] = "Demi-Hero";
	FABCardType["Equipment"] = "Equipment";
	FABCardType["Hero"] = "Hero";
	FABCardType["Instant"] = "Instant";
	FABCardType["Macro"] = "Macro";
	FABCardType["Mentor"] = "Mentor";
	FABCardType["Resource"] = "Resource";
	FABCardType["Token"] = "Token";
	FABCardType["Weapon"] = "Weapon";
	return FABCardType;
}({});
let FABCardSubType = /* @__PURE__ */ function(FABCardSubType) {
	FABCardSubType["OneHanded"] = "(1H)";
	FABCardSubType["TwoHanded"] = "(2H)";
	FABCardSubType["Affliction"] = "Affliction";
	FABCardSubType["Ally"] = "Ally";
	FABCardSubType["Angel"] = "Angel";
	FABCardSubType["Arms"] = "Arms";
	FABCardSubType["Arrow"] = "Arrow";
	FABCardSubType["Ash"] = "Ash";
	FABCardSubType["Attack"] = "Attack";
	FABCardSubType["Aura"] = "Aura";
	FABCardSubType["Axe"] = "Axe";
	FABCardSubType["Base"] = "Base";
	FABCardSubType["Book"] = "Book";
	FABCardSubType["Bow"] = "Bow";
	FABCardSubType["Brush"] = "Brush";
	FABCardSubType["Chest"] = "Chest";
	FABCardSubType["Chi"] = "Chi";
	FABCardSubType["Claw"] = "Claw";
	FABCardSubType["Club"] = "Club";
	FABCardSubType["Construct"] = "Construct";
	FABCardSubType["Dagger"] = "Dagger";
	FABCardSubType["Demon"] = "Demon";
	FABCardSubType["Dragon"] = "Dragon";
	FABCardSubType["Evo"] = "Evo";
	FABCardSubType["Fiddle"] = "Fiddle";
	FABCardSubType["Figment"] = "Figment";
	FABCardSubType["Flail"] = "Flail";
	FABCardSubType["Gem"] = "Gem";
	FABCardSubType["Gun"] = "Gun";
	FABCardSubType["Hammer"] = "Hammer";
	FABCardSubType["Head"] = "Head";
	FABCardSubType["Invocation"] = "Invocation";
	FABCardSubType["Item"] = "Item";
	FABCardSubType["Landmark"] = "Landmark";
	FABCardSubType["Legs"] = "Legs";
	FABCardSubType["Lute"] = "Lute";
	FABCardSubType["Mercenary"] = "Mercenary";
	FABCardSubType["OffHand"] = "Off-Hand";
	FABCardSubType["Orb"] = "Orb";
	FABCardSubType["Pistol"] = "Pistol";
	FABCardSubType["Polearm"] = "Polearm";
	FABCardSubType["Quiver"] = "Quiver";
	FABCardSubType["Rock"] = "Rock";
	FABCardSubType["Scepter"] = "Scepter";
	FABCardSubType["Scroll"] = "Scroll";
	FABCardSubType["Scythe"] = "Scythe";
	FABCardSubType["Shuriken"] = "Shuriken";
	FABCardSubType["Song"] = "Song";
	FABCardSubType["Staff"] = "Staff";
	FABCardSubType["Sword"] = "Sword";
	FABCardSubType["Trap"] = "Trap";
	FABCardSubType["Wrench"] = "Wrench";
	FABCardSubType["Young"] = "Young";
	return FABCardSubType;
}({});
let FABCardHeroClass = /* @__PURE__ */ function(FABCardHeroClass) {
	FABCardHeroClass["Adjudicator"] = "Adjudicator";
	FABCardHeroClass["Assassin"] = "Assassin";
	FABCardHeroClass["Bard"] = "Bard";
	FABCardHeroClass["Brute"] = "Brute";
	FABCardHeroClass["Generic"] = "Generic";
	FABCardHeroClass["Guardian"] = "Guardian";
	FABCardHeroClass["Illusionist"] = "Illusionist";
	FABCardHeroClass["Mechanologist"] = "Mechanologist";
	FABCardHeroClass["Merchant"] = "Merchant";
	FABCardHeroClass["Ninja"] = "Ninja";
	FABCardHeroClass["Ranger"] = "Ranger";
	FABCardHeroClass["Runeblade"] = "Runeblade";
	FABCardHeroClass["Shapeshifter"] = "Shapeshifter";
	FABCardHeroClass["Warrior"] = "Warrior";
	FABCardHeroClass["Wizard"] = "Wizard";
	return FABCardHeroClass;
}({});
let FABCardTalent = /* @__PURE__ */ function(FABCardTalent) {
	FABCardTalent["Chaos"] = "Chaos";
	FABCardTalent["Draconic"] = "Draconic";
	FABCardTalent["Earth"] = "Earth";
	FABCardTalent["Elemental"] = "Elemental";
	FABCardTalent["Ice"] = "Ice";
	FABCardTalent["Light"] = "Light";
	FABCardTalent["Lightning"] = "Lightning";
	FABCardTalent["Mystic"] = "Mystic";
	FABCardTalent["Rosetta"] = "Rosetta";
	FABCardTalent["Royal"] = "Royal";
	FABCardTalent["Shadow"] = "Shadow";
	return FABCardTalent;
}({});
let FABCardPrintType = /* @__PURE__ */ function(FABCardPrintType) {
	FABCardPrintType["Regular"] = "regular";
	FABCardPrintType["TwinCard"] = "twin-card";
	FABCardPrintType["FlipCard"] = "flip-card";
	FABCardPrintType["SplitCard"] = "split-card";
	FABCardPrintType["TranscendCard"] = "transcend-card";
	return FABCardPrintType;
}({});
let FABCardColor = /* @__PURE__ */ function(FABCardColor) {
	FABCardColor["Red"] = "Red";
	FABCardColor["Yellow"] = "Yellow";
	FABCardColor["Blue"] = "Blue";
	return FABCardColor;
}({});
let FABProductType = /* @__PURE__ */ function(FABProductType) {
	FABProductType["PROMO"] = "Promo";
	FABProductType["BOOSTER"] = "Booster";
	FABProductType["PRECON"] = "Precon";
	FABProductType["STARTER"] = "Starter";
	FABProductType["BUNDLE"] = "Bundle";
	FABProductType["DECK"] = "Deck";
	return FABProductType;
}({});

//#endregion
//#region src/lib/games/fab/fab.presets.ts
const PLAYSET_TYPES = [
	FABCardType.Action,
	FABCardType.AttackReaction,
	FABCardType.Block,
	FABCardType.DefenseReaction,
	FABCardType.Instant,
	FABCardType.Macro,
	FABCardType.Mentor,
	FABCardType.Resource,
	FABCardType.Token
];
const SINGLE_COPY_TYPES = [
	FABCardType.Equipment,
	FABCardType.Hero,
	FABCardType.DemiHero,
	FABCardType.Weapon
];
const LIMITED_RARITIES = [
	FABCardRarity.Legendary,
	FABCardRarity.Marvel,
	FABCardRarity.Fabled
];
const NON_LIMITED_RARITIES = [
	FABCardRarity.Common,
	FABCardRarity.Rare,
	FABCardRarity.SuperRare,
	FABCardRarity.Majestic,
	FABCardRarity.Token,
	FABCardRarity.Promo
];
const fabListPresets = {
	Standard: {
		name: translatable("fab.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("fab.presets.standard.description", "A complete set with one copy of each card in standard finish (Legendary, Marvel and Fabled cards in their available foil finish)"),
		matchers: [{
			filters: { finish: [FABCardFinish.Standard] },
			quantity: 1
		}, {
			filters: { rarity: LIMITED_RARITIES },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("fab.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("fab.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [
			{
				filters: {
					finish: [FABCardFinish.Standard],
					types: PLAYSET_TYPES
				},
				quantity: 3
			},
			{
				filters: {
					finish: [FABCardFinish.Standard],
					types: SINGLE_COPY_TYPES
				},
				quantity: 1
			},
			{
				filters: { rarity: LIMITED_RARITIES },
				quantity: 1
			}
		]
	},
	Foil: {
		name: translatable("fab.presets.foil.name", "Rainbow Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("fab.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: {
				finish: [FABCardFinish.RainbowFoil],
				rarity: NON_LIMITED_RARITIES
			},
			quantity: 1
		}, {
			filters: { rarity: LIMITED_RARITIES },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("fab.presets.foilPlayset.name", "Rainbow Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("fab.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [
			{
				filters: {
					finish: [FABCardFinish.RainbowFoil],
					types: PLAYSET_TYPES,
					rarity: NON_LIMITED_RARITIES
				},
				quantity: 3
			},
			{
				filters: {
					finish: [FABCardFinish.RainbowFoil],
					types: SINGLE_COPY_TYPES,
					rarity: NON_LIMITED_RARITIES
				},
				quantity: 1
			},
			{
				filters: { rarity: LIMITED_RARITIES },
				quantity: 1
			}
		]
	},
	"Master Set": {
		name: translatable("fab.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("fab.presets.masterset.description", "A complete set with one copy of each card in standard, rainbow foil, gold foil, and cold foil finishes, plus all promotional variants"),
		matchers: [
			{
				filters: { finish: [FABCardFinish.Standard] },
				quantity: 1
			},
			{
				filters: { finish: [FABCardFinish.RainbowFoil] },
				quantity: 1
			},
			{
				filters: { finish: [FABCardFinish.GoldFoil] },
				quantity: 1
			},
			{
				filters: { finish: [FABCardFinish.ColdFoil] },
				quantity: 1
			},
			{
				filters: { rarity: [FABCardRarity.Promo] },
				quantity: 1
			}
		]
	}
};

//#endregion
//#region src/lib/games/fab/fab.game.ts
/**
* Shared finish values for FAB - used by both finishes and finish attributes
*/
const fabFinishValues = {
	Standard: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	"Rainbow Foil": { label: translatable("global.attributes.finish.values.rainbowfoil", "Rainbow Foil") },
	"Cold Foil": { label: translatable("global.attributes.finish.values.coldfoil", "Cold Foil") },
	"Gold Foil": { label: translatable("global.attributes.finish.values.goldfoil", "Gold Foil") }
};
const fabFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(FABCardRarity).optional(),
	classes: makeZodEnumFilter(FABCardHeroClass).optional(),
	talents: makeZodEnumFilter(FABCardTalent).optional(),
	finish: makeZodEnumFilter(FABCardFinish).optional(),
	finishes: makeZodEnumFilter(FABCardFinish).optional(),
	types: makeZodEnumFilter(FABCardType).optional(),
	subTypes: makeZodEnumFilter(FABCardSubType).optional(),
	color: makeZodEnumFilter(FABCardColor).optional(),
	cost: zodNumberFilter.optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	variant: makeZodEnumFilter(FABCardVariant).optional()
});
const FABGameConfiguration = {
	name: "Flesh and Blood",
	shortName: "FAB",
	slug: "fab",
	icon: {
		dark: "fab-icon.png",
		light: "fab-icon.png"
	},
	image: {
		dark: "fab.png",
		light: "fab.png"
	},
	attributes: {
		name: nameAttribute,
		rarity: enumAttribute(FABCardRarity, {
			entity: "variant",
			name: translatable("fab.attributes.rarity.name", "Rarity"),
			description: translatable("fab.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				Common: {
					label: translatable("fab.attributes.rarity.values.common", "Common"),
					image: "/images/fab/common.svg"
				},
				Rare: {
					label: translatable("fab.attributes.rarity.values.rare", "Rare"),
					image: "/images/fab/rare.svg"
				},
				"Super Rare": {
					label: translatable("fab.attributes.rarity.values.super_rare", "Super Rare"),
					image: "/images/fab/super-rare.svg"
				},
				Majestic: {
					label: translatable("fab.attributes.rarity.values.majestic", "Majestic"),
					image: "/images/fab/majestic.svg"
				},
				Legendary: {
					label: translatable("fab.attributes.rarity.values.legendary", "Legendary"),
					image: "/images/fab/legendary.svg"
				},
				Fabled: {
					label: translatable("fab.attributes.rarity.values.fabled", "Fabled"),
					image: "/images/fab/fabled.svg"
				},
				Marvel: {
					label: translatable("fab.attributes.rarity.values.marvel", "Marvel"),
					image: "/images/fab/marvel.svg"
				},
				Token: {
					label: translatable("fab.attributes.rarity.values.token", "Token"),
					image: "/images/fab/token.svg"
				},
				Promo: {
					label: translatable("fab.attributes.rarity.values.promo", "Promo"),
					image: "/images/fab/promo.svg"
				}
			}
		}),
		classes: enumAttribute(FABCardHeroClass, {
			entity: "card",
			name: translatable("fab.attributes.classes.name", "Hero Classes"),
			description: translatable("fab.attributes.classes.description", "The hero classes of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				Adjudicator: { label: translatable("fab.attributes.classes.values.adjudicator", "Adjudicator") },
				Assassin: { label: translatable("fab.attributes.classes.values.assassin", "Assassin") },
				Bard: { label: translatable("fab.attributes.classes.values.bard", "Bard") },
				Brute: { label: translatable("fab.attributes.classes.values.brute", "Brute") },
				Generic: { label: translatable("fab.attributes.classes.values.generic", "Generic") },
				Guardian: { label: translatable("fab.attributes.classes.values.guardian", "Guardian") },
				Illusionist: { label: translatable("fab.attributes.classes.values.illusionist", "Illusionist") },
				Mechanologist: { label: translatable("fab.attributes.classes.values.mechanologist", "Mechanologist") },
				Merchant: { label: translatable("fab.attributes.classes.values.merchant", "Merchant") },
				Ninja: { label: translatable("fab.attributes.classes.values.ninja", "Ninja") },
				Ranger: { label: translatable("fab.attributes.classes.values.ranger", "Ranger") },
				Runeblade: { label: translatable("fab.attributes.classes.values.runeblade", "Runeblade") },
				Shapeshifter: { label: translatable("fab.attributes.classes.values.shapeshifter", "Shapeshifter") },
				Warrior: { label: translatable("fab.attributes.classes.values.warrior", "Warrior") },
				Wizard: { label: translatable("fab.attributes.classes.values.wizard", "Wizard") }
			}
		}),
		talents: enumAttribute(FABCardTalent, {
			entity: "card",
			name: translatable("fab.attributes.talents.name", "Talents"),
			description: translatable("fab.attributes.talents.description", "The talents associated with the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				Chaos: { label: translatable("fab.attributes.talents.values.chaos", "Chaos") },
				Draconic: { label: translatable("fab.attributes.talents.values.draconic", "Draconic") },
				Earth: { label: translatable("fab.attributes.talents.values.earth", "Earth") },
				Elemental: { label: translatable("fab.attributes.talents.values.elemental", "Elemental") },
				Ice: { label: translatable("fab.attributes.talents.values.ice", "Ice") },
				Light: { label: translatable("fab.attributes.talents.values.light", "Light") },
				Lightning: { label: translatable("fab.attributes.talents.values.lightning", "Lightning") },
				Mystic: { label: translatable("fab.attributes.talents.values.mystic", "Mystic") },
				Rosetta: { label: translatable("fab.attributes.talents.values.rosetta", "Rosetta") },
				Royal: { label: translatable("fab.attributes.talents.values.royal", "Royal") },
				Shadow: { label: translatable("fab.attributes.talents.values.shadow", "Shadow") }
			}
		}),
		finishes: enumAttribute(FABCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("fab.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: fabFinishValues
		}),
		finish: enumAttribute(FABCardFinish, {
			entity: "inventory",
			name: translatable("fab.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: fabFinishValues
		}),
		types: enumAttribute(FABCardType, {
			entity: "card",
			name: translatable("fab.attributes.types.name", "Types"),
			description: translatable("fab.attributes.types.description", "Types of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				Action: { label: translatable("fab.attributes.types.values.action", "Action") },
				"Attack Reaction": { label: translatable("fab.attributes.types.values.attackReaction", "Attack Reaction") },
				Block: { label: translatable("fab.attributes.types.values.block", "Block") },
				"Defense Reaction": { label: translatable("fab.attributes.types.values.defenseReaction", "Defense Reaction") },
				"Demi-Hero": { label: translatable("fab.attributes.types.values.demiHero", "Demi-Hero") },
				Equipment: { label: translatable("fab.attributes.types.values.equipment", "Equipment") },
				Hero: { label: translatable("fab.attributes.types.values.hero", "Hero") },
				Instant: { label: translatable("fab.attributes.types.values.instant", "Instant") },
				Macro: { label: translatable("fab.attributes.types.values.macro", "Macro") },
				Mentor: { label: translatable("fab.attributes.types.values.mentor", "Mentor") },
				Resource: { label: translatable("fab.attributes.types.values.resource", "Resource") },
				Token: { label: translatable("fab.attributes.types.values.token", "Token") },
				Weapon: { label: translatable("fab.attributes.types.values.weapon", "Weapon") }
			}
		}),
		subTypes: enumAttribute(FABCardSubType, {
			entity: "card",
			name: translatable("fab.attributes.subTypes.name", "Sub-Types"),
			description: translatable("fab.attributes.subTypes.description", "Subtypes of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				"(1H)": { label: translatable("fab.attributes.subTypes.values.oneHanded", "(1H)") },
				"(2H)": { label: translatable("fab.attributes.subTypes.values.twoHanded", "(2H)") },
				Affliction: { label: translatable("fab.attributes.subTypes.values.affliction", "Affliction") },
				Ally: { label: translatable("fab.attributes.subTypes.values.ally", "Ally") },
				Angel: { label: translatable("fab.attributes.subTypes.values.angel", "Angel") },
				Arms: { label: translatable("fab.attributes.subTypes.values.arms", "Arms") },
				Arrow: { label: translatable("fab.attributes.subTypes.values.arrow", "Arrow") },
				Ash: { label: translatable("fab.attributes.subTypes.values.ash", "Ash") },
				Attack: { label: translatable("fab.attributes.subTypes.values.attack", "Attack") },
				Aura: { label: translatable("fab.attributes.subTypes.values.aura", "Aura") },
				Axe: { label: translatable("fab.attributes.subTypes.values.axe", "Axe") },
				Base: { label: translatable("fab.attributes.subTypes.values.base", "Base") },
				Book: { label: translatable("fab.attributes.subTypes.values.book", "Book") },
				Bow: { label: translatable("fab.attributes.subTypes.values.bow", "Bow") },
				Brush: { label: translatable("fab.attributes.subTypes.values.brush", "Brush") },
				Chest: { label: translatable("fab.attributes.subTypes.values.chest", "Chest") },
				Chi: { label: translatable("fab.attributes.subTypes.values.chi", "Chi") },
				Claw: { label: translatable("fab.attributes.subTypes.values.claw", "Claw") },
				Club: { label: translatable("fab.attributes.subTypes.values.club", "Club") },
				Construct: { label: translatable("fab.attributes.subTypes.values.construct", "Construct") },
				Dagger: { label: translatable("fab.attributes.subTypes.values.dagger", "Dagger") },
				Demon: { label: translatable("fab.attributes.subTypes.values.demon", "Demon") },
				Dragon: { label: translatable("fab.attributes.subTypes.values.dragon", "Dragon") },
				Evo: { label: translatable("fab.attributes.subTypes.values.evo", "Evo") },
				Fiddle: { label: translatable("fab.attributes.subTypes.values.fiddle", "Fiddle") },
				Figment: { label: translatable("fab.attributes.subTypes.values.figment", "Figment") },
				Flail: { label: translatable("fab.attributes.subTypes.values.flail", "Flail") },
				Gem: { label: translatable("fab.attributes.subTypes.values.gem", "Gem") },
				Gun: { label: translatable("fab.attributes.subTypes.values.gun", "Gun") },
				Hammer: { label: translatable("fab.attributes.subTypes.values.hammer", "Hammer") },
				Head: { label: translatable("fab.attributes.subTypes.values.head", "Head") },
				Invocation: { label: translatable("fab.attributes.subTypes.values.invocation", "Invocation") },
				Item: { label: translatable("fab.attributes.subTypes.values.item", "Item") },
				Landmark: { label: translatable("fab.attributes.subTypes.values.landmark", "Landmark") },
				Legs: { label: translatable("fab.attributes.subTypes.values.legs", "Legs") },
				Lute: { label: translatable("fab.attributes.subTypes.values.lute", "Lute") },
				Mercenary: { label: translatable("fab.attributes.subTypes.values.mercenary", "Mercenary") },
				"Off-Hand": { label: translatable("fab.attributes.subTypes.values.offHand", "Off-Hand") },
				Orb: { label: translatable("fab.attributes.subTypes.values.orb", "Orb") },
				Pistol: { label: translatable("fab.attributes.subTypes.values.pistol", "Pistol") },
				Polearm: { label: translatable("fab.attributes.subTypes.values.polearm", "Polearm") },
				Quiver: { label: translatable("fab.attributes.subTypes.values.quiver", "Quiver") },
				Rock: { label: translatable("fab.attributes.subTypes.values.rock", "Rock") },
				Scepter: { label: translatable("fab.attributes.subTypes.values.scepter", "Scepter") },
				Scroll: { label: translatable("fab.attributes.subTypes.values.scroll", "Scroll") },
				Scythe: { label: translatable("fab.attributes.subTypes.values.scythe", "Scythe") },
				Shuriken: { label: translatable("fab.attributes.subTypes.values.shuriken", "Shuriken") },
				Song: { label: translatable("fab.attributes.subTypes.values.song", "Song") },
				Staff: { label: translatable("fab.attributes.subTypes.values.staff", "Staff") },
				Sword: { label: translatable("fab.attributes.subTypes.values.sword", "Sword") },
				Trap: { label: translatable("fab.attributes.subTypes.values.trap", "Trap") },
				Wrench: { label: translatable("fab.attributes.subTypes.values.wrench", "Wrench") },
				Young: { label: translatable("fab.attributes.subTypes.values.young", "Young") }
			}
		}),
		color: enumAttribute(FABCardColor, {
			entity: "card",
			name: translatable("fab.attributes.color.name", "Color"),
			description: translatable("fab.attributes.color.description", "The color of the card (pitch value)"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				Red: {
					label: translatable("fab.attributes.color.values.red", "Red"),
					image: "/images/fab/pitch-1.webp"
				},
				Yellow: {
					label: translatable("fab.attributes.color.values.yellow", "Yellow"),
					image: "/images/fab/pitch-2.webp"
				},
				Blue: {
					label: translatable("fab.attributes.color.values.blue", "Blue"),
					image: "/images/fab/pitch-3.webp"
				}
			}
		}),
		printNumber: printNumberAttribute,
		fabId: {
			entity: "variant",
			name: translatable("fab.attributes.fabId.name", "Fab ID"),
			description: "The id of the card in the set",
			type: "string",
			array: false
		},
		cost: {
			entity: "card",
			name: translatable("fab.attributes.cost.name", "Cost"),
			description: translatable("fab.attributes.cost.description", "The cost to play the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		pitch: {
			entity: "card",
			name: translatable("fab.attributes.pitch.name", "Pitch"),
			description: translatable("fab.attributes.pitch.description", "The pitch value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		attack: {
			entity: "card",
			name: translatable("fab.attributes.power.name", "Power"),
			description: translatable("fab.attributes.power.description", "The power of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		defense: {
			entity: "card",
			name: translatable("fab.attributes.defense.name", "Defense"),
			description: translatable("fab.attributes.defense.description", "The defense of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		intellect: {
			entity: "card",
			name: translatable("fab.attributes.intellect.name", "Intellect"),
			description: translatable("fab.attributes.intellect.description", "The intellect of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		life: {
			entity: "card",
			name: translatable("fab.attributes.life.name", "Life"),
			description: translatable("fab.attributes.life.description", "The life of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		variant: enumAttribute(FABCardVariant, {
			entity: "variant",
			name: translatable("fab.attributes.variant.name", "Variant"),
			description: translatable("fab.attributes.variant.description", "The variant of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				Unlimited: { label: translatable("fab.attributes.variant.values.unlimited", "Unlimited") },
				Marvel: { label: translatable("fab.attributes.variant.values.marvel", "Marvel") },
				"Marvel Alternate Art": { label: translatable("fab.attributes.variant.values.marvelAlternateArt", "Marvel Alternate Art") },
				"Extended Art": { label: translatable("fab.attributes.variant.values.extendedArt", "Extended Art") },
				"Alternate Art": { label: translatable("fab.attributes.variant.values.alternateArt", "Alternate Art") },
				"Alternate Art #A": { label: translatable("fab.attributes.variant.values.alternateArtA", "Alternate Art #A") },
				"Alternate Art #B": { label: translatable("fab.attributes.variant.values.alternateArtB", "Alternate Art #B") },
				"Alternate Art #C": { label: translatable("fab.attributes.variant.values.alternateArtC", "Alternate Art #C") },
				CC: { label: translatable("fab.attributes.variant.values.classicConstructed", "Classic Constructed") }
			}
		}),
		description: {
			entity: "card",
			name: translatable("fab.attributes.description.name", "Description"),
			description: translatable("fab.attributes.description.description", "Card ability text and description"),
			type: "string",
			array: false
		},
		artist: {
			entity: "variant",
			name: translatable("fab.attributes.artist.name", "Artist"),
			description: translatable("fab.attributes.artist.description", "The artist who created the card art"),
			type: "string",
			array: false
		},
		flavorText: {
			entity: "variant",
			name: translatable("fab.attributes.flavorText.name", "Flavor Text"),
			description: translatable("fab.attributes.flavorText.description", "The flavor text on the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"classes",
			"rarity"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"types",
			"subTypes",
			"talents",
			"color",
			"cost",
			"pitch",
			"attack",
			"defense"
		]
	}],
	productDetailsOrder: {
		details: ["description", "flavorText"],
		statistics: [
			"classes",
			"talents",
			"types",
			"subTypes",
			"abilities",
			"rarity",
			"cost",
			"pitch",
			"attack",
			"defense",
			"intellect",
			"life",
			"finish",
			"color",
			"artist"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: fabListPresets,
	finishHierarchy: [
		CardFinish.STANDARD,
		CardFinish.RAINBOW_FOIL,
		CardFinish.COLD_FOIL,
		CardFinish.GOLD_FOIL
	],
	basicAttributes: ["color", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Cardmarket, SupportedMarketplace.Tcgplayer],
	vendorIds: [
		{
			key: "fabId",
			label: "FAB ID",
			isMarketplaceId: false
		},
		cardmarketVendorId,
		tcgplayerVendorId
	],
	csvTemplateExample: {
		vendorIds: {
			Cardmarket: "750720",
			TCGplayer: "533448",
			fabId: "rhinar-reckless-rampage"
		},
		printNumber: "HVY003",
		cardName: "Rhinar, Reckless Rampage",
		expansion: "Heavy Hitters",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/grand-archive/grand-archive.types.ts
let GrandArchiveRarity = /* @__PURE__ */ function(GrandArchiveRarity) {
	GrandArchiveRarity["Common"] = "Common";
	GrandArchiveRarity["Uncommon"] = "Uncommon";
	GrandArchiveRarity["Rare"] = "Rare";
	GrandArchiveRarity["SuperRare"] = "Super Rare";
	GrandArchiveRarity["UltraRare"] = "Ultra Rare";
	GrandArchiveRarity["PromotionalRare"] = "Promotional Rare";
	GrandArchiveRarity["CollectorSuperRare"] = "Collector Super Rare";
	GrandArchiveRarity["CollectorUltraRare"] = "Collector Ultra Rare";
	GrandArchiveRarity["CollectorPromoRare"] = "Collector Promo Rare";
	return GrandArchiveRarity;
}({});
let GrandArchiveFinish = /* @__PURE__ */ function(GrandArchiveFinish) {
	GrandArchiveFinish["Standard"] = "Standard";
	GrandArchiveFinish["Foil"] = "Foil";
	return GrandArchiveFinish;
}({});
let GrandArchiveVariant = /* @__PURE__ */ function(GrandArchiveVariant) {
	GrandArchiveVariant["CurioFoil"] = "Curio Foil";
	GrandArchiveVariant["QuicksilverFoil"] = "Quicksilver Foil";
	return GrandArchiveVariant;
}({});
let GrandArchiveElement = /* @__PURE__ */ function(GrandArchiveElement) {
	GrandArchiveElement["Arcane"] = "Arcane";
	GrandArchiveElement["Astra"] = "Astra";
	GrandArchiveElement["Crux"] = "Crux";
	GrandArchiveElement["Exalted"] = "Exalted";
	GrandArchiveElement["Exia"] = "Exia";
	GrandArchiveElement["Fire"] = "Fire";
	GrandArchiveElement["Luxem"] = "Luxem";
	GrandArchiveElement["Neos"] = "Neos";
	GrandArchiveElement["Norm"] = "Norm";
	GrandArchiveElement["Tera"] = "Tera";
	GrandArchiveElement["Umbra"] = "Umbra";
	GrandArchiveElement["Water"] = "Water";
	GrandArchiveElement["Wind"] = "Wind";
	return GrandArchiveElement;
}({});
let GrandArchiveCardType = /* @__PURE__ */ function(GrandArchiveCardType) {
	GrandArchiveCardType["Action"] = "Action";
	GrandArchiveCardType["Ally"] = "Ally";
	GrandArchiveCardType["Attack"] = "Attack";
	GrandArchiveCardType["Champion"] = "Champion";
	GrandArchiveCardType["Domain"] = "Domain";
	GrandArchiveCardType["GreaterBoon"] = "Greater Boon";
	GrandArchiveCardType["Item"] = "Item";
	GrandArchiveCardType["LesserBoon"] = "Lesser Boon";
	GrandArchiveCardType["Mastery"] = "Mastery";
	GrandArchiveCardType["Phantasia"] = "Phantasia";
	GrandArchiveCardType["Regalia"] = "Regalia";
	GrandArchiveCardType["Status"] = "Status";
	GrandArchiveCardType["Token"] = "Token";
	GrandArchiveCardType["Unique"] = "Unique";
	GrandArchiveCardType["Weapon"] = "Weapon";
	return GrandArchiveCardType;
}({});
let GrandArchiveCardClass = /* @__PURE__ */ function(GrandArchiveCardClass) {
	GrandArchiveCardClass["Anomaly"] = "Anomaly";
	GrandArchiveCardClass["Assassin"] = "Assassin";
	GrandArchiveCardClass["Cleric"] = "Cleric";
	GrandArchiveCardClass["Guardian"] = "Guardian";
	GrandArchiveCardClass["Mage"] = "Mage";
	GrandArchiveCardClass["Ranger"] = "Ranger";
	GrandArchiveCardClass["Spirit"] = "Spirit";
	GrandArchiveCardClass["Tamer"] = "Tamer";
	GrandArchiveCardClass["Warrior"] = "Warrior";
	return GrandArchiveCardClass;
}({});
let GrandArchiveCostType = /* @__PURE__ */ function(GrandArchiveCostType) {
	GrandArchiveCostType["Memory"] = "Memory";
	GrandArchiveCostType["Reserve"] = "Reserve";
	GrandArchiveCostType["None"] = "None";
	return GrandArchiveCostType;
}({});
let GrandArchiveSpeed = /* @__PURE__ */ function(GrandArchiveSpeed) {
	GrandArchiveSpeed["Fast"] = "Fast";
	GrandArchiveSpeed["Slow"] = "Slow";
	return GrandArchiveSpeed;
}({});

//#endregion
//#region src/lib/games/grand-archive/grand-archive.presets.ts
const grandArchiveListPresets = {
	Standard: {
		name: translatable("grand-archive.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("grand-archive.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [GrandArchiveFinish.Standard] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("grand-archive.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("grand-archive.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [GrandArchiveFinish.Standard] },
			quantity: 4
		}]
	},
	Foil: {
		name: translatable("grand-archive.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("grand-archive.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [GrandArchiveFinish.Foil] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("grand-archive.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("grand-archive.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [{
			filters: { finish: [GrandArchiveFinish.Foil] },
			quantity: 4
		}]
	},
	"Master Set": {
		name: translatable("grand-archive.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("grand-archive.presets.masterset.description", "A complete set with one copy of each card in both standard and foil finishes"),
		matchers: [{
			filters: { finish: [GrandArchiveFinish.Standard] },
			quantity: 1
		}, {
			filters: { finish: [GrandArchiveFinish.Foil] },
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/grand-archive/grand-archive.game.ts
const grandArchiveFinishValues = {
	[GrandArchiveFinish.Standard]: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	[GrandArchiveFinish.Foil]: { label: translatable("global.attributes.finish.values.foil", "Foil") }
};
const grandArchiveFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(GrandArchiveRarity).optional(),
	finish: makeZodEnumFilter(GrandArchiveFinish).optional(),
	finishes: makeZodEnumFilter(GrandArchiveFinish).optional(),
	variant: makeZodEnumFilter(GrandArchiveVariant).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	element: makeZodEnumFilter(GrandArchiveElement).optional(),
	cardType: makeZodEnumFilter(GrandArchiveCardType).optional(),
	cardClass: makeZodEnumFilter(GrandArchiveCardClass).optional(),
	costType: makeZodEnumFilter(GrandArchiveCostType).optional(),
	costValue: zodNumericFilter.optional(),
	power: zodNumericFilter.optional(),
	life: zodNumericFilter.optional(),
	durability: zodNumericFilter.optional(),
	level: zodNumericFilter.optional(),
	speed: makeZodEnumFilter(GrandArchiveSpeed).optional()
});
const GrandArchiveGameConfiguration = {
	name: "Grand Archive",
	shortName: "Grand Archive",
	slug: "grand-archive",
	icon: {
		dark: "grand-archive-icon.png",
		light: "grand-archive-icon.png"
	},
	image: {
		dark: "grand-archive.png",
		light: "grand-archive.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(GrandArchiveFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: grandArchiveFinishValues
		}),
		finish: enumAttribute(GrandArchiveFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: grandArchiveFinishValues
		}),
		variant: enumAttribute(GrandArchiveVariant, {
			entity: "variant",
			name: translatable("grand-archive.attributes.variant.name", "Variant"),
			description: translatable("grand-archive.attributes.variant.description", "Special print treatment of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[GrandArchiveVariant.CurioFoil]: { label: translatable("grand-archive.attributes.variant.values.curio_foil", "Curio Foil") },
				[GrandArchiveVariant.QuicksilverFoil]: { label: translatable("grand-archive.attributes.variant.values.quicksilver_foil", "Quicksilver Foil") }
			}
		}),
		rarity: enumAttribute(GrandArchiveRarity, {
			entity: "variant",
			name: translatable("global.attributes.rarity.name", "Rarity"),
			description: translatable("global.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[GrandArchiveRarity.Common]: { label: translatable("grand-archive.attributes.rarity.values.common", "Common") },
				[GrandArchiveRarity.Uncommon]: { label: translatable("grand-archive.attributes.rarity.values.uncommon", "Uncommon") },
				[GrandArchiveRarity.Rare]: { label: translatable("grand-archive.attributes.rarity.values.rare", "Rare") },
				[GrandArchiveRarity.SuperRare]: { label: translatable("grand-archive.attributes.rarity.values.super_rare", "Super Rare") },
				[GrandArchiveRarity.UltraRare]: { label: translatable("grand-archive.attributes.rarity.values.ultra_rare", "Ultra Rare") },
				[GrandArchiveRarity.PromotionalRare]: { label: translatable("grand-archive.attributes.rarity.values.promotional_rare", "Promotional Rare") },
				[GrandArchiveRarity.CollectorSuperRare]: { label: translatable("grand-archive.attributes.rarity.values.collector_super_rare", "Collector Super Rare") },
				[GrandArchiveRarity.CollectorUltraRare]: { label: translatable("grand-archive.attributes.rarity.values.collector_ultra_rare", "Collector Ultra Rare") },
				[GrandArchiveRarity.CollectorPromoRare]: { label: translatable("grand-archive.attributes.rarity.values.collector_promo_rare", "Collector Promo Rare") }
			}
		}),
		printNumber: printNumberAttribute,
		element: enumAttribute(GrandArchiveElement, {
			entity: "card",
			name: translatable("grand-archive.attributes.element.name", "Element"),
			description: translatable("grand-archive.attributes.element.description", "The elemental affinity of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[GrandArchiveElement.Arcane]: {
					label: translatable("grand-archive.attributes.element.values.arcane", "Arcane"),
					image: "/images/grand-archive/elements/arcane.png"
				},
				[GrandArchiveElement.Astra]: {
					label: translatable("grand-archive.attributes.element.values.astra", "Astra"),
					image: "/images/grand-archive/elements/astra.png"
				},
				[GrandArchiveElement.Crux]: {
					label: translatable("grand-archive.attributes.element.values.crux", "Crux"),
					image: "/images/grand-archive/elements/crux.png"
				},
				[GrandArchiveElement.Exalted]: {
					label: translatable("grand-archive.attributes.element.values.exalted", "Exalted"),
					image: "/images/grand-archive/elements/exalted.png"
				},
				[GrandArchiveElement.Exia]: {
					label: translatable("grand-archive.attributes.element.values.exia", "Exia"),
					image: "/images/grand-archive/elements/exia.png"
				},
				[GrandArchiveElement.Fire]: {
					label: translatable("grand-archive.attributes.element.values.fire", "Fire"),
					image: "/images/grand-archive/elements/fire.png"
				},
				[GrandArchiveElement.Luxem]: {
					label: translatable("grand-archive.attributes.element.values.luxem", "Luxem"),
					image: "/images/grand-archive/elements/luxem.png"
				},
				[GrandArchiveElement.Neos]: {
					label: translatable("grand-archive.attributes.element.values.neos", "Neos"),
					image: "/images/grand-archive/elements/neos.png"
				},
				[GrandArchiveElement.Norm]: {
					label: translatable("grand-archive.attributes.element.values.norm", "Norm"),
					image: "/images/grand-archive/elements/norm.png"
				},
				[GrandArchiveElement.Tera]: {
					label: translatable("grand-archive.attributes.element.values.tera", "Tera"),
					image: "/images/grand-archive/elements/tera.png"
				},
				[GrandArchiveElement.Umbra]: {
					label: translatable("grand-archive.attributes.element.values.umbra", "Umbra"),
					image: "/images/grand-archive/elements/umbra.png"
				},
				[GrandArchiveElement.Water]: {
					label: translatable("grand-archive.attributes.element.values.water", "Water"),
					image: "/images/grand-archive/elements/water.png"
				},
				[GrandArchiveElement.Wind]: {
					label: translatable("grand-archive.attributes.element.values.wind", "Wind"),
					image: "/images/grand-archive/elements/wind.png"
				}
			}
		}),
		cardType: enumAttribute(GrandArchiveCardType, {
			entity: "card",
			name: translatable("grand-archive.attributes.cardType.name", "Card Type"),
			description: translatable("grand-archive.attributes.cardType.description", "The type of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[GrandArchiveCardType.Action]: {
					label: translatable("grand-archive.attributes.cardType.values.action", "Action"),
					image: "/images/grand-archive/types/action.png"
				},
				[GrandArchiveCardType.Ally]: {
					label: translatable("grand-archive.attributes.cardType.values.ally", "Ally"),
					image: "/images/grand-archive/types/ally.png"
				},
				[GrandArchiveCardType.Attack]: {
					label: translatable("grand-archive.attributes.cardType.values.attack", "Attack"),
					image: "/images/grand-archive/types/attack.png"
				},
				[GrandArchiveCardType.Champion]: {
					label: translatable("grand-archive.attributes.cardType.values.champion", "Champion"),
					image: "/images/grand-archive/types/champion.png"
				},
				[GrandArchiveCardType.Domain]: {
					label: translatable("grand-archive.attributes.cardType.values.domain", "Domain"),
					image: "/images/grand-archive/types/domain.png"
				},
				[GrandArchiveCardType.GreaterBoon]: {
					label: translatable("grand-archive.attributes.cardType.values.greater_boon", "Greater Boon"),
					image: "/images/grand-archive/types/greater-boon.png"
				},
				[GrandArchiveCardType.Item]: {
					label: translatable("grand-archive.attributes.cardType.values.item", "Item"),
					image: "/images/grand-archive/types/item.png"
				},
				[GrandArchiveCardType.LesserBoon]: {
					label: translatable("grand-archive.attributes.cardType.values.lesser_boon", "Lesser Boon"),
					image: "/images/grand-archive/types/lesser-boon.png"
				},
				[GrandArchiveCardType.Mastery]: {
					label: translatable("grand-archive.attributes.cardType.values.mastery", "Mastery"),
					image: "/images/grand-archive/types/mastery.png"
				},
				[GrandArchiveCardType.Phantasia]: {
					label: translatable("grand-archive.attributes.cardType.values.phantasia", "Phantasia"),
					image: "/images/grand-archive/types/phantasia.png"
				},
				[GrandArchiveCardType.Regalia]: {
					label: translatable("grand-archive.attributes.cardType.values.regalia", "Regalia"),
					image: "/images/grand-archive/types/item-regalia.png"
				},
				[GrandArchiveCardType.Status]: {
					label: translatable("grand-archive.attributes.cardType.values.status", "Status"),
					image: "/images/grand-archive/types/status.png"
				},
				[GrandArchiveCardType.Token]: { label: translatable("grand-archive.attributes.cardType.values.token", "Token") },
				[GrandArchiveCardType.Unique]: { label: translatable("grand-archive.attributes.cardType.values.unique", "Unique") },
				[GrandArchiveCardType.Weapon]: {
					label: translatable("grand-archive.attributes.cardType.values.weapon", "Weapon"),
					image: "/images/grand-archive/types/weapon.png"
				}
			}
		}),
		cardClass: enumAttribute(GrandArchiveCardClass, {
			entity: "card",
			name: translatable("grand-archive.attributes.cardClass.name", "Class"),
			description: translatable("grand-archive.attributes.cardClass.description", "The class of the champion"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[GrandArchiveCardClass.Anomaly]: { label: translatable("grand-archive.attributes.cardClass.values.anomaly", "Anomaly") },
				[GrandArchiveCardClass.Assassin]: {
					label: translatable("grand-archive.attributes.cardClass.values.assassin", "Assassin"),
					image: "/images/grand-archive/classes/assassin.png"
				},
				[GrandArchiveCardClass.Cleric]: {
					label: translatable("grand-archive.attributes.cardClass.values.cleric", "Cleric"),
					image: "/images/grand-archive/classes/cleric.png"
				},
				[GrandArchiveCardClass.Guardian]: {
					label: translatable("grand-archive.attributes.cardClass.values.guardian", "Guardian"),
					image: "/images/grand-archive/classes/guardian.png"
				},
				[GrandArchiveCardClass.Mage]: {
					label: translatable("grand-archive.attributes.cardClass.values.mage", "Mage"),
					image: "/images/grand-archive/classes/mage.png"
				},
				[GrandArchiveCardClass.Ranger]: {
					label: translatable("grand-archive.attributes.cardClass.values.ranger", "Ranger"),
					image: "/images/grand-archive/classes/ranger.png"
				},
				[GrandArchiveCardClass.Spirit]: { label: translatable("grand-archive.attributes.cardClass.values.spirit", "Spirit") },
				[GrandArchiveCardClass.Tamer]: {
					label: translatable("grand-archive.attributes.cardClass.values.tamer", "Tamer"),
					image: "/images/grand-archive/classes/tamer.png"
				},
				[GrandArchiveCardClass.Warrior]: {
					label: translatable("grand-archive.attributes.cardClass.values.warrior", "Warrior"),
					image: "/images/grand-archive/classes/warrior.png"
				}
			}
		}),
		costType: enumAttribute(GrandArchiveCostType, {
			entity: "card",
			name: translatable("grand-archive.attributes.costType.name", "Cost Type"),
			description: translatable("grand-archive.attributes.costType.description", "The type of cost to play the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[GrandArchiveCostType.Memory]: {
					label: translatable("grand-archive.attributes.costType.values.memory", "Memory"),
					image: "/images/grand-archive/costs/memory.png"
				},
				[GrandArchiveCostType.Reserve]: {
					label: translatable("grand-archive.attributes.costType.values.reserve", "Reserve"),
					image: "/images/grand-archive/costs/reserve.png"
				},
				[GrandArchiveCostType.None]: { label: translatable("grand-archive.attributes.costType.values.none", "None") }
			}
		}),
		costValue: {
			entity: "card",
			name: translatable("grand-archive.attributes.costValue.name", "Cost"),
			description: translatable("grand-archive.attributes.costValue.description", "The cost value to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		power: {
			entity: "card",
			name: translatable("grand-archive.attributes.power.name", "Power"),
			description: translatable("grand-archive.attributes.power.description", "Power value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		life: {
			entity: "card",
			name: translatable("grand-archive.attributes.life.name", "Life"),
			description: translatable("grand-archive.attributes.life.description", "Life value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		durability: {
			entity: "card",
			name: translatable("grand-archive.attributes.durability.name", "Durability"),
			description: translatable("grand-archive.attributes.durability.description", "Durability value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		level: {
			entity: "card",
			name: translatable("grand-archive.attributes.level.name", "Level"),
			description: translatable("grand-archive.attributes.level.description", "Level of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		speed: enumAttribute(GrandArchiveSpeed, {
			entity: "card",
			name: translatable("grand-archive.attributes.speed.name", "Speed"),
			description: translatable("grand-archive.attributes.speed.description", "Speed of the card (Fast or Slow)"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[GrandArchiveSpeed.Fast]: { label: translatable("grand-archive.attributes.speed.values.fast", "Fast") },
				[GrandArchiveSpeed.Slow]: { label: translatable("grand-archive.attributes.speed.values.slow", "Slow") }
			}
		}),
		rulesText: {
			entity: "variant",
			name: translatable("grand-archive.attributes.rulesText.name", "Rules Text"),
			description: translatable("grand-archive.attributes.rulesText.description", "Card rules and effect text"),
			type: "string",
			array: false
		},
		flavorText: {
			entity: "variant",
			name: translatable("grand-archive.attributes.flavorText.name", "Flavor Text"),
			description: translatable("grand-archive.attributes.flavorText.description", "Card flavor text"),
			type: "string",
			array: false
		},
		artist: {
			entity: "variant",
			name: translatable("grand-archive.attributes.artist.name", "Artist"),
			description: translatable("grand-archive.attributes.artist.description", "Card illustrator"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"finishes",
			"variant",
			"element",
			"cardType"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"cardClass",
			"costType",
			"costValue",
			"power",
			"life",
			"durability",
			"level",
			"speed"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText", "flavorText"],
		statistics: [
			"cardType",
			"element",
			"cardClass",
			"rarity",
			"variant",
			"costType",
			"costValue",
			"power",
			"life",
			"durability",
			"level",
			"speed",
			"artist"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: grandArchiveListPresets,
	basicAttributes: ["rarity", "element"],
	supportedMarketplaces: [SupportedMarketplace.Tcgplayer],
	vendorIds: [tcgplayerVendorId],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "DOA-001",
		cardName: "Lorraine, the Lone Wolf",
		expansion: "Dawn of Ashes",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/gundam/gundam.types.ts
let GundamRarity = /* @__PURE__ */ function(GundamRarity) {
	GundamRarity["COMMON"] = "common";
	GundamRarity["COMMON_PLUS"] = "common_plus";
	GundamRarity["COMMON_DOUBLE_PLUS"] = "common_double_plus";
	GundamRarity["UNCOMMON"] = "uncommon";
	GundamRarity["UNCOMMON_PLUS"] = "uncommon_plus";
	GundamRarity["RARE"] = "rare";
	GundamRarity["RARE_PLUS"] = "rare_plus";
	GundamRarity["LEGEND_RARE"] = "legend_rare";
	GundamRarity["LEGEND_RARE_PLUS"] = "legend_rare_plus";
	GundamRarity["LEGEND_RARE_DOUBLE_PLUS"] = "legend_rare_double_plus";
	GundamRarity["PROMO"] = "promo";
	return GundamRarity;
}({});
let GundamFinish = /* @__PURE__ */ function(GundamFinish) {
	GundamFinish["STANDARD"] = "Standard";
	GundamFinish["HOLOFOIL"] = "Holofoil";
	return GundamFinish;
}({});
let GundamColor = /* @__PURE__ */ function(GundamColor) {
	GundamColor["BLUE"] = "Blue";
	GundamColor["GREEN"] = "Green";
	GundamColor["RED"] = "Red";
	GundamColor["WHITE"] = "White";
	GundamColor["PURPLE"] = "Purple";
	return GundamColor;
}({});
let GundamCardType = /* @__PURE__ */ function(GundamCardType) {
	GundamCardType["UNIT"] = "Unit";
	GundamCardType["PILOT"] = "Pilot";
	GundamCardType["COMMAND"] = "Command";
	GundamCardType["BASE"] = "Base";
	GundamCardType["RESOURCE"] = "Resource";
	GundamCardType["EX_BASE"] = "EX Base";
	GundamCardType["EX_RESOURCE"] = "EX Resource";
	return GundamCardType;
}({});

//#endregion
//#region src/lib/games/gundam/gundam.presets.ts
const gundamListPresets = {
	Standard: {
		name: translatable("gundam.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("gundam.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [GundamFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("gundam.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("gundam.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [GundamFinish.STANDARD] },
			quantity: 4
		}]
	},
	Foil: {
		name: translatable("gundam.presets.foil.name", "Holofoil Set"),
		type: ListPresetType.FOIL,
		description: translatable("gundam.presets.foil.description", "A complete set with one copy of each card in holofoil finish"),
		matchers: [{
			filters: { finish: [GundamFinish.HOLOFOIL] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("gundam.presets.foilPlayset.name", "Holofoil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("gundam.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in holofoil finish"),
		matchers: [{
			filters: { finish: [GundamFinish.HOLOFOIL] },
			quantity: 4
		}]
	},
	"Master Set": {
		name: translatable("gundam.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("gundam.presets.masterset.description", "A complete set with one copy of each card in both standard and holofoil finishes"),
		matchers: [{
			filters: { finish: [GundamFinish.STANDARD] },
			quantity: 1
		}, {
			filters: { finish: [GundamFinish.HOLOFOIL] },
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/gundam/gundam.game.ts
const gundamFinishValues = {
	Standard: { label: translatable("gundam.attributes.finish.values.standard", "Standard") },
	Holofoil: { label: translatable("gundam.attributes.finish.values.holofoil", "Holofoil") }
};
const gundamFilters = z.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	finish: makeZodEnumFilter(GundamFinish).optional(),
	finishes: makeZodEnumFilter(GundamFinish).optional(),
	rarity: makeZodEnumFilter(GundamRarity).optional(),
	color: makeZodEnumFilter(GundamColor).optional(),
	type: makeZodEnumFilter(GundamCardType).optional(),
	level: zodNumericFilter.optional(),
	cost: zodNumericFilter.optional(),
	attackPoints: zodNumericFilter.optional(),
	hitPoints: zodNumericFilter.optional()
});
const GundamGameConfiguration = {
	name: "Gundam Card Game",
	shortName: "Gundam",
	slug: "gundam",
	icon: {
		dark: "gundam-icon.png",
		light: "gundam-icon.png"
	},
	image: {
		dark: "gundam.png",
		light: "gundam.png"
	},
	attributes: {
		name: nameAttribute,
		rarity: enumAttribute(GundamRarity, {
			entity: "variant",
			name: translatable("gundam.attributes.rarity.name", "Rarity"),
			description: translatable("gundam.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[GundamRarity.COMMON]: { label: translatable("gundam.attributes.rarity.values.common", "Common") },
				[GundamRarity.COMMON_PLUS]: { label: translatable("gundam.attributes.rarity.values.common_plus", "Common +") },
				[GundamRarity.COMMON_DOUBLE_PLUS]: { label: translatable("gundam.attributes.rarity.values.common_double_plus", "Common ++") },
				[GundamRarity.UNCOMMON]: { label: translatable("gundam.attributes.rarity.values.uncommon", "Uncommon") },
				[GundamRarity.UNCOMMON_PLUS]: { label: translatable("gundam.attributes.rarity.values.uncommon_plus", "Uncommon +") },
				[GundamRarity.RARE]: { label: translatable("gundam.attributes.rarity.values.rare", "Rare") },
				[GundamRarity.RARE_PLUS]: { label: translatable("gundam.attributes.rarity.values.rare_plus", "Rare +") },
				[GundamRarity.LEGEND_RARE]: { label: translatable("gundam.attributes.rarity.values.legend_rare", "Legend Rare") },
				[GundamRarity.LEGEND_RARE_PLUS]: { label: translatable("gundam.attributes.rarity.values.legend_rare_plus", "Legend Rare +") },
				[GundamRarity.LEGEND_RARE_DOUBLE_PLUS]: { label: translatable("gundam.attributes.rarity.values.legend_rare_double_plus", "Legend Rare ++") },
				[GundamRarity.PROMO]: { label: translatable("gundam.attributes.rarity.values.promo", "Promo") }
			}
		}),
		color: enumAttribute(GundamColor, {
			entity: "card",
			name: translatable("gundam.attributes.color.name", "Color"),
			description: translatable("gundam.attributes.color.description", "The color of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[GundamColor.BLUE]: { label: translatable("gundam.attributes.color.values.blue", "Blue") },
				[GundamColor.GREEN]: { label: translatable("gundam.attributes.color.values.green", "Green") },
				[GundamColor.RED]: { label: translatable("gundam.attributes.color.values.red", "Red") },
				[GundamColor.WHITE]: { label: translatable("gundam.attributes.color.values.white", "White") },
				[GundamColor.PURPLE]: { label: translatable("gundam.attributes.color.values.purple", "Purple") }
			}
		}),
		finishes: enumAttribute(GundamFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("gundam.attributes.finish.name", "Finish"),
			description: translatable("gundam.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: gundamFinishValues
		}),
		finish: enumAttribute(GundamFinish, {
			entity: "inventory",
			name: translatable("gundam.attributes.finish.name", "Finish"),
			description: translatable("gundam.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: gundamFinishValues
		}),
		printNumber: printNumberAttribute,
		type: enumAttribute(GundamCardType, {
			entity: "card",
			name: translatable("gundam.attributes.type.name", "Card Type"),
			description: translatable("gundam.attributes.type.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[GundamCardType.UNIT]: { label: translatable("gundam.attributes.type.values.unit", "Unit") },
				[GundamCardType.PILOT]: { label: translatable("gundam.attributes.type.values.pilot", "Pilot") },
				[GundamCardType.COMMAND]: { label: translatable("gundam.attributes.type.values.command", "Command") },
				[GundamCardType.BASE]: { label: translatable("gundam.attributes.type.values.base", "Base") },
				[GundamCardType.RESOURCE]: { label: translatable("gundam.attributes.type.values.resource", "Resource") },
				[GundamCardType.EX_BASE]: { label: translatable("gundam.attributes.type.values.ex_base", "EX Base") },
				[GundamCardType.EX_RESOURCE]: { label: translatable("gundam.attributes.type.values.ex_resource", "EX Resource") }
			}
		}),
		level: {
			entity: "card",
			name: translatable("gundam.attributes.level.name", "Level"),
			description: translatable("gundam.attributes.level.description", "The level of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		cost: {
			entity: "card",
			name: translatable("gundam.attributes.cost.name", "Cost"),
			description: translatable("gundam.attributes.cost.description", "Cost to play the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		attackPoints: {
			entity: "card",
			name: translatable("gundam.attributes.attackPoints.name", "Attack Points"),
			description: translatable("gundam.attributes.attackPoints.description", "The attack points of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		hitPoints: {
			entity: "card",
			name: translatable("gundam.attributes.hitPoints.name", "Hit Points"),
			description: translatable("gundam.attributes.hitPoints.description", "The hit points of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		traits: {
			entity: "card",
			name: translatable("gundam.attributes.traits.name", "Traits"),
			description: translatable("gundam.attributes.traits.description", "The traits of the card"),
			type: "string",
			array: true
		},
		linkCondition: {
			entity: "card",
			name: translatable("gundam.attributes.linkCondition.name", "Link Condition"),
			description: translatable("gundam.attributes.linkCondition.description", "The link condition of the card"),
			type: "string",
			array: false
		},
		rulesText: {
			entity: "variant",
			name: translatable("gundam.attributes.rulesText.name", "Rules Text"),
			description: translatable("gundam.attributes.rulesText.description", "The rules text of the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"rarity",
			"color"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"type",
			"level",
			"cost",
			"attackPoints",
			"hitPoints"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText"],
		statistics: [
			"rarity",
			"color",
			"finish",
			"type",
			"level",
			"cost",
			"attackPoints",
			"hitPoints",
			"traits",
			"linkCondition"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: gundamListPresets,
	finishHierarchy: [CardFinish.STANDARD, CardFinish.HOLOFOIL],
	basicAttributes: ["color", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Tcgplayer],
	vendorIds: [tcgplayerVendorId],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "GD01-001",
		cardName: "Gundam",
		expansion: "Newtype Rising",
		expansionCode: "GD01",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/lorcana/lorcana.types.ts
let LorcanaCardFinish = /* @__PURE__ */ function(LorcanaCardFinish) {
	LorcanaCardFinish["STANDARD"] = "Standard";
	LorcanaCardFinish["FOIL"] = "Foil";
	return LorcanaCardFinish;
}({});
let LorcanaRarity = /* @__PURE__ */ function(LorcanaRarity) {
	LorcanaRarity["COMMON"] = "Common";
	LorcanaRarity["UNCOMMON"] = "Uncommon";
	LorcanaRarity["RARE"] = "Rare";
	LorcanaRarity["SUPER_RARE"] = "Super Rare";
	LorcanaRarity["LEGENDARY"] = "Legendary";
	LorcanaRarity["ENCHANTED"] = "Enchanted";
	LorcanaRarity["EPIC"] = "Epic";
	LorcanaRarity["ICONIC"] = "Iconic";
	LorcanaRarity["PROMO"] = "Promo";
	return LorcanaRarity;
}({});
let LorcanaInk = /* @__PURE__ */ function(LorcanaInk) {
	LorcanaInk["INKABLE"] = "Inkable";
	LorcanaInk["NON_INKABLE"] = "NonInkable";
	return LorcanaInk;
}({});
let LorcanaCardColor = /* @__PURE__ */ function(LorcanaCardColor) {
	LorcanaCardColor["AMBER"] = "Amber";
	LorcanaCardColor["AMETHYST"] = "Amethyst";
	LorcanaCardColor["EMERALD"] = "Emerald";
	LorcanaCardColor["RUBY"] = "Ruby";
	LorcanaCardColor["SAPPHIRE"] = "Sapphire";
	LorcanaCardColor["STEEL"] = "Steel";
	return LorcanaCardColor;
}({});
let LorcanaCardType = /* @__PURE__ */ function(LorcanaCardType) {
	LorcanaCardType["ACTION"] = "Action";
	LorcanaCardType["CHARACTER"] = "Character";
	LorcanaCardType["ITEM"] = "Item";
	LorcanaCardType["LOCATION"] = "Location";
	return LorcanaCardType;
}({});
let LorcanaCardTrait = /* @__PURE__ */ function(LorcanaCardTrait) {
	LorcanaCardTrait["Action"] = "Action";
	LorcanaCardTrait["Alien"] = "Alien";
	LorcanaCardTrait["Ally"] = "Ally";
	LorcanaCardTrait["Boss"] = "Boss";
	LorcanaCardTrait["Broom"] = "Broom";
	LorcanaCardTrait["Captain"] = "Captain";
	LorcanaCardTrait["Colossus"] = "Colossus";
	LorcanaCardTrait["Deity"] = "Deity";
	LorcanaCardTrait["Detective"] = "Detective";
	LorcanaCardTrait["Dinosaur"] = "Dinosaur";
	LorcanaCardTrait["Dragon"] = "Dragon";
	LorcanaCardTrait["Dreamborn"] = "Dreamborn";
	LorcanaCardTrait["Entangled"] = "Entangled";
	LorcanaCardTrait["Fairy"] = "Fairy";
	LorcanaCardTrait["Floodborn"] = "Floodborn";
	LorcanaCardTrait["Gargoyle"] = "Gargoyle";
	LorcanaCardTrait["Ghost"] = "Ghost";
	LorcanaCardTrait["Giant"] = "Giant";
	LorcanaCardTrait["Hero"] = "Hero";
	LorcanaCardTrait["Hunny"] = "Hunny";
	LorcanaCardTrait["Hyena"] = "Hyena";
	LorcanaCardTrait["Hyperia_City"] = "Hyperia City";
	LorcanaCardTrait["Illusion"] = "Illusion";
	LorcanaCardTrait["Inventor"] = "Inventor";
	LorcanaCardTrait["Item"] = "Item";
	LorcanaCardTrait["King"] = "King";
	LorcanaCardTrait["Knight"] = "Knight";
	LorcanaCardTrait["Madrigal"] = "Madrigal";
	LorcanaCardTrait["Mentor"] = "Mentor";
	LorcanaCardTrait["Monster"] = "Monster";
	LorcanaCardTrait["Musketeer"] = "Musketeer";
	LorcanaCardTrait["Obstacle"] = "Obstacle";
	LorcanaCardTrait["Pirate"] = "Pirate";
	LorcanaCardTrait["Prince"] = "Prince";
	LorcanaCardTrait["Princess"] = "Princess";
	LorcanaCardTrait["Puppy"] = "Puppy";
	LorcanaCardTrait["Queen"] = "Queen";
	LorcanaCardTrait["Racer"] = "Racer";
	LorcanaCardTrait["Red_Panda"] = "Red Panda";
	LorcanaCardTrait["Robot"] = "Robot";
	LorcanaCardTrait["Seven_Dwarfs"] = "Seven Dwarfs";
	LorcanaCardTrait["Song"] = "Song";
	LorcanaCardTrait["Sorcerer"] = "Sorcerer";
	LorcanaCardTrait["Storyborn"] = "Storyborn";
	LorcanaCardTrait["Super"] = "Super";
	LorcanaCardTrait["Team"] = "Team";
	LorcanaCardTrait["Tigger"] = "Tigger";
	LorcanaCardTrait["Titan"] = "Titan";
	LorcanaCardTrait["Toy"] = "Toy";
	LorcanaCardTrait["Villain"] = "Villain";
	LorcanaCardTrait["Vineling"] = "Vineling";
	LorcanaCardTrait["Whisper"] = "Whisper";
	return LorcanaCardTrait;
}({});

//#endregion
//#region src/lib/games/lorcana/lorcana.presets.ts
const lorcanaListPresets = {
	Standard: {
		name: translatable("lorcana.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("lorcana.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [LorcanaCardFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("lorcana.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("lorcana.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [LorcanaCardFinish.STANDARD] },
			quantity: 4
		}]
	},
	Foil: {
		name: translatable("lorcana.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("lorcana.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [LorcanaCardFinish.FOIL] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("lorcana.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("lorcana.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [{
			filters: { finish: [LorcanaCardFinish.FOIL] },
			quantity: 4
		}]
	},
	"Master Set": {
		name: translatable("lorcana.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("lorcana.presets.masterset.description", "A complete set with one copy of each card in standard and foil finishes, plus all promotional variants"),
		matchers: [
			{
				filters: { finish: [LorcanaCardFinish.STANDARD] },
				quantity: 1
			},
			{
				filters: { finish: [LorcanaCardFinish.FOIL] },
				quantity: 1
			},
			{
				filters: { rarity: [LorcanaRarity.PROMO] },
				quantity: 1
			}
		]
	}
};

//#endregion
//#region src/lib/games/lorcana/lorcana.game.ts
/**
* Shared finish values for Lorcana - used by both finishes and finish attributes
*/
const lorcanaFinishValues = {
	Standard: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	Foil: { label: translatable("global.attributes.finish.values.foil", "Foil") }
};
const lorcanaFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(LorcanaRarity).optional(),
	color: makeZodEnumFilter(LorcanaCardColor).optional(),
	finish: makeZodEnumFilter(LorcanaCardFinish).optional(),
	finishes: makeZodEnumFilter(LorcanaCardFinish).optional(),
	cost: zodNumberFilter.optional(),
	ink: makeZodEnumFilter(LorcanaInk).optional(),
	traits: makeZodEnumFilter(LorcanaCardTrait).optional(),
	type: makeZodEnumFilter(LorcanaCardType).optional(),
	languages: makeZodEnumFilter(Languages).optional()
});
const LorcanaGameConfiguration = {
	name: "Lorcana",
	shortName: "Lorcana",
	slug: "lorcana",
	icon: {
		dark: "lorcana-icon.png",
		light: "lorcana-icon.png"
	},
	image: {
		dark: "lorcana.png",
		light: "lorcana.png"
	},
	attributes: {
		name: nameAttribute,
		color: enumAttribute(LorcanaCardColor, {
			entity: "card",
			name: translatable("lorcana.attributes.color.name", "Color"),
			description: translatable("lorcana.attributes.color.description", "The color of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				Amber: {
					label: translatable("lorcana.attributes.color.values.amber", "Amber"),
					image: "/images/lorcana/amber.svg"
				},
				Amethyst: {
					label: translatable("lorcana.attributes.color.values.amethyst", "Amethyst"),
					image: "/images/lorcana/amethyst.svg"
				},
				Emerald: {
					label: translatable("lorcana.attributes.color.values.emerald", "Emerald"),
					image: "/images/lorcana/emerald.svg"
				},
				Ruby: {
					label: translatable("lorcana.attributes.color.values.ruby", "Ruby"),
					image: "/images/lorcana/ruby.svg"
				},
				Sapphire: {
					label: translatable("lorcana.attributes.color.values.sapphire", "Sapphire"),
					image: "/images/lorcana/sapphire.svg"
				},
				Steel: {
					label: translatable("lorcana.attributes.color.values.steel", "Steel"),
					image: "/images/lorcana/steel.svg"
				}
			}
		}),
		type: enumAttribute(LorcanaCardType, {
			entity: "card",
			name: translatable("lorcana.attributes.type.name", "Type"),
			description: translatable("lorcana.attributes.type.description", "Type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				Action: { label: translatable("lorcana.attributes.type.values.action", "Action") },
				Character: { label: translatable("lorcana.attributes.type.values.character", "Character") },
				Item: { label: translatable("lorcana.attributes.type.values.item", "Item") },
				Location: { label: translatable("lorcana.attributes.type.values.location", "Location") }
			}
		}),
		cost: {
			entity: "card",
			name: translatable("lorcana.attributes.cost.name", "Cost"),
			description: translatable("lorcana.attributes.cost.description", "The ink cost to play the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		lore: {
			entity: "card",
			name: translatable("lorcana.attributes.lore.name", "Lore"),
			description: translatable("lorcana.attributes.lore.description", "The lore value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		attack: {
			entity: "card",
			name: translatable("lorcana.attributes.attack.name", "Strength"),
			description: translatable("lorcana.attributes.attack.description", "The attack power of the card the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		defense: {
			entity: "card",
			name: translatable("lorcana.attributes.defense.name", "Willpower"),
			description: translatable("lorcana.attributes.defense.description", "The defense of the card the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		ink: {
			entity: "card",
			name: translatable("lorcana.attributes.ink.name", "Inkable"),
			description: translatable("lorcana.attributes.ink.description", "Whether the card can be inked or not"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				Inkable: {
					label: translatable("lorcana.attributes.ink.values.true", "Inkable"),
					image: "/images/lorcana/inkwell.svg"
				},
				NonInkable: {
					label: translatable("lorcana.attributes.ink.values.false", "Non inkable"),
					image: "/images/lorcana/inkcost.svg"
				}
			}
		},
		shortName: {
			entity: "card",
			name: translatable("lorcana.attributes.shortName.name", "Short name"),
			description: translatable("lorcana.attributes.shortName.description", "The short name of the card, for example Elsa instead of Elsa - The Snow Queen"),
			type: "string",
			array: false
		},
		title: {
			entity: "card",
			name: translatable("lorcana.attributes.title.name", "Title"),
			description: translatable("lorcana.attributes.title.description", "The title of the card, positioned after the short name"),
			type: "string",
			array: false
		},
		traits: enumAttribute(LorcanaCardTrait, {
			entity: "card",
			name: translatable("lorcana.attributes.trait.name", "Traits"),
			description: translatable("lorcana.attributes.trait.description", "Traits the card has"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				Action: { label: translatable("lorcana.attributes.trait.values.action", "Action") },
				Alien: { label: translatable("lorcana.attributes.trait.values.alien", "Alien") },
				Ally: { label: translatable("lorcana.attributes.trait.values.ally", "Ally") },
				Boss: { label: translatable("lorcana.attributes.trait.values.boss", "Boss") },
				Broom: { label: translatable("lorcana.attributes.trait.values.broom", "Broom") },
				Captain: { label: translatable("lorcana.attributes.trait.values.captain", "Captain") },
				Colossus: { label: translatable("lorcana.attributes.trait.values.colossus", "Colossus") },
				Deity: { label: translatable("lorcana.attributes.trait.values.deity", "Deity") },
				Detective: { label: translatable("lorcana.attributes.trait.values.detective", "Detective") },
				Dinosaur: { label: translatable("lorcana.attributes.trait.values.dinosaur", "Dinosaur") },
				Dragon: { label: translatable("lorcana.attributes.trait.values.dragon", "Dragon") },
				Dreamborn: { label: translatable("lorcana.attributes.trait.values.dreamborn", "Dreamborn") },
				Entangled: { label: translatable("lorcana.attributes.trait.values.entangled", "Entangled") },
				Fairy: { label: translatable("lorcana.attributes.trait.values.fairy", "Fairy") },
				Floodborn: { label: translatable("lorcana.attributes.trait.values.floodborn", "Floodborn") },
				Gargoyle: { label: translatable("lorcana.attributes.trait.values.gargoyle", "Gargoyle") },
				Ghost: { label: translatable("lorcana.attributes.trait.values.ghost", "Ghost") },
				Giant: { label: translatable("lorcana.attributes.trait.values.giant", "Giant") },
				Hero: { label: translatable("lorcana.attributes.trait.values.hero", "Hero") },
				Hunny: { label: translatable("lorcana.attributes.trait.values.hunny", "Hunny") },
				Hyena: { label: translatable("lorcana.attributes.trait.values.hyena", "Hyena") },
				"Hyperia City": { label: translatable("lorcana.attributes.trait.values.hyperia_city", "Hyperia City") },
				Illusion: { label: translatable("lorcana.attributes.trait.values.illusion", "Illusion") },
				Inventor: { label: translatable("lorcana.attributes.trait.values.inventor", "Inventor") },
				Item: { label: translatable("lorcana.attributes.trait.values.item", "Item") },
				King: { label: translatable("lorcana.attributes.trait.values.king", "King") },
				Knight: { label: translatable("lorcana.attributes.trait.values.knight", "Knight") },
				Madrigal: { label: translatable("lorcana.attributes.trait.values.madrigal", "Madrigal") },
				Mentor: { label: translatable("lorcana.attributes.trait.values.mentor", "Mentor") },
				Monster: { label: translatable("lorcana.attributes.trait.values.monster", "Monster") },
				Musketeer: { label: translatable("lorcana.attributes.trait.values.musketeer", "Musketeer") },
				Obstacle: { label: translatable("lorcana.attributes.trait.values.obstacle", "Obstacle") },
				Pirate: { label: translatable("lorcana.attributes.trait.values.pirate", "Pirate") },
				Prince: { label: translatable("lorcana.attributes.trait.values.prince", "Prince") },
				Princess: { label: translatable("lorcana.attributes.trait.values.princess", "Princess") },
				Puppy: { label: translatable("lorcana.attributes.trait.values.puppy", "Puppy") },
				Queen: { label: translatable("lorcana.attributes.trait.values.queen", "Queen") },
				Racer: { label: translatable("lorcana.attributes.trait.values.racer", "Racer") },
				"Red Panda": { label: translatable("lorcana.attributes.trait.values.red_panda", "Red Panda") },
				Robot: { label: translatable("lorcana.attributes.trait.values.robot", "Robot") },
				"Seven Dwarfs": { label: translatable("lorcana.attributes.trait.values.seven_dwarfs", "Seven Dwarfs") },
				Song: { label: translatable("lorcana.attributes.trait.values.song", "Song") },
				Sorcerer: { label: translatable("lorcana.attributes.trait.values.sorcerer", "Sorcerer") },
				Storyborn: { label: translatable("lorcana.attributes.trait.values.storyborn", "Storyborn") },
				Super: { label: translatable("lorcana.attributes.trait.values.super", "Super") },
				Team: { label: translatable("lorcana.attributes.trait.values.team", "Team") },
				Tigger: { label: translatable("lorcana.attributes.trait.values.tigger", "Tigger") },
				Titan: { label: translatable("lorcana.attributes.trait.values.titan", "Titan") },
				Toy: { label: translatable("lorcana.attributes.trait.values.toy", "Toy") },
				Villain: { label: translatable("lorcana.attributes.trait.values.villain", "Villain") },
				Vineling: { label: translatable("lorcana.attributes.trait.values.vineling", "Vineling") },
				Whisper: { label: translatable("lorcana.attributes.trait.values.whisper", "Whisper") }
			}
		}),
		finishes: enumAttribute(LorcanaCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: true,
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			filter: FilterComponentType.CheckboxList,
			values: lorcanaFinishValues
		}),
		finish: enumAttribute(LorcanaCardFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: false,
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			filter: FilterComponentType.CheckboxList,
			values: lorcanaFinishValues
		}),
		cardSetId: {
			entity: "variant",
			name: translatable("lorcana.attributes.cardSetId.name", "Card Set ID"),
			description: "The id of the card in the set",
			type: "string",
			array: false
		},
		dreambornId: {
			entity: "variant",
			name: translatable("lorcana.attributes.dreambornId.name", "Dreamborn ID"),
			description: "The id of the card on dreamborn",
			type: "string",
			array: false
		},
		rarity: enumAttribute(LorcanaRarity, {
			entity: "variant",
			name: translatable("lorcana.attributes.rarity.name", "Rarity"),
			description: translatable("lorcana.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				Common: {
					label: translatable("lorcana.attributes.rarity.values.common", "Common"),
					image: "/images/lorcana/common.svg"
				},
				Uncommon: {
					label: translatable("lorcana.attributes.rarity.values.uncommon", "Uncommon"),
					image: "/images/lorcana/uncommon.svg"
				},
				Rare: {
					label: translatable("lorcana.attributes.rarity.values.rare", "Rare"),
					image: "/images/lorcana/rare.svg"
				},
				"Super Rare": {
					label: translatable("lorcana.attributes.rarity.values.super_rare", "Super Rare"),
					image: "/images/lorcana/super-rare.svg"
				},
				Legendary: {
					label: translatable("lorcana.attributes.rarity.values.legendary", "Legendary"),
					image: "/images/lorcana/legendary.svg"
				},
				Enchanted: {
					label: translatable("lorcana.attributes.rarity.values.enchanted", "Enchanted"),
					image: "/images/lorcana/enchanted.png"
				},
				Epic: {
					label: translatable("lorcana.attributes.rarity.values.epic", "Epic"),
					image: "/images/lorcana/epic.png"
				},
				Iconic: {
					label: translatable("lorcana.attributes.rarity.values.iconic", "Iconic"),
					image: "/images/lorcana/iconic.png"
				},
				Promo: {
					label: translatable("lorcana.attributes.rarity.values.promo", "Promo"),
					image: "/images/lorcana/promo.png"
				}
			}
		}),
		flavorText: {
			entity: "variant",
			name: translatable("lorcana.attributes.flavorText.name", "Flavor text"),
			description: "The flavor text of the card",
			type: "string",
			array: false
		},
		abilities: {
			entity: "variant",
			name: translatable("lorcana.attributes.abilities.name", "Abilities"),
			description: translatable("lorcana.attributes.abilities.description", "The abilities of the card"),
			type: "string",
			array: true
		},
		description: {
			entity: "variant",
			name: translatable("lorcana.attributes.description.name", "Card Text"),
			description: translatable("lorcana.attributes.description.description", "The full text description of the card"),
			type: "string",
			array: false
		},
		printNumber: printNumberAttribute
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"color",
			"rarity"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"type",
			"cost",
			"ink",
			"traits",
			"cost",
			"strength",
			"willpower",
			"lore"
		]
	}],
	productDetailsOrder: {
		details: [
			"traits",
			"abilities",
			"description"
		],
		statistics: [
			"color",
			"cost",
			"rarity",
			"attack",
			"type",
			"defense",
			"ink",
			"lore"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: lorcanaListPresets,
	basicAttributes: ["color", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Cardmarket, SupportedMarketplace.Tcgplayer],
	vendorIds: [
		{
			key: "dreambornId",
			label: "Dreamborn ID",
			isMarketplaceId: false
		},
		cardmarketVendorId,
		tcgplayerVendorId
	],
	csvTemplateExample: {
		vendorIds: {
			Cardmarket: "729228",
			TCGplayer: "492369"
		},
		printNumber: "51",
		cardName: "Mickey Mouse - Wayward Sorcerer",
		expansion: "The First Chapter",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/mtg/mtg.types.ts
let MTGCardFinish = /* @__PURE__ */ function(MTGCardFinish) {
	MTGCardFinish["STANDARD"] = "Standard";
	MTGCardFinish["FOIL"] = "Foil";
	MTGCardFinish["ETCHED"] = "Etched";
	MTGCardFinish["SIGNED"] = "Signed";
	return MTGCardFinish;
}({});
let MTGColor = /* @__PURE__ */ function(MTGColor) {
	MTGColor["BLACK"] = "B";
	MTGColor["GREEN"] = "G";
	MTGColor["RED"] = "R";
	MTGColor["WHITE"] = "W";
	MTGColor["BLUE"] = "U";
	MTGColor["COLORLESS"] = "C";
	return MTGColor;
}({});
let MTGRarity = /* @__PURE__ */ function(MTGRarity) {
	MTGRarity["BONUS"] = "bonus";
	MTGRarity["COMMON"] = "common";
	MTGRarity["UNCOMMON"] = "uncommon";
	MTGRarity["RARE"] = "rare";
	MTGRarity["MYTHIC"] = "mythic";
	MTGRarity["SPECIAL"] = "special";
	return MTGRarity;
}({});

//#endregion
//#region src/lib/games/mtg/mtg.presets.ts
const mtgListPresets = {
	Standard: {
		name: translatable("mtg.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("mtg.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [MTGCardFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("mtg.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("mtg.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [MTGCardFinish.STANDARD] },
			quantity: 4
		}]
	},
	Foil: {
		name: translatable("mtg.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("mtg.presets.foil.description", "A complete set with one copy of each card in traditional foil finish"),
		matchers: [{
			filters: { finish: [MTGCardFinish.FOIL] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("mtg.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("mtg.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in traditional foil finish"),
		matchers: [{
			filters: { finish: [MTGCardFinish.FOIL] },
			quantity: 4
		}]
	},
	"Master Set": {
		name: translatable("mtg.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("mtg.presets.masterset.description", "A complete set with one copy of each card in standard, traditional foil, and etched foil finishes"),
		matchers: [
			{
				filters: { finish: [MTGCardFinish.STANDARD] },
				quantity: 1
			},
			{
				filters: { finish: [MTGCardFinish.FOIL] },
				quantity: 1
			},
			{
				filters: { finish: [MTGCardFinish.ETCHED] },
				quantity: 1
			}
		]
	}
};

//#endregion
//#region src/lib/games/mtg/mtg.game.ts
/**
* Shared finish values for MTG - used by both finishes and finish attributes
*/
const mtgFinishValues = {
	Standard: { label: translatable("mtg.attributes.finish.values.standard", "Standard") },
	Foil: { label: translatable("mtg.attributes.finish.values.foil", "Foil") },
	Etched: { label: translatable("mtg.attributes.finish.values.etched", "Etched") },
	Signed: { label: translatable("mtg.attributes.finish.values.signed", "Signed") }
};
const mtgFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	finish: makeZodEnumFilter(MTGCardFinish).optional(),
	finishes: makeZodEnumFilter(MTGCardFinish).optional(),
	color: makeZodEnumFilter(MTGColor).optional(),
	colorIdentity: makeZodEnumFilter(MTGColor).optional(),
	rarity: makeZodEnumFilter(MTGRarity).optional(),
	languages: makeZodEnumFilter(Languages).optional()
});
const MtgGameConfiguration = {
	name: "Magic: The Gathering",
	shortName: "MTG",
	slug: "mtg",
	icon: {
		dark: "mtg-icon.png",
		light: "mtg-icon.png"
	},
	image: {
		dark: "mtg.png",
		light: "mtg.png"
	},
	attributes: {
		name: nameAttribute,
		rarity: enumAttribute(MTGRarity, {
			entity: "variant",
			name: translatable("mtg.attributes.rarity.name", "Rarity"),
			description: translatable("mtg.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				common: {
					label: translatable("mtg.attributes.rarity.values.common", "Common"),
					image: "/images/mtg/common.png"
				},
				uncommon: {
					label: translatable("mtg.attributes.rarity.values.uncommon", "Uncommon"),
					image: "/images/mtg/uncommon.png"
				},
				rare: {
					label: translatable("mtg.attributes.rarity.values.rare", "Rare"),
					image: "/images/mtg/rare.png"
				},
				mythic: {
					label: translatable("mtg.attributes.rarity.values.mythic", "Mythic"),
					image: "/images/mtg/mythic.png"
				},
				bonus: {
					label: translatable("mtg.attributes.rarity.values.bonus", "Bonus"),
					image: "/images/mtg/treasure.png"
				},
				special: {
					label: translatable("mtg.attributes.rarity.values.special", "Special"),
					image: "/images/mtg/timeshifted.png"
				}
			}
		}),
		color: enumAttribute(MTGColor, {
			entity: "card",
			name: translatable("mtg.attributes.color.name", "Color"),
			description: translatable("mtg.attributes.color.description", "The colors present on the mana cost of a card "),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				B: {
					label: translatable("mtg.attributes.color.values.black", "Black"),
					image: "/images/mtg/mana-b.svg"
				},
				G: {
					label: translatable("mtg.attributes.color.values.green", "Green"),
					image: "/images/mtg/mana-g.svg"
				},
				R: {
					label: translatable("mtg.attributes.color.values.red", "Red"),
					image: "/images/mtg/mana-r.svg"
				},
				W: {
					label: translatable("mtg.attributes.color.values.white", "White"),
					image: "/images/mtg/mana-w.svg"
				},
				U: {
					label: translatable("mtg.attributes.color.values.blue", "Blue"),
					image: "/images/mtg/mana-u.svg"
				},
				C: {
					label: translatable("mtg.attributes.color.values.colorless", "Colorless"),
					image: "/images/mtg/mana-c.svg"
				}
			}
		}),
		colorIdentity: enumAttribute(MTGColor, {
			entity: "card",
			name: translatable("mtg.attributes.colorIdentity.name", "Color Identity"),
			description: translatable("mtg.attributes.colorIdentity.description", "The colors present on the card "),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			setMatch: { alwaysFits: [MTGColor.COLORLESS] },
			values: {
				B: {
					label: translatable("mtg.attributes.color.values.black", "Black"),
					image: "/images/mtg/mana-b.svg"
				},
				G: {
					label: translatable("mtg.attributes.color.values.green", "Green"),
					image: "/images/mtg/mana-g.svg"
				},
				R: {
					label: translatable("mtg.attributes.color.values.red", "Red"),
					image: "/images/mtg/mana-r.svg"
				},
				W: {
					label: translatable("mtg.attributes.color.values.white", "White"),
					image: "/images/mtg/mana-w.svg"
				},
				U: {
					label: translatable("mtg.attributes.color.values.blue", "Blue"),
					image: "/images/mtg/mana-u.svg"
				},
				C: {
					label: translatable("mtg.attributes.color.values.colorless", "Colorless"),
					image: "/images/mtg/mana-c.svg"
				}
			}
		}),
		finishes: enumAttribute(MTGCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("mtg.attributes.finish.name", "Finish"),
			description: translatable("mtg.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: mtgFinishValues
		}),
		finish: enumAttribute(MTGCardFinish, {
			entity: "inventory",
			name: translatable("mtg.attributes.finish.name", "Finish"),
			description: translatable("mtg.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: mtgFinishValues
		}),
		printNumber: printNumberAttribute,
		types: {
			entity: "card",
			name: translatable("mtg.attributes.types.name", "Types"),
			description: translatable("mtg.attributes.types.description", "Card types (Creature, Instant, Sorcery, etc.)"),
			type: "string",
			array: true
		},
		description: {
			entity: "variant",
			name: translatable("mtg.attributes.description.name", "Card Text"),
			description: translatable("mtg.attributes.description.description", "The oracle/rules text of the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: ["rarity", "color"]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: []
	}],
	productDetailsOrder: {
		details: ["description"],
		statistics: [
			"rarity",
			"color",
			"finish",
			"types"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: mtgListPresets,
	basicAttributes: ["color", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Cardmarket, SupportedMarketplace.Tcgplayer],
	vendorIds: [
		{
			key: "scryfallId",
			label: "Scryfall ID",
			isMarketplaceId: false
		},
		cardmarketVendorId,
		tcgplayerVendorId
	],
	csvTemplateExample: {
		vendorIds: {
			Cardmarket: "4951",
			TCGplayer: "9121",
			scryfallId: "ff1b8fc5-604a-4449-a73d-861e53642a70"
		},
		printNumber: "162",
		cardName: "Lightning Bolt",
		expansion: "Unlimited Edition",
		expansionCode: "2ed",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/naruto-mythos/naruto-mythos.types.ts
let NarutoMythosCardType = /* @__PURE__ */ function(NarutoMythosCardType) {
	NarutoMythosCardType["CHARACTER"] = "Character";
	NarutoMythosCardType["ATTACHMENT"] = "Attachment";
	NarutoMythosCardType["MISSION"] = "Mission";
	return NarutoMythosCardType;
}({});
let NarutoMythosRarity = /* @__PURE__ */ function(NarutoMythosRarity) {
	NarutoMythosRarity["COMMON"] = "Common";
	NarutoMythosRarity["UNCOMMON"] = "Uncommon";
	NarutoMythosRarity["RARE"] = "Rare";
	NarutoMythosRarity["RARE_ART"] = "Rare Art";
	NarutoMythosRarity["SECRET"] = "Secret";
	NarutoMythosRarity["SECRET_VARIANT"] = "Secret Variant";
	NarutoMythosRarity["MYTHOS"] = "Mythos";
	NarutoMythosRarity["MYTHOS_VARIANT"] = "Mythos Variant";
	NarutoMythosRarity["LEGENDARY"] = "Legendary";
	NarutoMythosRarity["CHIBI"] = "Chibi";
	NarutoMythosRarity["POP"] = "POP";
	NarutoMythosRarity["SHINOBI"] = "Shinobi";
	NarutoMythosRarity["SPECIAL"] = "Special";
	return NarutoMythosRarity;
}({});
let NarutoMythosOrientation = /* @__PURE__ */ function(NarutoMythosOrientation) {
	NarutoMythosOrientation["PORTRAIT"] = "portrait";
	NarutoMythosOrientation["LANDSCAPE"] = "landscape";
	return NarutoMythosOrientation;
}({});
let NarutoMythosGroup = /* @__PURE__ */ function(NarutoMythosGroup) {
	NarutoMythosGroup["LEAF_VILLAGE"] = "Leaf Village";
	NarutoMythosGroup["AKATSUKI"] = "Akatsuki";
	NarutoMythosGroup["INDEPENDENT"] = "Independent";
	NarutoMythosGroup["SAND_VILLAGE"] = "Sand Village";
	NarutoMythosGroup["SOUND_VILLAGE"] = "Sound Village";
	return NarutoMythosGroup;
}({});
let NarutoMythosKeyword = /* @__PURE__ */ function(NarutoMythosKeyword) {
	NarutoMythosKeyword["ACADEMY"] = "Academy";
	NarutoMythosKeyword["ACADEMY_STUDENT"] = "Academy Student";
	NarutoMythosKeyword["ARMOR"] = "Armor";
	NarutoMythosKeyword["BOMB"] = "Bomb";
	NarutoMythosKeyword["BOOK"] = "Book";
	NarutoMythosKeyword["CIVILIAN"] = "Civilian";
	NarutoMythosKeyword["DEMON_BROTHER"] = "Demon Brother";
	NarutoMythosKeyword["FOOD"] = "Food";
	NarutoMythosKeyword["HEAD_OF_CLAN"] = "Head of Clan";
	NarutoMythosKeyword["HOKAGE"] = "Hokage";
	NarutoMythosKeyword["JUTSU"] = "Jutsu";
	NarutoMythosKeyword["KEKKEI_GENKAI"] = "Kekkei Genkai";
	NarutoMythosKeyword["LAIR"] = "Lair";
	NarutoMythosKeyword["LANDMARK"] = "Landmark";
	NarutoMythosKeyword["LOCATION"] = "Location";
	NarutoMythosKeyword["MEDICAL_NINJA"] = "Medical Ninja";
	NarutoMythosKeyword["NINJA_HOUND"] = "Ninja Hound";
	NarutoMythosKeyword["NINJA_PIG"] = "Ninja Pig";
	NarutoMythosKeyword["PUPPET"] = "Puppet";
	NarutoMythosKeyword["REANIMATION"] = "Reanimation";
	NarutoMythosKeyword["ROGUE_NINJA"] = "Rogue Ninja";
	NarutoMythosKeyword["SANNIN"] = "Sannin";
	NarutoMythosKeyword["SCROLL"] = "Scroll";
	NarutoMythosKeyword["SITUATION"] = "Situation";
	NarutoMythosKeyword["SOUND_FOUR"] = "Sound Four";
	NarutoMythosKeyword["SOUND_NINJA"] = "Sound Ninja";
	NarutoMythosKeyword["SPECIAL_JONIN"] = "Special Jonin";
	NarutoMythosKeyword["SUMMON"] = "Summon";
	NarutoMythosKeyword["TAIJUTSU"] = "Taijutsu";
	NarutoMythosKeyword["TAILED_BEAST"] = "Tailed Beast";
	NarutoMythosKeyword["TEAM_10"] = "Team 10";
	NarutoMythosKeyword["TEAM_7"] = "Team 7";
	NarutoMythosKeyword["TEAM_8"] = "Team 8";
	NarutoMythosKeyword["TEAM_BAKI"] = "Team Baki";
	NarutoMythosKeyword["TEAM_DOSU"] = "Team Dosu";
	NarutoMythosKeyword["TEAM_GUY"] = "Team Guy";
	NarutoMythosKeyword["TOOL"] = "Tool";
	NarutoMythosKeyword["VILLAGE"] = "Village";
	NarutoMythosKeyword["VILLAGE_OF_ARTISANS"] = "Village of Artisans";
	NarutoMythosKeyword["WEAPON"] = "Weapon";
	return NarutoMythosKeyword;
}({});
let NarutoMythosVariant = /* @__PURE__ */ function(NarutoMythosVariant) {
	NarutoMythosVariant["FULL_ART"] = "Full Art";
	NarutoMythosVariant["ALTERNATE_ART"] = "Alternate Art";
	return NarutoMythosVariant;
}({});
let NarutoMythosFinish = /* @__PURE__ */ function(NarutoMythosFinish) {
	NarutoMythosFinish["STANDARD"] = "Standard";
	NarutoMythosFinish["FOIL"] = "Foil";
	NarutoMythosFinish["HOLOGRAPHIC"] = "Holographic";
	NarutoMythosFinish["GOLD_FOIL"] = "Gold Foil";
	NarutoMythosFinish["EMBOSSED"] = "Embossed";
	return NarutoMythosFinish;
}({});

//#endregion
//#region src/lib/games/naruto-mythos/naruto-mythos.presets.ts
const narutoMythosListPresets = {
	Standard: {
		name: translatable("naruto-mythos.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("naruto-mythos.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [NarutoMythosFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("naruto-mythos.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("naruto-mythos.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [
			{
				filters: {
					finish: [NarutoMythosFinish.STANDARD],
					cardType: [NarutoMythosCardType.CHARACTER]
				},
				quantity: 2
			},
			{
				filters: {
					finish: [NarutoMythosFinish.STANDARD],
					cardType: [NarutoMythosCardType.ATTACHMENT]
				},
				quantity: 2
			},
			{
				filters: {
					finish: [NarutoMythosFinish.STANDARD],
					cardType: [NarutoMythosCardType.MISSION]
				},
				quantity: 1
			}
		]
	},
	Foil: {
		name: translatable("naruto-mythos.presets.foil.name", "Holographic Set"),
		type: ListPresetType.FOIL,
		description: translatable("naruto-mythos.presets.foil.description", "A complete set with one copy of each card in foil, holographic, gold foil, or embossed finish"),
		matchers: [{
			filters: { finish: [
				NarutoMythosFinish.FOIL,
				NarutoMythosFinish.HOLOGRAPHIC,
				NarutoMythosFinish.GOLD_FOIL,
				NarutoMythosFinish.EMBOSSED
			] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("naruto-mythos.presets.foilPlayset.name", "Holographic Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("naruto-mythos.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [{
			filters: {
				finish: [
					NarutoMythosFinish.FOIL,
					NarutoMythosFinish.HOLOGRAPHIC,
					NarutoMythosFinish.GOLD_FOIL,
					NarutoMythosFinish.EMBOSSED
				],
				cardType: [NarutoMythosCardType.CHARACTER]
			},
			quantity: 2
		}, {
			filters: {
				finish: [
					NarutoMythosFinish.FOIL,
					NarutoMythosFinish.HOLOGRAPHIC,
					NarutoMythosFinish.GOLD_FOIL,
					NarutoMythosFinish.EMBOSSED
				],
				cardType: [NarutoMythosCardType.MISSION]
			},
			quantity: 1
		}]
	},
	"Master Set": {
		name: translatable("naruto-mythos.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("naruto-mythos.presets.masterset.description", "A complete set with one copy of each card across all finishes - standard, foil, holographic, gold foil, and embossed"),
		matchers: [
			{
				filters: { finish: [NarutoMythosFinish.STANDARD] },
				quantity: 1
			},
			{
				filters: { finish: [NarutoMythosFinish.FOIL] },
				quantity: 1
			},
			{
				filters: { finish: [NarutoMythosFinish.HOLOGRAPHIC] },
				quantity: 1
			},
			{
				filters: { finish: [NarutoMythosFinish.GOLD_FOIL] },
				quantity: 1
			},
			{
				filters: { finish: [NarutoMythosFinish.EMBOSSED] },
				quantity: 1
			}
		]
	}
};

//#endregion
//#region src/lib/games/naruto-mythos/naruto-mythos.game.ts
const narutoMythosFinishValues = {
	[NarutoMythosFinish.STANDARD]: { label: translatable("naruto-mythos.attributes.finishes.values.standard", "Standard") },
	[NarutoMythosFinish.FOIL]: { label: translatable("naruto-mythos.attributes.finishes.values.foil", "Foil") },
	[NarutoMythosFinish.HOLOGRAPHIC]: { label: translatable("naruto-mythos.attributes.finishes.values.holographic", "Holographic") },
	[NarutoMythosFinish.GOLD_FOIL]: { label: translatable("naruto-mythos.attributes.finishes.values.goldFoil", "Gold Foil") },
	[NarutoMythosFinish.EMBOSSED]: { label: translatable("naruto-mythos.attributes.finishes.values.embossed", "Embossed") }
};
const narutoMythosFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(NarutoMythosRarity).optional(),
	finish: makeZodEnumFilter(NarutoMythosFinish).optional(),
	finishes: makeZodEnumFilter(NarutoMythosFinish).optional(),
	variant: makeZodEnumFilter(NarutoMythosVariant).optional(),
	cardType: makeZodEnumFilter(NarutoMythosCardType).optional(),
	group: makeZodEnumFilter(NarutoMythosGroup).optional(),
	keywords: makeZodEnumFilter(NarutoMythosKeyword).optional(),
	chakra: zodNumericFilter.optional(),
	power: zodNumericFilter.optional(),
	points: zodNumericFilter.optional()
});
const NarutoMythosGameConfiguration = {
	name: "Naruto Mythos TCG",
	shortName: "Naruto",
	slug: "naruto-mythos",
	icon: {
		dark: "naruto-mythos-icon.png",
		light: "naruto-mythos-icon.png"
	},
	image: {
		dark: "naruto-mythos.png",
		light: "naruto-mythos.png"
	},
	attributes: {
		name: nameAttribute,
		printNumber: printNumberAttribute,
		finishes: enumAttribute(NarutoMythosFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: true,
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			filter: FilterComponentType.CheckboxList,
			values: narutoMythosFinishValues
		}),
		finish: enumAttribute(NarutoMythosFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: false,
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			filter: FilterComponentType.CheckboxList,
			values: narutoMythosFinishValues
		}),
		variant: enumAttribute(NarutoMythosVariant, {
			entity: "variant",
			name: translatable("naruto-mythos.attributes.variant.name", "Variant"),
			description: translatable("naruto-mythos.attributes.variant.description", "The variant of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[NarutoMythosVariant.FULL_ART]: { label: translatable("naruto-mythos.attributes.variant.values.fullArt", "Full Art") },
				[NarutoMythosVariant.ALTERNATE_ART]: { label: translatable("naruto-mythos.attributes.variant.values.alternateArt", "Alternate Art") }
			}
		}),
		rarity: enumAttribute(NarutoMythosRarity, {
			entity: "variant",
			name: translatable("naruto-mythos.attributes.rarity.name", "Rarity"),
			description: translatable("naruto-mythos.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[NarutoMythosRarity.COMMON]: {
					label: translatable("naruto-mythos.attributes.rarity.values.common", "Common"),
					image: "/images/naruto-mythos/rarities/common.svg"
				},
				[NarutoMythosRarity.UNCOMMON]: {
					label: translatable("naruto-mythos.attributes.rarity.values.uncommon", "Uncommon"),
					image: "/images/naruto-mythos/rarities/uncommon.svg"
				},
				[NarutoMythosRarity.RARE]: {
					label: translatable("naruto-mythos.attributes.rarity.values.rare", "Rare"),
					image: "/images/naruto-mythos/rarities/rare.svg"
				},
				[NarutoMythosRarity.RARE_ART]: {
					label: translatable("naruto-mythos.attributes.rarity.values.rare_art", "Rare Art"),
					image: "/images/naruto-mythos/rarities/rare-art.svg"
				},
				[NarutoMythosRarity.SECRET]: {
					label: translatable("naruto-mythos.attributes.rarity.values.secret", "Secret"),
					image: "/images/naruto-mythos/rarities/secret.svg"
				},
				[NarutoMythosRarity.SECRET_VARIANT]: {
					label: translatable("naruto-mythos.attributes.rarity.values.secret_variant", "Secret Variant"),
					image: "/images/naruto-mythos/rarities/secret.svg"
				},
				[NarutoMythosRarity.MYTHOS]: {
					label: translatable("naruto-mythos.attributes.rarity.values.mythos", "Mythos"),
					image: "/images/naruto-mythos/rarities/mythos.svg"
				},
				[NarutoMythosRarity.MYTHOS_VARIANT]: {
					label: translatable("naruto-mythos.attributes.rarity.values.mythos_variant", "Mythos Variant"),
					image: "/images/naruto-mythos/rarities/mythos.svg"
				},
				[NarutoMythosRarity.LEGENDARY]: {
					label: translatable("naruto-mythos.attributes.rarity.values.legendary", "Legendary"),
					image: "/images/naruto-mythos/rarities/legendary.svg"
				},
				[NarutoMythosRarity.CHIBI]: {
					label: translatable("naruto-mythos.attributes.rarity.values.chibi", "Chibi"),
					image: "/images/naruto-mythos/rarities/chibi.svg"
				},
				[NarutoMythosRarity.POP]: {
					label: translatable("naruto-mythos.attributes.rarity.values.pop", "POP"),
					image: "/images/naruto-mythos/rarities/pop.svg"
				},
				[NarutoMythosRarity.SHINOBI]: {
					label: translatable("naruto-mythos.attributes.rarity.values.shinobi", "Shinobi"),
					image: "/images/naruto-mythos/rarities/shinobi.svg"
				},
				[NarutoMythosRarity.SPECIAL]: {
					label: translatable("naruto-mythos.attributes.rarity.values.special", "Special"),
					image: "/images/naruto-mythos/rarities/special.svg"
				}
			}
		}),
		cardType: enumAttribute(NarutoMythosCardType, {
			entity: "card",
			name: translatable("naruto-mythos.attributes.cardType.name", "Type"),
			description: translatable("naruto-mythos.attributes.cardType.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[NarutoMythosCardType.CHARACTER]: {
					label: translatable("naruto-mythos.attributes.cardType.values.character", "Character"),
					image: "/images/naruto-mythos/card-types/character.svg"
				},
				[NarutoMythosCardType.ATTACHMENT]: {
					label: translatable("naruto-mythos.attributes.cardType.values.attachment", "Attachment"),
					image: "/images/naruto-mythos/card-types/attachment.svg"
				},
				[NarutoMythosCardType.MISSION]: {
					label: translatable("naruto-mythos.attributes.cardType.values.mission", "Mission"),
					image: "/images/naruto-mythos/card-types/mission.svg"
				}
			}
		}),
		group: enumAttribute(NarutoMythosGroup, {
			entity: "card",
			name: translatable("naruto-mythos.attributes.group.name", "Group"),
			description: translatable("naruto-mythos.attributes.group.description", "The village or faction of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[NarutoMythosGroup.LEAF_VILLAGE]: { label: translatable("naruto-mythos.attributes.group.values.leafVillage", "Leaf Village") },
				[NarutoMythosGroup.AKATSUKI]: { label: translatable("naruto-mythos.attributes.group.values.akatsuki", "Akatsuki") },
				[NarutoMythosGroup.INDEPENDENT]: { label: translatable("naruto-mythos.attributes.group.values.independent", "Independent") },
				[NarutoMythosGroup.SAND_VILLAGE]: { label: translatable("naruto-mythos.attributes.group.values.sandVillage", "Sand Village") },
				[NarutoMythosGroup.SOUND_VILLAGE]: { label: translatable("naruto-mythos.attributes.group.values.soundVillage", "Sound Village") }
			}
		}),
		orientation: enumAttribute(NarutoMythosOrientation, {
			entity: "card",
			name: translatable("naruto-mythos.attributes.orientation.name", "Orientation"),
			description: translatable("naruto-mythos.attributes.orientation.description", "The orientation of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[NarutoMythosOrientation.PORTRAIT]: { label: translatable("naruto-mythos.attributes.orientation.values.portrait", "Portrait") },
				[NarutoMythosOrientation.LANDSCAPE]: { label: translatable("naruto-mythos.attributes.orientation.values.landscape", "Landscape") }
			},
			hidden: true
		}),
		chakra: {
			entity: "card",
			name: translatable("naruto-mythos.attributes.chakra.name", "Chakra"),
			description: translatable("naruto-mythos.attributes.chakra.description", "Chakra cost of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		power: {
			entity: "card",
			name: translatable("naruto-mythos.attributes.power.name", "Power"),
			description: translatable("naruto-mythos.attributes.power.description", "Power value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		points: {
			entity: "card",
			name: translatable("naruto-mythos.attributes.points.name", "Points"),
			description: translatable("naruto-mythos.attributes.points.description", "Mission points value"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		keywords: enumAttribute(NarutoMythosKeyword, {
			entity: "card",
			name: translatable("naruto-mythos.attributes.keywords.name", "Keywords"),
			description: translatable("naruto-mythos.attributes.keywords.description", "Keywords and traits of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[NarutoMythosKeyword.ACADEMY]: { label: translatable("naruto-mythos.attributes.keywords.values.academy", "Academy") },
				[NarutoMythosKeyword.ACADEMY_STUDENT]: { label: translatable("naruto-mythos.attributes.keywords.values.academyStudent", "Academy Student") },
				[NarutoMythosKeyword.ARMOR]: { label: translatable("naruto-mythos.attributes.keywords.values.armor", "Armor") },
				[NarutoMythosKeyword.BOMB]: { label: translatable("naruto-mythos.attributes.keywords.values.bomb", "Bomb") },
				[NarutoMythosKeyword.BOOK]: { label: translatable("naruto-mythos.attributes.keywords.values.book", "Book") },
				[NarutoMythosKeyword.CIVILIAN]: { label: translatable("naruto-mythos.attributes.keywords.values.civilian", "Civilian") },
				[NarutoMythosKeyword.DEMON_BROTHER]: { label: translatable("naruto-mythos.attributes.keywords.values.demonBrother", "Demon Brother") },
				[NarutoMythosKeyword.FOOD]: { label: translatable("naruto-mythos.attributes.keywords.values.food", "Food") },
				[NarutoMythosKeyword.HEAD_OF_CLAN]: { label: translatable("naruto-mythos.attributes.keywords.values.headOfClan", "Head of Clan") },
				[NarutoMythosKeyword.HOKAGE]: { label: translatable("naruto-mythos.attributes.keywords.values.hokage", "Hokage") },
				[NarutoMythosKeyword.JUTSU]: { label: translatable("naruto-mythos.attributes.keywords.values.jutsu", "Jutsu") },
				[NarutoMythosKeyword.KEKKEI_GENKAI]: { label: translatable("naruto-mythos.attributes.keywords.values.kekkeiGenkai", "Kekkei Genkai") },
				[NarutoMythosKeyword.LAIR]: { label: translatable("naruto-mythos.attributes.keywords.values.lair", "Lair") },
				[NarutoMythosKeyword.LANDMARK]: { label: translatable("naruto-mythos.attributes.keywords.values.landmark", "Landmark") },
				[NarutoMythosKeyword.LOCATION]: { label: translatable("naruto-mythos.attributes.keywords.values.location", "Location") },
				[NarutoMythosKeyword.MEDICAL_NINJA]: { label: translatable("naruto-mythos.attributes.keywords.values.medicalNinja", "Medical Ninja") },
				[NarutoMythosKeyword.NINJA_HOUND]: { label: translatable("naruto-mythos.attributes.keywords.values.ninjaHound", "Ninja Hound") },
				[NarutoMythosKeyword.NINJA_PIG]: { label: translatable("naruto-mythos.attributes.keywords.values.ninjaPig", "Ninja Pig") },
				[NarutoMythosKeyword.PUPPET]: { label: translatable("naruto-mythos.attributes.keywords.values.puppet", "Puppet") },
				[NarutoMythosKeyword.REANIMATION]: { label: translatable("naruto-mythos.attributes.keywords.values.reanimation", "Reanimation") },
				[NarutoMythosKeyword.ROGUE_NINJA]: { label: translatable("naruto-mythos.attributes.keywords.values.rogueNinja", "Rogue Ninja") },
				[NarutoMythosKeyword.SANNIN]: { label: translatable("naruto-mythos.attributes.keywords.values.sannin", "Sannin") },
				[NarutoMythosKeyword.SCROLL]: { label: translatable("naruto-mythos.attributes.keywords.values.scroll", "Scroll") },
				[NarutoMythosKeyword.SITUATION]: { label: translatable("naruto-mythos.attributes.keywords.values.situation", "Situation") },
				[NarutoMythosKeyword.SOUND_FOUR]: { label: translatable("naruto-mythos.attributes.keywords.values.soundFour", "Sound Four") },
				[NarutoMythosKeyword.SOUND_NINJA]: { label: translatable("naruto-mythos.attributes.keywords.values.soundNinja", "Sound Ninja") },
				[NarutoMythosKeyword.SPECIAL_JONIN]: { label: translatable("naruto-mythos.attributes.keywords.values.specialJonin", "Special Jonin") },
				[NarutoMythosKeyword.SUMMON]: { label: translatable("naruto-mythos.attributes.keywords.values.summon", "Summon") },
				[NarutoMythosKeyword.TAIJUTSU]: { label: translatable("naruto-mythos.attributes.keywords.values.taijutsu", "Taijutsu") },
				[NarutoMythosKeyword.TAILED_BEAST]: { label: translatable("naruto-mythos.attributes.keywords.values.tailedBeast", "Tailed Beast") },
				[NarutoMythosKeyword.TEAM_10]: { label: translatable("naruto-mythos.attributes.keywords.values.team10", "Team 10") },
				[NarutoMythosKeyword.TEAM_7]: { label: translatable("naruto-mythos.attributes.keywords.values.team7", "Team 7") },
				[NarutoMythosKeyword.TEAM_8]: { label: translatable("naruto-mythos.attributes.keywords.values.team8", "Team 8") },
				[NarutoMythosKeyword.TEAM_BAKI]: { label: translatable("naruto-mythos.attributes.keywords.values.teamBaki", "Team Baki") },
				[NarutoMythosKeyword.TEAM_DOSU]: { label: translatable("naruto-mythos.attributes.keywords.values.teamDosu", "Team Dosu") },
				[NarutoMythosKeyword.TEAM_GUY]: { label: translatable("naruto-mythos.attributes.keywords.values.teamGuy", "Team Guy") },
				[NarutoMythosKeyword.TOOL]: { label: translatable("naruto-mythos.attributes.keywords.values.tool", "Tool") },
				[NarutoMythosKeyword.VILLAGE]: { label: translatable("naruto-mythos.attributes.keywords.values.village", "Village") },
				[NarutoMythosKeyword.VILLAGE_OF_ARTISANS]: { label: translatable("naruto-mythos.attributes.keywords.values.villageOfArtisans", "Village of Artisans") },
				[NarutoMythosKeyword.WEAPON]: { label: translatable("naruto-mythos.attributes.keywords.values.weapon", "Weapon") }
			}
		}),
		rulesText: {
			entity: "variant",
			name: translatable("naruto-mythos.attributes.rulesText.name", "Card Text"),
			description: translatable("naruto-mythos.attributes.rulesText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		},
		version: {
			entity: "variant",
			name: translatable("naruto-mythos.attributes.version.name", "Version"),
			description: translatable("naruto-mythos.attributes.version.description", "The version or subtitle of the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"variant",
			"rarity",
			"cardType",
			"group"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"keywords",
			"chakra",
			"power",
			"points",
			"finish"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText"],
		statistics: [
			"cardType",
			"rarity",
			"group",
			"keywords",
			"chakra",
			"power",
			"points",
			"orientation"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: narutoMythosListPresets,
	basicAttributes: ["rarity", "cardType"],
	supportedMarketplaces: [],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "010",
		cardName: "Naruto Uzumaki",
		expansion: "Konoha Shidō",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/onepiece/onepiece.types.ts
let OnePieceCardFinish = /* @__PURE__ */ function(OnePieceCardFinish) {
	OnePieceCardFinish["STANDARD"] = "Standard";
	OnePieceCardFinish["FOIL"] = "Foil";
	return OnePieceCardFinish;
}({});
let OnePieceCardVariant = /* @__PURE__ */ function(OnePieceCardVariant) {
	OnePieceCardVariant["ALTERNATE_ART"] = "Alternate Art";
	OnePieceCardVariant["MANGA"] = "Manga";
	OnePieceCardVariant["PROMO"] = "Promo";
	OnePieceCardVariant["SPR"] = "Super Pre-Release";
	OnePieceCardVariant["JOLLY_ROGER_FOIL"] = "Jolly Roger Foil";
	OnePieceCardVariant["TEXTURED_FOIL"] = "Textured Foil";
	OnePieceCardVariant["ALTERNATE_MANGA_ART"] = "Alternate Manga Art";
	OnePieceCardVariant["FULL_ART"] = "Full Art";
	OnePieceCardVariant["WANTED_POSTER"] = "Wanted Poster";
	OnePieceCardVariant["SP"] = "SP";
	OnePieceCardVariant["PIRATE_FOIL"] = "Pirate Foil";
	OnePieceCardVariant["GOLD"] = "Gold";
	OnePieceCardVariant["SILVER"] = "Silver";
	OnePieceCardVariant["BOX_TOPPER"] = "Box Topper";
	OnePieceCardVariant["REPRINT"] = "Reprint";
	OnePieceCardVariant["SUPER_ALTERNATE_ART"] = "Super Alternate Art";
	OnePieceCardVariant["TREASURE_RARE"] = "Treasure Rare";
	OnePieceCardVariant["GEM"] = "Gem";
	OnePieceCardVariant["SERIAL_NUMBERED"] = "Serial Numbered";
	return OnePieceCardVariant;
}({});
let OnePieceColor = /* @__PURE__ */ function(OnePieceColor) {
	OnePieceColor["RED"] = "R";
	OnePieceColor["GREEN"] = "G";
	OnePieceColor["BLUE"] = "U";
	OnePieceColor["PURPLE"] = "P";
	OnePieceColor["BLACK"] = "B";
	OnePieceColor["YELLOW"] = "Y";
	return OnePieceColor;
}({});
let OnePieceRarity = /* @__PURE__ */ function(OnePieceRarity) {
	OnePieceRarity["COMMON"] = "common";
	OnePieceRarity["UNCOMMON"] = "uncommon";
	OnePieceRarity["RARE"] = "rare";
	OnePieceRarity["SUPER_RARE"] = "super_rare";
	OnePieceRarity["SECRET_RARE"] = "secret_rare";
	OnePieceRarity["PROMO"] = "promo";
	OnePieceRarity["LEADER"] = "leader";
	OnePieceRarity["SPECIAL"] = "special";
	OnePieceRarity["TREASURE_RARE"] = "treasure_rare";
	return OnePieceRarity;
}({});
let OnePieceCardType = /* @__PURE__ */ function(OnePieceCardType) {
	OnePieceCardType["LEADER"] = "Leader";
	OnePieceCardType["DON"] = "DON!!";
	OnePieceCardType["CHARACTER"] = "Character";
	OnePieceCardType["EVENT"] = "Event";
	OnePieceCardType["STAGE"] = "Stage";
	return OnePieceCardType;
}({});
let OnePieceCharacterAttribute = /* @__PURE__ */ function(OnePieceCharacterAttribute) {
	OnePieceCharacterAttribute["STRIKE"] = "Strike";
	OnePieceCharacterAttribute["SLASH"] = "Slash";
	OnePieceCharacterAttribute["SPECIAL"] = "Special";
	OnePieceCharacterAttribute["RANGED"] = "Ranged";
	OnePieceCharacterAttribute["WISDOM"] = "Wisdom";
	return OnePieceCharacterAttribute;
}({});

//#endregion
//#region src/lib/games/onepiece/onepiece.presets.ts
const onePieceListPresets = {
	Standard: {
		name: translatable("onepiece.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("onepiece.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [OnePieceCardFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("onepiece.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("onepiece.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [OnePieceCardFinish.STANDARD] },
			quantity: 4
		}]
	},
	Foil: {
		name: translatable("onepiece.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("onepiece.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [OnePieceCardFinish.FOIL] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("onepiece.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("onepiece.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [{
			filters: { finish: [OnePieceCardFinish.FOIL] },
			quantity: 4
		}]
	},
	"Master Set": {
		name: translatable("onepiece.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("onepiece.presets.masterset.description", "A complete set with one copy of each card in standard, alternate art, manga art, promo, super pre-release, Jolly Roger foil, and textured foil finishes"),
		matchers: [
			{
				filters: { finish: [OnePieceCardFinish.STANDARD] },
				quantity: 1
			},
			{
				filters: { finish: [OnePieceCardFinish.FOIL] },
				quantity: 1
			},
			{
				filters: { variant: [OnePieceCardVariant.ALTERNATE_ART] },
				quantity: 1
			},
			{
				filters: { variant: [OnePieceCardVariant.MANGA] },
				quantity: 1
			},
			{
				filters: { variant: [OnePieceCardVariant.PROMO] },
				quantity: 1
			},
			{
				filters: { variant: [OnePieceCardVariant.SPR] },
				quantity: 1
			},
			{
				filters: { variant: [OnePieceCardVariant.JOLLY_ROGER_FOIL] },
				quantity: 1
			},
			{
				filters: { variant: [OnePieceCardVariant.TEXTURED_FOIL] },
				quantity: 1
			},
			{
				filters: { variant: [OnePieceCardVariant.ALTERNATE_MANGA_ART] },
				quantity: 1
			},
			{
				filters: { variant: [OnePieceCardVariant.FULL_ART] },
				quantity: 1
			},
			{
				filters: { variant: [OnePieceCardVariant.WANTED_POSTER] },
				quantity: 1
			}
		]
	}
};

//#endregion
//#region src/lib/games/onepiece/onepiece.game.ts
/**
* Shared finish values for One Piece - used by both finishes and finish attributes
*/
const onepieceFinishValues = {
	Foil: { label: translatable("onepiece.attributes.finish.values.foil", "Foil") },
	Standard: { label: translatable("onepiece.attributes.finish.values.standard", "Standard") }
};
const onepieceFilters = z.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	finish: makeZodEnumFilter(OnePieceCardFinish).optional(),
	finishes: makeZodEnumFilter(OnePieceCardFinish).optional(),
	color: makeZodEnumFilter(OnePieceColor).optional(),
	rarity: makeZodEnumFilter(OnePieceRarity).optional(),
	variant: makeZodEnumFilter(OnePieceCardVariant).optional(),
	attribute: makeZodEnumFilter(OnePieceCharacterAttribute).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	type: makeZodEnumFilter(OnePieceCardType).optional(),
	life: zodNumberFilter.optional(),
	power: zodNumberFilter.optional(),
	cost: zodNumberFilter.optional(),
	counter: zodNumberFilter.optional()
});
const OnePieceGameConfiguration = {
	name: "One Piece Card Game",
	shortName: "One Piece",
	slug: "onepiece",
	icon: {
		dark: "onepiece-icon.png",
		light: "onepiece-icon.png"
	},
	image: {
		dark: "onepiece.png",
		light: "onepiece.png"
	},
	attributes: {
		name: nameAttribute,
		rarity: enumAttribute(OnePieceRarity, {
			entity: "variant",
			name: translatable("onepiece.attributes.rarity.name", "Rarity"),
			description: translatable("onepiece.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				common: { label: translatable("onepiece.attributes.rarity.values.common", "Common") },
				uncommon: { label: translatable("onepiece.attributes.rarity.values.uncommon", "Uncommon") },
				rare: { label: translatable("onepiece.attributes.rarity.values.rare", "Rare") },
				super_rare: { label: translatable("onepiece.attributes.rarity.values.super_rare", "Super Rare") },
				secret_rare: { label: translatable("onepiece.attributes.rarity.values.secret_rare", "Secret Rare") },
				leader: { label: translatable("onepiece.attributes.rarity.values.leader", "Leader") },
				special: { label: translatable("onepiece.attributes.rarity.values.special", "Special") },
				promo: { label: translatable("onepiece.attributes.rarity.values.promo", "Promo") },
				treasure_rare: { label: translatable("onepiece.attributes.rarity.values.treasure_rare", "Treasure Rare") }
			}
		}),
		color: enumAttribute(OnePieceColor, {
			entity: "card",
			name: translatable("onepiece.attributes.color.name", "Color"),
			description: translatable("onepiece.attributes.color.description", "The color present on the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				B: { label: translatable("onepiece.attributes.color.values.black", "Black") },
				G: { label: translatable("onepiece.attributes.color.values.green", "Green") },
				R: { label: translatable("onepiece.attributes.color.values.red", "Red") },
				Y: { label: translatable("onepiece.attributes.color.values.yellow", "Yellow") },
				U: { label: translatable("onepiece.attributes.color.values.blue", "Blue") },
				P: { label: translatable("onepiece.attributes.color.values.purple", "Purple") }
			}
		}),
		finishes: enumAttribute(OnePieceCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("onepiece.attributes.finish.name", "Finish"),
			description: translatable("onepiece.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: onepieceFinishValues
		}),
		finish: enumAttribute(OnePieceCardFinish, {
			entity: "inventory",
			name: translatable("onepiece.attributes.finish.name", "Finish"),
			description: translatable("onepiece.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: onepieceFinishValues
		}),
		attribute: enumAttribute(OnePieceCharacterAttribute, {
			entity: "card",
			name: translatable("onepiece.attributes.attribute.name", "Attribute"),
			description: translatable("onepiece.attributes.attribute.description", "The attribute of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				Ranged: { label: translatable("onepiece.attributes.attribute.values.ranged", "Ranged") },
				Strike: { label: translatable("onepiece.attributes.attribute.values.strike", "Strike") },
				Wisdom: { label: translatable("onepiece.attributes.attribute.values.wisdom", "Wisdom") },
				Special: { label: translatable("onepiece.attributes.attribute.values.special", "Special") },
				Slash: { label: translatable("onepiece.attributes.attribute.values.slash", "Slash") }
			}
		}),
		variant: enumAttribute(OnePieceCardVariant, {
			entity: "variant",
			name: translatable("onepiece.attributes.variant.name", "Variant"),
			description: translatable("onepiece.attributes.variant.description", "The variant of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				"Alternate Art": { label: translatable("onepiece.attributes.variant.values.alternate_art", "Alternate Art") },
				Manga: { label: translatable("onepiece.attributes.variant.values.manga", "Manga") },
				Promo: { label: translatable("onepiece.attributes.variant.values.promo", "Promo") },
				"Super Pre-Release": { label: translatable("onepiece.attributes.variant.values.spr", "Super Pre-Release") },
				"Jolly Roger Foil": { label: translatable("onepiece.attributes.variant.values.jolly_roger_foil", "Jolly Roger Foil") },
				"Textured Foil": { label: translatable("onepiece.attributes.variant.values.textured_foil", "Textured Foil") },
				"Alternate Manga Art": { label: translatable("onepiece.attributes.variant.values.alternate_manga_art", "Alternate Manga Art") },
				"Full Art": { label: translatable("onepiece.attributes.variant.values.full_art", "Full Art") },
				"Wanted Poster": { label: translatable("onepiece.attributes.variant.values.wanted_poster", "Wanted Poster") },
				SP: { label: translatable("onepiece.attributes.variant.values.sp", "SP") },
				"Pirate Foil": { label: translatable("onepiece.attributes.variant.values.pirate_foil", "Pirate Foil") },
				Gold: { label: translatable("onepiece.attributes.variant.values.gold", "Gold") },
				Silver: { label: translatable("onepiece.attributes.variant.values.silver", "Silver") },
				"Box Topper": { label: translatable("onepiece.attributes.variant.values.box_topper", "Box Topper") },
				Reprint: { label: translatable("onepiece.attributes.variant.values.reprint", "Reprint") },
				"Super Alternate Art": { label: translatable("onepiece.attributes.variant.values.super_alternate_art", "Super Alternate Art") },
				"Treasure Rare": { label: translatable("onepiece.attributes.variant.values.treasure_rare", "Treasure Rare") },
				Gem: { label: translatable("onepiece.attributes.variant.values.gem", "Gem") },
				"Serial Numbered": { label: translatable("onepiece.attributes.variant.values.serial_numbered", "Serial Numbered") }
			}
		}),
		printNumber: printNumberAttribute,
		type: enumAttribute(OnePieceCardType, {
			entity: "card",
			name: translatable("onepiece.attributes.type.name", "Card Type"),
			description: translatable("onepiece.attributes.type.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[OnePieceCardType.LEADER]: { label: translatable("onepiece.attributes.type.values.leader", "Leader") },
				[OnePieceCardType.DON]: { label: translatable("onepiece.attributes.type.values.don", "DON!!") },
				[OnePieceCardType.CHARACTER]: { label: translatable("onepiece.attributes.type.values.character", "Character") },
				[OnePieceCardType.EVENT]: { label: translatable("onepiece.attributes.type.values.event", "Event") },
				[OnePieceCardType.STAGE]: { label: translatable("onepiece.attributes.type.values.stage", "Stage") }
			}
		}),
		life: {
			entity: "card",
			name: translatable("onepiece.attributes.life.name", "Life"),
			description: translatable("onepiece.attributes.life.description", "Life points (for Leader cards)"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		power: {
			entity: "card",
			name: translatable("onepiece.attributes.power.name", "Power"),
			description: translatable("onepiece.attributes.power.description", "Power value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		cost: {
			entity: "card",
			name: translatable("onepiece.attributes.cost.name", "Cost"),
			description: translatable("onepiece.attributes.cost.description", "Cost to play the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		counter: {
			entity: "card",
			name: translatable("onepiece.attributes.counter.name", "Counter"),
			description: translatable("onepiece.attributes.counter.description", "Counter power value"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		subtypes: {
			entity: "card",
			name: translatable("onepiece.attributes.subtypes.name", "Subtypes"),
			description: translatable("onepiece.attributes.subtypes.description", "Character subtypes and traits"),
			type: "string",
			array: true
		},
		rulesText: {
			entity: "variant",
			name: translatable("onepiece.attributes.rulesText.name", "Rules Text"),
			description: translatable("onepiece.attributes.rulesText.description", "The rules text of the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"variant",
			"rarity",
			"color"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"attribute",
			"type",
			"life",
			"power",
			"cost",
			"counter"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText"],
		statistics: [
			"rarity",
			"color",
			"finish",
			"attribute",
			"variant",
			"type",
			"life",
			"power",
			"cost",
			"subtypes"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: onePieceListPresets,
	basicAttributes: ["color", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Tcgplayer, SupportedMarketplace.Cardmarket],
	vendorIds: [cardmarketVendorId, tcgplayerVendorId],
	csvTemplateExample: {
		vendorIds: {
			Cardmarket: "690795",
			TCGplayer: "454515"
		},
		printNumber: "OP01-003",
		cardName: "Monkey.D.Luffy (003)",
		expansion: "Romance Dawn",
		expansionCode: "OP01",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/palworld/palworld.types.ts
let PalworldRarity = /* @__PURE__ */ function(PalworldRarity) {
	PalworldRarity["COMMON"] = "Common";
	PalworldRarity["UNCOMMON"] = "Uncommon";
	PalworldRarity["RARE"] = "Rare";
	PalworldRarity["DOUBLE_RARE"] = "Double Rare";
	PalworldRarity["SUPER_RARE"] = "Super Rare";
	PalworldRarity["SPECIAL"] = "Special";
	PalworldRarity["SUPER_SPECIAL"] = "Super Special";
	PalworldRarity["OVER_SUPER_RARE"] = "Over Super Rare";
	PalworldRarity["TRIAL_DECK"] = "Trial Deck";
	PalworldRarity["TRIAL_DECK_RARE"] = "Trial Deck Rare";
	PalworldRarity["TRIAL_DECK_SPECIAL"] = "Trial Deck Special";
	PalworldRarity["TRIAL_DECK_SUPER_RARE"] = "Trial Deck Super Rare";
	PalworldRarity["PROMO"] = "Promo";
	return PalworldRarity;
}({});
let PalworldFinish = /* @__PURE__ */ function(PalworldFinish) {
	PalworldFinish["STANDARD"] = "Standard";
	return PalworldFinish;
}({});
let PalworldCardKind = /* @__PURE__ */ function(PalworldCardKind) {
	PalworldCardKind["PAL"] = "Pal";
	PalworldCardKind["EVENT"] = "Event";
	PalworldCardKind["GEAR"] = "Gear";
	PalworldCardKind["SOUL"] = "Soul";
	PalworldCardKind["STRUCTURE"] = "Structure";
	return PalworldCardKind;
}({});
let PalworldCardKindSub = /* @__PURE__ */ function(PalworldCardKindSub) {
	PalworldCardKindSub["NORMAL_PAL"] = "Normal Pal";
	PalworldCardKindSub["LUCKY_PAL"] = "Lucky Pal";
	return PalworldCardKindSub;
}({});
let PalworldColor = /* @__PURE__ */ function(PalworldColor) {
	PalworldColor["RED"] = "Red";
	PalworldColor["BLUE"] = "Blue";
	PalworldColor["GREEN"] = "Green";
	PalworldColor["PURPLE"] = "Purple";
	PalworldColor["COLORLESS"] = "Colorless";
	return PalworldColor;
}({});
let PalworldElement = /* @__PURE__ */ function(PalworldElement) {
	PalworldElement["NEUTRAL"] = "Neutral";
	PalworldElement["FIRE"] = "Fire";
	PalworldElement["WATER"] = "Water";
	PalworldElement["ELECTRIC"] = "Electric";
	PalworldElement["GRASS"] = "Grass";
	PalworldElement["DARK"] = "Dark";
	PalworldElement["DRAGON"] = "Dragon";
	PalworldElement["GROUND"] = "Ground";
	PalworldElement["ICE"] = "Ice";
	return PalworldElement;
}({});
let PalworldAptitude = /* @__PURE__ */ function(PalworldAptitude) {
	PalworldAptitude["HARVESTING"] = "Harvesting";
	PalworldAptitude["CRAFTING"] = "Crafting";
	PalworldAptitude["COLLECTING"] = "Collecting";
	PalworldAptitude["TRANSPORTING"] = "Transporting";
	PalworldAptitude["KINDLING"] = "Kindling";
	PalworldAptitude["COOLING"] = "Cooling";
	PalworldAptitude["ELECTRICITY"] = "Electricity";
	PalworldAptitude["FARMING"] = "Farming";
	return PalworldAptitude;
}({});

//#endregion
//#region src/lib/games/palworld/palworld.presets.ts
const palworldListPresets = {
	Standard: {
		name: translatable("palworld.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("palworld.presets.standard.description", "A complete set with one copy of each card"),
		matchers: [{
			filters: { finish: [PalworldFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("palworld.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("palworld.presets.playset.description", "A complete set with the maximum four copies of each card allowed in a deck"),
		matchers: [{
			filters: { finish: [PalworldFinish.STANDARD] },
			quantity: 4
		}]
	}
};

//#endregion
//#region src/lib/games/palworld/palworld.game.ts
const palworldFinishValues = { [PalworldFinish.STANDARD]: { label: translatable("global.attributes.finish.values.standard", "Standard") } };
const palworldFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(PalworldRarity).optional(),
	finish: makeZodEnumFilter(PalworldFinish).optional(),
	finishes: makeZodEnumFilter(PalworldFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	cardKind: makeZodEnumFilter(PalworldCardKind).optional(),
	cardKindSub: makeZodEnumFilter(PalworldCardKindSub).optional(),
	color: makeZodEnumFilter(PalworldColor).optional(),
	types: makeZodEnumFilter(PalworldElement).optional(),
	aptitudes: makeZodEnumFilter(PalworldAptitude).optional(),
	cost: zodNumericFilter.optional(),
	power: zodNumericFilter.optional(),
	attack: zodNumericFilter.optional()
});
const PalworldGameConfiguration = {
	name: "Palworld TCG",
	shortName: "Palworld",
	slug: "palworld",
	icon: {
		dark: "palworld-icon.png",
		light: "palworld-icon.png"
	},
	image: {
		dark: "palworld.png",
		light: "palworld.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(PalworldFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: palworldFinishValues
		}),
		finish: enumAttribute(PalworldFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: palworldFinishValues
		}),
		rarity: enumAttribute(PalworldRarity, {
			entity: "variant",
			name: translatable("palworld.attributes.rarity.name", "Rarity"),
			description: translatable("palworld.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[PalworldRarity.COMMON]: { label: translatable("palworld.attributes.rarity.values.common", "Common") },
				[PalworldRarity.UNCOMMON]: { label: translatable("palworld.attributes.rarity.values.uncommon", "Uncommon") },
				[PalworldRarity.RARE]: { label: translatable("palworld.attributes.rarity.values.rare", "Rare") },
				[PalworldRarity.DOUBLE_RARE]: { label: translatable("palworld.attributes.rarity.values.double_rare", "Double Rare") },
				[PalworldRarity.SUPER_RARE]: { label: translatable("palworld.attributes.rarity.values.super_rare", "Super Rare") },
				[PalworldRarity.SPECIAL]: { label: translatable("palworld.attributes.rarity.values.special", "Special") },
				[PalworldRarity.SUPER_SPECIAL]: { label: translatable("palworld.attributes.rarity.values.super_special", "Super Special") },
				[PalworldRarity.OVER_SUPER_RARE]: { label: translatable("palworld.attributes.rarity.values.over_super_rare", "Over Super Rare") },
				[PalworldRarity.TRIAL_DECK]: { label: translatable("palworld.attributes.rarity.values.trial_deck", "Trial Deck") },
				[PalworldRarity.TRIAL_DECK_RARE]: { label: translatable("palworld.attributes.rarity.values.trial_deck_rare", "Trial Deck Rare") },
				[PalworldRarity.TRIAL_DECK_SPECIAL]: { label: translatable("palworld.attributes.rarity.values.trial_deck_special", "Trial Deck Special") },
				[PalworldRarity.TRIAL_DECK_SUPER_RARE]: { label: translatable("palworld.attributes.rarity.values.trial_deck_super_rare", "Trial Deck Super Rare") },
				[PalworldRarity.PROMO]: { label: translatable("palworld.attributes.rarity.values.promo", "Promo") }
			}
		}),
		printNumber: printNumberAttribute,
		cardKind: enumAttribute(PalworldCardKind, {
			entity: "card",
			name: translatable("palworld.attributes.cardKind.name", "Card Type"),
			description: translatable("palworld.attributes.cardKind.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[PalworldCardKind.PAL]: { label: translatable("palworld.attributes.cardKind.values.pal", "Pal") },
				[PalworldCardKind.EVENT]: { label: translatable("palworld.attributes.cardKind.values.event", "Event") },
				[PalworldCardKind.GEAR]: { label: translatable("palworld.attributes.cardKind.values.gear", "Gear") },
				[PalworldCardKind.SOUL]: { label: translatable("palworld.attributes.cardKind.values.soul", "Soul") },
				[PalworldCardKind.STRUCTURE]: { label: translatable("palworld.attributes.cardKind.values.structure", "Structure") }
			}
		}),
		cardKindSub: enumAttribute(PalworldCardKindSub, {
			entity: "card",
			name: translatable("palworld.attributes.cardKindSub.name", "Card Subtype"),
			description: translatable("palworld.attributes.cardKindSub.description", "The subtype of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[PalworldCardKindSub.NORMAL_PAL]: { label: translatable("palworld.attributes.cardKindSub.values.normal_pal", "Normal Pal") },
				[PalworldCardKindSub.LUCKY_PAL]: { label: translatable("palworld.attributes.cardKindSub.values.lucky_pal", "Lucky Pal") }
			}
		}),
		color: enumAttribute(PalworldColor, {
			entity: "card",
			name: translatable("palworld.attributes.color.name", "Color"),
			description: translatable("palworld.attributes.color.description", "The color of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[PalworldColor.RED]: { label: translatable("palworld.attributes.color.values.red", "Red") },
				[PalworldColor.BLUE]: { label: translatable("palworld.attributes.color.values.blue", "Blue") },
				[PalworldColor.GREEN]: { label: translatable("palworld.attributes.color.values.green", "Green") },
				[PalworldColor.PURPLE]: { label: translatable("palworld.attributes.color.values.purple", "Purple") },
				[PalworldColor.COLORLESS]: { label: translatable("palworld.attributes.color.values.colorless", "Colorless") }
			}
		}),
		types: enumAttribute(PalworldElement, {
			entity: "card",
			name: translatable("palworld.attributes.types.name", "Element"),
			description: translatable("palworld.attributes.types.description", "The elements of the Pal"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[PalworldElement.NEUTRAL]: {
					label: translatable("palworld.attributes.types.values.neutral", "Neutral"),
					image: "/images/palworld/types/neutral.png"
				},
				[PalworldElement.FIRE]: {
					label: translatable("palworld.attributes.types.values.fire", "Fire"),
					image: "/images/palworld/types/fire.png"
				},
				[PalworldElement.WATER]: {
					label: translatable("palworld.attributes.types.values.water", "Water"),
					image: "/images/palworld/types/water.png"
				},
				[PalworldElement.ELECTRIC]: {
					label: translatable("palworld.attributes.types.values.electric", "Electric"),
					image: "/images/palworld/types/electric.png"
				},
				[PalworldElement.GRASS]: {
					label: translatable("palworld.attributes.types.values.grass", "Grass"),
					image: "/images/palworld/types/grass.png"
				},
				[PalworldElement.DARK]: {
					label: translatable("palworld.attributes.types.values.dark", "Dark"),
					image: "/images/palworld/types/dark.png"
				},
				[PalworldElement.DRAGON]: {
					label: translatable("palworld.attributes.types.values.dragon", "Dragon"),
					image: "/images/palworld/types/dragon.png"
				},
				[PalworldElement.GROUND]: {
					label: translatable("palworld.attributes.types.values.ground", "Ground"),
					image: "/images/palworld/types/ground.png"
				},
				[PalworldElement.ICE]: {
					label: translatable("palworld.attributes.types.values.ice", "Ice"),
					image: "/images/palworld/types/ice.png"
				}
			}
		}),
		aptitudes: enumAttribute(PalworldAptitude, {
			entity: "card",
			name: translatable("palworld.attributes.aptitudes.name", "Aptitudes"),
			description: translatable("palworld.attributes.aptitudes.description", "The work aptitudes of the Pal"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[PalworldAptitude.HARVESTING]: { label: translatable("palworld.attributes.aptitudes.values.harvesting", "Harvesting") },
				[PalworldAptitude.CRAFTING]: { label: translatable("palworld.attributes.aptitudes.values.crafting", "Crafting") },
				[PalworldAptitude.COLLECTING]: { label: translatable("palworld.attributes.aptitudes.values.collecting", "Collecting") },
				[PalworldAptitude.TRANSPORTING]: { label: translatable("palworld.attributes.aptitudes.values.transporting", "Transporting") },
				[PalworldAptitude.KINDLING]: { label: translatable("palworld.attributes.aptitudes.values.kindling", "Kindling") },
				[PalworldAptitude.COOLING]: { label: translatable("palworld.attributes.aptitudes.values.cooling", "Cooling") },
				[PalworldAptitude.ELECTRICITY]: { label: translatable("palworld.attributes.aptitudes.values.electricity", "Electricity") },
				[PalworldAptitude.FARMING]: { label: translatable("palworld.attributes.aptitudes.values.farming", "Farming") }
			}
		}),
		cost: {
			entity: "card",
			name: translatable("palworld.attributes.cost.name", "Cost"),
			description: translatable("palworld.attributes.cost.description", "Cost to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		power: {
			entity: "card",
			name: translatable("palworld.attributes.power.name", "Power"),
			description: translatable("palworld.attributes.power.description", "Power value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		attack: {
			entity: "card",
			name: translatable("palworld.attributes.attack.name", "Attack"),
			description: translatable("palworld.attributes.attack.description", "Attack value of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		rulesText: {
			entity: "variant",
			name: translatable("palworld.attributes.rulesText.name", "Rules Text"),
			description: translatable("palworld.attributes.rulesText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		},
		flavorText: {
			entity: "variant",
			name: translatable("palworld.attributes.flavorText.name", "Flavor Text"),
			description: translatable("palworld.attributes.flavorText.description", "Flavor text of the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"cardKind",
			"color"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"cardKindSub",
			"types",
			"aptitudes",
			"finishes"
		]
	}, {
		label: "Card Stats",
		filters: [
			"cost",
			"power",
			"attack"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText", "flavorText"],
		statistics: [
			"rarity",
			"cardKind",
			"cardKindSub",
			"color",
			"types",
			"aptitudes",
			"cost",
			"power",
			"attack"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: palworldListPresets,
	basicAttributes: ["rarity", "cardKind"],
	finishHierarchy: [CardFinish.STANDARD],
	supportedMarketplaces: [SupportedMarketplace.Tcgplayer],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "ETD01-023",
		cardName: "Lamball – My First Pal",
		expansion: "Trial Deck \"Dawn of Palpagos Red・Blue\"",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/pokemon/pokemon.types.ts
let PokemonCardFinish = /* @__PURE__ */ function(PokemonCardFinish) {
	PokemonCardFinish["REVERSE"] = "Reverse Holo";
	PokemonCardFinish["STANDARD"] = "Standard";
	PokemonCardFinish["FOIL"] = "Foil";
	return PokemonCardFinish;
}({});
let PokemonCardVariant = /* @__PURE__ */ function(PokemonCardVariant) {
	PokemonCardVariant["FIRST_EDITION"] = "1st Edition";
	PokemonCardVariant["UNLIMITED"] = "Unlimited";
	PokemonCardVariant["POKE_BALL"] = "Poke Ball Pattern";
	PokemonCardVariant["MASTER_BALL"] = "Master Ball Pattern";
	PokemonCardVariant["FRIEND_BALL"] = "Friend Ball Pattern";
	PokemonCardVariant["LOVE_BALL"] = "Love Ball Pattern";
	PokemonCardVariant["DUSK_BALL"] = "Dusk Ball Pattern";
	PokemonCardVariant["QUICK_BALL"] = "Quick Ball Pattern";
	PokemonCardVariant["TEAM_ROCKET"] = "Team Rocket";
	PokemonCardVariant["ENERGY_SYMBOL"] = "Energy Symbol Pattern";
	PokemonCardVariant["HOLIDAY_CALENDAR"] = "Holiday Calendar";
	return PokemonCardVariant;
}({});
let PokemonRarity = /* @__PURE__ */ function(PokemonRarity) {
	PokemonRarity["ACE_SPEC_RARE"] = "ACE_SPEC_RARE";
	PokemonRarity["AMAZING_RARE"] = "AMAZING_RARE";
	PokemonRarity["BLACK_WHITE_RARE"] = "BLACK_WHITE_RARE";
	PokemonRarity["CLASSIC"] = "CLASSIC";
	PokemonRarity["CLASSIC_COLLECTION"] = "CLASSIC_COLLECTION";
	PokemonRarity["COMMON"] = "COMMON";
	PokemonRarity["CROWN"] = "CROWN";
	PokemonRarity["DOUBLE_RARE"] = "DOUBLE_RARE";
	PokemonRarity["FOUR_DIAMOND"] = "FOUR_DIAMOND";
	PokemonRarity["FULL_ART_TRAINER"] = "FULL_ART_TRAINER";
	PokemonRarity["FUTURISTIC_RARE"] = "FUTURISTIC_RARE";
	PokemonRarity["HOLO_RARE_V"] = "HOLO_RARE_V";
	PokemonRarity["HOLO_RARE_VMAX"] = "HOLO_RARE_VMAX";
	PokemonRarity["HOLO_RARE_VSTAR"] = "HOLO_RARE_VSTAR";
	PokemonRarity["HOLO_RARE"] = "HOLO_RARE";
	PokemonRarity["HYPER_RARE"] = "HYPER_RARE";
	PokemonRarity["ILLUSTRATION_RARE"] = "ILLUSTRATION_RARE";
	PokemonRarity["LEGEND"] = "LEGEND";
	PokemonRarity["MEGA_ATTACK_RARE"] = "MEGA_ATTACK_RARE";
	PokemonRarity["NONE"] = "NONE";
	PokemonRarity["ONE_DIAMOND"] = "ONE_DIAMOND";
	PokemonRarity["ONE_SHINY"] = "ONE_SHINY";
	PokemonRarity["ONE_STAR"] = "ONE_STAR";
	PokemonRarity["PIKACHU_RARE"] = "PIKACHU_RARE";
	PokemonRarity["PROMO"] = "PROMO";
	PokemonRarity["RADIANT_RARE"] = "RADIANT_RARE";
	PokemonRarity["RARE_BREAK"] = "RARE_BREAK";
	PokemonRarity["RARE_HOLO_EX"] = "RARE_HOLO_EX";
	PokemonRarity["RARE_HOLO_GX"] = "RARE_HOLO_GX";
	PokemonRarity["RARE_HOLO_LV_X"] = "RARE_HOLO_LV_X";
	PokemonRarity["RARE_HOLO_STAR"] = "RARE_HOLO_STAR";
	PokemonRarity["RARE_HOLO"] = "RARE_HOLO";
	PokemonRarity["RARE_PRIME"] = "RARE_PRIME";
	PokemonRarity["RARE_PRISM_STAR"] = "RARE_PRISM_STAR";
	PokemonRarity["RARE_RAINBOW"] = "RARE_RAINBOW";
	PokemonRarity["RARE_SHINING"] = "RARE_SHINING";
	PokemonRarity["RARE_SHINY_GX"] = "RARE_SHINY_GX";
	PokemonRarity["RARE"] = "RARE";
	PokemonRarity["RGB_RARE"] = "RGB_RARE";
	PokemonRarity["SECRET_RARE"] = "SECRET_RARE";
	PokemonRarity["SHINY_RARE_V"] = "SHINY_RARE_V";
	PokemonRarity["SHINY_RARE_VMAX"] = "SHINY_RARE_VMAX";
	PokemonRarity["SHINY_RARE"] = "SHINY_RARE";
	PokemonRarity["SHINY_ULTRA_RARE"] = "SHINY_ULTRA_RARE";
	PokemonRarity["SPECIAL_ILLUSTRATION_RARE"] = "SPECIAL_ILLUSTRATION_RARE";
	PokemonRarity["THREE_DIAMOND"] = "THREE_DIAMOND";
	PokemonRarity["THREE_STAR"] = "THREE_STAR";
	PokemonRarity["TRAINER_GALLERY_RARE_HOLO"] = "TRAINER_GALLERY_RARE_HOLO";
	PokemonRarity["TWO_DIAMOND"] = "TWO_DIAMOND";
	PokemonRarity["TWO_SHINY"] = "TWO_SHINY";
	PokemonRarity["TWO_STAR"] = "TWO_STAR";
	PokemonRarity["ULTRA_RARE"] = "ULTRA_RARE";
	PokemonRarity["UNCOMMON"] = "UNCOMMON";
	return PokemonRarity;
}({});
let PokemonCardType = /* @__PURE__ */ function(PokemonCardType) {
	PokemonCardType["POKEMON"] = "Pokemon";
	PokemonCardType["TRAINER"] = "Trainer";
	PokemonCardType["ENERGY"] = "Energy";
	return PokemonCardType;
}({});
let PokemonType = /* @__PURE__ */ function(PokemonType) {
	PokemonType["COLORLESS"] = "Colorless";
	PokemonType["DARK"] = "Dark";
	PokemonType["DRAGON"] = "Dragon";
	PokemonType["FAIRY"] = "Fairy";
	PokemonType["FIGHTING"] = "Fighting";
	PokemonType["FIRE"] = "Fire";
	PokemonType["GRASS"] = "Grass";
	PokemonType["ELECTRIC"] = "Electric";
	PokemonType["STEEL"] = "Steel";
	PokemonType["PSYCHIC"] = "Psychic";
	PokemonType["WATER"] = "Water";
	return PokemonType;
}({});
let PokemonCardStage = /* @__PURE__ */ function(PokemonCardStage) {
	PokemonCardStage["BABY"] = "Baby";
	PokemonCardStage["BASIC"] = "Basic";
	PokemonCardStage["BREAK"] = "BREAK";
	PokemonCardStage["GX"] = "GX";
	PokemonCardStage["LEVEL_UP"] = "LEVEL-UP";
	PokemonCardStage["MEGA"] = "MEGA";
	PokemonCardStage["RESTORED"] = "RESTORED";
	PokemonCardStage["STAGE_1"] = "Stage1";
	PokemonCardStage["STAGE_2"] = "Stage2";
	PokemonCardStage["V"] = "V";
	PokemonCardStage["V_UNION"] = "V-UNION";
	PokemonCardStage["VMAX"] = "VMAX";
	PokemonCardStage["VSTAR"] = "VSTAR";
	return PokemonCardStage;
}({});

//#endregion
//#region src/lib/games/pokemon/pokemon.presets.ts
const pokemonListPresets = {
	Standard: {
		name: translatable("pokemon.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("pokemon.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [PokemonCardFinish.STANDARD] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("pokemon.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("pokemon.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: { finish: [PokemonCardFinish.STANDARD] },
			quantity: 4
		}]
	},
	Foil: {
		name: translatable("pokemon.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("pokemon.presets.foil.description", "A complete set with one copy of each card in holo foil finish"),
		matchers: [{
			filters: { finish: [PokemonCardFinish.FOIL] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("pokemon.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("pokemon.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in holo foil finish"),
		matchers: [{
			filters: { finish: [PokemonCardFinish.FOIL] },
			quantity: 4
		}]
	},
	"Master Set": {
		name: translatable("pokemon.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("pokemon.presets.masterset.description", "A complete set with one copy of each card in standard, holo foil, and reverse holo finishes"),
		matchers: [
			{
				filters: { finish: [PokemonCardFinish.STANDARD] },
				quantity: 1
			},
			{
				filters: { finish: [PokemonCardFinish.FOIL] },
				quantity: 1
			},
			{
				filters: { finish: [PokemonCardFinish.REVERSE] },
				quantity: 1
			}
		]
	}
};

//#endregion
//#region src/lib/games/pokemon/pokemon.game.ts
/**
* Shared finish values for Pokemon - used by both finishes and finish attributes
*/
const pokemonFinishValues = {
	[PokemonCardFinish.STANDARD]: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	[PokemonCardFinish.FOIL]: { label: translatable("global.attributes.finish.values.foil", "Foil") },
	[PokemonCardFinish.REVERSE]: { label: translatable("global.attributes.finish.values.reverse", "Reverse Holo") }
};
const pokemonFilters = z$1.object({
	cardType: makeZodEnumFilter(PokemonCardType).optional(),
	expansion: zodRelationFilter.optional(),
	name: zodStringFilter.optional(),
	rarity: makeZodEnumFilter(PokemonRarity).optional(),
	finish: makeZodEnumFilter(PokemonCardFinish).optional(),
	finishes: makeZodEnumFilter(PokemonCardFinish).optional(),
	variant: makeZodEnumFilter(PokemonCardVariant).optional(),
	pokemonTypes: makeZodEnumFilter(PokemonType).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	hp: zodNumberFilter.optional(),
	stage: makeZodEnumFilter(PokemonCardStage).optional(),
	retreatCost: zodNumberFilter.optional()
});
const PokemonGameConfiguration = {
	name: "Pokémon",
	shortName: "Pokémon",
	slug: "pokemon",
	icon: {
		dark: "pokemon-icon.png",
		light: "pokemon-icon.png"
	},
	image: {
		dark: "pokemon.png",
		light: "pokemon.png"
	},
	attributes: {
		name: nameAttribute,
		cardType: enumAttribute(PokemonCardType, {
			entity: "card",
			name: translatable("pokemon.attributes.cardType.name", "Card Type"),
			description: translatable("pokemon.attributes.cardType.description", "Type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[PokemonCardType.ENERGY]: { label: translatable("pokemon.attributes.cardType.values.energy", "Energy") },
				[PokemonCardType.POKEMON]: { label: translatable("pokemon.attributes.cardType.values.pokemon", "Pokemon") },
				[PokemonCardType.TRAINER]: { label: translatable("pokemon.attributes.cardType.values.trainer", "Trainer") }
			}
		}),
		finishes: enumAttribute(PokemonCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: true,
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			filter: FilterComponentType.CheckboxList,
			values: pokemonFinishValues
		}),
		finish: enumAttribute(PokemonCardFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: false,
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			filter: FilterComponentType.CheckboxList,
			values: pokemonFinishValues
		}),
		variant: enumAttribute(PokemonCardVariant, {
			entity: "variant",
			name: translatable("global.attributes.variant.name", "Variant"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			description: translatable("global.attributes.variant.description", "Variant of the card (first edition, unlimited, etc.)"),
			values: {
				[PokemonCardVariant.FIRST_EDITION]: { label: translatable("pokemon.attributes.variant.values.first_edition", "1st Edition") },
				[PokemonCardVariant.UNLIMITED]: { label: translatable("pokemon.attributes.variant.values.unlimited", "Unlimited") },
				[PokemonCardVariant.POKE_BALL]: { label: translatable("pokemon.attributes.variant.values.poke_ball", "Poke Ball Pattern") },
				[PokemonCardVariant.MASTER_BALL]: { label: translatable("pokemon.attributes.variant.values.master_ball", "Master Ball Pattern") },
				[PokemonCardVariant.FRIEND_BALL]: { label: translatable("pokemon.attributes.variant.values.friend_ball", "Friend Ball Pattern") },
				[PokemonCardVariant.LOVE_BALL]: { label: translatable("pokemon.attributes.variant.values.love_ball", "Love Ball Pattern") },
				[PokemonCardVariant.DUSK_BALL]: { label: translatable("pokemon.attributes.variant.values.dusk_ball", "Dusk Ball Pattern") },
				[PokemonCardVariant.QUICK_BALL]: { label: translatable("pokemon.attributes.variant.values.quick_ball", "Quick Ball Pattern") },
				[PokemonCardVariant.TEAM_ROCKET]: { label: translatable("pokemon.attributes.variant.values.team_rocket", "Team Rocket") },
				[PokemonCardVariant.ENERGY_SYMBOL]: { label: translatable("pokemon.attributes.variant.values.energy_symbol", "Energy Symbol Pattern") },
				[PokemonCardVariant.HOLIDAY_CALENDAR]: { label: translatable("pokemon.attributes.variant.values.holiday_calendar", "Holiday Calendar") }
			}
		}),
		pokemonTypes: enumAttribute(PokemonType, {
			entity: "card",
			name: translatable("pokemon.attributes.pokemonTypes.name", "Pokemon Type"),
			description: translatable("pokemon.attributes.pokemonTypes.description", "Type of the Pokemon"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[PokemonType.COLORLESS]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.colorless", "Colorless"),
					image: "/images/pokemon/colorless.png"
				},
				[PokemonType.DARK]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.dark", "Dark"),
					image: "/images/pokemon/dark.png"
				},
				[PokemonType.DRAGON]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.dragon", "Dragon"),
					image: "/images/pokemon/dragon.png"
				},
				[PokemonType.FAIRY]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.fairy", "Fairy"),
					image: "/images/pokemon/fairy.png"
				},
				[PokemonType.FIGHTING]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.fighting", "Fighting"),
					image: "/images/pokemon/fighting.png"
				},
				[PokemonType.FIRE]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.fire", "Fire"),
					image: "/images/pokemon/fire.png"
				},
				[PokemonType.GRASS]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.grass", "Grass"),
					image: "/images/pokemon/grass.png"
				},
				[PokemonType.ELECTRIC]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.electric", "Electric"),
					image: "/images/pokemon/electric.png"
				},
				[PokemonType.STEEL]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.steel", "Steel"),
					image: "/images/pokemon/steel.png"
				},
				[PokemonType.PSYCHIC]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.psychic", "Psychic"),
					image: "/images/pokemon/psychic.png"
				},
				[PokemonType.WATER]: {
					label: translatable("pokemon.attributes.pokemonTypes.values.water", "Water"),
					image: "/images/pokemon/water.png"
				}
			}
		}),
		rarity: enumAttribute(PokemonRarity, {
			entity: "variant",
			name: translatable("pokemon.attributes.rarity.name", "Rarity"),
			description: translatable("pokemon.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[PokemonRarity.ACE_SPEC_RARE]: { label: translatable("pokemon.attributes.rarity.values.ace_spec_rare", "ACE SPEC Rare") },
				[PokemonRarity.AMAZING_RARE]: { label: translatable("pokemon.attributes.rarity.values.amazing_rare", "Amazing Rare") },
				[PokemonRarity.BLACK_WHITE_RARE]: { label: translatable("pokemon.attributes.rarity.values.black_white_rare", "Black White Rare") },
				[PokemonRarity.CLASSIC]: { label: translatable("pokemon.attributes.rarity.values.classic", "Classic") },
				[PokemonRarity.CLASSIC_COLLECTION]: { label: translatable("pokemon.attributes.rarity.values.classic_collection", "Classic Collection") },
				[PokemonRarity.COMMON]: { label: translatable("pokemon.attributes.rarity.values.common", "Common") },
				[PokemonRarity.CROWN]: { label: translatable("pokemon.attributes.rarity.values.crown", "Crown") },
				[PokemonRarity.DOUBLE_RARE]: { label: translatable("pokemon.attributes.rarity.values.double_rare", "Double Rare") },
				[PokemonRarity.FOUR_DIAMOND]: { label: translatable("pokemon.attributes.rarity.values.four_diamond", "Four Diamond") },
				[PokemonRarity.FULL_ART_TRAINER]: { label: translatable("pokemon.attributes.rarity.values.full_art_trainer", "Full Art Trainer") },
				[PokemonRarity.FUTURISTIC_RARE]: { label: translatable("pokemon.attributes.rarity.values.futuristic_rare", "Futuristic Rare") },
				[PokemonRarity.HOLO_RARE]: { label: translatable("pokemon.attributes.rarity.values.holo_rare", "Rare Holo") },
				[PokemonRarity.HOLO_RARE_V]: { label: translatable("pokemon.attributes.rarity.values.holo_rare_v", "Rare Holo V") },
				[PokemonRarity.HOLO_RARE_VMAX]: { label: translatable("pokemon.attributes.rarity.values.holo_rare_vmax", "Rare Holo VMax") },
				[PokemonRarity.HOLO_RARE_VSTAR]: { label: translatable("pokemon.attributes.rarity.values.holo_rare_vstar", "Rare Holo VStar") },
				[PokemonRarity.HYPER_RARE]: { label: translatable("pokemon.attributes.rarity.values.hyper_rare", "Hyper Rare") },
				[PokemonRarity.ILLUSTRATION_RARE]: { label: translatable("pokemon.attributes.rarity.values.illustration_rare", "Illustration Rare") },
				[PokemonRarity.LEGEND]: { label: translatable("pokemon.attributes.rarity.values.legend", "Legend") },
				[PokemonRarity.MEGA_ATTACK_RARE]: { label: translatable("pokemon.attributes.rarity.values.mega_attack_rare", "Mega Attack Rare") },
				[PokemonRarity.NONE]: { label: translatable("pokemon.attributes.rarity.values.none", "None") },
				[PokemonRarity.ONE_DIAMOND]: { label: translatable("pokemon.attributes.rarity.values.one_diamond", "One Diamond") },
				[PokemonRarity.ONE_SHINY]: { label: translatable("pokemon.attributes.rarity.values.one_shiny", "One Shiny") },
				[PokemonRarity.ONE_STAR]: { label: translatable("pokemon.attributes.rarity.values.one_star", "One Star") },
				[PokemonRarity.PIKACHU_RARE]: { label: translatable("pokemon.attributes.rarity.values.pikachu_rare", "Pikachu Rare") },
				[PokemonRarity.PROMO]: { label: translatable("pokemon.attributes.rarity.values.promo", "Promo") },
				[PokemonRarity.RADIANT_RARE]: { label: translatable("pokemon.attributes.rarity.values.radiant_rare", "Radiant Rare") },
				[PokemonRarity.RARE]: { label: translatable("pokemon.attributes.rarity.values.rare", "Rare") },
				[PokemonRarity.RARE_BREAK]: { label: translatable("pokemon.attributes.rarity.values.rare_break", "Rare BREAK") },
				[PokemonRarity.RARE_HOLO]: { label: translatable("pokemon.attributes.rarity.values.rare_holo", "Rare Holo") },
				[PokemonRarity.RARE_HOLO_EX]: { label: translatable("pokemon.attributes.rarity.values.rare_holo_ex", "Rare Holo EX") },
				[PokemonRarity.RARE_HOLO_GX]: { label: translatable("pokemon.attributes.rarity.values.rare_holo_gx", "Rare Holo GX") },
				[PokemonRarity.RARE_HOLO_LV_X]: { label: translatable("pokemon.attributes.rarity.values.rare_holo_lv_x", "Rare Holo Lv.X") },
				[PokemonRarity.RARE_HOLO_STAR]: { label: translatable("pokemon.attributes.rarity.values.rare_holo_star", "Rare Holo Star") },
				[PokemonRarity.RARE_PRIME]: { label: translatable("pokemon.attributes.rarity.values.rare_prime", "Rare Prime") },
				[PokemonRarity.RARE_PRISM_STAR]: { label: translatable("pokemon.attributes.rarity.values.rare_prism_star", "Rare Prism Star") },
				[PokemonRarity.RARE_RAINBOW]: { label: translatable("pokemon.attributes.rarity.values.rare_rainbow", "Rare Rainbow") },
				[PokemonRarity.RARE_SHINING]: { label: translatable("pokemon.attributes.rarity.values.rare_shining", "Rare Shining") },
				[PokemonRarity.RARE_SHINY_GX]: { label: translatable("pokemon.attributes.rarity.values.rare_shiny_gx", "Rare Shiny GX") },
				[PokemonRarity.RGB_RARE]: { label: translatable("pokemon.attributes.rarity.values.rgb_rare", "RGB Rare") },
				[PokemonRarity.SECRET_RARE]: { label: translatable("pokemon.attributes.rarity.values.secret_rare", "Secret Rare") },
				[PokemonRarity.SHINY_RARE]: { label: translatable("pokemon.attributes.rarity.values.shiny_rare", "Shiny Rare") },
				[PokemonRarity.SHINY_RARE_V]: { label: translatable("pokemon.attributes.rarity.values.shiny_rare_v", "Shiny Rare V") },
				[PokemonRarity.SHINY_RARE_VMAX]: { label: translatable("pokemon.attributes.rarity.values.shiny_rare_vmax", "Shiny Rare VMax") },
				[PokemonRarity.SHINY_ULTRA_RARE]: { label: translatable("pokemon.attributes.rarity.values.shiny_ultra_rare", "Shiny Ultra Rare") },
				[PokemonRarity.SPECIAL_ILLUSTRATION_RARE]: { label: translatable("pokemon.attributes.rarity.values.special_illustration_rare", "Special Illustration Rare") },
				[PokemonRarity.THREE_DIAMOND]: { label: translatable("pokemon.attributes.rarity.values.three_diamond", "Three Diamond") },
				[PokemonRarity.THREE_STAR]: { label: translatable("pokemon.attributes.rarity.values.three_star", "Three Star") },
				[PokemonRarity.TRAINER_GALLERY_RARE_HOLO]: { label: translatable("pokemon.attributes.rarity.values.trainer_gallery_rare_holo", "Trainer Gallery Rare Holo") },
				[PokemonRarity.TWO_DIAMOND]: { label: translatable("pokemon.attributes.rarity.values.two_diamond", "Two Diamond") },
				[PokemonRarity.TWO_SHINY]: { label: translatable("pokemon.attributes.rarity.values.two_shiny", "Two Shiny") },
				[PokemonRarity.TWO_STAR]: { label: translatable("pokemon.attributes.rarity.values.two_star", "Two Star") },
				[PokemonRarity.ULTRA_RARE]: { label: translatable("pokemon.attributes.rarity.values.ultra_rare", "Ultra Rare") },
				[PokemonRarity.UNCOMMON]: { label: translatable("pokemon.attributes.rarity.values.uncommon", "Uncommon") }
			}
		}),
		printNumber: printNumberAttribute,
		hp: {
			entity: "card",
			name: translatable("pokemon.attributes.hp.name", "HP"),
			description: translatable("pokemon.attributes.hp.description", "Health points of the Pokemon"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		stage: enumAttribute(PokemonCardStage, {
			entity: "card",
			name: translatable("pokemon.attributes.stage.name", "Stage"),
			description: translatable("pokemon.attributes.stage.description", "Evolution stage of the Pokemon"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[PokemonCardStage.BABY]: { label: translatable("pokemon.attributes.stage.values.baby", "Baby") },
				[PokemonCardStage.BASIC]: { label: translatable("pokemon.attributes.stage.values.basic", "Basic") },
				[PokemonCardStage.BREAK]: { label: translatable("pokemon.attributes.stage.values.break", "BREAK") },
				[PokemonCardStage.GX]: { label: translatable("pokemon.attributes.stage.values.gx", "GX") },
				[PokemonCardStage.LEVEL_UP]: { label: translatable("pokemon.attributes.stage.values.level_up", "LEVEL-UP") },
				[PokemonCardStage.MEGA]: { label: translatable("pokemon.attributes.stage.values.mega", "MEGA") },
				[PokemonCardStage.RESTORED]: { label: translatable("pokemon.attributes.stage.values.restored", "RESTORED") },
				[PokemonCardStage.STAGE_1]: { label: translatable("pokemon.attributes.stage.values.stage_1", "Stage 1") },
				[PokemonCardStage.STAGE_2]: { label: translatable("pokemon.attributes.stage.values.stage_2", "Stage 2") },
				[PokemonCardStage.V]: { label: translatable("pokemon.attributes.stage.values.v", "V") },
				[PokemonCardStage.V_UNION]: { label: translatable("pokemon.attributes.stage.values.v_union", "V-UNION") },
				[PokemonCardStage.VMAX]: { label: translatable("pokemon.attributes.stage.values.vmax", "VMAX") },
				[PokemonCardStage.VSTAR]: { label: translatable("pokemon.attributes.stage.values.vstar", "VSTAR") }
			}
		}),
		retreatCost: {
			entity: "card",
			name: translatable("pokemon.attributes.retreatCost.name", "Retreat Cost"),
			description: translatable("pokemon.attributes.retreatCost.description", "Energy cost to retreat the Pokemon"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		level: {
			entity: "card",
			name: translatable("pokemon.attributes.level.name", "Level"),
			description: translatable("pokemon.attributes.level.description", "Level of the Pokemon"),
			type: "string",
			array: false
		},
		evolvesFrom: {
			entity: "card",
			name: translatable("pokemon.attributes.evolvesFrom.name", "Evolves From"),
			description: translatable("pokemon.attributes.evolvesFrom.description", "The Pokemon this card evolves from"),
			type: "string",
			array: false
		},
		cardText: {
			entity: "card",
			name: translatable("pokemon.attributes.cardText.name", "Card Text"),
			description: translatable("pokemon.attributes.cardText.description", "The abilities and attacks text of the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"variant",
			"rarity"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"cardType",
			"pokemonTypes",
			"stage",
			"hp",
			"retreatCost"
		]
	}],
	productDetailsOrder: {
		details: ["cardText"],
		statistics: [
			"rarity",
			"finish",
			"cardType",
			"pokemonTypes",
			"variant",
			"stage",
			"hp",
			"retreatCost",
			"evolvesFrom"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: pokemonListPresets,
	basicAttributes: ["pokemonTypes", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Cardmarket, SupportedMarketplace.Tcgplayer],
	vendorIds: [cardmarketVendorId, tcgplayerVendorId],
	csvTemplateExample: {
		vendorIds: {
			Cardmarket: "273927",
			TCGplayer: "42479"
		},
		printNumber: "004",
		cardName: "Charizard",
		expansion: "Base Set 2",
		finish: CardFinish.FOIL,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/pokemon-japan/pokemon-japan.types.ts
let PokemonJapanFinish = /* @__PURE__ */ function(PokemonJapanFinish) {
	PokemonJapanFinish["STANDARD"] = "Standard";
	PokemonJapanFinish["FOIL"] = "Foil";
	return PokemonJapanFinish;
}({});
let PokemonJapanRarity = /* @__PURE__ */ function(PokemonJapanRarity) {
	PokemonJapanRarity["ACE_RARE"] = "ACE_RARE";
	PokemonJapanRarity["AMAZING_RARE"] = "AMAZING_RARE";
	PokemonJapanRarity["ART_RARE"] = "ART_RARE";
	PokemonJapanRarity["BLACK_WHITE_RARE"] = "BLACK_WHITE_RARE";
	PokemonJapanRarity["CHARACTER_RARE"] = "CHARACTER_RARE";
	PokemonJapanRarity["CHARACTER_SUPER_RARE"] = "CHARACTER_SUPER_RARE";
	PokemonJapanRarity["COMMON"] = "COMMON";
	PokemonJapanRarity["COMMON_HOLO"] = "COMMON_HOLO";
	PokemonJapanRarity["DOUBLE_RARE"] = "DOUBLE_RARE";
	PokemonJapanRarity["HOLO_RARE"] = "HOLO_RARE";
	PokemonJapanRarity["HOLOFOIL"] = "HOLOFOIL";
	PokemonJapanRarity["HYPER_RARE"] = "HYPER_RARE";
	PokemonJapanRarity["KAGAYAKU"] = "KAGAYAKU";
	PokemonJapanRarity["MEGA_ATTACK_RARE"] = "MEGA_ATTACK_RARE";
	PokemonJapanRarity["MEGA_ULTRA_RARE"] = "MEGA_ULTRA_RARE";
	PokemonJapanRarity["NONE"] = "NONE";
	PokemonJapanRarity["NORMAL"] = "NORMAL";
	PokemonJapanRarity["PRISM_RARE"] = "PRISM_RARE";
	PokemonJapanRarity["PROMO"] = "PROMO";
	PokemonJapanRarity["RARE"] = "RARE";
	PokemonJapanRarity["RARE_HOLO_LEGEND"] = "RARE_HOLO_LEGEND";
	PokemonJapanRarity["RARE_HOLO_LV_X"] = "RARE_HOLO_LV_X";
	PokemonJapanRarity["SHINING"] = "SHINING";
	PokemonJapanRarity["SHINY_RARE"] = "SHINY_RARE";
	PokemonJapanRarity["SHINY_SECRET_RARE"] = "SHINY_SECRET_RARE";
	PokemonJapanRarity["SPECIAL_ART_RARE"] = "SPECIAL_ART_RARE";
	PokemonJapanRarity["SUPER_RARE"] = "SUPER_RARE";
	PokemonJapanRarity["SUPER_RARE_HOLO"] = "SUPER_RARE_HOLO";
	PokemonJapanRarity["TRAINER_RARE"] = "TRAINER_RARE";
	PokemonJapanRarity["TRIPLE_RARE"] = "TRIPLE_RARE";
	PokemonJapanRarity["ULTRA_RARE"] = "ULTRA_RARE";
	PokemonJapanRarity["ULTRA_RARE_COMMON"] = "ULTRA_RARE_COMMON";
	PokemonJapanRarity["ULTRA_RARE_UNCOMMON"] = "ULTRA_RARE_UNCOMMON";
	PokemonJapanRarity["UNCOMMON"] = "UNCOMMON";
	return PokemonJapanRarity;
}({});
let PokemonJapanCardType = /* @__PURE__ */ function(PokemonJapanCardType) {
	PokemonJapanCardType["POKEMON"] = "Pokemon";
	PokemonJapanCardType["TRAINER"] = "Trainer";
	PokemonJapanCardType["ENERGY"] = "Energy";
	return PokemonJapanCardType;
}({});
let PokemonJapanType = /* @__PURE__ */ function(PokemonJapanType) {
	PokemonJapanType["COLORLESS"] = "Colorless";
	PokemonJapanType["DARKNESS"] = "Darkness";
	PokemonJapanType["DRAGON"] = "Dragon";
	PokemonJapanType["FAIRY"] = "Fairy";
	PokemonJapanType["FIGHTING"] = "Fighting";
	PokemonJapanType["FIRE"] = "Fire";
	PokemonJapanType["GRASS"] = "Grass";
	PokemonJapanType["LIGHTNING"] = "Lightning";
	PokemonJapanType["METAL"] = "Metal";
	PokemonJapanType["PSYCHIC"] = "Psychic";
	PokemonJapanType["WATER"] = "Water";
	return PokemonJapanType;
}({});
let PokemonJapanStage = /* @__PURE__ */ function(PokemonJapanStage) {
	PokemonJapanStage["BABY"] = "Baby";
	PokemonJapanStage["BASIC"] = "Basic";
	PokemonJapanStage["BREAK"] = "BREAK";
	PokemonJapanStage["FOSSIL"] = "Fossil";
	PokemonJapanStage["LEGEND"] = "Legend";
	PokemonJapanStage["LV_X"] = "LV_X";
	PokemonJapanStage["MEGA_EX"] = "MegaEX";
	PokemonJapanStage["PRIMAL_EX"] = "PrimalEX";
	PokemonJapanStage["RESTORED"] = "Restored";
	PokemonJapanStage["STAGE_1"] = "Stage1";
	PokemonJapanStage["STAGE_2"] = "Stage2";
	PokemonJapanStage["V_UNION"] = "V-UNION";
	PokemonJapanStage["VMAX"] = "VMAX";
	PokemonJapanStage["VMAXG"] = "VMAXG";
	PokemonJapanStage["VSTAR"] = "VSTAR";
	return PokemonJapanStage;
}({});
let PokemonJapanVariant = /* @__PURE__ */ function(PokemonJapanVariant) {
	PokemonJapanVariant["FIRST_EDITION"] = "1st Edition";
	PokemonJapanVariant["UNLIMITED"] = "Unlimited";
	return PokemonJapanVariant;
}({});

//#endregion
//#region src/lib/games/pokemon-japan/pokemon-japan.presets.ts
const pokemonJapanListPresets = {
	Standard: {
		name: translatable("pokemon-japan.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("pokemon-japan.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [PokemonJapanFinish.STANDARD] },
			quantity: 1
		}]
	},
	Foil: {
		name: translatable("pokemon-japan.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("pokemon-japan.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [PokemonJapanFinish.FOIL] },
			quantity: 1
		}]
	},
	"Master Set": {
		name: translatable("pokemon-japan.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("pokemon-japan.presets.masterset.description", "A complete set with one copy of each card in standard and foil finishes"),
		matchers: [{
			filters: { finish: [PokemonJapanFinish.STANDARD] },
			quantity: 1
		}, {
			filters: { finish: [PokemonJapanFinish.FOIL] },
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/pokemon-japan/pokemon-japan.game.ts
const pokemonJapanFinishValues = {
	[PokemonJapanFinish.STANDARD]: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	[PokemonJapanFinish.FOIL]: { label: translatable("global.attributes.finish.values.foil", "Foil") }
};
const pokemonJapanFilters = z$1.object({
	cardType: makeZodEnumFilter(PokemonJapanCardType).optional(),
	expansion: zodRelationFilter.optional(),
	name: zodStringFilter.optional(),
	rarity: makeZodEnumFilter(PokemonJapanRarity).optional(),
	finish: makeZodEnumFilter(PokemonJapanFinish).optional(),
	finishes: makeZodEnumFilter(PokemonJapanFinish).optional(),
	variant: makeZodEnumFilter(PokemonJapanVariant).optional(),
	pokemonTypes: makeZodEnumFilter(PokemonJapanType).optional(),
	hp: zodNumberFilter.optional(),
	stage: makeZodEnumFilter(PokemonJapanStage).optional(),
	retreatCost: zodNumberFilter.optional()
});
const PokemonJapanGameConfiguration = {
	name: "Pokemon Japan",
	shortName: "Pokémon JP",
	slug: "pokemon-japan",
	icon: {
		dark: "pokemon-japan-icon.png",
		light: "pokemon-japan-icon.png"
	},
	image: {
		dark: "pokemon-japan.png",
		light: "pokemon-japan.png"
	},
	attributes: {
		name: nameAttribute,
		cardType: enumAttribute(PokemonJapanCardType, {
			entity: "card",
			name: translatable("pokemon-japan.attributes.cardType.name", "Card Type"),
			description: translatable("pokemon-japan.attributes.cardType.description", "Type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[PokemonJapanCardType.ENERGY]: { label: translatable("pokemon-japan.attributes.cardType.values.energy", "Energy") },
				[PokemonJapanCardType.POKEMON]: { label: translatable("pokemon-japan.attributes.cardType.values.pokemon", "Pokemon") },
				[PokemonJapanCardType.TRAINER]: { label: translatable("pokemon-japan.attributes.cardType.values.trainer", "Trainer") }
			}
		}),
		finishes: enumAttribute(PokemonJapanFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: true,
			description: translatable("global.attributes.finish.description", "Finish of the card (normal/holofoil)"),
			filter: FilterComponentType.CheckboxList,
			values: pokemonJapanFinishValues
		}),
		finish: enumAttribute(PokemonJapanFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: false,
			description: translatable("global.attributes.finish.description", "Finish of the card (normal/holofoil)"),
			filter: FilterComponentType.CheckboxList,
			values: pokemonJapanFinishValues
		}),
		variant: enumAttribute(PokemonJapanVariant, {
			entity: "variant",
			name: translatable("global.attributes.variant.name", "Variant"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			description: translatable("global.attributes.variant.description", "Variant of the card (1st edition, unlimited, etc.)"),
			values: {
				[PokemonJapanVariant.FIRST_EDITION]: { label: translatable("pokemon-japan.attributes.variant.values.first_edition", "1st Edition") },
				[PokemonJapanVariant.UNLIMITED]: { label: translatable("pokemon-japan.attributes.variant.values.unlimited", "Unlimited") }
			}
		}),
		pokemonTypes: enumAttribute(PokemonJapanType, {
			entity: "card",
			name: translatable("pokemon-japan.attributes.pokemonTypes.name", "Pokemon Type"),
			description: translatable("pokemon-japan.attributes.pokemonTypes.description", "Type of the Pokemon"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[PokemonJapanType.COLORLESS]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.colorless", "Colorless"),
					image: "/images/pokemon/colorless.png"
				},
				[PokemonJapanType.DARKNESS]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.darkness", "Darkness"),
					image: "/images/pokemon/dark.png"
				},
				[PokemonJapanType.DRAGON]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.dragon", "Dragon"),
					image: "/images/pokemon/dragon.png"
				},
				[PokemonJapanType.FAIRY]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.fairy", "Fairy"),
					image: "/images/pokemon/fairy.png"
				},
				[PokemonJapanType.FIGHTING]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.fighting", "Fighting"),
					image: "/images/pokemon/fighting.png"
				},
				[PokemonJapanType.FIRE]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.fire", "Fire"),
					image: "/images/pokemon/fire.png"
				},
				[PokemonJapanType.GRASS]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.grass", "Grass"),
					image: "/images/pokemon/grass.png"
				},
				[PokemonJapanType.LIGHTNING]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.lightning", "Lightning"),
					image: "/images/pokemon/electric.png"
				},
				[PokemonJapanType.METAL]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.metal", "Metal"),
					image: "/images/pokemon/steel.png"
				},
				[PokemonJapanType.PSYCHIC]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.psychic", "Psychic"),
					image: "/images/pokemon/psychic.png"
				},
				[PokemonJapanType.WATER]: {
					label: translatable("pokemon-japan.attributes.pokemonTypes.values.water", "Water"),
					image: "/images/pokemon/water.png"
				}
			}
		}),
		rarity: enumAttribute(PokemonJapanRarity, {
			entity: "variant",
			name: translatable("pokemon-japan.attributes.rarity.name", "Rarity"),
			description: translatable("pokemon-japan.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[PokemonJapanRarity.ACE_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.ace_rare", "ACE Rare") },
				[PokemonJapanRarity.AMAZING_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.amazing_rare", "Amazing Rare") },
				[PokemonJapanRarity.ART_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.art_rare", "Art Rare") },
				[PokemonJapanRarity.BLACK_WHITE_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.black_white_rare", "Black White Rare") },
				[PokemonJapanRarity.CHARACTER_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.character_rare", "Character Rare") },
				[PokemonJapanRarity.CHARACTER_SUPER_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.character_super_rare", "Character Super Rare") },
				[PokemonJapanRarity.COMMON]: { label: translatable("pokemon-japan.attributes.rarity.values.common", "Common") },
				[PokemonJapanRarity.COMMON_HOLO]: { label: translatable("pokemon-japan.attributes.rarity.values.common_holo", "Common Holo") },
				[PokemonJapanRarity.DOUBLE_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.double_rare", "Double Rare") },
				[PokemonJapanRarity.HOLO_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.holo_rare", "Holo Rare") },
				[PokemonJapanRarity.HOLOFOIL]: { label: translatable("pokemon-japan.attributes.rarity.values.holofoil", "Holofoil") },
				[PokemonJapanRarity.HYPER_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.hyper_rare", "Hyper Rare") },
				[PokemonJapanRarity.KAGAYAKU]: { label: translatable("pokemon-japan.attributes.rarity.values.kagayaku", "Kagayaku") },
				[PokemonJapanRarity.MEGA_ATTACK_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.mega_attack_rare", "Mega Attack Rare") },
				[PokemonJapanRarity.MEGA_ULTRA_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.mega_ultra_rare", "Mega Ultra Rare") },
				[PokemonJapanRarity.NONE]: { label: translatable("pokemon-japan.attributes.rarity.values.none", "None") },
				[PokemonJapanRarity.NORMAL]: { label: translatable("pokemon-japan.attributes.rarity.values.normal", "Normal") },
				[PokemonJapanRarity.PRISM_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.prism_rare", "Prism Rare") },
				[PokemonJapanRarity.PROMO]: { label: translatable("pokemon-japan.attributes.rarity.values.promo", "Promo") },
				[PokemonJapanRarity.RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.rare", "Rare") },
				[PokemonJapanRarity.RARE_HOLO_LEGEND]: { label: translatable("pokemon-japan.attributes.rarity.values.rare_holo_legend", "Rare Holo LEGEND") },
				[PokemonJapanRarity.RARE_HOLO_LV_X]: { label: translatable("pokemon-japan.attributes.rarity.values.rare_holo_lv_x", "Rare Holo LV.X") },
				[PokemonJapanRarity.SHINING]: { label: translatable("pokemon-japan.attributes.rarity.values.shining", "Shining") },
				[PokemonJapanRarity.SHINY_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.shiny_rare", "Shiny Rare") },
				[PokemonJapanRarity.SHINY_SECRET_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.shiny_secret_rare", "Shiny Secret Rare") },
				[PokemonJapanRarity.SPECIAL_ART_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.special_art_rare", "Special Art Rare") },
				[PokemonJapanRarity.SUPER_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.super_rare", "Super Rare") },
				[PokemonJapanRarity.SUPER_RARE_HOLO]: { label: translatable("pokemon-japan.attributes.rarity.values.super_rare_holo", "Super Rare Holo") },
				[PokemonJapanRarity.TRAINER_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.trainer_rare", "Trainer Rare") },
				[PokemonJapanRarity.TRIPLE_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.triple_rare", "Triple Rare") },
				[PokemonJapanRarity.ULTRA_RARE]: { label: translatable("pokemon-japan.attributes.rarity.values.ultra_rare", "Ultra Rare") },
				[PokemonJapanRarity.ULTRA_RARE_COMMON]: { label: translatable("pokemon-japan.attributes.rarity.values.ultra_rare_common", "Ultra-Rare Common") },
				[PokemonJapanRarity.ULTRA_RARE_UNCOMMON]: { label: translatable("pokemon-japan.attributes.rarity.values.ultra_rare_uncommon", "Ultra-Rare Uncommon") },
				[PokemonJapanRarity.UNCOMMON]: { label: translatable("pokemon-japan.attributes.rarity.values.uncommon", "Uncommon") }
			}
		}),
		printNumber: printNumberAttribute,
		hp: {
			entity: "card",
			name: translatable("pokemon-japan.attributes.hp.name", "HP"),
			description: translatable("pokemon-japan.attributes.hp.description", "Health points of the Pokemon"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		stage: enumAttribute(PokemonJapanStage, {
			entity: "card",
			name: translatable("pokemon-japan.attributes.stage.name", "Stage"),
			description: translatable("pokemon-japan.attributes.stage.description", "Evolution stage of the Pokemon"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[PokemonJapanStage.BABY]: { label: translatable("pokemon-japan.attributes.stage.values.baby", "Baby") },
				[PokemonJapanStage.BASIC]: { label: translatable("pokemon-japan.attributes.stage.values.basic", "Basic") },
				[PokemonJapanStage.BREAK]: { label: translatable("pokemon-japan.attributes.stage.values.break", "BREAK") },
				[PokemonJapanStage.FOSSIL]: { label: translatable("pokemon-japan.attributes.stage.values.fossil", "Fossil") },
				[PokemonJapanStage.LEGEND]: { label: translatable("pokemon-japan.attributes.stage.values.legend", "LEGEND") },
				[PokemonJapanStage.LV_X]: { label: translatable("pokemon-japan.attributes.stage.values.lv_x", "LV.X") },
				[PokemonJapanStage.MEGA_EX]: { label: translatable("pokemon-japan.attributes.stage.values.mega_ex", "Mega EX") },
				[PokemonJapanStage.PRIMAL_EX]: { label: translatable("pokemon-japan.attributes.stage.values.primal_ex", "Primal EX") },
				[PokemonJapanStage.RESTORED]: { label: translatable("pokemon-japan.attributes.stage.values.restored", "Restored") },
				[PokemonJapanStage.STAGE_1]: { label: translatable("pokemon-japan.attributes.stage.values.stage_1", "Stage 1") },
				[PokemonJapanStage.STAGE_2]: { label: translatable("pokemon-japan.attributes.stage.values.stage_2", "Stage 2") },
				[PokemonJapanStage.V_UNION]: { label: translatable("pokemon-japan.attributes.stage.values.v_union", "V-UNION") },
				[PokemonJapanStage.VMAX]: { label: translatable("pokemon-japan.attributes.stage.values.vmax", "VMAX") },
				[PokemonJapanStage.VMAXG]: { label: translatable("pokemon-japan.attributes.stage.values.vmaxg", "VMAX Gigantamax") },
				[PokemonJapanStage.VSTAR]: { label: translatable("pokemon-japan.attributes.stage.values.vstar", "VSTAR") }
			}
		}),
		retreatCost: {
			entity: "card",
			name: translatable("pokemon-japan.attributes.retreatCost.name", "Retreat Cost"),
			description: translatable("pokemon-japan.attributes.retreatCost.description", "Energy cost to retreat the Pokemon"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"variant",
			"rarity"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"cardType",
			"pokemonTypes",
			"stage",
			"hp",
			"retreatCost"
		]
	}],
	productDetailsOrder: {
		details: [],
		statistics: [
			"rarity",
			"finish",
			"cardType",
			"pokemonTypes",
			"variant",
			"stage",
			"hp",
			"retreatCost"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: pokemonJapanListPresets,
	basicAttributes: ["pokemonTypes", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Tcgplayer],
	vendorIds: [cardmarketVendorId, tcgplayerVendorId],
	csvTemplateExample: {
		vendorIds: { TCGplayer: "595672" },
		printNumber: "127",
		cardName: "Pikachu",
		expansion: "SI: Start Deck 100",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "Japanese",
		quantity: 1
	},
	defaultLanguages: [Languages.Japanese]
};

//#endregion
//#region src/lib/games/riftbound/riftbound.types.ts
let RiftboundRarity = /* @__PURE__ */ function(RiftboundRarity) {
	RiftboundRarity["Common"] = "Common";
	RiftboundRarity["Uncommon"] = "Uncommon";
	RiftboundRarity["Rare"] = "Rare";
	RiftboundRarity["Epic"] = "Epic";
	RiftboundRarity["Showcase"] = "Showcase";
	return RiftboundRarity;
}({});
let RiftboundCardFinish = /* @__PURE__ */ function(RiftboundCardFinish) {
	RiftboundCardFinish["Standard"] = "Standard";
	RiftboundCardFinish["Foil"] = "Foil";
	return RiftboundCardFinish;
}({});
let RiftboundCardType = /* @__PURE__ */ function(RiftboundCardType) {
	RiftboundCardType["Unit"] = "Unit";
	RiftboundCardType["Spell"] = "Spell";
	RiftboundCardType["Gear"] = "Gear";
	RiftboundCardType["Rune"] = "Rune";
	RiftboundCardType["Battlefield"] = "Battlefield";
	RiftboundCardType["Legend"] = "Legend";
	RiftboundCardType["Card"] = "Card";
	return RiftboundCardType;
}({});
let RiftboundSuperType = /* @__PURE__ */ function(RiftboundSuperType) {
	RiftboundSuperType["Basic"] = "Basic";
	RiftboundSuperType["Champion"] = "Champion";
	RiftboundSuperType["Signature"] = "Signature";
	RiftboundSuperType["Token"] = "Token";
	RiftboundSuperType["Unit"] = "Unit";
	return RiftboundSuperType;
}({});
let RiftboundDomain = /* @__PURE__ */ function(RiftboundDomain) {
	RiftboundDomain["Fury"] = "Fury";
	RiftboundDomain["Calm"] = "Calm";
	RiftboundDomain["Chaos"] = "Chaos";
	RiftboundDomain["Mind"] = "Mind";
	RiftboundDomain["Body"] = "Body";
	RiftboundDomain["Order"] = "Order";
	RiftboundDomain["Neutral"] = "Neutral";
	return RiftboundDomain;
}({});
let RiftboundTag = /* @__PURE__ */ function(RiftboundTag) {
	RiftboundTag["Ahri"] = "Ahri";
	RiftboundTag["Akali"] = "Akali";
	RiftboundTag["Akshan"] = "Akshan";
	RiftboundTag["Ambessa"] = "Ambessa";
	RiftboundTag["Anivia"] = "Anivia";
	RiftboundTag["Annie"] = "Annie";
	RiftboundTag["Aphelios"] = "Aphelios";
	RiftboundTag["Azir"] = "Azir";
	RiftboundTag["Bard"] = "Bard";
	RiftboundTag["Blitzcrank"] = "Blitzcrank";
	RiftboundTag["Caitlyn"] = "Caitlyn";
	RiftboundTag["Darius"] = "Darius";
	RiftboundTag["Diana"] = "Diana";
	RiftboundTag["DrMundo"] = "Dr. Mundo";
	RiftboundTag["Draven"] = "Draven";
	RiftboundTag["Ekko"] = "Ekko";
	RiftboundTag["Ezreal"] = "Ezreal";
	RiftboundTag["Fiora"] = "Fiora";
	RiftboundTag["Fizz"] = "Fizz";
	RiftboundTag["Gangplank"] = "Gangplank";
	RiftboundTag["Garen"] = "Garen";
	RiftboundTag["Heimerdinger"] = "Heimerdinger";
	RiftboundTag["Illaoi"] = "Illaoi";
	RiftboundTag["Irelia"] = "Irelia";
	RiftboundTag["Janna"] = "Janna";
	RiftboundTag["Jax"] = "Jax";
	RiftboundTag["Jayce"] = "Jayce";
	RiftboundTag["Jinx"] = "Jinx";
	RiftboundTag["KaiSa"] = "Kai'Sa";
	RiftboundTag["Karma"] = "Karma";
	RiftboundTag["Karthus"] = "Karthus";
	RiftboundTag["Kayle"] = "Kayle";
	RiftboundTag["Kayn"] = "Kayn";
	RiftboundTag["Kennen"] = "Kennen";
	RiftboundTag["KhaZix"] = "Kha'Zix";
	RiftboundTag["KogMaw"] = "Kog'Maw";
	RiftboundTag["LeeSin"] = "Lee Sin";
	RiftboundTag["Leona"] = "Leona";
	RiftboundTag["Lucian"] = "Lucian";
	RiftboundTag["Lux"] = "Lux";
	RiftboundTag["Malzahar"] = "Malzahar";
	RiftboundTag["MasterYi"] = "Master Yi";
	RiftboundTag["Mel"] = "Mel";
	RiftboundTag["MissFortune"] = "Miss Fortune";
	RiftboundTag["Morgana"] = "Morgana";
	RiftboundTag["Nasus"] = "Nasus";
	RiftboundTag["Nocturne"] = "Nocturne";
	RiftboundTag["Ornn"] = "Ornn";
	RiftboundTag["Qiyana"] = "Qiyana";
	RiftboundTag["RekSai"] = "Rek'Sai";
	RiftboundTag["RenataGlasc"] = "Renata Glasc";
	RiftboundTag["Renekton"] = "Renekton";
	RiftboundTag["Rengar"] = "Rengar";
	RiftboundTag["Riven"] = "Riven";
	RiftboundTag["Rumble"] = "Rumble";
	RiftboundTag["Sett"] = "Sett";
	RiftboundTag["Shen"] = "Shen";
	RiftboundTag["Sivir"] = "Sivir";
	RiftboundTag["Sona"] = "Sona";
	RiftboundTag["Soraka"] = "Soraka";
	RiftboundTag["Swain"] = "Swain";
	RiftboundTag["Taric"] = "Taric";
	RiftboundTag["Teemo"] = "Teemo";
	RiftboundTag["Tryndamere"] = "Tryndamere";
	RiftboundTag["TwistedFate"] = "Twisted Fate";
	RiftboundTag["Udyr"] = "Udyr";
	RiftboundTag["Vayne"] = "Vayne";
	RiftboundTag["Vex"] = "Vex";
	RiftboundTag["Vi"] = "Vi";
	RiftboundTag["Viktor"] = "Viktor";
	RiftboundTag["Volibear"] = "Volibear";
	RiftboundTag["Warwick"] = "Warwick";
	RiftboundTag["XinZhao"] = "Xin Zhao";
	RiftboundTag["Yasuo"] = "Yasuo";
	RiftboundTag["Yone"] = "Yone";
	RiftboundTag["Zed"] = "Zed";
	RiftboundTag["BandleCity"] = "Bandle City";
	RiftboundTag["Bilgewater"] = "Bilgewater";
	RiftboundTag["Demacia"] = "Demacia";
	RiftboundTag["Freljord"] = "Freljord";
	RiftboundTag["Icathia"] = "Icathia";
	RiftboundTag["Ionia"] = "Ionia";
	RiftboundTag["Ixtal"] = "Ixtal";
	RiftboundTag["MountTargon"] = "Mount Targon";
	RiftboundTag["Noxus"] = "Noxus";
	RiftboundTag["Piltover"] = "Piltover";
	RiftboundTag["ShadowIsles"] = "Shadow Isles";
	RiftboundTag["Shurima"] = "Shurima";
	RiftboundTag["TheVoid"] = "The Void";
	RiftboundTag["Zaun"] = "Zaun";
	RiftboundTag["Bird"] = "Bird";
	RiftboundTag["Cat"] = "Cat";
	RiftboundTag["Demon"] = "Demon";
	RiftboundTag["Dog"] = "Dog";
	RiftboundTag["Dragon"] = "Dragon";
	RiftboundTag["Elite"] = "Elite";
	RiftboundTag["Equipment"] = "Equipment";
	RiftboundTag["Fae"] = "Fae";
	RiftboundTag["Mech"] = "Mech";
	RiftboundTag["Mystic"] = "Mystic";
	RiftboundTag["Ninja"] = "Ninja";
	RiftboundTag["Pirate"] = "Pirate";
	RiftboundTag["Poro"] = "Poro";
	RiftboundTag["Recruit"] = "Recruit";
	RiftboundTag["Sentinel"] = "Sentinel";
	RiftboundTag["Soldier"] = "Soldier";
	RiftboundTag["Spider"] = "Spider";
	RiftboundTag["Spirit"] = "Spirit";
	RiftboundTag["Trifarian"] = "Trifarian";
	RiftboundTag["Vastaya"] = "Vastaya";
	RiftboundTag["Warrior"] = "Warrior";
	RiftboundTag["Wizard"] = "Wizard";
	RiftboundTag["Yordle"] = "Yordle";
	return RiftboundTag;
}({});
let RiftboundOrientation = /* @__PURE__ */ function(RiftboundOrientation) {
	RiftboundOrientation["Portrait"] = "portrait";
	RiftboundOrientation["Landscape"] = "landscape";
	return RiftboundOrientation;
}({});

//#endregion
//#region src/lib/games/riftbound/riftbound.presets.ts
const riftboundListPresets = {
	Standard: {
		name: translatable("riftbound.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("riftbound.presets.standard.description", "A complete set with one copy of each unique card - standard finish where available, foil otherwise"),
		matchers: [
			{
				filters: { finish: [RiftboundCardFinish.Standard] },
				quantity: 1
			},
			(
			/** Some cards are only available in foil */
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					rarity: [RiftboundRarity.Epic]
				},
				quantity: 1
			}),
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					rarity: [RiftboundRarity.Rare]
				},
				quantity: 1
			},
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					rarity: [RiftboundRarity.Showcase]
				},
				quantity: 1
			}
		]
	},
	Playset: {
		name: translatable("riftbound.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("riftbound.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [
			(
			/** Non-Legend cards in Standard finish: 3 copies each */
			{
				filters: {
					finish: [RiftboundCardFinish.Standard],
					cardType: [
						RiftboundCardType.Unit,
						RiftboundCardType.Spell,
						RiftboundCardType.Gear,
						RiftboundCardType.Card,
						RiftboundCardType.Rune
					]
				},
				quantity: 3
			}),
			(
			/** Non-Legend Epic cards in Foil: 3 copies each (Epic only exists in Foil) */
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					rarity: [RiftboundRarity.Epic],
					cardType: [
						RiftboundCardType.Unit,
						RiftboundCardType.Spell,
						RiftboundCardType.Gear,
						RiftboundCardType.Card,
						RiftboundCardType.Rune
					]
				},
				quantity: 3
			}),
			(
			/** Non-Legend Rare cards in Foil: 3 copies each (some Rare only exist in Foil) */
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					rarity: [RiftboundRarity.Rare],
					cardType: [
						RiftboundCardType.Unit,
						RiftboundCardType.Spell,
						RiftboundCardType.Gear,
						RiftboundCardType.Card,
						RiftboundCardType.Rune
					]
				},
				quantity: 3
			}),
			(
			/** Non-Legend Showcase cards in Foil: 3 copies each (some Showcase only exist in Foil) */
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					rarity: [RiftboundRarity.Showcase],
					cardType: [
						RiftboundCardType.Unit,
						RiftboundCardType.Spell,
						RiftboundCardType.Gear,
						RiftboundCardType.Card,
						RiftboundCardType.Rune
					]
				},
				quantity: 3
			}),
			(
			/** Legend cards: 1 copy each (all Legends only exist in Foil) */
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					cardType: [RiftboundCardType.Legend]
				},
				quantity: 1
			}),
			(
			/** Battlefield cards: 1 copy each (all Battlefields */
			{
				filters: { cardType: [RiftboundCardType.Battlefield] },
				quantity: 1
			})
		]
	},
	Foil: {
		name: translatable("riftbound.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("riftbound.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [RiftboundCardFinish.Foil] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("riftbound.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("riftbound.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					cardType: [
						RiftboundCardType.Unit,
						RiftboundCardType.Spell,
						RiftboundCardType.Gear,
						RiftboundCardType.Card,
						RiftboundCardType.Rune,
						RiftboundCardType.Battlefield
					]
				},
				quantity: 3
			},
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					cardType: [RiftboundCardType.Legend]
				},
				quantity: 1
			},
			{
				filters: {
					finish: [RiftboundCardFinish.Foil],
					cardType: [RiftboundCardType.Battlefield]
				},
				quantity: 1
			}
		]
	},
	"Master Set": {
		name: translatable("riftbound.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("riftbound.presets.masterset.description", "A complete set with one copy of each card in all finishes and variants"),
		matchers: [{
			filters: {},
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/riftbound/riftbound.game.ts
/**
* Shared finish values for Riftbound - used by both finishes and finish attributes
*/
const riftboundFinishValues = {
	[RiftboundCardFinish.Standard]: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	[RiftboundCardFinish.Foil]: { label: translatable("global.attributes.finish.values.foil", "Foil") }
};
const riftboundFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(RiftboundRarity).optional(),
	finish: makeZodEnumFilter(RiftboundCardFinish).optional(),
	finishes: makeZodEnumFilter(RiftboundCardFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	cardType: makeZodEnumFilter(RiftboundCardType).optional(),
	superType: makeZodEnumFilter(RiftboundSuperType).optional(),
	domains: makeZodEnumFilter(RiftboundDomain).optional(),
	cardTags: makeZodEnumFilter(RiftboundTag).optional(),
	energyCost: zodNumericFilter.optional(),
	mightCost: zodNumericFilter.optional()
});
const RiftboundGameConfiguration = {
	name: "Riftbound",
	shortName: "Riftbound",
	slug: "riftbound",
	icon: {
		dark: "riftbound-icon.png",
		light: "riftbound-icon.png"
	},
	image: {
		dark: "riftbound.png",
		light: "riftbound.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(RiftboundCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: true,
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			filter: FilterComponentType.CheckboxList,
			values: riftboundFinishValues
		}),
		finish: enumAttribute(RiftboundCardFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: false,
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			filter: FilterComponentType.CheckboxList,
			values: riftboundFinishValues
		}),
		rarity: enumAttribute(RiftboundRarity, {
			entity: "variant",
			name: translatable("riftbound.attributes.rarity.name", "Rarity"),
			description: translatable("riftbound.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[RiftboundRarity.Common]: {
					label: translatable("riftbound.attributes.rarity.values.common", "Common"),
					image: "/images/riftbound/rarities/common.svg"
				},
				[RiftboundRarity.Uncommon]: {
					label: translatable("riftbound.attributes.rarity.values.uncommon", "Uncommon"),
					image: "/images/riftbound/rarities/uncommon.svg"
				},
				[RiftboundRarity.Rare]: {
					label: translatable("riftbound.attributes.rarity.values.rare", "Rare"),
					image: "/images/riftbound/rarities/rare.svg"
				},
				[RiftboundRarity.Epic]: {
					label: translatable("riftbound.attributes.rarity.values.epic", "Epic"),
					image: "/images/riftbound/rarities/epic.svg"
				},
				[RiftboundRarity.Showcase]: {
					label: translatable("riftbound.attributes.rarity.values.showcase", "Showcase"),
					image: "/images/riftbound/rarities/showcase.svg"
				}
			}
		}),
		printNumber: printNumberAttribute,
		riotId: {
			entity: "variant",
			name: translatable("riftbound.attributes.riotId.name", "Riot ID"),
			description: translatable("riftbound.attributes.riotId.description", "The unique Riot identifier for the card"),
			type: "string",
			array: false
		},
		cardType: enumAttribute(RiftboundCardType, {
			entity: "card",
			name: translatable("riftbound.attributes.cardType.name", "Card Type"),
			description: translatable("riftbound.attributes.cardType.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[RiftboundCardType.Unit]: {
					label: translatable("riftbound.attributes.cardType.values.unit", "Unit"),
					image: "/images/riftbound/types/unit.webp"
				},
				[RiftboundCardType.Spell]: {
					label: translatable("riftbound.attributes.cardType.values.spell", "Spell"),
					image: "/images/riftbound/types/spell.webp"
				},
				[RiftboundCardType.Gear]: {
					label: translatable("riftbound.attributes.cardType.values.gear", "Gear"),
					image: "/images/riftbound/types/gear.webp"
				},
				[RiftboundCardType.Rune]: {
					label: translatable("riftbound.attributes.cardType.values.rune", "Rune"),
					image: "/images/riftbound/types/rune.webp"
				},
				[RiftboundCardType.Battlefield]: {
					label: translatable("riftbound.attributes.cardType.values.battlefield", "Battlefield"),
					image: "/images/riftbound/types/battlefield.webp"
				},
				[RiftboundCardType.Legend]: {
					label: translatable("riftbound.attributes.cardType.values.legend", "Legend"),
					image: "/images/riftbound/types/legend.webp"
				},
				[RiftboundCardType.Card]: {
					label: translatable("riftbound.attributes.cardType.values.card", "Card"),
					image: "/images/riftbound/types/card.webp"
				}
			}
		}),
		superType: enumAttribute(RiftboundSuperType, {
			entity: "card",
			name: translatable("riftbound.attributes.superType.name", "Super Type"),
			description: translatable("riftbound.attributes.superType.description", "The super type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[RiftboundSuperType.Basic]: { label: translatable("riftbound.attributes.superType.values.basic", "Basic") },
				[RiftboundSuperType.Champion]: {
					label: translatable("riftbound.attributes.superType.values.champion", "Champion"),
					image: "/images/riftbound/super-types/champion.webp"
				},
				[RiftboundSuperType.Signature]: {
					label: translatable("riftbound.attributes.superType.values.signature", "Signature"),
					image: "/images/riftbound/super-types/signature.webp"
				},
				[RiftboundSuperType.Token]: {
					label: translatable("riftbound.attributes.superType.values.token", "Token"),
					image: "/images/riftbound/super-types/token.webp"
				},
				[RiftboundSuperType.Unit]: {
					label: translatable("riftbound.attributes.superType.values.unit", "Unit"),
					image: "/images/riftbound/super-types/unit.webp"
				}
			}
		}),
		domains: enumAttribute(RiftboundDomain, {
			entity: "card",
			name: translatable("riftbound.attributes.domains.name", "Domains"),
			description: translatable("riftbound.attributes.domains.description", "The domains of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[RiftboundDomain.Fury]: {
					label: translatable("riftbound.attributes.domains.values.fury", "Fury"),
					image: "/images/riftbound/domains/fury.webp"
				},
				[RiftboundDomain.Calm]: {
					label: translatable("riftbound.attributes.domains.values.calm", "Calm"),
					image: "/images/riftbound/domains/calm.webp"
				},
				[RiftboundDomain.Chaos]: {
					label: translatable("riftbound.attributes.domains.values.chaos", "Chaos"),
					image: "/images/riftbound/domains/chaos.webp"
				},
				[RiftboundDomain.Mind]: {
					label: translatable("riftbound.attributes.domains.values.mind", "Mind"),
					image: "/images/riftbound/domains/mind.webp"
				},
				[RiftboundDomain.Body]: {
					label: translatable("riftbound.attributes.domains.values.body", "Body"),
					image: "/images/riftbound/domains/body.webp"
				},
				[RiftboundDomain.Order]: {
					label: translatable("riftbound.attributes.domains.values.order", "Order"),
					image: "/images/riftbound/domains/order.webp"
				},
				[RiftboundDomain.Neutral]: {
					label: translatable("riftbound.attributes.domains.values.neutral", "Neutral"),
					image: "/images/riftbound/domains/neutral.webp"
				}
			}
		}),
		cardTags: enumAttribute(RiftboundTag, {
			entity: "card",
			name: translatable("riftbound.attributes.cardTags.name", "Tags"),
			description: translatable("riftbound.attributes.cardTags.description", "Tags and champions associated with the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[RiftboundTag.Ahri]: { label: translatable("riftbound.attributes.cardTags.values.ahri", "Ahri") },
				[RiftboundTag.Akali]: { label: translatable("riftbound.attributes.cardTags.values.akali", "Akali") },
				[RiftboundTag.Akshan]: { label: translatable("riftbound.attributes.cardTags.values.akshan", "Akshan") },
				[RiftboundTag.Ambessa]: { label: translatable("riftbound.attributes.cardTags.values.ambessa", "Ambessa") },
				[RiftboundTag.Anivia]: { label: translatable("riftbound.attributes.cardTags.values.anivia", "Anivia") },
				[RiftboundTag.Annie]: { label: translatable("riftbound.attributes.cardTags.values.annie", "Annie") },
				[RiftboundTag.Aphelios]: { label: translatable("riftbound.attributes.cardTags.values.aphelios", "Aphelios") },
				[RiftboundTag.Azir]: { label: translatable("riftbound.attributes.cardTags.values.azir", "Azir") },
				[RiftboundTag.Bard]: { label: translatable("riftbound.attributes.cardTags.values.bard", "Bard") },
				[RiftboundTag.Blitzcrank]: { label: translatable("riftbound.attributes.cardTags.values.blitzcrank", "Blitzcrank") },
				[RiftboundTag.Caitlyn]: { label: translatable("riftbound.attributes.cardTags.values.caitlyn", "Caitlyn") },
				[RiftboundTag.Darius]: { label: translatable("riftbound.attributes.cardTags.values.darius", "Darius") },
				[RiftboundTag.Diana]: { label: translatable("riftbound.attributes.cardTags.values.diana", "Diana") },
				[RiftboundTag.DrMundo]: { label: translatable("riftbound.attributes.cardTags.values.drmundo", "Dr. Mundo") },
				[RiftboundTag.Draven]: { label: translatable("riftbound.attributes.cardTags.values.draven", "Draven") },
				[RiftboundTag.Ekko]: { label: translatable("riftbound.attributes.cardTags.values.ekko", "Ekko") },
				[RiftboundTag.Ezreal]: { label: translatable("riftbound.attributes.cardTags.values.ezreal", "Ezreal") },
				[RiftboundTag.Fiora]: { label: translatable("riftbound.attributes.cardTags.values.fiora", "Fiora") },
				[RiftboundTag.Fizz]: { label: translatable("riftbound.attributes.cardTags.values.fizz", "Fizz") },
				[RiftboundTag.Gangplank]: { label: translatable("riftbound.attributes.cardTags.values.gangplank", "Gangplank") },
				[RiftboundTag.Garen]: { label: translatable("riftbound.attributes.cardTags.values.garen", "Garen") },
				[RiftboundTag.Heimerdinger]: { label: translatable("riftbound.attributes.cardTags.values.heimerdinger", "Heimerdinger") },
				[RiftboundTag.Illaoi]: { label: translatable("riftbound.attributes.cardTags.values.illaoi", "Illaoi") },
				[RiftboundTag.Irelia]: { label: translatable("riftbound.attributes.cardTags.values.irelia", "Irelia") },
				[RiftboundTag.Janna]: { label: translatable("riftbound.attributes.cardTags.values.janna", "Janna") },
				[RiftboundTag.Jax]: { label: translatable("riftbound.attributes.cardTags.values.jax", "Jax") },
				[RiftboundTag.Jayce]: { label: translatable("riftbound.attributes.cardTags.values.jayce", "Jayce") },
				[RiftboundTag.Jinx]: { label: translatable("riftbound.attributes.cardTags.values.jinx", "Jinx") },
				[RiftboundTag.KaiSa]: { label: translatable("riftbound.attributes.cardTags.values.kaisa", "Kai'Sa") },
				[RiftboundTag.Karma]: { label: translatable("riftbound.attributes.cardTags.values.karma", "Karma") },
				[RiftboundTag.Karthus]: { label: translatable("riftbound.attributes.cardTags.values.karthus", "Karthus") },
				[RiftboundTag.Kayle]: { label: translatable("riftbound.attributes.cardTags.values.kayle", "Kayle") },
				[RiftboundTag.Kayn]: { label: translatable("riftbound.attributes.cardTags.values.kayn", "Kayn") },
				[RiftboundTag.Kennen]: { label: translatable("riftbound.attributes.cardTags.values.kennen", "Kennen") },
				[RiftboundTag.KhaZix]: { label: translatable("riftbound.attributes.cardTags.values.khazix", "Kha'Zix") },
				[RiftboundTag.KogMaw]: { label: translatable("riftbound.attributes.cardTags.values.kogmaw", "Kog'Maw") },
				[RiftboundTag.LeeSin]: { label: translatable("riftbound.attributes.cardTags.values.leesin", "Lee Sin") },
				[RiftboundTag.Leona]: { label: translatable("riftbound.attributes.cardTags.values.leona", "Leona") },
				[RiftboundTag.Lucian]: { label: translatable("riftbound.attributes.cardTags.values.lucian", "Lucian") },
				[RiftboundTag.Lux]: { label: translatable("riftbound.attributes.cardTags.values.lux", "Lux") },
				[RiftboundTag.Malzahar]: { label: translatable("riftbound.attributes.cardTags.values.malzahar", "Malzahar") },
				[RiftboundTag.MasterYi]: { label: translatable("riftbound.attributes.cardTags.values.masteryi", "Master Yi") },
				[RiftboundTag.Mel]: { label: translatable("riftbound.attributes.cardTags.values.mel", "Mel") },
				[RiftboundTag.MissFortune]: { label: translatable("riftbound.attributes.cardTags.values.missfortune", "Miss Fortune") },
				[RiftboundTag.Morgana]: { label: translatable("riftbound.attributes.cardTags.values.morgana", "Morgana") },
				[RiftboundTag.Nasus]: { label: translatable("riftbound.attributes.cardTags.values.nasus", "Nasus") },
				[RiftboundTag.Nocturne]: { label: translatable("riftbound.attributes.cardTags.values.nocturne", "Nocturne") },
				[RiftboundTag.Ornn]: { label: translatable("riftbound.attributes.cardTags.values.ornn", "Ornn") },
				[RiftboundTag.Qiyana]: { label: translatable("riftbound.attributes.cardTags.values.qiyana", "Qiyana") },
				[RiftboundTag.RekSai]: { label: translatable("riftbound.attributes.cardTags.values.reksai", "Rek'Sai") },
				[RiftboundTag.RenataGlasc]: { label: translatable("riftbound.attributes.cardTags.values.renataGlasc", "Renata Glasc") },
				[RiftboundTag.Renekton]: { label: translatable("riftbound.attributes.cardTags.values.renekton", "Renekton") },
				[RiftboundTag.Rengar]: { label: translatable("riftbound.attributes.cardTags.values.rengar", "Rengar") },
				[RiftboundTag.Riven]: { label: translatable("riftbound.attributes.cardTags.values.riven", "Riven") },
				[RiftboundTag.Rumble]: { label: translatable("riftbound.attributes.cardTags.values.rumble", "Rumble") },
				[RiftboundTag.Sett]: { label: translatable("riftbound.attributes.cardTags.values.sett", "Sett") },
				[RiftboundTag.Shen]: { label: translatable("riftbound.attributes.cardTags.values.shen", "Shen") },
				[RiftboundTag.Sivir]: { label: translatable("riftbound.attributes.cardTags.values.sivir", "Sivir") },
				[RiftboundTag.Sona]: { label: translatable("riftbound.attributes.cardTags.values.sona", "Sona") },
				[RiftboundTag.Soraka]: { label: translatable("riftbound.attributes.cardTags.values.soraka", "Soraka") },
				[RiftboundTag.Swain]: { label: translatable("riftbound.attributes.cardTags.values.swain", "Swain") },
				[RiftboundTag.Taric]: { label: translatable("riftbound.attributes.cardTags.values.taric", "Taric") },
				[RiftboundTag.Teemo]: { label: translatable("riftbound.attributes.cardTags.values.teemo", "Teemo") },
				[RiftboundTag.Tryndamere]: { label: translatable("riftbound.attributes.cardTags.values.tryndamere", "Tryndamere") },
				[RiftboundTag.TwistedFate]: { label: translatable("riftbound.attributes.cardTags.values.twistedfate", "Twisted Fate") },
				[RiftboundTag.Udyr]: { label: translatable("riftbound.attributes.cardTags.values.udyr", "Udyr") },
				[RiftboundTag.Vayne]: { label: translatable("riftbound.attributes.cardTags.values.vayne", "Vayne") },
				[RiftboundTag.Vex]: { label: translatable("riftbound.attributes.cardTags.values.vex", "Vex") },
				[RiftboundTag.Vi]: { label: translatable("riftbound.attributes.cardTags.values.vi", "Vi") },
				[RiftboundTag.Viktor]: { label: translatable("riftbound.attributes.cardTags.values.viktor", "Viktor") },
				[RiftboundTag.Volibear]: { label: translatable("riftbound.attributes.cardTags.values.volibear", "Volibear") },
				[RiftboundTag.Warwick]: { label: translatable("riftbound.attributes.cardTags.values.warwick", "Warwick") },
				[RiftboundTag.XinZhao]: { label: translatable("riftbound.attributes.cardTags.values.xinzhao", "Xin Zhao") },
				[RiftboundTag.Yasuo]: { label: translatable("riftbound.attributes.cardTags.values.yasuo", "Yasuo") },
				[RiftboundTag.Yone]: { label: translatable("riftbound.attributes.cardTags.values.yone", "Yone") },
				[RiftboundTag.Zed]: { label: translatable("riftbound.attributes.cardTags.values.zed", "Zed") },
				[RiftboundTag.BandleCity]: { label: translatable("riftbound.attributes.cardTags.values.bandlecity", "Bandle City") },
				[RiftboundTag.Bilgewater]: { label: translatable("riftbound.attributes.cardTags.values.bilgewater", "Bilgewater") },
				[RiftboundTag.Demacia]: { label: translatable("riftbound.attributes.cardTags.values.demacia", "Demacia") },
				[RiftboundTag.Freljord]: { label: translatable("riftbound.attributes.cardTags.values.freljord", "Freljord") },
				[RiftboundTag.Icathia]: { label: translatable("riftbound.attributes.cardTags.values.icathia", "Icathia") },
				[RiftboundTag.Ionia]: { label: translatable("riftbound.attributes.cardTags.values.ionia", "Ionia") },
				[RiftboundTag.Ixtal]: { label: translatable("riftbound.attributes.cardTags.values.ixtal", "Ixtal") },
				[RiftboundTag.MountTargon]: { label: translatable("riftbound.attributes.cardTags.values.mounttargon", "Mount Targon") },
				[RiftboundTag.Noxus]: { label: translatable("riftbound.attributes.cardTags.values.noxus", "Noxus") },
				[RiftboundTag.Piltover]: { label: translatable("riftbound.attributes.cardTags.values.piltover", "Piltover") },
				[RiftboundTag.ShadowIsles]: { label: translatable("riftbound.attributes.cardTags.values.shadowisles", "Shadow Isles") },
				[RiftboundTag.Shurima]: { label: translatable("riftbound.attributes.cardTags.values.shurima", "Shurima") },
				[RiftboundTag.TheVoid]: { label: translatable("riftbound.attributes.cardTags.values.thevoid", "The Void") },
				[RiftboundTag.Zaun]: { label: translatable("riftbound.attributes.cardTags.values.zaun", "Zaun") },
				[RiftboundTag.Bird]: { label: translatable("riftbound.attributes.cardTags.values.bird", "Bird") },
				[RiftboundTag.Cat]: { label: translatable("riftbound.attributes.cardTags.values.cat", "Cat") },
				[RiftboundTag.Demon]: { label: translatable("riftbound.attributes.cardTags.values.demon", "Demon") },
				[RiftboundTag.Dog]: { label: translatable("riftbound.attributes.cardTags.values.dog", "Dog") },
				[RiftboundTag.Dragon]: { label: translatable("riftbound.attributes.cardTags.values.dragon", "Dragon") },
				[RiftboundTag.Elite]: { label: translatable("riftbound.attributes.cardTags.values.elite", "Elite") },
				[RiftboundTag.Equipment]: { label: translatable("riftbound.attributes.cardTags.values.equipment", "Equipment") },
				[RiftboundTag.Fae]: { label: translatable("riftbound.attributes.cardTags.values.fae", "Fae") },
				[RiftboundTag.Mech]: { label: translatable("riftbound.attributes.cardTags.values.mech", "Mech") },
				[RiftboundTag.Mystic]: { label: translatable("riftbound.attributes.cardTags.values.mystic", "Mystic") },
				[RiftboundTag.Ninja]: { label: translatable("riftbound.attributes.cardTags.values.ninja", "Ninja") },
				[RiftboundTag.Pirate]: { label: translatable("riftbound.attributes.cardTags.values.pirate", "Pirate") },
				[RiftboundTag.Poro]: { label: translatable("riftbound.attributes.cardTags.values.poro", "Poro") },
				[RiftboundTag.Recruit]: { label: translatable("riftbound.attributes.cardTags.values.recruit", "Recruit") },
				[RiftboundTag.Sentinel]: { label: translatable("riftbound.attributes.cardTags.values.sentinel", "Sentinel") },
				[RiftboundTag.Soldier]: { label: translatable("riftbound.attributes.cardTags.values.soldier", "Soldier") },
				[RiftboundTag.Spider]: { label: translatable("riftbound.attributes.cardTags.values.spider", "Spider") },
				[RiftboundTag.Spirit]: { label: translatable("riftbound.attributes.cardTags.values.spirit", "Spirit") },
				[RiftboundTag.Trifarian]: { label: translatable("riftbound.attributes.cardTags.values.trifarian", "Trifarian") },
				[RiftboundTag.Vastaya]: { label: translatable("riftbound.attributes.cardTags.values.vastaya", "Vastaya") },
				[RiftboundTag.Warrior]: { label: translatable("riftbound.attributes.cardTags.values.warrior", "Warrior") },
				[RiftboundTag.Wizard]: { label: translatable("riftbound.attributes.cardTags.values.wizard", "Wizard") },
				[RiftboundTag.Yordle]: { label: translatable("riftbound.attributes.cardTags.values.yordle", "Yordle") }
			}
		}),
		orientation: enumAttribute(RiftboundOrientation, {
			entity: "variant",
			name: translatable("riftbound.attributes.orientation.name", "Orientation"),
			description: translatable("riftbound.attributes.orientation.description", "The orientation of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[RiftboundOrientation.Portrait]: { label: translatable("riftbound.attributes.orientation.values.portrait", "Portrait") },
				[RiftboundOrientation.Landscape]: { label: translatable("riftbound.attributes.orientation.values.landscape", "Landscape") }
			},
			hidden: true
		}),
		energyCost: {
			entity: "card",
			name: translatable("riftbound.attributes.energyCost.name", "Energy Cost"),
			description: translatable("riftbound.attributes.energyCost.description", "Energy cost to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		mightCost: {
			entity: "card",
			name: translatable("riftbound.attributes.mightCost.name", "Might Cost"),
			description: translatable("riftbound.attributes.mightCost.description", "Might cost of the unit"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		rulesText: {
			entity: "variant",
			name: translatable("riftbound.attributes.rulesText.name", "Rules Text"),
			description: translatable("riftbound.attributes.rulesText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"finish",
			"domains"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"cardType",
			"superType",
			"cardTags",
			"energyCost",
			"mightCost",
			"orientation"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText"],
		statistics: [
			"domains",
			"cardType",
			"superType",
			"cardTags",
			"rarity",
			"energyCost",
			"mightCost",
			"orientation"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: riftboundListPresets,
	basicAttributes: ["domains", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Cardmarket, SupportedMarketplace.Tcgplayer],
	vendorIds: [
		{
			key: "riotId",
			label: "Riot ID",
			isMarketplaceId: false
		},
		cardmarketVendorId,
		tcgplayerVendorId
	],
	csvTemplateExample: {
		vendorIds: {
			Cardmarket: "847146",
			TCGplayer: "652802"
		},
		printNumber: "030",
		cardName: "Jinx - Demolitionist",
		expansion: "Origins - Main Set",
		expansionCode: "OGN",
		finish: CardFinish.FOIL,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/rise/rise.types.ts
let RiseRarity = /* @__PURE__ */ function(RiseRarity) {
	RiseRarity["Common"] = "Common";
	RiseRarity["Uncommon"] = "Uncommon";
	RiseRarity["Rare"] = "Rare";
	RiseRarity["Legendary"] = "Legendary";
	return RiseRarity;
}({});
let RiseVariant = /* @__PURE__ */ function(RiseVariant) {
	RiseVariant["Standard"] = "Standard";
	RiseVariant["BlackBorder"] = "Black Border";
	RiseVariant["BoxTopper"] = "Box Topper";
	RiseVariant["Collector"] = "Collector";
	RiseVariant["CollectorGold"] = "Collector Gold";
	RiseVariant["CollectorPink"] = "Collector Pink";
	RiseVariant["CollectorSilver"] = "Collector Silver";
	RiseVariant["CollectorSerialized"] = "Collector Serialized";
	RiseVariant["FullArt"] = "Full Art";
	RiseVariant["Gamma"] = "Gamma";
	RiseVariant["GoldenTicket"] = "GoldenTicket";
	RiseVariant["OilCard"] = "Oil Card";
	RiseVariant["PoscaHandDrawn"] = "POSCA Hand Drawn Card";
	RiseVariant["PokerCard"] = "Poker Card";
	RiseVariant["SketchStamp"] = "Sketch stamp";
	RiseVariant["TestLenticular"] = "Test Lenticular Card";
	return RiseVariant;
}({});
let RiseCardFinish = /* @__PURE__ */ function(RiseCardFinish) {
	RiseCardFinish["Standard"] = "Standard";
	RiseCardFinish["Foil"] = "Foil";
	RiseCardFinish["BlastFoilRainbow"] = "Blast Foil Rainbow";
	RiseCardFinish["BubblesFoil"] = "Bubbles Foil";
	RiseCardFinish["CrackedIceFoil"] = "Cracked Ice Foil";
	RiseCardFinish["GalaxyFoil"] = "Galaxy Foil";
	RiseCardFinish["GlitterFoil"] = "Glitter Foil";
	RiseCardFinish["GoldFoil"] = "Gold Foil";
	RiseCardFinish["MultiSidelineFoil"] = "Multi Sideline Foil";
	RiseCardFinish["RainbowSolidTextureStamp"] = "Rainbow Solid Texture Stamp";
	RiseCardFinish["StarFoil"] = "Star Foil";
	RiseCardFinish["SurgeFoil"] = "Surge Foil";
	RiseCardFinish["FullArtGoldSign"] = "Full Art Gold Sign";
	RiseCardFinish["DepthLenticular"] = "Depth Lenticular";
	RiseCardFinish["FlipLenticular"] = "Flip Lenticular";
	RiseCardFinish["Lenticular"] = "Lenticular";
	return RiseCardFinish;
}({});
let RiseType = /* @__PURE__ */ function(RiseType) {
	RiseType["Creature"] = "Creature";
	RiseType["Incantation"] = "Incantation";
	RiseType["Imminent"] = "Imminent";
	RiseType["Summoner"] = "Summoner";
	RiseType["Door"] = "Door";
	RiseType["LevelUp"] = "Level Up";
	RiseType["Dungeon"] = "Dungeon";
	return RiseType;
}({});
let RiseRank = /* @__PURE__ */ function(RiseRank) {
	RiseRank["None"] = "None";
	RiseRank["Rank1"] = "Rank 1";
	RiseRank["Rank2"] = "Rank 2";
	RiseRank["Rank3"] = "Rank 3";
	RiseRank["Rank4"] = "Rank 4";
	RiseRank["RankInfinite"] = "Rank Infinite";
	return RiseRank;
}({});
let RiseSubtype = /* @__PURE__ */ function(RiseSubtype) {
	RiseSubtype["Angel"] = "Angel";
	RiseSubtype["Assassin"] = "Assassin";
	RiseSubtype["Demon"] = "Demon";
	RiseSubtype["Dragon"] = "Dragon";
	RiseSubtype["Fairy"] = "Fairy";
	RiseSubtype["Spirit"] = "Spirit";
	RiseSubtype["Zombie"] = "Zombie";
	RiseSubtype["Skeleton"] = "Skeleton";
	RiseSubtype["Cat"] = "Cat";
	RiseSubtype["Bat"] = "Bat";
	RiseSubtype["Chief"] = "Chief";
	RiseSubtype["Dog"] = "Dog";
	RiseSubtype["Dwarf"] = "Dwarf";
	RiseSubtype["Golem"] = "Golem";
	RiseSubtype["Knight"] = "Knight";
	RiseSubtype["Lich"] = "Lich";
	RiseSubtype["Clone"] = "Clone";
	RiseSubtype["Divinity"] = "Divinity";
	RiseSubtype["Monster"] = "Monster";
	RiseSubtype["Food"] = "Food";
	RiseSubtype["Octopus"] = "Octopus";
	RiseSubtype["Slime"] = "Slime";
	RiseSubtype["Spider"] = "Spider";
	RiseSubtype["Undead"] = "Undead";
	RiseSubtype["Vampyr"] = "Vampyr";
	RiseSubtype["Witch"] = "Witch";
	RiseSubtype["Worm"] = "Worm";
	RiseSubtype["Insect"] = "Insect";
	RiseSubtype["Keyper"] = "Keyper";
	RiseSubtype["Plant"] = "Plant";
	RiseSubtype["Unicorn"] = "Unicorn";
	RiseSubtype["Robot"] = "Robot";
	RiseSubtype["Unknown"] = "???";
	return RiseSubtype;
}({});
let RiseAbility = /* @__PURE__ */ function(RiseAbility) {
	RiseAbility["Flying"] = "Flying";
	RiseAbility["Reach"] = "Reach";
	RiseAbility["Pierce"] = "Pierce";
	RiseAbility["Fightback"] = "Fightback";
	RiseAbility["Immunity"] = "Immunity";
	RiseAbility["Persistence"] = "Persistence";
	RiseAbility["WallPass"] = "Wall-pass";
	RiseAbility["Reanimate"] = "Reanimate";
	RiseAbility["Root"] = "Root";
	RiseAbility["Horde"] = "Horde";
	RiseAbility["Symbiosis"] = "Symbiosis";
	RiseAbility["Morpher"] = "Morpher";
	RiseAbility["Together"] = "Together";
	RiseAbility["Equipment"] = "Equipment";
	RiseAbility["Disease"] = "Disease";
	RiseAbility["Candy"] = "Candy";
	RiseAbility["FixedPrice"] = "Fixed Price";
	RiseAbility["MultiTarget"] = "Multi-target";
	RiseAbility["Range"] = "Range";
	RiseAbility["Water"] = "Water";
	RiseAbility["Fire"] = "Fire";
	RiseAbility["Electric"] = "Electric";
	RiseAbility["Ice"] = "Ice";
	RiseAbility["Poison"] = "Poison";
	RiseAbility["Assimilation"] = "Assimilation";
	RiseAbility["Explosion"] = "Explosion";
	RiseAbility["Advantage"] = "Advantage";
	RiseAbility["Cloning"] = "Cloning";
	RiseAbility["IronSkin"] = "Iron Skin";
	RiseAbility["Drain"] = "Drain";
	RiseAbility["Dodge"] = "Dodge";
	RiseAbility["Possession"] = "Possession";
	RiseAbility["Bleeding"] = "Bleeding";
	RiseAbility["Haste"] = "Haste";
	RiseAbility["Riposte"] = "Riposte";
	RiseAbility["Set"] = "Set";
	RiseAbility["Amok"] = "Amok";
	RiseAbility["Diversion"] = "Diversion";
	RiseAbility["Exhumation"] = "Exhumation";
	RiseAbility["Chained"] = "Chained";
	RiseAbility["Bomb"] = "Bomb";
	RiseAbility["Adept"] = "Adept";
	RiseAbility["Parasite"] = "Parasite";
	RiseAbility["Allergy"] = "Allergy";
	RiseAbility["Imitator"] = "Imitator";
	RiseAbility["Lock"] = "Lock";
	RiseAbility["Madness"] = "Madness";
	RiseAbility["Paranoia"] = "Paranoia";
	RiseAbility["Paralysis"] = "Paralysis";
	RiseAbility["Schizophrenia"] = "Schizophrenia";
	RiseAbility["Cooling"] = "Cooling";
	RiseAbility["Cold"] = "Cold";
	RiseAbility["Constancy"] = "Constancy";
	RiseAbility["Copycat"] = "Copycat";
	RiseAbility["Distance"] = "Distance";
	RiseAbility["Earth"] = "Earth";
	RiseAbility["FixedTime"] = "Fixed Time";
	RiseAbility["Food"] = "Food";
	RiseAbility["Shadow"] = "Shadow";
	RiseAbility["Splash"] = "Splash";
	RiseAbility["Trauma"] = "Trauma";
	RiseAbility["Velocity"] = "Velocity";
	RiseAbility["Web"] = "Web";
	return RiseAbility;
}({});
let RisePlane = /* @__PURE__ */ function(RisePlane) {
	RisePlane["Ground"] = "Ground";
	RisePlane["Aerial"] = "Aerial";
	return RisePlane;
}({});

//#endregion
//#region src/lib/games/rise/rise.presets.ts
const riseListPresets = {
	Standard: {
		name: translatable("rise.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("rise.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: { finish: [RiseCardFinish.Standard] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("rise.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("rise.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [
			{
				filters: {
					finish: [RiseCardFinish.Standard],
					rank: [RiseRank.None]
				},
				quantity: 1
			},
			{
				filters: {
					finish: [RiseCardFinish.Standard],
					rank: [RiseRank.Rank1]
				},
				quantity: 1
			},
			{
				filters: {
					finish: [RiseCardFinish.Standard],
					rank: [RiseRank.Rank2]
				},
				quantity: 2
			},
			{
				filters: {
					finish: [RiseCardFinish.Standard],
					rank: [RiseRank.Rank3]
				},
				quantity: 3
			},
			{
				filters: {
					finish: [RiseCardFinish.Standard],
					rank: [RiseRank.Rank4]
				},
				quantity: 4
			},
			{
				filters: {
					finish: [RiseCardFinish.Standard],
					rank: [RiseRank.RankInfinite]
				},
				quantity: 4
			}
		]
	},
	Foil: {
		name: translatable("rise.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("rise.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [RiseCardFinish.Foil] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("rise.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("rise.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [
			{
				filters: {
					finish: [RiseCardFinish.Foil],
					rank: [RiseRank.None]
				},
				quantity: 1
			},
			{
				filters: {
					finish: [RiseCardFinish.Foil],
					rank: [RiseRank.Rank1]
				},
				quantity: 1
			},
			{
				filters: {
					finish: [RiseCardFinish.Foil],
					rank: [RiseRank.Rank2]
				},
				quantity: 2
			},
			{
				filters: {
					finish: [RiseCardFinish.Foil],
					rank: [RiseRank.Rank3]
				},
				quantity: 3
			},
			{
				filters: {
					finish: [RiseCardFinish.Foil],
					rank: [RiseRank.Rank4]
				},
				quantity: 4
			},
			{
				filters: {
					finish: [RiseCardFinish.Foil],
					rank: [RiseRank.RankInfinite]
				},
				quantity: 4
			}
		]
	},
	"Master Set": {
		name: translatable("rise.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("rise.presets.masterset.description", "A complete set with one copy of each card in all finishes and variants"),
		matchers: [{
			filters: {},
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/rise/rise.game.ts
/**
* Shared finish values for Rise - used by both finishes and finish attributes
*/
const riseFinishValues = {
	[RiseCardFinish.Standard]: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	[RiseCardFinish.Foil]: { label: translatable("global.attributes.finish.values.foil", "Foil") },
	[RiseCardFinish.BlastFoilRainbow]: { label: translatable("rise.attributes.finish.values.blastFoilRainbow", "Blast Foil Rainbow") },
	[RiseCardFinish.BubblesFoil]: { label: translatable("rise.attributes.finish.values.bubblesFoil", "Bubbles Foil") },
	[RiseCardFinish.CrackedIceFoil]: { label: translatable("rise.attributes.finish.values.crackedIceFoil", "Cracked Ice Foil") },
	[RiseCardFinish.GalaxyFoil]: { label: translatable("rise.attributes.finish.values.galaxyFoil", "Galaxy Foil") },
	[RiseCardFinish.GlitterFoil]: { label: translatable("rise.attributes.finish.values.glitterFoil", "Glitter Foil") },
	[RiseCardFinish.GoldFoil]: { label: translatable("rise.attributes.finish.values.goldFoil", "Gold Foil") },
	[RiseCardFinish.MultiSidelineFoil]: { label: translatable("rise.attributes.finish.values.multiSidelineFoil", "Multi Sideline Foil") },
	[RiseCardFinish.RainbowSolidTextureStamp]: { label: translatable("rise.attributes.finish.values.rainbowSolidTextureStamp", "Rainbow Solid Texture Stamp") },
	[RiseCardFinish.StarFoil]: { label: translatable("rise.attributes.finish.values.starFoil", "Star Foil") },
	[RiseCardFinish.SurgeFoil]: { label: translatable("rise.attributes.finish.values.surgeFoil", "Surge Foil") },
	[RiseCardFinish.FullArtGoldSign]: { label: translatable("rise.attributes.finish.values.fullArtGoldSign", "Full Art Gold Sign") },
	[RiseCardFinish.DepthLenticular]: { label: translatable("rise.attributes.finish.values.depthLenticular", "Depth Lenticular") },
	[RiseCardFinish.FlipLenticular]: { label: translatable("rise.attributes.finish.values.flipLenticular", "Flip Lenticular") },
	[RiseCardFinish.Lenticular]: { label: translatable("rise.attributes.finish.values.lenticular", "Lenticular") }
};
const riseFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(RiseRarity).optional(),
	variant: makeZodEnumFilter(RiseVariant).optional(),
	finish: makeZodEnumFilter(RiseCardFinish).optional(),
	finishes: makeZodEnumFilter(RiseCardFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	types: makeZodEnumFilter(RiseType).optional(),
	rank: makeZodEnumFilter(RiseRank).optional(),
	plane: makeZodEnumFilter(RisePlane).optional(),
	subtypes: makeZodEnumFilter(RiseSubtype).optional(),
	abilities: makeZodEnumFilter(RiseAbility).optional(),
	attack: zodNumericFilter.optional(),
	defense: zodNumericFilter.optional(),
	life: zodNumericFilter.optional(),
	maxLevel: zodNumericFilter.optional()
});
const RiseGameConfiguration = {
	name: "Rise",
	shortName: "Rise",
	slug: "rise",
	icon: {
		dark: "rise-icon.png",
		light: "rise-icon.png"
	},
	image: {
		dark: "rise.png",
		light: "rise.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(RiseCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: true,
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			filter: FilterComponentType.CheckboxList,
			values: riseFinishValues
		}),
		finish: enumAttribute(RiseCardFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: false,
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			filter: FilterComponentType.CheckboxList,
			values: riseFinishValues
		}),
		rarity: enumAttribute(RiseRarity, {
			entity: "variant",
			name: translatable("rise.attributes.rarity.name", "Rarity"),
			description: translatable("rise.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[RiseRarity.Common]: {
					label: translatable("rise.attributes.rarity.values.common", "Common"),
					image: "/images/rise/rarities/common.png"
				},
				[RiseRarity.Uncommon]: {
					label: translatable("rise.attributes.rarity.values.uncommon", "Uncommon"),
					image: "/images/rise/rarities/uncommon.png"
				},
				[RiseRarity.Rare]: {
					label: translatable("rise.attributes.rarity.values.rare", "Rare"),
					image: "/images/rise/rarities/rare.png"
				},
				[RiseRarity.Legendary]: {
					label: translatable("rise.attributes.rarity.values.legendary", "Legendary"),
					image: "/images/rise/rarities/legendary.png"
				}
			}
		}),
		variant: enumAttribute(RiseVariant, {
			entity: "variant",
			name: translatable("rise.attributes.variant.name", "Variant"),
			description: translatable("rise.attributes.variant.description", "Special edition or variant of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[RiseVariant.Standard]: { label: translatable("rise.attributes.variant.values.standard", "Standard") },
				[RiseVariant.BlackBorder]: { label: translatable("rise.attributes.variant.values.blackBorder", "Black Border") },
				[RiseVariant.BoxTopper]: { label: translatable("rise.attributes.variant.values.boxTopper", "Box Topper") },
				[RiseVariant.Collector]: { label: translatable("rise.attributes.variant.values.collector", "Collector") },
				[RiseVariant.CollectorGold]: { label: translatable("rise.attributes.variant.values.collectorGold", "Collector Gold") },
				[RiseVariant.CollectorPink]: { label: translatable("rise.attributes.variant.values.collectorPink", "Collector Pink") },
				[RiseVariant.CollectorSilver]: { label: translatable("rise.attributes.variant.values.collectorSilver", "Collector Silver") },
				[RiseVariant.CollectorSerialized]: { label: translatable("rise.attributes.variant.values.collectorSerialized", "Collector Serialized") },
				[RiseVariant.FullArt]: { label: translatable("rise.attributes.variant.values.fullArt", "Full Art") },
				[RiseVariant.Gamma]: { label: translatable("rise.attributes.variant.values.gamma", "Gamma") },
				[RiseVariant.GoldenTicket]: { label: translatable("rise.attributes.variant.values.goldenTicket", "GoldenTicket") },
				[RiseVariant.OilCard]: { label: translatable("rise.attributes.variant.values.oilCard", "Oil Card") },
				[RiseVariant.PoscaHandDrawn]: { label: translatable("rise.attributes.variant.values.poscaHandDrawn", "POSCA Hand Drawn Card") },
				[RiseVariant.PokerCard]: { label: translatable("rise.attributes.variant.values.pokerCard", "Poker Card") },
				[RiseVariant.SketchStamp]: { label: translatable("rise.attributes.variant.values.sketchStamp", "Sketch stamp") },
				[RiseVariant.TestLenticular]: { label: translatable("rise.attributes.variant.values.testLenticular", "Test Lenticular Card") }
			}
		}),
		printNumber: printNumberAttribute,
		types: enumAttribute(RiseType, {
			entity: "card",
			name: translatable("rise.attributes.type.name", "Card Type"),
			description: translatable("rise.attributes.type.description", "The type of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[RiseType.Creature]: {
					label: translatable("rise.attributes.type.values.creature", "Creature"),
					image: "/images/rise/types/creature.png"
				},
				[RiseType.Incantation]: {
					label: translatable("rise.attributes.type.values.incantation", "Incantation"),
					image: "/images/rise/types/incantation.png"
				},
				[RiseType.Imminent]: {
					label: translatable("rise.attributes.type.values.imminent", "Imminent"),
					image: "/images/rise/types/imminent.png"
				},
				[RiseType.Summoner]: {
					label: translatable("rise.attributes.type.values.summoner", "Summoner"),
					image: "/images/rise/types/summoner.png"
				},
				[RiseType.Door]: {
					label: translatable("rise.attributes.type.values.door", "Door"),
					image: "/images/rise/types/door.png"
				},
				[RiseType.LevelUp]: {
					label: translatable("rise.attributes.type.values.levelUp", "Level Up"),
					image: "/images/rise/types/level-up.png"
				},
				[RiseType.Dungeon]: {
					label: translatable("rise.attributes.type.values.dungeon", "Dungeon"),
					image: "/images/rise/types/dungeon.png"
				}
			}
		}),
		rank: enumAttribute(RiseRank, {
			entity: "card",
			name: translatable("rise.attributes.rank.name", "Card Rank"),
			description: translatable("rise.attributes.rank.description", "The rank of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[RiseRank.None]: { label: translatable("rise.attributes.rank.values.none", "None") },
				[RiseRank.Rank1]: { label: translatable("rise.attributes.rank.values.rank1", "Rank 1") },
				[RiseRank.Rank2]: { label: translatable("rise.attributes.rank.values.rank2", "Rank 2") },
				[RiseRank.Rank3]: { label: translatable("rise.attributes.rank.values.rank3", "Rank 3") },
				[RiseRank.Rank4]: { label: translatable("rise.attributes.rank.values.rank4", "Rank 4") },
				[RiseRank.RankInfinite]: { label: translatable("rise.attributes.rank.values.rankInfinite", "Rank Infinite") }
			}
		}),
		subtypes: enumAttribute(RiseSubtype, {
			entity: "card",
			name: translatable("rise.attributes.subtypes.name", "Subtypes"),
			description: translatable("rise.attributes.subtypes.description", "Creature subtypes"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[RiseSubtype.Angel]: { label: translatable("rise.attributes.subtypes.values.angel", "Angel") },
				[RiseSubtype.Assassin]: { label: translatable("rise.attributes.subtypes.values.assassin", "Assassin") },
				[RiseSubtype.Chief]: { label: translatable("rise.attributes.subtypes.values.chief", "Chief") },
				[RiseSubtype.Dog]: { label: translatable("rise.attributes.subtypes.values.dog", "Dog") },
				[RiseSubtype.Dwarf]: { label: translatable("rise.attributes.subtypes.values.dwarf", "Dwarf") },
				[RiseSubtype.Golem]: { label: translatable("rise.attributes.subtypes.values.golem", "Golem") },
				[RiseSubtype.Lich]: { label: translatable("rise.attributes.subtypes.values.lich", "Lich") },
				[RiseSubtype.Octopus]: { label: translatable("rise.attributes.subtypes.values.octopus", "Octopus") },
				[RiseSubtype.Slime]: { label: translatable("rise.attributes.subtypes.values.slime", "Slime") },
				[RiseSubtype.Spider]: { label: translatable("rise.attributes.subtypes.values.spider", "Spider") },
				[RiseSubtype.Undead]: { label: translatable("rise.attributes.subtypes.values.undead", "Undead") },
				[RiseSubtype.Vampyr]: { label: translatable("rise.attributes.subtypes.values.vampyr", "Vampyr") },
				[RiseSubtype.Worm]: { label: translatable("rise.attributes.subtypes.values.worm", "Worm") },
				[RiseSubtype.Demon]: { label: translatable("rise.attributes.subtypes.values.demon", "Demon") },
				[RiseSubtype.Dragon]: { label: translatable("rise.attributes.subtypes.values.dragon", "Dragon") },
				[RiseSubtype.Fairy]: { label: translatable("rise.attributes.subtypes.values.fairy", "Fairy") },
				[RiseSubtype.Spirit]: { label: translatable("rise.attributes.subtypes.values.spirit", "Spirit") },
				[RiseSubtype.Zombie]: { label: translatable("rise.attributes.subtypes.values.zombie", "Zombie") },
				[RiseSubtype.Skeleton]: { label: translatable("rise.attributes.subtypes.values.skeleton", "Skeleton") },
				[RiseSubtype.Cat]: { label: translatable("rise.attributes.subtypes.values.cat", "Cat") },
				[RiseSubtype.Bat]: { label: translatable("rise.attributes.subtypes.values.bat", "Bat") },
				[RiseSubtype.Knight]: { label: translatable("rise.attributes.subtypes.values.knight", "Knight") },
				[RiseSubtype.Clone]: { label: translatable("rise.attributes.subtypes.values.clone", "Clone") },
				[RiseSubtype.Divinity]: { label: translatable("rise.attributes.subtypes.values.divinity", "Divinity") },
				[RiseSubtype.Monster]: { label: translatable("rise.attributes.subtypes.values.monster", "Monster") },
				[RiseSubtype.Food]: { label: translatable("rise.attributes.subtypes.values.food", "Food") },
				[RiseSubtype.Witch]: { label: translatable("rise.attributes.subtypes.values.witch", "Witch") },
				[RiseSubtype.Insect]: { label: translatable("rise.attributes.subtypes.values.insect", "Insect") },
				[RiseSubtype.Keyper]: { label: translatable("rise.attributes.subtypes.values.keyper", "Keyper") },
				[RiseSubtype.Plant]: { label: translatable("rise.attributes.subtypes.values.plant", "Plant") },
				[RiseSubtype.Unicorn]: { label: translatable("rise.attributes.subtypes.values.unicorn", "Unicorn") },
				[RiseSubtype.Robot]: { label: translatable("rise.attributes.subtypes.values.robot", "Robot") },
				[RiseSubtype.Unknown]: { label: translatable("rise.attributes.subtypes.values.unknown", "Unknown") }
			}
		}),
		abilities: enumAttribute(RiseAbility, {
			entity: "card",
			name: translatable("rise.attributes.abilities.name", "Abilities"),
			description: translatable("rise.attributes.abilities.description", "Keyword abilities"),
			type: "enum",
			array: true,
			filter: FilterComponentType.Select,
			values: {
				[RiseAbility.Flying]: {
					label: translatable("rise.attributes.abilities.values.flying", "Flying"),
					image: "/images/rise/abilities/flying.png"
				},
				[RiseAbility.Reach]: {
					label: translatable("rise.attributes.abilities.values.reach", "Reach"),
					image: "/images/rise/abilities/reach.png"
				},
				[RiseAbility.Pierce]: {
					label: translatable("rise.attributes.abilities.values.pierce", "Breakthrough"),
					image: "/images/rise/abilities/pierce.png"
				},
				[RiseAbility.Immunity]: {
					label: translatable("rise.attributes.abilities.values.immunity", "Immunity"),
					image: "/images/rise/abilities/immunity.png"
				},
				[RiseAbility.Equipment]: {
					label: translatable("rise.attributes.abilities.values.equipment", "Equipment"),
					image: "/images/rise/abilities/equipment.png"
				},
				[RiseAbility.Disease]: {
					label: translatable("rise.attributes.abilities.values.disease", "Disease"),
					image: "/images/rise/abilities/disease.png"
				},
				[RiseAbility.Candy]: {
					label: translatable("rise.attributes.abilities.values.candy", "Candy"),
					image: "/images/rise/abilities/candy.png"
				},
				[RiseAbility.Water]: {
					label: translatable("rise.attributes.abilities.values.water", "Water"),
					image: "/images/rise/abilities/water.png"
				},
				[RiseAbility.Fire]: {
					label: translatable("rise.attributes.abilities.values.fire", "Fire"),
					image: "/images/rise/abilities/fire.png"
				},
				[RiseAbility.Electric]: {
					label: translatable("rise.attributes.abilities.values.electric", "Electric"),
					image: "/images/rise/abilities/electric.png"
				},
				[RiseAbility.Ice]: {
					label: translatable("rise.attributes.abilities.values.ice", "Ice"),
					image: "/images/rise/abilities/ice.png"
				},
				[RiseAbility.Fightback]: {
					label: translatable("rise.attributes.abilities.values.fightback", "Fightback"),
					image: "/images/rise/abilities/fightback.png"
				},
				[RiseAbility.Persistence]: {
					label: translatable("rise.attributes.abilities.values.persistence", "Persistence"),
					image: "/images/rise/abilities/persistence.png"
				},
				[RiseAbility.WallPass]: {
					label: translatable("rise.attributes.abilities.values.wallPass", "Wall-pass"),
					image: "/images/rise/abilities/wallpass.png"
				},
				[RiseAbility.Reanimate]: {
					label: translatable("rise.attributes.abilities.values.reanimate", "Reanimate"),
					image: "/images/rise/abilities/reanimate.png"
				},
				[RiseAbility.Root]: {
					label: translatable("rise.attributes.abilities.values.root", "Root"),
					image: "/images/rise/abilities/root.png"
				},
				[RiseAbility.Horde]: {
					label: translatable("rise.attributes.abilities.values.horde", "Horde"),
					image: "/images/rise/abilities/horde.png"
				},
				[RiseAbility.Symbiosis]: {
					label: translatable("rise.attributes.abilities.values.symbiosis", "Symbiosis"),
					image: "/images/rise/abilities/symbiosis.png"
				},
				[RiseAbility.Morpher]: {
					label: translatable("rise.attributes.abilities.values.morpher", "Morpher"),
					image: "/images/rise/abilities/morpher.png"
				},
				[RiseAbility.Together]: {
					label: translatable("rise.attributes.abilities.values.together", "Together"),
					image: "/images/rise/abilities/together.png"
				},
				[RiseAbility.FixedPrice]: {
					label: translatable("rise.attributes.abilities.values.fixedPrice", "Fixed Price"),
					image: "/images/rise/abilities/fixedprice.png"
				},
				[RiseAbility.MultiTarget]: {
					label: translatable("rise.attributes.abilities.values.multiTarget", "Multi-target"),
					image: "/images/rise/abilities/multitarget.png"
				},
				[RiseAbility.Range]: {
					label: translatable("rise.attributes.abilities.values.range", "Range"),
					image: "/images/rise/abilities/range.png"
				},
				[RiseAbility.Poison]: {
					label: translatable("rise.attributes.abilities.values.poison", "Poison"),
					image: "/images/rise/abilities/poison.png"
				},
				[RiseAbility.Assimilation]: {
					label: translatable("rise.attributes.abilities.values.assimilation", "Assimilation"),
					image: "/images/rise/abilities/assimilation.png"
				},
				[RiseAbility.Explosion]: {
					label: translatable("rise.attributes.abilities.values.explosion", "Explosion"),
					image: "/images/rise/abilities/explosion.png"
				},
				[RiseAbility.Advantage]: {
					label: translatable("rise.attributes.abilities.values.advantage", "Advantage"),
					image: "/images/rise/abilities/advantage.png"
				},
				[RiseAbility.Cloning]: {
					label: translatable("rise.attributes.abilities.values.cloning", "Cloning"),
					image: "/images/rise/abilities/cloning.png"
				},
				[RiseAbility.IronSkin]: {
					label: translatable("rise.attributes.abilities.values.ironSkin", "Iron Skin"),
					image: "/images/rise/abilities/ironskin.png"
				},
				[RiseAbility.Drain]: {
					label: translatable("rise.attributes.abilities.values.drain", "Drain"),
					image: "/images/rise/abilities/drain.png"
				},
				[RiseAbility.Dodge]: {
					label: translatable("rise.attributes.abilities.values.dodge", "Dodge"),
					image: "/images/rise/abilities/dodge.png"
				},
				[RiseAbility.Possession]: {
					label: translatable("rise.attributes.abilities.values.possession", "Possession"),
					image: "/images/rise/abilities/possession.png"
				},
				[RiseAbility.Bleeding]: {
					label: translatable("rise.attributes.abilities.values.bleeding", "Bleeding"),
					image: "/images/rise/abilities/bleeding.png"
				},
				[RiseAbility.Haste]: {
					label: translatable("rise.attributes.abilities.values.haste", "Haste"),
					image: "/images/rise/abilities/haste.png"
				},
				[RiseAbility.Riposte]: {
					label: translatable("rise.attributes.abilities.values.riposte", "Riposte"),
					image: "/images/rise/abilities/riposte.png"
				},
				[RiseAbility.Set]: { label: translatable("rise.attributes.abilities.values.set", "Set") },
				[RiseAbility.Amok]: {
					label: translatable("rise.attributes.abilities.values.amok", "Amok"),
					image: "/images/rise/abilities/amok.png"
				},
				[RiseAbility.Diversion]: {
					label: translatable("rise.attributes.abilities.values.diversion", "Diversion"),
					image: "/images/rise/abilities/diversion.png"
				},
				[RiseAbility.Exhumation]: {
					label: translatable("rise.attributes.abilities.values.exhumation", "Exhumation"),
					image: "/images/rise/abilities/exhumation.png"
				},
				[RiseAbility.Chained]: {
					label: translatable("rise.attributes.abilities.values.chained", "Chained"),
					image: "/images/rise/abilities/chained.png"
				},
				[RiseAbility.Bomb]: {
					label: translatable("rise.attributes.abilities.values.bomb", "Bomb"),
					image: "/images/rise/abilities/bomb.png"
				},
				[RiseAbility.Adept]: { label: translatable("rise.attributes.abilities.values.adept", "Adept") },
				[RiseAbility.Parasite]: {
					label: translatable("rise.attributes.abilities.values.parasite", "Parasite"),
					image: "/images/rise/abilities/parasite.png"
				},
				[RiseAbility.Allergy]: {
					label: translatable("rise.attributes.abilities.values.allergy", "Allergy"),
					image: "/images/rise/abilities/allergy.png"
				},
				[RiseAbility.Imitator]: {
					label: translatable("rise.attributes.abilities.values.imitator", "Imitator"),
					image: "/images/rise/abilities/imitator.png"
				},
				[RiseAbility.Lock]: {
					label: translatable("rise.attributes.abilities.values.lock", "Lock"),
					image: "/images/rise/abilities/lock.png"
				},
				[RiseAbility.Madness]: {
					label: translatable("rise.attributes.abilities.values.madness", "Madness"),
					image: "/images/rise/abilities/madness.png"
				},
				[RiseAbility.Paranoia]: {
					label: translatable("rise.attributes.abilities.values.paranoia", "Paranoia"),
					image: "/images/rise/abilities/paranoia.png"
				},
				[RiseAbility.Paralysis]: {
					label: translatable("rise.attributes.abilities.values.paralysis", "Paralysis"),
					image: "/images/rise/abilities/paralysis.png"
				},
				[RiseAbility.Schizophrenia]: {
					label: translatable("rise.attributes.abilities.values.schizophrenia", "Schizophrenia"),
					image: "/images/rise/abilities/schizophrenia.png"
				},
				[RiseAbility.Cooling]: {
					label: translatable("rise.attributes.abilities.values.cooling", "Cooling"),
					image: "/images/rise/abilities/cooling.png"
				},
				[RiseAbility.Cold]: { label: translatable("rise.attributes.abilities.values.cold", "Cold") },
				[RiseAbility.Constancy]: { label: translatable("rise.attributes.abilities.values.constancy", "Constancy") },
				[RiseAbility.Copycat]: { label: translatable("rise.attributes.abilities.values.copycat", "Copycat") },
				[RiseAbility.Distance]: { label: translatable("rise.attributes.abilities.values.distance", "Distance") },
				[RiseAbility.Earth]: { label: translatable("rise.attributes.abilities.values.earth", "Earth") },
				[RiseAbility.FixedTime]: { label: translatable("rise.attributes.abilities.values.fixedTime", "Fixed Time") },
				[RiseAbility.Food]: { label: translatable("rise.attributes.abilities.values.food", "Food") },
				[RiseAbility.Shadow]: { label: translatable("rise.attributes.abilities.values.shadow", "Shadow") },
				[RiseAbility.Splash]: { label: translatable("rise.attributes.abilities.values.splash", "Splash") },
				[RiseAbility.Trauma]: { label: translatable("rise.attributes.abilities.values.trauma", "Trauma") },
				[RiseAbility.Velocity]: { label: translatable("rise.attributes.abilities.values.velocity", "Velocity") },
				[RiseAbility.Web]: { label: translatable("rise.attributes.abilities.values.web", "Web") }
			}
		}),
		attack: {
			entity: "card",
			name: translatable("rise.attributes.attack.name", "Attack"),
			description: translatable("rise.attributes.attack.description", "Attack power of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		defense: {
			entity: "card",
			name: translatable("rise.attributes.defense.name", "Defense"),
			description: translatable("rise.attributes.defense.description", "Defense power of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		plane: enumAttribute(RisePlane, {
			entity: "card",
			name: translatable("rise.attributes.plane.name", "Plane"),
			description: translatable("rise.attributes.plane.description", "The plane where the card exists"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[RisePlane.Ground]: { label: translatable("rise.attributes.plane.values.ground", "Ground") },
				[RisePlane.Aerial]: { label: translatable("rise.attributes.plane.values.aerial", "Aerial") }
			}
		}),
		life: {
			entity: "variant",
			name: translatable("rise.attributes.life.name", "Life"),
			description: translatable("rise.attributes.life.description", "Starting life points"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		maxLevel: {
			entity: "variant",
			name: translatable("rise.attributes.maxLevel.name", "Max Level"),
			description: translatable("rise.attributes.maxLevel.description", "Maximum level of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		rulesText: {
			entity: "variant",
			name: translatable("rise.attributes.rulesText.name", "Rules Text"),
			description: translatable("rise.attributes.rulesText.description", "Card rules and abilities text"),
			type: "string",
			array: false
		},
		flavorText: {
			entity: "variant",
			name: translatable("rise.attributes.flavorText.name", "Flavor Text"),
			description: translatable("rise.attributes.flavorText.description", "Flavor text on the card"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"variant",
			"finish"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"types",
			"rank",
			"plane",
			"subtypes",
			"abilities",
			"attack",
			"defense",
			"life",
			"maxLevel"
		]
	}],
	productDetailsOrder: {
		details: ["rulesText", "flavorText"],
		statistics: [
			"types",
			"subtypes",
			"rank",
			"plane",
			"rarity",
			"attack",
			"defense",
			"life",
			"abilities",
			"maxLevel"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: riseListPresets,
	basicAttributes: ["rarity", "rank"],
	supportedMarketplaces: [],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "033",
		cardName: "Will-O",
		expansion: "Candypocalypse",
		expansionCode: "CANDY",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/sorcery/sorcery.types.ts
let SorceryCardFinish = /* @__PURE__ */ function(SorceryCardFinish) {
	SorceryCardFinish["STANDARD"] = "Standard";
	SorceryCardFinish["FOIL"] = "Foil";
	SorceryCardFinish["RAINBOW"] = "Rainbow";
	return SorceryCardFinish;
}({});
let SorceryCardVariant = /* @__PURE__ */ function(SorceryCardVariant) {
	SorceryCardVariant["BOOSTER"] = "Booster";
	SorceryCardVariant["PROMO"] = "Promo";
	SorceryCardVariant["PRECONSTRUCTED_DECK"] = "Preconstructed Deck";
	SorceryCardVariant["STORE_KIT"] = "Store Kit";
	SorceryCardVariant["DRAFT_KIT"] = "Draft Kit";
	SorceryCardVariant["DUST"] = "Dust";
	SorceryCardVariant["PLEDGE_PACK"] = "Pledge Pack";
	SorceryCardVariant["BOX_TOPPER"] = "Box Topper";
	SorceryCardVariant["WELCOME_KIT"] = "Welcome Kit";
	SorceryCardVariant["ORGANIZED_PLAY"] = "Organized Play";
	SorceryCardVariant["ALPHA_INVESTMENTS"] = "Alpha Investments";
	SorceryCardVariant["TEAM_COVENANT"] = "Team Covenant";
	SorceryCardVariant["KICKSTARTER"] = "Kickstarter";
	SorceryCardVariant["STAR_CITY_GAMES"] = "Star City Games";
	return SorceryCardVariant;
}({});
let SorceryRarity = /* @__PURE__ */ function(SorceryRarity) {
	SorceryRarity["UNIQUE"] = "Unique";
	SorceryRarity["ELITE"] = "Elite";
	SorceryRarity["EXCEPTIONAL"] = "Exceptional";
	SorceryRarity["ORDINARY"] = "Ordinary";
	SorceryRarity["CURIO"] = "Curio";
	return SorceryRarity;
}({});
let SorceryCardType = /* @__PURE__ */ function(SorceryCardType) {
	SorceryCardType["AVATAR"] = "Avatar";
	SorceryCardType["MINION"] = "Minion";
	SorceryCardType["MAGIC"] = "Magic";
	SorceryCardType["AURA"] = "Aura";
	SorceryCardType["ARTIFACT"] = "Artifact";
	SorceryCardType["SITE"] = "Site";
	return SorceryCardType;
}({});
let SorceryCardSubType = /* @__PURE__ */ function(SorceryCardSubType) {
	SorceryCardSubType["MORTAL"] = "Mortal";
	SorceryCardSubType["BEAST"] = "Beast";
	SorceryCardSubType["SPIRIT"] = "Spirit";
	SorceryCardSubType["UNDEAD"] = "Undead";
	SorceryCardSubType["DEMON"] = "Demon";
	SorceryCardSubType["DRAGON"] = "Dragon";
	SorceryCardSubType["MONSTER"] = "Monster";
	SorceryCardSubType["SPHINX"] = "Sphinx";
	SorceryCardSubType["FAERIE"] = "Faerie";
	SorceryCardSubType["ANGEL"] = "Angel";
	SorceryCardSubType["GIANT"] = "Giant";
	SorceryCardSubType["TROLL"] = "Troll";
	SorceryCardSubType["DWARF"] = "Dwarf";
	SorceryCardSubType["GNOME"] = "Gnome";
	SorceryCardSubType["OGRE"] = "Ogre";
	SorceryCardSubType["GOBLIN"] = "Goblin";
	SorceryCardSubType["MERFOLK"] = "Merfolk";
	SorceryCardSubType["ARMOR"] = "Armor";
	SorceryCardSubType["RELIC"] = "Relic";
	SorceryCardSubType["DEVICE"] = "Device";
	SorceryCardSubType["MONUMENT"] = "Monument";
	SorceryCardSubType["AUTOMATON"] = "Automaton";
	SorceryCardSubType["INSTRUMENTS"] = "Instruments";
	SorceryCardSubType["WEAPON"] = "Weapon";
	SorceryCardSubType["DOCUMENT"] = "Document";
	SorceryCardSubType["POTION"] = "Potion";
	SorceryCardSubType["TOWER"] = "Tower";
	SorceryCardSubType["VILLAGE"] = "Village";
	SorceryCardSubType["DESERT"] = "Desert";
	SorceryCardSubType["RIVER"] = "River";
	return SorceryCardSubType;
}({});
let SorceryCardKeyword = /* @__PURE__ */ function(SorceryCardKeyword) {
	SorceryCardKeyword["AIRBORNE"] = "Airborne";
	SorceryCardKeyword["BURROWING"] = "Burrowing";
	SorceryCardKeyword["GENESIS"] = "Genesis";
	SorceryCardKeyword["CHARGE"] = "Charge";
	SorceryCardKeyword["IMMOBILE"] = "Immobile";
	SorceryCardKeyword["DISABLED"] = "Disabled";
	SorceryCardKeyword["LETHAL"] = "Lethal";
	SorceryCardKeyword["MOVEMENT"] = "Movement";
	SorceryCardKeyword["RANGED"] = "Ranged";
	SorceryCardKeyword["SPELLCASTER"] = "Spellcaster";
	SorceryCardKeyword["STEALTH"] = "Stealth";
	SorceryCardKeyword["SUBMERGE"] = "Submerge";
	SorceryCardKeyword["VOIDWALK"] = "Voidwalk";
	SorceryCardKeyword["LANDBOUND"] = "Landbound";
	SorceryCardKeyword["WATERBOUND"] = "Waterbound";
	SorceryCardKeyword["DEATHRITE"] = "Deathrite";
	SorceryCardKeyword["FLOOD"] = "Flood";
	return SorceryCardKeyword;
}({});
let SorceryElement = /* @__PURE__ */ function(SorceryElement) {
	SorceryElement["WATER"] = "water";
	SorceryElement["EARTH"] = "earth";
	SorceryElement["FIRE"] = "fire";
	SorceryElement["AIR"] = "air";
	return SorceryElement;
}({});
let SorceryCardCategory = /* @__PURE__ */ function(SorceryCardCategory) {
	SorceryCardCategory["Site"] = "site";
	SorceryCardCategory["Spell"] = "spell";
	return SorceryCardCategory;
}({});

//#endregion
//#region src/lib/games/sorcery/sorcery.presets.ts
const sorceryListPresets = {
	Standard: {
		name: translatable("sorcery.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("sorcery.presets.standard.description", "A complete set with one copy of each card in standard finish"),
		matchers: [{
			filters: {
				finish: [SorceryCardFinish.STANDARD],
				variant: [SorceryCardVariant.BOOSTER]
			},
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("sorcery.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("sorcery.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [
			{
				filters: {
					finish: [SorceryCardFinish.STANDARD],
					rarity: [SorceryRarity.ORDINARY],
					type: [
						SorceryCardType.MINION,
						SorceryCardType.MAGIC,
						SorceryCardType.AURA,
						SorceryCardType.ARTIFACT,
						SorceryCardType.SITE
					],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 4
			},
			{
				filters: {
					finish: [SorceryCardFinish.STANDARD],
					rarity: [SorceryRarity.EXCEPTIONAL],
					type: [
						SorceryCardType.MINION,
						SorceryCardType.MAGIC,
						SorceryCardType.AURA,
						SorceryCardType.ARTIFACT,
						SorceryCardType.SITE
					],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 3
			},
			{
				filters: {
					finish: [SorceryCardFinish.STANDARD],
					rarity: [SorceryRarity.ELITE],
					type: [
						SorceryCardType.MINION,
						SorceryCardType.MAGIC,
						SorceryCardType.AURA,
						SorceryCardType.ARTIFACT,
						SorceryCardType.SITE
					],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 2
			},
			{
				filters: {
					finish: [SorceryCardFinish.STANDARD],
					rarity: [SorceryRarity.UNIQUE],
					type: [
						SorceryCardType.MINION,
						SorceryCardType.MAGIC,
						SorceryCardType.AURA,
						SorceryCardType.ARTIFACT,
						SorceryCardType.SITE
					],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 1
			},
			{
				filters: {
					finish: [SorceryCardFinish.STANDARD],
					type: [SorceryCardType.AVATAR],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 1
			}
		]
	},
	Foil: {
		name: translatable("sorcery.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("sorcery.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: {
				finish: [SorceryCardFinish.FOIL],
				variant: [SorceryCardVariant.BOOSTER]
			},
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("sorcery.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("sorcery.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [
			{
				filters: {
					finish: [SorceryCardFinish.FOIL],
					rarity: [SorceryRarity.ORDINARY],
					type: [
						SorceryCardType.MINION,
						SorceryCardType.MAGIC,
						SorceryCardType.AURA,
						SorceryCardType.ARTIFACT,
						SorceryCardType.SITE
					],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 4
			},
			{
				filters: {
					finish: [SorceryCardFinish.FOIL],
					rarity: [SorceryRarity.EXCEPTIONAL],
					type: [
						SorceryCardType.MINION,
						SorceryCardType.MAGIC,
						SorceryCardType.AURA,
						SorceryCardType.ARTIFACT,
						SorceryCardType.SITE
					],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 3
			},
			{
				filters: {
					finish: [SorceryCardFinish.FOIL],
					rarity: [SorceryRarity.ELITE],
					type: [
						SorceryCardType.MINION,
						SorceryCardType.MAGIC,
						SorceryCardType.AURA,
						SorceryCardType.ARTIFACT,
						SorceryCardType.SITE
					],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 2
			},
			{
				filters: {
					finish: [SorceryCardFinish.FOIL],
					rarity: [SorceryRarity.UNIQUE],
					type: [
						SorceryCardType.MINION,
						SorceryCardType.MAGIC,
						SorceryCardType.AURA,
						SorceryCardType.ARTIFACT,
						SorceryCardType.SITE
					],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 1
			},
			{
				filters: {
					finish: [SorceryCardFinish.FOIL],
					type: [SorceryCardType.AVATAR],
					variant: [SorceryCardVariant.BOOSTER]
				},
				quantity: 1
			}
		]
	},
	"Master Set": {
		name: translatable("sorcery.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("sorcery.presets.masterset.description", "A complete set with one copy of each card in both standard and foil finishes along with promo cards"),
		matchers: [{
			filters: {
				finish: [SorceryCardFinish.STANDARD],
				variant: [
					SorceryCardVariant.BOOSTER,
					SorceryCardVariant.PROMO,
					SorceryCardVariant.STORE_KIT,
					SorceryCardVariant.DRAFT_KIT,
					SorceryCardVariant.PLEDGE_PACK,
					SorceryCardVariant.BOX_TOPPER
				]
			},
			quantity: 1
		}, {
			filters: {
				finish: [SorceryCardFinish.FOIL],
				variant: [
					SorceryCardVariant.BOOSTER,
					SorceryCardVariant.PROMO,
					SorceryCardVariant.STORE_KIT,
					SorceryCardVariant.DRAFT_KIT,
					SorceryCardVariant.PLEDGE_PACK,
					SorceryCardVariant.BOX_TOPPER
				]
			},
			quantity: 1
		}]
	}
};

//#endregion
//#region src/lib/games/sorcery/sorcery.game.ts
/**
* Shared finish values for Sorcery - used by both finishes and finish attributes
*/
const sorceryFinishValues = {
	Standard: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	Foil: { label: translatable("global.attributes.finish.values.foil", "Foil") },
	Rainbow: { label: translatable("global.attributes.finish.values.rainbowfoil", "Rainbow Foil") }
};
const sorceryFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	finish: makeZodEnumFilter(SorceryCardFinish).optional(),
	finishes: makeZodEnumFilter(SorceryCardFinish).optional(),
	variant: makeZodEnumFilter(SorceryCardVariant).optional(),
	rarity: makeZodEnumFilter(SorceryRarity).optional(),
	type: makeZodEnumFilter(SorceryCardType).optional(),
	subType: makeZodEnumFilter(SorceryCardSubType).optional(),
	cost: zodNumericFilter.optional(),
	elements: makeZodEnumFilter(SorceryElement).optional(),
	airThreshold: zodNumericFilter.optional(),
	fireThreshold: zodNumericFilter.optional(),
	earthThreshold: zodNumericFilter.optional(),
	waterThreshold: zodNumericFilter.optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	attack: zodNumberFilter.optional(),
	defense: zodNumberFilter.optional(),
	life: zodNumberFilter.optional()
});
let SorceryFiltersOrder = /* @__PURE__ */ function(SorceryFiltersOrder) {
	SorceryFiltersOrder[SorceryFiltersOrder["rarity"] = 0] = "rarity";
	SorceryFiltersOrder[SorceryFiltersOrder["finish"] = 1] = "finish";
	SorceryFiltersOrder[SorceryFiltersOrder["expansion"] = 2] = "expansion";
	SorceryFiltersOrder[SorceryFiltersOrder["type"] = 3] = "type";
	SorceryFiltersOrder[SorceryFiltersOrder["subType"] = 4] = "subType";
	SorceryFiltersOrder[SorceryFiltersOrder["cost"] = 5] = "cost";
	SorceryFiltersOrder[SorceryFiltersOrder["airThreshold"] = 6] = "airThreshold";
	SorceryFiltersOrder[SorceryFiltersOrder["fireThreshold"] = 7] = "fireThreshold";
	SorceryFiltersOrder[SorceryFiltersOrder["earthThreshold"] = 8] = "earthThreshold";
	SorceryFiltersOrder[SorceryFiltersOrder["waterThreshold"] = 9] = "waterThreshold";
	return SorceryFiltersOrder;
}({});
const SorceryGameConfiguration = {
	name: "Sorcery: Contested Realms",
	shortName: "Sorcery",
	slug: "sorcery",
	icon: {
		dark: "sorcery-icon.png",
		light: "sorcery-icon.png"
	},
	image: {
		dark: "sorcery.png",
		light: "sorcery.png"
	},
	attributes: {
		name: nameAttribute,
		elements: enumAttribute(SorceryElement, {
			entity: "card",
			name: translatable("sorcery.attributes.elements.name", "Elements"),
			description: translatable("sorcery.attributes.elements.description", "Elements for this card (earth, water, fire, air)"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				air: {
					label: translatable("sorcery.attributes.elements.values.air", "Air"),
					image: "/images/sorcery/air.png"
				},
				earth: {
					label: translatable("sorcery.attributes.elements.values.earth", "Earth"),
					image: "/images/sorcery/earth.png"
				},
				fire: {
					label: translatable("sorcery.attributes.elements.values.fire", "Fire"),
					image: "/images/sorcery/fire.png"
				},
				water: {
					label: translatable("sorcery.attributes.elements.values.water", "Water"),
					image: "/images/sorcery/water.png"
				}
			}
		}),
		type: enumAttribute(SorceryCardType, {
			entity: "card",
			name: translatable("sorcery.attributes.type.name", "Type"),
			description: translatable("sorcery.attributes.type.description", "Type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				Avatar: { label: translatable("sorcery.attributes.type.values.avatar", "Avatar") },
				Minion: { label: translatable("sorcery.attributes.type.values.minion", "Minion") },
				Magic: { label: translatable("sorcery.attributes.type.values.magic", "Magic") },
				Aura: { label: translatable("sorcery.attributes.type.values.aura", "Aura") },
				Artifact: { label: translatable("sorcery.attributes.type.values.artifact", "Artifact") },
				Site: { label: translatable("sorcery.attributes.type.values.site", "Site") }
			}
		}),
		subType: enumAttribute(SorceryCardSubType, {
			entity: "card",
			name: translatable("sorcery.attributes.subType.name", "Sub type"),
			description: translatable("sorcery.attributes.subType.description", "Sub type of the card - for example weapon for Flaming sword"),
			type: "enum",
			filter: FilterComponentType.Select,
			array: true,
			values: {
				Mortal: { label: translatable("sorcery.attributes.type.values.mortal", "Mortal") },
				Beast: { label: translatable("sorcery.attributes.type.values.beast", "Beast") },
				Spirit: { label: translatable("sorcery.attributes.type.values.spirit", "Spirit") },
				Undead: { label: translatable("sorcery.attributes.type.values.undead", "Undead") },
				Demon: { label: translatable("sorcery.attributes.type.values.demon", "Demon") },
				Dragon: { label: translatable("sorcery.attributes.type.values.dragon", "Dragon") },
				Monster: { label: translatable("sorcery.attributes.type.values.monster", "Monster") },
				Sphinx: { label: translatable("sorcery.attributes.type.values.sphinx", "Sphinx") },
				Faerie: { label: translatable("sorcery.attributes.type.values.faerie", "Faerie") },
				Angel: { label: translatable("sorcery.attributes.type.values.angel", "Angel") },
				Giant: { label: translatable("sorcery.attributes.type.values.giant", "Giant") },
				Troll: { label: translatable("sorcery.attributes.type.values.troll", "Troll") },
				Dwarf: { label: translatable("sorcery.attributes.type.values.dwarf", "Dwarf") },
				Gnome: { label: translatable("sorcery.attributes.type.values.gnome", "Gnome") },
				Ogre: { label: translatable("sorcery.attributes.type.values.ogre", "Ogre") },
				Goblin: { label: translatable("sorcery.attributes.type.values.goblin", "Goblin") },
				Merfolk: { label: translatable("sorcery.attributes.type.values.merfolk", "Merfolk") },
				Armor: { label: translatable("sorcery.attributes.type.values.armor", "Armor") },
				Relic: { label: translatable("sorcery.attributes.type.values.relic", "Relic") },
				Device: { label: translatable("sorcery.attributes.type.values.device", "Device") },
				Monument: { label: translatable("sorcery.attributes.type.values.monument", "Monument") },
				Automaton: { label: translatable("sorcery.attributes.type.values.automaton", "Automaton") },
				Instruments: { label: translatable("sorcery.attributes.type.values.instruments", "Instruments") },
				Weapon: { label: translatable("sorcery.attributes.type.values.weapon", "Weapon") },
				Document: { label: translatable("sorcery.attributes.type.values.document", "Document") },
				Potion: { label: translatable("sorcery.attributes.type.values.potion", "Potion") },
				Tower: { label: translatable("sorcery.attributes.type.values.tower", "Tower") },
				Village: { label: translatable("sorcery.attributes.type.values.village", "Village") },
				Desert: { label: translatable("sorcery.attributes.type.values.desert", "Desert") },
				River: { label: translatable("sorcery.attributes.type.values.river", "River") }
			}
		}),
		cost: {
			entity: "card",
			name: translatable("sorcery.attributes.cost.name", "Cost"),
			description: translatable("sorcery.attributes.cost.description", "The mana cost to play the card"),
			type: "range",
			filter: FilterComponentType.RangeSlider,
			array: false
		},
		airThreshold: {
			entity: "card",
			name: translatable("sorcery.attributes.airThreshold.name", "Air Threshold"),
			description: translatable("sorcery.attributes.airThreshold.description", "The air threshold required to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		fireThreshold: {
			entity: "card",
			name: translatable("sorcery.attributes.fireThreshold.name", "Fire Threshold"),
			description: translatable("sorcery.attributes.fireThreshold.description", "The fire threshold required to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		earthThreshold: {
			entity: "card",
			name: translatable("sorcery.attributes.earthThreshold.name", "Earth Threshold"),
			description: translatable("sorcery.attributes.earthThreshold.description", "The earth threshold required to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		waterThreshold: {
			entity: "card",
			name: translatable("sorcery.attributes.waterThreshold.name", "Water Threshold"),
			description: translatable("sorcery.attributes.waterThreshold.description", "The water threshold required to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		finishes: enumAttribute(SorceryCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: true,
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			filter: FilterComponentType.CheckboxList,
			values: sorceryFinishValues
		}),
		finish: enumAttribute(SorceryCardFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: false,
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			filter: FilterComponentType.CheckboxList,
			values: sorceryFinishValues
		}),
		variant: enumAttribute(SorceryCardVariant, {
			entity: "variant",
			name: translatable("sorcery.attributes.variant.name", "Variant"),
			description: translatable("sorcery.attributes.variant.description", "The variant type of the card (Booster, Promo, Store Kit, etc.)"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[SorceryCardVariant.BOOSTER]: { label: translatable("sorcery.attributes.variant.values.booster", "Booster") },
				[SorceryCardVariant.PROMO]: { label: translatable("sorcery.attributes.variant.values.promo", "Promo") },
				[SorceryCardVariant.PRECONSTRUCTED_DECK]: { label: translatable("sorcery.attributes.variant.values.preconstructedDeck", "Preconstructed Deck") },
				[SorceryCardVariant.STORE_KIT]: { label: translatable("sorcery.attributes.variant.values.storeKit", "Store Kit") },
				[SorceryCardVariant.DRAFT_KIT]: { label: translatable("sorcery.attributes.variant.values.draftKit", "Draft Kit") },
				[SorceryCardVariant.KICKSTARTER]: { label: translatable("sorcery.attributes.variant.values.kickstarter", "Kickstarter") },
				[SorceryCardVariant.PLEDGE_PACK]: { label: translatable("sorcery.attributes.variant.values.pledgePack", "Pledge Pack") },
				[SorceryCardVariant.BOX_TOPPER]: { label: translatable("sorcery.attributes.variant.values.boxTopper", "Box Topper") },
				[SorceryCardVariant.WELCOME_KIT]: { label: translatable("sorcery.attributes.variant.values.welcomeKit", "Welcome Kit") },
				[SorceryCardVariant.ORGANIZED_PLAY]: { label: translatable("sorcery.attributes.variant.values.organizedPlay", "Organized Play") },
				[SorceryCardVariant.ALPHA_INVESTMENTS]: { label: translatable("sorcery.attributes.variant.values.alphaInvestments", "Alpha Investments") },
				[SorceryCardVariant.DUST]: { label: translatable("sorcery.attributes.variant.values.dust", "Dust") },
				[SorceryCardVariant.TEAM_COVENANT]: { label: translatable("sorcery.attributes.variant.values.teamCovenant", "Team Covenant") },
				[SorceryCardVariant.STAR_CITY_GAMES]: { label: translatable("sorcery.attributes.variant.values.starCityGames", "Star City Games") }
			}
		}),
		artist: {
			entity: "variant",
			name: translatable("sorcery.attributes.artist.name", "Artist"),
			description: "The artist of the card - Ex: \"Melison A. Benson\"",
			type: "string",
			array: false
		},
		flavorText: {
			entity: "variant",
			name: translatable("sorcery.attributes.flavorText.name", "Flavor text"),
			description: "The flavor text of the card",
			type: "string",
			array: false
		},
		typeText: {
			entity: "variant",
			name: translatable("sorcery.attributes.typeText.name", "Type text"),
			description: "The type text of the card",
			type: "string",
			array: false
		},
		rulesText: {
			entity: "variant",
			name: translatable("sorcery.attributes.rulesText.name", "Rules text"),
			description: "The rules text associated to this card",
			type: "string",
			array: false
		},
		rarity: enumAttribute(SorceryRarity, {
			entity: "variant",
			name: translatable("sorcery.attributes.rarity.name", "Rarity"),
			description: translatable("sorcery.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				Ordinary: { label: translatable("sorcery.attributes.rarity.values.ordinary", "Ordinary") },
				Exceptional: { label: translatable("sorcery.attributes.rarity.values.exceptional", "Exceptional") },
				Elite: { label: translatable("sorcery.attributes.rarity.values.elite", "Elite") },
				Unique: { label: translatable("sorcery.attributes.rarity.values.unique", "Unique") },
				Curio: { label: translatable("sorcery.attributes.rarity.values.curio", "Curio") }
			}
		}),
		attack: {
			entity: "card",
			name: translatable("sorcery.attributes.attack.name", "Attack"),
			description: translatable("sorcery.attributes.attack.description", "Attack value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		defense: {
			entity: "card",
			name: translatable("sorcery.attributes.defense.name", "Defense"),
			description: translatable("sorcery.attributes.defense.description", "Defense value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		life: {
			entity: "card",
			name: translatable("sorcery.attributes.life.name", "Life"),
			description: translatable("sorcery.attributes.life.description", "Life value (for Avatar cards)"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"variant",
			"rarity",
			"elements",
			"type"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: [
			"subType",
			"cost",
			"airThreshold",
			"fireThreshold",
			"earthThreshold",
			"waterThreshold",
			"attack",
			"defense",
			"life"
		]
	}],
	productDetailsOrder: {
		details: [
			"typeText",
			"rulesText",
			"flavorText",
			"artist"
		],
		statistics: [
			"rarity",
			"finish",
			"variant",
			"type",
			"subType",
			"cost",
			"elements",
			"airThreshold",
			"fireThreshold",
			"earthThreshold",
			"waterThreshold",
			"attack",
			"defense",
			"life"
		]
	},
	defaultSorting: {
		column: "name",
		direction: "asc"
	},
	listPresets: sorceryListPresets,
	basicAttributes: ["elements", "rarity"],
	supportedMarketplaces: [SupportedMarketplace.Tcgplayer],
	vendorIds: [tcgplayerVendorId],
	csvTemplateExample: {
		vendorIds: { TCGplayer: "522589" },
		cardName: "Philosopher's Stone",
		expansion: "Alpha",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/swu/swu.types.ts
let StarWarsUnlimitedRarity = /* @__PURE__ */ function(StarWarsUnlimitedRarity) {
	StarWarsUnlimitedRarity["COMMON"] = "Common";
	StarWarsUnlimitedRarity["UNCOMMON"] = "Uncommon";
	StarWarsUnlimitedRarity["RARE"] = "Rare";
	StarWarsUnlimitedRarity["LEGENDARY"] = "Legendary";
	StarWarsUnlimitedRarity["SPECIAL"] = "Special";
	return StarWarsUnlimitedRarity;
}({});
let StarWarsUnlimitedFinish = /* @__PURE__ */ function(StarWarsUnlimitedFinish) {
	StarWarsUnlimitedFinish["NORMAL"] = "Standard";
	StarWarsUnlimitedFinish["FOIL"] = "Foil";
	StarWarsUnlimitedFinish["SERIALIZED"] = "Serialized";
	StarWarsUnlimitedFinish["SERIALIZED_ROSE_GOLD"] = "Serialized Rose Gold";
	StarWarsUnlimitedFinish["SERIALIZED_GOLD"] = "Serialized Gold";
	return StarWarsUnlimitedFinish;
}({});
let StarWarsUnlimitedCardType = /* @__PURE__ */ function(StarWarsUnlimitedCardType) {
	StarWarsUnlimitedCardType["BASE"] = "Base";
	StarWarsUnlimitedCardType["CREDIT_TOKEN"] = "Credit Token";
	StarWarsUnlimitedCardType["EVENT"] = "Event";
	StarWarsUnlimitedCardType["FORCE_TOKEN"] = "Force Token";
	StarWarsUnlimitedCardType["LEADER"] = "Leader";
	StarWarsUnlimitedCardType["LEADER_UNIT"] = "Leader Unit";
	StarWarsUnlimitedCardType["TOKEN"] = "Token";
	StarWarsUnlimitedCardType["TOKEN_UNIT"] = "Token Unit";
	StarWarsUnlimitedCardType["TOKEN_UPGRADE"] = "Token Upgrade";
	StarWarsUnlimitedCardType["UNIT"] = "Unit";
	StarWarsUnlimitedCardType["UPGRADE"] = "Upgrade";
	return StarWarsUnlimitedCardType;
}({});
let StarWarsUnlimitedAspect = /* @__PURE__ */ function(StarWarsUnlimitedAspect) {
	StarWarsUnlimitedAspect["AGGRESSION"] = "Aggression";
	StarWarsUnlimitedAspect["COMMAND"] = "Command";
	StarWarsUnlimitedAspect["CUNNING"] = "Cunning";
	StarWarsUnlimitedAspect["HEROISM"] = "Heroism";
	StarWarsUnlimitedAspect["VIGILANCE"] = "Vigilance";
	StarWarsUnlimitedAspect["VILLAINY"] = "Villainy";
	return StarWarsUnlimitedAspect;
}({});
let StarWarsUnlimitedVariant = /* @__PURE__ */ function(StarWarsUnlimitedVariant) {
	StarWarsUnlimitedVariant["STANDARD"] = "Standard";
	StarWarsUnlimitedVariant["HYPERSPACE"] = "Hyperspace";
	StarWarsUnlimitedVariant["SHOWCASE"] = "Showcase";
	StarWarsUnlimitedVariant["PRESTIGE"] = "Prestige";
	return StarWarsUnlimitedVariant;
}({});
let StarWarsUnlimitedArenaType = /* @__PURE__ */ function(StarWarsUnlimitedArenaType) {
	StarWarsUnlimitedArenaType["GROUND"] = "Ground";
	StarWarsUnlimitedArenaType["SPACE"] = "Space";
	return StarWarsUnlimitedArenaType;
}({});
let StarWarsUnlimitedOrientation = /* @__PURE__ */ function(StarWarsUnlimitedOrientation) {
	StarWarsUnlimitedOrientation["PORTRAIT"] = "portrait";
	StarWarsUnlimitedOrientation["LANDSCAPE"] = "landscape";
	return StarWarsUnlimitedOrientation;
}({});

//#endregion
//#region src/lib/games/swu/swu.presets.ts
const starWarsUnlimitedListPresets = {
	Standard: {
		name: translatable("swu.presets.standard.name", "Standard Set"),
		type: ListPresetType.STANDARD,
		description: translatable("swu.presets.standard.description", "A complete set with one copy of each card in normal finish"),
		matchers: [{
			filters: { finish: [StarWarsUnlimitedFinish.NORMAL] },
			quantity: 1
		}]
	},
	Playset: {
		name: translatable("swu.presets.playset.name", "Playset"),
		type: ListPresetType.PLAYSET,
		description: translatable("swu.presets.playset.description", "A complete set with the maximum copies of each card allowed in a deck in standard finish"),
		matchers: [{
			filters: {
				finish: [StarWarsUnlimitedFinish.NORMAL],
				cardType: [StarWarsUnlimitedCardType.LEADER, StarWarsUnlimitedCardType.BASE]
			},
			quantity: 1
		}, {
			filters: {
				finish: [StarWarsUnlimitedFinish.NORMAL],
				cardType: [
					StarWarsUnlimitedCardType.EVENT,
					StarWarsUnlimitedCardType.UNIT,
					StarWarsUnlimitedCardType.UPGRADE,
					StarWarsUnlimitedCardType.LEADER_UNIT,
					StarWarsUnlimitedCardType.CREDIT_TOKEN,
					StarWarsUnlimitedCardType.FORCE_TOKEN,
					StarWarsUnlimitedCardType.TOKEN,
					StarWarsUnlimitedCardType.TOKEN_UNIT,
					StarWarsUnlimitedCardType.TOKEN_UPGRADE
				]
			},
			quantity: 3
		}]
	},
	Foil: {
		name: translatable("swu.presets.foil.name", "Foil Set"),
		type: ListPresetType.FOIL,
		description: translatable("swu.presets.foil.description", "A complete set with one copy of each card in foil finish"),
		matchers: [{
			filters: { finish: [StarWarsUnlimitedFinish.FOIL] },
			quantity: 1
		}]
	},
	"Foil Playset": {
		name: translatable("swu.presets.foilPlayset.name", "Foil Playset"),
		type: ListPresetType.FOIL_PLAYSET,
		description: translatable("swu.presets.foilPlayset.description", "A complete set with the maximum copies of each card allowed in a deck in foil finish"),
		matchers: [{
			filters: {
				finish: [StarWarsUnlimitedFinish.FOIL],
				cardType: [StarWarsUnlimitedCardType.LEADER, StarWarsUnlimitedCardType.BASE]
			},
			quantity: 1
		}, {
			filters: {
				finish: [StarWarsUnlimitedFinish.FOIL],
				cardType: [
					StarWarsUnlimitedCardType.EVENT,
					StarWarsUnlimitedCardType.UNIT,
					StarWarsUnlimitedCardType.UPGRADE,
					StarWarsUnlimitedCardType.LEADER_UNIT,
					StarWarsUnlimitedCardType.CREDIT_TOKEN,
					StarWarsUnlimitedCardType.FORCE_TOKEN,
					StarWarsUnlimitedCardType.TOKEN,
					StarWarsUnlimitedCardType.TOKEN_UNIT,
					StarWarsUnlimitedCardType.TOKEN_UPGRADE
				]
			},
			quantity: 3
		}]
	},
	"Master Set": {
		name: translatable("swu.presets.masterset.name", "Master Set"),
		type: ListPresetType.MASTERSET,
		description: translatable("swu.presets.masterset.description", "A complete set with one copy of each card in every available finish"),
		matchers: [
			{
				filters: { finish: [StarWarsUnlimitedFinish.NORMAL] },
				quantity: 1
			},
			{
				filters: { finish: [StarWarsUnlimitedFinish.FOIL] },
				quantity: 1
			},
			{
				filters: { finish: [StarWarsUnlimitedFinish.SERIALIZED] },
				quantity: 1
			},
			{
				filters: { finish: [StarWarsUnlimitedFinish.SERIALIZED_ROSE_GOLD] },
				quantity: 1
			},
			{
				filters: { finish: [StarWarsUnlimitedFinish.SERIALIZED_GOLD] },
				quantity: 1
			}
		]
	}
};

//#endregion
//#region src/lib/games/swu/swu.game.ts
/**
* Shared finish values for Star Wars: Unlimited
*/
const starWarsUnlimitedFinishValues = {
	[StarWarsUnlimitedFinish.NORMAL]: { label: translatable("swu.attributes.finish.values.standard", "Normal") },
	[StarWarsUnlimitedFinish.FOIL]: { label: translatable("swu.attributes.finish.values.foil", "Foil") },
	[StarWarsUnlimitedFinish.SERIALIZED]: { label: translatable("swu.attributes.finish.values.serialized", "Serialized") },
	[StarWarsUnlimitedFinish.SERIALIZED_ROSE_GOLD]: { label: translatable("swu.attributes.finish.values.serializedRoseGold", "Serialized Rose Gold") },
	[StarWarsUnlimitedFinish.SERIALIZED_GOLD]: { label: translatable("swu.attributes.finish.values.serializedGold", "Serialized Gold") }
};
const starWarsUnlimitedFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(StarWarsUnlimitedRarity).optional(),
	finish: makeZodEnumFilter(StarWarsUnlimitedFinish).optional(),
	finishes: makeZodEnumFilter(StarWarsUnlimitedFinish).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	cardType: makeZodEnumFilter(StarWarsUnlimitedCardType).optional(),
	variant: makeZodEnumFilter(StarWarsUnlimitedVariant).optional(),
	aspects: makeZodEnumFilter(StarWarsUnlimitedAspect).optional(),
	arenaType: makeZodEnumFilter(StarWarsUnlimitedArenaType).optional(),
	cost: zodNumericFilter.optional(),
	power: zodNumericFilter.optional(),
	hp: zodNumericFilter.optional()
});
const StarWarsUnlimitedGameConfiguration = {
	name: "Star Wars: Unlimited",
	shortName: "SWU",
	slug: "swu",
	icon: {
		dark: "swu-icon.png",
		light: "swu-icon.png"
	},
	image: {
		dark: "swu.png",
		light: "swu.png"
	},
	attributes: {
		name: nameAttribute,
		finishes: enumAttribute(StarWarsUnlimitedFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: starWarsUnlimitedFinishValues
		}),
		finish: enumAttribute(StarWarsUnlimitedFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			description: translatable("global.attributes.finish.description", "Finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: starWarsUnlimitedFinishValues
		}),
		rarity: enumAttribute(StarWarsUnlimitedRarity, {
			entity: "variant",
			name: translatable("global.attributes.rarity.name", "Rarity"),
			description: translatable("global.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[StarWarsUnlimitedRarity.COMMON]: { label: translatable("swu.attributes.rarity.values.common", "Common") },
				[StarWarsUnlimitedRarity.UNCOMMON]: { label: translatable("swu.attributes.rarity.values.uncommon", "Uncommon") },
				[StarWarsUnlimitedRarity.RARE]: { label: translatable("swu.attributes.rarity.values.rare", "Rare") },
				[StarWarsUnlimitedRarity.LEGENDARY]: { label: translatable("swu.attributes.rarity.values.legendary", "Legendary") },
				[StarWarsUnlimitedRarity.SPECIAL]: { label: translatable("swu.attributes.rarity.values.special", "Special") }
			}
		}),
		printNumber: printNumberAttribute,
		cardType: enumAttribute(StarWarsUnlimitedCardType, {
			entity: "card",
			name: translatable("swu.attributes.cardType.name", "Card Type"),
			description: translatable("swu.attributes.cardType.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[StarWarsUnlimitedCardType.BASE]: { label: translatable("swu.attributes.cardType.values.base", "Base") },
				[StarWarsUnlimitedCardType.CREDIT_TOKEN]: { label: translatable("swu.attributes.cardType.values.creditToken", "Credit Token") },
				[StarWarsUnlimitedCardType.EVENT]: { label: translatable("swu.attributes.cardType.values.event", "Event") },
				[StarWarsUnlimitedCardType.FORCE_TOKEN]: { label: translatable("swu.attributes.cardType.values.forceToken", "Force Token") },
				[StarWarsUnlimitedCardType.LEADER]: { label: translatable("swu.attributes.cardType.values.leader", "Leader") },
				[StarWarsUnlimitedCardType.LEADER_UNIT]: { label: translatable("swu.attributes.cardType.values.leaderUnit", "Leader Unit") },
				[StarWarsUnlimitedCardType.TOKEN]: { label: translatable("swu.attributes.cardType.values.token", "Token") },
				[StarWarsUnlimitedCardType.TOKEN_UNIT]: { label: translatable("swu.attributes.cardType.values.tokenUnit", "Token Unit") },
				[StarWarsUnlimitedCardType.TOKEN_UPGRADE]: { label: translatable("swu.attributes.cardType.values.tokenUpgrade", "Token Upgrade") },
				[StarWarsUnlimitedCardType.UNIT]: { label: translatable("swu.attributes.cardType.values.unit", "Unit") },
				[StarWarsUnlimitedCardType.UPGRADE]: { label: translatable("swu.attributes.cardType.values.upgrade", "Upgrade") }
			}
		}),
		variant: enumAttribute(StarWarsUnlimitedVariant, {
			entity: "variant",
			name: translatable("swu.attributes.variant.name", "Variant"),
			description: translatable("swu.attributes.variant.description", "The variant type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[StarWarsUnlimitedVariant.STANDARD]: { label: translatable("swu.attributes.variant.values.standard", "Standard") },
				[StarWarsUnlimitedVariant.HYPERSPACE]: { label: translatable("swu.attributes.variant.values.hyperspace", "Hyperspace") },
				[StarWarsUnlimitedVariant.SHOWCASE]: { label: translatable("swu.attributes.variant.values.showcase", "Showcase") },
				[StarWarsUnlimitedVariant.PRESTIGE]: { label: translatable("swu.attributes.variant.values.prestige", "Prestige") }
			}
		}),
		aspects: enumAttribute(StarWarsUnlimitedAspect, {
			entity: "card",
			name: translatable("swu.attributes.aspects.name", "Aspect"),
			description: translatable("swu.attributes.aspects.description", "The aspect (color) of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[StarWarsUnlimitedAspect.AGGRESSION]: {
					label: translatable("swu.attributes.aspects.values.aggression", "Aggression"),
					image: "/images/swu/aggression.png"
				},
				[StarWarsUnlimitedAspect.COMMAND]: {
					label: translatable("swu.attributes.aspects.values.command", "Command"),
					image: "/images/swu/command.png"
				},
				[StarWarsUnlimitedAspect.CUNNING]: {
					label: translatable("swu.attributes.aspects.values.cunning", "Cunning"),
					image: "/images/swu/cunning.png"
				},
				[StarWarsUnlimitedAspect.HEROISM]: {
					label: translatable("swu.attributes.aspects.values.heroism", "Heroism"),
					image: "/images/swu/heroism.png"
				},
				[StarWarsUnlimitedAspect.VIGILANCE]: {
					label: translatable("swu.attributes.aspects.values.vigilance", "Vigilance"),
					image: "/images/swu/vigilance.png"
				},
				[StarWarsUnlimitedAspect.VILLAINY]: {
					label: translatable("swu.attributes.aspects.values.villainy", "Villainy"),
					image: "/images/swu/villainy.png"
				}
			}
		}),
		arenaType: enumAttribute(StarWarsUnlimitedArenaType, {
			entity: "card",
			name: translatable("swu.attributes.arenaType.name", "Arena Type"),
			description: translatable("swu.attributes.arenaType.description", "The arena the card is played in"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: {
				[StarWarsUnlimitedArenaType.GROUND]: { label: translatable("swu.attributes.arenaType.values.ground", "Ground") },
				[StarWarsUnlimitedArenaType.SPACE]: { label: translatable("swu.attributes.arenaType.values.space", "Space") }
			}
		}),
		orientation: enumAttribute(StarWarsUnlimitedOrientation, {
			entity: "card",
			name: translatable("swu.attributes.orientation.name", "Orientation"),
			description: translatable("swu.attributes.orientation.description", "The orientation of the card image"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			hidden: true,
			values: {
				[StarWarsUnlimitedOrientation.PORTRAIT]: { label: translatable("swu.attributes.orientation.values.portrait", "Portrait") },
				[StarWarsUnlimitedOrientation.LANDSCAPE]: { label: translatable("swu.attributes.orientation.values.landscape", "Landscape") }
			}
		}),
		cost: {
			entity: "card",
			name: translatable("swu.attributes.cost.name", "Cost"),
			description: translatable("swu.attributes.cost.description", "Cost to play the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		power: {
			entity: "card",
			name: translatable("swu.attributes.power.name", "Power"),
			description: translatable("swu.attributes.power.description", "Attack power of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		hp: {
			entity: "card",
			name: translatable("swu.attributes.hp.name", "HP"),
			description: translatable("swu.attributes.hp.description", "Hit points of the card"),
			type: "range",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		description: {
			entity: "variant",
			name: translatable("swu.attributes.description.name", "Description"),
			description: translatable("swu.attributes.description.description", "Card ability text"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"finishes",
			"variant",
			"cardType",
			"aspects"
		]
	}],
	advancedFilters: [{
		label: "Card Properties",
		filters: [
			"arenaType",
			"cost",
			"power",
			"hp"
		]
	}],
	productDetailsOrder: {
		details: ["description"],
		statistics: [
			"cardType",
			"aspects",
			"arenaType",
			"rarity",
			"cost",
			"power",
			"hp"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: starWarsUnlimitedListPresets,
	basicAttributes: ["rarity"],
	supportedMarketplaces: [SupportedMarketplace.Tcgplayer, SupportedMarketplace.Cardmarket],
	vendorIds: [cardmarketVendorId, tcgplayerVendorId],
	csvTemplateExample: {
		vendorIds: {
			Cardmarket: "875768",
			TCGplayer: "672724"
		},
		printNumber: "80/264",
		cardName: "Luke Skywalker - Profit or Be Destroyed",
		expansion: "A Lawless Time",
		expansionCode: "law",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/wankul/wankul.presets.ts
const wankulListPresets = { Standard: {
	name: translatable("wankul.presets.standard.name", "Standard Set"),
	type: ListPresetType.STANDARD,
	description: translatable("wankul.presets.standard.description", "A complete set with one copy of each card in standard finish"),
	matchers: [{
		filters: {},
		quantity: 1
	}]
} };

//#endregion
//#region src/lib/games/wankul/wankul.types.ts
let WankulCardFinish = /* @__PURE__ */ function(WankulCardFinish) {
	WankulCardFinish["STANDARD"] = "Standard";
	WankulCardFinish["FOIL"] = "Foil";
	return WankulCardFinish;
}({});
let WankulRarity = /* @__PURE__ */ function(WankulRarity) {
	WankulRarity["TERRAIN"] = "T";
	WankulRarity["COMMUNE"] = "C";
	WankulRarity["PEU_COMMUNE"] = "UC";
	WankulRarity["RARE"] = "R";
	WankulRarity["ULTRA_RARE_HOLO_1"] = "UR1";
	WankulRarity["ULTRA_RARE_HOLO_2"] = "UR2";
	WankulRarity["LEGENDAIRE_BRONZE"] = "L-B";
	WankulRarity["LEGENDAIRE_ARGENT"] = "L-A";
	WankulRarity["LEGENDAIRE_OR"] = "L-O";
	WankulRarity["GAGNANT_TICKET_OR"] = "T-OR";
	WankulRarity["PGW_2023"] = "PGW-23";
	WankulRarity["PGW_2024"] = "PGW-24";
	WankulRarity["NOEL_2023"] = "NOEL-23";
	WankulRarity["STARTER_PACK_CIVILISATIONS"] = "SP-CIV";
	WankulRarity["STARTER_PACK_LEGENDES"] = "SP-LEG";
	WankulRarity["STARTER_PACK_CARRIERES"] = "SP-CAR";
	WankulRarity["STARTER_PACK_POP_CULTURE"] = "SP-POP";
	WankulRarity["STARTER_PACK_TV"] = "SP-TV";
	WankulRarity["STARTER_PACK_JEUX_VIDEO"] = "SP-JV";
	WankulRarity["GEMMES_PACK"] = "G-P";
	WankulRarity["EDITION_SPECIALE"] = "E-D";
	WankulRarity["EDITION_GOLD"] = "E-G";
	return WankulRarity;
}({});
let WankulRarityFullName = /* @__PURE__ */ function(WankulRarityFullName) {
	WankulRarityFullName["TERRAIN"] = "Terrain";
	WankulRarityFullName["COMMUNE"] = "Commune";
	WankulRarityFullName["PEU_COMMUNE"] = "Peu Commune";
	WankulRarityFullName["RARE"] = "Rare";
	WankulRarityFullName["ULTRA_RARE_HOLO_1"] = "Ultra rare holo 1";
	WankulRarityFullName["ULTRA_RARE_HOLO_2"] = "Ultra rare holo 2";
	WankulRarityFullName["LEGENDAIRE_BRONZE"] = "Légendaire Bronze";
	WankulRarityFullName["LEGENDAIRE_ARGENT"] = "Légendaire Argent";
	WankulRarityFullName["LEGENDAIRE_OR"] = "Légendaire Or";
	WankulRarityFullName["GAGNANT_TICKET_OR"] = "Gagnant Ticket Or";
	WankulRarityFullName["PGW_2023"] = "PGW 2023";
	WankulRarityFullName["PGW_2024"] = "PGW 2024";
	WankulRarityFullName["NOEL_2023"] = "Noël 2023";
	WankulRarityFullName["STARTER_PACK_CIVILISATIONS"] = "Starter Pack Civilisations";
	WankulRarityFullName["STARTER_PACK_LEGENDES"] = "Starter Pack Légendes";
	WankulRarityFullName["STARTER_PACK_CARRIERES"] = "Starter Pack Carrières";
	WankulRarityFullName["STARTER_PACK_POP_CULTURE"] = "Starter Pack Pop-Culture";
	WankulRarityFullName["STARTER_PACK_TV"] = "Starter Pack TV";
	WankulRarityFullName["STARTER_PACK_JEUX_VIDEO"] = "Starter Pack Jeux Vidéo";
	WankulRarityFullName["GEMMES_PACK"] = "Gemmes Pack";
	WankulRarityFullName["EDITION_SPECIALE"] = "Edition Speciale";
	WankulRarityFullName["EDITION_GOLD"] = "Edition Gold";
	return WankulRarityFullName;
}({});
let WankulEffigy = /* @__PURE__ */ function(WankulEffigy) {
	WankulEffigy["TERRAIN"] = "Terrain";
	WankulEffigy["LAINK"] = "Laink";
	WankulEffigy["TERRACID"] = "Terracid";
	WankulEffigy["GUEST"] = "Guest";
	WankulEffigy["RANDOM"] = "Random";
	WankulEffigy["GAGNANT_TICKET_OR"] = "Gagnant Ticket Or";
	return WankulEffigy;
}({});

//#endregion
//#region src/lib/games/wankul/wankul.game.ts
/**
* Shared finish values for Wankul - used by both finishes and finish attributes
*/
const wankulFinishValues = {
	Standard: { label: translatable("global.attributes.finish.values.standard", "Standard") },
	Foil: { label: translatable("global.attributes.finish.values.foil", "Foil") }
};
const wankulFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(WankulRarity).optional(),
	finish: makeZodEnumFilter(WankulCardFinish).optional(),
	finishes: makeZodEnumFilter(WankulCardFinish).optional(),
	cost: zodNumberFilter.optional(),
	force: zodNumberFilter.optional(),
	effigyName: makeZodEnumFilter(WankulEffigy).optional(),
	languages: makeZodEnumFilter(Languages).optional()
});
const WankulGameConfiguration = {
	name: "Wankul",
	shortName: "Wankul",
	slug: "wankul",
	icon: {
		dark: "wankul-icon.png",
		light: "wankul-icon.png"
	},
	image: {
		dark: "wankul.png",
		light: "wankul.png"
	},
	attributes: {
		name: nameAttribute,
		cost: {
			entity: "card",
			name: translatable("wankul.attributes.cost.name", "Cost"),
			description: translatable("wankul.attributes.cost.description", "The cost to play the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		force: {
			entity: "card",
			name: translatable("wankul.attributes.force.name", "Force"),
			description: translatable("wankul.attributes.force.description", "The force value of the card"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		rules: {
			entity: "card",
			name: translatable("wankul.attributes.rules.name", "Rules"),
			description: translatable("wankul.attributes.rules.description", "The rules text of the card"),
			type: "string",
			array: false
		},
		combo: {
			entity: "card",
			name: translatable("wankul.attributes.combo.name", "Combo"),
			description: translatable("wankul.attributes.combo.description", "The combo text of the card"),
			type: "string",
			array: false
		},
		quote: {
			entity: "card",
			name: translatable("wankul.attributes.quote.name", "Quote"),
			description: translatable("wankul.attributes.quote.description", "The flavor text of the card"),
			type: "string",
			array: false
		},
		finishes: enumAttribute(WankulCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: true,
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			filter: FilterComponentType.CheckboxList,
			values: wankulFinishValues
		}),
		finish: enumAttribute(WankulCardFinish, {
			entity: "inventory",
			name: translatable("global.attributes.finish.name", "Finish"),
			type: "enum",
			array: false,
			description: translatable("global.attributes.finish.description", "Finish of the card (foil/non-foil)"),
			filter: FilterComponentType.CheckboxList,
			values: wankulFinishValues
		}),
		cardSetId: {
			entity: "variant",
			name: "Card Set ID",
			description: "The id of the card in the set",
			type: "string",
			array: false
		},
		rarity: enumAttribute(WankulRarity, {
			entity: "variant",
			name: translatable("wankul.attributes.rarity.name", "Rarity"),
			description: translatable("wankul.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				T: { label: translatable("wankul.attributes.rarity.values.t", WankulRarityFullName.TERRAIN) },
				C: { label: translatable("wankul.attributes.rarity.values.c", WankulRarityFullName.COMMUNE) },
				UC: { label: translatable("wankul.attributes.rarity.values.uc", WankulRarityFullName.PEU_COMMUNE) },
				R: { label: translatable("wankul.attributes.rarity.values.r", WankulRarityFullName.RARE) },
				UR1: { label: translatable("wankul.attributes.rarity.values.ur1", WankulRarityFullName.ULTRA_RARE_HOLO_1) },
				UR2: { label: translatable("wankul.attributes.rarity.values.ur2", WankulRarityFullName.ULTRA_RARE_HOLO_2) },
				"L-B": { label: translatable("wankul.attributes.rarity.values.lb", WankulRarityFullName.LEGENDAIRE_BRONZE) },
				"L-A": { label: translatable("wankul.attributes.rarity.values.la", WankulRarityFullName.LEGENDAIRE_ARGENT) },
				"L-O": { label: translatable("wankul.attributes.rarity.values.lo", WankulRarityFullName.LEGENDAIRE_OR) },
				"T-OR": { label: translatable("wankul.attributes.rarity.values.tor", WankulRarityFullName.GAGNANT_TICKET_OR) },
				"PGW-23": { label: translatable("wankul.attributes.rarity.values.pgw23", WankulRarityFullName.PGW_2023) },
				"PGW-24": { label: translatable("wankul.attributes.rarity.values.pgw24", WankulRarityFullName.PGW_2024) },
				"NOEL-23": { label: translatable("wankul.attributes.rarity.values.noel23", WankulRarityFullName.NOEL_2023) },
				"SP-CIV": { label: translatable("wankul.attributes.rarity.values.spciv", WankulRarityFullName.STARTER_PACK_CIVILISATIONS) },
				"SP-LEG": { label: translatable("wankul.attributes.rarity.values.spleg", WankulRarityFullName.STARTER_PACK_LEGENDES) },
				"SP-CAR": { label: translatable("wankul.attributes.rarity.values.spcar", WankulRarityFullName.STARTER_PACK_CARRIERES) },
				"SP-POP": { label: translatable("wankul.attributes.rarity.values.sppop", WankulRarityFullName.STARTER_PACK_POP_CULTURE) },
				"SP-TV": { label: translatable("wankul.attributes.rarity.values.sptv", WankulRarityFullName.STARTER_PACK_TV) },
				"SP-JV": { label: translatable("wankul.attributes.rarity.values.spjv", WankulRarityFullName.STARTER_PACK_JEUX_VIDEO) },
				"G-P": { label: translatable("wankul.attributes.rarity.values.gp", WankulRarityFullName.GEMMES_PACK) },
				"E-D": { label: translatable("wankul.attributes.rarity.values.ed", WankulRarityFullName.EDITION_SPECIALE) },
				"E-G": { label: translatable("wankul.attributes.rarity.values.eg", WankulRarityFullName.EDITION_GOLD) }
			}
		}),
		artistName: {
			entity: "variant",
			name: translatable("wankul.attributes.artistName.name", "Artist"),
			description: translatable("wankul.attributes.artistName.description", "The artist who created the card art"),
			type: "string",
			array: false
		},
		effigyName: enumAttribute(WankulEffigy, {
			entity: "variant",
			name: translatable("wankul.attributes.effigyName.name", "Effigy"),
			description: translatable("wankul.attributes.effigyName.description", "The effigy featured on the card"),
			type: "enum",
			selectable: true,
			array: true,
			filter: FilterComponentType.ButtonGroup,
			values: {
				Laink: { label: translatable("wankul.attributes.effigyName.values.laink", "Laink") },
				Terracid: { label: translatable("wankul.attributes.effigyName.values.terracid", "Terracid") },
				Guest: { label: translatable("wankul.attributes.effigyName.values.guest", "Guest") },
				Random: { label: translatable("wankul.attributes.effigyName.values.random", "Random") },
				"Gagnant Ticket Or": { label: translatable("wankul.attributes.effigyName.values.gagnant_ticket_or", "Golden Ticket Winner") },
				Terrain: { label: translatable("wankul.attributes.effigyName.values.terrain", "Terrain") }
			}
		}),
		diamonds: {
			entity: "variant",
			name: translatable("wankul.attributes.diamonds.name", "Diamonds"),
			description: translatable("wankul.attributes.diamonds.description", "The diamond positions on the card"),
			type: "string",
			array: false
		},
		printNumber: printNumberAttribute
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"finish",
			"rarity",
			"effigyName",
			"cost",
			"force"
		]
	}],
	advancedFilters: [{
		label: "Advanced filters",
		filters: ["cost", "force"]
	}],
	productDetailsOrder: {
		details: [
			"rules",
			"combo",
			"quote"
		],
		statistics: ["cost", "force"]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: wankulListPresets,
	basicAttributes: ["rarity"],
	supportedMarketplaces: [],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "1",
		cardName: "Sardoche",
		expansion: "Wankul Saison 1",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "French",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/yugioh/yugioh.types.ts
const YUGIOH_TCG_LANGUAGES = [
	Languages.English,
	Languages.French,
	Languages.German,
	Languages.Italian,
	Languages.Spanish,
	Languages.PortugueseBrazil
];
const YUGIOH_OCG_LANGUAGES = [Languages.Japanese, Languages.Korean];
const YUGIOH_LANGUAGES = [...YUGIOH_TCG_LANGUAGES, ...YUGIOH_OCG_LANGUAGES];
let YugiohCardFinish = /* @__PURE__ */ function(YugiohCardFinish) {
	YugiohCardFinish["STANDARD"] = "Standard";
	return YugiohCardFinish;
}({});
let YugiohEdition = /* @__PURE__ */ function(YugiohEdition) {
	YugiohEdition["FIRST_EDITION"] = "1st Edition";
	YugiohEdition["UNLIMITED"] = "Unlimited";
	YugiohEdition["LIMITED"] = "Limited";
	return YugiohEdition;
}({});
let YugiohVariant = /* @__PURE__ */ function(YugiohVariant) {
	YugiohVariant["ALTERNATE_ART"] = "Alternate Art";
	YugiohVariant["NEW_ART"] = "New Art";
	YugiohVariant["ART_7"] = "Art 7";
	YugiohVariant["ART_8"] = "Art 8";
	YugiohVariant["HERO_ART"] = "HERO Art";
	YugiohVariant["EXTENDED_ART"] = "Extended Art";
	YugiohVariant["JAPANESE_ARTWORK"] = "Japanese Artwork";
	YugiohVariant["BLUE"] = "Blue";
	YugiohVariant["GREEN"] = "Green";
	YugiohVariant["PURPLE"] = "Purple";
	YugiohVariant["RED"] = "Red";
	YugiohVariant["SILVER"] = "Silver";
	YugiohVariant["BRONZE"] = "Bronze";
	YugiohVariant["YELLOW"] = "Yellow";
	YugiohVariant["ORANGE"] = "Orange";
	YugiohVariant["BLACK"] = "Black";
	YugiohVariant["OTS_STAMP"] = "OTS Stamp";
	YugiohVariant["JUDGE_STAMP"] = "Judge Stamp";
	YugiohVariant["REGIONAL_STAMP"] = "Regional Stamp";
	YugiohVariant["STAMP"] = "Stamp";
	YugiohVariant["MISPRINT"] = "Misprint";
	YugiohVariant["OVERSIZED"] = "Oversized";
	YugiohVariant["LIMITED_EDITION"] = "Limited Edition";
	YugiohVariant["REPRINT"] = "Reprint";
	YugiohVariant["CLASSIC_STYLE"] = "Classic Style";
	YugiohVariant["LOGO"] = "Logo";
	YugiohVariant["ORIGINAL_LAYOUT"] = "Original Layout";
	return YugiohVariant;
}({});
let YugiohRarity = /* @__PURE__ */ function(YugiohRarity) {
	YugiohRarity["COMMON"] = "Common";
	YugiohRarity["RARE"] = "Rare";
	YugiohRarity["SUPER_RARE"] = "Super Rare";
	YugiohRarity["ULTRA_RARE"] = "Ultra Rare";
	YugiohRarity["SECRET_RARE"] = "Secret Rare";
	YugiohRarity["ULTIMATE_RARE"] = "Ultimate Rare";
	YugiohRarity["GHOST_RARE"] = "Ghost Rare";
	YugiohRarity["GHOST_GOLD_RARE"] = "Ghost/Gold Rare";
	YugiohRarity["GOLD_RARE"] = "Gold Rare";
	YugiohRarity["GOLD_SECRET_RARE"] = "Gold Secret Rare";
	YugiohRarity["PREMIUM_GOLD_RARE"] = "Premium Gold Rare";
	YugiohRarity["PLATINUM_RARE"] = "Platinum Rare";
	YugiohRarity["PLATINUM_SECRET_RARE"] = "Platinum Secret Rare";
	YugiohRarity["PRISMATIC_SECRET_RARE"] = "Prismatic Secret Rare";
	YugiohRarity["PRISMATIC_ULTIMATE_RARE"] = "Prismatic Ultimate Rare";
	YugiohRarity["PRISMATIC_COLLECTORS_RARE"] = "Prismatic Collector's Rare";
	YugiohRarity["QUARTER_CENTURY_SECRET_RARE"] = "Quarter Century Secret Rare";
	YugiohRarity["TEN_THOUSAND_SECRET_RARE"] = "10000 Secret Rare";
	YugiohRarity["STARLIGHT_RARE"] = "Starlight Rare";
	YugiohRarity["COLLECTORS_RARE"] = "Collector's Rare";
	YugiohRarity["EXTRA_SECRET_RARE"] = "Extra Secret Rare";
	YugiohRarity["ULTRA_SECRET_RARE"] = "Ultra Secret Rare";
	YugiohRarity["STARFOIL_RARE"] = "Starfoil Rare";
	YugiohRarity["SHATTERFOIL_RARE"] = "Shatterfoil Rare";
	YugiohRarity["MOSAIC_RARE"] = "Mosaic Rare";
	YugiohRarity["HOLOGRAPHIC_RARE"] = "Holographic Rare";
	YugiohRarity["PARALLEL_RARE"] = "Parallel Rare";
	YugiohRarity["NORMAL_PARALLEL_RARE"] = "Normal Parallel Rare";
	YugiohRarity["SUPER_PARALLEL_RARE"] = "Super Parallel Rare";
	YugiohRarity["ULTRA_PARALLEL_RARE"] = "Ultra Parallel Rare";
	YugiohRarity["SECRET_PARALLEL_RARE"] = "Secret Parallel Rare";
	YugiohRarity["DUEL_TERMINAL_NORMAL_PARALLEL_RARE"] = "Duel Terminal Normal Parallel Rare";
	YugiohRarity["DUEL_TERMINAL_RARE_PARALLEL_RARE"] = "Duel Terminal Rare Parallel Rare";
	YugiohRarity["DUEL_TERMINAL_SUPER_PARALLEL_RARE"] = "Duel Terminal Super Parallel Rare";
	YugiohRarity["DUEL_TERMINAL_ULTRA_PARALLEL_RARE"] = "Duel Terminal Ultra Parallel Rare";
	YugiohRarity["DUEL_TERMINAL_SECRET_PARALLEL_RARE"] = "Duel Terminal Secret Parallel Rare";
	YugiohRarity["DUEL_TERMINAL_TECHNOLOGY_COMMON"] = "Duel Terminal Technology Common";
	YugiohRarity["DUEL_TERMINAL_TECHNOLOGY_ULTRA_RARE"] = "Duel Terminal Technology Ultra Rare";
	YugiohRarity["EMBLAZONED_RARE"] = "Emblazoned Rare";
	YugiohRarity["EMBLAZONED_ULTRA_RARE"] = "Emblazoned Ultra Rare";
	YugiohRarity["EMBLAZONED_SECRET_RARE"] = "Emblazoned Secret Rare";
	YugiohRarity["ULTRA_PHARAOHS_RARE"] = "Ultra Pharaoh's Rare";
	YugiohRarity["SECRET_PHARAOHS_RARE"] = "Secret Pharaoh's Rare";
	YugiohRarity["MILLENNIUM_RARE"] = "Millennium Rare";
	YugiohRarity["MILLENNIUM_SUPER_RARE"] = "Millennium Super Rare";
	YugiohRarity["MILLENNIUM_ULTRA_RARE"] = "Millennium Ultra Rare";
	YugiohRarity["MILLENNIUM_SECRET_RARE"] = "Millennium Secret Rare";
	YugiohRarity["MILLENNIUM_GOLD_RARE"] = "Millennium Gold Rare";
	YugiohRarity["GRAND_MASTER_RARE"] = "Grand Master Rare";
	YugiohRarity["TWENTIETH_SECRET_RARE"] = "20th Secret Rare";
	YugiohRarity["KAIBA_CORPORATION_COMMON"] = "Kaiba Corporation Common";
	YugiohRarity["KAIBA_CORPORATION_RARE"] = "Kaiba Corporation Rare";
	YugiohRarity["KAIBA_CORPORATION_ULTRA_RARE"] = "Kaiba Corporation Ultra Rare";
	YugiohRarity["SHORT_PRINT"] = "Short Print";
	YugiohRarity["SUPER_SHORT_PRINT"] = "Super Short Print";
	YugiohRarity["SPEED_DUEL_SKILL"] = "Speed Duel Skill";
	YugiohRarity["TOKEN"] = "Token";
	YugiohRarity["PROMO"] = "Promo";
	return YugiohRarity;
}({});
let YugiohBanStatus = /* @__PURE__ */ function(YugiohBanStatus) {
	YugiohBanStatus["FORBIDDEN"] = "Forbidden";
	YugiohBanStatus["LIMITED"] = "Limited";
	YugiohBanStatus["SEMI_LIMITED"] = "Semi-Limited";
	return YugiohBanStatus;
}({});
let YugiohLinkMarker = /* @__PURE__ */ function(YugiohLinkMarker) {
	YugiohLinkMarker["TOP_LEFT"] = "Top-Left";
	YugiohLinkMarker["TOP"] = "Top";
	YugiohLinkMarker["TOP_RIGHT"] = "Top-Right";
	YugiohLinkMarker["LEFT"] = "Left";
	YugiohLinkMarker["RIGHT"] = "Right";
	YugiohLinkMarker["BOTTOM_LEFT"] = "Bottom-Left";
	YugiohLinkMarker["BOTTOM"] = "Bottom";
	YugiohLinkMarker["BOTTOM_RIGHT"] = "Bottom-Right";
	return YugiohLinkMarker;
}({});
let YugiohCardType = /* @__PURE__ */ function(YugiohCardType) {
	YugiohCardType["EFFECT_MONSTER"] = "Effect Monster";
	YugiohCardType["FLIP_EFFECT_MONSTER"] = "Flip Effect Monster";
	YugiohCardType["FLIP_TUNER_EFFECT_MONSTER"] = "Flip Tuner Effect Monster";
	YugiohCardType["FUSION_MONSTER"] = "Fusion Monster";
	YugiohCardType["GEMINI_MONSTER"] = "Gemini Monster";
	YugiohCardType["LINK_MONSTER"] = "Link Monster";
	YugiohCardType["NORMAL_MONSTER"] = "Normal Monster";
	YugiohCardType["NORMAL_TUNER_MONSTER"] = "Normal Tuner Monster";
	YugiohCardType["PENDULUM_EFFECT_FUSION_MONSTER"] = "Pendulum Effect Fusion Monster";
	YugiohCardType["PENDULUM_EFFECT_MONSTER"] = "Pendulum Effect Monster";
	YugiohCardType["PENDULUM_EFFECT_RITUAL_MONSTER"] = "Pendulum Effect Ritual Monster";
	YugiohCardType["PENDULUM_FLIP_EFFECT_MONSTER"] = "Pendulum Flip Effect Monster";
	YugiohCardType["PENDULUM_NORMAL_MONSTER"] = "Pendulum Normal Monster";
	YugiohCardType["PENDULUM_TUNER_EFFECT_MONSTER"] = "Pendulum Tuner Effect Monster";
	YugiohCardType["RITUAL_EFFECT_MONSTER"] = "Ritual Effect Monster";
	YugiohCardType["RITUAL_MONSTER"] = "Ritual Monster";
	YugiohCardType["SKILL_CARD"] = "Skill Card";
	YugiohCardType["SPELL_CARD"] = "Spell Card";
	YugiohCardType["SPIRIT_MONSTER"] = "Spirit Monster";
	YugiohCardType["SYNCHRO_MONSTER"] = "Synchro Monster";
	YugiohCardType["SYNCHRO_PENDULUM_EFFECT_MONSTER"] = "Synchro Pendulum Effect Monster";
	YugiohCardType["SYNCHRO_TUNER_MONSTER"] = "Synchro Tuner Monster";
	YugiohCardType["TOKEN"] = "Token";
	YugiohCardType["TOON_MONSTER"] = "Toon Monster";
	YugiohCardType["TRAP_CARD"] = "Trap Card";
	YugiohCardType["TUNER_MONSTER"] = "Tuner Monster";
	YugiohCardType["UNION_EFFECT_MONSTER"] = "Union Effect Monster";
	YugiohCardType["XYZ_MONSTER"] = "XYZ Monster";
	YugiohCardType["XYZ_PENDULUM_EFFECT_MONSTER"] = "XYZ Pendulum Effect Monster";
	return YugiohCardType;
}({});
/**
* YGOPRODeck `race`: monster type for monsters, spell/trap type for
* spells and traps, character name (as truncated upstream) for Skill cards.
*/
let YugiohRace = /* @__PURE__ */ function(YugiohRace) {
	YugiohRace["ABIDOS_THE_TH"] = "Abidos the Th";
	YugiohRace["ADRIAN_GECKO"] = "Adrian Gecko";
	YugiohRace["ALEXIS_RHODES"] = "Alexis Rhodes";
	YugiohRace["AMNAEL"] = "Amnael";
	YugiohRace["ANDREW"] = "Andrew";
	YugiohRace["AQUA"] = "Aqua";
	YugiohRace["ARKANA"] = "Arkana";
	YugiohRace["ASTER_PHOENIX"] = "Aster Phoenix";
	YugiohRace["AXEL_BRODIE"] = "Axel Brodie";
	YugiohRace["BASTION_MISAW"] = "Bastion Misaw";
	YugiohRace["BEAST"] = "Beast";
	YugiohRace["BEAST_WARRIOR"] = "Beast-Warrior";
	YugiohRace["BONZ"] = "Bonz";
	YugiohRace["CAMULA"] = "Camula";
	YugiohRace["CHAZZ_PRINCET"] = "Chazz Princet";
	YugiohRace["CHRISTINE"] = "Christine";
	YugiohRace["CHUMLEY_HUFFI"] = "Chumley Huffi";
	YugiohRace["CONTINUOUS"] = "Continuous";
	YugiohRace["COUNTER"] = "Counter";
	YugiohRace["CREATOR_GOD"] = "Creator God";
	YugiohRace["CYBERSE"] = "Cyberse";
	YugiohRace["DAVID"] = "David";
	YugiohRace["DINOSAUR"] = "Dinosaur";
	YugiohRace["DIVINE_BEAST"] = "Divine-Beast";
	YugiohRace["DON_ZALOOG"] = "Don Zaloog";
	YugiohRace["DR_VELLIAN_C"] = "Dr. Vellian C";
	YugiohRace["DRAGON"] = "Dragon";
	YugiohRace["EMMA"] = "Emma";
	YugiohRace["EQUIP"] = "Equip";
	YugiohRace["ESPA_ROBA"] = "Espa Roba";
	YugiohRace["FAIRY"] = "Fairy";
	YugiohRace["FIELD"] = "Field";
	YugiohRace["FIEND"] = "Fiend";
	YugiohRace["FISH"] = "Fish";
	YugiohRace["ILLUSION"] = "Illusion";
	YugiohRace["INSECT"] = "Insect";
	YugiohRace["ISHIZU"] = "Ishizu";
	YugiohRace["ISHIZU_ISHTAR"] = "Ishizu Ishtar";
	YugiohRace["JADEN_YUKI"] = "Jaden Yuki";
	YugiohRace["JESSE_ANDERSO"] = "Jesse Anderso";
	YugiohRace["JOEY"] = "Joey";
	YugiohRace["JOEY_WHEELER"] = "Joey Wheeler";
	YugiohRace["KAGEMARU"] = "Kagemaru";
	YugiohRace["KAIBA"] = "Kaiba";
	YugiohRace["KEITH"] = "Keith";
	YugiohRace["LUMIS_UMBRA"] = "Lumis Umbra";
	YugiohRace["LUMIS_AND_UMB"] = "Lumis and Umb";
	YugiohRace["MACHINE"] = "Machine";
	YugiohRace["MAI"] = "Mai";
	YugiohRace["MAI_VALENTINE"] = "Mai Valentine";
	YugiohRace["MAKO"] = "Mako";
	YugiohRace["NIGHTSHROUD"] = "Nightshroud";
	YugiohRace["NORMAL"] = "Normal";
	YugiohRace["ODION"] = "Odion";
	YugiohRace["PARADOX_BROTH"] = "Paradox Broth";
	YugiohRace["PEGASUS"] = "Pegasus";
	YugiohRace["PLANT"] = "Plant";
	YugiohRace["PSYCHIC"] = "Psychic";
	YugiohRace["PYRO"] = "Pyro";
	YugiohRace["QUICK_PLAY"] = "Quick-Play";
	YugiohRace["REPTILE"] = "Reptile";
	YugiohRace["REX"] = "Rex";
	YugiohRace["RITUAL"] = "Ritual";
	YugiohRace["ROCK"] = "Rock";
	YugiohRace["SEA_SERPENT"] = "Sea Serpent";
	YugiohRace["SETO_KAIBA"] = "Seto Kaiba";
	YugiohRace["SPELLCASTER"] = "Spellcaster";
	YugiohRace["SYRUS_TRUESDA"] = "Syrus Truesda";
	YugiohRace["TANIA"] = "Tania";
	YugiohRace["TEA_GARDNER"] = "Tea Gardner";
	YugiohRace["THE_SUPREME_K"] = "The Supreme K";
	YugiohRace["THELONIOUS_VI"] = "Thelonious Vi";
	YugiohRace["THUNDER"] = "Thunder";
	YugiohRace["TITAN"] = "Titan";
	YugiohRace["TYRANNO_HASSL"] = "Tyranno Hassl";
	YugiohRace["WARRIOR"] = "Warrior";
	YugiohRace["WEEVIL"] = "Weevil";
	YugiohRace["WINGED_BEAST"] = "Winged Beast";
	YugiohRace["WYRM"] = "Wyrm";
	YugiohRace["YAMI_BAKURA"] = "Yami Bakura";
	YugiohRace["YAMI_MARIK"] = "Yami Marik";
	YugiohRace["YAMI_YUGI"] = "Yami Yugi";
	YugiohRace["YUBEL"] = "Yubel";
	YugiohRace["YUGI"] = "Yugi";
	YugiohRace["ZANE_TRUESDAL"] = "Zane Truesdal";
	YugiohRace["ZOMBIE"] = "Zombie";
	return YugiohRace;
}({});
let YugiohAttribute = /* @__PURE__ */ function(YugiohAttribute) {
	YugiohAttribute["DARK"] = "DARK";
	YugiohAttribute["DIVINE"] = "DIVINE";
	YugiohAttribute["EARTH"] = "EARTH";
	YugiohAttribute["FIRE"] = "FIRE";
	YugiohAttribute["LIGHT"] = "LIGHT";
	YugiohAttribute["WATER"] = "WATER";
	YugiohAttribute["WIND"] = "WIND";
	return YugiohAttribute;
}({});

//#endregion
//#region src/lib/games/yugioh/yugioh.presets.ts
/** Maximum copies of a card allowed in a Yu-Gi-Oh! deck */
const PLAYSET_QUANTITY = 3;
/**
* Every rarity from the base printing of a card number up to its rarest
* upgrade. A set reprints a number in upgrade rarities (Starfoil in Battle
* Packs, Quarter Century Secret Rare, Ultimate Rare, Prismatic, the seven
* rarities of a Rarity Collection, OCG parallels), so the earliest rarity a
* number has in this list is the printing a standard set holds.
*/
const YUGIOH_RARITY_ORDER = [
	YugiohRarity.COMMON,
	YugiohRarity.SHORT_PRINT,
	YugiohRarity.SUPER_SHORT_PRINT,
	YugiohRarity.KAIBA_CORPORATION_COMMON,
	YugiohRarity.DUEL_TERMINAL_TECHNOLOGY_COMMON,
	YugiohRarity.RARE,
	YugiohRarity.KAIBA_CORPORATION_RARE,
	YugiohRarity.TOKEN,
	YugiohRarity.SPEED_DUEL_SKILL,
	YugiohRarity.NORMAL_PARALLEL_RARE,
	YugiohRarity.DUEL_TERMINAL_NORMAL_PARALLEL_RARE,
	YugiohRarity.DUEL_TERMINAL_RARE_PARALLEL_RARE,
	YugiohRarity.PARALLEL_RARE,
	YugiohRarity.STARFOIL_RARE,
	YugiohRarity.MOSAIC_RARE,
	YugiohRarity.SHATTERFOIL_RARE,
	YugiohRarity.SUPER_RARE,
	YugiohRarity.SUPER_PARALLEL_RARE,
	YugiohRarity.DUEL_TERMINAL_SUPER_PARALLEL_RARE,
	YugiohRarity.ULTRA_RARE,
	YugiohRarity.KAIBA_CORPORATION_ULTRA_RARE,
	YugiohRarity.DUEL_TERMINAL_TECHNOLOGY_ULTRA_RARE,
	YugiohRarity.ULTRA_PARALLEL_RARE,
	YugiohRarity.DUEL_TERMINAL_ULTRA_PARALLEL_RARE,
	YugiohRarity.SECRET_RARE,
	YugiohRarity.SECRET_PARALLEL_RARE,
	YugiohRarity.DUEL_TERMINAL_SECRET_PARALLEL_RARE,
	YugiohRarity.GOLD_RARE,
	YugiohRarity.PREMIUM_GOLD_RARE,
	YugiohRarity.GOLD_SECRET_RARE,
	YugiohRarity.GHOST_GOLD_RARE,
	YugiohRarity.PRISMATIC_SECRET_RARE,
	YugiohRarity.EXTRA_SECRET_RARE,
	YugiohRarity.ULTIMATE_RARE,
	YugiohRarity.PLATINUM_RARE,
	YugiohRarity.PLATINUM_SECRET_RARE,
	YugiohRarity.MILLENNIUM_RARE,
	YugiohRarity.MILLENNIUM_SUPER_RARE,
	YugiohRarity.MILLENNIUM_ULTRA_RARE,
	YugiohRarity.MILLENNIUM_SECRET_RARE,
	YugiohRarity.MILLENNIUM_GOLD_RARE,
	YugiohRarity.EMBLAZONED_RARE,
	YugiohRarity.EMBLAZONED_ULTRA_RARE,
	YugiohRarity.EMBLAZONED_SECRET_RARE,
	YugiohRarity.ULTRA_PHARAOHS_RARE,
	YugiohRarity.SECRET_PHARAOHS_RARE,
	YugiohRarity.HOLOGRAPHIC_RARE,
	YugiohRarity.STARLIGHT_RARE,
	YugiohRarity.COLLECTORS_RARE,
	YugiohRarity.GHOST_RARE,
	YugiohRarity.TWENTIETH_SECRET_RARE,
	YugiohRarity.QUARTER_CENTURY_SECRET_RARE,
	YugiohRarity.ULTRA_SECRET_RARE,
	YugiohRarity.PRISMATIC_ULTIMATE_RARE,
	YugiohRarity.PRISMATIC_COLLECTORS_RARE,
	YugiohRarity.GRAND_MASTER_RARE,
	YugiohRarity.TEN_THOUSAND_SECRET_RARE,
	YugiohRarity.PROMO
];
/**
* Rarities printed without foil artwork. Yu-Gi-Oh! cards have a single
* finish: foil is a property of the rarity, from parallel commons up.
*/
const NON_FOIL_RARITIES = new Set([
	YugiohRarity.COMMON,
	YugiohRarity.SHORT_PRINT,
	YugiohRarity.SUPER_SHORT_PRINT,
	YugiohRarity.KAIBA_CORPORATION_COMMON,
	YugiohRarity.DUEL_TERMINAL_TECHNOLOGY_COMMON,
	YugiohRarity.RARE,
	YugiohRarity.KAIBA_CORPORATION_RARE,
	YugiohRarity.TOKEN,
	YugiohRarity.SPEED_DUEL_SKILL,
	YugiohRarity.PROMO
]);
const FOIL_RARITIES = YUGIOH_RARITY_ORDER.filter((rarity) => !NON_FOIL_RARITIES.has(rarity));
const baseCardMatcher = (quantity) => ({
	filters: {},
	quantity,
	onePerPrintNumber: { rarityOrder: YUGIOH_RARITY_ORDER }
});
const foilCardMatcher = (quantity) => ({
	filters: { rarity: FOIL_RARITIES },
	quantity,
	onePerPrintNumber: { rarityOrder: FOIL_RARITIES }
});
/**
* Builds the Yu-Gi-Oh! list presets under a game's translation namespace.
* The TCG and the OCG share the rarity model, so they share the presets.
*
* @param namespace - Translation namespace of the game (`ygo` or `ygo-ocg`).
*/
function buildYugiohListPresets(namespace) {
	return {
		Standard: {
			name: translatable(`${namespace}.presets.standard.name`, "Standard Set"),
			type: ListPresetType.STANDARD,
			description: translatable(`${namespace}.presets.standard.description`, "A complete set with one copy of each card in its lowest rarity"),
			matchers: [baseCardMatcher(1)]
		},
		Playset: {
			name: translatable(`${namespace}.presets.playset.name`, "Playset"),
			type: ListPresetType.PLAYSET,
			description: translatable(`${namespace}.presets.playset.description`, "A complete set with three copies of each card in its lowest rarity"),
			matchers: [baseCardMatcher(PLAYSET_QUANTITY)]
		},
		Foil: {
			name: translatable(`${namespace}.presets.foil.name`, "Foil Cards"),
			type: ListPresetType.FOIL,
			description: translatable(`${namespace}.presets.foil.description`, "Every card in the set that has a foil printing (Super Rare and above, Starfoil and other parallels), one copy in its lowest foil rarity. Commons and Rares with no foil version are not included."),
			matchers: [foilCardMatcher(1)]
		},
		"Foil Playset": {
			name: translatable(`${namespace}.presets.foilPlayset.name`, "Foil Playset"),
			type: ListPresetType.FOIL_PLAYSET,
			description: translatable(`${namespace}.presets.foilPlayset.description`, "Three copies of every card in the set that has a foil printing, in its lowest foil rarity."),
			matchers: [foilCardMatcher(PLAYSET_QUANTITY)]
		},
		"Master Set": {
			name: translatable(`${namespace}.presets.masterset.name`, "Master Set"),
			type: ListPresetType.MASTERSET,
			description: translatable(`${namespace}.presets.masterset.description`, "A complete set with one copy of every printing, in every rarity and variant"),
			matchers: [{
				filters: {},
				quantity: 1
			}]
		}
	};
}
const yugiohListPresets = buildYugiohListPresets("ygo");

//#endregion
//#region src/lib/games/yugioh/yugioh.game.ts
const yugiohFinishValues = { [YugiohCardFinish.STANDARD]: { label: translatable("ygo.attributes.finish.values.standard", "Standard") } };
const yugiohFilters = z$1.object({
	name: zodStringFilter.optional(),
	expansion: zodRelationFilter.optional(),
	rarity: makeZodEnumFilter(YugiohRarity).optional(),
	variant: makeZodEnumFilter(YugiohVariant).optional(),
	finish: makeZodEnumFilter(YugiohCardFinish).optional(),
	finishes: makeZodEnumFilter(YugiohCardFinish).optional(),
	type: makeZodEnumFilter(YugiohCardType).optional(),
	attribute: makeZodEnumFilter(YugiohAttribute).optional(),
	race: makeZodEnumFilter(YugiohRace).optional(),
	linkMarkers: makeZodEnumFilter(YugiohLinkMarker).optional(),
	languages: makeZodEnumFilter(Languages).optional(),
	atk: zodNumberFilter.optional(),
	def: zodNumberFilter.optional(),
	level: zodNumberFilter.optional(),
	rank: zodNumberFilter.optional(),
	scale: zodNumberFilter.optional(),
	linkRating: zodNumberFilter.optional()
});
const YuGiOhGameConfiguration = {
	name: "Yu-Gi-Oh!",
	shortName: "Yu-Gi-Oh!",
	slug: "ygo",
	printNumberAliasesAttribute: "printedCodes",
	icon: {
		dark: "ygo-icon.png",
		light: "ygo-icon.png"
	},
	image: {
		dark: "ygo.png",
		light: "ygo.png"
	},
	attributes: {
		name: nameAttribute,
		type: enumAttribute(YugiohCardType, {
			entity: "card",
			name: translatable("ygo.attributes.type.name", "Card Type"),
			description: translatable("ygo.attributes.type.description", "The type of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[YugiohCardType.EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.effect_monster", "Effect Monster") },
				[YugiohCardType.FLIP_EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.flip_effect_monster", "Flip Effect Monster") },
				[YugiohCardType.FLIP_TUNER_EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.flip_tuner_effect_monster", "Flip Tuner Effect Monster") },
				[YugiohCardType.FUSION_MONSTER]: { label: translatable("ygo.attributes.type.values.fusion_monster", "Fusion Monster") },
				[YugiohCardType.GEMINI_MONSTER]: { label: translatable("ygo.attributes.type.values.gemini_monster", "Gemini Monster") },
				[YugiohCardType.LINK_MONSTER]: { label: translatable("ygo.attributes.type.values.link_monster", "Link Monster") },
				[YugiohCardType.NORMAL_MONSTER]: { label: translatable("ygo.attributes.type.values.normal_monster", "Normal Monster") },
				[YugiohCardType.NORMAL_TUNER_MONSTER]: { label: translatable("ygo.attributes.type.values.normal_tuner_monster", "Normal Tuner Monster") },
				[YugiohCardType.PENDULUM_EFFECT_FUSION_MONSTER]: { label: translatable("ygo.attributes.type.values.pendulum_effect_fusion_monster", "Pendulum Effect Fusion Monster") },
				[YugiohCardType.PENDULUM_EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.pendulum_effect_monster", "Pendulum Effect Monster") },
				[YugiohCardType.PENDULUM_EFFECT_RITUAL_MONSTER]: { label: translatable("ygo.attributes.type.values.pendulum_effect_ritual_monster", "Pendulum Effect Ritual Monster") },
				[YugiohCardType.PENDULUM_FLIP_EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.pendulum_flip_effect_monster", "Pendulum Flip Effect Monster") },
				[YugiohCardType.PENDULUM_NORMAL_MONSTER]: { label: translatable("ygo.attributes.type.values.pendulum_normal_monster", "Pendulum Normal Monster") },
				[YugiohCardType.PENDULUM_TUNER_EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.pendulum_tuner_effect_monster", "Pendulum Tuner Effect Monster") },
				[YugiohCardType.RITUAL_EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.ritual_effect_monster", "Ritual Effect Monster") },
				[YugiohCardType.RITUAL_MONSTER]: { label: translatable("ygo.attributes.type.values.ritual_monster", "Ritual Monster") },
				[YugiohCardType.SKILL_CARD]: { label: translatable("ygo.attributes.type.values.skill_card", "Skill Card") },
				[YugiohCardType.SPELL_CARD]: { label: translatable("ygo.attributes.type.values.spell_card", "Spell Card") },
				[YugiohCardType.SPIRIT_MONSTER]: { label: translatable("ygo.attributes.type.values.spirit_monster", "Spirit Monster") },
				[YugiohCardType.SYNCHRO_MONSTER]: { label: translatable("ygo.attributes.type.values.synchro_monster", "Synchro Monster") },
				[YugiohCardType.SYNCHRO_PENDULUM_EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.synchro_pendulum_effect_monster", "Synchro Pendulum Effect Monster") },
				[YugiohCardType.SYNCHRO_TUNER_MONSTER]: { label: translatable("ygo.attributes.type.values.synchro_tuner_monster", "Synchro Tuner Monster") },
				[YugiohCardType.TOKEN]: { label: translatable("ygo.attributes.type.values.token", "Token") },
				[YugiohCardType.TOON_MONSTER]: { label: translatable("ygo.attributes.type.values.toon_monster", "Toon Monster") },
				[YugiohCardType.TRAP_CARD]: { label: translatable("ygo.attributes.type.values.trap_card", "Trap Card") },
				[YugiohCardType.TUNER_MONSTER]: { label: translatable("ygo.attributes.type.values.tuner_monster", "Tuner Monster") },
				[YugiohCardType.UNION_EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.union_effect_monster", "Union Effect Monster") },
				[YugiohCardType.XYZ_MONSTER]: { label: translatable("ygo.attributes.type.values.xyz_monster", "XYZ Monster") },
				[YugiohCardType.XYZ_PENDULUM_EFFECT_MONSTER]: { label: translatable("ygo.attributes.type.values.xyz_pendulum_effect_monster", "XYZ Pendulum Effect Monster") }
			}
		}),
		attribute: enumAttribute(YugiohAttribute, {
			entity: "card",
			name: translatable("ygo.attributes.attribute.name", "Attribute"),
			description: translatable("ygo.attributes.attribute.description", "The attribute of the monster"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[YugiohAttribute.DARK]: { label: translatable("ygo.attributes.attribute.values.dark", "Dark") },
				[YugiohAttribute.DIVINE]: { label: translatable("ygo.attributes.attribute.values.divine", "Divine") },
				[YugiohAttribute.EARTH]: { label: translatable("ygo.attributes.attribute.values.earth", "Earth") },
				[YugiohAttribute.FIRE]: { label: translatable("ygo.attributes.attribute.values.fire", "Fire") },
				[YugiohAttribute.LIGHT]: { label: translatable("ygo.attributes.attribute.values.light", "Light") },
				[YugiohAttribute.WATER]: { label: translatable("ygo.attributes.attribute.values.water", "Water") },
				[YugiohAttribute.WIND]: { label: translatable("ygo.attributes.attribute.values.wind", "Wind") }
			}
		}),
		race: enumAttribute(YugiohRace, {
			entity: "card",
			name: translatable("ygo.attributes.race.name", "Type"),
			description: translatable("ygo.attributes.race.description", "Monster type, or spell/trap type"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[YugiohRace.ABIDOS_THE_TH]: { label: translatable("ygo.attributes.race.values.abidos_the_th", "Abidos the Third") },
				[YugiohRace.ADRIAN_GECKO]: { label: translatable("ygo.attributes.race.values.adrian_gecko", "Adrian Gecko") },
				[YugiohRace.ALEXIS_RHODES]: { label: translatable("ygo.attributes.race.values.alexis_rhodes", "Alexis Rhodes") },
				[YugiohRace.AMNAEL]: { label: translatable("ygo.attributes.race.values.amnael", "Amnael") },
				[YugiohRace.ANDREW]: { label: translatable("ygo.attributes.race.values.andrew", "Andrew") },
				[YugiohRace.AQUA]: { label: translatable("ygo.attributes.race.values.aqua", "Aqua") },
				[YugiohRace.ARKANA]: { label: translatable("ygo.attributes.race.values.arkana", "Arkana") },
				[YugiohRace.ASTER_PHOENIX]: { label: translatable("ygo.attributes.race.values.aster_phoenix", "Aster Phoenix") },
				[YugiohRace.AXEL_BRODIE]: { label: translatable("ygo.attributes.race.values.axel_brodie", "Axel Brodie") },
				[YugiohRace.BASTION_MISAW]: { label: translatable("ygo.attributes.race.values.bastion_misaw", "Bastion Misawa") },
				[YugiohRace.BEAST]: { label: translatable("ygo.attributes.race.values.beast", "Beast") },
				[YugiohRace.BEAST_WARRIOR]: { label: translatable("ygo.attributes.race.values.beast_warrior", "Beast-Warrior") },
				[YugiohRace.BONZ]: { label: translatable("ygo.attributes.race.values.bonz", "Bonz") },
				[YugiohRace.CAMULA]: { label: translatable("ygo.attributes.race.values.camula", "Camula") },
				[YugiohRace.CHAZZ_PRINCET]: { label: translatable("ygo.attributes.race.values.chazz_princet", "Chazz Princeton") },
				[YugiohRace.CHRISTINE]: { label: translatable("ygo.attributes.race.values.christine", "Christine") },
				[YugiohRace.CHUMLEY_HUFFI]: { label: translatable("ygo.attributes.race.values.chumley_huffi", "Chumley Huffington") },
				[YugiohRace.CONTINUOUS]: { label: translatable("ygo.attributes.race.values.continuous", "Continuous") },
				[YugiohRace.COUNTER]: { label: translatable("ygo.attributes.race.values.counter", "Counter") },
				[YugiohRace.CREATOR_GOD]: { label: translatable("ygo.attributes.race.values.creator_god", "Creator God") },
				[YugiohRace.CYBERSE]: { label: translatable("ygo.attributes.race.values.cyberse", "Cyberse") },
				[YugiohRace.DAVID]: { label: translatable("ygo.attributes.race.values.david", "David") },
				[YugiohRace.DINOSAUR]: { label: translatable("ygo.attributes.race.values.dinosaur", "Dinosaur") },
				[YugiohRace.DIVINE_BEAST]: { label: translatable("ygo.attributes.race.values.divine_beast", "Divine-Beast") },
				[YugiohRace.DON_ZALOOG]: { label: translatable("ygo.attributes.race.values.don_zaloog", "Don Zaloog") },
				[YugiohRace.DR_VELLIAN_C]: { label: translatable("ygo.attributes.race.values.dr_vellian_c", "Dr. Vellian Crowler") },
				[YugiohRace.DRAGON]: { label: translatable("ygo.attributes.race.values.dragon", "Dragon") },
				[YugiohRace.EMMA]: { label: translatable("ygo.attributes.race.values.emma", "Emma") },
				[YugiohRace.EQUIP]: { label: translatable("ygo.attributes.race.values.equip", "Equip") },
				[YugiohRace.ESPA_ROBA]: { label: translatable("ygo.attributes.race.values.espa_roba", "Espa Roba") },
				[YugiohRace.FAIRY]: { label: translatable("ygo.attributes.race.values.fairy", "Fairy") },
				[YugiohRace.FIELD]: { label: translatable("ygo.attributes.race.values.field", "Field") },
				[YugiohRace.FIEND]: { label: translatable("ygo.attributes.race.values.fiend", "Fiend") },
				[YugiohRace.FISH]: { label: translatable("ygo.attributes.race.values.fish", "Fish") },
				[YugiohRace.ILLUSION]: { label: translatable("ygo.attributes.race.values.illusion", "Illusion") },
				[YugiohRace.INSECT]: { label: translatable("ygo.attributes.race.values.insect", "Insect") },
				[YugiohRace.ISHIZU]: { label: translatable("ygo.attributes.race.values.ishizu", "Ishizu") },
				[YugiohRace.ISHIZU_ISHTAR]: { label: translatable("ygo.attributes.race.values.ishizu_ishtar", "Ishizu Ishtar") },
				[YugiohRace.JADEN_YUKI]: { label: translatable("ygo.attributes.race.values.jaden_yuki", "Jaden Yuki") },
				[YugiohRace.JESSE_ANDERSO]: { label: translatable("ygo.attributes.race.values.jesse_anderso", "Jesse Anderson") },
				[YugiohRace.JOEY]: { label: translatable("ygo.attributes.race.values.joey", "Joey") },
				[YugiohRace.JOEY_WHEELER]: { label: translatable("ygo.attributes.race.values.joey_wheeler", "Joey Wheeler") },
				[YugiohRace.KAGEMARU]: { label: translatable("ygo.attributes.race.values.kagemaru", "Kagemaru") },
				[YugiohRace.KAIBA]: { label: translatable("ygo.attributes.race.values.kaiba", "Kaiba") },
				[YugiohRace.KEITH]: { label: translatable("ygo.attributes.race.values.keith", "Keith") },
				[YugiohRace.LUMIS_UMBRA]: { label: translatable("ygo.attributes.race.values.lumis_umbra", "Lumis Umbra") },
				[YugiohRace.LUMIS_AND_UMB]: { label: translatable("ygo.attributes.race.values.lumis_and_umb", "Lumis and Umbra") },
				[YugiohRace.MACHINE]: { label: translatable("ygo.attributes.race.values.machine", "Machine") },
				[YugiohRace.MAI]: { label: translatable("ygo.attributes.race.values.mai", "Mai") },
				[YugiohRace.MAI_VALENTINE]: { label: translatable("ygo.attributes.race.values.mai_valentine", "Mai Valentine") },
				[YugiohRace.MAKO]: { label: translatable("ygo.attributes.race.values.mako", "Mako") },
				[YugiohRace.NIGHTSHROUD]: { label: translatable("ygo.attributes.race.values.nightshroud", "Nightshroud") },
				[YugiohRace.NORMAL]: { label: translatable("ygo.attributes.race.values.normal", "Normal") },
				[YugiohRace.ODION]: { label: translatable("ygo.attributes.race.values.odion", "Odion") },
				[YugiohRace.PARADOX_BROTH]: { label: translatable("ygo.attributes.race.values.paradox_broth", "Paradox Brothers") },
				[YugiohRace.PEGASUS]: { label: translatable("ygo.attributes.race.values.pegasus", "Pegasus") },
				[YugiohRace.PLANT]: { label: translatable("ygo.attributes.race.values.plant", "Plant") },
				[YugiohRace.PSYCHIC]: { label: translatable("ygo.attributes.race.values.psychic", "Psychic") },
				[YugiohRace.PYRO]: { label: translatable("ygo.attributes.race.values.pyro", "Pyro") },
				[YugiohRace.QUICK_PLAY]: { label: translatable("ygo.attributes.race.values.quick_play", "Quick-Play") },
				[YugiohRace.REPTILE]: { label: translatable("ygo.attributes.race.values.reptile", "Reptile") },
				[YugiohRace.REX]: { label: translatable("ygo.attributes.race.values.rex", "Rex") },
				[YugiohRace.RITUAL]: { label: translatable("ygo.attributes.race.values.ritual", "Ritual") },
				[YugiohRace.ROCK]: { label: translatable("ygo.attributes.race.values.rock", "Rock") },
				[YugiohRace.SEA_SERPENT]: { label: translatable("ygo.attributes.race.values.sea_serpent", "Sea Serpent") },
				[YugiohRace.SETO_KAIBA]: { label: translatable("ygo.attributes.race.values.seto_kaiba", "Seto Kaiba") },
				[YugiohRace.SPELLCASTER]: { label: translatable("ygo.attributes.race.values.spellcaster", "Spellcaster") },
				[YugiohRace.SYRUS_TRUESDA]: { label: translatable("ygo.attributes.race.values.syrus_truesda", "Syrus Truesdale") },
				[YugiohRace.TANIA]: { label: translatable("ygo.attributes.race.values.tania", "Tania") },
				[YugiohRace.TEA_GARDNER]: { label: translatable("ygo.attributes.race.values.tea_gardner", "Tea Gardner") },
				[YugiohRace.THE_SUPREME_K]: { label: translatable("ygo.attributes.race.values.the_supreme_k", "The Supreme King") },
				[YugiohRace.THELONIOUS_VI]: { label: translatable("ygo.attributes.race.values.thelonious_vi", "Thelonious Viper") },
				[YugiohRace.THUNDER]: { label: translatable("ygo.attributes.race.values.thunder", "Thunder") },
				[YugiohRace.TITAN]: { label: translatable("ygo.attributes.race.values.titan", "Titan") },
				[YugiohRace.TYRANNO_HASSL]: { label: translatable("ygo.attributes.race.values.tyranno_hassl", "Tyranno Hassleberry") },
				[YugiohRace.WARRIOR]: { label: translatable("ygo.attributes.race.values.warrior", "Warrior") },
				[YugiohRace.WEEVIL]: { label: translatable("ygo.attributes.race.values.weevil", "Weevil") },
				[YugiohRace.WINGED_BEAST]: { label: translatable("ygo.attributes.race.values.winged_beast", "Winged Beast") },
				[YugiohRace.WYRM]: { label: translatable("ygo.attributes.race.values.wyrm", "Wyrm") },
				[YugiohRace.YAMI_BAKURA]: { label: translatable("ygo.attributes.race.values.yami_bakura", "Yami Bakura") },
				[YugiohRace.YAMI_MARIK]: { label: translatable("ygo.attributes.race.values.yami_marik", "Yami Marik") },
				[YugiohRace.YAMI_YUGI]: { label: translatable("ygo.attributes.race.values.yami_yugi", "Yami Yugi") },
				[YugiohRace.YUBEL]: { label: translatable("ygo.attributes.race.values.yubel", "Yubel") },
				[YugiohRace.YUGI]: { label: translatable("ygo.attributes.race.values.yugi", "Yugi") },
				[YugiohRace.ZANE_TRUESDAL]: { label: translatable("ygo.attributes.race.values.zane_truesdal", "Zane Truesdale") },
				[YugiohRace.ZOMBIE]: { label: translatable("ygo.attributes.race.values.zombie", "Zombie") }
			}
		}),
		rarity: enumAttribute(YugiohRarity, {
			entity: "variant",
			name: translatable("ygo.attributes.rarity.name", "Rarity"),
			description: translatable("ygo.attributes.rarity.description", "The rarity of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.ButtonGroup,
			values: {
				[YugiohRarity.COMMON]: { label: translatable("ygo.attributes.rarity.values.common", "Common") },
				[YugiohRarity.RARE]: { label: translatable("ygo.attributes.rarity.values.rare", "Rare") },
				[YugiohRarity.SUPER_RARE]: { label: translatable("ygo.attributes.rarity.values.super_rare", "Super Rare") },
				[YugiohRarity.ULTRA_RARE]: { label: translatable("ygo.attributes.rarity.values.ultra_rare", "Ultra Rare") },
				[YugiohRarity.SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.secret_rare", "Secret Rare") },
				[YugiohRarity.ULTIMATE_RARE]: { label: translatable("ygo.attributes.rarity.values.ultimate_rare", "Ultimate Rare") },
				[YugiohRarity.GHOST_RARE]: { label: translatable("ygo.attributes.rarity.values.ghost_rare", "Ghost Rare") },
				[YugiohRarity.GHOST_GOLD_RARE]: { label: translatable("ygo.attributes.rarity.values.ghostgold_rare", "Ghost/Gold Rare") },
				[YugiohRarity.GOLD_RARE]: { label: translatable("ygo.attributes.rarity.values.gold_rare", "Gold Rare") },
				[YugiohRarity.GOLD_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.gold_secret_rare", "Gold Secret Rare") },
				[YugiohRarity.PREMIUM_GOLD_RARE]: { label: translatable("ygo.attributes.rarity.values.premium_gold_rare", "Premium Gold Rare") },
				[YugiohRarity.PLATINUM_RARE]: { label: translatable("ygo.attributes.rarity.values.platinum_rare", "Platinum Rare") },
				[YugiohRarity.PLATINUM_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.platinum_secret_rare", "Platinum Secret Rare") },
				[YugiohRarity.PRISMATIC_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.prismatic_secret_rare", "Prismatic Secret Rare") },
				[YugiohRarity.PRISMATIC_ULTIMATE_RARE]: { label: translatable("ygo.attributes.rarity.values.prismatic_ultimate_rare", "Prismatic Ultimate Rare") },
				[YugiohRarity.PRISMATIC_COLLECTORS_RARE]: { label: translatable("ygo.attributes.rarity.values.prismatic_collectors_rare", "Prismatic Collector's Rare") },
				[YugiohRarity.QUARTER_CENTURY_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.quarter_century_secret_rare", "Quarter Century Secret Rare") },
				[YugiohRarity.TEN_THOUSAND_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.10000_secret_rare", "10000 Secret Rare") },
				[YugiohRarity.STARLIGHT_RARE]: { label: translatable("ygo.attributes.rarity.values.starlight_rare", "Starlight Rare") },
				[YugiohRarity.COLLECTORS_RARE]: { label: translatable("ygo.attributes.rarity.values.collectors_rare", "Collector's Rare") },
				[YugiohRarity.EXTRA_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.extra_secret_rare", "Extra Secret Rare") },
				[YugiohRarity.ULTRA_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.ultra_secret_rare", "Ultra Secret Rare") },
				[YugiohRarity.STARFOIL_RARE]: { label: translatable("ygo.attributes.rarity.values.starfoil_rare", "Starfoil Rare") },
				[YugiohRarity.SHATTERFOIL_RARE]: { label: translatable("ygo.attributes.rarity.values.shatterfoil_rare", "Shatterfoil Rare") },
				[YugiohRarity.MOSAIC_RARE]: { label: translatable("ygo.attributes.rarity.values.mosaic_rare", "Mosaic Rare") },
				[YugiohRarity.HOLOGRAPHIC_RARE]: { label: translatable("ygo.attributes.rarity.values.holographic_rare", "Holographic Rare") },
				[YugiohRarity.PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.parallel_rare", "Parallel Rare") },
				[YugiohRarity.NORMAL_PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.normal_parallel_rare", "Normal Parallel Rare") },
				[YugiohRarity.SUPER_PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.super_parallel_rare", "Super Parallel Rare") },
				[YugiohRarity.ULTRA_PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.ultra_parallel_rare", "Ultra Parallel Rare") },
				[YugiohRarity.SECRET_PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.secret_parallel_rare", "Secret Parallel Rare") },
				[YugiohRarity.DUEL_TERMINAL_NORMAL_PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.duel_terminal_normal_parallel_rare", "Duel Terminal Normal Parallel Rare") },
				[YugiohRarity.DUEL_TERMINAL_RARE_PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.duel_terminal_rare_parallel_rare", "Duel Terminal Rare Parallel Rare") },
				[YugiohRarity.DUEL_TERMINAL_SUPER_PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.duel_terminal_super_parallel_rare", "Duel Terminal Super Parallel Rare") },
				[YugiohRarity.DUEL_TERMINAL_ULTRA_PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.duel_terminal_ultra_parallel_rare", "Duel Terminal Ultra Parallel Rare") },
				[YugiohRarity.DUEL_TERMINAL_SECRET_PARALLEL_RARE]: { label: translatable("ygo.attributes.rarity.values.duel_terminal_secret_parallel_rare", "Duel Terminal Secret Parallel Rare") },
				[YugiohRarity.DUEL_TERMINAL_TECHNOLOGY_COMMON]: { label: translatable("ygo.attributes.rarity.values.duel_terminal_technology_common", "Duel Terminal Technology Common") },
				[YugiohRarity.DUEL_TERMINAL_TECHNOLOGY_ULTRA_RARE]: { label: translatable("ygo.attributes.rarity.values.duel_terminal_technology_ultra_rare", "Duel Terminal Technology Ultra Rare") },
				[YugiohRarity.EMBLAZONED_RARE]: { label: translatable("ygo.attributes.rarity.values.emblazoned_rare", "Emblazoned Rare") },
				[YugiohRarity.EMBLAZONED_ULTRA_RARE]: { label: translatable("ygo.attributes.rarity.values.emblazoned_ultra_rare", "Emblazoned Ultra Rare") },
				[YugiohRarity.EMBLAZONED_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.emblazoned_secret_rare", "Emblazoned Secret Rare") },
				[YugiohRarity.ULTRA_PHARAOHS_RARE]: { label: translatable("ygo.attributes.rarity.values.ultra_pharaohs_rare", "Ultra Pharaoh's Rare") },
				[YugiohRarity.SECRET_PHARAOHS_RARE]: { label: translatable("ygo.attributes.rarity.values.secret_pharaohs_rare", "Secret Pharaoh's Rare") },
				[YugiohRarity.MILLENNIUM_RARE]: { label: translatable("ygo.attributes.rarity.values.millennium_rare", "Millennium Rare") },
				[YugiohRarity.MILLENNIUM_SUPER_RARE]: { label: translatable("ygo.attributes.rarity.values.millennium_super_rare", "Millennium Super Rare") },
				[YugiohRarity.MILLENNIUM_ULTRA_RARE]: { label: translatable("ygo.attributes.rarity.values.millennium_ultra_rare", "Millennium Ultra Rare") },
				[YugiohRarity.MILLENNIUM_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.millennium_secret_rare", "Millennium Secret Rare") },
				[YugiohRarity.MILLENNIUM_GOLD_RARE]: { label: translatable("ygo.attributes.rarity.values.millennium_gold_rare", "Millennium Gold Rare") },
				[YugiohRarity.GRAND_MASTER_RARE]: { label: translatable("ygo.attributes.rarity.values.grand_master_rare", "Grand Master Rare") },
				[YugiohRarity.TWENTIETH_SECRET_RARE]: { label: translatable("ygo.attributes.rarity.values.20th_secret_rare", "20th Secret Rare") },
				[YugiohRarity.KAIBA_CORPORATION_COMMON]: { label: translatable("ygo.attributes.rarity.values.kaiba_corporation_common", "Kaiba Corporation Common") },
				[YugiohRarity.KAIBA_CORPORATION_RARE]: { label: translatable("ygo.attributes.rarity.values.kaiba_corporation_rare", "Kaiba Corporation Rare") },
				[YugiohRarity.KAIBA_CORPORATION_ULTRA_RARE]: { label: translatable("ygo.attributes.rarity.values.kaiba_corporation_ultra_rare", "Kaiba Corporation Ultra Rare") },
				[YugiohRarity.SHORT_PRINT]: { label: translatable("ygo.attributes.rarity.values.short_print", "Short Print") },
				[YugiohRarity.SUPER_SHORT_PRINT]: { label: translatable("ygo.attributes.rarity.values.super_short_print", "Super Short Print") },
				[YugiohRarity.SPEED_DUEL_SKILL]: { label: translatable("ygo.attributes.rarity.values.speed_duel_skill", "Speed Duel Skill") },
				[YugiohRarity.TOKEN]: { label: translatable("ygo.attributes.rarity.values.token", "Token") },
				[YugiohRarity.PROMO]: { label: translatable("ygo.attributes.rarity.values.promo", "Promo") }
			}
		}),
		variant: enumAttribute(YugiohVariant, {
			entity: "variant",
			name: translatable("ygo.attributes.variant.name", "Variant"),
			description: translatable("ygo.attributes.variant.description", "Art, colour or stamp variant of the printing"),
			type: "enum",
			array: false,
			filter: FilterComponentType.Select,
			values: {
				[YugiohVariant.ALTERNATE_ART]: { label: translatable("ygo.attributes.variant.values.alternate_art", "Alternate Art") },
				[YugiohVariant.NEW_ART]: { label: translatable("ygo.attributes.variant.values.new_art", "New Art") },
				[YugiohVariant.ART_7]: { label: translatable("ygo.attributes.variant.values.art_7", "7th Art") },
				[YugiohVariant.ART_8]: { label: translatable("ygo.attributes.variant.values.art_8", "8th Art") },
				[YugiohVariant.HERO_ART]: { label: translatable("ygo.attributes.variant.values.hero_art", "HERO Art") },
				[YugiohVariant.EXTENDED_ART]: { label: translatable("ygo.attributes.variant.values.extended_art", "Extended Art") },
				[YugiohVariant.JAPANESE_ARTWORK]: { label: translatable("ygo.attributes.variant.values.japanese_artwork", "Japanese Artwork") },
				[YugiohVariant.BLUE]: { label: translatable("ygo.attributes.variant.values.blue", "Blue") },
				[YugiohVariant.GREEN]: { label: translatable("ygo.attributes.variant.values.green", "Green") },
				[YugiohVariant.PURPLE]: { label: translatable("ygo.attributes.variant.values.purple", "Purple") },
				[YugiohVariant.RED]: { label: translatable("ygo.attributes.variant.values.red", "Red") },
				[YugiohVariant.SILVER]: { label: translatable("ygo.attributes.variant.values.silver", "Silver") },
				[YugiohVariant.BRONZE]: { label: translatable("ygo.attributes.variant.values.bronze", "Bronze") },
				[YugiohVariant.YELLOW]: { label: translatable("ygo.attributes.variant.values.yellow", "Yellow") },
				[YugiohVariant.ORANGE]: { label: translatable("ygo.attributes.variant.values.orange", "Orange") },
				[YugiohVariant.BLACK]: { label: translatable("ygo.attributes.variant.values.black", "Black") },
				[YugiohVariant.OTS_STAMP]: { label: translatable("ygo.attributes.variant.values.ots_stamp", "OTS Stamp") },
				[YugiohVariant.JUDGE_STAMP]: { label: translatable("ygo.attributes.variant.values.judge_stamp", "Judge Stamp") },
				[YugiohVariant.REGIONAL_STAMP]: { label: translatable("ygo.attributes.variant.values.regional_stamp", "Regional Qualifier Stamp") },
				[YugiohVariant.STAMP]: { label: translatable("ygo.attributes.variant.values.stamp", "Stamp") },
				[YugiohVariant.MISPRINT]: { label: translatable("ygo.attributes.variant.values.misprint", "Misprint") },
				[YugiohVariant.OVERSIZED]: { label: translatable("ygo.attributes.variant.values.oversized", "Oversized") },
				[YugiohVariant.LIMITED_EDITION]: { label: translatable("ygo.attributes.variant.values.limited_edition", "Limited Edition") },
				[YugiohVariant.REPRINT]: { label: translatable("ygo.attributes.variant.values.reprint", "Reprint") },
				[YugiohVariant.CLASSIC_STYLE]: { label: translatable("ygo.attributes.variant.values.classic_style", "Classic Style") },
				[YugiohVariant.LOGO]: { label: translatable("ygo.attributes.variant.values.logo", "Logo") },
				[YugiohVariant.ORIGINAL_LAYOUT]: { label: translatable("ygo.attributes.variant.values.original_layout", "Original Layout") }
			}
		}),
		printedCodes: {
			entity: "variant",
			name: translatable("ygo.attributes.printedCodes.name", "Printed codes"),
			description: translatable("ygo.attributes.printedCodes.description", "Every set code printed for this card across regions and generations"),
			type: "string",
			array: true
		},
		cardmarketVersion: {
			entity: "variant",
			name: translatable("ygo.attributes.cardmarketVersion.name", "Cardmarket version"),
			description: translatable("ygo.attributes.cardmarketVersion.description", "Distinguishes Cardmarket versions of the same expansion, number and rarity"),
			type: "number",
			array: false
		},
		finishes: enumAttribute(YugiohCardFinish, {
			entity: "variant",
			path: "finishes",
			name: translatable("ygo.attributes.finish.name", "Finish"),
			description: translatable("ygo.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: yugiohFinishValues
		}),
		finish: enumAttribute(YugiohCardFinish, {
			entity: "inventory",
			name: translatable("ygo.attributes.finish.name", "Finish"),
			description: translatable("ygo.attributes.finish.description", "The finish of the card"),
			type: "enum",
			array: false,
			filter: FilterComponentType.CheckboxList,
			values: yugiohFinishValues
		}),
		printNumber: printNumberAttribute,
		linkMarkers: enumAttribute(YugiohLinkMarker, {
			entity: "card",
			name: translatable("ygo.attributes.linkMarkers.name", "Link Markers"),
			description: translatable("ygo.attributes.linkMarkers.description", "Link arrows of a Link monster"),
			type: "enum",
			array: true,
			filter: FilterComponentType.CheckboxList,
			values: {
				[YugiohLinkMarker.TOP_LEFT]: { label: translatable("ygo.attributes.linkMarkers.values.top_left", "Top-Left") },
				[YugiohLinkMarker.TOP]: { label: translatable("ygo.attributes.linkMarkers.values.top", "Top") },
				[YugiohLinkMarker.TOP_RIGHT]: { label: translatable("ygo.attributes.linkMarkers.values.top_right", "Top-Right") },
				[YugiohLinkMarker.LEFT]: { label: translatable("ygo.attributes.linkMarkers.values.left", "Left") },
				[YugiohLinkMarker.RIGHT]: { label: translatable("ygo.attributes.linkMarkers.values.right", "Right") },
				[YugiohLinkMarker.BOTTOM_LEFT]: { label: translatable("ygo.attributes.linkMarkers.values.bottom_left", "Bottom-Left") },
				[YugiohLinkMarker.BOTTOM]: { label: translatable("ygo.attributes.linkMarkers.values.bottom", "Bottom") },
				[YugiohLinkMarker.BOTTOM_RIGHT]: { label: translatable("ygo.attributes.linkMarkers.values.bottom_right", "Bottom-Right") }
			}
		}),
		level: {
			entity: "card",
			name: translatable("ygo.attributes.level.name", "Level"),
			description: translatable("ygo.attributes.level.description", "Monster level"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		rank: {
			entity: "card",
			name: translatable("ygo.attributes.rank.name", "Rank"),
			description: translatable("ygo.attributes.rank.description", "Rank of an Xyz monster"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		linkRating: {
			entity: "card",
			name: translatable("ygo.attributes.linkRating.name", "Link Rating"),
			description: translatable("ygo.attributes.linkRating.description", "Link rating of a Link monster"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		scale: {
			entity: "card",
			name: translatable("ygo.attributes.scale.name", "Pendulum Scale"),
			description: translatable("ygo.attributes.scale.description", "Pendulum scale value"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		atk: {
			entity: "card",
			name: translatable("ygo.attributes.atk.name", "ATK"),
			description: translatable("ygo.attributes.atk.description", "Attack points of the monster"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		def: {
			entity: "card",
			name: translatable("ygo.attributes.def.name", "DEF"),
			description: translatable("ygo.attributes.def.description", "Defense points of the monster"),
			type: "number",
			array: false,
			filter: FilterComponentType.RangeSlider
		},
		archetype: {
			entity: "card",
			name: translatable("ygo.attributes.archetype.name", "Archetype"),
			description: translatable("ygo.attributes.archetype.description", "Card archetype (e.g., Blue-Eyes, Dark Magician)"),
			type: "string",
			array: false
		},
		desc: {
			entity: "card",
			name: translatable("ygo.attributes.desc.name", "Description"),
			description: translatable("ygo.attributes.desc.description", "Card effect text and description"),
			type: "string",
			array: false
		}
	},
	basicFilters: [{
		label: "Basic filters",
		filters: [
			"expansion",
			"rarity",
			"attribute",
			"type"
		]
	}],
	advancedFilters: [{
		label: "Card properties",
		filters: [
			"race",
			"variant",
			"linkMarkers"
		]
	}, {
		label: "Card stats",
		filters: [
			"atk",
			"def",
			"level",
			"rank",
			"scale",
			"linkRating"
		]
	}],
	productDetailsOrder: {
		details: ["desc"],
		statistics: [
			"rarity",
			"variant",
			"attribute",
			"type",
			"race",
			"level",
			"rank",
			"atk",
			"def",
			"scale",
			"linkRating",
			"linkMarkers",
			"archetype",
			"printedCodes",
			"cardmarketVersion"
		]
	},
	defaultSorting: {
		column: "sortNumber",
		direction: "asc"
	},
	listPresets: yugiohListPresets,
	finishHierarchy: [CardFinish.STANDARD],
	basicAttributes: ["rarity"],
	supportedMarketplaces: [SupportedMarketplace.Cardmarket, SupportedMarketplace.Tcgplayer],
	vendorIds: [
		{
			key: "konamiId",
			label: "Konami ID",
			isMarketplaceId: false
		},
		cardmarketVendorId,
		tcgplayerVendorId
	],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "LOB-005",
		cardName: "Blue-Eyes White Dragon",
		expansion: "Legend of Blue Eyes White Dragon",
		expansionCode: "LOB",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "English",
		quantity: 1
	}
};

//#endregion
//#region src/lib/games/yugioh-ocg/yugioh-ocg.presets.ts
const yugiohOcgListPresets = buildYugiohListPresets("ygo-ocg");

//#endregion
//#region src/lib/games/yugioh-ocg/yugioh-ocg.game.ts
/** Yu-Gi-Oh! OCG cards carry the same attributes as the TCG, so they filter the same way. */
const yugiohOcgFilters = yugiohFilters;
const TCG_NAMESPACE = `${YuGiOhGameConfiguration.slug}.`;
const OCG_NAMESPACE = "ygo-ocg.";
function inOcgNamespace(key) {
	return key.startsWith(TCG_NAMESPACE) ? `${OCG_NAMESPACE}${key.slice(TCG_NAMESPACE.length)}` : key;
}
function withOcgLabel(value) {
	return {
		...value,
		label: inOcgNamespace(value.label)
	};
}
/**
* Points an attribute's labels at the `ygo-ocg` message namespace. The UI
* resolves labels per game slug, so the OCG game reads its own copy of the
* Yu-Gi-Oh! bundle rather than the TCG one.
*/
function withOcgTranslationKeys(attribute) {
	const renamed = {
		...attribute,
		name: inOcgNamespace(attribute.name),
		description: inOcgNamespace(attribute.description)
	};
	if (renamed.type === "boolean") return {
		...renamed,
		true: withOcgLabel(renamed.true),
		false: withOcgLabel(renamed.false)
	};
	if (renamed.type === "relation") return {
		...renamed,
		values: renamed.values.map(withOcgLabel)
	};
	if (!("values" in renamed)) return renamed;
	const values = Object.fromEntries(Object.entries(renamed.values).map(([value, label]) => [value, withOcgLabel(label)]));
	return {
		...renamed,
		values
	};
}
const YuGiOhOcgGameConfiguration = {
	...YuGiOhGameConfiguration,
	name: "Yu-Gi-Oh! OCG",
	shortName: "Yu-Gi-Oh! OCG",
	slug: "ygo-ocg",
	icon: {
		dark: "ygo-ocg-icon.png",
		light: "ygo-ocg-icon.png"
	},
	image: {
		dark: "ygo-ocg.png",
		light: "ygo-ocg.png"
	},
	attributes: Object.fromEntries(Object.entries(YuGiOhGameConfiguration.attributes).map(([key, attribute]) => [key, withOcgTranslationKeys(attribute)])),
	listPresets: yugiohOcgListPresets,
	supportedMarketplaces: [SupportedMarketplace.Cardmarket],
	vendorIds: [{
		key: "konamiId",
		label: "Konami ID",
		isMarketplaceId: false
	}, cardmarketVendorId],
	csvTemplateExample: {
		vendorIds: {},
		printNumber: "DL2-001",
		cardName: "Blue-Eyes White Dragon",
		expansion: "Duelist Legacy Volume 2",
		expansionCode: "DL2-JP",
		finish: CardFinish.STANDARD,
		condition: "Near Mint",
		language: "Japanese",
		quantity: 1
	}
};

//#endregion
//#region src/lib/filters/game-filters.ts
const gameFilters = z.discriminatedUnion("game", [
	z.object({
		game: z.literal("sorcery"),
		filters: sorceryFilters.default({})
	}).meta({
		id: "GameFiltersSorcery",
		title: "Sorcery"
	}),
	z.object({
		game: z.literal("lorcana"),
		filters: lorcanaFilters.default({})
	}).meta({
		id: "GameFiltersLorcana",
		title: "Lorcana"
	}),
	z.object({
		game: z.literal("mtg"),
		filters: mtgFilters.default({})
	}).meta({
		id: "GameFiltersMTG",
		title: "Magic: The Gathering"
	}),
	z.object({
		game: z.literal("fab"),
		filters: fabFilters.default({})
	}).meta({
		id: "GameFiltersFAB",
		title: "Flesh and Blood"
	}),
	z.object({
		game: z.literal("pokemon"),
		filters: pokemonFilters.default({})
	}).meta({
		id: "GameFiltersPokemon",
		title: "Pokémon"
	}),
	z.object({
		game: z.literal("wankul"),
		filters: wankulFilters.default({})
	}).meta({
		id: "GameFiltersWankul",
		title: "Wankul"
	}),
	z.object({
		game: z.literal("onepiece"),
		filters: onepieceFilters.default({})
	}).meta({
		id: "GameFiltersOnePiece",
		title: "One Piece"
	}),
	z.object({
		game: z.literal("ygo"),
		filters: yugiohFilters.default({})
	}).meta({
		id: "GameFiltersYuGiOh",
		title: "Yu-Gi-Oh!"
	}),
	z.object({
		game: z.literal("ygo-ocg"),
		filters: yugiohOcgFilters.default({})
	}).meta({
		id: "GameFiltersYuGiOhOcg",
		title: "Yu-Gi-Oh! OCG"
	}),
	z.object({
		game: z.literal("rise"),
		filters: riseFilters.default({})
	}).meta({
		id: "GameFiltersRise",
		title: "Rise"
	}),
	z.object({
		game: z.literal("riftbound"),
		filters: riftboundFilters.default({})
	}).meta({
		id: "GameFiltersRiftbound",
		title: "Riftbound"
	}),
	z.object({
		game: z.literal("drakerion"),
		filters: drakerionFilters.default({})
	}).meta({
		id: "GameFiltersDrakerion",
		title: "Drakerion"
	}),
	z.object({
		game: z.literal("cyberpunk"),
		filters: cyberpunkFilters.default({})
	}).meta({
		id: "GameFiltersCyberpunk",
		title: "Cyberpunk"
	}),
	z.object({
		game: z.literal("pokemon-japan"),
		filters: pokemonJapanFilters.default({})
	}).meta({
		id: "GameFiltersPokemonJapan",
		title: "Pokémon (Japan)"
	}),
	z.object({
		game: z.literal("swu"),
		filters: starWarsUnlimitedFilters.default({})
	}).meta({
		id: "GameFiltersSWU",
		title: "Star Wars: Unlimited"
	}),
	z.object({
		game: z.literal("example-game"),
		filters: exampleGameFilters.default({})
	}).meta({
		id: "GameFiltersExample",
		title: "Example Game"
	}),
	z.object({
		game: z.literal("naruto-mythos"),
		filters: narutoMythosFilters.default({})
	}).meta({
		id: "GameFiltersNarutoMythos",
		title: "Naruto Mythos"
	}),
	z.object({
		game: z.literal("grand-archive"),
		filters: grandArchiveFilters.default({})
	}).meta({
		id: "GameFiltersGrandArchive",
		title: "Grand Archive"
	}),
	z.object({
		game: z.literal("eoa"),
		filters: eoaFilters.default({})
	}).meta({
		id: "GameFiltersEoa",
		title: "Echoes of Astra"
	}),
	z.object({
		game: z.literal("dbs-fusion"),
		filters: dbsFusionFilters.default({})
	}).meta({
		id: "GameFiltersDBSFusion",
		title: "Dragon Ball Super: Fusion World"
	}),
	z.object({
		game: z.literal("dbs-masters"),
		filters: dbsMastersFilters.default({})
	}).meta({
		id: "GameFiltersDBSMasters",
		title: "Dragon Ball Super: Masters"
	}),
	z.object({
		game: z.literal("gundam"),
		filters: gundamFilters.default({})
	}).meta({
		id: "GameFiltersGundam",
		title: "Gundam Card Game"
	}),
	z.object({
		game: z.literal("chrono-core"),
		filters: chronoCoreFilters.default({})
	}).meta({
		id: "GameFiltersChronoCore",
		title: "Chrono Core"
	}),
	z.object({
		game: z.literal("palworld"),
		filters: palworldFilters.default({})
	}).meta({
		id: "GameFiltersPalworld",
		title: "Palworld TCG"
	}),
	z.object({
		game: z.literal("azuki"),
		filters: azukiFilters.default({})
	}).meta({
		id: "GameFiltersAzuki",
		title: "Azuki TCG"
	}),
	z.object({
		game: z.literal("dreamland"),
		filters: dreamlandFilters.default({})
	}).meta({
		id: "GameFiltersDreamland",
		title: "Dreamland TCG"
	})
]);

//#endregion
//#region src/lib/filters/product-filters.ts
const productFilters = z.object({
	change24hUs: zodNumericFilter.optional(),
	change24hEu: zodNumericFilter.optional(),
	change7dUs: zodNumericFilter.optional(),
	change7dEu: zodNumericFilter.optional(),
	change30dUs: zodNumericFilter.optional(),
	change30dEu: zodNumericFilter.optional(),
	priceUs: zodNumericFilter.optional(),
	priceEu: zodNumericFilter.optional(),
	productType: makeZodEnumFilter(ProductType).optional(),
	productCategory: z.string().optional()
});

//#endregion
//#region src/lib/filters.ts
let FiltersGroupType = /* @__PURE__ */ function(FiltersGroupType) {
	FiltersGroupType["Basic"] = "basic";
	FiltersGroupType["Advanced"] = "advanced";
	return FiltersGroupType;
}({});

//#endregion
//#region src/lib/game-companies.ts
const gameCompanies = {
	[SupportedGames.MTG]: {
		name: "Magic: The Gathering",
		trademarkOwner: "Wizards of the Coast LLC",
		websiteUrl: "https://company.wizards.com/"
	},
	[SupportedGames.Pokemon]: {
		name: "Pokémon",
		trademarkOwner: "The Pokémon Company International",
		websiteUrl: "https://www.pokemon.com/"
	},
	[SupportedGames.Lorcana]: {
		name: "Disney Lorcana",
		trademarkOwner: "Disney",
		websiteUrl: "https://www.disneylorcana.com/"
	},
	[SupportedGames.YuGiOh]: {
		name: "Yu-Gi-Oh!",
		trademarkOwner: "Konami Digital Entertainment",
		websiteUrl: "https://www.konami.com/"
	},
	[SupportedGames.YuGiOhOcg]: {
		name: "Yu-Gi-Oh!",
		trademarkOwner: "Konami Digital Entertainment",
		websiteUrl: "https://www.konami.com/"
	},
	[SupportedGames.OnePiece]: {
		name: "One Piece Card Game",
		trademarkOwner: "Bandai Namco Entertainment Inc.",
		websiteUrl: "https://en.onepiece-cardgame.com/"
	},
	[SupportedGames.FAB]: {
		name: "Flesh and Blood",
		trademarkOwner: "Legend Story Studios",
		websiteUrl: "https://fabtcg.com/"
	},
	[SupportedGames.Sorcery]: {
		name: "Sorcery: Contested Realm",
		trademarkOwner: "Erik's Curiosa Limited",
		websiteUrl: "https://sorcerytcg.com/"
	},
	[SupportedGames.Wankul]: {
		name: "Wankul",
		trademarkOwner: "Wankul",
		websiteUrl: "https://wankul.fr/"
	},
	[SupportedGames.ExampleGame]: {
		name: "Example TCG",
		trademarkOwner: "Example Company",
		websiteUrl: "https://example.com/"
	},
	[SupportedGames.Rise]: {
		name: "Rise",
		trademarkOwner: "Rise TCG Official",
		websiteUrl: "https://www.risetcgofficial.com/"
	},
	[SupportedGames.Riftbound]: {
		name: "Riftbound",
		trademarkOwner: "Riftbound Games",
		websiteUrl: "https://riftboundtcg.com/"
	},
	[SupportedGames.Drakerion]: {
		name: "Drakerion",
		trademarkOwner: "LoreMaster Game",
		websiteUrl: "https://www.drakerion.com/"
	},
	[SupportedGames.Cyberpunk]: {
		name: "Cyberpunk TCG",
		trademarkOwner: "CD PROJEKT RED",
		websiteUrl: "https://www.cyberpunk.net/"
	},
	[SupportedGames.PokemonJapan]: {
		name: "Pokémon Card Game",
		trademarkOwner: "The Pokémon Company",
		websiteUrl: "https://www.pokemon-card.com/"
	},
	[SupportedGames.StarWarsUnlimited]: {
		name: "Star Wars: Unlimited",
		trademarkOwner: "Fantasy Flight Games / Lucasfilm Ltd.",
		websiteUrl: "https://starwarsunlimited.com/"
	},
	[SupportedGames.NarutoMythos]: {
		name: "Naruto Mythos TCG",
		trademarkOwner: "Naruto Mythos",
		websiteUrl: "https://www.narutotcgmythos.com/"
	},
	[SupportedGames.GrandArchive]: {
		name: "Grand Archive TCG",
		trademarkOwner: "Weebs of the Shore LLC",
		websiteUrl: "https://grandarchivetcg.com/"
	},
	[SupportedGames.Eoa]: {
		name: "Echoes of Astra",
		trademarkOwner: "Echoes of Astra",
		websiteUrl: "https://echoesofastra.com/"
	},
	[SupportedGames.DbsFusion]: {
		name: "Dragon Ball Super: Fusion World",
		trademarkOwner: "Bandai Co., Ltd.",
		websiteUrl: "https://www.dbs-cardgame.com/"
	},
	[SupportedGames.DbsMasters]: {
		name: "Dragon Ball Super: Masters",
		trademarkOwner: "Bandai Co., Ltd.",
		websiteUrl: "https://www.dbs-cardgame.com/"
	},
	[SupportedGames.Gundam]: {
		name: "Gundam Card Game",
		trademarkOwner: "Bandai Co., Ltd.",
		websiteUrl: "https://www.gundam-gcg.com/en/"
	},
	[SupportedGames.ChronoCore]: {
		name: "Chrono Core",
		trademarkOwner: "Chrono Core Studios, LLC",
		websiteUrl: "https://chronocoretcg.com/"
	},
	[SupportedGames.Palworld]: {
		name: "Palworld TCG",
		trademarkOwner: "Pocketpair, Inc.",
		websiteUrl: "https://en.palworld-official-cardgame.com/"
	},
	[SupportedGames.Azuki]: {
		name: "Azuki TCG",
		trademarkOwner: "Azuki Labs, Inc.",
		websiteUrl: "https://tcg.azuki.com/"
	},
	[SupportedGames.Dreamland]: {
		name: "Dreamland TCG",
		trademarkOwner: "Kettei Art",
		websiteUrl: "https://dreamlandtcg.com/"
	}
};
const priceDataSources = [{
	name: "Cardmarket",
	url: "https://www.cardmarket.com/"
}, {
	name: "TCGplayer",
	url: "https://www.tcgplayer.com/"
}];

//#endregion
//#region src/lib/game-configuration.ts
const Configurations = {
	[SupportedGames.Lorcana]: LorcanaGameConfiguration,
	[SupportedGames.Sorcery]: SorceryGameConfiguration,
	[SupportedGames.MTG]: MtgGameConfiguration,
	[SupportedGames.FAB]: FABGameConfiguration,
	[SupportedGames.Pokemon]: PokemonGameConfiguration,
	[SupportedGames.Wankul]: WankulGameConfiguration,
	[SupportedGames.OnePiece]: OnePieceGameConfiguration,
	[SupportedGames.YuGiOh]: YuGiOhGameConfiguration,
	[SupportedGames.YuGiOhOcg]: YuGiOhOcgGameConfiguration,
	[SupportedGames.ExampleGame]: ExampleGameConfiguration,
	[SupportedGames.Rise]: RiseGameConfiguration,
	[SupportedGames.Riftbound]: RiftboundGameConfiguration,
	[SupportedGames.Drakerion]: DrakerionGameConfiguration,
	[SupportedGames.Cyberpunk]: CyberpunkGameConfiguration,
	[SupportedGames.PokemonJapan]: PokemonJapanGameConfiguration,
	[SupportedGames.StarWarsUnlimited]: StarWarsUnlimitedGameConfiguration,
	[SupportedGames.NarutoMythos]: NarutoMythosGameConfiguration,
	[SupportedGames.GrandArchive]: GrandArchiveGameConfiguration,
	[SupportedGames.Eoa]: EoaGameConfiguration,
	[SupportedGames.DbsFusion]: DbsFusionGameConfiguration,
	[SupportedGames.DbsMasters]: DbsMastersGameConfiguration,
	[SupportedGames.Gundam]: GundamGameConfiguration,
	[SupportedGames.ChronoCore]: ChronoCoreGameConfiguration,
	[SupportedGames.Palworld]: PalworldGameConfiguration,
	[SupportedGames.Azuki]: AzukiGameConfiguration,
	[SupportedGames.Dreamland]: DreamlandGameConfiguration
};
function getGameConfiguration(game) {
	return Configurations[game];
}
/**
* Languages an imported product falls back to when its source lists none:
* the game's configured `defaultLanguages`, or English for unknown games and
* games without one.
*/
function getGameDefaultLanguages(game) {
	return Configurations[game]?.defaultLanguages ?? [Languages.English];
}

//#endregion
//#region src/lib/game-definitions.ts
const gameDefinitions = {
	[SupportedGames.Sorcery]: {
		name: "Sorcery: Contested Realm",
		slug: SupportedGames.Sorcery,
		logo: "/images/game-logos/sorcery.png",
		releaseDate: "2023-04-19"
	},
	[SupportedGames.Lorcana]: {
		name: "Lorcana",
		slug: SupportedGames.Lorcana,
		logo: "/images/game-logos/lorcana.png",
		releaseDate: "2023-09-01"
	},
	[SupportedGames.MTG]: {
		name: "Magic: The Gathering",
		slug: SupportedGames.MTG,
		logo: "/images/game-logos/mtg.png",
		releaseDate: "1993-08-05"
	},
	[SupportedGames.FAB]: {
		name: "Flesh and Blood",
		slug: SupportedGames.FAB,
		logo: "/images/game-logos/fab.png",
		releaseDate: "2020-03-13"
	},
	[SupportedGames.Pokemon]: {
		name: "Pokémon",
		slug: SupportedGames.Pokemon,
		logo: "/images/game-logos/pokemon.png",
		releaseDate: "1996-10-20"
	},
	[SupportedGames.Wankul]: {
		name: "Wankul",
		slug: SupportedGames.Wankul,
		logo: "/images/game-logos/wankul.png",
		releaseDate: "2023-11-01"
	},
	[SupportedGames.OnePiece]: {
		name: "One Piece Card Game",
		slug: SupportedGames.OnePiece,
		logo: "/images/game-logos/small/onepiece.png",
		releaseDate: "2022-12-02"
	},
	[SupportedGames.YuGiOh]: {
		name: "Yu-Gi-Oh!",
		slug: SupportedGames.YuGiOh,
		logo: "/images/game-logos/ygo.png",
		releaseDate: "2002-03-08"
	},
	[SupportedGames.YuGiOhOcg]: {
		name: "Yu-Gi-Oh! OCG",
		slug: SupportedGames.YuGiOhOcg,
		logo: "/images/game-logos/ygo-ocg.png",
		releaseDate: "1999-02-04"
	},
	[SupportedGames.ExampleGame]: {
		name: "Example Game",
		slug: SupportedGames.ExampleGame,
		logo: "/images/game-logos/example-game.png",
		releaseDate: "2025-08-01"
	},
	[SupportedGames.Rise]: {
		name: "Rise TCG",
		slug: "rise",
		logo: "/images/game-logos/rise.png",
		releaseDate: "2020-05-01"
	},
	[SupportedGames.Riftbound]: {
		name: "Riftbound: League of Legends TCG",
		slug: "riftbound",
		logo: "/images/game-logos/riftbound.png",
		releaseDate: "2025-10-31"
	},
	[SupportedGames.Drakerion]: {
		name: "Drakerion",
		slug: SupportedGames.Drakerion,
		logo: "/images/game-logos/drakerion.png",
		releaseDate: "2025-03-30"
	},
	[SupportedGames.Cyberpunk]: {
		name: "Cyberpunk TCG",
		slug: SupportedGames.Cyberpunk,
		logo: "/images/game-logos/cyberpunk.png",
		releaseDate: "2025-01-01"
	},
	[SupportedGames.PokemonJapan]: {
		name: "Pokemon Japan",
		slug: SupportedGames.PokemonJapan,
		logo: "/images/game-logos/pokemon-japan.png",
		releaseDate: "1996-10-20"
	},
	[SupportedGames.StarWarsUnlimited]: {
		name: "Star Wars: Unlimited",
		slug: SupportedGames.StarWarsUnlimited,
		logo: "/images/game-logos/swu.png",
		releaseDate: "2024-03-08"
	},
	[SupportedGames.NarutoMythos]: {
		name: "Naruto Mythos TCG",
		slug: SupportedGames.NarutoMythos,
		logo: "/images/game-logos/naruto-mythos.png",
		releaseDate: "2026-03-13"
	},
	[SupportedGames.GrandArchive]: {
		name: "Grand Archive TCG",
		slug: SupportedGames.GrandArchive,
		logo: "/images/game-logos/grand-archive.png",
		releaseDate: "2023-04-28"
	},
	[SupportedGames.Eoa]: {
		name: "Echoes of Astra",
		slug: SupportedGames.Eoa,
		logo: "/images/game-logos/eoa.png",
		releaseDate: "2026-08-30"
	},
	[SupportedGames.DbsFusion]: {
		name: "Dragon Ball Super: Fusion World",
		slug: "dbs-fusion",
		logo: "/images/game-logos/dbs-fusion.png",
		releaseDate: "2024-02-23"
	},
	[SupportedGames.DbsMasters]: {
		name: "Dragon Ball Super: Masters",
		slug: "dbs-masters",
		logo: "/images/game-logos/dbs-masters.png",
		releaseDate: "2017-07-28"
	},
	[SupportedGames.Gundam]: {
		name: "Gundam Card Game",
		slug: "gundam",
		logo: "/images/game-logos/gundam.png",
		releaseDate: "2025-02-06"
	},
	[SupportedGames.ChronoCore]: {
		name: "Chrono Core",
		slug: SupportedGames.ChronoCore,
		logo: "/images/game-logos/chrono-core.png",
		releaseDate: "2026-05-22"
	},
	[SupportedGames.Palworld]: {
		name: "Palworld TCG",
		slug: SupportedGames.Palworld,
		logo: "/images/game-logos/palworld.png",
		releaseDate: "2026-07-30"
	},
	[SupportedGames.Azuki]: {
		name: "Azuki TCG",
		slug: SupportedGames.Azuki,
		logo: "/images/game-logos/azuki.png",
		releaseDate: "2026-06-26"
	},
	[SupportedGames.Dreamland]: {
		name: "Dreamland TCG",
		slug: SupportedGames.Dreamland,
		logo: "/images/game-logos/dreamland.png",
		releaseDate: "2026-09-11"
	}
};

//#endregion
//#region src/lib/game-display-order.ts
const GAME_DISPLAY_ORDER = [
	SupportedGames.MTG,
	SupportedGames.Pokemon,
	SupportedGames.YuGiOh,
	SupportedGames.Sorcery,
	SupportedGames.NarutoMythos,
	SupportedGames.OnePiece,
	SupportedGames.Riftbound,
	SupportedGames.Lorcana,
	SupportedGames.FAB,
	SupportedGames.PokemonJapan,
	SupportedGames.StarWarsUnlimited,
	SupportedGames.Rise,
	SupportedGames.Cyberpunk,
	SupportedGames.DbsFusion,
	SupportedGames.DbsMasters,
	SupportedGames.GrandArchive,
	SupportedGames.Eoa,
	SupportedGames.Gundam,
	SupportedGames.ChronoCore,
	SupportedGames.Wankul,
	SupportedGames.YuGiOhOcg,
	SupportedGames.ExampleGame,
	SupportedGames.Drakerion,
	SupportedGames.Palworld,
	SupportedGames.Azuki,
	SupportedGames.Dreamland
];
const CANONICAL_RANK = new Map(GAME_DISPLAY_ORDER.map((slug, index) => [slug, index]));
function canonicalGameRank(slug) {
	const rank = CANONICAL_RANK.get(slug);
	return rank === void 0 ? Number.POSITIVE_INFINITY : rank;
}
function reorderGamesByPreference(items, myGameSlugs, getSlug) {
	const mine = new Set(myGameSlugs);
	return [...items].sort((a, b) => {
		const aMine = mine.has(getSlug(a)) ? 0 : 1;
		const bMine = mine.has(getSlug(b)) ? 0 : 1;
		if (aMine !== bMine) return aMine - bMine;
		const ra = canonicalGameRank(getSlug(a));
		const rb = canonicalGameRank(getSlug(b));
		return ra === rb ? 0 : ra < rb ? -1 : 1;
	});
}

//#endregion
//#region src/lib/game-filters.ts
const GameFilters = {
	[SupportedGames.Lorcana]: lorcanaFilters,
	[SupportedGames.Sorcery]: sorceryFilters,
	[SupportedGames.MTG]: mtgFilters,
	[SupportedGames.FAB]: fabFilters,
	[SupportedGames.Pokemon]: pokemonFilters,
	[SupportedGames.Wankul]: wankulFilters,
	[SupportedGames.OnePiece]: onepieceFilters,
	[SupportedGames.YuGiOh]: yugiohFilters,
	[SupportedGames.YuGiOhOcg]: yugiohOcgFilters,
	[SupportedGames.ExampleGame]: exampleGameFilters,
	[SupportedGames.Rise]: riseFilters,
	[SupportedGames.Riftbound]: riftboundFilters,
	[SupportedGames.Drakerion]: drakerionFilters,
	[SupportedGames.Cyberpunk]: cyberpunkFilters,
	[SupportedGames.PokemonJapan]: pokemonJapanFilters,
	[SupportedGames.StarWarsUnlimited]: starWarsUnlimitedFilters,
	[SupportedGames.NarutoMythos]: narutoMythosFilters,
	[SupportedGames.GrandArchive]: grandArchiveFilters,
	[SupportedGames.Eoa]: eoaFilters,
	[SupportedGames.DbsFusion]: dbsFusionFilters,
	[SupportedGames.DbsMasters]: dbsMastersFilters,
	[SupportedGames.Gundam]: gundamFilters,
	[SupportedGames.ChronoCore]: chronoCoreFilters,
	[SupportedGames.Palworld]: palworldFilters,
	[SupportedGames.Azuki]: azukiFilters,
	[SupportedGames.Dreamland]: dreamlandFilters
};

//#endregion
//#region src/lib/game-helpers.ts
const CARD_CONDITION_VALUE_SET = new Set(Object.values(CardCondition));
const SHORT_CONDITION_MAPPING = {
	[SupportedMarketplace.Tcgplayer]: {
		[CardCondition.NEAR_MINT]: ShortAmericanCardCondition.NM,
		[CardCondition.LIGHTLY_PLAYED]: ShortAmericanCardCondition.LP,
		[CardCondition.MODERATELY_PLAYED]: ShortAmericanCardCondition.MP,
		[CardCondition.HEAVILY_PLAYED]: ShortAmericanCardCondition.HP,
		[CardCondition.DAMAGED]: ShortAmericanCardCondition.DMG
	},
	[SupportedMarketplace.Cardmarket]: {
		[CardCondition.NEAR_MINT]: ShortEuropeanCardCondition.NM,
		[CardCondition.LIGHTLY_PLAYED]: ShortEuropeanCardCondition.EX,
		[CardCondition.MODERATELY_PLAYED]: ShortEuropeanCardCondition.GD,
		[CardCondition.HEAVILY_PLAYED]: ShortEuropeanCardCondition.PL,
		[CardCondition.DAMAGED]: ShortEuropeanCardCondition.PO
	}
};
const FULL_CONDITION_MAPPING = {
	[SupportedMarketplace.Tcgplayer]: {
		[CardCondition.NEAR_MINT]: AmericanCardCondition.NEAR_MINT,
		[CardCondition.LIGHTLY_PLAYED]: AmericanCardCondition.LIGHTLY_PLAYED,
		[CardCondition.MODERATELY_PLAYED]: AmericanCardCondition.MODERATELY_PLAYED,
		[CardCondition.HEAVILY_PLAYED]: AmericanCardCondition.HEAVILY_PLAYED,
		[CardCondition.DAMAGED]: AmericanCardCondition.DAMAGED
	},
	[SupportedMarketplace.Cardmarket]: {
		[CardCondition.NEAR_MINT]: EuropeanCardCondition.NEAR_MINT,
		[CardCondition.LIGHTLY_PLAYED]: EuropeanCardCondition.EXCELLENT,
		[CardCondition.MODERATELY_PLAYED]: EuropeanCardCondition.GOOD,
		[CardCondition.HEAVILY_PLAYED]: EuropeanCardCondition.PLAYED,
		[CardCondition.DAMAGED]: EuropeanCardCondition.POOR
	}
};
const CONDITION_TO_BACKEND_MAPPING = {
	"Near Mint": CardCondition.NEAR_MINT,
	NM: CardCondition.NEAR_MINT,
	[AmericanCardCondition.LIGHTLY_PLAYED]: CardCondition.LIGHTLY_PLAYED,
	[AmericanCardCondition.MODERATELY_PLAYED]: CardCondition.MODERATELY_PLAYED,
	[AmericanCardCondition.HEAVILY_PLAYED]: CardCondition.HEAVILY_PLAYED,
	[AmericanCardCondition.DAMAGED]: CardCondition.DAMAGED,
	[EuropeanCardCondition.EXCELLENT]: CardCondition.LIGHTLY_PLAYED,
	[EuropeanCardCondition.GOOD]: CardCondition.MODERATELY_PLAYED,
	[EuropeanCardCondition.PLAYED]: CardCondition.HEAVILY_PLAYED,
	[EuropeanCardCondition.POOR]: CardCondition.DAMAGED,
	[ShortAmericanCardCondition.LP]: CardCondition.LIGHTLY_PLAYED,
	[ShortAmericanCardCondition.MP]: CardCondition.MODERATELY_PLAYED,
	[ShortAmericanCardCondition.HP]: CardCondition.HEAVILY_PLAYED,
	[ShortAmericanCardCondition.DMG]: CardCondition.DAMAGED,
	[ShortEuropeanCardCondition.EX]: CardCondition.LIGHTLY_PLAYED,
	[ShortEuropeanCardCondition.GD]: CardCondition.MODERATELY_PLAYED,
	[ShortEuropeanCardCondition.PL]: CardCondition.HEAVILY_PLAYED,
	[ShortEuropeanCardCondition.PO]: CardCondition.DAMAGED
};
const isConditionAttribute = (attribute) => {
	if (attribute.type !== "enum") return false;
	const attributeKeys = Object.keys(attribute.values);
	if (attributeKeys.length !== CARD_CONDITION_VALUE_SET.size) return false;
	return attributeKeys.every((key) => CARD_CONDITION_VALUE_SET.has(key));
};
/** i18n key for the sealed-product "Unopened" label. */
const UNOPENED_TRANSLATION_KEY = "global.attributes.condition.values.unopened";
const resolveUnopenedLabel = (t) => t ? t(UNOPENED_TRANSLATION_KEY) : "Unopened";
/**
* Get the short condition code (e.g., "NM", "LP") for a card condition.
*
* For sealed products, returns "Unopened" (or its translation when a
* translator is supplied). Pass `t` when calling from a surface that has i18n
* context so the label is localized; omit it for backend/CSV contexts where
* the English literal is acceptable.
*/
const getShortCondition = (condition, marketplace, productType, t) => {
	if (productType === ProductType.SEALED) return resolveUnopenedLabel(t);
	return SHORT_CONDITION_MAPPING[marketplace][condition];
};
/**
* Get the full text condition (e.g., "Near Mint", "Lightly Played") for a card
* condition.
*
* For sealed products, returns "Unopened" (or its translation when a
* translator is supplied). Pass `t` when calling from a surface that has i18n
* context so the label is localized; omit it for backend/CSV contexts where
* the English literal is acceptable.
*/
const getFullCondition = (condition, marketplace, productType, t) => {
	if (productType === ProductType.SEALED) return resolveUnopenedLabel(t);
	return FULL_CONDITION_MAPPING[marketplace][condition];
};
/** Get the condition text in either short or full format. */
const getCondition = (condition, marketplace, format = "full", productType, t) => {
	return format === "short" ? getShortCondition(condition, marketplace, productType, t) : getFullCondition(condition, marketplace, productType, t);
};
/**
* Get the game icon filename for the given theme.
*/
const getGameIcon = (gameSlug, theme = "dark") => {
	const gameConfig = Configurations[gameSlug];
	if (!gameConfig) return void 0;
	return gameConfig.icon[theme];
};
/**
* Get the game image/logo filename for the given theme.
*/
const getGameImage = (gameSlug, theme = "dark") => {
	const gameConfig = Configurations[gameSlug];
	if (!gameConfig) return void 0;
	return gameConfig.image[theme];
};
/**
* Get the full local path to a game icon for the given theme.
* @returns Path like "/images/game-logos/dark/mtg-icon.png"
*/
const getGameIconPath = (gameSlug, theme = "dark") => {
	const icon = getGameIcon(gameSlug, theme);
	if (!icon) return void 0;
	return `/images/game-logos/${theme}/${icon}`;
};
/**
* Get the full local path to a game logo/image for the given theme.
* @returns Path like "/images/game-logos/dark/mtg.png"
*/
const getGameImagePath = (gameSlug, theme = "dark") => {
	const image = getGameImage(gameSlug, theme);
	if (!image) return void 0;
	return `/images/game-logos/${theme}/${image}`;
};
/**
* Get the full game name from a game slug
* @param gameSlug The slug of the game (e.g., "mtg", "sorcery")
* @returns The full name of the game (e.g., "Magic: The Gathering", "Sorcery: Contested Realms")
* or undefined if the game slug is not found
*/
const getGameNameFromSlug = (gameSlug) => {
	const gameConfig = Configurations[gameSlug];
	if (gameConfig) return gameConfig.name;
};
/**
* Default variant values for each game
* These variants are considered "standard" and should not be displayed to avoid redundancy
*/
const DEFAULT_VARIANTS_BY_GAME = {
	[SupportedGames.Sorcery]: ["Booster"],
	[SupportedGames.Pokemon]: [],
	[SupportedGames.Lorcana]: [],
	[SupportedGames.MTG]: [],
	[SupportedGames.FAB]: [],
	[SupportedGames.Wankul]: [],
	[SupportedGames.OnePiece]: [],
	[SupportedGames.YuGiOh]: [],
	[SupportedGames.YuGiOhOcg]: [],
	[SupportedGames.Rise]: ["Standard"],
	[SupportedGames.ExampleGame]: [],
	[SupportedGames.Riftbound]: ["Standard"],
	[SupportedGames.Drakerion]: [],
	[SupportedGames.Cyberpunk]: [],
	[SupportedGames.PokemonJapan]: [],
	[SupportedGames.StarWarsUnlimited]: [],
	[SupportedGames.NarutoMythos]: [],
	[SupportedGames.GrandArchive]: [],
	[SupportedGames.Eoa]: ["Normal"],
	[SupportedGames.DbsFusion]: [],
	[SupportedGames.DbsMasters]: [],
	[SupportedGames.Gundam]: [],
	[SupportedGames.ChronoCore]: [],
	[SupportedGames.Palworld]: [],
	[SupportedGames.Azuki]: [],
	[SupportedGames.Dreamland]: []
};
/**
* Get the variant value for display, returning null if it's a default value for the game
* @param variant The variant value to check
* @param gameSlug The slug of the game
* @returns The variant value if it should be displayed, null if it's a default value
* @example
* getDisplayVariant("Booster", "sorcery") // returns null (don't display)
* getDisplayVariant("Promo", "sorcery") // returns "Promo" (display it)
* getDisplayVariant("Unlimited", "pokemon") // returns null (don't display)
* getDisplayVariant("1st Edition", "pokemon") // returns "1st Edition" (display it)
*/
const getDisplayVariant = (variant, gameSlug) => {
	if (!variant) return null;
	const defaultVariants = DEFAULT_VARIANTS_BY_GAME[gameSlug];
	if (!defaultVariants) return variant;
	return defaultVariants.includes(variant) ? null : variant;
};
/**
* Get the lowest foil finish for a game based on its finish hierarchy
* @param gameSlug The slug of the game
* @returns The lowest foil finish (first non-standard finish) or CardFinish.FOIL as fallback
* @example
* getLowestFoilForGame("fab") // returns CardFinish.COLD_FOIL
* getLowestFoilForGame("mtg") // returns CardFinish.FOIL (fallback)
*/
const getLowestFoilForGame = (gameSlug) => {
	const gameConfig = Configurations[gameSlug];
	if (!gameConfig || !gameConfig.finishHierarchy || gameConfig.finishHierarchy.length < 2) return CardFinish.FOIL;
	return gameConfig.finishHierarchy.filter((finish) => finish !== CardFinish.STANDARD)[0] || CardFinish.FOIL;
};
/**
* Every finish rendered with the foil treatment (gold badge, foil icon),
* as opposed to standard/etched-style finishes.
*/
const FOIL_FINISHES = new Set([
	CardFinish.FOIL,
	CardFinish.RAINBOW_FOIL,
	CardFinish.COLD_FOIL,
	CardFinish.GOLD_FOIL,
	CardFinish.BLAST_FOIL_RAINBOW,
	CardFinish.BUBBLES_FOIL,
	CardFinish.CRACKED_ICE_FOIL,
	CardFinish.GALAXY_FOIL,
	CardFinish.GLOSSY_FOIL,
	CardFinish.MULTI_SIDELINE_FOIL,
	CardFinish.STAR_FOIL,
	CardFinish.SURGE_FOIL,
	CardFinish.HOLOGRAPHIC,
	CardFinish.RAINBOW,
	CardFinish.REVERSE,
	CardFinish.HOLOFOIL
]);
const isFoilFinish = (finish) => FOIL_FINISHES.has(finish);
const CONDITION_TRANSLATION_KEYS = {
	[CardCondition.NEAR_MINT]: "nearMint",
	[CardCondition.LIGHTLY_PLAYED]: "lightlyPlayed",
	[CardCondition.MODERATELY_PLAYED]: "moderatelyPlayed",
	[CardCondition.HEAVILY_PLAYED]: "heavilyPlayed",
	[CardCondition.DAMAGED]: "damaged"
};
/**
* Get the translation key suffix for a card condition.
* Use with `global.attributes.condition.values.${key}` to get the translated label.
* @param condition The card condition enum value
* @param productType Optional product type - returns "unopened" for sealed products
* @returns The translation key suffix (e.g., "nearMint", "lightlyPlayed", "unopened")
*/
const getConditionTranslationKey = (condition, productType) => {
	if (productType === ProductType.SEALED) return "unopened";
	return CONDITION_TRANSLATION_KEYS[condition];
};
/**
* Fields that exist on BaseCard and should NOT be duplicated in the attributes object.
* These are mapped separately at the card product level (printNumber, rarity, variant, finishes).
*/
const BASE_CARD_FIELDS = [
	"name",
	"rarity",
	"finishes",
	"finish",
	"printNumber",
	"variant"
];
/**
* Gets attribute field names for a given game slug.
*
* The source of truth is the GameConfiguration.attributes object for each game.
* This ensures that only fields explicitly defined in the game configuration
* are exposed to clients.
*
* Excludes fields that are already on the base card (name, rarity, finishes, finish, printNumber, variant).
*
* @param gameSlug - The game slug to get attribute fields for
* @returns Array of field names to extract as attributes
*/
function getGameAttributeFields(gameSlug) {
	const configuration = Configurations[gameSlug];
	if (!configuration?.attributes) return [];
	return Object.keys(configuration.attributes).filter((field) => !BASE_CARD_FIELDS.includes(field));
}
/**
* Reference keys carried in `attributes` that identify the product in an
* external card database but are not CSV-import vendor-id matching keys.
*/
const ADDITIONAL_EXTERNAL_REFERENCE_KEYS = { [SupportedGames.MTG]: ["scryfallOracleId"] };
/**
* The attribute keys holding a game's external card-database references
* (e.g. `scryfallId` for MTG, `konamiId` for Yu-Gi-Oh!): the game's declared
* non-marketplace vendor ids plus the additional reference keys above.
*/
function getExternalReferenceKeys(gameSlug) {
	return [...(Configurations[gameSlug]?.vendorIds ?? []).filter((vendor) => !vendor.isMarketplaceId).map((vendor) => vendor.key), ...ADDITIONAL_EXTERNAL_REFERENCE_KEYS[gameSlug] ?? []];
}
/**
* Extracts game-specific attributes from a ProductView document.
*
* This function extracts those fields into an attributes object based on the game.
*
* @param object - The object to extract attributes from
* @param gameSlug - The game slug to determine which fields to extract
* @returns An object containing only the game-specific attributes
*/
function extractGameAttributes(object, gameSlug) {
	const fieldNames = getGameAttributeFields(gameSlug);
	const attributes = {};
	for (const field of fieldNames) {
		const value = object[field];
		if (value !== void 0) attributes[field] = value;
	}
	return attributes;
}
/**
* Clamps a marketplace preference to what the game actually supports, so a
* Cardmarket-preference user still sees TCGplayer prices on a TCGplayer-only
* game instead of no prices at all.
*/
function clampMarketplaceToGame(marketplace, gameSlug) {
	const supported = getGameConfiguration(gameSlug).supportedMarketplaces;
	if (supported.length === 0 || supported.includes(marketplace)) return marketplace;
	return supported[0];
}
function getPrintNumberAliasesAttribute(gameSlug) {
	return Configurations[gameSlug]?.printNumberAliasesAttribute;
}
/**
* Alternate printed codes of a product, read from the attribute the game
* configuration declares. Uppercased and deduped; undefined for games without one.
*/
function readGamePrintNumberAliases(gameSlug, attributes) {
	if (!gameSlug) return void 0;
	const attribute = getPrintNumberAliasesAttribute(gameSlug);
	if (!attribute || !attributes || typeof attributes !== "object") return void 0;
	const raw = attributes[attribute];
	if (!Array.isArray(raw)) return void 0;
	const codes = /* @__PURE__ */ new Set();
	for (const value of raw) {
		if (typeof value !== "string") continue;
		const code = value.trim().toUpperCase();
		if (code) codes.add(code);
	}
	return codes.size > 0 ? [...codes] : void 0;
}
/**
* The finish that represents a product when one must be picked: `Standard`
* when present, otherwise the first finish in sorted order. Undefined for an
* empty set.
*/
function primaryFinish(finishes) {
	if (!finishes || finishes.length === 0) return void 0;
	if (finishes.includes("Standard")) return "Standard";
	return [...finishes].sort()[0];
}

//#endregion
//#region src/lib/games/additional-games.ts
const additionalGameNames = [
	"Akora TCG",
	"Alpha Clash",
	"Argent Saga TCG",
	"Ashes: Rise of the Phoenixborn",
	"Bakugan TCG",
	"Battle Spirits Saga",
	"Cardfight!! Vanguard",
	"Chaotic",
	"Chrono Clash System",
	"D & D Miniatures",
	"Dice Masters",
	"Dragoborne",
	"Dragon Ball Super Card Game",
	"Dragon Ball Super: Fusion World",
	"Dragon Ball Super: Masters",
	"Dragon Ball Z TCG",
	"Duel Masters",
	"Elestrals",
	"Exodus TCG",
	"Final Fantasy TCG",
	"Force of Will",
	"Funko Pop! Vinyl",
	"Future Card Buddyfight",
	"Gate Ruler",
	"Genesis: Battle of Champions",
	"Grand Archive TCG",
	"Harry Potter TCG",
	"HeroClix",
	"Highlander: The Card Game",
	"KeyForge",
	"Kryptik",
	"Legends of Runeterra",
	"Lightseekers TCG",
	"MetaX TCG",
	"MetaZoo",
	"Munchkin CCG",
	"My Hero Academia Collectible Card Game",
	"My Little Pony CCG",
	"Osprey Books",
	"Shadowverse: Evolve",
	"Star Wars: Destiny",
	"Summoner Wars",
	"The Caster Chronicles",
	"The Spoils TCG",
	"Transformers TCG",
	"Union Arena",
	"UniVersus",
	"VS System",
	"Warhammer 40,000: Conquest",
	"Warhammer Age of Sigmar: Champions",
	"Weiss Schwarz",
	"WIXOSS",
	"World of Warcraft TCG",
	"Zatch Bell! The Card Battle",
	"Zombie World Order TCG"
];
const additionalGames = additionalGameNames.map((name) => ({
	key: slugify(name, {
		lower: true,
		strict: true,
		trim: true
	}),
	label: name
}));

//#endregion
//#region src/lib/game-search-match.ts
const GAME_SEARCH_ALIASES = {
	[SupportedGames.Pokemon]: ["pkmn"],
	[SupportedGames.PokemonJapan]: ["pokemon jp", "pokemon japanese"],
	[SupportedGames.OnePiece]: ["opcg"],
	[SupportedGames.YuGiOhOcg]: ["ocg"],
	[SupportedGames.Lorcana]: ["disney", "disney lorcana"],
	[SupportedGames.FAB]: ["flesh & blood"],
	[SupportedGames.Riftbound]: ["league of legends", "lol"],
	[SupportedGames.NarutoMythos]: ["naruto"],
	[SupportedGames.DbsMasters]: [
		"dragon ball",
		"dragonball",
		"dbs",
		"dragon ball super"
	],
	[SupportedGames.DbsFusion]: ["fusion world", "dragon ball fusion world"],
	[SupportedGames.ChronoCore]: ["chrono"]
};
const NOT_CARRIED_GAME_ALIASES = {
	Digimon: ["digimon", "digimon card game"],
	Neuroscape: ["neuroscape"],
	"Spider-Man": [
		"spiderman",
		"spider-man",
		"spider man"
	],
	Marvel: ["marvel"],
	Altered: ["altered"],
	"Cardfight!! Vanguard": ["vanguard"],
	"Weiss Schwarz": ["weiss"],
	"Battle Spirits Saga": ["battle spirits"],
	Kayou: ["kayou"],
	Bleach: ["bleach"],
	"JoJo's Bizarre Adventure": ["jojo"],
	Hearthstone: ["hearthstone"]
};
const DROPPED_SUFFIXES = [
	"cardgame",
	"tcg",
	"ccg"
];
/**
* Folds a game name or a typed query to the form both sides are compared in:
* ASCII letters and digits only, lowercased, without a trailing "TCG",
* "card game" or "CCG".
*/
function normalizeGameSearchQuery(value) {
	let normalized = value.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/ß/g, "ss").toLowerCase().replace(/[^a-z0-9]/g, "");
	for (const suffix of DROPPED_SUFFIXES) if (normalized.length > suffix.length && normalized.endsWith(suffix)) {
		normalized = normalized.slice(0, -suffix.length);
		break;
	}
	return normalized;
}
function nameCandidates(name) {
	const beforeColon = name.split(":")[0] ?? name;
	return [normalizeGameSearchQuery(name), normalizeGameSearchQuery(beforeColon)];
}
function buildCarriedLookup() {
	const lookup = /* @__PURE__ */ new Map();
	for (const slug of GAME_DISPLAY_ORDER) {
		const candidates = [...nameCandidates(gameDefinitions[slug].name), normalizeGameSearchQuery(slug)];
		for (const candidate of candidates) if (candidate && !lookup.has(candidate)) lookup.set(candidate, slug);
	}
	for (const [slug, aliases] of Object.entries(GAME_SEARCH_ALIASES)) for (const alias of aliases) lookup.set(normalizeGameSearchQuery(alias), slug);
	return lookup;
}
function buildNotCarriedLookup() {
	const lookup = /* @__PURE__ */ new Map();
	for (const game of additionalGames) for (const candidate of nameCandidates(game.label)) if (candidate && !lookup.has(candidate)) lookup.set(candidate, game.label);
	for (const [label, aliases] of Object.entries(NOT_CARRIED_GAME_ALIASES)) for (const alias of aliases) lookup.set(normalizeGameSearchQuery(alias), label);
	return lookup;
}
const CARRIED_LOOKUP = buildCarriedLookup();
const NOT_CARRIED_LOOKUP = buildNotCarriedLookup();
/**
* Recognises a search query that is a game name rather than a card name.
* A carried game is returned only when its slug is in `liveSlugs`; a known
* game that is not live, and any game from the "other games" list, comes back
* as `notCarried` with its display label. Matching is exact after
* normalisation, so a prefix typed on the way returns `null`.
*/
function matchGameSearch(query, liveSlugs) {
	const normalized = normalizeGameSearchQuery(query);
	if (!normalized) return null;
	const slug = CARRIED_LOOKUP.get(normalized);
	if (slug) {
		if (liveSlugs.includes(slug)) return {
			kind: "carried",
			slug
		};
		return {
			kind: "notCarried",
			label: gameDefinitions[slug].name
		};
	}
	const label = NOT_CARRIED_LOOKUP.get(normalized);
	if (label) return {
		kind: "notCarried",
		label
	};
	return null;
}

//#endregion
//#region src/lib/games/azuki/i18n/en.json
var en_default$24 = {
	name: "Azuki TCG",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"super_rare_1": "Super Rare ★",
				"super_rare_2": "Super Rare ★★",
				"leader": "Leader",
				"leader_1": "Leader ★",
				"leader_2": "Leader ★★",
				"gate": "Gate",
				"gate_1": "Gate ★",
				"ikz": "IKZ",
				"ikz_1": "IKZ ★"
			}
		},
		"element": {
			"name": "Element",
			"description": "The element of the card",
			"values": {
				"neutral": "Neutral",
				"water": "Water",
				"lightning": "Lightning",
				"earth": "Earth",
				"fire": "Fire"
			}
		},
		"category": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"entity": "Entity",
				"spell": "Spell",
				"weapon": "Weapon",
				"leader": "Leader",
				"gate": "Gate",
				"ikz": "IKZ"
			}
		},
		"subtypes": {
			"name": "Subtypes",
			"description": "The subtypes of the card"
		},
		"abilities": {
			"name": "Abilities",
			"description": "The abilities of the card"
		},
		"cardText": {
			"name": "Card Text",
			"description": "Card rules and abilities text"
		},
		"errata": {
			"name": "Errata",
			"description": "Corrected wording published after the card was printed"
		},
		"ikzCost": {
			"name": "IKZ Cost",
			"description": "IKZ cost to play the card"
		},
		"attack": {
			"name": "Attack",
			"description": "Attack value of the card"
		},
		"health": {
			"name": "Health",
			"description": "Health value of the card"
		},
		"gatePower": {
			"name": "Gate Power",
			"description": "Gate power value of the card"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum four copies of each card allowed in a deck"
		}
	}
};

//#endregion
//#region src/lib/games/azuki/i18n/fr.json
var fr_default$24 = {
	name: "Azuki TCG",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"super_rare_1": "Super Rare ★",
				"super_rare_2": "Super Rare ★★",
				"leader": "Leader",
				"leader_1": "Leader ★",
				"leader_2": "Leader ★★",
				"gate": "Gate",
				"gate_1": "Gate ★",
				"ikz": "IKZ",
				"ikz_1": "IKZ ★"
			}
		},
		"element": {
			"name": "Élément",
			"description": "L'élément de la carte",
			"values": {
				"neutral": "Neutre",
				"water": "Eau",
				"lightning": "Foudre",
				"earth": "Terre",
				"fire": "Feu"
			}
		},
		"category": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"entity": "Entité",
				"spell": "Sort",
				"weapon": "Arme",
				"leader": "Leader",
				"gate": "Gate",
				"ikz": "IKZ"
			}
		},
		"subtypes": {
			"name": "Sous-types",
			"description": "Les sous-types de la carte"
		},
		"abilities": {
			"name": "Capacités",
			"description": "Les capacités de la carte"
		},
		"cardText": {
			"name": "Texte de la carte",
			"description": "Texte des règles et capacités de la carte"
		},
		"errata": {
			"name": "Errata",
			"description": "Formulation corrigée publiée après l'impression de la carte"
		},
		"ikzCost": {
			"name": "Coût IKZ",
			"description": "Coût en IKZ pour jouer la carte"
		},
		"attack": {
			"name": "Attaque",
			"description": "Valeur d'attaque de la carte"
		},
		"health": {
			"name": "Points de vie",
			"description": "Valeur de points de vie de la carte"
		},
		"gatePower": {
			"name": "Puissance de Gate",
			"description": "Valeur de puissance de Gate de la carte"
		}
	},
	presets: {
		"standard": {
			"name": "Set standard",
			"description": "Un set complet avec un exemplaire de chaque carte"
		},
		"playset": {
			"name": "Playset",
			"description": "Un set complet avec le maximum de quatre exemplaires de chaque carte autorisés dans un deck"
		}
	}
};

//#endregion
//#region src/lib/games/chrono-core/i18n/en.json
var en_default$23 = {
	name: "Chrono Core",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"secret": "Secret",
				"promo": "Promo"
			}
		},
		"cardType": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"configuration": "Configuration",
				"core": "Core",
				"equipment": "Equipment",
				"gem": "Gem",
				"pilot": "Pilot",
				"support": "Support",
				"weapon": "Weapon"
			}
		},
		"subtype": {
			"name": "Subtype",
			"description": "The subtype of the card",
			"values": {
				"auxiliary": "Auxiliary",
				"gauntlets": "Gauntlets",
				"greaves": "Greaves",
				"onehanded_melee": "One-Handed Melee",
				"onehanded_ranged": "One-Handed Ranged",
				"onehanded_shield": "One-Handed Shield",
				"twohanded_melee": "Two-Handed Melee",
				"twohanded_ranged": "Two-Handed Ranged",
				"visor": "Visor"
			}
		},
		"chassisType": {
			"name": "Chassis Type",
			"description": "The chassis type of the card",
			"values": {
				"lightweight": "Lightweight",
				"midweight": "Midweight",
				"heavyweight": "Heavyweight"
			}
		},
		"traits": {
			"name": "Traits",
			"description": "The traits of the card",
			"values": {
				"gadget": "Gadget",
				"wildcard": "Wildcard"
			}
		},
		"damageType": {
			"name": "Damage Type",
			"description": "The damage type of the card",
			"values": {
				"ballistic": "Ballistic",
				"beam": "Beam",
				"plasma": "Plasma",
				"strike": "Strike"
			}
		},
		"realm": {
			"name": "Realm",
			"description": "The realm of the card",
			"values": {
				"artifice": "Artifice",
				"castra": "Castra",
				"nekris": "Nekris",
				"umbra": "Umbra"
			}
		},
		"equipRequirements": {
			"name": "Equip Requirements",
			"description": "The equip requirements of the card",
			"values": {
				"all_slots": "All Slots",
				"auxiliary": "Auxiliary",
				"equipment": "Equipment",
				"gauntlets": "Gauntlets",
				"greaves": "Greaves",
				"onehanded_melee": "One-Handed Melee",
				"onehanded_ranged": "One-Handed Ranged",
				"onehanded_shield": "One-Handed Shield",
				"twohanded_melee": "Two-Handed Melee",
				"twohanded_ranged": "Two-Handed Ranged",
				"visor": "Visor",
				"weapons": "Weapons"
			}
		},
		"orientation": {
			"name": "Orientation",
			"description": "The orientation of the card",
			"values": {
				"portrait": "Portrait",
				"landscape": "Landscape"
			}
		},
		"mkValue": {
			"name": "MK Value",
			"description": "The MK value of the card",
			"values": {
				"mki": "MKI",
				"mkii": "MKII",
				"mkiii": "MKIII",
				"mkiv": "MKIV"
			}
		},
		"timingRestrictions": {
			"name": "Timing Restrictions",
			"description": "The timing restrictions of the card",
			"values": {
				"active_main": "Active Main",
				"beginning_of_turn": "Beginning of Turn",
				"counter_attack": "Counter Attack",
				"counter_play": "Counter Play",
				"on_attack": "On Attack",
				"on_overclock": "On Overclock",
				"on_play": "On Play",
				"on_reveal": "On Reveal",
				"on_scrap": "On Scrap",
				"overclock": "Overclock"
			}
		},
		"keywords": {
			"name": "Keywords",
			"description": "The keywords of the card",
			"values": {
				"cloaking": "Cloaking",
				"data_storm": "Data Storm",
				"datamine": "Datamine",
				"decoding": "Decoding",
				"feedback_loop": "Feedback Loop",
				"firewall": "Firewall",
				"lockout": "Lockout",
				"optimization_protocol": "Optimization Protocol",
				"overshield": "Overshield",
				"power_burst": "Power Burst",
				"quantum_blow": "Quantum Blow",
				"reconfigure": "Reconfigure",
				"regenerate": "Regenerate",
				"reinforce": "Reinforce",
				"renew": "Renew",
				"rootkit": "Rootkit",
				"scrapheap": "Scrapheap",
				"shockwave": "Shockwave",
				"short_circuit": "Short Circuit",
				"siphon": "Siphon",
				"system_override": "System Override",
				"system_reboot": "System Reboot",
				"virus": "Virus",
				"worm": "Worm"
			}
		},
		"origins": {
			"name": "Origins",
			"description": "The origins of the card",
			"values": {
				"boxtopper": "Boxtopper",
				"chrono_core_crew": "Chrono Core Crew",
				"gen_con": "Gen Con",
				"houston_premier_event": "Houston Premier Event",
				"kickstarter_exclusive": "Kickstarter Exclusive",
				"release_event_kit": "Release Event Kit"
			}
		},
		"maxShieldValue": {
			"name": "Max Shield Value",
			"description": "Maximum shield value of the card"
		},
		"maxLifeValue": {
			"name": "Max Life Value",
			"description": "Maximum life value of the card"
		},
		"coreCost": {
			"name": "Core Cost",
			"description": "Core cost to play the card"
		},
		"damageValue": {
			"name": "Damage Value",
			"description": "Damage value of the card"
		},
		"chargeValue": {
			"name": "Charge Value",
			"description": "Charge value of the card"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "Card rules and abilities text"
		},
		"variant": {
			"name": "Variant",
			"description": "The printing variant/treatment of the card",
			"values": {
				"normal": "Normal",
				"immersive_art": "Immersive Art",
				"serialized": "Serialized",
				"artist_signature": "Artist Signature",
				"circuitry": "Circuitry",
				"early_adopter": "Early Adopter",
				"participant": "Participant",
				"rilynn_art": "Rilynn Art",
				"top_4": "Top 4",
				"top_8": "Top 8",
				"winner": "Winner"
			}
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card - standard finish where available, holographic otherwise"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck or loadout - standard finish where available, holographic otherwise"
		},
		"foil": {
			"name": "Holographic Set",
			"description": "A complete set with one copy of each card in holographic finish"
		},
		"foilPlayset": {
			"name": "Holographic Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck or loadout in holographic finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in all finishes"
		}
	}
};

//#endregion
//#region src/lib/games/chrono-core/i18n/fr.json
var fr_default$23 = {
	name: "Chrono Core",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"super_rare": "Super rare",
				"secret": "Secrète",
				"promo": "Promo"
			}
		},
		"cardType": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"configuration": "Configuration",
				"core": "Core",
				"equipment": "Équipement",
				"gem": "Gemme",
				"pilot": "Pilote",
				"support": "Soutien",
				"weapon": "Arme"
			}
		},
		"subtype": {
			"name": "Sous-type",
			"description": "Le sous-type de la carte",
			"values": {
				"auxiliary": "Auxiliaire",
				"gauntlets": "Gantelets",
				"greaves": "Jambières",
				"onehanded_melee": "Mêlée à une main",
				"onehanded_ranged": "Distance à une main",
				"onehanded_shield": "Bouclier à une main",
				"twohanded_melee": "Mêlée à deux mains",
				"twohanded_ranged": "Distance à deux mains",
				"visor": "Visière"
			}
		},
		"chassisType": {
			"name": "Type de châssis",
			"description": "Le type de châssis de la carte",
			"values": {
				"lightweight": "Léger",
				"midweight": "Moyen",
				"heavyweight": "Lourd"
			}
		},
		"traits": {
			"name": "Traits",
			"description": "Les traits de la carte",
			"values": {
				"gadget": "Gadget",
				"wildcard": "Joker"
			}
		},
		"damageType": {
			"name": "Type de dégâts",
			"description": "Le type de dégâts de la carte",
			"values": {
				"ballistic": "Balistique",
				"beam": "Rayon",
				"plasma": "Plasma",
				"strike": "Frappe"
			}
		},
		"realm": {
			"name": "Royaume",
			"description": "Le royaume de la carte",
			"values": {
				"artifice": "Artifice",
				"castra": "Castra",
				"nekris": "Nekris",
				"umbra": "Umbra"
			}
		},
		"equipRequirements": {
			"name": "Conditions d'équipement",
			"description": "Les conditions d'équipement de la carte",
			"values": {
				"all_slots": "Tous les emplacements",
				"auxiliary": "Auxiliaire",
				"equipment": "Équipement",
				"gauntlets": "Gantelets",
				"greaves": "Jambières",
				"onehanded_melee": "Mêlée à une main",
				"onehanded_ranged": "Distance à une main",
				"onehanded_shield": "Bouclier à une main",
				"twohanded_melee": "Mêlée à deux mains",
				"twohanded_ranged": "Distance à deux mains",
				"visor": "Visière",
				"weapons": "Armes"
			}
		},
		"orientation": {
			"name": "Orientation",
			"description": "L'orientation de la carte",
			"values": {
				"portrait": "Portrait",
				"landscape": "Paysage"
			}
		},
		"mkValue": {
			"name": "Valeur MK",
			"description": "La valeur MK de la carte",
			"values": {
				"mki": "MKI",
				"mkii": "MKII",
				"mkiii": "MKIII",
				"mkiv": "MKIV"
			}
		},
		"timingRestrictions": {
			"name": "Restrictions de timing",
			"description": "Les restrictions de timing de la carte",
			"values": {
				"active_main": "Phase principale active",
				"beginning_of_turn": "Début de tour",
				"counter_attack": "Contre-attaque",
				"counter_play": "Contre-jeu",
				"on_attack": "À l'attaque",
				"on_overclock": "À l'overclock",
				"on_play": "Au jeu",
				"on_reveal": "À la révélation",
				"on_scrap": "À la mise au rebut",
				"overclock": "Overclock"
			}
		},
		"keywords": {
			"name": "Mots-clés",
			"description": "Les mots-clés de la carte",
			"values": {
				"cloaking": "Camouflage",
				"data_storm": "Tempête de données",
				"datamine": "Extraction de données",
				"decoding": "Décodage",
				"feedback_loop": "Boucle de rétroaction",
				"firewall": "Pare-feu",
				"lockout": "Verrouillage",
				"optimization_protocol": "Protocole d'optimisation",
				"overshield": "Surbouclier",
				"power_burst": "Décharge de puissance",
				"quantum_blow": "Coup quantique",
				"reconfigure": "Reconfiguration",
				"regenerate": "Régénération",
				"reinforce": "Renforcement",
				"renew": "Renouvellement",
				"rootkit": "Rootkit",
				"scrapheap": "Tas de ferraille",
				"shockwave": "Onde de choc",
				"short_circuit": "Court-circuit",
				"siphon": "Siphon",
				"system_override": "Prise de contrôle système",
				"system_reboot": "Redémarrage système",
				"virus": "Virus",
				"worm": "Ver"
			}
		},
		"origins": {
			"name": "Origines",
			"description": "Les origines de la carte",
			"values": {
				"boxtopper": "Boxtopper",
				"chrono_core_crew": "Chrono Core Crew",
				"gen_con": "Gen Con",
				"houston_premier_event": "Houston Premier Event",
				"kickstarter_exclusive": "Exclusivité Kickstarter",
				"release_event_kit": "Kit d'événement de sortie"
			}
		},
		"maxShieldValue": {
			"name": "Valeur de bouclier max",
			"description": "Valeur de bouclier maximale de la carte"
		},
		"maxLifeValue": {
			"name": "Points de vie max",
			"description": "Points de vie maximum de la carte"
		},
		"coreCost": {
			"name": "Coût de Core",
			"description": "Coût en Core pour jouer la carte"
		},
		"damageValue": {
			"name": "Valeur de dégâts",
			"description": "Valeur de dégâts de la carte"
		},
		"chargeValue": {
			"name": "Valeur de charge",
			"description": "Valeur de charge de la carte"
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Texte de règles et de capacités de la carte"
		},
		"variant": {
			"name": "Variante",
			"description": "La variante/le traitement d'impression de la carte",
			"values": {
				"normal": "Normale",
				"immersive_art": "Immersive Art",
				"serialized": "Numérotée",
				"artist_signature": "Signature d'artiste",
				"circuitry": "Circuitry",
				"early_adopter": "Early Adopter",
				"participant": "Participant",
				"rilynn_art": "Rilynn Art",
				"top_4": "Top 4",
				"top_8": "Top 8",
				"winner": "Vainqueur"
			}
		}
	},
	presets: {
		"standard": {
			"name": "Set Standard",
			"description": "Un set complet avec une copie de chaque carte - finition standard si disponible, sinon holographique"
		},
		"playset": {
			"name": "Playset",
			"description": "Un set complet avec le nombre maximum de copies de chaque carte autorisé dans un deck ou un chargement - finition standard si disponible, sinon holographique"
		},
		"foil": {
			"name": "Set Holographique",
			"description": "Un set complet avec une copie de chaque carte en finition holographique"
		},
		"foilPlayset": {
			"name": "Playset Holographique",
			"description": "Un set complet avec le nombre maximum de copies de chaque carte autorisé dans un deck ou un chargement en finition holographique"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Un set complet avec une copie de chaque carte dans toutes les finitions"
		}
	}
};

//#endregion
//#region src/lib/games/cyberpunk/i18n/en.json
var en_default$22 = {
	name: "Cyberpunk TCG",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"epic": "Epic",
				"secret": "Secret",
				"iconic": "Iconic",
				"nova": "Nova"
			}
		},
		"color": {
			"name": "Color",
			"description": "The color faction of the card",
			"values": {
				"red": "Red",
				"blue": "Blue",
				"green": "Green",
				"yellow": "Yellow"
			}
		},
		"cardType": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"legend": "Legend",
				"unit": "Unit",
				"gear": "Gear",
				"program": "Program"
			}
		},
		"classification": {
			"name": "Classification",
			"description": "Card classifications and affiliations",
			"values": {
				"arasaka": "Arasaka",
				"corpo": "Corpo",
				"merc": "Merc",
				"doll": "Doll",
				"netrunner": "Netrunner",
				"ripperdoc": "Ripperdoc",
				"vehicle": "Vehicle",
				"drone": "Drone",
				"militech": "Militech",
				"zetatech": "Zetatech",
				"cyberware": "Cyberware",
				"weapon": "Weapon",
				"plan": "Plan",
				"implant": "Implant",
				"tech": "Tech",
				"overclocking": "Overclocking",
				"sixthStreet": "6th Street",
				"aldecado": "Aldecado",
				"braindance": "Braindance",
				"extreme": "Extreme",
				"fixer": "Fixer",
				"ganger": "Ganger",
				"maelstrom": "Maelstrom",
				"mainesCrew": "Maine's Crew",
				"medtech": "Medtech",
				"mox": "Mox",
				"mystic": "Mystic",
				"ncpd": "NCPD",
				"nomad": "Nomad",
				"quickhack": "Quickhack",
				"raffenShiv": "Raffen Shiv",
				"rocker": "Rocker",
				"samurai": "Samurai",
				"scavenger": "Scavenger",
				"techie": "Techie",
				"traumaTeam": "Trauma Team",
				"tygerClaws": "Tyger Claws",
				"valentino": "Valentino",
				"voodooBoys": "Voodoo Boys",
				"animal": "Animal"
			}
		},
		"keywords": {
			"name": "Keywords",
			"description": "Card keywords and abilities",
			"values": {
				"goSolo": "Go Solo",
				"blocker": "Blocker",
				"play": "Play",
				"attack": "Attack",
				"flip": "Flip"
			}
		},
		"cost": {
			"name": "Cost",
			"description": "Cost to play the card"
		},
		"power": {
			"name": "Power",
			"description": "Power value of the card"
		},
		"ram": {
			"name": "Ram",
			"description": "Ram requirement of the card"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "Card rules and abilities text"
		},
		"artist": {
			"name": "Artist",
			"description": "Card artist"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in standard finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Foil Set",
			"description": "A complete set with one copy of each card in foil finish"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in both standard and foil finishes"
		}
	}
};

//#endregion
//#region src/lib/games/cyberpunk/i18n/fr.json
var fr_default$22 = {
	name: "Cyberpunk TCG",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"epic": "Épique",
				"secret": "Secrète",
				"iconic": "Iconique",
				"nova": "Nova"
			}
		},
		"color": {
			"name": "Couleur",
			"description": "La faction de couleur de la carte",
			"values": {
				"red": "Rouge",
				"blue": "Bleu",
				"green": "Vert",
				"yellow": "Jaune"
			}
		},
		"cardType": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"legend": "Légende",
				"unit": "Unité",
				"gear": "Équipement",
				"program": "Programme"
			}
		},
		"classification": {
			"name": "Classification",
			"description": "Classifications et affiliations de la carte",
			"values": {
				"arasaka": "Arasaka",
				"corpo": "Corpo",
				"merc": "Merc",
				"doll": "Poupée",
				"netrunner": "Netrunner",
				"ripperdoc": "Ripperdoc",
				"vehicle": "Véhicule",
				"drone": "Drone",
				"militech": "Militech",
				"zetatech": "Zetatech",
				"cyberware": "Cyberware",
				"weapon": "Arme",
				"plan": "Plan",
				"implant": "Implant",
				"tech": "Tech",
				"overclocking": "Overclocking",
				"sixthStreet": "6th Street",
				"aldecado": "Aldecado",
				"braindance": "Braindance",
				"extreme": "Extreme",
				"fixer": "Fixer",
				"ganger": "Ganger",
				"maelstrom": "Maelstrom",
				"mainesCrew": "Maine's Crew",
				"medtech": "Medtech",
				"mox": "Mox",
				"mystic": "Mystic",
				"ncpd": "NCPD",
				"nomad": "Nomad",
				"quickhack": "Quickhack",
				"raffenShiv": "Raffen Shiv",
				"rocker": "Rocker",
				"samurai": "Samurai",
				"scavenger": "Scavenger",
				"techie": "Techie",
				"traumaTeam": "Trauma Team",
				"tygerClaws": "Tyger Claws",
				"valentino": "Valentino",
				"voodooBoys": "Voodoo Boys",
				"animal": "Animal"
			}
		},
		"keywords": {
			"name": "Mots-clés",
			"description": "Mots-clés et capacités de la carte",
			"values": {
				"goSolo": "Go Solo",
				"blocker": "Bloqueur",
				"play": "Jouer",
				"attack": "Attaque",
				"flip": "Retourner"
			}
		},
		"cost": {
			"name": "Coût",
			"description": "Coût pour jouer la carte"
		},
		"power": {
			"name": "Puissance",
			"description": "Valeur de puissance de la carte"
		},
		"ram": {
			"name": "Ram",
			"description": "Exigence de Ram de la carte"
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Texte des règles et capacités de la carte"
		},
		"artist": {
			"name": "Artiste",
			"description": "Artiste de la carte"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Foil Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition foil"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard et foil"
		}
	}
};

//#endregion
//#region src/lib/games/dbs-fusion/i18n/en.json
var en_default$21 = {
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"leader": "Leader",
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"secret_rare": "Secret Rare",
				"promo": "Promo",
				"none": "None"
			}
		},
		"color": {
			"name": "Color",
			"description": "The color of the card",
			"values": {
				"red": "Red",
				"blue": "Blue",
				"green": "Green",
				"yellow": "Yellow",
				"black": "Black"
			}
		},
		"finish": {
			"name": "Finish",
			"description": "The finish of the card",
			"values": {
				"standard": "Standard",
				"holofoil": "Holofoil"
			}
		},
		"type": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"leader": "Leader",
				"battle": "Battle",
				"extra": "Extra",
				"energy_marker": "Energy Marker",
				"code_card": "Code Card"
			}
		},
		"power": {
			"name": "Power",
			"description": "Power value of the card"
		},
		"comboPower": {
			"name": "Combo Power",
			"description": "Combo power value of the card"
		},
		"cost": {
			"name": "Cost",
			"description": "Cost to play the card"
		},
		"energyCost": {
			"name": "Energy Cost",
			"description": "Energy cost to play the card"
		},
		"characterTraits": {
			"name": "Character Traits",
			"description": "The character traits of the card"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "The rules text of the card"
		}
	},
	name: "Dragon Ball Super: Fusion World",
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in standard finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Holofoil Set",
			"description": "A complete set with one copy of each card in holofoil finish"
		},
		"foilPlayset": {
			"name": "Holofoil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in holofoil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in both standard and holofoil finishes"
		}
	}
};

//#endregion
//#region src/lib/games/dbs-fusion/i18n/fr.json
var fr_default$21 = {
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"leader": "Leader",
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"secret_rare": "Secret Rare",
				"promo": "Promo",
				"none": "Aucune"
			}
		},
		"color": {
			"name": "Couleur",
			"description": "La couleur de la carte",
			"values": {
				"red": "Rouge",
				"blue": "Bleu",
				"green": "Vert",
				"yellow": "Jaune",
				"black": "Noir"
			}
		},
		"finish": {
			"name": "Finition",
			"description": "La finition de la carte",
			"values": {
				"standard": "Standard",
				"holofoil": "Holofoil"
			}
		},
		"type": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"leader": "Leader",
				"battle": "Combat",
				"extra": "Extra",
				"energy_marker": "Marqueur d'énergie",
				"code_card": "Carte code"
			}
		},
		"power": {
			"name": "Puissance",
			"description": "Valeur de puissance de la carte"
		},
		"comboPower": {
			"name": "Puissance Combo",
			"description": "Valeur de puissance combo de la carte"
		},
		"cost": {
			"name": "Coût",
			"description": "Coût pour jouer la carte"
		},
		"energyCost": {
			"name": "Coût en énergie",
			"description": "Coût en énergie pour jouer la carte"
		},
		"characterTraits": {
			"name": "Traits de personnage",
			"description": "Les traits de personnage de la carte"
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Le texte de règles de la carte"
		}
	},
	name: "Dragon Ball Super: Fusion World",
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Holofoil Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition holofoil"
		},
		"foilPlayset": {
			"name": "Holofoil Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition holofoil"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard et holofoil"
		}
	}
};

//#endregion
//#region src/lib/games/dbs-masters/i18n/en.json
var en_default$20 = {
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"special_rare": "Special Rare",
				"special_leader_rare": "Special Leader Rare",
				"concept_rare": "Concept Rare",
				"god_rare": "God Rare",
				"secret_rare": "Secret Rare",
				"starter_rare": "Starter Rare",
				"campaign_rare": "Campaign Rare",
				"expansion_rare": "Expansion Rare",
				"promo": "Promo",
				"giant_force_rare": "Giant Force Rare",
				"dragon_ball_rare": "Dragon Ball Rare",
				"duo_power_rare": "Duo Power Rare",
				"iconic_attack_rare": "Iconic Attack Rare",
				"destroyer_and_angel_rare": "Destroyer & Angel Rare",
				"infinite_saiyan_rare": "Infinite Saiyan Rare",
				"son_gohan_rare": "Son Gohan Rare",
				"reboot_leader_rare": "Reboot Leader Rare",
				"noble_hero_rare": "Noble Hero Rare",
				"ignoble_villain_rare": "Ignoble Villain Rare",
				"destruction_rare": "Destruction Rare",
				"special_rare_signature": "Special Rare Signature",
				"feature_rare": "Feature Rare",
				"token": "Token",
				"merit": "Merit",
				"none": "None"
			}
		},
		"color": {
			"name": "Color",
			"description": "The color of the card",
			"values": {
				"red": "Red",
				"blue": "Blue",
				"green": "Green",
				"yellow": "Yellow",
				"black": "Black",
				"white": "White"
			}
		},
		"finish": {
			"name": "Finish",
			"description": "The finish of the card",
			"values": {
				"standard": "Standard",
				"foil": "Foil"
			}
		},
		"type": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"leader": "Leader",
				"battle": "Battle",
				"extra": "Extra",
				"z_battle": "Z-Battle",
				"z_unison": "Z-Unison",
				"z_extra": "Z-Extra",
				"unison": "Unison",
				"z_leader": "Z-Leader",
				"token": "Token",
				"energy_marker": "Energy Marker"
			}
		},
		"power": {
			"name": "Power",
			"description": "Power value of the card"
		},
		"comboPower": {
			"name": "Combo Power",
			"description": "Combo power value of the card"
		},
		"comboEnergy": {
			"name": "Combo Energy",
			"description": "Combo energy cost of the card"
		},
		"cost": {
			"name": "Cost",
			"description": "Energy cost to play the card"
		},
		"zEnergyCost": {
			"name": "Z-Energy Cost",
			"description": "Z-Energy cost for Z-type cards"
		},
		"specialTraits": {
			"name": "Special Traits",
			"description": "The special traits of the card"
		},
		"era": {
			"name": "Era",
			"description": "The saga or era of the card",
			"values": {
				"young_son_goku_saga": "Young Son Goku Saga",
				"pilaf_saga": "Pilaf Saga",
				"fortuneteller_baba_saga": "Fortuneteller Baba Saga",
				"king_piccolo_saga": "King Piccolo Saga",
				"saiyan_saga": "Saiyan Saga",
				"frieza_saga": "Frieza Saga",
				"garlic_jr_saga": "Garlic Jr. Saga",
				"android_13_saga": "Android 13 Saga",
				"android_cell_saga": "Android Cell Saga",
				"world_martial_arts_tournament_saga": "World Martial Arts Tournament Saga",
				"majin_buu_saga": "Majin Buu Saga",
				"evil_wizard_babidi_saga": "The Evil Wizard Babidi Saga",
				"battle_of_gods_saga": "Battle of Gods Saga",
				"resurrection_f_saga": "Resurrection 'F' Saga",
				"universe_survival_saga": "Universe Survival Saga",
				"future_trunks_saga": "Future Trunks Saga",
				"dbs_broly_saga": "DBS: Broly Saga",
				"dbs_super_hero_saga": "DBS: SUPER HERO Saga",
				"daima_saga": "DAIMA Saga",
				"dark_empire_saga": "Dark Empire Saga",
				"dark_demon_realm_saga": "Dark Demon Realm Saga",
				"prison_planet_saga": "Prison Planet Saga",
				"shadow_dragon_saga": "Shadow Dragon Saga",
				"black_star_dragon_ball_saga": "Black Star Dragon Ball Saga",
				"baby_saga": "Baby Saga",
				"super_17_saga": "Super 17 Saga",
				"broly_saga": "Broly Saga",
				"cooler_saga": "Cooler Saga",
				"meta_cooler_saga": "Meta-Cooler Saga",
				"bardock_saga": "Bardock Saga",
				"turles_saga": "Turles Saga",
				"lord_slug_saga": "Lord Slug Saga",
				"boujack_saga": "Boujack Saga",
				"janemba_saga": "Janemba Saga",
				"hirudegarn_saga": "Hirudegarn Saga",
				"champa_saga": "Champa Saga",
				"another_world_budokai_saga": "Another World Budokai Saga",
				"dr_uiro_saga": "Dr. Uiro Saga",
				"the_path_to_power_saga": "The Path to Power Saga",
				"chilled_saga": "Chilled Saga",
				"hatchiyack_saga": "Hatchiyack Saga",
				"mystical_adventure_saga": "Mystical Adventure Saga",
				"galactic_patrolman_saga": "Galactic Patrolman Saga",
				"curse_of_the_blood_rubies_saga": "Curse of the Blood Rubies Saga",
				"sleeping_princess_in_devils_castle_saga": "Sleeping Princess in Devil's Castle Saga",
				"dragon_ball_minus_saga": "Dragon Ball Minus Saga",
				"special": "Special"
			}
		},
		"character": {
			"name": "Character",
			"description": "The character featured on the card"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "The rules text of the card"
		}
	},
	name: "Dragon Ball Super: Masters",
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in standard finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Foil Set",
			"description": "A complete set with one copy of each card in foil finish"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in both standard and foil finishes"
		}
	}
};

//#endregion
//#region src/lib/games/dbs-masters/i18n/fr.json
var fr_default$20 = {
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"special_rare": "Special Rare",
				"special_leader_rare": "Special Leader Rare",
				"concept_rare": "Concept Rare",
				"god_rare": "God Rare",
				"secret_rare": "Secret Rare",
				"starter_rare": "Starter Rare",
				"campaign_rare": "Campaign Rare",
				"expansion_rare": "Expansion Rare",
				"promo": "Promo",
				"giant_force_rare": "Giant Force Rare",
				"dragon_ball_rare": "Dragon Ball Rare",
				"duo_power_rare": "Duo Power Rare",
				"iconic_attack_rare": "Iconic Attack Rare",
				"destroyer_and_angel_rare": "Destroyer & Angel Rare",
				"infinite_saiyan_rare": "Infinite Saiyan Rare",
				"son_gohan_rare": "Son Gohan Rare",
				"reboot_leader_rare": "Reboot Leader Rare",
				"noble_hero_rare": "Noble Hero Rare",
				"ignoble_villain_rare": "Ignoble Villain Rare",
				"destruction_rare": "Destruction Rare",
				"special_rare_signature": "Special Rare Signature",
				"feature_rare": "Feature Rare",
				"token": "Token",
				"merit": "Mérite",
				"none": "Aucune"
			}
		},
		"color": {
			"name": "Couleur",
			"description": "La couleur de la carte",
			"values": {
				"red": "Rouge",
				"blue": "Bleu",
				"green": "Vert",
				"yellow": "Jaune",
				"black": "Noir",
				"white": "Blanc"
			}
		},
		"finish": {
			"name": "Finition",
			"description": "La finition de la carte",
			"values": {
				"standard": "Standard",
				"foil": "Foil"
			}
		},
		"type": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"leader": "Leader",
				"battle": "Combat",
				"extra": "Extra",
				"z_battle": "Z-Combat",
				"z_unison": "Z-Unison",
				"z_extra": "Z-Extra",
				"unison": "Unison",
				"z_leader": "Z-Leader",
				"token": "Token",
				"energy_marker": "Marqueur d'énergie"
			}
		},
		"power": {
			"name": "Puissance",
			"description": "Valeur de puissance de la carte"
		},
		"comboPower": {
			"name": "Puissance Combo",
			"description": "Valeur de puissance combo de la carte"
		},
		"comboEnergy": {
			"name": "Énergie Combo",
			"description": "Coût d'énergie combo de la carte"
		},
		"cost": {
			"name": "Coût",
			"description": "Coût en énergie pour jouer la carte"
		},
		"zEnergyCost": {
			"name": "Coût Z-Énergie",
			"description": "Coût en Z-Énergie pour les cartes de type Z"
		},
		"specialTraits": {
			"name": "Traits spéciaux",
			"description": "Les traits spéciaux de la carte"
		},
		"era": {
			"name": "Ère",
			"description": "La saga ou l'ère de la carte",
			"values": {
				"young_son_goku_saga": "Saga du jeune Son Goku",
				"pilaf_saga": "Saga de Pilaf",
				"fortuneteller_baba_saga": "Saga de Baba la voyante",
				"king_piccolo_saga": "Saga du Roi Piccolo",
				"saiyan_saga": "Saga des Saïyans",
				"frieza_saga": "Saga de Freezer",
				"garlic_jr_saga": "Saga de Garlic Jr.",
				"android_13_saga": "Saga de l'Androïde 13",
				"android_cell_saga": "Saga des Androïdes et de Cell",
				"world_martial_arts_tournament_saga": "Saga du Tournoi des arts martiaux",
				"majin_buu_saga": "Saga de Majin Boo",
				"evil_wizard_babidi_saga": "Saga du sorcier maléfique Babidi",
				"battle_of_gods_saga": "Saga Battle of Gods",
				"resurrection_f_saga": "Saga La Résurrection de « F »",
				"universe_survival_saga": "Saga de la Survie de l'univers",
				"future_trunks_saga": "Saga de Trunks du futur",
				"dbs_broly_saga": "Saga DBS : Broly",
				"dbs_super_hero_saga": "Saga DBS : SUPER HERO",
				"daima_saga": "Saga DAIMA",
				"dark_empire_saga": "Saga de l'Empire des ténèbres",
				"dark_demon_realm_saga": "Saga du Royaume démoniaque des ténèbres",
				"prison_planet_saga": "Saga de la Planète prison",
				"shadow_dragon_saga": "Saga des Dragons maléfiques",
				"black_star_dragon_ball_saga": "Saga des Dragon Balls de l'étoile noire",
				"baby_saga": "Saga de Baby",
				"super_17_saga": "Saga de Super 17",
				"broly_saga": "Saga de Broly",
				"cooler_saga": "Saga de Cooler",
				"meta_cooler_saga": "Saga de Méta-Cooler",
				"bardock_saga": "Saga de Baddack",
				"turles_saga": "Saga de Thalès",
				"lord_slug_saga": "Saga de Lord Slug",
				"boujack_saga": "Saga de Bojack",
				"janemba_saga": "Saga de Janemba",
				"hirudegarn_saga": "Saga de Hirudegarn",
				"champa_saga": "Saga de Champa",
				"another_world_budokai_saga": "Saga du Budokai de l'autre monde",
				"dr_uiro_saga": "Saga du Dr Uiro",
				"the_path_to_power_saga": "Saga La Voie de la puissance",
				"chilled_saga": "Saga de Chilled",
				"hatchiyack_saga": "Saga de Hatchiyack",
				"mystical_adventure_saga": "Saga L'Aventure mystique",
				"galactic_patrolman_saga": "Saga du Patrouilleur galactique",
				"curse_of_the_blood_rubies_saga": "Saga La Légende de Shenron",
				"sleeping_princess_in_devils_castle_saga": "Saga Le Château du démon",
				"dragon_ball_minus_saga": "Saga Dragon Ball Minus",
				"special": "Spécial"
			}
		},
		"character": {
			"name": "Personnage",
			"description": "Le personnage sur la carte"
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Le texte de règles de la carte"
		}
	},
	name: "Dragon Ball Super: Masters",
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Foil Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition foil"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard et foil"
		}
	}
};

//#endregion
//#region src/lib/games/drakerion/i18n/en.json
var en_default$19 = {
	name: "Drakerion",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"rare": "Rare",
				"starter": "Starter",
				"unique": "Unique",
				"promo": "Promo"
			}
		},
		"finish": {
			"name": "Finish",
			"description": "Finish of the card",
			"values": {
				"standard": "Standard",
				"foil": "Foil",
				"gold": "Gold Foil",
				"zarimoth": "Zarimoth"
			}
		},
		"type": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"character": "Character",
				"attachment": "Attachment",
				"event": "Event",
				"city": "City",
				"banner": "Banner",
				"maneuver": "Maneuver"
			}
		},
		"kingdom": {
			"name": "Kingdom",
			"description": "The kingdom the card belongs to",
			"values": {
				"lokmar": "Lokmar",
				"gilEstel": "Gil Estel",
				"wasteland": "Wasteland",
				"tyraslin": "Tyraslin",
				"kartej": "Kartej",
				"neutral": "Neutral"
			}
		},
		"traits": {
			"name": "Traits",
			"description": "Card traits and subtypes",
			"values": {
				"acolyte": "Acolyte",
				"apprentice": "Apprentice",
				"archer": "Archer",
				"artisan": "Artisan",
				"artist": "Artist",
				"bodyguard": "Bodyguard",
				"captain": "Captain",
				"crossbowman": "Crossbowman",
				"healer": "Healer",
				"horseRider": "Horserider",
				"knight": "Knight",
				"merchant": "Merchant",
				"messenger": "Messenger",
				"necromancer": "Necromancer",
				"priest": "Priest",
				"priestess": "Priestess",
				"recruiter": "Recruiter",
				"scalesRider": "Scales Rider",
				"scavenger": "Scavenger",
				"scholar": "Scholar",
				"scout": "Scout",
				"squire": "Squire",
				"demon": "Demon",
				"dragon": "Dragon",
				"dwarf": "Dwarf",
				"elf": "Elf",
				"garuda": "Garuda",
				"ghoul": "Ghoul",
				"golem": "Golem",
				"hippogriff": "Hippogriff",
				"hound": "Hound",
				"lilith": "Lilith",
				"skeleton": "Skeleton",
				"strigoi": "Strigoï",
				"succubus": "Succubus",
				"funestus": "Funestus",
				"incubus": "Incubus",
				"alania": "Alania",
				"autumnGrove": "Autumn Grove",
				"blood": "Blood",
				"clan": "Clan",
				"crimsonGuard": "Crimson Guard",
				"crownCircle": "Crown Circle",
				"dyad": "Dyad",
				"edgeCircle": "Edge Circle",
				"elderCouncil": "Elder Council",
				"primeLegion": "Prime Legion",
				"summerGrove": "Summer Grove",
				"tyraslin": "Tyraslin",
				"vassalCircle": "Vassal Circle",
				"vonKringstein": "Von Kringstein",
				"avatar": "Avatar",
				"bannerBearer": "Banner-Bearer",
				"elite": "Elite",
				"exiled": "Exiled",
				"faceless": "Faceless",
				"founder": "Founder",
				"general": "General",
				"king": "King",
				"legend": "Legend",
				"master": "Master",
				"noble": "Noble",
				"prince": "Prince",
				"ambush": "Ambush",
				"infiltration": "Infiltration",
				"invocation": "Invocation",
				"roaming": "Roaming",
				"sabotage": "Sabotage",
				"spying": "Spying",
				"ancestral": "Ancestral",
				"arcane": "Arcane",
				"corruption": "Corruption",
				"dread": "Dread",
				"elemental": "Elemental",
				"exhumation": "Exhumation",
				"hellios": "Hellios",
				"hunt": "Hunt",
				"illusion": "Illusion",
				"moonlight": "Moonlight",
				"nature": "Nature",
				"obsidian": "Obsidian",
				"obscur": "Obscur",
				"portal": "Portal",
				"possessed": "Possessed",
				"ritual": "Ritual",
				"ruins": "Ruins",
				"ruinsSeeker": "Ruins Seeker",
				"shadow": "Shadow",
				"skill": "Skill",
				"song": "Song",
				"spell": "Spell",
				"state": "State",
				"tower": "Tower",
				"vindicare": "Vindicare",
				"void": "Void",
				"weather": "Weather",
				"armory": "Armory",
				"army": "Army",
				"battle": "Battle",
				"bow": "Bow",
				"cemetery": "Cemetery",
				"diplomacy": "Diplomacy",
				"forest": "Forest",
				"fortress": "Fortress",
				"heal": "Heal",
				"horse": "Horse",
				"item": "Item",
				"mount": "Mount",
				"mountain": "Mountain",
				"necropolis": "Necropolis",
				"north": "North",
				"object": "Object",
				"order": "Order",
				"reputation": "Reputation",
				"shield": "Shield",
				"south": "South",
				"stable": "Stable",
				"weapon": "Weapon"
			}
		},
		"cost": {
			"name": "Cost",
			"description": "Cost to play the card"
		},
		"attack": {
			"name": "Attack",
			"description": "Attack power of the card"
		},
		"riposte": {
			"name": "Riposte",
			"description": "Riposte value of the card"
		},
		"health": {
			"name": "Health",
			"description": "Health points of the card"
		},
		"rangedAttack": {
			"name": "Ranged Attack",
			"description": "Ranged attack power of the card"
		},
		"prestige": {
			"name": "Prestige",
			"description": "Prestige points of the card"
		},
		"nbTactic": {
			"name": "Tactics",
			"description": "Number of tactics"
		},
		"nbSupply": {
			"name": "Supplies",
			"description": "Number of supplies"
		},
		"goldGain": {
			"name": "Gold Gain",
			"description": "Gold gained from the card"
		},
		"drawCount": {
			"name": "Draw Count",
			"description": "Number of cards drawn"
		},
		"keywords": {
			"name": "Keywords",
			"description": "Card keywords and abilities"
		},
		"subtitle": {
			"name": "Subtitle",
			"description": "Card subtitle"
		},
		"requiresBanner": {
			"name": "Requires Banner",
			"description": "Whether the card requires a banner",
			"values": {
				"requiresBanner": "Requires Banner",
				"noRequirement": "No Requirement"
			}
		},
		"requiresCity": {
			"name": "Requires City",
			"description": "Whether the card requires a city",
			"values": {
				"requiresCity": "Requires City",
				"noRequirement": "No Requirement"
			}
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "Card rules and abilities text"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in standard finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Foil Set",
			"description": "A complete set with one copy of each card in foil finish"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in all finishes"
		}
	}
};

//#endregion
//#region src/lib/games/drakerion/i18n/fr.json
var fr_default$19 = {
	name: "Drakerion",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"rare": "Rare",
				"starter": "Starter",
				"unique": "Unique",
				"promo": "Promo"
			}
		},
		"finish": {
			"name": "Finition",
			"description": "Finition de la carte",
			"values": {
				"standard": "Standard",
				"foil": "Foil",
				"gold": "Gold Foil",
				"zarimoth": "Zarimoth"
			}
		},
		"type": {
			"name": "Type de Carte",
			"description": "Le type de la carte",
			"values": {
				"character": "Personnage",
				"attachment": "Attachement",
				"event": "Événement",
				"city": "Ville",
				"banner": "Bannière",
				"maneuver": "Maneuvre"
			}
		},
		"kingdom": {
			"name": "Royaume",
			"description": "Le royaume auquel appartient la carte",
			"values": {
				"lokmar": "Lokmar",
				"gilEstel": "Gil Estel",
				"wasteland": "Terres Désolées",
				"tyraslin": "Tyraslin",
				"kartej": "Kartej",
				"neutral": "Neutral"
			}
		},
		"traits": {
			"name": "Traits",
			"description": "Traits et sous-types de la carte",
			"values": {
				"acolyte": "Acolyte",
				"apprentice": "Apprenti",
				"archer": "Archer",
				"artisan": "Artisan",
				"artist": "Artiste",
				"bodyguard": "Garde du Corps",
				"captain": "Capitaine",
				"crossbowman": "Arbalétrier",
				"healer": "Guérisseur",
				"horseRider": "Cavalier",
				"knight": "Chevalier",
				"merchant": "Marchand",
				"messenger": "Messager",
				"necromancer": "Nécromancien",
				"priest": "Prêtre",
				"priestess": "Prêtresse",
				"recruiter": "Recruteur",
				"scalesRider": "Chevaucheur d'Écailles",
				"scavenger": "Charognard",
				"scholar": "Érudit",
				"scout": "Éclaireur",
				"squire": "Écuyer",
				"demon": "Démon",
				"dragon": "Dragon",
				"dwarf": "Nain",
				"elf": "Elfe",
				"garuda": "Garuda",
				"ghoul": "Goule",
				"golem": "Golem",
				"hippogriff": "Hippogriffe",
				"hound": "Limier",
				"lilith": "Lilith",
				"skeleton": "Squelette",
				"strigoi": "Strigoï",
				"succubus": "Succube",
				"funestus": "Funestus",
				"incubus": "Incube",
				"alania": "Alania",
				"autumnGrove": "Bosquet d'Automne",
				"blood": "Sang",
				"clan": "Clan",
				"crimsonGuard": "Garde Cramoisie",
				"crownCircle": "Cercle de la Couronne",
				"dyad": "Dyade",
				"edgeCircle": "Cercle Extérieur",
				"elderCouncil": "Conseil des Anciens",
				"primeLegion": "Légion Primaire",
				"summerGrove": "Bosquet d'Été",
				"tyraslin": "Tyraslin",
				"vassalCircle": "Cercle Vassal",
				"vonKringstein": "Von Kringstein",
				"avatar": "Avatar",
				"bannerBearer": "Porte-Bannière",
				"elite": "Élite",
				"exiled": "Exilé",
				"faceless": "Sans-Visage",
				"founder": "Fondateur",
				"general": "Général",
				"king": "Roi",
				"legend": "Légende",
				"master": "Maître",
				"noble": "Noble",
				"prince": "Prince",
				"ambush": "Embuscade",
				"infiltration": "Infiltration",
				"invocation": "Invocation",
				"roaming": "Errance",
				"sabotage": "Sabotage",
				"spying": "Espionnage",
				"ancestral": "Ancestral",
				"arcane": "Arcane",
				"corruption": "Corruption",
				"dread": "Effroi",
				"elemental": "Élémentaire",
				"exhumation": "Exhumation",
				"hellios": "Hellios",
				"hunt": "Chasse",
				"illusion": "Illusion",
				"moonlight": "Clair de Lune",
				"nature": "Nature",
				"obsidian": "Obsidienne",
				"obscur": "Obscur",
				"portal": "Portail",
				"possessed": "Possédé",
				"ritual": "Rituel",
				"ruins": "Ruines",
				"ruinsSeeker": "Chercheur de Ruines",
				"shadow": "Ombre",
				"skill": "Compétence",
				"song": "Chant",
				"spell": "Sort",
				"state": "État",
				"tower": "Tour",
				"vindicare": "Vindicare",
				"void": "Vide",
				"weather": "Météo",
				"armory": "Armurerie",
				"army": "Armée",
				"battle": "Bataille",
				"bow": "Arc",
				"cemetery": "Cimetière",
				"diplomacy": "Diplomatie",
				"forest": "Forêt",
				"fortress": "Forteresse",
				"heal": "Soin",
				"horse": "Cheval",
				"item": "Objet",
				"mount": "Monture",
				"mountain": "Montagne",
				"necropolis": "Nécropole",
				"north": "Nord",
				"object": "Objet",
				"order": "Ordre",
				"reputation": "Réputation",
				"shield": "Bouclier",
				"south": "Sud",
				"stable": "Écurie",
				"weapon": "Arme"
			}
		},
		"cost": {
			"name": "Coût",
			"description": "Coût pour jouer la carte"
		},
		"attack": {
			"name": "Attaque",
			"description": "Puissance d'attaque de la carte"
		},
		"riposte": {
			"name": "Riposte",
			"description": "Valeur de riposte de la carte"
		},
		"health": {
			"name": "Santé",
			"description": "Points de vie de la carte"
		},
		"rangedAttack": {
			"name": "Attaque à Distance",
			"description": "Puissance d'attaque à distance de la carte"
		},
		"prestige": {
			"name": "Prestige",
			"description": "Points de prestige de la carte"
		},
		"nbTactic": {
			"name": "Tactiques",
			"description": "Nombre de tactiques"
		},
		"nbSupply": {
			"name": "Ravitaillement",
			"description": "Nombre de ravitaillements"
		},
		"goldGain": {
			"name": "Gain d'Or",
			"description": "Or gagné avec la carte"
		},
		"drawCount": {
			"name": "Pioche",
			"description": "Nombre de cartes piochées"
		},
		"keywords": {
			"name": "Mots-clés",
			"description": "Mots-clés et capacités de la carte"
		},
		"subtitle": {
			"name": "Sous-titre",
			"description": "Sous-titre de la carte"
		},
		"requiresBanner": {
			"name": "Nécessite une Bannière",
			"description": "Si la carte nécessite une bannière",
			"values": {
				"requiresBanner": "Nécessite une Bannière",
				"noRequirement": "Aucune Exigence"
			}
		},
		"requiresCity": {
			"name": "Nécessite une Ville",
			"description": "Si la carte nécessite une ville",
			"values": {
				"requiresCity": "Nécessite une Ville",
				"noRequirement": "Aucune Exigence"
			}
		},
		"rulesText": {
			"name": "Texte de Règles",
			"description": "Texte des règles et capacités de la carte"
		}
	},
	presets: {
		"standard": {
			"name": "Set Standard",
			"description": "Un set complet avec une copie de chaque carte en finition standard"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Set Foil",
			"description": "Un set complet avec une copie de chaque carte en finition foil"
		},
		"foilPlayset": {
			"name": "Playset Foil",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil"
		},
		"masterset": {
			"name": "Set Maître",
			"description": "Un set complet avec une copie de chaque carte dans toutes les finitions"
		}
	}
};

//#endregion
//#region src/lib/games/dreamland/i18n/en.json
var en_default$18 = {
	name: "Dreamland TCG",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"banale": "Banale",
				"peu_banale": "Peu banale",
				"kibrill": "Kibrill'",
				"crazy": "Crazy",
				"dream": "Dream",
				"promo": "Promo",
				"initiation": "Initiation",
				"winner": "Winner"
			}
		},
		"dreamlandId": {
			"name": "Dreamland ID",
			"description": "Identifier of the card in the Dreamland app"
		},
		"cardType": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"perso": "Perso",
				"kick": "Kick",
				"soutien": "Soutien",
				"lieu": "Lieu"
			}
		},
		"cardClass": {
			"name": "Class",
			"description": "Whether the Perso is a Voyageur or a Créature",
			"values": {
				"voyageur": "Voyageur",
				"creature": "Créature"
			}
		},
		"keywords": {
			"name": "Keywords",
			"description": "The keywords of the card"
		},
		"abilityName": {
			"name": "Ability",
			"description": "Name of the card's ability"
		},
		"abilityType": {
			"name": "Ability Type",
			"description": "Whether the ability is a Capacité or a Dégât",
			"values": {
				"capacite": "Capacité",
				"degat": "Dégât"
			}
		},
		"activationCondition": {
			"name": "Activation Condition",
			"description": "When the ability can be activated"
		},
		"rulesText": {
			"name": "Card Text",
			"description": "Card rules and abilities text"
		},
		"aura": {
			"name": "Aura",
			"description": "Aura of the Perso"
		},
		"force": {
			"name": "Force",
			"description": "Force of the Kick"
		},
		"rank": {
			"name": "Rank",
			"description": "Rank of the Kick",
			"values": {
				"b": "B",
				"m": "M",
				"s": "S",
				"star": "★"
			}
		},
		"comboRanks": {
			"name": "Combo Ranks",
			"description": "Ranks of the Kicks required to activate the combo"
		},
		"comboBonus": {
			"name": "Combo Bonus",
			"description": "Bonus granted when the combo is activated"
		}
	},
	presets: { "standard": {
		"name": "Standard Set",
		"description": "A complete set with one copy of each card"
	} }
};

//#endregion
//#region src/lib/games/dreamland/i18n/fr.json
var fr_default$18 = {
	name: "Dreamland TCG",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"banale": "Banale",
				"peu_banale": "Peu banale",
				"kibrill": "Kibrill'",
				"crazy": "Crazy",
				"dream": "Dream",
				"promo": "Promo",
				"initiation": "Initiation",
				"winner": "Winner"
			}
		},
		"dreamlandId": {
			"name": "ID Dreamland",
			"description": "Identifiant de la carte dans l'application Dreamland"
		},
		"cardType": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"perso": "Perso",
				"kick": "Kick",
				"soutien": "Soutien",
				"lieu": "Lieu"
			}
		},
		"cardClass": {
			"name": "Classe",
			"description": "Indique si le Perso est un Voyageur ou une Créature",
			"values": {
				"voyageur": "Voyageur",
				"creature": "Créature"
			}
		},
		"keywords": {
			"name": "Mots-clés",
			"description": "Les mots-clés de la carte"
		},
		"abilityName": {
			"name": "Capacité",
			"description": "Nom de la capacité de la carte"
		},
		"abilityType": {
			"name": "Type de capacité",
			"description": "Indique si la capacité est une Capacité ou un Dégât",
			"values": {
				"capacite": "Capacité",
				"degat": "Dégât"
			}
		},
		"activationCondition": {
			"name": "Condition d'activation",
			"description": "Quand la capacité peut être activée"
		},
		"rulesText": {
			"name": "Texte de la carte",
			"description": "Texte de règles et capacités de la carte"
		},
		"aura": {
			"name": "Aura",
			"description": "Aura du Perso"
		},
		"force": {
			"name": "Force",
			"description": "Force du Kick"
		},
		"rank": {
			"name": "Rang",
			"description": "Rang du Kick",
			"values": {
				"b": "B",
				"m": "M",
				"s": "S",
				"star": "★"
			}
		},
		"comboRanks": {
			"name": "Rangs du Combo",
			"description": "Rangs des Kicks requis pour activer le Combo"
		},
		"comboBonus": {
			"name": "Bonus de Combo",
			"description": "Bonus accordé quand le Combo est activé"
		}
	},
	presets: { "standard": {
		"name": "Set standard",
		"description": "Un set complet avec un exemplaire de chaque carte"
	} }
};

//#endregion
//#region src/lib/games/eoa/i18n/en.json
var en_default$17 = {
	name: "Echoes of Astra",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"legendary": "Legendary"
			}
		},
		"affinity": {
			"name": "Affinity",
			"description": "The affinity (colour) of the card",
			"values": {
				"solara": "Solara",
				"lux": "Lux",
				"terra": "Terra",
				"caelum": "Caelum",
				"nox": "Nox",
				"neutral": "Neutral"
			}
		},
		"cardType": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"unit": "Unit",
				"tactic": "Tactic",
				"territory": "Territory",
				"item": "Item"
			}
		},
		"subtypes": {
			"name": "Subtypes",
			"description": "Card subtypes and traits",
			"values": {
				"renowned": "Renowned",
				"human": "Human",
				"equipment": "Equipment",
				"vampire": "Vampire",
				"counter": "Counter",
				"sylvan": "Sylvan",
				"fenrir": "Fenrir",
				"vehicle": "Vehicle",
				"beast": "Beast",
				"demon": "Demon",
				"dragon": "Dragon",
				"dragonkin": "Dragonkin",
				"leporin": "Leporin",
				"kitsune": "Kitsune",
				"automaton": "Automaton",
				"undead": "Undead",
				"avian": "Avian",
				"nekora": "Nekora",
				"angel": "Angel",
				"drone": "Drone",
				"primal": "Primal",
				"monster": "Monster",
				"plant": "Plant",
				"territory": "Territory"
			}
		},
		"variant": {
			"name": "Variant",
			"description": "The printing variant/treatment of the card",
			"values": {
				"normal": "Normal",
				"serialized": "Serialized",
				"framebreak": "Framebreak",
				"signature": "Signature"
			}
		},
		"supplyCost": {
			"name": "Supply Cost",
			"description": "Supply cost to play the card"
		},
		"influence": {
			"name": "Influence",
			"description": "Influence generated by the card"
		},
		"attack": {
			"name": "Attack",
			"description": "Attack power of the card"
		},
		"health": {
			"name": "Health",
			"description": "Health points of the card"
		},
		"artist": {
			"name": "Artist",
			"description": "The artist(s) who illustrated the card"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "Card rules and abilities text"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card - standard finish where available, foil otherwise"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Foil Set",
			"description": "A complete set with one copy of each card in foil finish"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in all finishes and variants"
		}
	}
};

//#endregion
//#region src/lib/games/eoa/i18n/fr.json
var fr_default$17 = {
	name: "Echoes of Astra",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"legendary": "Légendaire"
			}
		},
		"affinity": {
			"name": "Affinité",
			"description": "L'affinité (couleur) de la carte",
			"values": {
				"solara": "Solara",
				"lux": "Lux",
				"terra": "Terra",
				"caelum": "Caelum",
				"nox": "Nox",
				"neutral": "Neutre"
			}
		},
		"cardType": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"unit": "Unité",
				"tactic": "Tactique",
				"territory": "Territoire",
				"item": "Objet"
			}
		},
		"subtypes": {
			"name": "Sous-types",
			"description": "Sous-types et traits de la carte",
			"values": {
				"renowned": "Renommé",
				"human": "Humain",
				"equipment": "Équipement",
				"vampire": "Vampire",
				"counter": "Contre",
				"sylvan": "Sylvestre",
				"fenrir": "Fenrir",
				"vehicle": "Véhicule",
				"beast": "Bête",
				"demon": "Démon",
				"dragon": "Dragon",
				"dragonkin": "Draconide",
				"leporin": "Léporin",
				"kitsune": "Kitsune",
				"automaton": "Automate",
				"undead": "Mort-vivant",
				"avian": "Avien",
				"nekora": "Nekora",
				"angel": "Ange",
				"drone": "Drone",
				"primal": "Primordial",
				"monster": "Monstre",
				"plant": "Plante",
				"territory": "Territoire"
			}
		},
		"variant": {
			"name": "Variante",
			"description": "La variante/le traitement d'impression de la carte",
			"values": {
				"normal": "Normale",
				"serialized": "Numérotée",
				"framebreak": "Hors-cadre",
				"signature": "Signature"
			}
		},
		"supplyCost": {
			"name": "Coût de réserve",
			"description": "Coût de réserve pour jouer la carte"
		},
		"influence": {
			"name": "Influence",
			"description": "Influence générée par la carte"
		},
		"attack": {
			"name": "Attaque",
			"description": "Puissance d'attaque de la carte"
		},
		"health": {
			"name": "Points de vie",
			"description": "Points de vie de la carte"
		},
		"artist": {
			"name": "Artiste",
			"description": "Le ou les artistes ayant illustré la carte"
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Texte de règles et de capacités de la carte"
		}
	},
	presets: {
		"standard": {
			"name": "Set Standard",
			"description": "Un set complet avec une copie de chaque carte - finition standard si disponible, sinon foil"
		},
		"playset": {
			"name": "Playset",
			"description": "Un set complet avec le nombre maximum de copies de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Set Foil",
			"description": "Un set complet avec une copie de chaque carte en finition foil"
		},
		"foilPlayset": {
			"name": "Playset Foil",
			"description": "Un set complet avec le nombre maximum de copies de chaque carte autorisé dans un deck en finition foil"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Un set complet avec une copie de chaque carte dans toutes les finitions et variantes"
		}
	}
};

//#endregion
//#region src/lib/games/fab/i18n/en.json
var en_default$16 = {
	attributes: {
		"classes": {
			"description": "The hero classes of the card",
			"name": "Hero Classes",
			"values": {
				"adjudicator": "Adjudicator",
				"assassin": "Assassin",
				"bard": "Bard",
				"brute": "Brute",
				"generic": "Generic",
				"guardian": "Guardian",
				"illusionist": "Illusionist",
				"mechanologist": "Mechanologist",
				"merchant": "Merchant",
				"ninja": "Ninja",
				"ranger": "Ranger",
				"runeblade": "Runeblade",
				"shapeshifter": "Shapeshifter",
				"warrior": "Warrior",
				"wizard": "Wizard"
			}
		},
		"color": {
			"description": "The color of the card (pitch value)",
			"name": "Color",
			"values": {
				"blue": "Blue",
				"red": "Red",
				"yellow": "Yellow"
			}
		},
		"cost": {
			"description": "The cost to play the card",
			"name": "Cost"
		},
		"defense": {
			"description": "The defense of the card",
			"name": "Defense"
		},
		"finish": {
			"description": "Finish of the card (foil/non-foil)",
			"name": "Finish",
			"values": {
				"coldFoil": "Cold Foil",
				"goldFoil": "Gold Foil",
				"rainbowFoil": "Rainbow Foil",
				"standard": "Standard"
			}
		},
		"intellect": {
			"description": "The intellect of the card",
			"name": "Intellect"
		},
		"life": {
			"description": "The life of the card",
			"name": "Life"
		},
		"name": {
			"description": "The name of the card",
			"name": "Name"
		},
		"pitch": {
			"description": "The pitch value of the card",
			"name": "Pitch"
		},
		"power": {
			"description": "The power of the card",
			"name": "Power"
		},
		"rarity": {
			"description": "The rarity of the card",
			"name": "Rarity",
			"values": {
				"common": "Common",
				"fabled": "Fabled",
				"legendary": "Legendary",
				"majestic": "Majestic",
				"marvel": "Marvel",
				"promo": "Promo",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"token": "Token"
			}
		},
		"subTypes": {
			"description": "Subtypes of the card",
			"name": "Sub-Types",
			"values": {
				"affliction": "Affliction",
				"ally": "Ally",
				"angel": "Angel",
				"arms": "Arms",
				"arrow": "Arrow",
				"ash": "Ash",
				"attack": "Attack",
				"aura": "Aura",
				"axe": "Axe",
				"base": "Base",
				"book": "Book",
				"bow": "Bow",
				"brush": "Brush",
				"chest": "Chest",
				"chi": "Chi",
				"claw": "Claw",
				"club": "Club",
				"construct": "Construct",
				"dagger": "Dagger",
				"demon": "Demon",
				"dragon": "Dragon",
				"evo": "Evo",
				"fiddle": "Fiddle",
				"figment": "Figment",
				"flail": "Flail",
				"gem": "Gem",
				"gun": "Gun",
				"hammer": "Hammer",
				"head": "Head",
				"invocation": "Invocation",
				"item": "Item",
				"landmark": "Landmark",
				"legs": "Legs",
				"lute": "Lute",
				"mercenary": "Mercenary",
				"offHand": "Off-Hand",
				"oneHanded": "(1H)",
				"orb": "Orb",
				"pistol": "Pistol",
				"polearm": "Polearm",
				"quiver": "Quiver",
				"rock": "Rock",
				"scepter": "Scepter",
				"scroll": "Scroll",
				"scythe": "Scythe",
				"shuriken": "Shuriken",
				"song": "Song",
				"staff": "Staff",
				"sword": "Sword",
				"trap": "Trap",
				"twoHanded": "(2H)",
				"wrench": "Wrench",
				"young": "Young"
			}
		},
		"talents": {
			"description": "The talents associated with the card",
			"name": "Talents",
			"values": {
				"chaos": "Chaos",
				"draconic": "Draconic",
				"earth": "Earth",
				"elemental": "Elemental",
				"ice": "Ice",
				"light": "Light",
				"lightning": "Lightning",
				"mystic": "Mystic",
				"rosetta": "Rosetta",
				"royal": "Royal",
				"shadow": "Shadow"
			}
		},
		"types": {
			"description": "Types of the card",
			"name": "Types",
			"values": {
				"action": "Action",
				"attackReaction": "Attack Reaction",
				"block": "Block",
				"defenseReaction": "Defense Reaction",
				"demiHero": "Demi-Hero",
				"equipment": "Equipment",
				"hero": "Hero",
				"instant": "Instant",
				"macro": "Macro",
				"mentor": "Mentor",
				"resource": "Resource",
				"token": "Token",
				"weapon": "Weapon"
			}
		},
		"variant": {
			"description": "The variant of the card",
			"name": "Variant",
			"values": {
				"unlimited": "Unlimited",
				"marvel": "Marvel",
				"marvelAlternateArt": "Marvel Alternate Art",
				"extendedArt": "Extended Art",
				"alternateArt": "Alternate Art",
				"alternateArtA": "Alternate Art #A",
				"alternateArtB": "Alternate Art #B",
				"alternateArtC": "Alternate Art #C",
				"classicConstructed": "Classic Constructed"
			}
		},
		"fabId": {
			"description": "The id of the card in the set",
			"name": "Fab ID"
		},
		"image": {
			"description": "The image of the card",
			"name": "Image"
		},
		"description": {
			"name": "Description",
			"description": "Card ability text and description"
		},
		"artist": {
			"name": "Artist",
			"description": "The artist who created the card art"
		},
		"flavorText": {
			"name": "Flavor Text",
			"description": "The flavor text on the card"
		}
	},
	name: "Flesh and Blood",
	presets: {
		"foil": {
			"description": "A complete set with one copy of each card in foil finish",
			"name": "Rainbow Foil Set"
		},
		"foilPlayset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish",
			"name": "Rainbow Foil Playset"
		},
		"masterset": {
			"description": "A complete set with one copy of each card in standard, rainbow foil, gold foil, and cold foil finishes, plus all promotional variants",
			"name": "Master Set"
		},
		"playset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish",
			"name": "Playset"
		},
		"standard": {
			"description": "A complete set with one copy of each card in standard finish (Legendary, Marvel and Fabled cards in their available foil finish)",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/fab/i18n/fr.json
var fr_default$16 = {
	attributes: {
		"classes": {
			"description": "Les classes de héros de la carte",
			"name": "Classes de héros",
			"values": {
				"adjudicator": "Adjudicator",
				"assassin": "Assassin",
				"bard": "Bard",
				"brute": "Brute",
				"generic": "Générique",
				"guardian": "Gardien",
				"illusionist": "Illusionniste",
				"mechanologist": "Mécanologue",
				"merchant": "Marchand",
				"ninja": "Ninja",
				"ranger": "Ranger",
				"runeblade": "Lamederune",
				"shapeshifter": "Changeforme",
				"warrior": "Guerrier",
				"wizard": "Sorcier"
			}
		},
		"color": {
			"description": "La couleur de la carte (valeur pitch)",
			"name": "Couleur",
			"values": {
				"blue": "Bleu",
				"red": "Rouge",
				"yellow": "Jaune"
			}
		},
		"cost": {
			"description": "Le coût pour jouer la carte",
			"name": "Coût"
		},
		"defense": {
			"description": "La défense de la carte",
			"name": "Défense"
		},
		"finish": {
			"description": "Finition de la carte (foil/non-foil)",
			"name": "Finition",
			"values": {
				"coldFoil": "Cold Foil",
				"goldFoil": "Gold Foil",
				"rainbowFoil": "Rainbow Foil",
				"standard": "Standard"
			}
		},
		"intellect": {
			"description": "L'intellect de la carte",
			"name": "Intellect"
		},
		"life": {
			"description": "La vie de la carte",
			"name": "Vie"
		},
		"name": {
			"description": "Le nom de la carte",
			"name": "Nom"
		},
		"pitch": {
			"description": "La valeur Pitch de la carte",
			"name": "Pitch"
		},
		"power": {
			"description": "La puissance de la carte",
			"name": "Puissance"
		},
		"rarity": {
			"description": "La rareté de la carte",
			"name": "Rareté",
			"values": {
				"common": "Common",
				"fabled": "Fabled",
				"legendary": "Legendary",
				"majestic": "Majestic",
				"marvel": "Marvel",
				"promo": "Promo",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"token": "Token"
			}
		},
		"subTypes": {
			"description": "Sous-types de la carte",
			"name": "Sous-types",
			"values": {
				"affliction": "Affliction",
				"ally": "Allié",
				"angel": "Ange",
				"arms": "Bras",
				"arrow": "Flèche",
				"ash": "Cendre",
				"attack": "Attaque",
				"aura": "Aura",
				"axe": "Hache",
				"base": "Base",
				"book": "Book",
				"bow": "Arc",
				"brush": "Rejet",
				"chest": "Torse",
				"chi": "Chi",
				"claw": "Griffe",
				"club": "Gourdin",
				"construct": "Construction",
				"dagger": "Dague",
				"demon": "Démon",
				"dragon": "Dragon",
				"evo": "Évo",
				"fiddle": "Fiddle",
				"figment": "Illusion",
				"flail": "Fléau",
				"gem": "Gemme",
				"gun": "Pistolet",
				"hammer": "Marteau",
				"head": "Tête",
				"invocation": "Invocation",
				"item": "Objet",
				"landmark": "Point de repère",
				"legs": "Jambes",
				"lute": "Lute",
				"mercenary": "Mercenaire",
				"offHand": "Main non dominante",
				"oneHanded": "(1M)",
				"orb": "Orbe",
				"pistol": "Pistolet",
				"polearm": "Arme d'hast",
				"quiver": "Carquois",
				"rock": "Rock",
				"scepter": "Sceptre",
				"scroll": "Parchemin",
				"scythe": "Scythe",
				"shuriken": "Shuriken",
				"song": "Song",
				"staff": "Bâton",
				"sword": "Épée",
				"trap": "Piège",
				"twoHanded": "(2M)",
				"wrench": "Clé",
				"young": "Jeune"
			}
		},
		"talents": {
			"description": "Les talents associés à la carte",
			"name": "Talents",
			"values": {
				"chaos": "Chaos",
				"draconic": "Draconique",
				"earth": "Terre",
				"elemental": "Élémentaire",
				"ice": "Glace",
				"light": "Lumière",
				"lightning": "Foudre",
				"mystic": "Mystique",
				"rosetta": "Rosetta",
				"royal": "Royal",
				"shadow": "Shadow"
			}
		},
		"types": {
			"description": "Types de la carte",
			"name": "Types",
			"values": {
				"action": "Action",
				"attackReaction": "Réaction d'attaque",
				"block": "Blocage",
				"defenseReaction": "Réaction de défense",
				"demiHero": "Demi-héros",
				"equipment": "Équipement",
				"hero": "Héros",
				"instant": "Instant",
				"macro": "Macro",
				"mentor": "Mentor",
				"resource": "Ressource",
				"token": "Jeton",
				"weapon": "Arme"
			}
		},
		"fabId": {
			"description": "L'id de la carte dans le set",
			"name": "Fab ID"
		},
		"image": {
			"description": "L'image de la carte",
			"name": "Image"
		},
		"variant": {
			"description": "La variante de la carte",
			"name": "Variant",
			"values": {
				"unlimited": "Unlimited",
				"marvel": "Marvel",
				"marvelAlternateArt": "Marvel Alternate Art",
				"extendedArt": "Extended Art",
				"alternateArt": "Alternate Art",
				"alternateArtA": "Alternate Art #A",
				"alternateArtB": "Alternate Art #B",
				"alternateArtC": "Alternate Art #C",
				"classicConstructed": "Classic Constructed"
			}
		},
		"description": {
			"name": "Description",
			"description": "Texte de capacité et description de la carte"
		},
		"artist": {
			"name": "Artiste",
			"description": "L'artiste qui a créé l'illustration de la carte"
		},
		"flavorText": {
			"name": "Texte d'ambiance",
			"description": "Le texte d'ambiance sur la carte"
		}
	},
	name: "Flesh and Blood",
	presets: {
		"foil": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition foil",
			"name": "Rainbow Foil Set"
		},
		"foilPlayset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil",
			"name": "Rainbow Foil Playset"
		},
		"masterset": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard, rainbow foil, gold foil et cold foil, plus toutes les variantes promotionnelles",
			"name": "Master Set"
		},
		"playset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard",
			"name": "Playset"
		},
		"standard": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard (les cartes Legendary, Marvel et Fabled dans leur finition foil disponible)",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/grand-archive/i18n/en.json
var en_default$15 = {
	name: "Grand Archive TCG",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"ultra_rare": "Ultra Rare",
				"promotional_rare": "Promotional Rare",
				"collector_super_rare": "Collector Super Rare",
				"collector_ultra_rare": "Collector Ultra Rare",
				"collector_promo_rare": "Collector Promo Rare"
			}
		},
		"variant": {
			"name": "Variant",
			"description": "Special print treatment of the card",
			"values": {
				"curio_foil": "Curio Foil",
				"quicksilver_foil": "Quicksilver Foil"
			}
		},
		"element": {
			"name": "Element",
			"description": "The elemental affinity of the card",
			"values": {
				"arcane": "Arcane",
				"astra": "Astra",
				"crux": "Crux",
				"exalted": "Exalted",
				"exia": "Exia",
				"fire": "Fire",
				"luxem": "Luxem",
				"neos": "Neos",
				"norm": "Norm",
				"tera": "Tera",
				"umbra": "Umbra",
				"water": "Water",
				"wind": "Wind"
			}
		},
		"cardType": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"action": "Action",
				"ally": "Ally",
				"attack": "Attack",
				"champion": "Champion",
				"domain": "Domain",
				"greater_boon": "Greater Boon",
				"item": "Item",
				"lesser_boon": "Lesser Boon",
				"mastery": "Mastery",
				"phantasia": "Phantasia",
				"regalia": "Regalia",
				"status": "Status",
				"token": "Token",
				"unique": "Unique",
				"weapon": "Weapon"
			}
		},
		"cardClass": {
			"name": "Class",
			"description": "The class of the champion",
			"values": {
				"anomaly": "Anomaly",
				"assassin": "Assassin",
				"cleric": "Cleric",
				"guardian": "Guardian",
				"mage": "Mage",
				"ranger": "Ranger",
				"spirit": "Spirit",
				"tamer": "Tamer",
				"warrior": "Warrior"
			}
		},
		"costType": {
			"name": "Cost Type",
			"description": "The type of cost to play the card",
			"values": {
				"memory": "Memory",
				"reserve": "Reserve",
				"none": "None"
			}
		},
		"costValue": {
			"name": "Cost",
			"description": "The cost value to play the card"
		},
		"power": {
			"name": "Power",
			"description": "Power value of the card"
		},
		"life": {
			"name": "Life",
			"description": "Life value of the card"
		},
		"durability": {
			"name": "Durability",
			"description": "Durability value of the card"
		},
		"level": {
			"name": "Level",
			"description": "Level of the card"
		},
		"speed": {
			"name": "Speed",
			"description": "Speed of the card (Fast or Slow)",
			"values": {
				"fast": "Fast",
				"slow": "Slow"
			}
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "Card rules and effect text"
		},
		"flavorText": {
			"name": "Flavor Text",
			"description": "Card flavor text"
		},
		"artist": {
			"name": "Artist",
			"description": "Card illustrator"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in standard finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Foil Set",
			"description": "A complete set with one copy of each card in foil finish"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in both standard and foil finishes"
		}
	}
};

//#endregion
//#region src/lib/games/grand-archive/i18n/fr.json
var fr_default$15 = {
	name: "Grand Archive TCG",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"ultra_rare": "Ultra Rare",
				"promotional_rare": "Promotionnelle Rare",
				"collector_super_rare": "Collector Super Rare",
				"collector_ultra_rare": "Collector Ultra Rare",
				"collector_promo_rare": "Collector Promo Rare"
			}
		},
		"variant": {
			"name": "Variante",
			"description": "Traitement d'impression spécial de la carte",
			"values": {
				"curio_foil": "Curio Foil",
				"quicksilver_foil": "Quicksilver Foil"
			}
		},
		"element": {
			"name": "Élément",
			"description": "L'affinité élémentaire de la carte",
			"values": {
				"arcane": "Arcane",
				"astra": "Astra",
				"crux": "Crux",
				"exalted": "Exalté",
				"exia": "Exia",
				"fire": "Feu",
				"luxem": "Luxem",
				"neos": "Neos",
				"norm": "Norm",
				"tera": "Tera",
				"umbra": "Umbra",
				"water": "Eau",
				"wind": "Vent"
			}
		},
		"cardType": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"action": "Action",
				"ally": "Allié",
				"attack": "Attaque",
				"champion": "Champion",
				"domain": "Domaine",
				"greater_boon": "Bénédiction majeure",
				"item": "Objet",
				"lesser_boon": "Bénédiction mineure",
				"mastery": "Maîtrise",
				"phantasia": "Phantasia",
				"regalia": "Régalia",
				"status": "Statut",
				"token": "Jeton",
				"unique": "Unique",
				"weapon": "Arme"
			}
		},
		"cardClass": {
			"name": "Classe",
			"description": "La classe du champion",
			"values": {
				"anomaly": "Anomalie",
				"assassin": "Assassin",
				"cleric": "Clerc",
				"guardian": "Gardien",
				"mage": "Mage",
				"ranger": "Rôdeur",
				"spirit": "Esprit",
				"tamer": "Dompteur",
				"warrior": "Guerrier"
			}
		},
		"costType": {
			"name": "Type de coût",
			"description": "Le type de coût pour jouer la carte",
			"values": {
				"memory": "Mémoire",
				"reserve": "Réserve",
				"none": "Aucun"
			}
		},
		"costValue": {
			"name": "Coût",
			"description": "La valeur de coût pour jouer la carte"
		},
		"power": {
			"name": "Puissance",
			"description": "Valeur de puissance de la carte"
		},
		"life": {
			"name": "Vie",
			"description": "Valeur de vie de la carte"
		},
		"durability": {
			"name": "Durabilité",
			"description": "Valeur de durabilité de la carte"
		},
		"level": {
			"name": "Niveau",
			"description": "Niveau de la carte"
		},
		"speed": {
			"name": "Vitesse",
			"description": "Vitesse de la carte (Rapide ou Lent)",
			"values": {
				"fast": "Rapide",
				"slow": "Lent"
			}
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Texte des règles et effets de la carte"
		},
		"flavorText": {
			"name": "Texte d'ambiance",
			"description": "Texte d'ambiance de la carte"
		},
		"artist": {
			"name": "Artiste",
			"description": "Illustrateur de la carte"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Foil Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition foil"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard et foil"
		}
	}
};

//#endregion
//#region src/lib/games/gundam/i18n/en.json
var en_default$14 = {
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"common_plus": "Common +",
				"common_double_plus": "Common ++",
				"uncommon": "Uncommon",
				"uncommon_plus": "Uncommon +",
				"rare": "Rare",
				"rare_plus": "Rare +",
				"legend_rare": "Legend Rare",
				"legend_rare_plus": "Legend Rare +",
				"legend_rare_double_plus": "Legend Rare ++",
				"promo": "Promo"
			}
		},
		"color": {
			"name": "Color",
			"description": "The color of the card",
			"values": {
				"blue": "Blue",
				"green": "Green",
				"red": "Red",
				"white": "White",
				"purple": "Purple"
			}
		},
		"finish": {
			"name": "Finish",
			"description": "The finish of the card",
			"values": {
				"standard": "Standard",
				"holofoil": "Holofoil"
			}
		},
		"type": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"unit": "Unit",
				"pilot": "Pilot",
				"command": "Command",
				"base": "Base",
				"resource": "Resource",
				"ex_base": "EX Base",
				"ex_resource": "EX Resource"
			}
		},
		"level": {
			"name": "Level",
			"description": "The level of the card"
		},
		"cost": {
			"name": "Cost",
			"description": "Cost to play the card"
		},
		"attackPoints": {
			"name": "Attack Points",
			"description": "The attack points of the card"
		},
		"hitPoints": {
			"name": "Hit Points",
			"description": "The hit points of the card"
		},
		"traits": {
			"name": "Traits",
			"description": "The traits of the card"
		},
		"linkCondition": {
			"name": "Link Condition",
			"description": "The link condition of the card"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "The rules text of the card"
		}
	},
	name: "Gundam Card Game",
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in standard finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Holofoil Set",
			"description": "A complete set with one copy of each card in holofoil finish"
		},
		"foilPlayset": {
			"name": "Holofoil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in holofoil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in both standard and holofoil finishes"
		}
	}
};

//#endregion
//#region src/lib/games/gundam/i18n/fr.json
var fr_default$14 = {
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"common_plus": "Commune +",
				"common_double_plus": "Commune ++",
				"uncommon": "Peu commune",
				"uncommon_plus": "Peu commune +",
				"rare": "Rare",
				"rare_plus": "Rare +",
				"legend_rare": "Légendaire",
				"legend_rare_plus": "Légendaire +",
				"legend_rare_double_plus": "Légendaire ++",
				"promo": "Promo"
			}
		},
		"color": {
			"name": "Couleur",
			"description": "La couleur de la carte",
			"values": {
				"blue": "Bleu",
				"green": "Vert",
				"red": "Rouge",
				"white": "Blanc",
				"purple": "Violet"
			}
		},
		"finish": {
			"name": "Finition",
			"description": "La finition de la carte",
			"values": {
				"standard": "Standard",
				"holofoil": "Holographique"
			}
		},
		"type": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"unit": "Unité",
				"pilot": "Pilote",
				"command": "Commande",
				"base": "Base",
				"resource": "Ressource",
				"ex_base": "Base EX",
				"ex_resource": "Ressource EX"
			}
		},
		"level": {
			"name": "Niveau",
			"description": "Le niveau de la carte"
		},
		"cost": {
			"name": "Coût",
			"description": "Coût pour jouer la carte"
		},
		"attackPoints": {
			"name": "Points d'attaque",
			"description": "Les points d'attaque de la carte"
		},
		"hitPoints": {
			"name": "Points de vie",
			"description": "Les points de vie de la carte"
		},
		"traits": {
			"name": "Traits",
			"description": "Les traits de la carte"
		},
		"linkCondition": {
			"name": "Condition de lien",
			"description": "La condition de lien de la carte"
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Le texte de règles de la carte"
		}
	},
	name: "Gundam Card Game",
	presets: {
		"standard": {
			"name": "Set standard",
			"description": "Un set complet avec une copie de chaque carte en finition standard"
		},
		"playset": {
			"name": "Playset",
			"description": "Un set complet avec le nombre maximum de copies de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Set holographique",
			"description": "Un set complet avec une copie de chaque carte en finition holographique"
		},
		"foilPlayset": {
			"name": "Playset holographique",
			"description": "Un set complet avec le nombre maximum de copies de chaque carte autorisé dans un deck en finition holographique"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Un set complet avec une copie de chaque carte en finition standard et holographique"
		}
	}
};

//#endregion
//#region src/lib/games/lorcana/i18n/en.json
var en_default$13 = {
	attributes: {
		"attack": {
			"description": "The attack power of the card",
			"name": "Strength"
		},
		"color": {
			"description": "The color of the card",
			"name": "Color",
			"values": {
				"amber": "Amber",
				"amethyst": "Amethyst",
				"emerald": "Emerald",
				"ruby": "Ruby",
				"sapphire": "Sapphire",
				"steel": "Steel"
			}
		},
		"cost": {
			"description": "The ink cost to play the card",
			"name": "Cost"
		},
		"defense": {
			"description": "The defense of the card",
			"name": "Willpower"
		},
		"ink": {
			"description": "Whether the card can be inked or not",
			"name": "Inkable",
			"values": {
				"false": "Non inkable",
				"true": "Inkable"
			}
		},
		"lore": {
			"description": "The lore value of the card",
			"name": "Lore"
		},
		"rarity": {
			"description": "The rarity of the card",
			"name": "Rarity",
			"values": {
				"common": "Common",
				"enchanted": "Enchanted",
				"epic": "Epic",
				"iconic": "Iconic",
				"legendary": "Legendary",
				"promo": "Promo",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"uncommon": "Uncommon"
			}
		},
		"cardSetId": {
			"description": "The id of the card in the set",
			"name": "Card Set ID"
		},
		"dreambornId": {
			"description": "The id of the card on dreamborn",
			"name": "Dreamborn ID"
		},
		"shortName": {
			"description": "The short name of the card, for example Elsa instead of Elsa - The Snow Queen",
			"name": "Short name"
		},
		"flavorText": {
			"description": "The flavor text of the card",
			"name": "Flavor text"
		},
		"abilities": {
			"name": "Abilities",
			"description": "The abilities of the card"
		},
		"description": {
			"name": "Card Text",
			"description": "The full text description of the card"
		},
		"title": {
			"description": "The title of the card, positioned after the short name",
			"name": "Title"
		},
		"trait": {
			"description": "Traits the card has",
			"name": "Traits",
			"values": {
				"action": "Action",
				"alien": "Alien",
				"ally": "Ally",
				"boss": "Boss",
				"broom": "Broom",
				"captain": "Captain",
				"colossus": "Colossus",
				"deity": "Deity",
				"detective": "Detective",
				"dragon": "Dragon",
				"dreamborn": "Dreamborn",
				"entangled": "Entangled",
				"fairy": "Fairy",
				"floodborn": "Floodborn",
				"gargoyle": "Gargoyle",
				"ghost": "Ghost",
				"hero": "Hero",
				"hyena": "Hyena",
				"illusion": "Illusion",
				"inventor": "Inventor",
				"item": "Item",
				"king": "King",
				"knight": "Knight",
				"madrigal": "Madrigal",
				"mentor": "Mentor",
				"musketeer": "Musketeer",
				"obstacle": "Obstacle",
				"pirate": "Pirate",
				"prince": "Prince",
				"princess": "Princess",
				"puppy": "Puppy",
				"queen": "Queen",
				"racer": "Racer",
				"robot": "Robot",
				"seven_dwarfs": "Seven Dwarfs",
				"song": "Song",
				"sorcerer": "Sorcerer",
				"storyborn": "Storyborn",
				"tigger": "Tigger",
				"titan": "Titan",
				"villain": "Villain",
				"whisper": "Whisper",
				"dinosaur": "Dinosaur",
				"giant": "Giant",
				"hunny": "Hunny",
				"monster": "Monster",
				"red_panda": "Red Panda",
				"super": "Super",
				"team": "Team",
				"toy": "Toy",
				"vineling": "Vineling",
				"hyperia_city": "Hyperia City"
			}
		},
		"type": {
			"description": "Type of the card",
			"name": "Type",
			"values": {
				"action": "Action",
				"character": "Character",
				"item": "Item",
				"location": "Location"
			}
		}
	},
	name: "Disney Lorcana",
	presets: {
		"foil": {
			"description": "A complete set with one copy of each card in foil finish",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "A complete set with one copy of each card in standard and foil finishes, plus all promotional variants",
			"name": "Master Set"
		},
		"playset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish",
			"name": "Playset"
		},
		"standard": {
			"description": "A complete set with one copy of each card in standard finish",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/lorcana/i18n/fr.json
var fr_default$13 = {
	attributes: {
		"attack": {
			"description": "La force de la carte",
			"name": "Force"
		},
		"color": {
			"description": "La couleur de la carte",
			"name": "Couleur",
			"values": {
				"amber": "Ambre",
				"amethyst": "Améthyste",
				"emerald": "Émeraude",
				"ruby": "Rubis",
				"sapphire": "Saphir",
				"steel": "Acier"
			}
		},
		"cost": {
			"description": "Le coût en encre pour jouer la carte",
			"name": "Coût"
		},
		"defense": {
			"description": "La volonté de la carte",
			"name": "Volonté"
		},
		"ink": {
			"description": "Si la carte peut être encrée ou non",
			"name": "Encrable",
			"values": {
				"false": "Non encrable",
				"true": "Encrable"
			}
		},
		"lore": {
			"description": "La valeur de lore de la carte",
			"name": "Lore"
		},
		"rarity": {
			"description": "La rareté de la carte",
			"name": "Rareté",
			"values": {
				"common": "Common",
				"enchanted": "Enchanted",
				"epic": "Epic",
				"iconic": "Iconic",
				"legendary": "Legendary",
				"promo": "Promo",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"uncommon": "Uncommon"
			}
		},
		"cardSetId": {
			"description": "L'id de la carte dans le set",
			"name": "Card Set ID"
		},
		"dreambornId": {
			"description": "L'id de la carte sur dreamborn",
			"name": "Dreamborn ID"
		},
		"shortName": {
			"description": "Le nom raccourci de la carte, par exemple Elsa au lieu d'Elsa - La Reine des Neiges",
			"name": "Nom raccourci"
		},
		"flavorText": {
			"description": "Le texte d'ambiance de la carte",
			"name": "Texte d'ambiance"
		},
		"abilities": {
			"name": "Capacités",
			"description": "Les capacités de la carte"
		},
		"description": {
			"name": "Texte de carte",
			"description": "Le texte complet de la carte"
		},
		"title": {
			"description": "Le titre de la carte, positionné après le nom raccourci",
			"name": "Titre"
		},
		"trait": {
			"description": "Les traits que possède la carte",
			"name": "Traits",
			"values": {
				"action": "Action",
				"alien": "Alien",
				"ally": "Allié",
				"boss": "Boss",
				"broom": "Balai",
				"captain": "Capitaine",
				"colossus": "Colosse",
				"deity": "Dieu",
				"detective": "Détective",
				"dragon": "Dragon",
				"dreamborn": "Dreamborn",
				"entangled": "Entangled",
				"fairy": "Fée",
				"floodborn": "Floodborn",
				"gargoyle": "Gargouille",
				"ghost": "Fantôme",
				"hero": "Héros",
				"hyena": "Hyène",
				"illusion": "Illusion",
				"inventor": "Inventeur",
				"item": "Objet",
				"king": "Roi",
				"knight": "Chevalier",
				"madrigal": "Madrigal",
				"mentor": "Mentor",
				"musketeer": "Mousquetaire",
				"obstacle": "Obstacle",
				"pirate": "Pirate",
				"prince": "Prince",
				"princess": "Princesse",
				"puppy": "Chiot",
				"queen": "Reine",
				"racer": "Coureur",
				"robot": "Robot",
				"seven_dwarfs": "Sept Nains",
				"song": "Chanson",
				"sorcerer": "Mage",
				"storyborn": "Storyborn",
				"tigger": "Tigrou",
				"titan": "Titan",
				"villain": "Méchant",
				"whisper": "Murmure",
				"dinosaur": "Dinosaure",
				"giant": "Géant",
				"hunny": "Miel",
				"monster": "Monstre",
				"red_panda": "Panda roux",
				"super": "Super",
				"team": "Équipe",
				"toy": "Jouet",
				"vineling": "Vineling",
				"hyperia_city": "Hyperia City"
			}
		},
		"type": {
			"description": "Type de la carte",
			"name": "Type",
			"values": {
				"action": "Action",
				"character": "Personnage",
				"item": "Objet",
				"location": "Lieu"
			}
		}
	},
	name: "Disney Lorcana",
	presets: {
		"foil": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition foil",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard et foil, plus toutes les variantes promotionnelles",
			"name": "Master Set"
		},
		"playset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard",
			"name": "Playset"
		},
		"standard": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/mtg/i18n/en.json
var en_default$12 = {
	attributes: {
		"color": {
			"description": "The colors present on the mana cost of a card",
			"name": "Color",
			"values": {
				"black": "Black",
				"blue": "Blue",
				"colorless": "Colorless",
				"green": "Green",
				"red": "Red",
				"white": "White"
			}
		},
		"colorIdentity": {
			"description": "The colors present on the card",
			"name": "Color Identity",
			"values": {
				"black": "Black",
				"blue": "Blue",
				"colorless": "Colorless",
				"green": "Green",
				"red": "Red",
				"white": "White"
			}
		},
		"finish": {
			"description": "The finish of the card",
			"name": "Finish",
			"values": {
				"etched": "Etched",
				"foil": "Foil",
				"signed": "Signed",
				"standard": "Standard"
			}
		},
		"rarity": {
			"description": "The rarity of the card",
			"name": "Rarity",
			"values": {
				"bonus": "Bonus",
				"common": "Common",
				"mythic": "Mythic",
				"rare": "Rare",
				"special": "Special",
				"uncommon": "Uncommon"
			}
		},
		"types": {
			"name": "Types",
			"description": "Card types (Creature, Instant, Sorcery, etc.)"
		},
		"description": {
			"name": "Card Text",
			"description": "The oracle/rules text of the card"
		}
	},
	name: "Magic: The Gathering",
	presets: {
		"foil": {
			"description": "A complete set with one copy of each card in traditional foil finish",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in traditional foil finish",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "A complete set with one copy of each card in standard, traditional foil, and etched foil finishes",
			"name": "Master Set"
		},
		"playset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish",
			"name": "Playset"
		},
		"standard": {
			"description": "A complete set with one copy of each card in standard finish",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/mtg/i18n/fr.json
var fr_default$12 = {
	attributes: {
		"color": {
			"description": "Les couleurs présentes dans le coût de mana d'une carte",
			"name": "Couleur",
			"values": {
				"black": "Noir",
				"blue": "Bleu",
				"colorless": "Incolore",
				"green": "Vert",
				"red": "Rouge",
				"white": "Blanc"
			}
		},
		"colorIdentity": {
			"description": "Les couleurs présentes sur la carte",
			"name": "Identité de couleur",
			"values": {
				"black": "Noir",
				"blue": "Bleu",
				"colorless": "Incolore",
				"green": "Vert",
				"red": "Rouge",
				"white": "Blanc"
			}
		},
		"finish": {
			"description": "La finition de la carte",
			"name": "Finition",
			"values": {
				"etched": "Etched",
				"foil": "Foil",
				"signed": "Signed",
				"standard": "Standard"
			}
		},
		"rarity": {
			"description": "La rareté de la carte",
			"name": "Rareté",
			"values": {
				"bonus": "Bonus",
				"common": "Common",
				"mythic": "Mythic",
				"rare": "Rare",
				"special": "Special",
				"uncommon": "Uncommon"
			}
		},
		"types": {
			"name": "Types",
			"description": "Types de carte (Créature, Éphémère, Rituel, etc.)"
		},
		"description": {
			"name": "Texte de carte",
			"description": "Le texte oracle/règles de la carte"
		}
	},
	name: "Magic: The Gathering",
	presets: {
		"foil": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition foil",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard, foil et etched foil",
			"name": "Master Set"
		},
		"playset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard",
			"name": "Playset"
		},
		"standard": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/naruto-mythos/i18n/en.json
var en_default$11 = {
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"rare_art": "Rare Art",
				"secret": "Secret",
				"secret_variant": "Secret Variant",
				"mythos": "Mythos",
				"mythos_variant": "Mythos Variant",
				"legendary": "Legendary",
				"chibi": "Chibi",
				"pop": "POP",
				"shinobi": "Shinobi",
				"special": "Special"
			}
		},
		"variant": {
			"name": "Variant",
			"description": "The variant of the card",
			"values": {
				"fullArt": "Full Art",
				"alternateArt": "Alternate Art"
			}
		},
		"cardType": {
			"name": "Type",
			"description": "The type of the card",
			"values": {
				"character": "Character",
				"attachment": "Attachment",
				"mission": "Mission"
			}
		},
		"group": {
			"name": "Group",
			"description": "The village or faction of the card",
			"values": {
				"leafVillage": "Leaf Village",
				"akatsuki": "Akatsuki",
				"independent": "Independent",
				"sandVillage": "Sand Village",
				"soundVillage": "Sound Village"
			}
		},
		"orientation": {
			"name": "Orientation",
			"description": "The orientation of the card",
			"values": {
				"portrait": "Portrait",
				"landscape": "Landscape"
			}
		},
		"chakra": {
			"name": "Chakra",
			"description": "Chakra cost of the card"
		},
		"power": {
			"name": "Power",
			"description": "Power value of the card"
		},
		"keywords": {
			"name": "Keywords",
			"description": "Keywords and traits of the card",
			"values": {
				"academy": "Academy",
				"academyStudent": "Academy Student",
				"armor": "Armor",
				"bomb": "Bomb",
				"book": "Book",
				"civilian": "Civilian",
				"demonBrother": "Demon Brother",
				"food": "Food",
				"headOfClan": "Head of Clan",
				"hokage": "Hokage",
				"jutsu": "Jutsu",
				"kekkeiGenkai": "Kekkei Genkai",
				"lair": "Lair",
				"landmark": "Landmark",
				"location": "Location",
				"medicalNinja": "Medical Ninja",
				"ninjaHound": "Ninja Hound",
				"ninjaPig": "Ninja Pig",
				"puppet": "Puppet",
				"reanimation": "Reanimation",
				"rogueNinja": "Rogue Ninja",
				"sannin": "Sannin",
				"scroll": "Scroll",
				"situation": "Situation",
				"soundFour": "Sound Four",
				"soundNinja": "Sound Ninja",
				"specialJonin": "Special Jonin",
				"summon": "Summon",
				"taijutsu": "Taijutsu",
				"tailedBeast": "Tailed Beast",
				"team10": "Team 10",
				"team7": "Team 7",
				"team8": "Team 8",
				"teamBaki": "Team Baki",
				"teamDosu": "Team Dosu",
				"teamGuy": "Team Guy",
				"tool": "Tool",
				"village": "Village",
				"villageOfArtisans": "Village of Artisans",
				"weapon": "Weapon"
			}
		},
		"finishes": {
			"name": "Finish",
			"description": "The surface finish of the card",
			"values": {
				"standard": "Standard",
				"foil": "Foil",
				"holographic": "Holographic",
				"goldFoil": "Gold Foil",
				"embossed": "Embossed"
			}
		},
		"rulesText": {
			"name": "Card Text",
			"description": "Card rules and abilities text"
		},
		"version": {
			"name": "Version",
			"description": "The version or subtitle of the card"
		},
		"points": {
			"name": "Points",
			"description": "Mission points value"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in standard finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Holographic Set",
			"description": "A complete set with one copy of each card in holographic, gold, gold foil, or embossed finish"
		},
		"foilPlayset": {
			"name": "Holographic Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card across all finishes - standard, holographic, gold, gold foil, and embossed"
		}
	}
};

//#endregion
//#region src/lib/games/naruto-mythos/i18n/fr.json
var fr_default$11 = {
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"rare_art": "Rare Art",
				"secret": "Secrète",
				"secret_variant": "Variante Secrète",
				"mythos": "Mythos",
				"mythos_variant": "Mythos Variante",
				"legendary": "Légendaire",
				"chibi": "Chibi",
				"pop": "POP",
				"shinobi": "Shinobi",
				"special": "Spéciale"
			}
		},
		"variant": {
			"name": "Variante",
			"description": "La variante de la carte",
			"values": {
				"fullArt": "Full Art",
				"alternateArt": "Alternate Art"
			}
		},
		"cardType": {
			"name": "Type",
			"description": "Le type de la carte",
			"values": {
				"character": "Personnage",
				"attachment": "Équipement",
				"mission": "Mission"
			}
		},
		"group": {
			"name": "Groupe",
			"description": "Le village ou la faction de la carte",
			"values": {
				"leafVillage": "Konoha",
				"akatsuki": "Akatsuki",
				"independent": "Indépendant",
				"sandVillage": "Suna",
				"soundVillage": "Oto"
			}
		},
		"orientation": {
			"name": "Orientation",
			"description": "L'orientation de la carte",
			"values": {
				"portrait": "Portrait",
				"landscape": "Paysage"
			}
		},
		"chakra": {
			"name": "Chakra",
			"description": "Coût en chakra de la carte"
		},
		"power": {
			"name": "Puissance",
			"description": "Valeur de puissance de la carte"
		},
		"keywords": {
			"name": "Mots-clés",
			"description": "Mots-clés et traits de la carte",
			"values": {
				"academy": "Académie",
				"academyStudent": "Étudiant de l'Académie",
				"armor": "Armure",
				"bomb": "Bombe",
				"book": "Livre",
				"civilian": "Civil",
				"demonBrother": "Frère Démon",
				"food": "Nourriture",
				"headOfClan": "Chef de Clan",
				"hokage": "Hokage",
				"jutsu": "Jutsu",
				"kekkeiGenkai": "Attribut Génétique",
				"lair": "Repaire",
				"landmark": "Site Remarquable",
				"location": "Lieu",
				"medicalNinja": "Ninja Médecin",
				"ninjaHound": "Ninken",
				"ninjaPig": "Cochon Ninja",
				"puppet": "Marionnette",
				"reanimation": "Réanimation",
				"rogueNinja": "Ninja Déserteur",
				"sannin": "Le Trio Légendaire",
				"scroll": "Parchemin",
				"situation": "Situation",
				"soundFour": "Le Quartet D'Oto",
				"soundNinja": "Ninja Du Son",
				"specialJonin": "Jônin Spéciaux",
				"summon": "Invocation",
				"taijutsu": "Taijutsu",
				"tailedBeast": "Bijû",
				"team10": "Équipe 10",
				"team7": "Équipe 7",
				"team8": "Équipe 8",
				"teamBaki": "Équipe de Baki",
				"teamDosu": "Équipe de Dosu",
				"teamGuy": "Équipe de Gaï",
				"tool": "Outil",
				"village": "Village",
				"villageOfArtisans": "Village des Artisans",
				"weapon": "Buki"
			}
		},
		"finishes": {
			"name": "Finition",
			"description": "La finition de surface de la carte",
			"values": {
				"standard": "Standard",
				"foil": "Foil",
				"holographic": "Holographique",
				"goldFoil": "Feuille d'Or",
				"embossed": "Embossée"
			}
		},
		"rulesText": {
			"name": "Texte de carte",
			"description": "Texte des règles et capacités de la carte"
		},
		"version": {
			"name": "Version",
			"description": "La version ou le sous-titre de la carte"
		},
		"points": {
			"name": "Points",
			"description": "Valeur en points de la mission"
		}
	},
	presets: {
		"standard": {
			"name": "Set Standard",
			"description": "Un set complet avec un exemplaire de chaque carte en finition standard"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Set Holographique",
			"description": "Un set complet avec un exemplaire de chaque carte en finition holographique, or, feuille d'or ou embossée"
		},
		"foilPlayset": {
			"name": "Playset Holographique",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Un set complet avec un exemplaire de chaque carte dans toutes les finitions - standard, holographique, or, feuille d'or et embossée"
		}
	}
};

//#endregion
//#region src/lib/games/onepiece/i18n/en.json
var en_default$10 = {
	attributes: {
		"attribute": {
			"description": "The attribute of the card",
			"name": "Attribute",
			"values": {
				"ranged": "Ranged",
				"slash": "Slash",
				"special": "Special",
				"strike": "Strike",
				"wisdom": "Wisdom"
			}
		},
		"color": {
			"description": "The color present on the card",
			"name": "Color",
			"values": {
				"black": "Black",
				"blue": "Blue",
				"green": "Green",
				"purple": "Purple",
				"red": "Red",
				"yellow": "Yellow"
			}
		},
		"finish": {
			"description": "The finish of the card",
			"name": "Finish",
			"values": {
				"foil": "Foil",
				"standard": "Standard"
			}
		},
		"rarity": {
			"description": "The rarity of the card",
			"name": "Rarity",
			"values": {
				"common": "Common",
				"leader": "Leader",
				"promo": "Promo",
				"rare": "Rare",
				"secret_rare": "Secret Rare",
				"special": "Special",
				"super_rare": "Super Rare",
				"uncommon": "Uncommon",
				"treasure_rare": "Treasure Rare"
			}
		},
		"variant": {
			"description": "The variant of the card",
			"name": "Variant",
			"values": {
				"alternate_art": "Alternate Art",
				"alternate_manga_art": "Alternate Manga Art",
				"full_art": "Full Art",
				"jolly_roger_foil": "Jolly Roger Foil",
				"manga": "Manga",
				"promo": "Promo",
				"spr": "Super Pre-Release",
				"textured_foil": "Textured Foil",
				"wanted_poster": "Wanted Poster",
				"sp": "SP",
				"pirate_foil": "Pirate Foil",
				"gold": "Gold",
				"silver": "Silver",
				"box_topper": "Box Topper",
				"reprint": "Reprint",
				"super_alternate_art": "Super Alternate Art",
				"treasure_rare": "Treasure Rare",
				"gem": "Gem",
				"serial_numbered": "Serial Numbered"
			}
		},
		"type": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"leader": "Leader",
				"don": "DON!!",
				"character": "Character",
				"event": "Event",
				"stage": "Stage"
			}
		},
		"life": {
			"name": "Life",
			"description": "Life points (for Leader cards)"
		},
		"power": {
			"name": "Power",
			"description": "Power value of the card"
		},
		"cost": {
			"name": "Cost",
			"description": "Cost to play the card"
		},
		"counter": {
			"name": "Counter",
			"description": "Counter power value"
		},
		"subtypes": {
			"name": "Subtypes",
			"description": "Character subtypes and traits"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "The rules text of the card"
		}
	},
	name: "One Piece Card Game",
	presets: {
		"foil": {
			"description": "A complete set with one copy of each card in foil finish",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "A complete set with one copy of each card in standard, foil, and alternate art finishes",
			"name": "Masterset"
		},
		"playset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish",
			"name": "Playset"
		},
		"standard": {
			"description": "A complete set with one copy of each card in standard finish",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/onepiece/i18n/fr.json
var fr_default$10 = {
	attributes: {
		"attribute": {
			"description": "L'attribut de la carte",
			"name": "Attribut",
			"values": {
				"ranged": "Distance",
				"slash": "Tranche",
				"special": "Spécial",
				"strike": "Frappe",
				"wisdom": "Sagesse"
			}
		},
		"color": {
			"description": "La couleur présente sur la carte",
			"name": "Couleur",
			"values": {
				"black": "Noir",
				"blue": "Bleu",
				"green": "Vert",
				"purple": "Violet",
				"red": "Rouge",
				"yellow": "Jaune"
			}
		},
		"finish": {
			"description": "La finition de la carte",
			"name": "Finition",
			"values": {
				"foil": "Foil",
				"standard": "Standard"
			}
		},
		"rarity": {
			"description": "La rareté de la carte",
			"name": "Rareté",
			"values": {
				"common": "Common",
				"leader": "Leader",
				"promo": "Promo",
				"rare": "Rare",
				"secret_rare": "Secret Rare",
				"special": "Special",
				"super_rare": "Super Rare",
				"uncommon": "Uncommon",
				"treasure_rare": "Treasure Rare"
			}
		},
		"variant": {
			"description": "La variante de la carte",
			"name": "Variante",
			"values": {
				"alternate_art": "Alternate Art",
				"alternate_manga_art": "Alternate Manga Art",
				"full_art": "Full Art",
				"jolly_roger_foil": "Jolly Roger Foil",
				"manga": "Manga",
				"promo": "Promo",
				"spr": "Super Pre-Release",
				"textured_foil": "Textured Foil",
				"wanted_poster": "Wanted Poster",
				"sp": "SP",
				"pirate_foil": "Pirate Foil",
				"gold": "Or",
				"silver": "Argent",
				"box_topper": "Box Topper",
				"reprint": "Réimpression",
				"super_alternate_art": "Super Alternate Art",
				"treasure_rare": "Treasure Rare",
				"gem": "Gem",
				"serial_numbered": "Numéroté"
			}
		},
		"type": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"leader": "Leader",
				"don": "DON!!",
				"character": "Personnage",
				"event": "Événement",
				"stage": "Terrain"
			}
		},
		"life": {
			"name": "Vie",
			"description": "Points de vie (pour les cartes Leader)"
		},
		"power": {
			"name": "Puissance",
			"description": "Valeur de puissance de la carte"
		},
		"cost": {
			"name": "Coût",
			"description": "Coût pour jouer la carte"
		},
		"counter": {
			"name": "Contre",
			"description": "Valeur de contre-attaque"
		},
		"subtypes": {
			"name": "Sous-types",
			"description": "Sous-types et traits du personnage"
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Le texte de règles de la carte"
		}
	},
	name: "One Piece Card Game",
	presets: {
		"foil": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition foil",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard, foil et alternative art",
			"name": "Masterset"
		},
		"playset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard",
			"name": "Playset"
		},
		"standard": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/palworld/i18n/en.json
var en_default$9 = {
	name: "Palworld TCG",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"double_rare": "Double Rare",
				"super_rare": "Super Rare",
				"special": "Special",
				"super_special": "Super Special",
				"over_super_rare": "Over Super Rare",
				"trial_deck": "Trial Deck",
				"trial_deck_rare": "Trial Deck Rare",
				"trial_deck_special": "Trial Deck Special",
				"trial_deck_super_rare": "Trial Deck Super Rare",
				"promo": "Promo"
			}
		},
		"cardKind": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"pal": "Pal",
				"event": "Event",
				"gear": "Gear",
				"soul": "Soul",
				"structure": "Structure"
			}
		},
		"cardKindSub": {
			"name": "Card Subtype",
			"description": "The subtype of the card",
			"values": {
				"normal_pal": "Normal Pal",
				"lucky_pal": "Lucky Pal"
			}
		},
		"color": {
			"name": "Color",
			"description": "The color of the card",
			"values": {
				"red": "Red",
				"blue": "Blue",
				"green": "Green",
				"purple": "Purple",
				"colorless": "Colorless"
			}
		},
		"types": {
			"name": "Element",
			"description": "The elements of the Pal",
			"values": {
				"neutral": "Neutral",
				"fire": "Fire",
				"water": "Water",
				"electric": "Electric",
				"grass": "Grass",
				"dark": "Dark",
				"dragon": "Dragon",
				"ground": "Ground",
				"ice": "Ice"
			}
		},
		"aptitudes": {
			"name": "Aptitudes",
			"description": "The work aptitudes of the Pal",
			"values": {
				"harvesting": "Harvesting",
				"crafting": "Crafting",
				"collecting": "Collecting",
				"transporting": "Transporting",
				"kindling": "Kindling",
				"cooling": "Cooling",
				"electricity": "Electricity",
				"farming": "Farming"
			}
		},
		"cost": {
			"name": "Cost",
			"description": "Cost to play the card"
		},
		"power": {
			"name": "Power",
			"description": "Power value of the card"
		},
		"attack": {
			"name": "Attack",
			"description": "Attack value of the card"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "Card rules and abilities text"
		},
		"flavorText": {
			"name": "Flavor Text",
			"description": "Flavor text of the card"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum four copies of each card allowed in a deck"
		}
	}
};

//#endregion
//#region src/lib/games/palworld/i18n/fr.json
var fr_default$9 = {
	name: "Palworld TCG",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"double_rare": "Double Rare",
				"super_rare": "Super Rare",
				"special": "Spéciale",
				"super_special": "Super Spéciale",
				"over_super_rare": "Over Super Rare",
				"trial_deck": "Deck d'initiation",
				"trial_deck_rare": "Deck d'initiation Rare",
				"trial_deck_special": "Deck d'initiation spécial",
				"trial_deck_super_rare": "Deck d'initiation Super Rare",
				"promo": "Promo"
			}
		},
		"cardKind": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"pal": "Pal",
				"event": "Événement",
				"gear": "Équipement",
				"soul": "Âme",
				"structure": "Structure"
			}
		},
		"cardKindSub": {
			"name": "Sous-type de carte",
			"description": "Le sous-type de la carte",
			"values": {
				"normal_pal": "Pal normal",
				"lucky_pal": "Pal chanceux"
			}
		},
		"color": {
			"name": "Couleur",
			"description": "La couleur de la carte",
			"values": {
				"red": "Rouge",
				"blue": "Bleu",
				"green": "Vert",
				"purple": "Violet",
				"colorless": "Incolore"
			}
		},
		"types": {
			"name": "Élément",
			"description": "Les éléments du Pal",
			"values": {
				"neutral": "Neutre",
				"fire": "Feu",
				"water": "Eau",
				"electric": "Électrique",
				"grass": "Plante",
				"dark": "Ténèbres",
				"dragon": "Dragon",
				"ground": "Sol",
				"ice": "Glace"
			}
		},
		"aptitudes": {
			"name": "Aptitudes",
			"description": "Les aptitudes de travail du Pal",
			"values": {
				"harvesting": "Récolte",
				"crafting": "Artisanat",
				"collecting": "Collecte",
				"transporting": "Transport",
				"kindling": "Allumage",
				"cooling": "Refroidissement",
				"electricity": "Électricité",
				"farming": "Agriculture"
			}
		},
		"cost": {
			"name": "Coût",
			"description": "Coût pour jouer la carte"
		},
		"power": {
			"name": "Puissance",
			"description": "Valeur de puissance de la carte"
		},
		"attack": {
			"name": "Attaque",
			"description": "Valeur d'attaque de la carte"
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Texte des règles et capacités de la carte"
		},
		"flavorText": {
			"name": "Texte d'ambiance",
			"description": "Texte d'ambiance de la carte"
		}
	},
	presets: {
		"standard": {
			"name": "Set standard",
			"description": "Un set complet avec un exemplaire de chaque carte"
		},
		"playset": {
			"name": "Playset",
			"description": "Un set complet avec le maximum de quatre exemplaires de chaque carte autorisés dans un deck"
		}
	}
};

//#endregion
//#region src/lib/games/pokemon/i18n/en.json
var en_default$8 = {
	attributes: {
		"cardType": {
			"description": "Type of the card",
			"name": "Card Type",
			"values": {
				"energy": "Energy",
				"pokemon": "Pokemon",
				"trainer": "Trainer"
			}
		},
		"pokemonTypes": {
			"description": "Type of the Pokemon",
			"name": "Pokemon Type",
			"values": {
				"colorless": "Colorless",
				"dark": "Dark",
				"dragon": "Dragon",
				"electric": "Electric",
				"fairy": "Fairy",
				"fighting": "Fighting",
				"fire": "Fire",
				"grass": "Grass",
				"psychic": "Psychic",
				"steel": "Steel",
				"water": "Water"
			}
		},
		"rarity": {
			"description": "The rarity of the card",
			"name": "Rarity",
			"values": {
				"ace_spec_rare": "ACE SPEC Rare",
				"amazing_rare": "Amazing Rare",
				"black_white_rare": "Black White Rare",
				"classic": "Classic",
				"classic_collection": "Classic Collection",
				"common": "Common",
				"crown": "Crown",
				"double_rare": "Double Rare",
				"four_diamond": "Four Diamond",
				"full_art_trainer": "Full Art Trainer",
				"futuristic_rare": "Futuristic Rare",
				"holo_rare": "Rare Holo",
				"holo_rare_v": "Rare Holo V",
				"holo_rare_vmax": "Rare Holo VMax",
				"holo_rare_vstar": "Rare Holo VStar",
				"hyper_rare": "Hyper Rare",
				"illustration_rare": "Illustration Rare",
				"legend": "Legend",
				"mega_attack_rare": "Mega Attack Rare",
				"none": "None",
				"one_diamond": "One Diamond",
				"one_shiny": "One Shiny",
				"one_star": "One Star",
				"pikachu_rare": "Pikachu Rare",
				"promo": "Promo",
				"radiant_rare": "Radiant Rare",
				"rare": "Rare",
				"rare_break": "Rare BREAK",
				"rare_holo": "Rare Holo",
				"rare_holo_ex": "Rare Holo EX",
				"rare_holo_gx": "Rare Holo GX",
				"rare_holo_lv_x": "Rare Holo Lv.X",
				"rare_holo_star": "Rare Holo Star",
				"rare_prime": "Rare Prime",
				"rare_prism_star": "Rare Prism Star",
				"rare_rainbow": "Rare Rainbow",
				"rare_shining": "Rare Shining",
				"rare_shiny_gx": "Rare Shiny GX",
				"rgb_rare": "RGB Rare",
				"secret_rare": "Secret Rare",
				"shiny_rare": "Shiny Rare",
				"shiny_rare_v": "Shiny Rare V",
				"shiny_rare_vmax": "Shiny Rare VMax",
				"shiny_ultra_rare": "Shiny Ultra Rare",
				"special_illustration_rare": "Special Illustration Rare",
				"three_diamond": "Three Diamond",
				"three_star": "Three Star",
				"trainer_gallery_rare_holo": "Trainer Gallery Rare Holo",
				"two_diamond": "Two Diamond",
				"two_shiny": "Two Shiny",
				"two_star": "Two Star",
				"ultra_rare": "Ultra Rare",
				"uncommon": "Uncommon"
			}
		},
		"variant": {
			"description": "The variant of the card",
			"name": "Variant",
			"values": {
				"first_edition": "1st Edition",
				"master_ball": "Master Ball Pattern",
				"poke_ball": "Poke Ball Pattern",
				"unlimited": "Unlimited",
				"friend_ball": "Friend Ball Pattern",
				"love_ball": "Love Ball Pattern",
				"dusk_ball": "Dusk Ball Pattern",
				"quick_ball": "Quick Ball Pattern",
				"team_rocket": "Team Rocket",
				"energy_symbol": "Energy Symbol Pattern",
				"holiday_calendar": "Holiday Calendar"
			}
		},
		"hp": {
			"name": "HP",
			"description": "Health points of the Pokemon"
		},
		"stage": {
			"name": "Stage",
			"description": "Evolution stage of the Pokemon",
			"values": {
				"baby": "Baby",
				"basic": "Basic",
				"break": "BREAK",
				"gx": "GX",
				"level_up": "LEVEL-UP",
				"mega": "MEGA",
				"restored": "RESTORED",
				"stage_1": "Stage 1",
				"stage_2": "Stage 2",
				"v": "V",
				"v_union": "V-UNION",
				"vmax": "VMAX",
				"vstar": "VSTAR"
			}
		},
		"retreatCost": {
			"name": "Retreat Cost",
			"description": "Energy cost to retreat the Pokemon"
		},
		"level": {
			"name": "Level",
			"description": "Level of the Pokemon"
		},
		"evolvesFrom": {
			"name": "Evolves From",
			"description": "The Pokemon this card evolves from"
		},
		"cardText": {
			"name": "Card Text",
			"description": "The abilities and attacks text of the card"
		}
	},
	name: "Pokemon Trading Card Game",
	presets: {
		"foil": {
			"description": "A complete set with one copy of each card in holo foil finish",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in holo foil finish",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "A complete set with one copy of each card in standard, holo foil, and reverse holo finishes",
			"name": "Master Set"
		},
		"playset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish",
			"name": "Playset"
		},
		"standard": {
			"description": "A complete set with one copy of each card in standard finish",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/pokemon/i18n/fr.json
var fr_default$8 = {
	attributes: {
		"cardType": {
			"description": "Type de la carte",
			"name": "Type de carte",
			"values": {
				"energy": "Énergie",
				"pokemon": "Pokémon",
				"trainer": "Dresseur"
			}
		},
		"pokemonTypes": {
			"description": "Type de Pokémon",
			"name": "Type du Pokémon",
			"values": {
				"colorless": "Normal",
				"dark": "Obscurité",
				"dragon": "Dragon",
				"electric": "Électrique",
				"fairy": "Fée",
				"fighting": "Combat",
				"fire": "Feu",
				"grass": "Plante",
				"psychic": "Psy",
				"steel": "Métal",
				"water": "Eau"
			}
		},
		"rarity": {
			"description": "La rareté de la carte",
			"name": "Rareté",
			"values": {
				"ace_spec_rare": "ACE SPEC Rare",
				"amazing_rare": "Amazing Rare",
				"black_white_rare": "Black White Rare",
				"classic": "Classic",
				"classic_collection": "Classic Collection",
				"common": "Common",
				"crown": "Crown",
				"double_rare": "Double Rare",
				"four_diamond": "Four Diamond",
				"full_art_trainer": "Full Art Trainer",
				"futuristic_rare": "Futuriste Rare",
				"holo_rare": "Rare Holo",
				"holo_rare_v": "Rare Holo V",
				"holo_rare_vmax": "Rare Holo VMax",
				"holo_rare_vstar": "Rare Holo VStar",
				"hyper_rare": "Hyper Rare",
				"illustration_rare": "Illustration Rare",
				"legend": "Legend",
				"mega_attack_rare": "Méga Attaque Rare",
				"none": "None",
				"one_diamond": "One Diamond",
				"one_shiny": "One Shiny",
				"one_star": "One Star",
				"pikachu_rare": "Pikachu Rare",
				"promo": "Promo",
				"radiant_rare": "Radiant Rare",
				"rare": "Rare",
				"rare_break": "Rare BREAK",
				"rare_holo": "Rare Holo",
				"rare_holo_ex": "Rare Holo EX",
				"rare_holo_gx": "Rare Holo GX",
				"rare_holo_lv_x": "Rare Holo Lv.X",
				"rare_holo_star": "Rare Holo Star",
				"rare_prime": "Rare Prime",
				"rare_prism_star": "Rare Prism Star",
				"rare_rainbow": "Arc-en-ciel Rare",
				"rare_shining": "Rare Shining",
				"rare_shiny_gx": "Rare Shiny GX",
				"rgb_rare": "RGB Rare",
				"secret_rare": "Secret Rare",
				"shiny_rare": "Shiny Rare",
				"shiny_rare_v": "Shiny Rare V",
				"shiny_rare_vmax": "Shiny Rare VMax",
				"shiny_ultra_rare": "Shiny Ultra Rare",
				"special_illustration_rare": "Special Illustration Rare",
				"three_diamond": "Three Diamond",
				"three_star": "Three Star",
				"trainer_gallery_rare_holo": "Trainer Gallery Rare Holo",
				"two_diamond": "Two Diamond",
				"two_shiny": "Two Shiny",
				"two_star": "Two Star",
				"ultra_rare": "Ultra Rare",
				"uncommon": "Uncommon"
			}
		},
		"variant": {
			"description": "La variante de la carte",
			"name": "Variante",
			"values": {
				"first_edition": "1st Edition",
				"master_ball": "Master Ball Pattern",
				"poke_ball": "Poke Ball Pattern",
				"unlimited": "Unlimited",
				"friend_ball": "Friend Ball Pattern",
				"love_ball": "Love Ball Pattern",
				"dusk_ball": "Dusk Ball Pattern",
				"quick_ball": "Quick Ball Pattern",
				"team_rocket": "Team Rocket",
				"energy_symbol": "Energy Symbol Pattern",
				"holiday_calendar": "Calendrier des fêtes"
			}
		},
		"hp": {
			"name": "PV",
			"description": "Points de vie du Pokémon"
		},
		"stage": {
			"name": "Stade",
			"description": "Stade d'évolution du Pokémon",
			"values": {
				"baby": "Bébé",
				"basic": "Base",
				"break": "BREAK",
				"gx": "GX",
				"level_up": "NIVEAU-UP",
				"mega": "MEGA",
				"restored": "RESTAURÉ",
				"stage_1": "Niveau 1",
				"stage_2": "Niveau 2",
				"v": "V",
				"v_union": "V-UNION",
				"vmax": "VMAX",
				"vstar": "VSTAR"
			}
		},
		"retreatCost": {
			"name": "Coût de retraite",
			"description": "Coût en énergie pour battre en retraite"
		},
		"level": {
			"name": "Niveau",
			"description": "Niveau du Pokémon"
		},
		"evolvesFrom": {
			"name": "Évolue de",
			"description": "Le Pokémon dont cette carte évolue"
		},
		"cardText": {
			"name": "Texte de carte",
			"description": "Le texte des capacités et attaques de la carte"
		}
	},
	name: "Jeu de Cartes à Collectionner Pokémon",
	presets: {
		"foil": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition holo foil",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition holo foil",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard, holo foil et reverse holo",
			"name": "Master Set"
		},
		"playset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard",
			"name": "Playset"
		},
		"standard": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/pokemon-japan/i18n/en.json
var en_default$7 = {
	attributes: {
		"cardType": {
			"description": "Type of the card",
			"name": "Card Type",
			"values": {
				"energy": "Energy",
				"pokemon": "Pokemon",
				"trainer": "Trainer"
			}
		},
		"pokemonTypes": {
			"description": "Type of the Pokemon",
			"name": "Pokemon Type",
			"values": {
				"colorless": "Colorless",
				"darkness": "Darkness",
				"dragon": "Dragon",
				"fairy": "Fairy",
				"fighting": "Fighting",
				"fire": "Fire",
				"grass": "Grass",
				"lightning": "Lightning",
				"metal": "Metal",
				"psychic": "Psychic",
				"water": "Water"
			}
		},
		"rarity": {
			"description": "The rarity of the card",
			"name": "Rarity",
			"values": {
				"ace_rare": "ACE Rare",
				"amazing_rare": "Amazing Rare",
				"art_rare": "Art Rare",
				"black_white_rare": "Black White Rare",
				"character_rare": "Character Rare",
				"character_super_rare": "Character Super Rare",
				"common": "Common",
				"common_holo": "Common Holo",
				"double_rare": "Double Rare",
				"holo_rare": "Holo Rare",
				"holofoil": "Holofoil",
				"hyper_rare": "Hyper Rare",
				"kagayaku": "Kagayaku",
				"mega_attack_rare": "Mega Attack Rare",
				"mega_ultra_rare": "Mega Ultra Rare",
				"none": "None",
				"normal": "Normal",
				"prism_rare": "Prism Rare",
				"promo": "Promo",
				"rare": "Rare",
				"rare_holo_legend": "Rare Holo LEGEND",
				"rare_holo_lv_x": "Rare Holo LV.X",
				"shining": "Shining",
				"shiny_rare": "Shiny Rare",
				"shiny_secret_rare": "Shiny Secret Rare",
				"special_art_rare": "Special Art Rare",
				"super_rare": "Super Rare",
				"super_rare_holo": "Super Rare Holo",
				"trainer_rare": "Trainer Rare",
				"triple_rare": "Triple Rare",
				"ultra_rare": "Ultra Rare",
				"ultra_rare_common": "Ultra-Rare Common",
				"ultra_rare_uncommon": "Ultra-Rare Uncommon",
				"uncommon": "Uncommon"
			}
		},
		"variant": {
			"description": "The variant of the card",
			"name": "Variant",
			"values": {
				"first_edition": "1st Edition",
				"unlimited": "Unlimited"
			}
		},
		"hp": {
			"name": "HP",
			"description": "Health points of the Pokemon"
		},
		"stage": {
			"name": "Stage",
			"description": "Evolution stage of the Pokemon",
			"values": {
				"baby": "Baby",
				"basic": "Basic",
				"break": "BREAK",
				"fossil": "Fossil",
				"legend": "LEGEND",
				"lv_x": "LV.X",
				"mega_ex": "Mega EX",
				"primal_ex": "Primal EX",
				"restored": "Restored",
				"stage_1": "Stage 1",
				"stage_2": "Stage 2",
				"v_union": "V-UNION",
				"vmax": "VMAX",
				"vmaxg": "VMAX Gigantamax",
				"vstar": "VSTAR"
			}
		},
		"retreatCost": {
			"name": "Retreat Cost",
			"description": "Energy cost to retreat the Pokemon"
		}
	},
	name: "Pokemon Japan Trading Card Game",
	presets: {
		"holofoil": {
			"description": "A complete set with one copy of each card in holofoil finish",
			"name": "Holofoil Set"
		},
		"masterset": {
			"description": "A complete set with one copy of each card in normal and holofoil finishes",
			"name": "Master Set"
		},
		"standard": {
			"description": "A complete set with one copy of each card in normal finish",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/pokemon-japan/i18n/fr.json
var fr_default$7 = {
	attributes: {
		"cardType": {
			"description": "Type de la carte",
			"name": "Type de carte",
			"values": {
				"energy": "Énergie",
				"pokemon": "Pokémon",
				"trainer": "Dresseur"
			}
		},
		"pokemonTypes": {
			"description": "Type du Pokémon",
			"name": "Type de Pokémon",
			"values": {
				"colorless": "Incolore",
				"darkness": "Obscurité",
				"dragon": "Dragon",
				"fairy": "Fée",
				"fighting": "Combat",
				"fire": "Feu",
				"grass": "Plante",
				"lightning": "Électrik",
				"metal": "Métal",
				"psychic": "Psy",
				"water": "Eau"
			}
		},
		"rarity": {
			"description": "La rareté de la carte",
			"name": "Rareté",
			"values": {
				"ace_rare": "ACE Rare",
				"amazing_rare": "Amazing Rare",
				"art_rare": "Art Rare",
				"black_white_rare": "Black White Rare",
				"character_rare": "Character Rare",
				"character_super_rare": "Character Super Rare",
				"common": "Commune",
				"common_holo": "Commune Holo",
				"double_rare": "Double Rare",
				"holo_rare": "Holo Rare",
				"holofoil": "Holofoil",
				"hyper_rare": "Hyper Rare",
				"kagayaku": "Kagayaku",
				"mega_attack_rare": "Mega Attack Rare",
				"mega_ultra_rare": "Mega Ultra Rare",
				"none": "Aucune",
				"normal": "Normale",
				"prism_rare": "Prism Rare",
				"promo": "Promo",
				"rare": "Rare",
				"rare_holo_legend": "Rare Holo LEGEND",
				"rare_holo_lv_x": "Rare Holo LV.X",
				"shining": "Shining",
				"shiny_rare": "Shiny Rare",
				"shiny_secret_rare": "Shiny Secret Rare",
				"special_art_rare": "Special Art Rare",
				"super_rare": "Super Rare",
				"super_rare_holo": "Super Rare Holo",
				"trainer_rare": "Trainer Rare",
				"triple_rare": "Triple Rare",
				"ultra_rare": "Ultra Rare",
				"ultra_rare_common": "Ultra-Rare Commune",
				"ultra_rare_uncommon": "Ultra-Rare Peu commune",
				"uncommon": "Peu commune"
			}
		},
		"variant": {
			"description": "La variante de la carte",
			"name": "Variante",
			"values": {
				"first_edition": "1ère Édition",
				"unlimited": "Illimité"
			}
		},
		"hp": {
			"name": "PV",
			"description": "Points de vie du Pokémon"
		},
		"stage": {
			"name": "Niveau",
			"description": "Niveau d'évolution du Pokémon",
			"values": {
				"baby": "Bébé",
				"basic": "Base",
				"break": "BREAK",
				"fossil": "Fossile",
				"legend": "LÉGENDE",
				"lv_x": "NIV.X",
				"mega_ex": "Méga EX",
				"primal_ex": "Primo EX",
				"restored": "Restauré",
				"stage_1": "Niveau 1",
				"stage_2": "Niveau 2",
				"v_union": "V-UNION",
				"vmax": "VMAX",
				"vmaxg": "VMAX Gigamax",
				"vstar": "VSTAR"
			}
		},
		"retreatCost": {
			"name": "Coût de retraite",
			"description": "Coût en énergie pour battre en retraite"
		}
	},
	name: "Pokémon Japan Jeu de Cartes à Collectionner",
	presets: {
		"holofoil": {
			"description": "Un set complet avec une copie de chaque carte en finition holofoil",
			"name": "Set Holofoil"
		},
		"masterset": {
			"description": "Un set complet avec une copie de chaque carte en finition normale et holofoil",
			"name": "Master Set"
		},
		"standard": {
			"description": "Un set complet avec une copie de chaque carte en finition normale",
			"name": "Set Standard"
		}
	}
};

//#endregion
//#region src/lib/games/riftbound/i18n/en.json
var en_default$6 = {
	name: "Riftbound: League of Legends TCG",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"epic": "Epic",
				"showcase": "Showcase"
			}
		},
		"riotId": {
			"name": "Riot ID",
			"description": "The unique Riot identifier for the card"
		},
		"cardType": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"unit": "Unit",
				"spell": "Spell",
				"gear": "Gear",
				"rune": "Rune",
				"battlefield": "Battlefield",
				"legend": "Legend",
				"card": "Card"
			}
		},
		"superType": {
			"name": "Super Type",
			"description": "The super type of the card",
			"values": {
				"basic": "Basic",
				"champion": "Champion",
				"signature": "Signature",
				"token": "Token",
				"unit": "Unit"
			}
		},
		"domains": {
			"name": "Domains",
			"description": "The domains of the card",
			"values": {
				"fury": "Fury",
				"calm": "Calm",
				"chaos": "Chaos",
				"mind": "Mind",
				"body": "Body",
				"order": "Order",
				"neutral": "Neutral"
			}
		},
		"cardTags": {
			"name": "Tags",
			"description": "Tags and champions associated with the card",
			"values": {
				"ahri": "Ahri",
				"akali": "Akali",
				"akshan": "Akshan",
				"ambessa": "Ambessa",
				"anivia": "Anivia",
				"annie": "Annie",
				"aphelios": "Aphelios",
				"azir": "Azir",
				"bandlecity": "Bandle City",
				"bard": "Bard",
				"bilgewater": "Bilgewater",
				"bird": "Bird",
				"blitzcrank": "Blitzcrank",
				"caitlyn": "Caitlyn",
				"cat": "Cat",
				"darius": "Darius",
				"demacia": "Demacia",
				"demon": "Demon",
				"diana": "Diana",
				"dog": "Dog",
				"dragon": "Dragon",
				"draven": "Draven",
				"drmundo": "Dr. Mundo",
				"ekko": "Ekko",
				"elite": "Elite",
				"equipment": "Equipment",
				"ezreal": "Ezreal",
				"fae": "Fae",
				"fiora": "Fiora",
				"fizz": "Fizz",
				"freljord": "Freljord",
				"gangplank": "Gangplank",
				"garen": "Garen",
				"heimerdinger": "Heimerdinger",
				"icathia": "Icathia",
				"illaoi": "Illaoi",
				"ionia": "Ionia",
				"irelia": "Irelia",
				"ixtal": "Ixtal",
				"janna": "Janna",
				"jax": "Jax",
				"jayce": "Jayce",
				"jinx": "Jinx",
				"kaisa": "Kai'Sa",
				"karma": "Karma",
				"karthus": "Karthus",
				"kayle": "Kayle",
				"kayn": "Kayn",
				"kennen": "Kennen",
				"khazix": "Kha'Zix",
				"kogmaw": "Kog'Maw",
				"leesin": "Lee Sin",
				"leona": "Leona",
				"lucian": "Lucian",
				"lux": "Lux",
				"malzahar": "Malzahar",
				"masteryi": "Master Yi",
				"mech": "Mech",
				"mel": "Mel",
				"missfortune": "Miss Fortune",
				"morgana": "Morgana",
				"mounttargon": "Mount Targon",
				"mystic": "Mystic",
				"nasus": "Nasus",
				"ninja": "Ninja",
				"nocturne": "Nocturne",
				"noxus": "Noxus",
				"ornn": "Ornn",
				"piltover": "Piltover",
				"pirate": "Pirate",
				"poro": "Poro",
				"qiyana": "Qiyana",
				"recruit": "Recruit",
				"reksai": "Rek'Sai",
				"renataGlasc": "Renata Glasc",
				"renekton": "Renekton",
				"rengar": "Rengar",
				"riven": "Riven",
				"rumble": "Rumble",
				"sentinel": "Sentinel",
				"sett": "Sett",
				"shadowisles": "Shadow Isles",
				"shen": "Shen",
				"shurima": "Shurima",
				"sivir": "Sivir",
				"soldier": "Soldier",
				"sona": "Sona",
				"soraka": "Soraka",
				"spider": "Spider",
				"spirit": "Spirit",
				"swain": "Swain",
				"taric": "Taric",
				"teemo": "Teemo",
				"thevoid": "The Void",
				"trifarian": "Trifarian",
				"tryndamere": "Tryndamere",
				"twistedfate": "Twisted Fate",
				"udyr": "Udyr",
				"vastaya": "Vastaya",
				"vayne": "Vayne",
				"vex": "Vex",
				"vi": "Vi",
				"viktor": "Viktor",
				"volibear": "Volibear",
				"warrior": "Warrior",
				"warwick": "Warwick",
				"wizard": "Wizard",
				"xinzhao": "Xin Zhao",
				"yasuo": "Yasuo",
				"yone": "Yone",
				"yordle": "Yordle",
				"zaun": "Zaun",
				"zed": "Zed"
			}
		},
		"orientation": {
			"name": "Orientation",
			"description": "The orientation of the card",
			"values": {
				"portrait": "Portrait",
				"landscape": "Landscape"
			}
		},
		"energyCost": {
			"name": "Energy Cost",
			"description": "Energy cost to play the card"
		},
		"mightCost": {
			"name": "Might Cost",
			"description": "Might cost of the unit"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "Card rules and abilities text"
		},
		"finish": {
			"name": "Finish",
			"description": "Finish of the card",
			"values": {
				"Standard": "Standard",
				"Foil": "Foil"
			}
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in standard finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Foil Set",
			"description": "A complete set with one copy of each card in foil finish"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in all finishes and variants"
		}
	}
};

//#endregion
//#region src/lib/games/riftbound/i18n/fr.json
var fr_default$6 = {
	name: "Riftbound: League of Legends TCG",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"epic": "Épique",
				"showcase": "Showcase"
			}
		},
		"riotId": {
			"name": "ID Riot",
			"description": "L'identifiant Riot unique de la carte"
		},
		"cardType": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"unit": "Unité",
				"spell": "Sort",
				"gear": "Équipement",
				"rune": "Rune",
				"battlefield": "Champ de bataille",
				"legend": "Légende",
				"card": "Carte"
			}
		},
		"superType": {
			"name": "Super Type",
			"description": "Le super type de la carte",
			"values": {
				"basic": "Basique",
				"champion": "Champion",
				"signature": "Signature",
				"token": "Jeton",
				"unit": "Unité"
			}
		},
		"domains": {
			"name": "Domaines",
			"description": "Les domaines de la carte",
			"values": {
				"fury": "Furie",
				"calm": "Calme",
				"chaos": "Chaos",
				"mind": "Esprit",
				"body": "Corps",
				"order": "Ordre",
				"neutral": "Neutre"
			}
		},
		"cardTags": {
			"name": "Tags",
			"description": "Tags et champions associés à la carte",
			"values": {
				"ahri": "Ahri",
				"akali": "Akali",
				"akshan": "Akshan",
				"ambessa": "Ambessa",
				"anivia": "Anivia",
				"annie": "Annie",
				"aphelios": "Aphelios",
				"azir": "Azir",
				"bandlecity": "Bandle",
				"bard": "Bard",
				"bilgewater": "Bilgewater",
				"bird": "Oiseau",
				"blitzcrank": "Blitzcrank",
				"caitlyn": "Caitlyn",
				"cat": "Chat",
				"darius": "Darius",
				"demacia": "Demacia",
				"demon": "Démon",
				"diana": "Diana",
				"dog": "Chien",
				"dragon": "Dragon",
				"draven": "Draven",
				"drmundo": "Dr. Mundo",
				"ekko": "Ekko",
				"elite": "Élite",
				"equipment": "Équipement",
				"ezreal": "Ezreal",
				"fae": "Fée",
				"fiora": "Fiora",
				"fizz": "Fizz",
				"freljord": "Freljord",
				"gangplank": "Gangplank",
				"garen": "Garen",
				"heimerdinger": "Heimerdinger",
				"icathia": "Icathia",
				"illaoi": "Illaoi",
				"ionia": "Ionia",
				"irelia": "Irelia",
				"ixtal": "Ixtal",
				"janna": "Janna",
				"jax": "Jax",
				"jayce": "Jayce",
				"jinx": "Jinx",
				"kaisa": "Kai'Sa",
				"karma": "Karma",
				"karthus": "Karthus",
				"kayle": "Kayle",
				"kayn": "Kayn",
				"kennen": "Kennen",
				"khazix": "Kha'Zix",
				"kogmaw": "Kog'Maw",
				"leesin": "Lee Sin",
				"leona": "Leona",
				"lucian": "Lucian",
				"lux": "Lux",
				"malzahar": "Malzahar",
				"masteryi": "Master Yi",
				"mech": "Méca",
				"mel": "Mel",
				"missfortune": "Miss Fortune",
				"morgana": "Morgana",
				"mounttargon": "Mont Targon",
				"mystic": "Mystique",
				"nasus": "Nasus",
				"ninja": "Ninja",
				"nocturne": "Nocturne",
				"noxus": "Noxus",
				"ornn": "Ornn",
				"piltover": "Piltover",
				"pirate": "Pirate",
				"poro": "Poro",
				"qiyana": "Qiyana",
				"recruit": "Recrue",
				"reksai": "Rek'Sai",
				"renataGlasc": "Renata Glasc",
				"renekton": "Renekton",
				"rengar": "Rengar",
				"riven": "Riven",
				"rumble": "Rumble",
				"sentinel": "Sentinelle",
				"sett": "Sett",
				"shadowisles": "Îles Obscures",
				"shen": "Shen",
				"shurima": "Shurima",
				"sivir": "Sivir",
				"soldier": "Soldat",
				"sona": "Sona",
				"soraka": "Soraka",
				"spider": "Araignée",
				"spirit": "Esprit",
				"swain": "Swain",
				"taric": "Taric",
				"teemo": "Teemo",
				"thevoid": "Le Néant",
				"trifarian": "Trifarian",
				"tryndamere": "Tryndamere",
				"twistedfate": "Twisted Fate",
				"udyr": "Udyr",
				"vastaya": "Vastaya",
				"vayne": "Vayne",
				"vex": "Vex",
				"vi": "Vi",
				"viktor": "Viktor",
				"volibear": "Volibear",
				"warrior": "Guerrier",
				"warwick": "Warwick",
				"wizard": "Magicien",
				"xinzhao": "Xin Zhao",
				"yasuo": "Yasuo",
				"yone": "Yone",
				"yordle": "Yordle",
				"zaun": "Zaun",
				"zed": "Zed"
			}
		},
		"orientation": {
			"name": "Orientation",
			"description": "L'orientation de la carte",
			"values": {
				"portrait": "Portrait",
				"landscape": "Paysage"
			}
		},
		"energyCost": {
			"name": "Coût d'énergie",
			"description": "Coût d'énergie pour jouer la carte"
		},
		"mightCost": {
			"name": "Coût de puissance",
			"description": "Coût de puissance de l'unité"
		},
		"rulesText": {
			"name": "Texte de règles",
			"description": "Texte de règles et capacités de la carte"
		},
		"finish": {
			"name": "Finition",
			"description": "Finition de la carte",
			"values": {
				"Standard": "Standard",
				"Foil": "Brillante"
			}
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "Une extension complète avec une copie de chaque carte en finition standard"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Foil Set",
			"description": "Une extension complète avec une copie de chaque carte en finition foil"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Une extension complète avec une copie de chaque carte dans toutes les finitions et variantes"
		}
	}
};

//#endregion
//#region src/lib/games/rise/i18n/en.json
var en_default$5 = {
	name: "Rise",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"legendary": "Legendary"
			}
		},
		"variant": {
			"name": "Variant",
			"description": "Special edition or variant of the card",
			"values": {
				"standard": "Standard",
				"blackBorder": "Black Border",
				"boxTopper": "Box Topper",
				"collector": "Collector",
				"collectorGold": "Collector Gold",
				"collectorPink": "Collector Pink",
				"collectorSilver": "Collector Silver",
				"collectorSerialized": "Collector Serialized",
				"fullArt": "Full Art",
				"gamma": "Gamma",
				"goldenTicket": "GoldenTicket",
				"oilCard": "Oil Card",
				"poscaHandDrawn": "POSCA Hand Drawn Card",
				"pokerCard": "Poker Card",
				"sketchStamp": "Sketch stamp",
				"testLenticular": "Test Lenticular Card"
			}
		},
		"finish": { "values": {
			"standard": "Standard",
			"foil": "Foil",
			"blastFoilRainbow": "Blast Foil Rainbow",
			"bubblesFoil": "Bubbles Foil",
			"crackedIceFoil": "Cracked Ice Foil",
			"galaxyFoil": "Galaxy Foil",
			"glitterFoil": "Glitter Foil",
			"goldFoil": "Gold Foil",
			"multiSidelineFoil": "Multi Sideline Foil",
			"rainbowSolidTextureStamp": "Rainbow Solid Texture Stamp",
			"starFoil": "Star Foil",
			"surgeFoil": "Surge Foil",
			"fullArtGoldSign": "Full Art Gold Sign",
			"depthLenticular": "Depth Lenticular",
			"flipLenticular": "Flip Lenticular",
			"lenticular": "Lenticular"
		} },
		"type": {
			"name": "Type",
			"description": "The type of the card",
			"values": {
				"creature": "Creature",
				"incantation": "Incantation",
				"imminent": "Imminent",
				"summoner": "Summoner",
				"door": "Door",
				"levelUp": "Level Up",
				"dungeon": "Dungeon"
			}
		},
		"rank": {
			"name": "Rank",
			"description": "The rank of the card",
			"values": {
				"none": "None",
				"rank1": "Rank 1",
				"rank2": "Rank 2",
				"rank3": "Rank 3",
				"rank4": "Rank 4",
				"rankInfinite": "Rank Infinite"
			}
		},
		"subtypes": {
			"name": "Subtypes",
			"description": "Creature subtypes",
			"values": {
				"angel": "Angel",
				"demon": "Demon",
				"dragon": "Dragon",
				"fairy": "Fairy",
				"spirit": "Spirit",
				"zombie": "Zombie",
				"skeleton": "Skeleton",
				"cat": "Cat",
				"bat": "Bat",
				"knight": "Knight",
				"clone": "Clone",
				"divinity": "Divinity",
				"monster": "Monster",
				"food": "Food",
				"witch": "Witch",
				"insect": "Insect",
				"keyper": "Keyper",
				"plant": "Plant",
				"unicorn": "Unicorn",
				"robot": "Robot",
				"unknown": "Unknown",
				"assassin": "Assassin",
				"chief": "Chief",
				"dog": "Dog",
				"dwarf": "Dwarf",
				"golem": "Golem",
				"lich": "Lich",
				"octopus": "Octopus",
				"slime": "Slime",
				"spider": "Spider",
				"undead": "Undead",
				"vampyr": "Vampyr",
				"worm": "Worm"
			}
		},
		"abilities": {
			"name": "Abilities",
			"description": "Keyword abilities",
			"values": {
				"flying": "Flying",
				"reach": "Reach",
				"pierce": "Pierce",
				"fightback": "Fightback",
				"immunity": "Immunity",
				"persistence": "Persistence",
				"wallPass": "Wall-pass",
				"reanimate": "Reanimate",
				"root": "Root",
				"horde": "Horde",
				"symbiosis": "Symbiosis",
				"morpher": "Morpher",
				"together": "Together",
				"equipment": "Equipment",
				"disease": "Disease",
				"candy": "Candy",
				"fixedPrice": "Fixed Price",
				"multiTarget": "Multi-target",
				"range": "Range",
				"water": "Water",
				"fire": "Fire",
				"electric": "Electric",
				"ice": "Ice",
				"poison": "Poison",
				"assimilation": "Assimilation",
				"explosion": "Explosion",
				"advantage": "Advantage",
				"cloning": "Cloning",
				"ironSkin": "Iron Skin",
				"drain": "Drain",
				"dodge": "Dodge",
				"possession": "Possession",
				"bleeding": "Bleeding",
				"haste": "Haste",
				"riposte": "Riposte",
				"set": "Set",
				"amok": "Amok",
				"diversion": "Diversion",
				"exhumation": "Exhumation",
				"chained": "Chained",
				"bomb": "Bomb",
				"adept": "Adept",
				"parasite": "Parasite",
				"allergy": "Allergy",
				"imitator": "Imitator",
				"lock": "Lock",
				"madness": "Madness",
				"paranoia": "Paranoia",
				"paralysis": "Paralysis",
				"schizophrenia": "Schizophrenia",
				"cooling": "Cooling",
				"cold": "Cold",
				"constancy": "Constancy",
				"copycat": "Copycat",
				"distance": "Distance",
				"earth": "Earth",
				"fixedTime": "Fixed Time",
				"shadow": "Shadow",
				"splash": "Splash",
				"trauma": "Trauma",
				"velocity": "Velocity",
				"web": "Web",
				"food": "Food"
			}
		},
		"attack": {
			"name": "Attack",
			"description": "Attack power of the card"
		},
		"defense": {
			"name": "Defense",
			"description": "Defense power of the card"
		},
		"plane": {
			"name": "Plane",
			"description": "The plane where the card exists",
			"values": {
				"ground": "Ground",
				"aerial": "Aerial"
			}
		},
		"life": {
			"name": "Life",
			"description": "Starting life points"
		},
		"maxLevel": {
			"name": "Max Level",
			"description": "Maximum level of the card"
		},
		"rulesText": {
			"name": "Rules Text",
			"description": "Card rules and abilities text"
		},
		"flavorText": {
			"name": "Flavor Text",
			"description": "Flavor text on the card"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in standard finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Foil Set",
			"description": "A complete set with one copy of each card in foil finish"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in all finishes and variants"
		}
	}
};

//#endregion
//#region src/lib/games/rise/i18n/fr.json
var fr_default$5 = {
	name: "Rise",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"uncommon": "Peu Commune",
				"rare": "Rare",
				"legendary": "Légendaire"
			}
		},
		"variant": {
			"name": "Variante",
			"description": "Édition spéciale ou variante de la carte",
			"values": {
				"standard": "Standard",
				"blackBorder": "Black Border",
				"boxTopper": "Box Topper",
				"collector": "Collector",
				"collectorGold": "Collector Gold",
				"collectorPink": "Collector Pink",
				"collectorSilver": "Collector Silver",
				"collectorSerialized": "Collector Numéroté",
				"fullArt": "Full Art",
				"gamma": "Gamma",
				"goldenTicket": "GoldenTicket",
				"oilCard": "Oil Card",
				"poscaHandDrawn": "POSCA Hand Drawn Card",
				"pokerCard": "Poker Card",
				"sketchStamp": "Sketch stamp",
				"testLenticular": "Test Lenticular Card"
			}
		},
		"finish": { "values": {
			"standard": "Standard",
			"foil": "Foil",
			"blastFoilRainbow": "Blast Foil Rainbow",
			"bubblesFoil": "Bubbles Foil",
			"crackedIceFoil": "Cracked Ice Foil",
			"galaxyFoil": "Galaxy Foil",
			"glitterFoil": "Glitter Foil",
			"goldFoil": "Gold Foil",
			"multiSidelineFoil": "Multi Sideline Foil",
			"rainbowSolidTextureStamp": "Rainbow Solid Texture Stamp",
			"starFoil": "Star Foil",
			"surgeFoil": "Surge Foil",
			"fullArtGoldSign": "Full Art Gold Sign",
			"depthLenticular": "Depth Lenticular",
			"flipLenticular": "Flip Lenticular",
			"lenticular": "Lenticular"
		} },
		"type": {
			"name": "Type",
			"description": "Le type de la carte",
			"values": {
				"creature": "Créature",
				"incantation": "Incantation",
				"imminent": "Imminent",
				"summoner": "Invocateur",
				"door": "Porte",
				"levelUp": "Niveau Supérieur",
				"dungeon": "Donjon"
			}
		},
		"rank": {
			"name": "Rang",
			"description": "Le rang de la carte",
			"values": {
				"none": "Aucun",
				"rank1": "Rang 1",
				"rank2": "Rang 2",
				"rank3": "Rang 3",
				"rank4": "Rang 4",
				"rankInfinite": "Rang Infini"
			}
		},
		"subtypes": {
			"name": "Sous-types",
			"description": "Sous-types de créature",
			"values": {
				"angel": "Ange",
				"demon": "Démon",
				"dragon": "Dragon",
				"fairy": "Fée",
				"spirit": "Esprit",
				"zombie": "Zombie",
				"skeleton": "Squelette",
				"cat": "Chat",
				"bat": "Chauve-souris",
				"knight": "Chevalier",
				"clone": "Clone",
				"divinity": "Divinité",
				"monster": "Monstre",
				"food": "Nourriture",
				"witch": "Sorcière",
				"insect": "Insecte",
				"keyper": "Gardien",
				"plant": "Plante",
				"unicorn": "Licorne",
				"robot": "Robot",
				"unknown": "Inconnu",
				"assassin": "Assassin",
				"chief": "Chef",
				"dog": "Chien",
				"dwarf": "Nain",
				"golem": "Golem",
				"lich": "Liche",
				"octopus": "Pieuvre",
				"slime": "Slime",
				"spider": "Araignée",
				"undead": "Mort-vivant",
				"vampyr": "Vampyr",
				"worm": "Ver"
			}
		},
		"abilities": {
			"name": "Capacités",
			"description": "Capacités mot-clé",
			"values": {
				"flying": "Vol",
				"reach": "Portée",
				"pierce": "Perçant",
				"fightback": "Riposte",
				"immunity": "Immunité",
				"persistence": "Persistance",
				"wallPass": "Passe-muraille",
				"reanimate": "Réanimation",
				"root": "Enraciné",
				"horde": "Horde",
				"symbiosis": "Symbiose",
				"morpher": "Métamorphe",
				"together": "Ensemble",
				"equipment": "Équipement",
				"disease": "Maladie",
				"candy": "Bonbon",
				"fixedPrice": "Prix Fixe",
				"multiTarget": "Multi-cible",
				"range": "Portée",
				"water": "Eau",
				"fire": "Feu",
				"electric": "Électrique",
				"ice": "Glace",
				"poison": "Poison",
				"assimilation": "Assimilation",
				"explosion": "Explosion",
				"advantage": "Avantage",
				"cloning": "Clonage",
				"ironSkin": "Peau de Fer",
				"drain": "Drain",
				"dodge": "Esquive",
				"possession": "Possession",
				"bleeding": "Saignement",
				"haste": "Célérité",
				"riposte": "Riposte",
				"set": "Ensemble",
				"amok": "Amok",
				"diversion": "Diversion",
				"exhumation": "Exhumation",
				"chained": "Enchaîné",
				"bomb": "Bombe",
				"adept": "Adepte",
				"parasite": "Parasite",
				"allergy": "Allergie",
				"imitator": "Imitateur",
				"lock": "Verrouillage",
				"madness": "Folie",
				"paranoia": "Paranoïa",
				"paralysis": "Paralysie",
				"schizophrenia": "Schizophrénie",
				"cooling": "Refroidissement",
				"cold": "Froid",
				"constancy": "Constance",
				"copycat": "Copieur",
				"distance": "Distance",
				"earth": "Terre",
				"fixedTime": "Temps fixe",
				"shadow": "Ombre",
				"splash": "Éclaboussure",
				"trauma": "Trauma",
				"velocity": "Vélocité",
				"web": "Entoilement",
				"food": "Nourriture"
			}
		},
		"attack": {
			"name": "Attaque",
			"description": "Puissance d'attaque de la carte"
		},
		"defense": {
			"name": "Défense",
			"description": "Puissance de défense de la carte"
		},
		"plane": {
			"name": "Plan",
			"description": "Le plan où existe la carte",
			"values": {
				"ground": "Sol",
				"aerial": "Aérien"
			}
		},
		"life": {
			"name": "Vie",
			"description": "Points de vie de départ"
		},
		"maxLevel": {
			"name": "Niveau Maximum",
			"description": "Niveau maximum de la carte"
		},
		"rulesText": {
			"name": "Texte de Règles",
			"description": "Règles et capacités de la carte"
		},
		"flavorText": {
			"name": "Texte d'Ambiance",
			"description": "Texte d'ambiance sur la carte"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "Une extension complète avec une copie de chaque carte en finition standard"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Foil Set",
			"description": "Une extension complète avec une copie de chaque carte en finition foil"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Une extension complète avec une copie de chaque carte dans toutes les finitions et variantes"
		}
	}
};

//#endregion
//#region src/lib/games/sorcery/i18n/en.json
var en_default$4 = {
	attributes: {
		"airThreshold": {
			"description": "The air threshold required to play the card",
			"name": "Air Threshold"
		},
		"cost": {
			"description": "The mana cost to play the card",
			"name": "Cost"
		},
		"earthThreshold": {
			"description": "The earth threshold required to play the card",
			"name": "Earth Threshold"
		},
		"elements": {
			"description": "Elements for this card (earth, water, fire, air)",
			"name": "Elements",
			"values": {
				"air": "Air",
				"earth": "Earth",
				"fire": "Fire",
				"water": "Water"
			}
		},
		"finish": {
			"description": "The finish of the card",
			"name": "Finish",
			"values": {
				"standard": "Standard",
				"foil": "Foil",
				"rainbow": "Rainbow"
			}
		},
		"fireThreshold": {
			"description": "The fire threshold required to play the card",
			"name": "Fire Threshold"
		},
		"rarity": {
			"description": "The rarity of the card",
			"name": "Rarity",
			"values": {
				"elite": "Elite",
				"exceptional": "Exceptional",
				"ordinary": "Ordinary",
				"unique": "Unique",
				"curio": "Curio"
			}
		},
		"subType": {
			"description": "Sub type of the card - for example weapon for Flaming sword",
			"name": "Sub type"
		},
		"type": {
			"description": "Type of the card",
			"name": "Type",
			"values": {
				"angel": "Angel",
				"armor": "Armor",
				"artifact": "Artifact",
				"aura": "Aura",
				"automaton": "Automaton",
				"avatar": "Avatar",
				"beast": "Beast",
				"demon": "Demon",
				"desert": "Desert",
				"device": "Device",
				"document": "Document",
				"dragon": "Dragon",
				"dwarf": "Dwarf",
				"faerie": "Faerie",
				"giant": "Giant",
				"gnome": "Gnome",
				"goblin": "Goblin",
				"instruments": "Instruments",
				"magic": "Magic",
				"merfolk": "Merfolk",
				"minion": "Minion",
				"monster": "Monster",
				"monument": "Monument",
				"mortal": "Mortal",
				"ogre": "Ogre",
				"potion": "Potion",
				"relic": "Relic",
				"river": "River",
				"site": "Site",
				"sphinx": "Sphinx",
				"spirit": "Spirit",
				"tower": "Tower",
				"troll": "Troll",
				"undead": "Undead",
				"village": "Village",
				"weapon": "Weapon"
			}
		},
		"variant": {
			"description": "The variant type of the card (Booster, Promo, Store Kit, etc.)",
			"name": "Variant",
			"values": {
				"alphaInvestments": "Alpha Investments",
				"booster": "Booster",
				"boxTopper": "Box Topper",
				"draftKit": "Draft Kit",
				"dust": "Dust",
				"kickstarter": "Kickstarter",
				"organizedPlay": "Organized Play",
				"pledgePack": "Pledge Pack",
				"preconstructedDeck": "Preconstructed Deck",
				"promo": "Promo",
				"starCityGames": "Star City Games",
				"storeKit": "Store Kit",
				"teamCovenant": "Team Covenant",
				"welcomeKit": "Welcome Kit"
			}
		},
		"waterThreshold": {
			"description": "The water threshold required to play the card",
			"name": "Water Threshold"
		},
		"artist": {
			"description": "The artist of the card",
			"name": "Artist"
		},
		"flavorText": {
			"description": "The flavor text of the card",
			"name": "Flavor text"
		},
		"typeText": {
			"description": "The type text of the card",
			"name": "Type text"
		},
		"rulesText": {
			"description": "The rules text of the card",
			"name": "Rules text"
		},
		"attack": {
			"name": "Attack",
			"description": "Attack value of the card"
		},
		"defense": {
			"name": "Defense",
			"description": "Defense value of the card"
		},
		"life": {
			"name": "Life",
			"description": "Life value (for Avatar cards)"
		}
	},
	name: "Sorcery: Contested Realm",
	presets: {
		"foil": {
			"description": "A complete set with one copy of each card in foil finish",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "A complete set with one copy of each card in both standard and foil finishes along with promo cards",
			"name": "Master Set"
		},
		"playset": {
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish",
			"name": "Playset"
		},
		"standard": {
			"description": "A complete set with one copy of each card in standard finish",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/sorcery/i18n/fr.json
var fr_default$4 = {
	attributes: {
		"airThreshold": {
			"description": "\"Air Threshold\" requis pour jouer la carte",
			"name": "Air Threshold"
		},
		"cost": {
			"description": "Le coût en mana pour jouer la carte",
			"name": "Coût"
		},
		"earthThreshold": {
			"description": "\"Earth Threshold\" requis pour jouer la carte",
			"name": "Earth Threshold"
		},
		"elements": {
			"description": "Éléments pour cette carte (earth, water, fire, air)",
			"name": "Elements",
			"values": {
				"air": "Air",
				"earth": "Earth",
				"fire": "Fire",
				"water": "Water"
			}
		},
		"finish": {
			"description": "La finition de la carte",
			"name": "Finition",
			"values": {
				"standard": "Standard",
				"foil": "Foil",
				"rainbow": "Rainbow"
			}
		},
		"fireThreshold": {
			"description": "\"Fire Threshold\" requis pour jouer la carte",
			"name": "Fire Threshold"
		},
		"rarity": {
			"description": "La rareté de la carte",
			"name": "Rareté",
			"values": {
				"elite": "Élite",
				"exceptional": "Exceptionnelle",
				"ordinary": "Ordinaire",
				"unique": "Unique",
				"curio": "Curio"
			}
		},
		"subType": {
			"description": "Sous-type de la carte - par exemple weapon pour Flaming Sword",
			"name": "Sous-type"
		},
		"type": {
			"description": "Type de la carte",
			"name": "Type",
			"values": {
				"angel": "Angel",
				"armor": "Armor",
				"artifact": "Artifact",
				"aura": "Aura",
				"automaton": "Automaton",
				"avatar": "Avatar",
				"beast": "Beast",
				"demon": "Demon",
				"desert": "Desert",
				"device": "Device",
				"document": "Document",
				"dragon": "Dragon",
				"dwarf": "Dwarf",
				"faerie": "Faerie",
				"giant": "Giant",
				"gnome": "Gnome",
				"goblin": "Goblin",
				"instruments": "Instruments",
				"magic": "Magic",
				"merfolk": "Merfolk",
				"minion": "Minion",
				"monster": "Monster",
				"monument": "Monument",
				"mortal": "Mortal",
				"ogre": "Ogre",
				"potion": "Potion",
				"relic": "Relic",
				"river": "River",
				"site": "Site",
				"sphinx": "Sphinx",
				"spirit": "Spirit",
				"tower": "Tower",
				"troll": "Troll",
				"undead": "Undead",
				"village": "Village",
				"weapon": "Weapon"
			}
		},
		"variant": {
			"description": "Le type de variante de la carte (Booster, Promo, Store Kit, etc.)",
			"name": "Variante",
			"values": {
				"alphaInvestments": "Alpha Investments",
				"booster": "Booster",
				"boxTopper": "Box Topper",
				"draftKit": "Draft Kit",
				"dust": "Dust",
				"kickstarter": "Kickstarter",
				"organizedPlay": "Organized Play",
				"pledgePack": "Pledge Pack",
				"preconstructedDeck": "Preconstructed Deck",
				"promo": "Promo",
				"starCityGames": "Star City Games",
				"storeKit": "Store Kit",
				"teamCovenant": "Team Covenant",
				"welcomeKit": "Welcome Kit"
			}
		},
		"waterThreshold": {
			"description": "\"Water Threshold\" requis pour jouer la carte",
			"name": "Water Threshold"
		},
		"artist": {
			"description": "L'artiste de la carte",
			"name": "Artiste"
		},
		"flavorText": {
			"description": "Le texte d'ambiance de la carte",
			"name": "Texte d'ambiance"
		},
		"typeText": {
			"description": "Le texte de type de la carte",
			"name": "Texte de type"
		},
		"rulesText": {
			"description": "Le texte des règles de la carte",
			"name": "Texte des règles"
		},
		"attack": {
			"name": "Attaque",
			"description": "Valeur d'attaque de la carte"
		},
		"defense": {
			"name": "Défense",
			"description": "Valeur de défense de la carte"
		},
		"life": {
			"name": "Vie",
			"description": "Valeur de vie (pour les cartes Avatar)"
		}
	},
	name: "Sorcery: Contested Realm",
	presets: {
		"foil": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition foil",
			"name": "Foil Set"
		},
		"foilPlayset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil",
			"name": "Foil Playset"
		},
		"masterset": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finitions standard et foil ainsi que les cartes promo",
			"name": "Master Set"
		},
		"playset": {
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard",
			"name": "Playset"
		},
		"standard": {
			"description": "Une extension complète avec un exemplaire de chaque carte en finition standard",
			"name": "Standard Set"
		}
	}
};

//#endregion
//#region src/lib/games/swu/i18n/en.json
var en_default$3 = {
	name: "Star Wars: Unlimited",
	attributes: {
		"rarity": {
			"name": "Rarity",
			"values": {
				"common": "Common",
				"uncommon": "Uncommon",
				"rare": "Rare",
				"legendary": "Legendary",
				"special": "Special"
			}
		},
		"cardType": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"base": "Base",
				"creditToken": "Credit Token",
				"event": "Event",
				"forceToken": "Force Token",
				"leader": "Leader",
				"leaderUnit": "Leader Unit",
				"token": "Token",
				"tokenUnit": "Token Unit",
				"tokenUpgrade": "Token Upgrade",
				"unit": "Unit",
				"upgrade": "Upgrade"
			}
		},
		"aspects": {
			"name": "Aspect",
			"description": "The aspect (color) of the card",
			"values": {
				"aggression": "Aggression",
				"command": "Command",
				"cunning": "Cunning",
				"heroism": "Heroism",
				"vigilance": "Vigilance",
				"villainy": "Villainy"
			}
		},
		"arenaType": {
			"name": "Arena Type",
			"description": "The arena the card is played in",
			"values": {
				"ground": "Ground",
				"space": "Space"
			}
		},
		"cost": {
			"name": "Cost",
			"description": "Cost to play the card"
		},
		"power": {
			"name": "Power",
			"description": "Attack power of the card"
		},
		"hp": {
			"name": "HP",
			"description": "Hit points of the card"
		},
		"description": {
			"name": "Description",
			"description": "Card ability text"
		},
		"orientation": {
			"name": "Orientation",
			"description": "The orientation of the card image",
			"values": {
				"portrait": "Portrait",
				"landscape": "Landscape"
			}
		},
		"finish": { "values": {
			"standard": "Normal",
			"foil": "Foil",
			"serialized": "Serialized",
			"serializedRoseGold": "Serialized Rose Gold",
			"serializedGold": "Serialized Gold"
		} },
		"variant": {
			"name": "Variant",
			"description": "The variant type of the card",
			"values": {
				"standard": "Standard",
				"hyperspace": "Hyperspace",
				"showcase": "Showcase",
				"prestige": "Prestige"
			}
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in normal finish"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in standard finish"
		},
		"foil": {
			"name": "Foil Set",
			"description": "A complete set with one copy of each card in foil finish"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "A complete set with the maximum copies of each card allowed in a deck in foil finish"
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of each card in every available finish"
		}
	}
};

//#endregion
//#region src/lib/games/swu/i18n/fr.json
var fr_default$3 = {
	name: "Star Wars: Unlimited",
	attributes: {
		"rarity": {
			"name": "Rareté",
			"values": {
				"common": "Commune",
				"uncommon": "Peu commune",
				"rare": "Rare",
				"legendary": "Légendaire",
				"special": "Spéciale"
			}
		},
		"cardType": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"base": "Base",
				"creditToken": "Jeton Crédit",
				"event": "Événement",
				"forceToken": "Jeton Force",
				"leader": "Leader",
				"leaderUnit": "Unité Leader",
				"token": "Jeton",
				"tokenUnit": "Unité Jeton",
				"tokenUpgrade": "Amélioration Jeton",
				"unit": "Unité",
				"upgrade": "Amélioration"
			}
		},
		"aspects": {
			"name": "Aspect",
			"description": "L'aspect (couleur) de la carte",
			"values": {
				"aggression": "Agression",
				"command": "Commandement",
				"cunning": "Ruse",
				"heroism": "Héroïsme",
				"vigilance": "Vigilance",
				"villainy": "Vilenie"
			}
		},
		"arenaType": {
			"name": "Type d'arène",
			"description": "L'arène dans laquelle la carte est jouée",
			"values": {
				"ground": "Sol",
				"space": "Espace"
			}
		},
		"cost": {
			"name": "Coût",
			"description": "Coût pour jouer la carte"
		},
		"power": {
			"name": "Puissance",
			"description": "Puissance d'attaque de la carte"
		},
		"hp": {
			"name": "PV",
			"description": "Points de vie de la carte"
		},
		"description": {
			"name": "Description",
			"description": "Texte de capacité de la carte"
		},
		"orientation": {
			"name": "Orientation",
			"description": "L'orientation de l'image de la carte",
			"values": {
				"portrait": "Portrait",
				"landscape": "Paysage"
			}
		},
		"finish": { "values": {
			"standard": "Normal",
			"foil": "Foil",
			"serialized": "Sérialisé",
			"serializedRoseGold": "Sérialisé Rose Gold",
			"serializedGold": "Sérialisé Gold"
		} },
		"variant": {
			"name": "Variante",
			"description": "Le type de variante de la carte",
			"values": {
				"standard": "Standard",
				"hyperspace": "Hyperespace",
				"showcase": "Showcase",
				"prestige": "Prestige"
			}
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition normale"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition standard"
		},
		"foil": {
			"name": "Foil Set",
			"description": "Une extension complète avec un exemplaire de chaque carte en finition foil"
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "Une extension complète avec le maximum d'exemplaires de chaque carte autorisé dans un deck en finition foil"
		},
		"masterset": {
			"name": "Master Set",
			"description": "Une extension complète avec un exemplaire de chaque carte dans toutes les finitions disponibles"
		}
	}
};

//#endregion
//#region src/lib/games/wankul/i18n/en.json
var en_default$2 = {
	name: "Wankul",
	attributes: {
		"combo": {
			"name": "Combo",
			"description": "The combo text of the card"
		},
		"cost": {
			"name": "Cost",
			"description": "The cost to play the card"
		},
		"force": {
			"name": "Force",
			"description": "The force value of the card"
		},
		"rules": {
			"name": "Rules",
			"description": "The rules text of the card"
		},
		"quote": {
			"name": "Quote",
			"description": "The flavor text of the card"
		},
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"t": "Terrain",
				"c": "Common",
				"uc": "Uncommon",
				"r": "Rare",
				"ur1": "Ultra Rare Holo 1",
				"ur2": "Ultra Rare Holo 2",
				"lb": "Legendary Bronze",
				"la": "Legendary Silver",
				"lo": "Legendary Gold",
				"tor": "Golden Ticket Winner",
				"pgw23": "PGW 2023",
				"pgw24": "PGW 2024",
				"noel23": "Christmas 2023",
				"spciv": "Starter Pack - Civilizations",
				"spleg": "Starter Pack - Legends",
				"spcar": "Starter Pack - Careers",
				"sppop": "Starter Pack - Pop Culture",
				"sptv": "Starter Pack - TV",
				"spjv": "Starter Pack - Video Games",
				"gp": "Gems Pack",
				"ed": "Special Edition",
				"eg": "Gold Edition"
			}
		},
		"artistName": {
			"name": "Artist",
			"description": "The artist who created the card art"
		},
		"effigyName": {
			"name": "Effigy",
			"description": "The effigy featured on the card",
			"values": {
				"terrain": "Terrain",
				"laink": "Laink",
				"terracid": "Terracid",
				"guest": "Guest",
				"random": "Random",
				"gagnant_ticket_or": "Golden Ticket Winner"
			}
		},
		"diamonds": {
			"name": "Diamonds",
			"description": "The diamond positions on the card"
		}
	},
	presets: { "standard": {
		"name": "Standard Set",
		"description": "A complete set with one copy of each card"
	} }
};

//#endregion
//#region src/lib/games/wankul/i18n/fr.json
var fr_default$2 = {
	name: "Wankul",
	attributes: {
		"combo": {
			"name": "Combo",
			"description": "Le texte de combo de la carte"
		},
		"cost": {
			"name": "Coût",
			"description": "Le coût pour jouer la carte"
		},
		"force": {
			"name": "Force",
			"description": "La valeur de force de la carte"
		},
		"rules": {
			"name": "Règles",
			"description": "Le texte de règles de la carte"
		},
		"quote": {
			"name": "Citation",
			"description": "Le texte d'ambiance de la carte"
		},
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"t": "Terrain",
				"c": "Commune",
				"uc": "Peu commune",
				"r": "Rare",
				"ur1": "Ultra Rare Holo 1",
				"ur2": "Ultra Rare Holo 2",
				"lb": "Légendaire Bronze",
				"la": "Légendaire Argent",
				"lo": "Légendaire Or",
				"tor": "Gagnant Ticket d'Or",
				"pgw23": "PGW 2023",
				"pgw24": "PGW 2024",
				"noel23": "Noël 2023",
				"spciv": "Pack de démarrage - Civilisations",
				"spleg": "Pack de démarrage - Légendes",
				"spcar": "Pack de démarrage - Carrières",
				"sppop": "Pack de démarrage - Pop Culture",
				"sptv": "Pack de démarrage - TV",
				"spjv": "Pack de démarrage - Jeux Vidéo",
				"gp": "Pack de gemmes",
				"ed": "Édition spéciale",
				"eg": "Édition or"
			}
		},
		"artistName": {
			"name": "Artiste",
			"description": "L'artiste qui a créé l'illustration de la carte"
		},
		"effigyName": {
			"name": "Effigie",
			"description": "L'effigie représentée sur la carte",
			"values": {
				"terrain": "Terrain",
				"laink": "Laink",
				"terracid": "Terracid",
				"guest": "Invité",
				"random": "Aléatoire",
				"gagnant_ticket_or": "Gagnant Ticket d'Or"
			}
		},
		"diamonds": {
			"name": "Diamants",
			"description": "Les positions des diamants sur la carte"
		}
	},
	presets: { "standard": {
		"name": "Extension standard",
		"description": "Une extension complète avec un exemplaire de chaque carte"
	} }
};

//#endregion
//#region src/lib/games/yugioh/i18n/en.json
var en_default$1 = {
	name: "Yu-Gi-Oh!",
	attributes: {
		"finish": {
			"name": "Finish",
			"description": "The finish of the card",
			"values": { "standard": "Standard" }
		},
		"type": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"effect_monster": "Effect Monster",
				"flip_effect_monster": "Flip Effect Monster",
				"flip_tuner_effect_monster": "Flip Tuner Effect Monster",
				"fusion_monster": "Fusion Monster",
				"gemini_monster": "Gemini Monster",
				"link_monster": "Link Monster",
				"normal_monster": "Normal Monster",
				"normal_tuner_monster": "Normal Tuner Monster",
				"pendulum_effect_fusion_monster": "Pendulum Effect Fusion Monster",
				"pendulum_effect_monster": "Pendulum Effect Monster",
				"pendulum_effect_ritual_monster": "Pendulum Effect Ritual Monster",
				"pendulum_flip_effect_monster": "Pendulum Flip Effect Monster",
				"pendulum_normal_monster": "Pendulum Normal Monster",
				"pendulum_tuner_effect_monster": "Pendulum Tuner Effect Monster",
				"ritual_effect_monster": "Ritual Effect Monster",
				"ritual_monster": "Ritual Monster",
				"skill_card": "Skill Card",
				"spell_card": "Spell Card",
				"spirit_monster": "Spirit Monster",
				"synchro_monster": "Synchro Monster",
				"synchro_pendulum_effect_monster": "Synchro Pendulum Effect Monster",
				"synchro_tuner_monster": "Synchro Tuner Monster",
				"token": "Token",
				"toon_monster": "Toon Monster",
				"trap_card": "Trap Card",
				"tuner_monster": "Tuner Monster",
				"union_effect_monster": "Union Effect Monster",
				"xyz_monster": "XYZ Monster",
				"xyz_pendulum_effect_monster": "XYZ Pendulum Effect Monster"
			}
		},
		"attribute": {
			"name": "Attribute",
			"description": "The attribute of the monster",
			"values": {
				"dark": "Dark",
				"divine": "Divine",
				"earth": "Earth",
				"fire": "Fire",
				"light": "Light",
				"water": "Water",
				"wind": "Wind"
			}
		},
		"race": {
			"name": "Type",
			"description": "Monster type, or spell/trap type",
			"values": {
				"abidos_the_th": "Abidos the Third",
				"adrian_gecko": "Adrian Gecko",
				"alexis_rhodes": "Alexis Rhodes",
				"amnael": "Amnael",
				"andrew": "Andrew",
				"aqua": "Aqua",
				"arkana": "Arkana",
				"aster_phoenix": "Aster Phoenix",
				"axel_brodie": "Axel Brodie",
				"bastion_misaw": "Bastion Misawa",
				"beast": "Beast",
				"beast_warrior": "Beast-Warrior",
				"bonz": "Bonz",
				"camula": "Camula",
				"chazz_princet": "Chazz Princeton",
				"christine": "Christine",
				"chumley_huffi": "Chumley Huffington",
				"continuous": "Continuous",
				"counter": "Counter",
				"creator_god": "Creator God",
				"cyberse": "Cyberse",
				"david": "David",
				"dinosaur": "Dinosaur",
				"divine_beast": "Divine-Beast",
				"don_zaloog": "Don Zaloog",
				"dr_vellian_c": "Dr. Vellian Crowler",
				"dragon": "Dragon",
				"emma": "Emma",
				"equip": "Equip",
				"espa_roba": "Espa Roba",
				"fairy": "Fairy",
				"field": "Field",
				"fiend": "Fiend",
				"fish": "Fish",
				"illusion": "Illusion",
				"insect": "Insect",
				"ishizu": "Ishizu",
				"ishizu_ishtar": "Ishizu Ishtar",
				"jaden_yuki": "Jaden Yuki",
				"jesse_anderso": "Jesse Anderson",
				"joey": "Joey",
				"joey_wheeler": "Joey Wheeler",
				"kagemaru": "Kagemaru",
				"kaiba": "Kaiba",
				"keith": "Keith",
				"lumis_umbra": "Lumis Umbra",
				"lumis_and_umb": "Lumis and Umbra",
				"machine": "Machine",
				"mai": "Mai",
				"mai_valentine": "Mai Valentine",
				"mako": "Mako",
				"nightshroud": "Nightshroud",
				"normal": "Normal",
				"odion": "Odion",
				"paradox_broth": "Paradox Brothers",
				"pegasus": "Pegasus",
				"plant": "Plant",
				"psychic": "Psychic",
				"pyro": "Pyro",
				"quick_play": "Quick-Play",
				"reptile": "Reptile",
				"rex": "Rex",
				"ritual": "Ritual",
				"rock": "Rock",
				"sea_serpent": "Sea Serpent",
				"seto_kaiba": "Seto Kaiba",
				"spellcaster": "Spellcaster",
				"syrus_truesda": "Syrus Truesdale",
				"tania": "Tania",
				"tea_gardner": "Tea Gardner",
				"the_supreme_k": "The Supreme King",
				"thelonious_vi": "Thelonious Viper",
				"thunder": "Thunder",
				"titan": "Titan",
				"tyranno_hassl": "Tyranno Hassleberry",
				"warrior": "Warrior",
				"weevil": "Weevil",
				"winged_beast": "Winged Beast",
				"wyrm": "Wyrm",
				"yami_bakura": "Yami Bakura",
				"yami_marik": "Yami Marik",
				"yami_yugi": "Yami Yugi",
				"yubel": "Yubel",
				"yugi": "Yugi",
				"zane_truesdal": "Zane Truesdale",
				"zombie": "Zombie"
			}
		},
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"ultra_rare": "Ultra Rare",
				"secret_rare": "Secret Rare",
				"ultimate_rare": "Ultimate Rare",
				"ghost_rare": "Ghost Rare",
				"ghostgold_rare": "Ghost/Gold Rare",
				"gold_rare": "Gold Rare",
				"gold_secret_rare": "Gold Secret Rare",
				"premium_gold_rare": "Premium Gold Rare",
				"platinum_rare": "Platinum Rare",
				"platinum_secret_rare": "Platinum Secret Rare",
				"prismatic_secret_rare": "Prismatic Secret Rare",
				"prismatic_ultimate_rare": "Prismatic Ultimate Rare",
				"prismatic_collectors_rare": "Prismatic Collector's Rare",
				"quarter_century_secret_rare": "Quarter Century Secret Rare",
				"10000_secret_rare": "10000 Secret Rare",
				"starlight_rare": "Starlight Rare",
				"collectors_rare": "Collector's Rare",
				"extra_secret_rare": "Extra Secret Rare",
				"ultra_secret_rare": "Ultra Secret Rare",
				"starfoil_rare": "Starfoil Rare",
				"shatterfoil_rare": "Shatterfoil Rare",
				"mosaic_rare": "Mosaic Rare",
				"holographic_rare": "Holographic Rare",
				"parallel_rare": "Parallel Rare",
				"normal_parallel_rare": "Normal Parallel Rare",
				"super_parallel_rare": "Super Parallel Rare",
				"ultra_parallel_rare": "Ultra Parallel Rare",
				"secret_parallel_rare": "Secret Parallel Rare",
				"duel_terminal_normal_parallel_rare": "Duel Terminal Normal Parallel Rare",
				"duel_terminal_rare_parallel_rare": "Duel Terminal Rare Parallel Rare",
				"duel_terminal_super_parallel_rare": "Duel Terminal Super Parallel Rare",
				"duel_terminal_ultra_parallel_rare": "Duel Terminal Ultra Parallel Rare",
				"duel_terminal_secret_parallel_rare": "Duel Terminal Secret Parallel Rare",
				"duel_terminal_technology_common": "Duel Terminal Technology Common",
				"duel_terminal_technology_ultra_rare": "Duel Terminal Technology Ultra Rare",
				"emblazoned_rare": "Emblazoned Rare",
				"emblazoned_ultra_rare": "Emblazoned Ultra Rare",
				"emblazoned_secret_rare": "Emblazoned Secret Rare",
				"ultra_pharaohs_rare": "Ultra Pharaoh's Rare",
				"secret_pharaohs_rare": "Secret Pharaoh's Rare",
				"millennium_rare": "Millennium Rare",
				"millennium_super_rare": "Millennium Super Rare",
				"millennium_ultra_rare": "Millennium Ultra Rare",
				"millennium_secret_rare": "Millennium Secret Rare",
				"millennium_gold_rare": "Millennium Gold Rare",
				"grand_master_rare": "Grand Master Rare",
				"20th_secret_rare": "20th Secret Rare",
				"kaiba_corporation_common": "Kaiba Corporation Common",
				"kaiba_corporation_rare": "Kaiba Corporation Rare",
				"kaiba_corporation_ultra_rare": "Kaiba Corporation Ultra Rare",
				"short_print": "Short Print",
				"super_short_print": "Super Short Print",
				"speed_duel_skill": "Speed Duel Skill",
				"token": "Token",
				"promo": "Promo"
			}
		},
		"printedCodes": {
			"name": "Printed codes",
			"description": "Every set code printed for this card across regions and generations"
		},
		"cardmarketVersion": {
			"name": "Cardmarket version",
			"description": "Distinguishes Cardmarket versions of the same expansion, number and rarity"
		},
		"variant": {
			"name": "Variant",
			"description": "Art, colour or stamp variant of the printing",
			"values": {
				"alternate_art": "Alternate Art",
				"new_art": "New Art",
				"art_7": "Art 7",
				"art_8": "Art 8",
				"hero_art": "HERO Art",
				"extended_art": "Extended Art",
				"japanese_artwork": "Japanese Artwork",
				"blue": "Blue",
				"green": "Green",
				"purple": "Purple",
				"red": "Red",
				"silver": "Silver",
				"bronze": "Bronze",
				"yellow": "Yellow",
				"orange": "Orange",
				"black": "Black",
				"ots_stamp": "OTS Stamp",
				"judge_stamp": "Judge Stamp",
				"regional_stamp": "Regional Stamp",
				"stamp": "Stamp",
				"misprint": "Misprint",
				"oversized": "Oversized",
				"limited_edition": "Limited Edition",
				"reprint": "Reprint",
				"classic_style": "Classic Style",
				"logo": "Logo",
				"original_layout": "Original Layout"
			}
		},
		"linkMarkers": {
			"name": "Link Markers",
			"description": "Link arrows of a Link monster",
			"values": {
				"top_left": "Top-Left",
				"top": "Top",
				"top_right": "Top-Right",
				"left": "Left",
				"right": "Right",
				"bottom_left": "Bottom-Left",
				"bottom": "Bottom",
				"bottom_right": "Bottom-Right"
			}
		},
		"level": {
			"name": "Level",
			"description": "Monster level"
		},
		"rank": {
			"name": "Rank",
			"description": "Rank of an Xyz monster"
		},
		"linkRating": {
			"name": "Link Rating",
			"description": "Link rating of a Link monster"
		},
		"scale": {
			"name": "Pendulum Scale",
			"description": "Pendulum scale value"
		},
		"atk": {
			"name": "ATK",
			"description": "Attack points of the monster"
		},
		"def": {
			"name": "DEF",
			"description": "Defense points of the monster"
		},
		"archetype": {
			"name": "Archetype",
			"description": "Card archetype (e.g., Blue-Eyes, Dark Magician)"
		},
		"desc": {
			"name": "Description",
			"description": "Card effect text and description"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in its lowest rarity"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with three copies of each card in its lowest rarity"
		},
		"foil": {
			"name": "Foil Cards",
			"description": "Every card in the set that has a foil printing (Super Rare and above, Starfoil and other parallels), one copy in its lowest foil rarity. Commons and Rares with no foil version are not included."
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "Three copies of every card in the set that has a foil printing, in its lowest foil rarity."
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of every printing, in every rarity and variant"
		}
	}
};

//#endregion
//#region src/lib/games/yugioh/i18n/fr.json
var fr_default$1 = {
	name: "Yu-Gi-Oh!",
	attributes: {
		"finish": {
			"name": "Finition",
			"description": "La finition de la carte",
			"values": { "standard": "Standard" }
		},
		"type": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"effect_monster": "Monstre à Effet",
				"flip_effect_monster": "Monstre à Effet Flip",
				"flip_tuner_effect_monster": "Monstre Syntoniseur à Effet Flip",
				"fusion_monster": "Monstre Fusion",
				"gemini_monster": "Monstre Gémeau",
				"link_monster": "Monstre Lien",
				"normal_monster": "Monstre Normal",
				"normal_tuner_monster": "Monstre Syntoniseur Normal",
				"pendulum_effect_fusion_monster": "Monstre Fusion à Effet Pendule",
				"pendulum_effect_monster": "Monstre à Effet Pendule",
				"pendulum_effect_ritual_monster": "Monstre Rituel à Effet Pendule",
				"pendulum_flip_effect_monster": "Monstre à Effet Flip Pendule",
				"pendulum_normal_monster": "Monstre Normal Pendule",
				"pendulum_tuner_effect_monster": "Monstre Syntoniseur à Effet Pendule",
				"ritual_effect_monster": "Monstre Rituel à Effet",
				"ritual_monster": "Monstre Rituel",
				"skill_card": "Carte Compétence",
				"spell_card": "Carte Magie",
				"spirit_monster": "Monstre Esprit",
				"synchro_monster": "Monstre Synchro",
				"synchro_pendulum_effect_monster": "Monstre Synchro à Effet Pendule",
				"synchro_tuner_monster": "Monstre Syntoniseur Synchro",
				"token": "Jeton",
				"toon_monster": "Monstre Toon",
				"trap_card": "Carte Piège",
				"tuner_monster": "Monstre Syntoniseur",
				"union_effect_monster": "Monstre Union à Effet",
				"xyz_monster": "Monstre Xyz",
				"xyz_pendulum_effect_monster": "Monstre Xyz à Effet Pendule"
			}
		},
		"attribute": {
			"name": "Attribut",
			"description": "L'attribut du monstre",
			"values": {
				"dark": "Ténèbres",
				"divine": "Divin",
				"earth": "Terre",
				"fire": "Feu",
				"light": "Lumière",
				"water": "Eau",
				"wind": "Vent"
			}
		},
		"race": {
			"name": "Type",
			"description": "Type de monstre, ou type de magie/piège",
			"values": {
				"abidos_the_th": "Abidos le Troisième",
				"adrian_gecko": "Adrian Gecko",
				"alexis_rhodes": "Alexis Rhodes",
				"amnael": "Amnael",
				"andrew": "Andrew",
				"aqua": "Aqua",
				"arkana": "Arkana",
				"aster_phoenix": "Aster Phoenix",
				"axel_brodie": "Axel Brodie",
				"bastion_misaw": "Bastion Misawa",
				"beast": "Bête",
				"beast_warrior": "Bête-Guerrier",
				"bonz": "Bonz",
				"camula": "Camula",
				"chazz_princet": "Chazz Princeton",
				"christine": "Christine",
				"chumley_huffi": "Chumley Huffington",
				"continuous": "Continue",
				"counter": "Contre",
				"creator_god": "Dieu Créateur",
				"cyberse": "Cyberse",
				"david": "David",
				"dinosaur": "Dinosaure",
				"divine_beast": "Bête Divine",
				"don_zaloog": "Don Zaloog",
				"dr_vellian_c": "Dr. Vellian Crowler",
				"dragon": "Dragon",
				"emma": "Emma",
				"equip": "Équipement",
				"espa_roba": "Espa Roba",
				"fairy": "Elfe",
				"field": "Terrain",
				"fiend": "Démon",
				"fish": "Poisson",
				"illusion": "Illusion",
				"insect": "Insecte",
				"ishizu": "Ishizu",
				"ishizu_ishtar": "Ishizu Ishtar",
				"jaden_yuki": "Jaden Yuki",
				"jesse_anderso": "Jesse Anderson",
				"joey": "Joey",
				"joey_wheeler": "Joey Wheeler",
				"kagemaru": "Kagemaru",
				"kaiba": "Kaiba",
				"keith": "Keith",
				"lumis_umbra": "Lumis Umbra",
				"lumis_and_umb": "Lumis et Umbra",
				"machine": "Machine",
				"mai": "Mai",
				"mai_valentine": "Mai Valentine",
				"mako": "Mako",
				"nightshroud": "Nightshroud",
				"normal": "Normal",
				"odion": "Odion",
				"paradox_broth": "Frères Paradox",
				"pegasus": "Pegasus",
				"plant": "Plante",
				"psychic": "Psychique",
				"pyro": "Pyro",
				"quick_play": "Jeu Rapide",
				"reptile": "Reptile",
				"rex": "Rex",
				"ritual": "Rituel",
				"rock": "Rocher",
				"sea_serpent": "Serpent de Mer",
				"seto_kaiba": "Seto Kaiba",
				"spellcaster": "Magicien",
				"syrus_truesda": "Syrus Truesdale",
				"tania": "Tania",
				"tea_gardner": "Tea Gardner",
				"the_supreme_k": "Le Roi Suprême",
				"thelonious_vi": "Thelonious Viper",
				"thunder": "Tonnerre",
				"titan": "Titan",
				"tyranno_hassl": "Tyranno Hassleberry",
				"warrior": "Guerrier",
				"weevil": "Weevil",
				"winged_beast": "Bête Ailée",
				"wyrm": "Wyrm",
				"yami_bakura": "Yami Bakura",
				"yami_marik": "Yami Marik",
				"yami_yugi": "Yami Yugi",
				"yubel": "Yubel",
				"yugi": "Yugi",
				"zane_truesdal": "Zane Truesdale",
				"zombie": "Zombie"
			}
		},
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"ultra_rare": "Ultra Rare",
				"secret_rare": "Secret Rare",
				"ultimate_rare": "Ultimate Rare",
				"ghost_rare": "Ghost Rare",
				"ghostgold_rare": "Ghost/Gold Rare",
				"gold_rare": "Gold Rare",
				"gold_secret_rare": "Gold Secret Rare",
				"premium_gold_rare": "Premium Gold Rare",
				"platinum_rare": "Platine Rare",
				"platinum_secret_rare": "Platine Secret Rare",
				"prismatic_secret_rare": "Prismatique Secret Rare",
				"prismatic_ultimate_rare": "Prismatique Ultimate Rare",
				"prismatic_collectors_rare": "Rare de Collection Prismatique",
				"quarter_century_secret_rare": "Quart de Siècle Secret Rare",
				"10000_secret_rare": "10000 Secret Rare",
				"starlight_rare": "Starlight Rare",
				"collectors_rare": "Rare de Collection",
				"extra_secret_rare": "Extra Secret Rare",
				"ultra_secret_rare": "Ultra Secret Rare",
				"starfoil_rare": "Starfoil Rare",
				"shatterfoil_rare": "Shatterfoil Rare",
				"mosaic_rare": "Mosaïque Rare",
				"holographic_rare": "Holographique Rare",
				"parallel_rare": "Parallèle Rare",
				"normal_parallel_rare": "Normal Parallèle Rare",
				"super_parallel_rare": "Super Parallèle Rare",
				"ultra_parallel_rare": "Ultra Parallèle Rare",
				"secret_parallel_rare": "Secret Parallèle Rare",
				"duel_terminal_normal_parallel_rare": "Duel Terminal Normal Parallèle Rare",
				"duel_terminal_rare_parallel_rare": "Duel Terminal Rare Parallèle Rare",
				"duel_terminal_super_parallel_rare": "Duel Terminal Super Parallèle Rare",
				"duel_terminal_ultra_parallel_rare": "Duel Terminal Ultra Parallèle Rare",
				"duel_terminal_secret_parallel_rare": "Duel Terminal Secret Parallèle Rare",
				"duel_terminal_technology_common": "Duel Terminal Technology Commune",
				"duel_terminal_technology_ultra_rare": "Duel Terminal Technology Ultra Rare",
				"emblazoned_rare": "Emblazoned Rare",
				"emblazoned_ultra_rare": "Emblazoned Ultra Rare",
				"emblazoned_secret_rare": "Emblazoned Secret Rare",
				"ultra_pharaohs_rare": "Ultra Rare du Pharaon",
				"secret_pharaohs_rare": "Secret Rare du Pharaon",
				"millennium_rare": "Millénaire Rare",
				"millennium_super_rare": "Millénaire Super Rare",
				"millennium_ultra_rare": "Millénaire Ultra Rare",
				"millennium_secret_rare": "Millénaire Secret Rare",
				"millennium_gold_rare": "Millénaire Gold Rare",
				"grand_master_rare": "Grand Master Rare",
				"20th_secret_rare": "20th Secret Rare",
				"kaiba_corporation_common": "Kaiba Corporation Common",
				"kaiba_corporation_rare": "Kaiba Corporation Rare",
				"kaiba_corporation_ultra_rare": "Kaiba Corporation Ultra Rare",
				"short_print": "Tirage Court",
				"super_short_print": "Super Tirage Court",
				"speed_duel_skill": "Compétence Speed Duel",
				"token": "Jeton",
				"promo": "Promo"
			}
		},
		"printedCodes": {
			"name": "Codes imprimés",
			"description": "Tous les codes d'extension imprimés pour cette carte selon les régions et générations"
		},
		"cardmarketVersion": {
			"name": "Version Cardmarket",
			"description": "Distingue les versions Cardmarket ayant la même extension, le même numéro et la même rareté"
		},
		"variant": {
			"name": "Variante",
			"description": "Variante d'illustration, de couleur ou de tampon de l'impression",
			"values": {
				"alternate_art": "Illustration alternative",
				"new_art": "Nouvelle illustration",
				"art_7": "7e illustration",
				"art_8": "8e illustration",
				"hero_art": "Illustration HERO",
				"extended_art": "Illustration étendue",
				"japanese_artwork": "Illustration japonaise",
				"blue": "Bleu",
				"green": "Vert",
				"purple": "Violet",
				"red": "Rouge",
				"silver": "Argent",
				"bronze": "Bronze",
				"yellow": "Jaune",
				"orange": "Orange",
				"black": "Noir",
				"ots_stamp": "Tampon OTS",
				"judge_stamp": "Tampon Juge",
				"regional_stamp": "Tampon Régional",
				"stamp": "Tampon",
				"misprint": "Erreur d'impression",
				"oversized": "Surdimensionnée",
				"limited_edition": "Édition limitée",
				"reprint": "Réimpression",
				"classic_style": "Style classique",
				"logo": "Logo",
				"original_layout": "Mise en page originale"
			}
		},
		"linkMarkers": {
			"name": "Flèches Lien",
			"description": "Flèches Lien d'un monstre Lien",
			"values": {
				"top_left": "Haut-Gauche",
				"top": "Haut",
				"top_right": "Haut-Droite",
				"left": "Gauche",
				"right": "Droite",
				"bottom_left": "Bas-Gauche",
				"bottom": "Bas",
				"bottom_right": "Bas-Droite"
			}
		},
		"level": {
			"name": "Niveau",
			"description": "Niveau du monstre"
		},
		"rank": {
			"name": "Rang",
			"description": "Rang d'un monstre Xyz"
		},
		"linkRating": {
			"name": "Valeur Lien",
			"description": "Valeur Lien d'un monstre Lien"
		},
		"scale": {
			"name": "Échelle Pendule",
			"description": "Valeur de l'échelle Pendule"
		},
		"atk": {
			"name": "ATK",
			"description": "Points d'attaque du monstre"
		},
		"def": {
			"name": "DEF",
			"description": "Points de défense du monstre"
		},
		"archetype": {
			"name": "Archétype",
			"description": "Archétype de la carte (ex: Yeux Bleus, Magicien Sombre)"
		},
		"desc": {
			"name": "Description",
			"description": "Texte d'effet et description de la carte"
		}
	},
	presets: {
		"standard": {
			"name": "Extension standard",
			"description": "Une extension complète avec un exemplaire de chaque carte dans sa rareté la plus basse"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec trois exemplaires de chaque carte dans sa rareté la plus basse"
		},
		"foil": {
			"name": "Cartes foil",
			"description": "Toutes les cartes de l'extension qui existent en foil (Super Rare et au-delà, Starfoil et autres parallèles), en un exemplaire dans leur rareté foil la plus basse. Les Communes et Rares sans version foil ne sont pas incluses."
		},
		"foilPlayset": {
			"name": "Playset foil",
			"description": "Trois exemplaires de chaque carte de l'extension qui existe en foil, dans sa rareté foil la plus basse."
		},
		"masterset": {
			"name": "Masterset",
			"description": "Une extension complète avec un exemplaire de chaque impression, dans toutes les raretés et variantes"
		}
	}
};

//#endregion
//#region src/lib/games/yugioh-ocg/i18n/en.json
var en_default = {
	name: "Yu-Gi-Oh! OCG",
	attributes: {
		"finish": {
			"name": "Finish",
			"description": "The finish of the card",
			"values": { "standard": "Standard" }
		},
		"type": {
			"name": "Card Type",
			"description": "The type of the card",
			"values": {
				"effect_monster": "Effect Monster",
				"flip_effect_monster": "Flip Effect Monster",
				"flip_tuner_effect_monster": "Flip Tuner Effect Monster",
				"fusion_monster": "Fusion Monster",
				"gemini_monster": "Gemini Monster",
				"link_monster": "Link Monster",
				"normal_monster": "Normal Monster",
				"normal_tuner_monster": "Normal Tuner Monster",
				"pendulum_effect_fusion_monster": "Pendulum Effect Fusion Monster",
				"pendulum_effect_monster": "Pendulum Effect Monster",
				"pendulum_effect_ritual_monster": "Pendulum Effect Ritual Monster",
				"pendulum_flip_effect_monster": "Pendulum Flip Effect Monster",
				"pendulum_normal_monster": "Pendulum Normal Monster",
				"pendulum_tuner_effect_monster": "Pendulum Tuner Effect Monster",
				"ritual_effect_monster": "Ritual Effect Monster",
				"ritual_monster": "Ritual Monster",
				"skill_card": "Skill Card",
				"spell_card": "Spell Card",
				"spirit_monster": "Spirit Monster",
				"synchro_monster": "Synchro Monster",
				"synchro_pendulum_effect_monster": "Synchro Pendulum Effect Monster",
				"synchro_tuner_monster": "Synchro Tuner Monster",
				"token": "Token",
				"toon_monster": "Toon Monster",
				"trap_card": "Trap Card",
				"tuner_monster": "Tuner Monster",
				"union_effect_monster": "Union Effect Monster",
				"xyz_monster": "XYZ Monster",
				"xyz_pendulum_effect_monster": "XYZ Pendulum Effect Monster"
			}
		},
		"attribute": {
			"name": "Attribute",
			"description": "The attribute of the monster",
			"values": {
				"dark": "Dark",
				"divine": "Divine",
				"earth": "Earth",
				"fire": "Fire",
				"light": "Light",
				"water": "Water",
				"wind": "Wind"
			}
		},
		"race": {
			"name": "Type",
			"description": "Monster type, or spell/trap type",
			"values": {
				"abidos_the_th": "Abidos the Third",
				"adrian_gecko": "Adrian Gecko",
				"alexis_rhodes": "Alexis Rhodes",
				"amnael": "Amnael",
				"andrew": "Andrew",
				"aqua": "Aqua",
				"arkana": "Arkana",
				"aster_phoenix": "Aster Phoenix",
				"axel_brodie": "Axel Brodie",
				"bastion_misaw": "Bastion Misawa",
				"beast": "Beast",
				"beast_warrior": "Beast-Warrior",
				"bonz": "Bonz",
				"camula": "Camula",
				"chazz_princet": "Chazz Princeton",
				"christine": "Christine",
				"chumley_huffi": "Chumley Huffington",
				"continuous": "Continuous",
				"counter": "Counter",
				"creator_god": "Creator God",
				"cyberse": "Cyberse",
				"david": "David",
				"dinosaur": "Dinosaur",
				"divine_beast": "Divine-Beast",
				"don_zaloog": "Don Zaloog",
				"dr_vellian_c": "Dr. Vellian Crowler",
				"dragon": "Dragon",
				"emma": "Emma",
				"equip": "Equip",
				"espa_roba": "Espa Roba",
				"fairy": "Fairy",
				"field": "Field",
				"fiend": "Fiend",
				"fish": "Fish",
				"illusion": "Illusion",
				"insect": "Insect",
				"ishizu": "Ishizu",
				"ishizu_ishtar": "Ishizu Ishtar",
				"jaden_yuki": "Jaden Yuki",
				"jesse_anderso": "Jesse Anderson",
				"joey": "Joey",
				"joey_wheeler": "Joey Wheeler",
				"kagemaru": "Kagemaru",
				"kaiba": "Kaiba",
				"keith": "Keith",
				"lumis_umbra": "Lumis Umbra",
				"lumis_and_umb": "Lumis and Umbra",
				"machine": "Machine",
				"mai": "Mai",
				"mai_valentine": "Mai Valentine",
				"mako": "Mako",
				"nightshroud": "Nightshroud",
				"normal": "Normal",
				"odion": "Odion",
				"paradox_broth": "Paradox Brothers",
				"pegasus": "Pegasus",
				"plant": "Plant",
				"psychic": "Psychic",
				"pyro": "Pyro",
				"quick_play": "Quick-Play",
				"reptile": "Reptile",
				"rex": "Rex",
				"ritual": "Ritual",
				"rock": "Rock",
				"sea_serpent": "Sea Serpent",
				"seto_kaiba": "Seto Kaiba",
				"spellcaster": "Spellcaster",
				"syrus_truesda": "Syrus Truesdale",
				"tania": "Tania",
				"tea_gardner": "Tea Gardner",
				"the_supreme_k": "The Supreme King",
				"thelonious_vi": "Thelonious Viper",
				"thunder": "Thunder",
				"titan": "Titan",
				"tyranno_hassl": "Tyranno Hassleberry",
				"warrior": "Warrior",
				"weevil": "Weevil",
				"winged_beast": "Winged Beast",
				"wyrm": "Wyrm",
				"yami_bakura": "Yami Bakura",
				"yami_marik": "Yami Marik",
				"yami_yugi": "Yami Yugi",
				"yubel": "Yubel",
				"yugi": "Yugi",
				"zane_truesdal": "Zane Truesdale",
				"zombie": "Zombie"
			}
		},
		"rarity": {
			"name": "Rarity",
			"description": "The rarity of the card",
			"values": {
				"common": "Common",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"ultra_rare": "Ultra Rare",
				"secret_rare": "Secret Rare",
				"ultimate_rare": "Ultimate Rare",
				"ghost_rare": "Ghost Rare",
				"ghostgold_rare": "Ghost/Gold Rare",
				"gold_rare": "Gold Rare",
				"gold_secret_rare": "Gold Secret Rare",
				"premium_gold_rare": "Premium Gold Rare",
				"platinum_rare": "Platinum Rare",
				"platinum_secret_rare": "Platinum Secret Rare",
				"prismatic_secret_rare": "Prismatic Secret Rare",
				"prismatic_ultimate_rare": "Prismatic Ultimate Rare",
				"prismatic_collectors_rare": "Prismatic Collector's Rare",
				"quarter_century_secret_rare": "Quarter Century Secret Rare",
				"10000_secret_rare": "10000 Secret Rare",
				"starlight_rare": "Starlight Rare",
				"collectors_rare": "Collector's Rare",
				"extra_secret_rare": "Extra Secret Rare",
				"ultra_secret_rare": "Ultra Secret Rare",
				"starfoil_rare": "Starfoil Rare",
				"shatterfoil_rare": "Shatterfoil Rare",
				"mosaic_rare": "Mosaic Rare",
				"holographic_rare": "Holographic Rare",
				"parallel_rare": "Parallel Rare",
				"normal_parallel_rare": "Normal Parallel Rare",
				"super_parallel_rare": "Super Parallel Rare",
				"ultra_parallel_rare": "Ultra Parallel Rare",
				"secret_parallel_rare": "Secret Parallel Rare",
				"duel_terminal_normal_parallel_rare": "Duel Terminal Normal Parallel Rare",
				"duel_terminal_rare_parallel_rare": "Duel Terminal Rare Parallel Rare",
				"duel_terminal_super_parallel_rare": "Duel Terminal Super Parallel Rare",
				"duel_terminal_ultra_parallel_rare": "Duel Terminal Ultra Parallel Rare",
				"duel_terminal_secret_parallel_rare": "Duel Terminal Secret Parallel Rare",
				"duel_terminal_technology_common": "Duel Terminal Technology Common",
				"duel_terminal_technology_ultra_rare": "Duel Terminal Technology Ultra Rare",
				"emblazoned_rare": "Emblazoned Rare",
				"emblazoned_ultra_rare": "Emblazoned Ultra Rare",
				"emblazoned_secret_rare": "Emblazoned Secret Rare",
				"ultra_pharaohs_rare": "Ultra Pharaoh's Rare",
				"secret_pharaohs_rare": "Secret Pharaoh's Rare",
				"millennium_rare": "Millennium Rare",
				"millennium_super_rare": "Millennium Super Rare",
				"millennium_ultra_rare": "Millennium Ultra Rare",
				"millennium_secret_rare": "Millennium Secret Rare",
				"millennium_gold_rare": "Millennium Gold Rare",
				"grand_master_rare": "Grand Master Rare",
				"20th_secret_rare": "20th Secret Rare",
				"kaiba_corporation_common": "Kaiba Corporation Common",
				"kaiba_corporation_rare": "Kaiba Corporation Rare",
				"kaiba_corporation_ultra_rare": "Kaiba Corporation Ultra Rare",
				"short_print": "Short Print",
				"super_short_print": "Super Short Print",
				"speed_duel_skill": "Speed Duel Skill",
				"token": "Token",
				"promo": "Promo"
			}
		},
		"printedCodes": {
			"name": "Printed codes",
			"description": "Every set code printed for this card across regions and generations"
		},
		"cardmarketVersion": {
			"name": "Cardmarket version",
			"description": "Distinguishes Cardmarket versions of the same expansion, number and rarity"
		},
		"variant": {
			"name": "Variant",
			"description": "Art, colour or stamp variant of the printing",
			"values": {
				"alternate_art": "Alternate Art",
				"new_art": "New Art",
				"art_7": "Art 7",
				"art_8": "Art 8",
				"hero_art": "HERO Art",
				"extended_art": "Extended Art",
				"japanese_artwork": "Japanese Artwork",
				"blue": "Blue",
				"green": "Green",
				"purple": "Purple",
				"red": "Red",
				"silver": "Silver",
				"bronze": "Bronze",
				"yellow": "Yellow",
				"orange": "Orange",
				"black": "Black",
				"ots_stamp": "OTS Stamp",
				"judge_stamp": "Judge Stamp",
				"regional_stamp": "Regional Stamp",
				"stamp": "Stamp",
				"misprint": "Misprint",
				"oversized": "Oversized",
				"limited_edition": "Limited Edition",
				"reprint": "Reprint",
				"classic_style": "Classic Style",
				"logo": "Logo",
				"original_layout": "Original Layout"
			}
		},
		"linkMarkers": {
			"name": "Link Markers",
			"description": "Link arrows of a Link monster",
			"values": {
				"top_left": "Top-Left",
				"top": "Top",
				"top_right": "Top-Right",
				"left": "Left",
				"right": "Right",
				"bottom_left": "Bottom-Left",
				"bottom": "Bottom",
				"bottom_right": "Bottom-Right"
			}
		},
		"level": {
			"name": "Level",
			"description": "Monster level"
		},
		"rank": {
			"name": "Rank",
			"description": "Rank of an Xyz monster"
		},
		"linkRating": {
			"name": "Link Rating",
			"description": "Link rating of a Link monster"
		},
		"scale": {
			"name": "Pendulum Scale",
			"description": "Pendulum scale value"
		},
		"atk": {
			"name": "ATK",
			"description": "Attack points of the monster"
		},
		"def": {
			"name": "DEF",
			"description": "Defense points of the monster"
		},
		"archetype": {
			"name": "Archetype",
			"description": "Card archetype (e.g., Blue-Eyes, Dark Magician)"
		},
		"desc": {
			"name": "Description",
			"description": "Card effect text and description"
		}
	},
	presets: {
		"standard": {
			"name": "Standard Set",
			"description": "A complete set with one copy of each card in its lowest rarity"
		},
		"playset": {
			"name": "Playset",
			"description": "A complete set with three copies of each card in its lowest rarity"
		},
		"foil": {
			"name": "Foil Cards",
			"description": "Every card in the set that has a foil printing (Super Rare and above, Starfoil and other parallels), one copy in its lowest foil rarity. Commons and Rares with no foil version are not included."
		},
		"foilPlayset": {
			"name": "Foil Playset",
			"description": "Three copies of every card in the set that has a foil printing, in its lowest foil rarity."
		},
		"masterset": {
			"name": "Master Set",
			"description": "A complete set with one copy of every printing, in every rarity and variant"
		}
	}
};

//#endregion
//#region src/lib/games/yugioh-ocg/i18n/fr.json
var fr_default = {
	name: "Yu-Gi-Oh! OCG",
	attributes: {
		"finish": {
			"name": "Finition",
			"description": "La finition de la carte",
			"values": { "standard": "Standard" }
		},
		"type": {
			"name": "Type de carte",
			"description": "Le type de la carte",
			"values": {
				"effect_monster": "Monstre à Effet",
				"flip_effect_monster": "Monstre à Effet Flip",
				"flip_tuner_effect_monster": "Monstre Syntoniseur à Effet Flip",
				"fusion_monster": "Monstre Fusion",
				"gemini_monster": "Monstre Gémeau",
				"link_monster": "Monstre Lien",
				"normal_monster": "Monstre Normal",
				"normal_tuner_monster": "Monstre Syntoniseur Normal",
				"pendulum_effect_fusion_monster": "Monstre Fusion à Effet Pendule",
				"pendulum_effect_monster": "Monstre à Effet Pendule",
				"pendulum_effect_ritual_monster": "Monstre Rituel à Effet Pendule",
				"pendulum_flip_effect_monster": "Monstre à Effet Flip Pendule",
				"pendulum_normal_monster": "Monstre Normal Pendule",
				"pendulum_tuner_effect_monster": "Monstre Syntoniseur à Effet Pendule",
				"ritual_effect_monster": "Monstre Rituel à Effet",
				"ritual_monster": "Monstre Rituel",
				"skill_card": "Carte Compétence",
				"spell_card": "Carte Magie",
				"spirit_monster": "Monstre Esprit",
				"synchro_monster": "Monstre Synchro",
				"synchro_pendulum_effect_monster": "Monstre Synchro à Effet Pendule",
				"synchro_tuner_monster": "Monstre Syntoniseur Synchro",
				"token": "Jeton",
				"toon_monster": "Monstre Toon",
				"trap_card": "Carte Piège",
				"tuner_monster": "Monstre Syntoniseur",
				"union_effect_monster": "Monstre Union à Effet",
				"xyz_monster": "Monstre Xyz",
				"xyz_pendulum_effect_monster": "Monstre Xyz à Effet Pendule"
			}
		},
		"attribute": {
			"name": "Attribut",
			"description": "L'attribut du monstre",
			"values": {
				"dark": "Ténèbres",
				"divine": "Divin",
				"earth": "Terre",
				"fire": "Feu",
				"light": "Lumière",
				"water": "Eau",
				"wind": "Vent"
			}
		},
		"race": {
			"name": "Type",
			"description": "Type de monstre, ou type de magie/piège",
			"values": {
				"abidos_the_th": "Abidos le Troisième",
				"adrian_gecko": "Adrian Gecko",
				"alexis_rhodes": "Alexis Rhodes",
				"amnael": "Amnael",
				"andrew": "Andrew",
				"aqua": "Aqua",
				"arkana": "Arkana",
				"aster_phoenix": "Aster Phoenix",
				"axel_brodie": "Axel Brodie",
				"bastion_misaw": "Bastion Misawa",
				"beast": "Bête",
				"beast_warrior": "Bête-Guerrier",
				"bonz": "Bonz",
				"camula": "Camula",
				"chazz_princet": "Chazz Princeton",
				"christine": "Christine",
				"chumley_huffi": "Chumley Huffington",
				"continuous": "Continue",
				"counter": "Contre",
				"creator_god": "Dieu Créateur",
				"cyberse": "Cyberse",
				"david": "David",
				"dinosaur": "Dinosaure",
				"divine_beast": "Bête Divine",
				"don_zaloog": "Don Zaloog",
				"dr_vellian_c": "Dr. Vellian Crowler",
				"dragon": "Dragon",
				"emma": "Emma",
				"equip": "Équipement",
				"espa_roba": "Espa Roba",
				"fairy": "Elfe",
				"field": "Terrain",
				"fiend": "Démon",
				"fish": "Poisson",
				"illusion": "Illusion",
				"insect": "Insecte",
				"ishizu": "Ishizu",
				"ishizu_ishtar": "Ishizu Ishtar",
				"jaden_yuki": "Jaden Yuki",
				"jesse_anderso": "Jesse Anderson",
				"joey": "Joey",
				"joey_wheeler": "Joey Wheeler",
				"kagemaru": "Kagemaru",
				"kaiba": "Kaiba",
				"keith": "Keith",
				"lumis_umbra": "Lumis Umbra",
				"lumis_and_umb": "Lumis et Umbra",
				"machine": "Machine",
				"mai": "Mai",
				"mai_valentine": "Mai Valentine",
				"mako": "Mako",
				"nightshroud": "Nightshroud",
				"normal": "Normal",
				"odion": "Odion",
				"paradox_broth": "Frères Paradox",
				"pegasus": "Pegasus",
				"plant": "Plante",
				"psychic": "Psychique",
				"pyro": "Pyro",
				"quick_play": "Jeu Rapide",
				"reptile": "Reptile",
				"rex": "Rex",
				"ritual": "Rituel",
				"rock": "Rocher",
				"sea_serpent": "Serpent de Mer",
				"seto_kaiba": "Seto Kaiba",
				"spellcaster": "Magicien",
				"syrus_truesda": "Syrus Truesdale",
				"tania": "Tania",
				"tea_gardner": "Tea Gardner",
				"the_supreme_k": "Le Roi Suprême",
				"thelonious_vi": "Thelonious Viper",
				"thunder": "Tonnerre",
				"titan": "Titan",
				"tyranno_hassl": "Tyranno Hassleberry",
				"warrior": "Guerrier",
				"weevil": "Weevil",
				"winged_beast": "Bête Ailée",
				"wyrm": "Wyrm",
				"yami_bakura": "Yami Bakura",
				"yami_marik": "Yami Marik",
				"yami_yugi": "Yami Yugi",
				"yubel": "Yubel",
				"yugi": "Yugi",
				"zane_truesdal": "Zane Truesdale",
				"zombie": "Zombie"
			}
		},
		"rarity": {
			"name": "Rareté",
			"description": "La rareté de la carte",
			"values": {
				"common": "Commune",
				"rare": "Rare",
				"super_rare": "Super Rare",
				"ultra_rare": "Ultra Rare",
				"secret_rare": "Secret Rare",
				"ultimate_rare": "Ultimate Rare",
				"ghost_rare": "Ghost Rare",
				"ghostgold_rare": "Ghost/Gold Rare",
				"gold_rare": "Gold Rare",
				"gold_secret_rare": "Gold Secret Rare",
				"premium_gold_rare": "Premium Gold Rare",
				"platinum_rare": "Platine Rare",
				"platinum_secret_rare": "Platine Secret Rare",
				"prismatic_secret_rare": "Prismatique Secret Rare",
				"prismatic_ultimate_rare": "Prismatique Ultimate Rare",
				"prismatic_collectors_rare": "Rare de Collection Prismatique",
				"quarter_century_secret_rare": "Quart de Siècle Secret Rare",
				"10000_secret_rare": "10000 Secret Rare",
				"starlight_rare": "Starlight Rare",
				"collectors_rare": "Rare de Collection",
				"extra_secret_rare": "Extra Secret Rare",
				"ultra_secret_rare": "Ultra Secret Rare",
				"starfoil_rare": "Starfoil Rare",
				"shatterfoil_rare": "Shatterfoil Rare",
				"mosaic_rare": "Mosaïque Rare",
				"holographic_rare": "Holographique Rare",
				"parallel_rare": "Parallèle Rare",
				"normal_parallel_rare": "Normal Parallèle Rare",
				"super_parallel_rare": "Super Parallèle Rare",
				"ultra_parallel_rare": "Ultra Parallèle Rare",
				"secret_parallel_rare": "Secret Parallèle Rare",
				"duel_terminal_normal_parallel_rare": "Duel Terminal Normal Parallèle Rare",
				"duel_terminal_rare_parallel_rare": "Duel Terminal Rare Parallèle Rare",
				"duel_terminal_super_parallel_rare": "Duel Terminal Super Parallèle Rare",
				"duel_terminal_ultra_parallel_rare": "Duel Terminal Ultra Parallèle Rare",
				"duel_terminal_secret_parallel_rare": "Duel Terminal Secret Parallèle Rare",
				"duel_terminal_technology_common": "Duel Terminal Technology Commune",
				"duel_terminal_technology_ultra_rare": "Duel Terminal Technology Ultra Rare",
				"emblazoned_rare": "Emblazoned Rare",
				"emblazoned_ultra_rare": "Emblazoned Ultra Rare",
				"emblazoned_secret_rare": "Emblazoned Secret Rare",
				"ultra_pharaohs_rare": "Ultra Rare du Pharaon",
				"secret_pharaohs_rare": "Secret Rare du Pharaon",
				"millennium_rare": "Millénaire Rare",
				"millennium_super_rare": "Millénaire Super Rare",
				"millennium_ultra_rare": "Millénaire Ultra Rare",
				"millennium_secret_rare": "Millénaire Secret Rare",
				"millennium_gold_rare": "Millénaire Gold Rare",
				"grand_master_rare": "Grand Master Rare",
				"20th_secret_rare": "20th Secret Rare",
				"kaiba_corporation_common": "Kaiba Corporation Common",
				"kaiba_corporation_rare": "Kaiba Corporation Rare",
				"kaiba_corporation_ultra_rare": "Kaiba Corporation Ultra Rare",
				"short_print": "Tirage Court",
				"super_short_print": "Super Tirage Court",
				"speed_duel_skill": "Compétence Speed Duel",
				"token": "Jeton",
				"promo": "Promo"
			}
		},
		"printedCodes": {
			"name": "Codes imprimés",
			"description": "Tous les codes d'extension imprimés pour cette carte selon les régions et générations"
		},
		"cardmarketVersion": {
			"name": "Version Cardmarket",
			"description": "Distingue les versions Cardmarket ayant la même extension, le même numéro et la même rareté"
		},
		"variant": {
			"name": "Variante",
			"description": "Variante d'illustration, de couleur ou de tampon de l'impression",
			"values": {
				"alternate_art": "Illustration alternative",
				"new_art": "Nouvelle illustration",
				"art_7": "7e illustration",
				"art_8": "8e illustration",
				"hero_art": "Illustration HERO",
				"extended_art": "Illustration étendue",
				"japanese_artwork": "Illustration japonaise",
				"blue": "Bleu",
				"green": "Vert",
				"purple": "Violet",
				"red": "Rouge",
				"silver": "Argent",
				"bronze": "Bronze",
				"yellow": "Jaune",
				"orange": "Orange",
				"black": "Noir",
				"ots_stamp": "Tampon OTS",
				"judge_stamp": "Tampon Juge",
				"regional_stamp": "Tampon Régional",
				"stamp": "Tampon",
				"misprint": "Erreur d'impression",
				"oversized": "Surdimensionnée",
				"limited_edition": "Édition limitée",
				"reprint": "Réimpression",
				"classic_style": "Style classique",
				"logo": "Logo",
				"original_layout": "Mise en page originale"
			}
		},
		"linkMarkers": {
			"name": "Flèches Lien",
			"description": "Flèches Lien d'un monstre Lien",
			"values": {
				"top_left": "Haut-Gauche",
				"top": "Haut",
				"top_right": "Haut-Droite",
				"left": "Gauche",
				"right": "Droite",
				"bottom_left": "Bas-Gauche",
				"bottom": "Bas",
				"bottom_right": "Bas-Droite"
			}
		},
		"level": {
			"name": "Niveau",
			"description": "Niveau du monstre"
		},
		"rank": {
			"name": "Rang",
			"description": "Rang d'un monstre Xyz"
		},
		"linkRating": {
			"name": "Valeur Lien",
			"description": "Valeur Lien d'un monstre Lien"
		},
		"scale": {
			"name": "Échelle Pendule",
			"description": "Valeur de l'échelle Pendule"
		},
		"atk": {
			"name": "ATK",
			"description": "Points d'attaque du monstre"
		},
		"def": {
			"name": "DEF",
			"description": "Points de défense du monstre"
		},
		"archetype": {
			"name": "Archétype",
			"description": "Archétype de la carte (ex: Yeux Bleus, Magicien Sombre)"
		},
		"desc": {
			"name": "Description",
			"description": "Texte d'effet et description de la carte"
		}
	},
	presets: {
		"standard": {
			"name": "Extension standard",
			"description": "Une extension complète avec un exemplaire de chaque carte dans sa rareté la plus basse"
		},
		"playset": {
			"name": "Playset",
			"description": "Une extension complète avec trois exemplaires de chaque carte dans sa rareté la plus basse"
		},
		"foil": {
			"name": "Cartes foil",
			"description": "Toutes les cartes de l'extension qui existent en foil (Super Rare et au-delà, Starfoil et autres parallèles), en un exemplaire dans leur rareté foil la plus basse. Les Communes et Rares sans version foil ne sont pas incluses."
		},
		"foilPlayset": {
			"name": "Playset foil",
			"description": "Trois exemplaires de chaque carte de l'extension qui existe en foil, dans sa rareté foil la plus basse."
		},
		"masterset": {
			"name": "Masterset",
			"description": "Une extension complète avec un exemplaire de chaque impression, dans toutes les raretés et variantes"
		}
	}
};

//#endregion
//#region src/lib/game-translations.ts
const gameTranslationsMap = {
	mtg: {
		[Locale.English]: en_default$12,
		[Locale.French]: fr_default$12
	},
	lorcana: {
		[Locale.English]: en_default$13,
		[Locale.French]: fr_default$13
	},
	fab: {
		[Locale.English]: en_default$16,
		[Locale.French]: fr_default$16
	},
	onepiece: {
		[Locale.English]: en_default$10,
		[Locale.French]: fr_default$10
	},
	pokemon: {
		[Locale.English]: en_default$8,
		[Locale.French]: fr_default$8
	},
	sorcery: {
		[Locale.English]: en_default$4,
		[Locale.French]: fr_default$4
	},
	wankul: {
		[Locale.English]: en_default$2,
		[Locale.French]: fr_default$2
	},
	ygo: {
		[Locale.English]: en_default$1,
		[Locale.French]: fr_default$1
	},
	"ygo-ocg": {
		[Locale.English]: en_default,
		[Locale.French]: fr_default
	},
	rise: {
		[Locale.English]: en_default$5,
		[Locale.French]: fr_default$5
	},
	riftbound: {
		[Locale.English]: en_default$6,
		[Locale.French]: fr_default$6
	},
	drakerion: {
		[Locale.English]: en_default$19,
		[Locale.French]: fr_default$19
	},
	cyberpunk: {
		[Locale.English]: en_default$22,
		[Locale.French]: fr_default$22
	},
	"pokemon-japan": {
		[Locale.English]: en_default$7,
		[Locale.French]: fr_default$7
	},
	swu: {
		[Locale.English]: en_default$3,
		[Locale.French]: fr_default$3
	},
	"naruto-mythos": {
		[Locale.English]: en_default$11,
		[Locale.French]: fr_default$11
	},
	"grand-archive": {
		[Locale.English]: en_default$15,
		[Locale.French]: fr_default$15
	},
	eoa: {
		[Locale.English]: en_default$17,
		[Locale.French]: fr_default$17
	},
	"dbs-fusion": {
		[Locale.English]: en_default$21,
		[Locale.French]: fr_default$21
	},
	"dbs-masters": {
		[Locale.English]: en_default$20,
		[Locale.French]: fr_default$20
	},
	gundam: {
		[Locale.English]: en_default$14,
		[Locale.French]: fr_default$14
	},
	"chrono-core": {
		[Locale.English]: en_default$23,
		[Locale.French]: fr_default$23
	},
	palworld: {
		[Locale.English]: en_default$9,
		[Locale.French]: fr_default$9
	},
	azuki: {
		[Locale.English]: en_default$24,
		[Locale.French]: fr_default$24
	},
	dreamland: {
		[Locale.English]: en_default$18,
		[Locale.French]: fr_default$18
	}
};

//#endregion
//#region src/lib/importer-user-agent.ts
/** Scryfall and TCGCSV reject a generic User-Agent with a 400. */
const IMPORTER_USER_AGENT = "CardNexus-Importer/1.0 (contact@cardnexus.com)";

//#endregion
//#region src/lib/games/azuki/azuki.seo.ts
const azukiSeoConfig = {
	detailKeys: [
		"category",
		"rarity",
		"element"
	],
	cardTextKeys: ["cardText"],
	additionalPropertyKeys: [
		"category",
		"rarity",
		"element"
	]
};

//#endregion
//#region src/lib/games/chrono-core/chrono-core.seo.ts
const chronoCoreSeoConfig = {
	detailKeys: [
		"cardType",
		"realm",
		"coreCost",
		"damageValue",
		"maxLifeValue"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"cardType",
		"subtype",
		"chassisType",
		"realm",
		"damageType",
		"mkValue",
		"rarity",
		"coreCost",
		"damageValue",
		"chargeValue",
		"maxShieldValue",
		"maxLifeValue"
	]
};

//#endregion
//#region src/lib/games/dreamland/dreamland.seo.ts
const dreamlandSeoConfig = {
	detailKeys: [
		"cardType",
		"rarity",
		"cardClass",
		"keywords"
	],
	cardTextKeys: ["abilityName", "rulesText"],
	additionalPropertyKeys: [
		"cardType",
		"rarity",
		"cardClass",
		"aura",
		"force"
	]
};

//#endregion
//#region src/lib/games/eoa/eoa.seo.ts
const eoaSeoConfig = {
	detailKeys: [
		"cardType",
		"affinity",
		"supplyCost",
		"attack",
		"health"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"cardType",
		"affinity",
		"rarity",
		"supplyCost",
		"influence",
		"attack",
		"health"
	]
};

//#endregion
//#region src/lib/games/palworld/palworld.seo.ts
const palworldSeoConfig = {
	detailKeys: [
		"cardKind",
		"rarity",
		"color"
	],
	cardTextKeys: ["rulesText", "flavorText"],
	additionalPropertyKeys: [
		"cardKind",
		"rarity",
		"color"
	]
};

//#endregion
//#region src/lib/games/pokemon/pokemon.seo.ts
const pokemonSeoConfig = {
	detailKeys: [
		"pokemonTypes",
		"hp",
		"stage",
		"cardType"
	],
	cardTextKeys: ["cardText"],
	additionalPropertyKeys: [
		"pokemonTypes",
		"hp",
		"stage",
		"cardType"
	],
	expansionCodeIsSearchable: true
};

//#endregion
//#region src/lib/games/pokemon-japan/pokemon-japan.seo.ts
const pokemonJapanSeoConfig = {
	detailKeys: [
		"pokemonTypes",
		"hp",
		"stage",
		"cardType",
		"retreatCost"
	],
	cardTextKeys: ["description"],
	additionalPropertyKeys: [
		"pokemonTypes",
		"hp",
		"stage",
		"cardType",
		"retreatCost"
	],
	expansionCodeIsSearchable: true
};

//#endregion
//#region src/lib/games/yugioh/yugioh.seo.ts
const yugiohSeoConfig = {
	detailKeys: [
		"type",
		"attribute",
		"race",
		"level",
		"atk",
		"archetype"
	],
	cardTextKeys: ["desc"],
	additionalPropertyKeys: [
		"type",
		"attribute",
		"race",
		"level",
		"rank",
		"linkRating",
		"atk",
		"def",
		"archetype",
		"rarity"
	],
	compositeDetails: { atk: "def" },
	expansionCodeIsSearchable: true
};

//#endregion
//#region src/lib/games/yugioh-ocg/yugioh-ocg.seo.ts
const yugiohOcgSeoConfig = yugiohSeoConfig;

//#endregion
//#region src/lib/finish-source.ts
/**
* Where a game's finish set comes from.
*
* - `authoritative`: the catalogue source states finishes (Scryfall, curated
*   sheets, official APIs, rarity rules). A removal is a correction and flows
*   through the import.
* - `listing-derived`: finishes are read from TCGCSV's `ProductsAndPrices`
*   feed, a LEFT JOIN of the catalogue against live TCGplayer listings. An
*   import proves a finish exists but never that one stopped existing, so the
*   diff folds finishes add-only.
* - `tcgplayer-skus`: same catalogue feed, but the TCGplayer SKU list is the
*   finish source: a product it represents gets its exact finish set, so a
*   removal can reach the review, and a product it has no result for stages
*   no finishes at all. The import fails when the list cannot be read.
*/
const FinishSource = {
	Authoritative: "authoritative",
	ListingDerived: "listing-derived",
	TcgplayerSkus: "tcgplayer-skus"
};
const FINISH_SOURCE = {
	[SupportedGames.MTG]: FinishSource.Authoritative,
	[SupportedGames.Riftbound]: FinishSource.Authoritative,
	[SupportedGames.Lorcana]: FinishSource.Authoritative,
	[SupportedGames.Sorcery]: FinishSource.Authoritative,
	[SupportedGames.FAB]: FinishSource.Authoritative,
	[SupportedGames.Pokemon]: FinishSource.TcgplayerSkus,
	[SupportedGames.Wankul]: FinishSource.Authoritative,
	[SupportedGames.OnePiece]: FinishSource.TcgplayerSkus,
	[SupportedGames.YuGiOh]: FinishSource.Authoritative,
	[SupportedGames.ExampleGame]: FinishSource.TcgplayerSkus,
	[SupportedGames.Rise]: FinishSource.Authoritative,
	[SupportedGames.Drakerion]: FinishSource.Authoritative,
	[SupportedGames.Cyberpunk]: FinishSource.Authoritative,
	[SupportedGames.PokemonJapan]: FinishSource.TcgplayerSkus,
	[SupportedGames.StarWarsUnlimited]: FinishSource.ListingDerived,
	[SupportedGames.NarutoMythos]: FinishSource.Authoritative,
	[SupportedGames.GrandArchive]: FinishSource.Authoritative,
	[SupportedGames.Eoa]: FinishSource.Authoritative,
	[SupportedGames.DbsFusion]: FinishSource.TcgplayerSkus,
	[SupportedGames.DbsMasters]: FinishSource.TcgplayerSkus,
	[SupportedGames.Gundam]: FinishSource.TcgplayerSkus,
	[SupportedGames.ChronoCore]: FinishSource.Authoritative,
	[SupportedGames.Palworld]: FinishSource.Authoritative,
	[SupportedGames.Azuki]: FinishSource.Authoritative,
	[SupportedGames.YuGiOhOcg]: FinishSource.Authoritative,
	[SupportedGames.Dreamland]: FinishSource.Authoritative
};
function isSupportedGame(gameSlug) {
	return Object.hasOwn(FINISH_SOURCE, gameSlug);
}
/**
* Finish source of a game; an unknown slug is treated as authoritative so no
* fold applies to it.
*/
function getFinishSource(gameSlug) {
	return isSupportedGame(gameSlug) ? FINISH_SOURCE[gameSlug] : FinishSource.Authoritative;
}
/** Whether the TCGplayer SKU list is the finish source for the game. */
function usesTcgplayerSkus(gameSlug) {
	return getFinishSource(gameSlug) === FinishSource.TcgplayerSkus;
}

//#endregion
//#region src/lib/grading/get-certification-url.ts
/**
* Generates a verification URL for a graded card certification based on the grading service.
*
* @param gradingService - The grading service (PSA, Beckett, CGC, TAG)
* @param certificationNumber - The certification/serial number from the graded card
* @returns The URL to verify the certification, or null if the service is not supported
*
* @example
* ```ts
* getCertificationUrl("PSA", "24311536") // Returns: "https://www.psacard.com/cert/24311536"
* getCertificationUrl("CGC", "1400294317663") // Returns: "https://www.cgccards.com/certlookup/1400294317663/"
* getCertificationUrl("TAG", "100000th") // Returns: "https://my.taggrading.com/card/100000th"
* getCertificationUrl("Beckett", "12345") // Returns: "https://www.beckett.com/grading/card-lookup" (general lookup page)
* ```
*/
function getCertificationUrl(gradingService, certificationNumber) {
	if (!certificationNumber) return null;
	switch (gradingService) {
		case SupportedGradingService.PSA: return `https://www.psacard.com/cert/${certificationNumber}`;
		case SupportedGradingService.CGC: return `https://www.cgccards.com/certlookup/${certificationNumber}/`;
		case SupportedGradingService.TAG: return `https://my.taggrading.com/card/${certificationNumber}`;
		case SupportedGradingService.Beckett: return `https://www.beckett.com/grading/card-lookup?item_id=${certificationNumber}`;
		case SupportedGradingService.PCG: return `https://pcgpopreport.com/report/${certificationNumber}`;
		case SupportedGradingService.CCC: return `https://cccgrading.com/fr/verification-carte-ccc?cert=${certificationNumber}`;
		case SupportedGradingService.PCA: return "https://pcagrade.com/en/check-certification";
		case SupportedGradingService.CollectAura: return `https://www.collectaura.com/pages/cardsteps-view-card?serialnumber=${certificationNumber}`;
		case SupportedGradingService.MTGGrade: return "https://www.mtggrade.com/en/check-the-authenticity-of-a-card/";
		case SupportedGradingService.PureGrading: return `https://app.puregrading.com/authenticite/${certificationNumber}`;
		case SupportedGradingService.SGS: return `https://sgscards.com/cardpage/${certificationNumber}`;
		case SupportedGradingService.AceGrading: return `https://acegrading.com/cert/${certificationNumber}`;
		default: return null;
	}
}

//#endregion
//#region src/lib/grading/grading-standards.ts
/**
* PSA Grading Scale
* Professional Sports Authenticator (PSA) uses a 1-10 scale
* Source: https://www.psacard.com/resources/gradingstandards
*/
const PSAGradingScale = {
	10: {
		grade: 10,
		description: "Gem Mint - Pristine condition. No signs of wear, perfectly centered, four sharp corners, sharp focus and original gloss.",
		category: "Gem Mint"
	},
	9: {
		grade: 9,
		description: "Mint - A superb card with only one minor flaw. Slight wax stain, slight off-white borders, or slight rotation is acceptable.",
		category: "Mint"
	},
	8: {
		grade: 8,
		description: "Near Mint-Mint - A super premium card that appears Mint 9 at first glance but upon closer inspection, has a slight flaw that prevents it from grading higher.",
		category: "Near Mint-Mint"
	},
	7: {
		grade: 7,
		description: "Near Mint - A card with minor surface or corner wear. May have slight edge wear or slightly off-white borders.",
		category: "Near Mint"
	},
	6: {
		grade: 6,
		description: "Excellent-Mint - A card that may show light surface wear or one or two slight corner touches, minor border wear or slight off-whiteness.",
		category: "Excellent-Mint"
	},
	5: {
		grade: 5,
		description: "Excellent - Minor rounding of corners, minor surface wear, slight edge wear, minor printing defects, minor scratches or creases.",
		category: "Excellent"
	},
	4: {
		grade: 4,
		description: "Very Good-Excellent - Noticeable edge or corner wear, minor creases, and/or slight discoloration possible.",
		category: "Very Good-Excellent"
	},
	3: {
		grade: 3,
		description: "Very Good - Noticeable corner or surface wear, creasing, minor scuffing, minor notching or slightly off-center.",
		category: "Very Good"
	},
	2: {
		grade: 2,
		description: "Good - Significant corner, edge and surface wear, creasing, scuffing, minor tears, slight discoloration possible.",
		category: "Good"
	},
	1: {
		grade: 1,
		description: "Poor - Exhibits substantial wear, possible missing paper, heavy creases, major tears or other damage.",
		category: "Poor"
	}
};
/**
* Beckett Grading Scale
* Beckett Grading Services (BGS) uses a 1-10 scale with half-point increments
* Source: https://www.beckett.com/grading/standards
*/
const BeckettGradingScale = {
	"10B": {
		grade: 10,
		sortOrder: 10.1,
		description: "Black Label - Perfect in every way. All four subgrades (centering, corners, edges, surface) must each score a perfect 10.",
		category: "Black Label"
	},
	10: {
		grade: 10,
		description: "Pristine - Virtually perfect in every way. 50/50 centering, four sharp corners, sharp focus and free of stains.",
		category: "Pristine"
	},
	9.5: {
		grade: 9.5,
		description: "Gem Mint - Card appears perfect at first glance. 55/45 to 60/40 centering, four sharp corners, free of stains.",
		category: "Gem Mint"
	},
	9: {
		grade: 9,
		description: "Mint - A virtually perfect card with only one slight flaw. 60/40 to 65/35 centering or slight print defect.",
		category: "Mint"
	},
	8.5: {
		grade: 8.5,
		description: "Near Mint-Mint+ - Slightly superior to Near Mint-Mint with only 1-2 minor flaws.",
		category: "NM-MT+"
	},
	8: {
		grade: 8,
		description: "Near Mint-Mint - An exceptional card that displays minor wear or slight printing defects.",
		category: "NM-MT"
	},
	7.5: {
		grade: 7.5,
		description: "Near Mint+ - Better than Near Mint but not quite Near Mint-Mint.",
		category: "NM+"
	},
	7: {
		grade: 7,
		description: "Near Mint - Very light wear visible on close inspection. May have slight corner touches or minor print defects.",
		category: "NM"
	},
	6.5: {
		grade: 6.5,
		description: "Excellent-Mint+ - Between Excellent-Mint and Near Mint.",
		category: "EX-MT+"
	},
	6: {
		grade: 6,
		description: "Excellent-Mint - Only slight surface wear visible, minor rounding of corners, and possibly slight edge wear.",
		category: "EX-MT"
	},
	5.5: {
		grade: 5.5,
		description: "Excellent+ - Between Excellent and Excellent-Mint.",
		category: "EX+"
	},
	5: {
		grade: 5,
		description: "Excellent - Minor rounding of corners, surface wear visible, slight edge wear, and possibly minor scratches.",
		category: "EX"
	},
	4.5: {
		grade: 4.5,
		description: "Very Good-Excellent+ - Between Very Good-Excellent and Excellent.",
		category: "VG-EX+"
	},
	4: {
		grade: 4,
		description: "Very Good-Excellent - Noticeable corner and edge wear, possible minor creasing.",
		category: "VG-EX"
	},
	3.5: {
		grade: 3.5,
		description: "Very Good+ - Between Very Good and Very Good-Excellent.",
		category: "VG+"
	},
	3: {
		grade: 3,
		description: "Very Good - Significant corner wear, edge scuffing, possible minor tears or creasing.",
		category: "VG"
	},
	2.5: {
		grade: 2.5,
		description: "Good+ - Between Good and Very Good.",
		category: "G+"
	},
	2: {
		grade: 2,
		description: "Good - Heavy corner, edge, and surface wear, with possible creasing, scuffing, or minor tears.",
		category: "G"
	},
	1.5: {
		grade: 1.5,
		description: "Fair+ - Between Fair and Good.",
		category: "F+"
	},
	1: {
		grade: 1,
		description: "Fair - Heavily worn with major creases, tears, staining, or writing that does not obscure important features.",
		category: "F"
	},
	AUTH: {
		grade: 0,
		description: "Authentic - The card is genuine but may have significant damage, alterations, or restoration that prevents it from receiving a numeric grade.",
		category: "Authentic"
	}
};
/**
* CGC Grading Scale
* Collectible Grading Company (CGC) uses a 1-10 scale with half-point increments
* Source: https://www.cgccards.com/card-grading/grading-scale/
*/
const CGCGradingScale = {
	"10P": {
		grade: 10,
		sortOrder: 10.1,
		description: "Pristine - A flawless card with perfect centering, corners, edges, and surface. Must be flawless under 10x magnification.",
		category: "Pristine"
	},
	10: {
		grade: 10,
		description: "Gem Mint - A card that receives an overall grade of 10 where one of the criteria doesn't meet the Pristine 10 standard. Near-perfect centering, corners, edges, and surface.",
		category: "Gem Mint"
	},
	9.5: {
		grade: 9.5,
		description: "Mint+ - Nearly perfect with only a few minor imperfections.",
		category: "Mint+"
	},
	9: {
		grade: 9,
		description: "Mint - Appears perfect at first glance, but has minor imperfections upon close inspection.",
		category: "Mint"
	},
	8.5: {
		grade: 8.5,
		description: "Near Mint/Mint+ - Just short of Mint condition.",
		category: "NM/M+"
	},
	8: {
		grade: 8,
		description: "Near Mint/Mint - Slight imperfections, but still excellent condition.",
		category: "NM/M"
	},
	7.5: {
		grade: 7.5,
		description: "Near Mint+ - Very minor wear, slightly better than Near Mint.",
		category: "NM+"
	},
	7: {
		grade: 7,
		description: "Near Mint - Minor wear visible on close inspection.",
		category: "NM"
	},
	6.5: {
		grade: 6.5,
		description: "Excellent/Near Mint+ - Between Excellent and Near Mint.",
		category: "EX/NM+"
	},
	6: {
		grade: 6,
		description: "Excellent - Light wear, minor edge and corner wear visible.",
		category: "EX"
	},
	5.5: {
		grade: 5.5,
		description: "Excellent- - Slightly more wear than an Excellent card.",
		category: "EX-"
	},
	5: {
		grade: 5,
		description: "Very Good/Excellent - Moderate wear, rounded corners, but still appealing.",
		category: "VG/EX"
	},
	4.5: {
		grade: 4.5,
		description: "Very Good+ - Better than Very Good but not quite VG/EX.",
		category: "VG+"
	},
	4: {
		grade: 4,
		description: "Very Good - Significant wear, rounded corners, possible creasing.",
		category: "VG"
	},
	3: {
		grade: 3,
		description: "Good - Heavy wear, scuffing, rounded corners, possible minor tears.",
		category: "G"
	},
	2: {
		grade: 2,
		description: "Fair - Excessive wear, possible severe creasing, large tears, or tape.",
		category: "F"
	},
	1: {
		grade: 1,
		description: "Poor - Severe damage but still complete and recognizable.",
		category: "PR"
	},
	A: {
		grade: 0,
		description: "Authentic - The card is genuine but in such poor condition it can only be certified as authentic.",
		category: "Authentic"
	}
};
/**
* PCG Grading Scale (Premier Card Grading)
* PCG uses a 1-10 scale with half-point increments
* Notable: PCG has three tiers of 10 grades (Flawless > Pristine > Gem Mint)
* We use "10F", "10P", 10 as keys - sortOrder field handles proper ordering
* Source: https://premiercardgrading.com/pages/grading-standards
*/
const PCGGradingScale = {
	"10F": {
		grade: 10,
		sortOrder: 10.2,
		description: "Flawless - The highest possible grade. Absolutely perfect in every aspect with no visible flaws under magnification.",
		category: "Flawless"
	},
	"10P": {
		grade: 10,
		sortOrder: 10.1,
		description: "Pristine - Near-perfect condition with virtually no visible flaws. Exceptional quality in all aspects.",
		category: "Pristine"
	},
	10: {
		grade: 10,
		description: "Gem Mint - Outstanding condition with minimal imperfections. Excellent centering, sharp corners, and pristine surface.",
		category: "Gem Mint"
	},
	9.5: {
		grade: 9.5,
		description: "Gem Mint - Near-perfect with only the slightest imperfections visible under close inspection.",
		category: "Gem Mint"
	},
	9: {
		grade: 9,
		description: "Mint - Excellent condition with minor imperfections. Very slight corner wear or minor centering issues may be present.",
		category: "Mint"
	},
	8.5: {
		grade: 8.5,
		description: "NM-Mint+ - Superior to Near Mint-Mint with only slight wear visible.",
		category: "NM-Mint+"
	},
	8: {
		grade: 8,
		description: "NM-Mint - Very minor wear visible on close inspection. Corners may show slight touches.",
		category: "NM-Mint"
	},
	7.5: {
		grade: 7.5,
		description: "Near Mint+ - Better than Near Mint with minimal visible wear.",
		category: "Near Mint+"
	},
	7: {
		grade: 7,
		description: "Near Mint - Minor wear visible. Slight corner or edge wear may be present.",
		category: "Near Mint"
	},
	6: {
		grade: 6,
		description: "Excellent - Light wear visible on corners and edges. May have minor surface wear.",
		category: "Excellent"
	},
	5: {
		grade: 5,
		description: "Good - Moderate wear visible. Rounded corners, surface scratches, or minor creasing may be present.",
		category: "Good"
	},
	4: {
		grade: 4,
		description: "Fair - Noticeable wear and possible damage. May have creases, scuffing, or discoloration.",
		category: "Fair"
	},
	3: {
		grade: 3,
		description: "Worn - Significant wear throughout. Heavy corner and edge wear, possible creases or tears.",
		category: "Worn"
	},
	2: {
		grade: 2,
		description: "Poor - Heavy wear and damage. Major creases, tears, or staining may be present.",
		category: "Poor"
	},
	1: {
		grade: 1,
		description: "Damaged - Severe damage but card is still intact and recognizable. Major tears, heavy creasing, or significant staining.",
		category: "Damaged"
	}
};
/**
* CCC Grading Scale (Certificateur de Cartes de Collection)
* CCC uses a 1-10 scale with special Black Label and Gold Label tiers for 10
* Source: https://cccgrading.com/fr/echelle-notation
*/
const CCCGradingScale = {
	"10B": {
		grade: 10,
		sortOrder: 10.2,
		description: "Black Label - Perfect in every aspect. Card must score 10 on all four grading criteria (corners, edges, surface, centering).",
		category: "Black Label"
	},
	"10G": {
		grade: 10,
		sortOrder: 10.1,
		description: "Gold Label - Near-perfect condition. Card must score 10 on three criteria and 9.5 on one criterion.",
		category: "Gold Label"
	},
	10: {
		grade: 10,
		description: "Gem Mint - Exceptional condition. Very close to perfect with only the slightest imperfections.",
		category: "Gem Mint"
	},
	9.5: {
		grade: 9.5,
		description: "Gem Mint - Exceptional condition with minimal imperfections. Near-perfect in all aspects.",
		category: "Gem Mint"
	},
	9: {
		grade: 9,
		description: "Mint - Excellent condition with very minor imperfections visible only under close inspection.",
		category: "Mint"
	},
	8.5: {
		grade: 8.5,
		description: "Near Mint - Very light wear, slightly better than Excellent-NM.",
		category: "Near Mint"
	},
	8: {
		grade: 8,
		description: "Excellent-NM - Minor wear visible on close inspection. Very good overall condition.",
		category: "Exc-NM"
	},
	7: {
		grade: 7,
		description: "Excellent - Light wear visible on corners and edges. Good overall appearance.",
		category: "Excellent"
	},
	6: {
		grade: 6,
		description: "Very Good - Moderate wear visible. Some corner and edge wear present.",
		category: "Very Good"
	},
	5: {
		grade: 5,
		description: "Good - Noticeable wear throughout. Rounded corners, surface wear visible.",
		category: "Good"
	},
	4: {
		grade: 4,
		description: "Fair - Significant wear and possible minor damage. Creasing or scuffing may be present.",
		category: "Fair"
	},
	3: {
		grade: 3,
		description: "Lightly Played - Heavy wear throughout. Notable creases, edge wear, or surface damage.",
		category: "Light Play"
	},
	2: {
		grade: 2,
		description: "Played/Poor - Severe wear with major creases, tears, or staining.",
		category: "LP-PR"
	},
	1: {
		grade: 1,
		description: "Poor - Extreme damage but card is still intact and recognizable.",
		category: "Poor"
	}
};
/**
* TAG Grading Scale (Trophy Authentic Grading)
* TAG uses a 1-10 scale with half-point increments
* Source: Based on available information about TAG grading
*/
const TAGGradingScale = {
	10: {
		grade: 10,
		description: "Pristine - Perfect in every way with 50/50 centering, four pristine corners, pristine edges, perfect surface, and perfect print quality.",
		category: "Pristine"
	},
	9.5: {
		grade: 9.5,
		description: "Gem Mint - Near perfect with minimal factory flaws.",
		category: "Gem Mint"
	},
	9: {
		grade: 9,
		description: "Mint - Only one slight factory imperfection from perfect condition.",
		category: "Mint"
	},
	8.5: {
		grade: 8.5,
		description: "Near Mint-Mint+ - Slightly better than Near Mint-Mint.",
		category: "NM-MT+"
	},
	8: {
		grade: 8,
		description: "Near Mint-Mint - Very minor wear or factory defects.",
		category: "NM-MT"
	},
	7.5: {
		grade: 7.5,
		description: "Near Mint+ - Better than Near Mint with minimal wear.",
		category: "NM+"
	},
	7: {
		grade: 7,
		description: "Near Mint - Slight wear visible on close inspection.",
		category: "NM"
	},
	6: {
		grade: 6,
		description: "Excellent-Mint - Minor wear to corners, edges or surface.",
		category: "EX-MT"
	},
	5: {
		grade: 5,
		description: "Excellent - Noticeable corner and edge wear.",
		category: "EX"
	},
	4: {
		grade: 4,
		description: "Very Good-Excellent - Obvious wear and possible minor creasing.",
		category: "VG-EX"
	},
	3: {
		grade: 3,
		description: "Very Good - Significant edge wear, corner rounding, and surface wear.",
		category: "VG"
	},
	2: {
		grade: 2,
		description: "Good - Heavy wear with possible creases or tears.",
		category: "G"
	},
	1: {
		grade: 1,
		description: "Poor - Extreme wear, creases, stains, or tears, but still an intact and authentic card.",
		category: "PR"
	}
};
/**
* PCA Grading Scale
* French grading company (PCA Grade)
* Uses a 1-10 scale with 9.5 half-point and special 10+ (Collector) and Black Label tiers
* @see https://pcagrade.com/en/ressources#cards
*/
const PCAGradingScale = {
	"10B": {
		grade: 10,
		sortOrder: 10.2,
		description: "Black Label - Perfect card: 10 on every sub-grade (centering, corners, edges, surface).",
		category: "Black Label"
	},
	"10+": {
		grade: 10,
		sortOrder: 10.1,
		description: "Collector - Exceptional quality new card, perfect appearance, very well centered, without any origin error or wear visible.",
		category: "Collector"
	},
	10: {
		grade: 10,
		description: "Gem Mint - Superior quality card, perfect appearance, without any wear, but may present very rare micro defects of origin after thorough inspection.",
		category: "Gem Mint"
	},
	9.5: {
		grade: 9.5,
		description: "Mint - Card seeming to be gem mint but having a few white dots. Thorough inspection may reveal rare and discreet scratches from manipulation.",
		category: "Mint"
	},
	9: {
		grade: 9,
		description: "Near Mint - Card in great condition with rare and small wear marks. May have original defects revealed under adequate lighting.",
		category: "Near Mint"
	},
	8: {
		grade: 8,
		description: "Excellent-NM - Card presenting clear manipulation defects that are numerous and visible (micro scratches, white points, damaged corners and edges, surface deposits).",
		category: "Exc-NM"
	},
	7: {
		grade: 7,
		description: "Excellent - Card with more visible wear, presenting noticeable defects.",
		category: "Excellent"
	},
	6: {
		grade: 6,
		description: "Very Good - Visible wear traces like rounded corners, scratches, and shine loss.",
		category: "Very Good"
	},
	5: {
		grade: 5,
		description: "Good - Important wear traces including damaged edges, rounded corners, and small folds.",
		category: "Good"
	},
	4: {
		grade: 4,
		description: "Light played - Visible wear such as rounded corners, scratches, and gloss loss, while image and text remain intact.",
		category: "Light played"
	},
	3: {
		grade: 3,
		description: "Played - Significant wear such as damaged edges, rounded corners, and small folds.",
		category: "Played"
	},
	2: {
		grade: 2,
		description: "Poor - Very heavy wear with obvious folds and major surface damage.",
		category: "Poor"
	},
	1: {
		grade: 1,
		description: "Very poor - Card has lost most of its visual appeal but may retain sentimental value.",
		category: "Very poor"
	},
	A: {
		grade: 0,
		description: "Authentic - Card authenticated without a numeric grade.",
		category: "Authentic"
	}
};
/**
* CollectAura Grading Scale
* French grading and authentication company
* Uses a 1-10 scale with 9.5 half-point, a special 10P (Perfect 10) tier, and an A (Authentic) grade
* @see https://www.collectaura.com/pages/grading-scale
*/
const CollectAuraGradingScale = {
	"10P": {
		grade: 10,
		sortOrder: 10.1,
		description: "Perfect 10 - Virtually perfect card with all four subgrades (centering, corners, surface, edges) graded 10.",
		category: "Perfect 10"
	},
	10: {
		grade: 10,
		description: "Gem Mint - Card in perfect condition with smooth surface and no visible defects. Colors and cuts are uniform. Slight factory imperfections tolerated.",
		category: "Gem Mint"
	},
	9.5: {
		grade: 9.5,
		description: "Mint+ - Card in near-perfect condition with minor factory or wear defects that do not affect overall appearance.",
		category: "Mint+"
	},
	9: {
		grade: 9,
		description: "Mint - Card in near-perfect condition with minimal flaws such as micro-scratches or small white spots on corners and/or edges.",
		category: "Mint"
	},
	8: {
		grade: 8,
		description: "Near Mint - Visually appealing card despite slight imperfections such as micro-scratches, small white dots, or minor factory defects.",
		category: "Near Mint"
	},
	7: {
		grade: 7,
		description: "Excellent - Good visual appearance but evident wear under inspection, including partially whitened corners and edges, and light to moderate surface scratches.",
		category: "Excellent"
	},
	6: {
		grade: 6,
		description: "Very Good - Moderate and clearly visible wear: noticeably whitened corners and edges, stains, marks, or surface dullness.",
		category: "Very Good"
	},
	5: {
		grade: 5,
		description: "Good - Moderate wear, played condition.",
		category: "Good"
	},
	4: {
		grade: 4,
		description: "Average - Visible wear but still structurally intact: light creases, whitened edges, heavily worn corners, handling marks.",
		category: "Average"
	},
	3: {
		grade: 3,
		description: "Fair - Severe damage: partial tear, deep creases or heavy stains, very worn corners, and heavily altered surface.",
		category: "Fair"
	},
	2: {
		grade: 2,
		description: "Poor - Heavily damaged card with pronounced creases, deep scratches, severely worn corners, visible stains, significant loss of readability.",
		category: "Poor"
	},
	1: {
		grade: 1,
		description: "Very Poor - Missing parts, barely readable text, extreme creasing. Surface and structure are heavily damaged.",
		category: "Very Poor"
	},
	A: {
		grade: 0,
		description: "Authentic - Card is authenticated but not given a numeric grade.",
		category: "Authentic"
	},
	OC: {
		grade: 0,
		description: "Off-Centered - Card suffering from centering greater than 70% on front or back but retaining all original elements.",
		category: "Off-Centered"
	},
	MP: {
		grade: 0,
		description: "Misprint - Card with a printing error in coloration, clarity, or mispositioned elements. May also have vertical deformations of origin.",
		category: "Misprint"
	},
	MC: {
		grade: 0,
		description: "Miscut - Card with an atypical cut, resulting in parts being cut off or more than one card appearing.",
		category: "Miscut"
	},
	CP: {
		grade: 0,
		description: "Crimped - Card with a crush mark caused by packaging machines, often along the edges.",
		category: "Crimped"
	},
	MK: {
		grade: 0,
		description: "Marked - Card with ink marks, pen marks, or traces left by such marks.",
		category: "Marked"
	}
};
/**
* MTG Grade Grading Scale
* French grading and authentication company
* Uses a 1-10 scale with half-point increments and an A (Authentic) grade
* Subgrades: centering, corners, edges, surface (centering weighted less)
* @see https://www.mtggrade.com/en/grading_scale/
*/
const MTGGradeGradingScale = {
	10: {
		grade: 10,
		description: "Perfect - Perfect centering (50%-50%) front and back. A card that has never been played or moved, no marks on the front or back, no factory defects (white point, print line, etc.).",
		category: "Perfect"
	},
	9.5: {
		grade: 9.5,
		description: "Gem Mint - Exceptional card very close to perfection. Only the most minor factory imperfections visible under close inspection.",
		category: "Gem Mint"
	},
	9: {
		grade: 9,
		description: "Mint - Careful examination may reveal a minor flaw (micro-scratch, minor color defect, print line, white point, etc.), or the card may not be perfectly centered. Close to perfection.",
		category: "Mint"
	},
	8.5: {
		grade: 8.5,
		description: "Near Mint+ - A card between Near Mint and Mint condition. Very light wear or minor factory defects visible only under careful inspection.",
		category: "Near Mint+"
	},
	8: {
		grade: 8,
		description: "Near Mint - Very minor surface or corner wear visible under close inspection. May have slight centering issues or minor print defects.",
		category: "Near Mint"
	},
	7.5: {
		grade: 7.5,
		description: "Excellent+ - Between Excellent and Near Mint. Light wear visible but overall very good condition.",
		category: "Excellent+"
	},
	7: {
		grade: 7,
		description: "Excellent - Light surface wear, minor corner touches, or slight edge wear visible. Overall good appearance.",
		category: "Excellent"
	},
	6: {
		grade: 6,
		description: "Very Good - Moderate wear visible including corner rounding, surface scratches, or minor edge damage.",
		category: "Very Good"
	},
	5: {
		grade: 5,
		description: "Good - Noticeable wear throughout. Rounded corners, surface wear, and possible minor creasing.",
		category: "Good"
	},
	4: {
		grade: 4,
		description: "Fair - Significant wear and possible minor damage. Creases, scuffing, or discoloration may be present.",
		category: "Fair"
	},
	3: {
		grade: 3,
		description: "Played - Heavy wear throughout. Notable creases, edge wear, or surface damage.",
		category: "Played"
	},
	2: {
		grade: 2,
		description: "Poor - Severe wear with major creases, tears, or staining. Card is heavily damaged.",
		category: "Poor"
	},
	1: {
		grade: 1,
		description: "Very Poor - Extreme damage but card is still intact and recognizable. Missing parts, barely readable text, extreme creasing.",
		category: "Very Poor"
	},
	A: {
		grade: 0,
		description: "Authentic - Card is authenticated as genuine but not given a numeric grade.",
		category: "Authentic"
	}
};
const PureGradingGradingScale = {
	10: {
		grade: 10,
		description: "Gem Mint - Perfect new card with completely intact corners, no visible defects, and no white edge points. Centering ratios: 55/45 front, 60/40 back minimum.",
		category: "Gem Mint"
	},
	9.5: {
		grade: 9.5,
		description: "Mint - New condition card from booster with extremely minor imperfections: micro white point on edges or corners, or light print-lines visible under strong lighting.",
		category: "Mint"
	},
	9: {
		grade: 9,
		description: "Near Mint - Card showing only minute wear defects requiring strong light inspection. Minor scratches, one or two white points, slight surface deposits allowed.",
		category: "Near Mint"
	},
	8: {
		grade: 8,
		description: "Excellent + - Excellent appearance card where inspection reveals multiple defects: scratches, white points, and print-lines. At least one of these defects must appear clearly.",
		category: "Excellent +"
	},
	7: {
		grade: 7,
		description: "Excellent - Card in excellent condition with minor handling wear. Micro-scratches from wear on the front and slight edge wear, remaining minimally defective overall.",
		category: "Excellent"
	},
	6: {
		grade: 6,
		description: "Very Good - Frequently handled card with light visible wear. Surfaces show various defects like scratches and white spots. Light corner or edge impact present.",
		category: "Very Good"
	},
	5: {
		grade: 5,
		description: "Good + - Card with apparent defects on surfaces, slightly more pronounced scratches than grade 6. Corners and edges present slight impacts.",
		category: "Good +"
	},
	4: {
		grade: 4,
		description: "Good - Card showing obvious wear from play or poor storage. Damaged corners and edges with silvering possible. Surfaces less glossy than original condition.",
		category: "Good"
	},
	3: {
		grade: 3,
		description: "Fair + - Well-used card without full-width creases. Visible surface damage, white spots on edges, and slight corner bends. Marked scratches and visible surface deposits.",
		category: "Fair +"
	},
	2: {
		grade: 2,
		description: "Fair - Heavily marked card showing creases on multiple areas, severely damaged surfaces, and significant deposits. Card lacks tears but surfaces are particularly damaged.",
		category: "Fair"
	},
	1: {
		grade: 1,
		description: "Poor - Extremely damaged card with severe creases, stains, and wear. Minimal protective history. Partial tears acceptable only at this level.",
		category: "Poor"
	}
};
const SGSGradingScale = {
	"10P": {
		grade: 10,
		sortOrder: 10.1,
		description: "Pristine - A virtually perfect card devoid of the slightest trace of handling, however minor. No observable production defects.",
		category: "Pristine"
	},
	10: {
		grade: 10,
		description: "Gem Mint - Very good production quality with tolerated minor flaws like discrete print lines, air bubbles, or slight silvering that do not compromise visual appeal.",
		category: "Gem Mint"
	},
	9.5: {
		grade: 9.5,
		description: "Mint - Good production quality allowing multiple light imperfections including print lines, small ink defects, and moderate silvering.",
		category: "Mint"
	},
	9: {
		grade: 9,
		description: "NM/MT - Average to poor production quality or discrete wear traces with multiple print lines, notable ink defects, and pronounced silvering.",
		category: "NM/MT"
	},
	8: {
		grade: 8,
		description: "Near Mint - Poor production quality or light wear with numerous print lines, pronounced defects, extended silvering, and minor scratches.",
		category: "Near Mint"
	},
	7: {
		grade: 7,
		description: "Excellent - Deplorable production quality or multiple light wear signs affecting front visual appeal, including multiple scratches and white wear spots.",
		category: "Excellent"
	},
	6: {
		grade: 6,
		description: "Very Good - Numerous moderate signs of wear on back and front with numerous scratches, discoloration, and notched edges.",
		category: "Very Good"
	},
	5: {
		grade: 5,
		description: "Good - Numerous pronounced signs of wear with very numerous scratches, notable discoloration, multiple deposits, and pronounced edge wear.",
		category: "Good"
	},
	4: {
		grade: 4,
		description: "Average - Very numerous signs of wear, some particularly heavy, with pronounced scratches covering most surfaces and significant corner/edge deterioration.",
		category: "Average"
	},
	3: {
		grade: 3,
		description: "Fair - Very numerous signs of wear, some of major importance, with deep scratches, extreme discoloration, and severely worn edges.",
		category: "Fair"
	},
	2: {
		grade: 2,
		description: "Poor - Very advanced signs of wear likely to deeply compromise integrity, with surface peeling, severe discoloration, and pronounced corner damage.",
		category: "Poor"
	},
	1: {
		grade: 1,
		description: "Very Poor - Extreme signs of wear likely to seriously compromise integrity, with extensive surface peeling, extreme discoloration, and severe corner deterioration.",
		category: "Very Poor"
	},
	A: {
		grade: 0,
		description: "Authentic - Card receiving authenticity attestation without a numeric grade.",
		category: "Authentic"
	}
};
/**
* Ace Grading Scale
* UK grading company (Ace Grading Ltd)
* Uses a whole-number 1-10 scale with subgrades (centering, corners, edges, surface)
* @see https://acegrading.com/grading-scale
*/
const AceGradingGradingScale = {
	10: {
		grade: 10,
		description: "Gem Mint - Four undamaged corners, sharp edges and a clean surface free of marks, stains or damage. Only very minor defects that do not affect eye appeal. Centering better than 60/40 front and back.",
		category: "Gem Mint"
	},
	9: {
		grade: 9,
		description: "Mint - Nearly identical to a Gem Mint 10, with one minor imperfection on the corners, edges or surface. Centering better than 65/35 front and 70/30 back.",
		category: "Mint"
	},
	8: {
		grade: 8,
		description: "Near Mint - Mint - Closely resembles a 9 with a few minor imperfections, such as whitening on the corners, edges or surface. Centering better than 70/30 front and 75/25 back.",
		category: "Near Mint - Mint"
	},
	7: {
		grade: 7,
		description: "Near Mint - Slight wear, more visible than an 8, with more noticeable edge, corner or surface damage such as whitening, and more perceptible print defects. Centering better than 75/25 front and 80/20 back.",
		category: "Near Mint"
	},
	6: {
		grade: 6,
		description: "Excellent - Mint - More noticeable damage or print defects, with whitening on more than one corner or edge; corners and edges may not be sharp. Centering better than 80/20 front and back.",
		category: "Excellent - Mint"
	},
	5: {
		grade: 5,
		description: "Excellent - More visible print defects and damage. Corners may be misshapen with whitening or fraying, and scratches may obstruct the artwork or text. Centering better than 80/20 front and back.",
		category: "Excellent"
	},
	4: {
		grade: 4,
		description: "Very Good - Starting to lose integrity in one area: moderate scratching on key focal points, and corners or edges may be misshapen, warped or not sharp. Centering better than 80/20 front and back.",
		category: "Very Good"
	},
	3: {
		grade: 3,
		description: "Good - Starting to lose integrity in multiple areas: moderate scratching, misshapen corners, warped or unsharp edges and very obvious defects. Centering better than 85/15 front and back.",
		category: "Good"
	},
	2: {
		grade: 2,
		description: "Fair - Has lost integrity in one area: heavy scratching on key focal points, misshapen corners, warped or unsharp edges and very obvious defects. Centering better than 85/15 front and back.",
		category: "Fair"
	},
	1: {
		grade: 1,
		description: "Poor - Has lost integrity in multiple areas: heavy scratching, misshapen corners, warped or unsharp edges and very obvious defects. Centering better than 85/15 front and back.",
		category: "Poor"
	}
};
/**
* Master grading standards object
* Access grading descriptions with gradingStandards[service][grade]
*/
const gradingStandards = {
	[SupportedGradingService.PSA]: PSAGradingScale,
	[SupportedGradingService.Beckett]: BeckettGradingScale,
	[SupportedGradingService.CGC]: CGCGradingScale,
	[SupportedGradingService.TAG]: TAGGradingScale,
	[SupportedGradingService.PCG]: PCGGradingScale,
	[SupportedGradingService.CCC]: CCCGradingScale,
	[SupportedGradingService.PCA]: PCAGradingScale,
	[SupportedGradingService.CollectAura]: CollectAuraGradingScale,
	[SupportedGradingService.MTGGrade]: MTGGradeGradingScale,
	[SupportedGradingService.PureGrading]: PureGradingGradingScale,
	[SupportedGradingService.SGS]: SGSGradingScale,
	[SupportedGradingService.AceGrading]: AceGradingGradingScale
};

//#endregion
//#region src/lib/grading/grade-keys.ts
/**
* Grade labels (as stored on listings, e.g. "10", "9.5", "10B", "10P") whose
* numeric grade is at least `min`, across every supported grading service.
* Grades are stored as strings, so a numeric threshold has to be expanded into
* the matching label set.
*/
function getGradeKeysAtOrAbove(min) {
	const keys = /* @__PURE__ */ new Set();
	for (const scale of Object.values(gradingStandards)) for (const [key, description] of Object.entries(scale)) if (description.grade >= min) keys.add(key);
	return [...keys].sort();
}
/** Highest rank a grade label has across all scales (`sortOrder` beats numeric grade); unknown labels fall back to their numeric prefix. */
function getGradeRank(key) {
	let rank = Number.NEGATIVE_INFINITY;
	for (const scale of Object.values(gradingStandards)) {
		const description = scale[key];
		if (description) rank = Math.max(rank, description.sortOrder ?? description.grade);
	}
	if (Number.isFinite(rank)) return rank;
	const numeric = Number.parseFloat(key);
	return Number.isNaN(numeric) ? 0 : numeric;
}
/** Every known grade label mapped to its rank, for consumers that need the whole table at once (e.g. a search sort script). */
function getGradeRankMap() {
	const ranks = {};
	for (const scale of Object.values(gradingStandards)) for (const key of Object.keys(scale)) ranks[key] = getGradeRank(key);
	return ranks;
}
/** Highest rank first; equally ranked labels fall back to label order. */
function compareGradeKeys(a, b) {
	return getGradeRank(b) - getGradeRank(a) || a.localeCompare(b);
}
/** "10B" → "10B - Black Label"; plain numeric labels stay as-is since their category differs per grader. */
function formatGradeKeyLabel(key) {
	if (!Number.isNaN(Number(key))) return key;
	const categories = /* @__PURE__ */ new Set();
	for (const scale of Object.values(gradingStandards)) {
		const category = scale[key]?.category;
		if (category) categories.add(category);
	}
	return categories.size > 0 ? `${key} - ${[...categories].join(" / ")}` : key;
}

//#endregion
//#region src/lib/listing-derived-finishes.ts
/**
* Games whose finishes are derived from TCGCSV's `ProductsAndPrices` feed — a
* LEFT JOIN of the product catalogue against live TCGplayer listings. For
* these games a finish row only exists while a SKU has a live listing, so an
* import proves a finish exists but never that one stopped existing: imports
* may add finishes, never remove them, unless the TCGplayer SKU list covers
* the product (`FinishSource.TcgplayerSkus`).
*
* Every other game's source (Scryfall, curated sheets, official APIs) is
* authoritative on finishes — a removal there is a correction and flows
* through the import.
*/
const LISTING_DERIVED_FINISH_GAMES = new Set(Object.entries(FINISH_SOURCE).filter(([, source]) => source !== FinishSource.Authoritative).map(([gameSlug]) => gameSlug));
/** Whether the game's finishes come from listings and fall back to the add-only fold. */
function hasListingDerivedFinishes(gameSlug) {
	return getFinishSource(gameSlug) !== FinishSource.Authoritative;
}

//#endregion
//#region src/lib/games/fab/fab-metadata-matcher.ts
var FABMetadataMatcher = class {
	slugify(name) {
		return baseSlugify(name);
	}
	buildSearchMatchers(cardName, metadata, locale) {
		const matchers = [];
		if (metadata?.pitch) {
			const fullName = `${cardName} (${metadata.pitch.charAt(0).toUpperCase() + metadata.pitch.slice(1).toLowerCase()})`;
			matchers.push({ name: fullName });
			if (locale) matchers.push({ [`translations.${locale}.name`]: fullName });
		}
		const nameSlug = this.slugify(cardName);
		matchers.push({ nameSlug });
		if (locale) matchers.push({ [`translations.${locale}.name`]: cardName });
		return matchers;
	}
	matchPrinting(printing, requestedMetadata) {
		if (!requestedMetadata?.pitch) return true;
		const requestedPitch = requestedMetadata.pitch.toLowerCase();
		if (printing.pitch !== void 0) {
			if ({
				1: "red",
				2: "yellow",
				3: "blue"
			}[printing.pitch] === requestedPitch) return true;
		}
		if (printing.color && printing.color.length > 0) {
			if (printing.color[0].toLowerCase() === requestedPitch) return true;
		}
		if (printing.variant) {
			const variantLower = printing.variant.toLowerCase();
			if (variantLower === requestedPitch || variantLower === `(${requestedPitch})`) return true;
		}
		if (printing.name) {
			const nameMatch = printing.name.match(/\((Red|Yellow|Blue)\)/i);
			if (nameMatch && nameMatch[1].toLowerCase() === requestedPitch) return true;
		}
		return false;
	}
	getMetadataMismatchError(printing, requestedMetadata) {
		if (!requestedMetadata?.pitch) return null;
		let actualPitchColor = null;
		if (printing.pitch !== void 0) actualPitchColor = {
			1: "red",
			2: "yellow",
			3: "blue"
		}[printing.pitch] || null;
		else if (printing.color && printing.color.length > 0) actualPitchColor = printing.color[0].toLowerCase();
		else if (printing.variant) {
			for (const color of [
				"red",
				"yellow",
				"blue"
			]) if (printing.variant.toLowerCase().includes(color)) {
				actualPitchColor = color;
				break;
			}
		} else if (printing.name) {
			const nameMatch = printing.name.match(/\((Red|Yellow|Blue)\)/i);
			if (nameMatch) actualPitchColor = nameMatch[1].toLowerCase();
		}
		if (!actualPitchColor) return {
			errorMessage: `Product "${printing.name}" with pitch color "${requestedMetadata.pitch}" not found. No pitch information available.`,
			errorDetails: {
				requestedPitchColor: requestedMetadata.pitch,
				availablePitchColor: null
			}
		};
		if (actualPitchColor !== requestedMetadata.pitch.toLowerCase()) return {
			errorMessage: `Product "${printing.name}" with pitch color "${requestedMetadata.pitch}" not found. Available pitch: ${actualPitchColor}`,
			errorDetails: {
				requestedPitchColor: requestedMetadata.pitch,
				availablePitchColor: actualPitchColor
			}
		};
		return null;
	}
};

//#endregion
//#region src/lib/metadata-matcher-factory.ts
const matchers = {
	[SupportedGames.FAB]: new FABMetadataMatcher(),
	[SupportedGames.Lorcana]: null,
	[SupportedGames.Sorcery]: null,
	[SupportedGames.MTG]: null,
	[SupportedGames.Pokemon]: null,
	[SupportedGames.Wankul]: null,
	[SupportedGames.OnePiece]: null,
	[SupportedGames.YuGiOh]: null,
	[SupportedGames.YuGiOhOcg]: null,
	[SupportedGames.ExampleGame]: null,
	[SupportedGames.Rise]: null,
	[SupportedGames.Riftbound]: null,
	[SupportedGames.Drakerion]: null,
	[SupportedGames.Cyberpunk]: null,
	[SupportedGames.PokemonJapan]: null,
	[SupportedGames.StarWarsUnlimited]: null,
	[SupportedGames.NarutoMythos]: null,
	[SupportedGames.GrandArchive]: null,
	[SupportedGames.Eoa]: null,
	[SupportedGames.DbsFusion]: null,
	[SupportedGames.DbsMasters]: null,
	[SupportedGames.Gundam]: null,
	[SupportedGames.ChronoCore]: null,
	[SupportedGames.Palworld]: null,
	[SupportedGames.Azuki]: null,
	[SupportedGames.Dreamland]: null
};
const metadataMatcherFactory = { getMatcherForGame(gameSlug) {
	return matchers[gameSlug] || null;
} };

//#endregion
//#region src/lib/games/cyberpunk/cyberpunk.seo.ts
const cyberpunkSeoConfig = {
	detailKeys: [
		"color",
		"cardType",
		"cost",
		"power"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"color",
		"cardType",
		"classification",
		"cost",
		"power",
		"ram"
	]
};

//#endregion
//#region src/lib/games/dbs-fusion/dbs-fusion.seo.ts
const dbsFusionSeoConfig = {
	detailKeys: [
		"color",
		"type",
		"power",
		"cost",
		"energyCost"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"color",
		"type",
		"power",
		"cost",
		"energyCost"
	]
};

//#endregion
//#region src/lib/games/dbs-masters/dbs-masters.seo.ts
const dbsMastersSeoConfig = {
	detailKeys: [
		"color",
		"type",
		"power",
		"cost",
		"era"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"color",
		"type",
		"power",
		"cost",
		"era"
	]
};

//#endregion
//#region src/lib/games/drakerion/drakerion.seo.ts
const drakerionSeoConfig = {
	detailKeys: [
		"type",
		"kingdom",
		"cost",
		"attack",
		"health"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"type",
		"kingdom",
		"cost",
		"attack",
		"riposte",
		"health",
		"prestige"
	]
};

//#endregion
//#region src/lib/games/fab/fab.seo.ts
const fabSeoConfig = {
	detailKeys: [
		"types",
		"classes",
		"cost",
		"attack"
	],
	cardTextKeys: ["description"],
	additionalPropertyKeys: [
		"types",
		"classes",
		"cost",
		"attack",
		"defense"
	],
	compositeDetails: { attack: "defense" },
	expansionCodeIsSearchable: true
};

//#endregion
//#region src/lib/games/grand-archive/grand-archive.seo.ts
const grandArchiveSeoConfig = {
	detailKeys: [
		"element",
		"cardType",
		"costType",
		"costValue",
		"power"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"element",
		"cardType",
		"cardClass",
		"costType",
		"costValue",
		"power",
		"life",
		"durability",
		"level"
	],
	expansionCodeIsSearchable: true
};

//#endregion
//#region src/lib/games/gundam/gundam.seo.ts
const gundamSeoConfig = {
	detailKeys: [
		"color",
		"type",
		"level",
		"cost",
		"attackPoints",
		"hitPoints"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"color",
		"type",
		"level",
		"cost",
		"attackPoints",
		"hitPoints"
	],
	expansionCodeIsSearchable: true
};

//#endregion
//#region src/lib/games/lorcana/lorcana.seo.ts
const lorcanaSeoConfig = {
	detailKeys: [
		"color",
		"type",
		"cost",
		"lore"
	],
	cardTextKeys: ["description"],
	additionalPropertyKeys: [
		"color",
		"type",
		"cost",
		"lore"
	]
};

//#endregion
//#region src/lib/games/mtg/mtg.seo.ts
const mtgSeoConfig = {
	detailKeys: ["color", "types"],
	cardTextKeys: ["description"],
	additionalPropertyKeys: ["color", "types"],
	expansionCodeIsSearchable: true
};

//#endregion
//#region src/lib/games/naruto-mythos/naruto-mythos.seo.ts
const narutoMythosSeoConfig = {
	detailKeys: [
		"cardType",
		"rarity",
		"group"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: ["cardType", "rarity"]
};

//#endregion
//#region src/lib/games/onepiece/onepiece.seo.ts
const onepieceSeoConfig = {
	detailKeys: [
		"color",
		"type",
		"power",
		"cost"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"color",
		"type",
		"power",
		"cost"
	],
	expansionCodeIsSearchable: true
};

//#endregion
//#region src/lib/games/riftbound/riftbound.seo.ts
const riftboundSeoConfig = {
	detailKeys: [
		"cardType",
		"superType",
		"domains",
		"energyCost",
		"mightCost"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"cardType",
		"superType",
		"domains",
		"energyCost",
		"mightCost"
	]
};

//#endregion
//#region src/lib/games/rise/rise.seo.ts
const riseSeoConfig = {
	detailKeys: [
		"types",
		"rank",
		"plane",
		"attack"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"types",
		"rank",
		"subtypes",
		"plane",
		"attack",
		"defense"
	],
	compositeDetails: { attack: "defense" }
};

//#endregion
//#region src/lib/games/sorcery/sorcery.seo.ts
const sorcerySeoConfig = {
	detailKeys: [
		"type",
		"elements",
		"cost"
	],
	cardTextKeys: ["rulesText"],
	additionalPropertyKeys: [
		"type",
		"elements",
		"cost"
	]
};

//#endregion
//#region src/lib/games/swu/swu.seo.ts
const starWarsUnlimitedSeoConfig = {
	detailKeys: [
		"cardType",
		"aspects",
		"arenaType",
		"cost",
		"power",
		"hp"
	],
	cardTextKeys: ["description"],
	additionalPropertyKeys: [
		"cardType",
		"aspects",
		"arenaType",
		"cost",
		"power",
		"hp"
	],
	expansionCodeIsSearchable: true
};

//#endregion
//#region src/lib/games/wankul/wankul.seo.ts
const wankulSeoConfig = {
	detailKeys: [
		"effigyName",
		"cost",
		"force"
	],
	cardTextKeys: ["rules"],
	additionalPropertyKeys: [
		"effigyName",
		"cost",
		"force"
	]
};

//#endregion
//#region src/lib/seo-builder.ts
const defaultSeoConfig = {
	detailKeys: ["type"],
	cardTextKeys: [
		"description",
		"rulesText",
		"rules",
		"cardText",
		"desc"
	],
	additionalPropertyKeys: []
};
const seoRegistry = {
	pokemon: pokemonSeoConfig,
	mtg: mtgSeoConfig,
	ygo: yugiohSeoConfig,
	"ygo-ocg": yugiohOcgSeoConfig,
	fab: fabSeoConfig,
	lorcana: lorcanaSeoConfig,
	onepiece: onepieceSeoConfig,
	sorcery: sorcerySeoConfig,
	cyberpunk: cyberpunkSeoConfig,
	rise: riseSeoConfig,
	riftbound: riftboundSeoConfig,
	wankul: wankulSeoConfig,
	drakerion: drakerionSeoConfig,
	"pokemon-japan": pokemonJapanSeoConfig,
	swu: starWarsUnlimitedSeoConfig,
	"naruto-mythos": narutoMythosSeoConfig,
	"grand-archive": grandArchiveSeoConfig,
	eoa: eoaSeoConfig,
	"dbs-fusion": dbsFusionSeoConfig,
	"dbs-masters": dbsMastersSeoConfig,
	gundam: gundamSeoConfig,
	"chrono-core": chronoCoreSeoConfig,
	palworld: palworldSeoConfig,
	azuki: azukiSeoConfig,
	dreamland: dreamlandSeoConfig
};
function getGameSeoConfig(slug) {
	return seoRegistry[slug] ?? defaultSeoConfig;
}
/** Longest string still recognisable as a set code ("OP01", "FB03_AR"). */
const MAX_EXPANSION_CODE_LENGTH = 8;
/**
* Several importers fall back to the expansion slug when upstream has no code
* (`apiSet?.code ?? slug`), and `expansions.code` is NOT NULL so they cannot
* simply leave it empty. A code equal to the slug is therefore not a code at
* all, however short it happens to be; length and whitespace catch the rest.
*/
function isSearchableCode(code, slug) {
	return code.length <= MAX_EXPANSION_CODE_LENGTH && !/\s/.test(code) && code.toLowerCase() !== slug.toLowerCase();
}
/**
* The expansion as it should read in a page title: with its code only where
* that code is something players actually search
* ({@link GameSeoConfig.expansionCodeIsSearchable}).
*/
function formatExpansionTitlePart(expansion, gameSlug) {
	const { name, slug, code } = expansion;
	if (!code || !isSearchableCode(code, slug) || !getGameSeoConfig(gameSlug).expansionCodeIsSearchable) return name;
	return `${name} (${code.toUpperCase()})`;
}
/**
* Extracts a typed value from a raw attribute without metadata guidance.
* Falls back to basic type detection.
*/
function extractRawValue(raw) {
	if (raw == null) return null;
	if (typeof raw === "string" && raw.length > 0) return raw;
	if (typeof raw === "number") return raw;
	if (typeof raw === "boolean") return raw;
	if (Array.isArray(raw)) return raw.filter((v) => typeof v === "string");
	return null;
}
/**
* Extracts a typed value from a raw attribute, guided by attribute metadata.
* Uses `meta.type` and `meta.array` to coerce the raw value.
* Returns null if the value is missing or doesn't match the expected type.
*/
function extractValue(raw, meta) {
	if (raw == null) return null;
	switch (meta.type) {
		case "number":
		case "range": return typeof raw === "number" ? raw : null;
		case "enum":
		case "string":
		case "i18n-string":
			if (meta.array) return Array.isArray(raw) ? raw.filter((v) => typeof v === "string") : [];
			return typeof raw === "string" && raw.length > 0 ? raw : null;
		case "boolean": return typeof raw === "boolean" ? raw : null;
		default: return extractRawValue(raw);
	}
}
/**
* Formats a single attribute value as a detail phrase using metadata.
* Returns null if the value should be skipped.
*/
function formatDetailDefault(value, label, meta) {
	if (value == null) return null;
	switch (meta.type) {
		case "number":
		case "range":
			if (typeof value !== "number") return null;
			return `${value} ${label}`;
		case "enum":
		case "string":
		case "i18n-string":
			if (meta.array) {
				if (!Array.isArray(value) || value.length === 0) return null;
				return value.join(", ");
			}
			if (typeof value !== "string" || value.length === 0) return null;
			return value;
		default: return String(value);
	}
}
/**
* Stringifies any extracted value for additionalProperty entries.
*/
function stringifyValue(value) {
	if (value == null) return null;
	if (Array.isArray(value)) {
		const filtered = value.filter((v) => typeof v === "string" || typeof v === "number");
		return filtered.length > 0 ? filtered.join(", ") : null;
	}
	if (typeof value === "string") return value.length > 0 ? value : null;
	if (typeof value === "number") return String(value);
	if (typeof value === "boolean") return String(value);
	return null;
}
function buildDetails(config, metadata, attrs, t) {
	const parts = [];
	const composites = config.compositeDetails ?? {};
	const companionKeys = new Set(Object.values(composites));
	for (const key of config.detailKeys) {
		if (companionKeys.has(key)) continue;
		const meta = metadata[key];
		const raw = attrs[key];
		const value = meta ? extractValue(raw, meta) : extractRawValue(raw);
		const label = meta ? t(meta.name) : key;
		const companionKey = composites[key];
		if (companionKey) {
			const companionMeta = metadata[companionKey];
			const companionValue = companionMeta ? extractValue(attrs[companionKey], companionMeta) : extractRawValue(attrs[companionKey]);
			const companionLabel = companionMeta ? t(companionMeta.name) : companionKey;
			if (value != null && typeof value === "number") {
				let phrase = `${label} ${value}`;
				if (companionValue != null && typeof companionValue === "number") phrase += ` / ${companionLabel} ${companionValue}`;
				parts.push(phrase);
			}
			continue;
		}
		if (meta) {
			const formatted = formatDetailDefault(value, label, meta);
			if (formatted) parts.push(formatted);
		} else {
			const str = stringifyValue(value);
			if (str) parts.push(str);
		}
	}
	return parts.length > 0 ? `${parts.join(", ")}.` : null;
}
function buildCardText(config, attrs) {
	for (const key of config.cardTextKeys) {
		const raw = attrs[key];
		if (typeof raw === "string" && raw.length > 0) return raw;
	}
	return null;
}
function buildAdditionalProperties(config, metadata, attrs, t) {
	const props = [];
	for (const key of config.additionalPropertyKeys) {
		const meta = metadata[key];
		const str = stringifyValue(meta ? extractValue(attrs[key], meta) : extractRawValue(attrs[key]));
		if (str) props.push({
			name: meta ? t(meta.name) : key,
			value: str
		});
	}
	return props;
}
/**
* Builds game-specific SEO data from a declarative config and attribute metadata.
* The builder is purely generic — all game-specific logic lives in the config.
*/
function buildGameSeoData(input) {
	const { seoConfig, attributeMetadata, attrs, t } = input;
	return {
		details: buildDetails(seoConfig, attributeMetadata, attrs, t),
		cardText: buildCardText(seoConfig, attrs),
		additionalProperties: buildAdditionalProperties(seoConfig, attributeMetadata, attrs, t)
	};
}

//#endregion
//#region src/lib/unmapped-attribute-values.ts
/**
* Returns the values an importer produced that the game's configuration does
* not declare for `attributeName`, sorted and de-duplicated.
*
* The importer counterpart to `sanitizeFilter`: both read the same declaration
* via `getAllowedAttributeValues`. A value outside it is rejected by the API's
* Zod enums, and oRPC validates a whole response at once — so one unknown value
* rejects the entire payload and takes down the page that requested it.
*/
function findUnmappedAttributeValues(game, attributeName, values) {
	const attribute = getGameConfiguration(game)?.attributes?.[attributeName];
	const allowed = attribute && getAllowedAttributeValues(attribute);
	if (!allowed) return [];
	const unmapped = /* @__PURE__ */ new Set();
	for (const value of values) if (value && !allowed.has(value)) unmapped.add(value);
	return [...unmapped].sort();
}

//#endregion
export { AceGradingGradingScale, AmericanCardCondition, AzukiCategory, AzukiElement, AzukiFinish, AzukiGameConfiguration, AzukiRarity, BeckettGradingScale, CARD_LEVEL_ID_NAMESPACES, CATALOGUE_QUALITY, CCCGradingScale, CGCGradingScale, CONDITION_TO_BACKEND_MAPPING, CardCondition, CardFinish, ChronoCoreCardType, ChronoCoreChassisType, ChronoCoreDamageType, ChronoCoreEquipRequirement, ChronoCoreFinish, ChronoCoreGameConfiguration, ChronoCoreKeyword, ChronoCoreMkValue, ChronoCoreOrientation, ChronoCoreOrigin, ChronoCoreRarity, ChronoCoreRealm, ChronoCoreSubtype, ChronoCoreTimingRestriction, ChronoCoreTrait, ChronoCoreVariant, CollectAuraGradingScale, Configurations, CyberpunkCardType, CyberpunkClassification, CyberpunkColor, CyberpunkFinish, CyberpunkGameConfiguration, CyberpunkKeyword, CyberpunkRarity, DbsFusionCardType, DbsFusionColor, DbsFusionFinish, DbsFusionGameConfiguration, DbsFusionRarity, DbsMastersCardType, DbsMastersColor, DbsMastersEra, DbsMastersFinish, DbsMastersGameConfiguration, DbsMastersRarity, DrakerionFinish, DrakerionGameConfiguration, DrakerionKingdom, DrakerionRarity, DrakerionRequiresBanner, DrakerionRequiresCity, DrakerionTrait, DrakerionType, DreamlandAbilityType, DreamlandCardType, DreamlandClass, DreamlandFinish, DreamlandGameConfiguration, DreamlandRank, DreamlandRarity, EoaAffinity, EoaCardType, EoaFinish, EoaGameConfiguration, EoaRarity, EoaSubtype, EoaVariant, EuropeanCardCondition, ExampleGameColor, ExampleGameConfiguration, ExampleGameFinish, ExampleGameRarity, FABCardColor, FABCardFinish, FABCardHeroClass, FABCardPrintType, FABCardRarity, FABCardSubType, FABCardTalent, FABCardType, FABCardVariant, FABGameConfiguration, FABPrintingSuffix, FABProductType, FINISH_SOURCE, FOIL_FINISHES, FiltersGroupType, FinishSource, GAME_DISPLAY_ORDER, GameFilters, GrandArchiveCardClass, GrandArchiveCardType, GrandArchiveCostType, GrandArchiveElement, GrandArchiveFinish, GrandArchiveGameConfiguration, GrandArchiveRarity, GrandArchiveSpeed, GrandArchiveVariant, GundamCardType, GundamColor, GundamFinish, GundamGameConfiguration, GundamRarity, HINT_ID_NAMESPACES, IMPORTER_USER_AGENT, LISTING_DERIVED_FINISH_GAMES, ListPresetType, LorcanaCardColor, LorcanaCardFinish, LorcanaCardTrait, LorcanaCardType, LorcanaGameConfiguration, LorcanaInk, LorcanaRarity, MTGCardFinish, MTGColor, MTGGradeGradingScale, MTGRarity, MtgGameConfiguration, NarutoMythosCardType, NarutoMythosFinish, NarutoMythosGameConfiguration, NarutoMythosGroup, NarutoMythosKeyword, NarutoMythosOrientation, NarutoMythosRarity, NarutoMythosVariant, OnePieceCardFinish, OnePieceCardType, OnePieceCardVariant, OnePieceCharacterAttribute, OnePieceColor, OnePieceGameConfiguration, OnePieceRarity, PCAGradingScale, PCGGradingScale, PRIMARY_GAMES, PSAGradingScale, PalworldAptitude, PalworldCardKind, PalworldCardKindSub, PalworldColor, PalworldElement, PalworldFinish, PalworldGameConfiguration, PalworldRarity, PokemonCardFinish, PokemonCardStage, PokemonCardType, PokemonCardVariant, PokemonGameConfiguration, PokemonJapanCardType, PokemonJapanFinish, PokemonJapanGameConfiguration, PokemonJapanRarity, PokemonJapanStage, PokemonJapanType, PokemonJapanVariant, PokemonRarity, PokemonType, ProductType, PureGradingGradingScale, RiftboundCardFinish, RiftboundCardType, RiftboundDomain, RiftboundGameConfiguration, RiftboundOrientation, RiftboundRarity, RiftboundSuperType, RiftboundTag, RiseAbility, RiseCardFinish, RiseGameConfiguration, RisePlane, RiseRank, RiseRarity, RiseSubtype, RiseType, RiseVariant, SGSGradingScale, SetType, ShortAmericanCardCondition, ShortEuropeanCardCondition, SorceryCardCategory, SorceryCardFinish, SorceryCardKeyword, SorceryCardSubType, SorceryCardType, SorceryCardVariant, SorceryElement, SorceryFiltersOrder, SorceryGameConfiguration, SorceryRarity, StarWarsUnlimitedArenaType, StarWarsUnlimitedAspect, StarWarsUnlimitedCardType, StarWarsUnlimitedFinish, StarWarsUnlimitedGameConfiguration, StarWarsUnlimitedOrientation, StarWarsUnlimitedRarity, StarWarsUnlimitedVariant, SupportedGames, SupportedGradingService, SupportedMarketplace, TAGGradingScale, UNOPENED_TRANSLATION_KEY, WankulCardFinish, WankulEffigy, WankulGameConfiguration, WankulRarity, WankulRarityFullName, YUGIOH_LANGUAGES, YUGIOH_OCG_LANGUAGES, YUGIOH_TCG_LANGUAGES, YuGiOhGameConfiguration, YuGiOhOcgGameConfiguration, YugiohAttribute, YugiohBanStatus, YugiohCardFinish, YugiohCardType, YugiohEdition, YugiohLinkMarker, YugiohRace, YugiohRarity, YugiohVariant, additionalGames, azukiFilters, azukiSeoConfig, buildGameSeoData, canonicalGameRank, cardLevelIdNamespaces, cardmarketVendorId, change24hEuAttribute, change24hUsAttribute, change30dEuAttribute, change30dUsAttribute, change7dEuAttribute, change7dUsAttribute, chronoCoreFilters, chronoCoreSeoConfig, clampMarketplaceToGame, commonAttributeTranslationsMap, commonAttributes, compareGradeKeys, cyberpunkFilters, dbsFusionFilters, dbsMastersFilters, defaultPriceSourceForCountry, drakerionFilters, dreamlandFilters, dreamlandSeoConfig, eoaFilters, eoaSeoConfig, exampleGameFilters, expansionAttribute, extractGameAttributes, extractRawValue, extractValue, fabFilters, findUnmappedAttributeValues, finishAttribute, finishAttributeValues, finishesAttribute, formatExpansionTitlePart, formatGradeKeyLabel, gameAttribute, gameCompanies, gameDefinitions, gameFilters, gameTranslationsMap, getCatalogueQuality, getCertificationUrl, getCondition, getConditionTranslationKey, getDisplayVariant, getExternalReferenceKeys, getFinishSource, getFullCondition, getGameAttributeFields, getGameConfiguration, getGameDefaultLanguages, getGameIcon, getGameIconPath, getGameImage, getGameImagePath, getGameNameFromSlug, getGameSeoConfig, getGradeKeysAtOrAbove, getGradeRank, getGradeRankMap, getLowestFoilForGame, getPrintNumberAliasesAttribute, getShortCondition, gradingStandards, grandArchiveFilters, gundamFilters, hasListingDerivedFinishes, hintIdNamespaces, imageAttribute, isCardFinish, isConditionAttribute, isFoilFinish, languagesAttribute, listingPriceAttribute, lorcanaFilters, matchGameSearch, metadataMatcherFactory, mtgFilters, nameAttribute, narutoMythosFilters, onepieceFilters, palworldFilters, palworldSeoConfig, pokemonFilters, pokemonJapanFilters, pokemonJapanSeoConfig, pokemonSeoConfig, priceDataSources, priceEuAttribute, priceUsAttribute, primaryFinish, printNumberAttribute, productFilters, productTypeAttribute, readGamePrintNumberAliases, reorderGamesByPreference, riftboundFilters, riseFilters, sorceryFilters, sortNumberAttribute, starWarsUnlimitedFilters, tcgplayerVendorId, usesTcgplayerSkus, wankulFilters, yugiohFilters, yugiohOcgFilters, yugiohOcgSeoConfig, yugiohSeoConfig };
//# sourceMappingURL=index.js.map